import { isFunction as isFunction$1, get, find, cloneDeep, extend, isEqual, isArray as isArray$1, concat, set, first, values, map as map$1, each, isEmpty, filter, pickBy, size, mapValues, findIndex, sortBy, indexOf, uniq, isNil, reject, isString, last, sumBy, omit, remove as remove$1, forEach, without, isObject as isObject$1 } from 'lodash';
import { Subject, BehaviorSubject } from 'rxjs';
import * as signalR from '@microsoft/signalr';

// Import package.json file
//
const pkg = require('../package.json');
/**
 * Dependency free uuid generator
 *
 * @param {*} [a]
 * @returns {string}
 */
/* tslint:disable */
function uuid(a) {
    return a ? (0 | Math.random() * 16).toString(16) : ("" + 1e7 + -1e3 + -4e3 + -8e3 + -1e11).replace(/1|0/g, uuid);
}
/**
 * Settings for the EVA Redux SDK
 *
 * @export
 * @class Settings
 */
class Settings {
    constructor() {
        /**
         * The settings change stream
         * Not all settings are emitted. Only the following:
         *
         * - language
         * - defaultToken
         * - userToken
         *
         * @type {Subject<ISettingsChange>}
         * @memberof Settings
         */
        this.changes$ = new Subject();
        /**
         * By default the sentinel will handle parseBarcode calls when it is
         * available. Use this setting to disable that behavior.
         * You can always use `sentinel.performance.parseBarcode(..)` manually
         *
         */
        this.disableSentinelParseCode = false;
        /**
         * This setting can be used to keep using the last known sentinel endpoint
         * when the application configuration has none defined
         * Useful for debugging and fixed store deployments
         *
         */
        this.preserveLastKnownSentinel = false;
        /**
         * The client version used for calls from this SDK.
         * Used for the ClientVersion header field value
         *
         * @public
         * @type {string}
         * @memberof Settings
         */
        this.clientVersion = pkg.version;
        /**
         * The client name used for calls from this SDK.
         * Used for the ClientName header field value
         *
         * @public
         * @type {string}
         * @memberof Settings
         */
        this.clientName = pkg.name;
        /**
         * The app version used for calls from this SDK.
         * Used for the Eva-User-Agent header field value
         *
         * @public
         * @type {string}
         * @memberof Settings
         */
        this.appVersion = '0.0.0';
        /**
         * The app name used for calls from this SDK.
         * Used for the Eva-User-Agent header field value
         *
         * @public
         * @type {string}
         * @memberof Settings
         */
        this.appName = 'UNKNOWN';
        /**
         * The default included product properties for services such as:
         *
         * - Core.GetOrder
         * - Core.GetShoppingCart
         * - Core.SearchProducts
         * - Core.GetProducts
         * - Core.GetBundleProductDetails
         * - Core.ListProductBundles
         *
         * This is not an exhaustive list. In a service request these can be either
         * called IncludedFields or ProductProperties.
         *
         * @type {string[]}
         */
        this.defaultProductProperties = [
            'backend_id',
            'barcodes',
            'clothing_primary_color',
            'clothing_size',
            'configurable_properties',
            'currency_id',
            'custom_id',
            'display_price',
            'display_value',
            'is_sellable',
            'logical_level',
            'primary_image.blob',
            'product_description',
            'product_id',
            'product_media',
            'product_name',
            'product_requirements',
            'product_status',
            'product_types',
            'recommended_retail_price',
            'short_product_description',
            'unstackable',
        ];
        /**
         * The additional request options for the getShoppingCart state.
         * Defaults are set to retrieve the most logical data needed
         * before entering the checkout. Discounts, product requirements, etc.
         *
         * @type {EVA.Core.AdditionalOrderDataOptions}
         */
        this.shoppingCartOptions = {
            IncludeAvailablePaymentMethods: false,
            IncludeAvailableRefundPaymentMethods: false,
            IncludeAvailableShippingMethods: false,
            IncludeCheckoutOptions: true,
            IncludeGiftWrapping: true,
            IncludeOrganizationUnitData: true,
            IncludePaymentTransactionActions: false,
            IncludePickProductOptions: true,
            IncludePrefigureDiscounts: true,
            IncludeProductRequirements: true,
            IncludeValidateShipment: false,
            ProductProperties: this.defaultProductProperties,
            RequiredFor: 7,
        };
        /**
         * Optional chain to fetch availability indication for the products in the cart
         *
         */
        this.enableGetShoppingCartProductAvailabilityIndication = false;
        /**
         * The additional request options for the getOrder state.
         * Defaults are set to retrieve the most logical data needed
         * during the checkout. Shipping methods, payment methods, etc.
         *
         * @type {EVA.Core.AdditionalOrderDataOptions}
         */
        this.currentOrderOptions = {
            IncludeAvailablePaymentMethods: true,
            IncludeAvailableRefundPaymentMethods: true,
            IncludeAvailableShippingMethods: true,
            IncludeCheckoutOptions: true,
            IncludeGiftWrapping: true,
            IncludeOrganizationUnitData: true,
            IncludePaymentTransactionActions: true,
            IncludePickProductOptions: false,
            IncludePrefigureDiscounts: false,
            IncludeProductRequirements: true,
            IncludeValidateShipment: true,
            ProductProperties: this.defaultProductProperties,
            RequiredFor: 7,
        };
        /**
         * The additional request options for the searchProducts state.
         * Defaults are to enable retrieving of availability and discounts
         *
         * @type {EVA.Core.SearchProductsOptions}
         */
        this.productSearchOptions = {
            AvailabilityOptions: {
                Bundles: {
                    IncludeBundleInformation: false,
                },
                Delivery: {
                    AvailabilityDate: true,
                },
                Pickup: {
                    AvailabilityDate: false,
                },
                QuantityAvailable: true,
                ProductAvailabilityDetails: {},
            },
            IncludePrefigureDiscounts: true,
        };
        /**
         * Enables sorting of discount tiers on from value before calculating
         * tier change
         *
         * @type {boolean}
         * @memberof Settings
         */
        this.enableDiscountTierSorting = false;
        /**
         * Add product to order placeholder are stacked by default
         * Meaning that if the product is already in the cart no placeholder will be
         * added
         *
         * @type {boolean}
         * @memberof Settings
         */
        this.placeholderStacking = true;
        /**
         * Enabled debug logging for the SDK
         *
         * @type {boolean}
         * @memberof Settings
         */
        this.enableLogging = false;
        /**
         * Cart changes post-login filter out older order lines. This ensure we do not
         * get changes from an anonymous cart being claimed. Newer lines can include
         * welcome gift for instance.
         * Default allowed age is 300000ms (5 minutes)
         *
         * @type {number}
         */
        this.cartChangeLoginOrderLineAge = 300000;
        /**
         * The current order id
         *
         * @private
         * @type {number}
         */
        this.theCurrentOrderId = 0;
        /**
         * The in memory state store for logged in users
         *
         * @private
         * @type {{ [userId: number]: ISavedState }}
         * @memberof Settings
         */
        this.stateMemory = {};
    }
    /**
     * Getter method for the defaultToken
     *
     * @type {string}
     * @memberof Settings
     */
    get defaultToken() {
        return this.theDefaultToken;
    }
    /**
     * Sets the user token and emits the changed value
     *
     * @memberof Settings
     */
    set defaultToken(token) {
        // Prepare the setting change
        //
        const change = {
            name: 'defaultToken',
            old: this.theDefaultToken,
            new: token,
        };
        if (change.old === change.new) {
            return;
        }
        // Change the setting
        //
        this.theDefaultToken = token;
        // Emit the changed setting
        //
        this.changes$.next(change);
        if (isFunction$1(this.changeCallback)) {
            this.changeCallback(change);
        }
    }
    /**
     * Getter method for the userToken
     *
     * @type {string}
     * @memberof Settings
     */
    get userToken() {
        return this.theUserToken;
    }
    /**
     * Sets the user token and emits the changed value
     *
     * @memberof Settings
     */
    set userToken(token) {
        // Prepare the setting change
        //
        const change = {
            name: 'userToken',
            old: this.theUserToken,
            new: token,
        };
        if (change.old === change.new) {
            return;
        }
        // Change the setting
        //
        this.theUserToken = token;
        // Emit the changed setting
        //
        this.changes$.next(change);
        if (isFunction$1(this.changeCallback)) {
            this.changeCallback(change);
        }
    }
    /**
     * Getter method for the applicationToken
     *
     * @type {string}
     * @memberof Settings
     */
    get applicationToken() {
        return this.theApplicationToken;
    }
    /**
     * Sets the user token and emits the changed value
     *
     * @memberof Settings
     */
    set applicationToken(token) {
        // Prepare the setting change
        //
        const change = {
            name: 'applicationToken',
            old: this.theApplicationToken,
            new: token,
        };
        if (change.old === change.new) {
            return;
        }
        // Change the setting
        //
        this.theApplicationToken = token;
        // Emit the changed setting
        //
        this.changes$.next(change);
        if (isFunction$1(this.changeCallback)) {
            this.changeCallback(change);
        }
    }
    /**
     * Getter method for the applicationToken
     *
     * @type {string}
     * @memberof Settings
     */
    get currentOrderId() {
        return this.theCurrentOrderId;
    }
    /**
     * Sets the user token and emits the changed value
     *
     * @memberof Settings
     */
    set currentOrderId(orderId) {
        // Prepare the setting change
        //
        const change = {
            name: 'currentOrderId',
            old: this.theCurrentOrderId,
            new: orderId,
        };
        if (change.old === change.new) {
            return;
        }
        // Change the setting
        //
        this.theCurrentOrderId = orderId;
        // Emit the changed setting
        //
        this.changes$.next(change);
        if (isFunction$1(this.changeCallback)) {
            this.changeCallback(change);
        }
    }
    /**
     * Getter method for the language
     *
     * @type {string}
     * @memberof Settings
     */
    get language() {
        return this.theLanguage;
    }
    /**
     * Sets the language and emits the changed value
     *
     * @memberof Settings
     */
    set language(newLanguage) {
        // Prepare the setting change
        //
        const change = {
            name: 'language',
            old: this.theLanguage,
            new: newLanguage,
        };
        if (change.old === change.new) {
            return;
        }
        // Change the setting
        //
        this.theLanguage = newLanguage;
        // Emit the changed setting
        //
        this.changes$.next(change);
        if (isFunction$1(this.changeCallback)) {
            this.changeCallback(change);
        }
    }
    /**
     * Getter method for the sentinel endpoint
     *
     * @type {string}
     * @memberof Settings
     */
    get sentinelEndPointUrl() {
        return this.theSentinelEndPointURL;
    }
    /**
     * Sets the sentinel endpoint and emits the changed value
     *
     * @memberof Settings
     */
    set sentinelEndPointUrl(newEndpointUrl) {
        // Prepare the setting change
        //
        const change = {
            name: 'sentinelEndPointUrl',
            old: this.theSentinelEndPointURL,
            new: newEndpointUrl,
        };
        if (change.old === change.new) {
            return;
        }
        // Change the setting
        //
        this.theSentinelEndPointURL = newEndpointUrl;
        // Emit the changed setting
        //
        this.changes$.next(change);
        if (isFunction$1(this.changeCallback)) {
            this.changeCallback(change);
        }
    }
    /**
     * Saves a users state in memory for reuse
     *
     * @param {IRootState} state The state to preserve
     * @memberof Settings
     */
    saveState(userId, state) {
        this.stateMemory[userId] = { state };
    }
    /**
     * Retrieves a previously stored state
     *
     * @param {number} userId The users identifier
     * @returns
     * @memberof Settings
     */
    getSavedState(userId) {
        return this.stateMemory[userId];
    }
    /**
     * Removes any save state for a user
     *
     * @param {number} userId
     * @memberof Settings
     */
    purgeSavedState(userId) {
        delete this.stateMemory[userId];
    }
    /**
     * Removes all cached states
     *
     * @memberof Settings
     */
    purgeAllSavedStates() {
        this.stateMemory = {};
    }
}
const settings = new Settings();

function log$1(message, ...optionalParams) {
    if (settings.enableLogging || !console) {
        console.log.apply(console.log, [message, optionalParams]);
    }
}
function debug(message, ...optionalParams) {
    if (settings.enableLogging || !console) {
        console.log.apply(console.log, [message, optionalParams]);
    }
}
function warn(message, ...optionalParams) {
    // Always log warning
    //
    if (console) {
        console.warn.apply(console.warn, [message, optionalParams]);
    }
}
function error(message, ...optionalParams) {
    // Always log errors
    //
    if (console) {
        console.error.apply(console.error, [message, optionalParams]);
    }
}

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof global == 'object' && global && global.Object === Object && global;

var freeGlobal$1 = freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root$1 = freeGlobal$1 || freeSelf || Function('return this')();

var root$2 = root$1;

/** Built-in value references. */
var Symbol$1 = root$2.Symbol;

var Symbol$2 = Symbol$1;

/** Used for built-in method references. */
var objectProto$2 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$2.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$2.toString;

/** Built-in value references. */
var symToStringTag$1 = Symbol$2 ? Symbol$2.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty$2.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString.call(value);
}

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag = Symbol$2 ? Symbol$2.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? getRawTag(value)
    : objectToString(value);
}

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

/** Built-in value references. */
var getPrototype = overArg(Object.getPrototypeOf, Object);

var getPrototype$1 = getPrototype;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

/** `Object#toString` result references. */
var objectTag = '[object Object]';

/** Used for built-in method references. */
var funcProto = Function.prototype,
    objectProto = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto.hasOwnProperty;

/** Used to infer the `Object` constructor. */
var objectCtorString = funcToString.call(Object);

/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @static
 * @memberOf _
 * @since 0.8.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * _.isPlainObject(new Foo);
 * // => false
 *
 * _.isPlainObject([1, 2, 3]);
 * // => false
 *
 * _.isPlainObject({ 'x': 0, 'y': 0 });
 * // => true
 *
 * _.isPlainObject(Object.create(null));
 * // => true
 */
function isPlainObject(value) {
  if (!isObjectLike(value) || baseGetTag(value) != objectTag) {
    return false;
  }
  var proto = getPrototype$1(value);
  if (proto === null) {
    return true;
  }
  var Ctor = hasOwnProperty$1.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor == 'function' && Ctor instanceof Ctor &&
    funcToString.call(Ctor) == objectCtorString;
}

function symbolObservablePonyfill(root) {
	var result;
	var Symbol = root.Symbol;

	if (typeof Symbol === 'function') {
		if (Symbol.observable) {
			result = Symbol.observable;
		} else {
			result = Symbol('observable');
			Symbol.observable = result;
		}
	} else {
		result = '@@observable';
	}

	return result;
}

/* global window */

var root;

if (typeof self !== 'undefined') {
  root = self;
} else if (typeof window !== 'undefined') {
  root = window;
} else if (typeof global !== 'undefined') {
  root = global;
} else if (typeof module !== 'undefined') {
  root = module;
} else {
  root = Function('return this')();
}

var result = symbolObservablePonyfill(root);

/**
 * These are private action types reserved by Redux.
 * For any unknown actions, you must return the current state.
 * If the current state is undefined, you must return the initial state.
 * Do not reference these action types directly in your code.
 */
var ActionTypes = {
  INIT: '@@redux/INIT'

  /**
   * Creates a Redux store that holds the state tree.
   * The only way to change the data in the store is to call `dispatch()` on it.
   *
   * There should only be a single store in your app. To specify how different
   * parts of the state tree respond to actions, you may combine several reducers
   * into a single reducer function by using `combineReducers`.
   *
   * @param {Function} reducer A function that returns the next state tree, given
   * the current state tree and the action to handle.
   *
   * @param {any} [preloadedState] The initial state. You may optionally specify it
   * to hydrate the state from the server in universal apps, or to restore a
   * previously serialized user session.
   * If you use `combineReducers` to produce the root reducer function, this must be
   * an object with the same shape as `combineReducers` keys.
   *
   * @param {Function} [enhancer] The store enhancer. You may optionally specify it
   * to enhance the store with third-party capabilities such as middleware,
   * time travel, persistence, etc. The only store enhancer that ships with Redux
   * is `applyMiddleware()`.
   *
   * @returns {Store} A Redux store that lets you read the state, dispatch actions
   * and subscribe to changes.
   */
};function createStore(reducer, preloadedState, enhancer) {
  var _ref2;

  if (typeof preloadedState === 'function' && typeof enhancer === 'undefined') {
    enhancer = preloadedState;
    preloadedState = undefined;
  }

  if (typeof enhancer !== 'undefined') {
    if (typeof enhancer !== 'function') {
      throw new Error('Expected the enhancer to be a function.');
    }

    return enhancer(createStore)(reducer, preloadedState);
  }

  if (typeof reducer !== 'function') {
    throw new Error('Expected the reducer to be a function.');
  }

  var currentReducer = reducer;
  var currentState = preloadedState;
  var currentListeners = [];
  var nextListeners = currentListeners;
  var isDispatching = false;

  function ensureCanMutateNextListeners() {
    if (nextListeners === currentListeners) {
      nextListeners = currentListeners.slice();
    }
  }

  /**
   * Reads the state tree managed by the store.
   *
   * @returns {any} The current state tree of your application.
   */
  function getState() {
    return currentState;
  }

  /**
   * Adds a change listener. It will be called any time an action is dispatched,
   * and some part of the state tree may potentially have changed. You may then
   * call `getState()` to read the current state tree inside the callback.
   *
   * You may call `dispatch()` from a change listener, with the following
   * caveats:
   *
   * 1. The subscriptions are snapshotted just before every `dispatch()` call.
   * If you subscribe or unsubscribe while the listeners are being invoked, this
   * will not have any effect on the `dispatch()` that is currently in progress.
   * However, the next `dispatch()` call, whether nested or not, will use a more
   * recent snapshot of the subscription list.
   *
   * 2. The listener should not expect to see all state changes, as the state
   * might have been updated multiple times during a nested `dispatch()` before
   * the listener is called. It is, however, guaranteed that all subscribers
   * registered before the `dispatch()` started will be called with the latest
   * state by the time it exits.
   *
   * @param {Function} listener A callback to be invoked on every dispatch.
   * @returns {Function} A function to remove this change listener.
   */
  function subscribe(listener) {
    if (typeof listener !== 'function') {
      throw new Error('Expected listener to be a function.');
    }

    var isSubscribed = true;

    ensureCanMutateNextListeners();
    nextListeners.push(listener);

    return function unsubscribe() {
      if (!isSubscribed) {
        return;
      }

      isSubscribed = false;

      ensureCanMutateNextListeners();
      var index = nextListeners.indexOf(listener);
      nextListeners.splice(index, 1);
    };
  }

  /**
   * Dispatches an action. It is the only way to trigger a state change.
   *
   * The `reducer` function, used to create the store, will be called with the
   * current state tree and the given `action`. Its return value will
   * be considered the **next** state of the tree, and the change listeners
   * will be notified.
   *
   * The base implementation only supports plain object actions. If you want to
   * dispatch a Promise, an Observable, a thunk, or something else, you need to
   * wrap your store creating function into the corresponding middleware. For
   * example, see the documentation for the `redux-thunk` package. Even the
   * middleware will eventually dispatch plain object actions using this method.
   *
   * @param {Object} action A plain object representing “what changed”. It is
   * a good idea to keep actions serializable so you can record and replay user
   * sessions, or use the time travelling `redux-devtools`. An action must have
   * a `type` property which may not be `undefined`. It is a good idea to use
   * string constants for action types.
   *
   * @returns {Object} For convenience, the same action object you dispatched.
   *
   * Note that, if you use a custom middleware, it may wrap `dispatch()` to
   * return something else (for example, a Promise you can await).
   */
  function dispatch(action) {
    if (!isPlainObject(action)) {
      throw new Error('Actions must be plain objects. ' + 'Use custom middleware for async actions.');
    }

    if (typeof action.type === 'undefined') {
      throw new Error('Actions may not have an undefined "type" property. ' + 'Have you misspelled a constant?');
    }

    if (isDispatching) {
      throw new Error('Reducers may not dispatch actions.');
    }

    try {
      isDispatching = true;
      currentState = currentReducer(currentState, action);
    } finally {
      isDispatching = false;
    }

    var listeners = currentListeners = nextListeners;
    for (var i = 0; i < listeners.length; i++) {
      var listener = listeners[i];
      listener();
    }

    return action;
  }

  /**
   * Replaces the reducer currently used by the store to calculate the state.
   *
   * You might need this if your app implements code splitting and you want to
   * load some of the reducers dynamically. You might also need this if you
   * implement a hot reloading mechanism for Redux.
   *
   * @param {Function} nextReducer The reducer for the store to use instead.
   * @returns {void}
   */
  function replaceReducer(nextReducer) {
    if (typeof nextReducer !== 'function') {
      throw new Error('Expected the nextReducer to be a function.');
    }

    currentReducer = nextReducer;
    dispatch({ type: ActionTypes.INIT });
  }

  /**
   * Interoperability point for observable/reactive libraries.
   * @returns {observable} A minimal observable of state changes.
   * For more information, see the observable proposal:
   * https://github.com/tc39/proposal-observable
   */
  function observable() {
    var _ref;

    var outerSubscribe = subscribe;
    return _ref = {
      /**
       * The minimal observable subscription method.
       * @param {Object} observer Any object that can be used as an observer.
       * The observer object should have a `next` method.
       * @returns {subscription} An object with an `unsubscribe` method that can
       * be used to unsubscribe the observable from the store, and prevent further
       * emission of values from the observable.
       */
      subscribe: function subscribe(observer) {
        if (typeof observer !== 'object') {
          throw new TypeError('Expected the observer to be an object.');
        }

        function observeState() {
          if (observer.next) {
            observer.next(getState());
          }
        }

        observeState();
        var unsubscribe = outerSubscribe(observeState);
        return { unsubscribe: unsubscribe };
      }
    }, _ref[result] = function () {
      return this;
    }, _ref;
  }

  // When a store is created, an "INIT" action is dispatched so that every
  // reducer returns their initial state. This effectively populates
  // the initial state tree.
  dispatch({ type: ActionTypes.INIT });

  return _ref2 = {
    dispatch: dispatch,
    subscribe: subscribe,
    getState: getState,
    replaceReducer: replaceReducer
  }, _ref2[result] = observable, _ref2;
}

/**
 * Prints a warning in the console if it exists.
 *
 * @param {String} message The warning message.
 * @returns {void}
 */
function warning(message) {
  /* eslint-disable no-console */
  if (typeof console !== 'undefined' && typeof console.error === 'function') {
    console.error(message);
  }
  /* eslint-enable no-console */
  try {
    // This error was thrown as a convenience so that if you enable
    // "break on all exceptions" in your console,
    // it would pause the execution at this line.
    throw new Error(message);
    /* eslint-disable no-empty */
  } catch (e) {}
  /* eslint-enable no-empty */
}

function getUndefinedStateErrorMessage(key, action) {
  var actionType = action && action.type;
  var actionName = actionType && '"' + actionType.toString() + '"' || 'an action';

  return 'Given action ' + actionName + ', reducer "' + key + '" returned undefined. ' + 'To ignore an action, you must explicitly return the previous state. ' + 'If you want this reducer to hold no value, you can return null instead of undefined.';
}

function getUnexpectedStateShapeWarningMessage(inputState, reducers, action, unexpectedKeyCache) {
  var reducerKeys = Object.keys(reducers);
  var argumentName = action && action.type === ActionTypes.INIT ? 'preloadedState argument passed to createStore' : 'previous state received by the reducer';

  if (reducerKeys.length === 0) {
    return 'Store does not have a valid reducer. Make sure the argument passed ' + 'to combineReducers is an object whose values are reducers.';
  }

  if (!isPlainObject(inputState)) {
    return 'The ' + argumentName + ' has unexpected type of "' + {}.toString.call(inputState).match(/\s([a-z|A-Z]+)/)[1] + '". Expected argument to be an object with the following ' + ('keys: "' + reducerKeys.join('", "') + '"');
  }

  var unexpectedKeys = Object.keys(inputState).filter(function (key) {
    return !reducers.hasOwnProperty(key) && !unexpectedKeyCache[key];
  });

  unexpectedKeys.forEach(function (key) {
    unexpectedKeyCache[key] = true;
  });

  if (unexpectedKeys.length > 0) {
    return 'Unexpected ' + (unexpectedKeys.length > 1 ? 'keys' : 'key') + ' ' + ('"' + unexpectedKeys.join('", "') + '" found in ' + argumentName + '. ') + 'Expected to find one of the known reducer keys instead: ' + ('"' + reducerKeys.join('", "') + '". Unexpected keys will be ignored.');
  }
}

function assertReducerShape(reducers) {
  Object.keys(reducers).forEach(function (key) {
    var reducer = reducers[key];
    var initialState = reducer(undefined, { type: ActionTypes.INIT });

    if (typeof initialState === 'undefined') {
      throw new Error('Reducer "' + key + '" returned undefined during initialization. ' + 'If the state passed to the reducer is undefined, you must ' + 'explicitly return the initial state. The initial state may ' + 'not be undefined. If you don\'t want to set a value for this reducer, ' + 'you can use null instead of undefined.');
    }

    var type = '@@redux/PROBE_UNKNOWN_ACTION_' + Math.random().toString(36).substring(7).split('').join('.');
    if (typeof reducer(undefined, { type: type }) === 'undefined') {
      throw new Error('Reducer "' + key + '" returned undefined when probed with a random type. ' + ('Don\'t try to handle ' + ActionTypes.INIT + ' or other actions in "redux/*" ') + 'namespace. They are considered private. Instead, you must return the ' + 'current state for any unknown actions, unless it is undefined, ' + 'in which case you must return the initial state, regardless of the ' + 'action type. The initial state may not be undefined, but can be null.');
    }
  });
}

/**
 * Turns an object whose values are different reducer functions, into a single
 * reducer function. It will call every child reducer, and gather their results
 * into a single state object, whose keys correspond to the keys of the passed
 * reducer functions.
 *
 * @param {Object} reducers An object whose values correspond to different
 * reducer functions that need to be combined into one. One handy way to obtain
 * it is to use ES6 `import * as reducers` syntax. The reducers may never return
 * undefined for any action. Instead, they should return their initial state
 * if the state passed to them was undefined, and the current state for any
 * unrecognized action.
 *
 * @returns {Function} A reducer function that invokes every reducer inside the
 * passed object, and builds a state object with the same shape.
 */
function combineReducers(reducers) {
  var reducerKeys = Object.keys(reducers);
  var finalReducers = {};
  for (var i = 0; i < reducerKeys.length; i++) {
    var key = reducerKeys[i];

    if (process.env.NODE_ENV !== 'production') {
      if (typeof reducers[key] === 'undefined') {
        warning('No reducer provided for key "' + key + '"');
      }
    }

    if (typeof reducers[key] === 'function') {
      finalReducers[key] = reducers[key];
    }
  }
  var finalReducerKeys = Object.keys(finalReducers);

  var unexpectedKeyCache = void 0;
  if (process.env.NODE_ENV !== 'production') {
    unexpectedKeyCache = {};
  }

  var shapeAssertionError = void 0;
  try {
    assertReducerShape(finalReducers);
  } catch (e) {
    shapeAssertionError = e;
  }

  return function combination() {
    var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var action = arguments[1];

    if (shapeAssertionError) {
      throw shapeAssertionError;
    }

    if (process.env.NODE_ENV !== 'production') {
      var warningMessage = getUnexpectedStateShapeWarningMessage(state, finalReducers, action, unexpectedKeyCache);
      if (warningMessage) {
        warning(warningMessage);
      }
    }

    var hasChanged = false;
    var nextState = {};
    for (var _i = 0; _i < finalReducerKeys.length; _i++) {
      var _key = finalReducerKeys[_i];
      var reducer = finalReducers[_key];
      var previousStateForKey = state[_key];
      var nextStateForKey = reducer(previousStateForKey, action);
      if (typeof nextStateForKey === 'undefined') {
        var errorMessage = getUndefinedStateErrorMessage(_key, action);
        throw new Error(errorMessage);
      }
      nextState[_key] = nextStateForKey;
      hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
    }
    return hasChanged ? nextState : state;
  };
}

/**
 * Composes single-argument functions from right to left. The rightmost
 * function can take multiple arguments as it provides the signature for
 * the resulting composite function.
 *
 * @param {...Function} funcs The functions to compose.
 * @returns {Function} A function obtained by composing the argument functions
 * from right to left. For example, compose(f, g, h) is identical to doing
 * (...args) => f(g(h(...args))).
 */

function compose() {
  for (var _len = arguments.length, funcs = Array(_len), _key = 0; _key < _len; _key++) {
    funcs[_key] = arguments[_key];
  }

  if (funcs.length === 0) {
    return function (arg) {
      return arg;
    };
  }

  if (funcs.length === 1) {
    return funcs[0];
  }

  return funcs.reduce(function (a, b) {
    return function () {
      return a(b.apply(undefined, arguments));
    };
  });
}

var _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

/**
 * Creates a store enhancer that applies middleware to the dispatch method
 * of the Redux store. This is handy for a variety of tasks, such as expressing
 * asynchronous actions in a concise manner, or logging every action payload.
 *
 * See `redux-thunk` package as an example of the Redux middleware.
 *
 * Because middleware is potentially asynchronous, this should be the first
 * store enhancer in the composition chain.
 *
 * Note that each middleware will be given the `dispatch` and `getState` functions
 * as named arguments.
 *
 * @param {...Function} middlewares The middleware chain to be applied.
 * @returns {Function} A store enhancer applying the middleware.
 */
function applyMiddleware() {
  for (var _len = arguments.length, middlewares = Array(_len), _key = 0; _key < _len; _key++) {
    middlewares[_key] = arguments[_key];
  }

  return function (createStore) {
    return function (reducer, preloadedState, enhancer) {
      var store = createStore(reducer, preloadedState, enhancer);
      var _dispatch = store.dispatch;
      var chain = [];

      var middlewareAPI = {
        getState: store.getState,
        dispatch: function dispatch(action) {
          return _dispatch(action);
        }
      };
      chain = middlewares.map(function (middleware) {
        return middleware(middlewareAPI);
      });
      _dispatch = compose.apply(undefined, chain)(store.dispatch);

      return _extends$3({}, store, {
        dispatch: _dispatch
      });
    };
  };
}

/*
* This is a dummy function to check if the function name has been altered by minification.
* If the function has been minified and NODE_ENV !== 'production', warn the user.
*/
function isCrushed() {}

if (process.env.NODE_ENV !== 'production' && typeof isCrushed.name === 'string' && isCrushed.name !== 'isCrushed') {
  warning('You are currently using minified code outside of NODE_ENV === \'production\'. ' + 'This means that you are running a slower development build of Redux. ' + 'You can use loose-envify (https://github.com/zertosh/loose-envify) for browserify ' + 'or DefinePlugin for webpack (http://stackoverflow.com/questions/30030031) ' + 'to ensure you have the correct code for your production build.');
}

var _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _typeof$1 = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var sym = function sym(id) {
  return '@@redux-saga/' + id;
};

var TASK = /*#__PURE__*/sym('TASK');
var HELPER = /*#__PURE__*/sym('HELPER');
var MATCH = /*#__PURE__*/sym('MATCH');
var CANCEL$1 = /*#__PURE__*/sym('CANCEL_PROMISE');
var SAGA_ACTION = /*#__PURE__*/sym('SAGA_ACTION');
var SELF_CANCELLATION = /*#__PURE__*/sym('SELF_CANCELLATION');
var konst = function konst(v) {
  return function () {
    return v;
  };
};
var kTrue = /*#__PURE__*/konst(true);
var noop = function noop() {};
var ident = function ident(v) {
  return v;
};

function check(value, predicate, error) {
  if (!predicate(value)) {
    log('error', 'uncaught at check', error);
    throw new Error(error);
  }
}

var hasOwnProperty = Object.prototype.hasOwnProperty;
function hasOwn(object, property) {
  return is.notUndef(object) && hasOwnProperty.call(object, property);
}

var is = {
  undef: function undef(v) {
    return v === null || v === undefined;
  },
  notUndef: function notUndef(v) {
    return v !== null && v !== undefined;
  },
  func: function func(f) {
    return typeof f === 'function';
  },
  number: function number(n) {
    return typeof n === 'number';
  },
  string: function string(s) {
    return typeof s === 'string';
  },
  array: Array.isArray,
  object: function object(obj) {
    return obj && !is.array(obj) && (typeof obj === 'undefined' ? 'undefined' : _typeof$1(obj)) === 'object';
  },
  promise: function promise(p) {
    return p && is.func(p.then);
  },
  iterator: function iterator(it) {
    return it && is.func(it.next) && is.func(it.throw);
  },
  iterable: function iterable(it) {
    return it && is.func(Symbol) ? is.func(it[Symbol.iterator]) : is.array(it);
  },
  task: function task(t) {
    return t && t[TASK];
  },
  observable: function observable(ob) {
    return ob && is.func(ob.subscribe);
  },
  buffer: function buffer(buf) {
    return buf && is.func(buf.isEmpty) && is.func(buf.take) && is.func(buf.put);
  },
  pattern: function pattern(pat) {
    return pat && (is.string(pat) || (typeof pat === 'undefined' ? 'undefined' : _typeof$1(pat)) === 'symbol' || is.func(pat) || is.array(pat));
  },
  channel: function channel(ch) {
    return ch && is.func(ch.take) && is.func(ch.close);
  },
  helper: function helper(it) {
    return it && it[HELPER];
  },
  stringableFunc: function stringableFunc(f) {
    return is.func(f) && hasOwn(f, 'toString');
  }
};

var object = {
  assign: function assign(target, source) {
    for (var i in source) {
      if (hasOwn(source, i)) {
        target[i] = source[i];
      }
    }
  }
};

function remove(array, item) {
  var index = array.indexOf(item);
  if (index >= 0) {
    array.splice(index, 1);
  }
}

var array = {
  from: function from(obj) {
    var arr = Array(obj.length);
    for (var i in obj) {
      if (hasOwn(obj, i)) {
        arr[i] = obj[i];
      }
    }
    return arr;
  }
};

function deferred() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  var def = _extends$2({}, props);
  var promise = new Promise(function (resolve, reject) {
    def.resolve = resolve;
    def.reject = reject;
  });
  def.promise = promise;
  return def;
}

function autoInc() {
  var seed = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;

  return function () {
    return ++seed;
  };
}

var uid = /*#__PURE__*/autoInc();

var kThrow = function kThrow(err) {
  throw err;
};
var kReturn = function kReturn(value) {
  return { value: value, done: true };
};
function makeIterator(next) {
  var thro = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : kThrow;
  var name = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
  var isHelper = arguments[3];

  var iterator = { name: name, next: next, throw: thro, return: kReturn };

  if (isHelper) {
    iterator[HELPER] = true;
  }
  if (typeof Symbol !== 'undefined') {
    iterator[Symbol.iterator] = function () {
      return iterator;
    };
  }
  return iterator;
}

/**
  Print error in a useful way whether in a browser environment
  (with expandable error stack traces), or in a node.js environment
  (text-only log output)
 **/
function log(level, message) {
  var error = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';

  /*eslint-disable no-console*/
  if (typeof window === 'undefined') {
    console.log('redux-saga ' + level + ': ' + message + '\n' + (error && error.stack || error));
  } else {
    console[level](message, error);
  }
}

function deprecate(fn, deprecationWarning) {
  return function () {
    if (process.env.NODE_ENV === 'development') log('warn', deprecationWarning);
    return fn.apply(undefined, arguments);
  };
}

var updateIncentive = function updateIncentive(deprecated, preferred) {
  return deprecated + ' has been deprecated in favor of ' + preferred + ', please update your code';
};

var internalErr = function internalErr(err) {
  return new Error('\n  redux-saga: Error checking hooks detected an inconsistent state. This is likely a bug\n  in redux-saga code and not yours. Thanks for reporting this in the project\'s github repo.\n  Error: ' + err + '\n');
};

var createSetContextWarning = function createSetContextWarning(ctx, props) {
  return (ctx ? ctx + '.' : '') + 'setContext(props): argument ' + props + ' is not a plain object';
};

var wrapSagaDispatch = function wrapSagaDispatch(dispatch) {
  return function (action) {
    return dispatch(Object.defineProperty(action, SAGA_ACTION, { value: true }));
  };
};

var BUFFER_OVERFLOW = "Channel's Buffer overflow!";

var ON_OVERFLOW_THROW = 1;
var ON_OVERFLOW_DROP = 2;
var ON_OVERFLOW_SLIDE = 3;
var ON_OVERFLOW_EXPAND = 4;

var zeroBuffer = { isEmpty: kTrue, put: noop, take: noop };

function ringBuffer() {
  var limit = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 10;
  var overflowAction = arguments[1];

  var arr = new Array(limit);
  var length = 0;
  var pushIndex = 0;
  var popIndex = 0;

  var push = function push(it) {
    arr[pushIndex] = it;
    pushIndex = (pushIndex + 1) % limit;
    length++;
  };

  var take = function take() {
    if (length != 0) {
      var it = arr[popIndex];
      arr[popIndex] = null;
      length--;
      popIndex = (popIndex + 1) % limit;
      return it;
    }
  };

  var flush = function flush() {
    var items = [];
    while (length) {
      items.push(take());
    }
    return items;
  };

  return {
    isEmpty: function isEmpty() {
      return length == 0;
    },
    put: function put(it) {
      if (length < limit) {
        push(it);
      } else {
        var doubledLimit = void 0;
        switch (overflowAction) {
          case ON_OVERFLOW_THROW:
            throw new Error(BUFFER_OVERFLOW);
          case ON_OVERFLOW_SLIDE:
            arr[pushIndex] = it;
            pushIndex = (pushIndex + 1) % limit;
            popIndex = pushIndex;
            break;
          case ON_OVERFLOW_EXPAND:
            doubledLimit = 2 * limit;

            arr = flush();

            length = arr.length;
            pushIndex = arr.length;
            popIndex = 0;

            arr.length = doubledLimit;
            limit = doubledLimit;

            push(it);
            break;
          // DROP
        }
      }
    },
    take: take,
    flush: flush
  };
}

var buffers = {
  none: function none() {
    return zeroBuffer;
  },
  fixed: function fixed(limit) {
    return ringBuffer(limit, ON_OVERFLOW_THROW);
  },
  dropping: function dropping(limit) {
    return ringBuffer(limit, ON_OVERFLOW_DROP);
  },
  sliding: function sliding(limit) {
    return ringBuffer(limit, ON_OVERFLOW_SLIDE);
  },
  expanding: function expanding(initialSize) {
    return ringBuffer(initialSize, ON_OVERFLOW_EXPAND);
  }
};

var queue = [];
/**
  Variable to hold a counting semaphore
  - Incrementing adds a lock and puts the scheduler in a `suspended` state (if it's not
    already suspended)
  - Decrementing releases a lock. Zero locks puts the scheduler in a `released` state. This
    triggers flushing the queued tasks.
**/
var semaphore = 0;

/**
  Executes a task 'atomically'. Tasks scheduled during this execution will be queued
  and flushed after this task has finished (assuming the scheduler endup in a released
  state).
**/
function exec(task) {
  try {
    suspend();
    task();
  } finally {
    release();
  }
}

/**
  Executes or queues a task depending on the state of the scheduler (`suspended` or `released`)
**/
function asap(task) {
  queue.push(task);

  if (!semaphore) {
    suspend();
    flush();
  }
}

/**
  Puts the scheduler in a `suspended` state. Scheduled tasks will be queued until the
  scheduler is released.
**/
function suspend() {
  semaphore++;
}

/**
  Puts the scheduler in a `released` state.
**/
function release() {
  semaphore--;
}

/**
  Releases the current lock. Executes all queued tasks if the scheduler is in the released state.
**/
function flush() {
  release();

  var task = void 0;
  while (!semaphore && (task = queue.shift()) !== undefined) {
    exec(task);
  }
}

var _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var CHANNEL_END_TYPE = '@@redux-saga/CHANNEL_END';
var END = { type: CHANNEL_END_TYPE };
var isEnd = function isEnd(a) {
  return a && a.type === CHANNEL_END_TYPE;
};

function emitter() {
  var subscribers = [];

  function subscribe(sub) {
    subscribers.push(sub);
    return function () {
      return remove(subscribers, sub);
    };
  }

  function emit(item) {
    var arr = subscribers.slice();
    for (var i = 0, len = arr.length; i < len; i++) {
      arr[i](item);
    }
  }

  return {
    subscribe: subscribe,
    emit: emit
  };
}

var INVALID_BUFFER = 'invalid buffer passed to channel factory function';
var UNDEFINED_INPUT_ERROR = 'Saga was provided with an undefined action';

if (process.env.NODE_ENV !== 'production') {
  UNDEFINED_INPUT_ERROR += '\nHints:\n    - check that your Action Creator returns a non-undefined value\n    - if the Saga was started using runSaga, check that your subscribe source provides the action to its listeners\n  ';
}

function channel() {
  var buffer = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : buffers.fixed();

  var closed = false;
  var takers = [];

  check(buffer, is.buffer, INVALID_BUFFER);

  function checkForbiddenStates() {
    if (closed && takers.length) {
      throw internalErr('Cannot have a closed channel with pending takers');
    }
    if (takers.length && !buffer.isEmpty()) {
      throw internalErr('Cannot have pending takers with non empty buffer');
    }
  }

  function put(input) {
    checkForbiddenStates();
    check(input, is.notUndef, UNDEFINED_INPUT_ERROR);
    if (closed) {
      return;
    }
    if (!takers.length) {
      return buffer.put(input);
    }
    for (var i = 0; i < takers.length; i++) {
      var cb = takers[i];
      if (!cb[MATCH] || cb[MATCH](input)) {
        takers.splice(i, 1);
        return cb(input);
      }
    }
  }

  function take(cb) {
    checkForbiddenStates();
    check(cb, is.func, "channel.take's callback must be a function");

    if (closed && buffer.isEmpty()) {
      cb(END);
    } else if (!buffer.isEmpty()) {
      cb(buffer.take());
    } else {
      takers.push(cb);
      cb.cancel = function () {
        return remove(takers, cb);
      };
    }
  }

  function flush(cb) {
    checkForbiddenStates(); // TODO: check if some new state should be forbidden now
    check(cb, is.func, "channel.flush' callback must be a function");
    if (closed && buffer.isEmpty()) {
      cb(END);
      return;
    }
    cb(buffer.flush());
  }

  function close() {
    checkForbiddenStates();
    if (!closed) {
      closed = true;
      if (takers.length) {
        var arr = takers;
        takers = [];
        for (var i = 0, len = arr.length; i < len; i++) {
          arr[i](END);
        }
      }
    }
  }

  return {
    take: take,
    put: put,
    flush: flush,
    close: close,
    get __takers__() {
      return takers;
    },
    get __closed__() {
      return closed;
    }
  };
}

function eventChannel(subscribe) {
  var buffer = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : buffers.none();
  var matcher = arguments[2];

  /**
    should be if(typeof matcher !== undefined) instead?
    see PR #273 for a background discussion
  **/
  if (arguments.length > 2) {
    check(matcher, is.func, 'Invalid match function passed to eventChannel');
  }

  var chan = channel(buffer);
  var close = function close() {
    if (!chan.__closed__) {
      if (unsubscribe) {
        unsubscribe();
      }
      chan.close();
    }
  };
  var unsubscribe = subscribe(function (input) {
    if (isEnd(input)) {
      close();
      return;
    }
    if (matcher && !matcher(input)) {
      return;
    }
    chan.put(input);
  });
  if (chan.__closed__) {
    unsubscribe();
  }

  if (!is.func(unsubscribe)) {
    throw new Error('in eventChannel: subscribe should return a function to unsubscribe');
  }

  return {
    take: chan.take,
    flush: chan.flush,
    close: close
  };
}

function stdChannel(subscribe) {
  var chan = eventChannel(function (cb) {
    return subscribe(function (input) {
      if (input[SAGA_ACTION]) {
        cb(input);
        return;
      }
      asap(function () {
        return cb(input);
      });
    });
  });

  return _extends$1({}, chan, {
    take: function take(cb, matcher) {
      if (arguments.length > 1) {
        check(matcher, is.func, "channel.take's matcher argument must be a function");
        cb[MATCH] = matcher;
      }
      chan.take(cb);
    }
  });
}

var IO = /*#__PURE__*/sym('IO');
var TAKE = 'TAKE';
var PUT = 'PUT';
var ALL = 'ALL';
var RACE = 'RACE';
var CALL = 'CALL';
var CPS = 'CPS';
var FORK = 'FORK';
var JOIN = 'JOIN';
var CANCEL = 'CANCEL';
var SELECT = 'SELECT';
var ACTION_CHANNEL = 'ACTION_CHANNEL';
var CANCELLED = 'CANCELLED';
var FLUSH = 'FLUSH';
var GET_CONTEXT = 'GET_CONTEXT';
var SET_CONTEXT = 'SET_CONTEXT';

var effect = function effect(type, payload) {
  var _ref;

  return _ref = {}, _ref[IO] = true, _ref[type] = payload, _ref;
};

function take() {
  var patternOrChannel = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '*';

  if (arguments.length) {
    check(arguments[0], is.notUndef, 'take(patternOrChannel): patternOrChannel is undefined');
  }
  if (is.pattern(patternOrChannel)) {
    return effect(TAKE, { pattern: patternOrChannel });
  }
  if (is.channel(patternOrChannel)) {
    return effect(TAKE, { channel: patternOrChannel });
  }
  throw new Error('take(patternOrChannel): argument ' + String(patternOrChannel) + ' is not valid channel or a valid pattern');
}

take.maybe = function () {
  var eff = take.apply(undefined, arguments);
  eff[TAKE].maybe = true;
  return eff;
};

function put(channel, action) {
  if (arguments.length > 1) {
    check(channel, is.notUndef, 'put(channel, action): argument channel is undefined');
    check(channel, is.channel, 'put(channel, action): argument ' + channel + ' is not a valid channel');
    check(action, is.notUndef, 'put(channel, action): argument action is undefined');
  } else {
    check(channel, is.notUndef, 'put(action): argument action is undefined');
    action = channel;
    channel = null;
  }
  return effect(PUT, { channel: channel, action: action });
}

put.resolve = function () {
  var eff = put.apply(undefined, arguments);
  eff[PUT].resolve = true;
  return eff;
};

put.sync = /*#__PURE__*/deprecate(put.resolve, /*#__PURE__*/updateIncentive('put.sync', 'put.resolve'));

function getFnCallDesc(meth, fn, args) {
  check(fn, is.notUndef, meth + ': argument fn is undefined');

  var context = null;
  if (is.array(fn)) {
    var _fn = fn;
    context = _fn[0];
    fn = _fn[1];
  } else if (fn.fn) {
    var _fn2 = fn;
    context = _fn2.context;
    fn = _fn2.fn;
  }
  if (context && is.string(fn) && is.func(context[fn])) {
    fn = context[fn];
  }
  check(fn, is.func, meth + ': argument ' + fn + ' is not a function');

  return { context: context, fn: fn, args: args };
}

function call(fn) {
  for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }

  return effect(CALL, getFnCallDesc('call', fn, args));
}

function fork(fn) {
  for (var _len3 = arguments.length, args = Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
    args[_key3 - 1] = arguments[_key3];
  }

  return effect(FORK, getFnCallDesc('fork', fn, args));
}

var createAsEffectType = function createAsEffectType(type) {
  return function (effect) {
    return effect && effect[IO] && effect[type];
  };
};

var asEffect = {
  take: /*#__PURE__*/createAsEffectType(TAKE),
  put: /*#__PURE__*/createAsEffectType(PUT),
  all: /*#__PURE__*/createAsEffectType(ALL),
  race: /*#__PURE__*/createAsEffectType(RACE),
  call: /*#__PURE__*/createAsEffectType(CALL),
  cps: /*#__PURE__*/createAsEffectType(CPS),
  fork: /*#__PURE__*/createAsEffectType(FORK),
  join: /*#__PURE__*/createAsEffectType(JOIN),
  cancel: /*#__PURE__*/createAsEffectType(CANCEL),
  select: /*#__PURE__*/createAsEffectType(SELECT),
  actionChannel: /*#__PURE__*/createAsEffectType(ACTION_CHANNEL),
  cancelled: /*#__PURE__*/createAsEffectType(CANCELLED),
  flush: /*#__PURE__*/createAsEffectType(FLUSH),
  getContext: /*#__PURE__*/createAsEffectType(GET_CONTEXT),
  setContext: /*#__PURE__*/createAsEffectType(SET_CONTEXT)
};

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

function _defineEnumerableProperties(obj, descs) { for (var key in descs) { var desc = descs[key]; desc.configurable = desc.enumerable = true; if ("value" in desc) desc.writable = true; Object.defineProperty(obj, key, desc); } return obj; }

var NOT_ITERATOR_ERROR = 'proc first argument (Saga function result) must be an iterator';

var CHANNEL_END = {
  toString: function toString() {
    return '@@redux-saga/CHANNEL_END';
  }
};
var TASK_CANCEL = {
  toString: function toString() {
    return '@@redux-saga/TASK_CANCEL';
  }
};

var matchers = {
  wildcard: function wildcard() {
    return kTrue;
  },
  default: function _default(pattern) {
    return (typeof pattern === 'undefined' ? 'undefined' : _typeof(pattern)) === 'symbol' ? function (input) {
      return input.type === pattern;
    } : function (input) {
      return input.type === String(pattern);
    };
  },
  array: function array(patterns) {
    return function (input) {
      return patterns.some(function (p) {
        return matcher(p)(input);
      });
    };
  },
  predicate: function predicate(_predicate) {
    return function (input) {
      return _predicate(input);
    };
  }
};

function matcher(pattern) {
  // prettier-ignore
  return (pattern === '*' ? matchers.wildcard : is.array(pattern) ? matchers.array : is.stringableFunc(pattern) ? matchers.default : is.func(pattern) ? matchers.predicate : matchers.default)(pattern);
}

/**
  Used to track a parent task and its forks
  In the new fork model, forked tasks are attached by default to their parent
  We model this using the concept of Parent task && main Task
  main task is the main flow of the current Generator, the parent tasks is the
  aggregation of the main tasks + all its forked tasks.
  Thus the whole model represents an execution tree with multiple branches (vs the
  linear execution tree in sequential (non parallel) programming)

  A parent tasks has the following semantics
  - It completes if all its forks either complete or all cancelled
  - If it's cancelled, all forks are cancelled as well
  - It aborts if any uncaught error bubbles up from forks
  - If it completes, the return value is the one returned by the main task
**/
function forkQueue(name, mainTask, cb) {
  var tasks = [],
      result = void 0,
      completed = false;
  addTask(mainTask);

  function abort(err) {
    cancelAll();
    cb(err, true);
  }

  function addTask(task) {
    tasks.push(task);
    task.cont = function (res, isErr) {
      if (completed) {
        return;
      }

      remove(tasks, task);
      task.cont = noop;
      if (isErr) {
        abort(res);
      } else {
        if (task === mainTask) {
          result = res;
        }
        if (!tasks.length) {
          completed = true;
          cb(result);
        }
      }
    };
    // task.cont.cancel = task.cancel
  }

  function cancelAll() {
    if (completed) {
      return;
    }
    completed = true;
    tasks.forEach(function (t) {
      t.cont = noop;
      t.cancel();
    });
    tasks = [];
  }

  return {
    addTask: addTask,
    cancelAll: cancelAll,
    abort: abort,
    getTasks: function getTasks() {
      return tasks;
    },
    taskNames: function taskNames() {
      return tasks.map(function (t) {
        return t.name;
      });
    }
  };
}

function createTaskIterator(_ref) {
  var context = _ref.context,
      fn = _ref.fn,
      args = _ref.args;

  if (is.iterator(fn)) {
    return fn;
  }

  // catch synchronous failures; see #152 and #441
  var result = void 0,
      error = void 0;
  try {
    result = fn.apply(context, args);
  } catch (err) {
    error = err;
  }

  // i.e. a generator function returns an iterator
  if (is.iterator(result)) {
    return result;
  }

  // do not bubble up synchronous failures for detached forks
  // instead create a failed task. See #152 and #441
  return error ? makeIterator(function () {
    throw error;
  }) : makeIterator(function () {
    var pc = void 0;
    var eff = { done: false, value: result };
    var ret = function ret(value) {
      return { done: true, value: value };
    };
    return function (arg) {
      if (!pc) {
        pc = true;
        return eff;
      } else {
        return ret(arg);
      }
    };
  }());
}

var wrapHelper = function wrapHelper(helper) {
  return { fn: helper };
};

function proc(iterator) {
  var subscribe = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function () {
    return noop;
  };
  var dispatch = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : noop;
  var getState = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : noop;
  var parentContext = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
  var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
  var parentEffectId = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : 0;
  var name = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : 'anonymous';
  var cont = arguments[8];

  check(iterator, is.iterator, NOT_ITERATOR_ERROR);

  var effectsString = '[...effects]';
  var runParallelEffect = deprecate(runAllEffect, updateIncentive(effectsString, 'all(' + effectsString + ')'));

  var sagaMonitor = options.sagaMonitor,
      logger = options.logger,
      onError = options.onError;

  var log$1 = logger || log;
  var logError = function logError(err) {
    var message = err.sagaStack;

    if (!message && err.stack) {
      message = err.stack.split('\n')[0].indexOf(err.message) !== -1 ? err.stack : 'Error: ' + err.message + '\n' + err.stack;
    }

    log$1('error', 'uncaught at ' + name, message || err.message || err);
  };
  var stdChannel$1 = stdChannel(subscribe);
  var taskContext = Object.create(parentContext);
  /**
    Tracks the current effect cancellation
    Each time the generator progresses. calling runEffect will set a new value
    on it. It allows propagating cancellation to child effects
  **/
  next.cancel = noop;

  /**
    Creates a new task descriptor for this generator, We'll also create a main task
    to track the main flow (besides other forked tasks)
  **/
  var task = newTask(parentEffectId, name, iterator, cont);
  var mainTask = { name: name, cancel: cancelMain, isRunning: true };
  var taskQueue = forkQueue(name, mainTask, end);

  /**
    cancellation of the main task. We'll simply resume the Generator with a Cancel
  **/
  function cancelMain() {
    if (mainTask.isRunning && !mainTask.isCancelled) {
      mainTask.isCancelled = true;
      next(TASK_CANCEL);
    }
  }

  /**
    This may be called by a parent generator to trigger/propagate cancellation
    cancel all pending tasks (including the main task), then end the current task.
     Cancellation propagates down to the whole execution tree holded by this Parent task
    It's also propagated to all joiners of this task and their execution tree/joiners
     Cancellation is noop for terminated/Cancelled tasks tasks
  **/
  function cancel() {
    /**
      We need to check both Running and Cancelled status
      Tasks can be Cancelled but still Running
    **/
    if (iterator._isRunning && !iterator._isCancelled) {
      iterator._isCancelled = true;
      taskQueue.cancelAll();
      /**
        Ending with a Never result will propagate the Cancellation to all joiners
      **/
      end(TASK_CANCEL);
    }
  }
  /**
    attaches cancellation logic to this task's continuation
    this will permit cancellation to propagate down the call chain
  **/
  cont && (cont.cancel = cancel);

  // tracks the running status
  iterator._isRunning = true;

  // kicks up the generator
  next();

  // then return the task descriptor to the caller
  return task;

  /**
    This is the generator driver
    It's a recursive async/continuation function which calls itself
    until the generator terminates or throws
  **/
  function next(arg, isErr) {
    // Preventive measure. If we end up here, then there is really something wrong
    if (!mainTask.isRunning) {
      throw new Error('Trying to resume an already finished generator');
    }

    try {
      var result = void 0;
      if (isErr) {
        result = iterator.throw(arg);
      } else if (arg === TASK_CANCEL) {
        /**
          getting TASK_CANCEL automatically cancels the main task
          We can get this value here
           - By cancelling the parent task manually
          - By joining a Cancelled task
        **/
        mainTask.isCancelled = true;
        /**
          Cancels the current effect; this will propagate the cancellation down to any called tasks
        **/
        next.cancel();
        /**
          If this Generator has a `return` method then invokes it
          This will jump to the finally block
        **/
        result = is.func(iterator.return) ? iterator.return(TASK_CANCEL) : { done: true, value: TASK_CANCEL };
      } else if (arg === CHANNEL_END) {
        // We get CHANNEL_END by taking from a channel that ended using `take` (and not `takem` used to trap End of channels)
        result = is.func(iterator.return) ? iterator.return() : { done: true };
      } else {
        result = iterator.next(arg);
      }

      if (!result.done) {
        runEffect(result.value, parentEffectId, '', next);
      } else {
        /**
          This Generator has ended, terminate the main task and notify the fork queue
        **/
        mainTask.isMainRunning = false;
        mainTask.cont && mainTask.cont(result.value);
      }
    } catch (error) {
      if (mainTask.isCancelled) {
        logError(error);
      }
      mainTask.isMainRunning = false;
      mainTask.cont(error, true);
    }
  }

  function end(result, isErr) {
    iterator._isRunning = false;
    stdChannel$1.close();
    if (!isErr) {
      iterator._result = result;
      iterator._deferredEnd && iterator._deferredEnd.resolve(result);
    } else {
      if (result instanceof Error) {
        Object.defineProperty(result, 'sagaStack', {
          value: 'at ' + name + ' \n ' + (result.sagaStack || result.stack),
          configurable: true
        });
      }
      if (!task.cont) {
        if (result instanceof Error && onError) {
          onError(result);
        } else {
          logError(result);
        }
      }
      iterator._error = result;
      iterator._isAborted = true;
      iterator._deferredEnd && iterator._deferredEnd.reject(result);
    }
    task.cont && task.cont(result, isErr);
    task.joiners.forEach(function (j) {
      return j.cb(result, isErr);
    });
    task.joiners = null;
  }

  function runEffect(effect, parentEffectId) {
    var label = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
    var cb = arguments[3];

    var effectId = uid();
    sagaMonitor && sagaMonitor.effectTriggered({ effectId: effectId, parentEffectId: parentEffectId, label: label, effect: effect });

    /**
      completion callback and cancel callback are mutually exclusive
      We can't cancel an already completed effect
      And We can't complete an already cancelled effectId
    **/
    var effectSettled = void 0;

    // Completion callback passed to the appropriate effect runner
    function currCb(res, isErr) {
      if (effectSettled) {
        return;
      }

      effectSettled = true;
      cb.cancel = noop; // defensive measure
      if (sagaMonitor) {
        isErr ? sagaMonitor.effectRejected(effectId, res) : sagaMonitor.effectResolved(effectId, res);
      }
      cb(res, isErr);
    }
    // tracks down the current cancel
    currCb.cancel = noop;

    // setup cancellation logic on the parent cb
    cb.cancel = function () {
      // prevents cancelling an already completed effect
      if (effectSettled) {
        return;
      }

      effectSettled = true;
      /**
        propagates cancel downward
        catch uncaught cancellations errors; since we can no longer call the completion
        callback, log errors raised during cancellations into the console
      **/
      try {
        currCb.cancel();
      } catch (err) {
        logError(err);
      }
      currCb.cancel = noop; // defensive measure

      sagaMonitor && sagaMonitor.effectCancelled(effectId);
    };

    /**
      each effect runner must attach its own logic of cancellation to the provided callback
      it allows this generator to propagate cancellation downward.
       ATTENTION! effect runners must setup the cancel logic by setting cb.cancel = [cancelMethod]
      And the setup must occur before calling the callback
       This is a sort of inversion of control: called async functions are responsible
      for completing the flow by calling the provided continuation; while caller functions
      are responsible for aborting the current flow by calling the attached cancel function
       Library users can attach their own cancellation logic to promises by defining a
      promise[CANCEL] method in their returned promises
      ATTENTION! calling cancel must have no effect on an already completed or cancelled effect
    **/
    var data = void 0;
    // prettier-ignore
    return (
      // Non declarative effect
      is.promise(effect) ? resolvePromise(effect, currCb) : is.helper(effect) ? runForkEffect(wrapHelper(effect), effectId, currCb) : is.iterator(effect) ? resolveIterator(effect, effectId, name, currCb)

      // declarative effects
      : is.array(effect) ? runParallelEffect(effect, effectId, currCb) : (data = asEffect.take(effect)) ? runTakeEffect(data, currCb) : (data = asEffect.put(effect)) ? runPutEffect(data, currCb) : (data = asEffect.all(effect)) ? runAllEffect(data, effectId, currCb) : (data = asEffect.race(effect)) ? runRaceEffect(data, effectId, currCb) : (data = asEffect.call(effect)) ? runCallEffect(data, effectId, currCb) : (data = asEffect.cps(effect)) ? runCPSEffect(data, currCb) : (data = asEffect.fork(effect)) ? runForkEffect(data, effectId, currCb) : (data = asEffect.join(effect)) ? runJoinEffect(data, currCb) : (data = asEffect.cancel(effect)) ? runCancelEffect(data, currCb) : (data = asEffect.select(effect)) ? runSelectEffect(data, currCb) : (data = asEffect.actionChannel(effect)) ? runChannelEffect(data, currCb) : (data = asEffect.flush(effect)) ? runFlushEffect(data, currCb) : (data = asEffect.cancelled(effect)) ? runCancelledEffect(data, currCb) : (data = asEffect.getContext(effect)) ? runGetContextEffect(data, currCb) : (data = asEffect.setContext(effect)) ? runSetContextEffect(data, currCb) : /* anything else returned as is */currCb(effect)
    );
  }

  function resolvePromise(promise, cb) {
    var cancelPromise = promise[CANCEL$1];
    if (is.func(cancelPromise)) {
      cb.cancel = cancelPromise;
    } else if (is.func(promise.abort)) {
      cb.cancel = function () {
        return promise.abort();
      };
      // TODO: add support for the fetch API, whenever they get around to
      // adding cancel support
    }
    promise.then(cb, function (error) {
      return cb(error, true);
    });
  }

  function resolveIterator(iterator, effectId, name, cb) {
    proc(iterator, subscribe, dispatch, getState, taskContext, options, effectId, name, cb);
  }

  function runTakeEffect(_ref2, cb) {
    var channel = _ref2.channel,
        pattern = _ref2.pattern,
        maybe = _ref2.maybe;

    channel = channel || stdChannel$1;
    var takeCb = function takeCb(inp) {
      return inp instanceof Error ? cb(inp, true) : isEnd(inp) && !maybe ? cb(CHANNEL_END) : cb(inp);
    };
    try {
      channel.take(takeCb, matcher(pattern));
    } catch (err) {
      return cb(err, true);
    }
    cb.cancel = takeCb.cancel;
  }

  function runPutEffect(_ref3, cb) {
    var channel = _ref3.channel,
        action = _ref3.action,
        resolve = _ref3.resolve;

    /**
      Schedule the put in case another saga is holding a lock.
      The put will be executed atomically. ie nested puts will execute after
      this put has terminated.
    **/
    asap(function () {
      var result = void 0;
      try {
        result = (channel ? channel.put : dispatch)(action);
      } catch (error) {
        // If we have a channel or `put.resolve` was used then bubble up the error.
        if (channel || resolve) return cb(error, true);
        logError(error);
      }

      if (resolve && is.promise(result)) {
        resolvePromise(result, cb);
      } else {
        return cb(result);
      }
    });
    // Put effects are non cancellables
  }

  function runCallEffect(_ref4, effectId, cb) {
    var context = _ref4.context,
        fn = _ref4.fn,
        args = _ref4.args;

    var result = void 0;
    // catch synchronous failures; see #152
    try {
      result = fn.apply(context, args);
    } catch (error) {
      return cb(error, true);
    }
    return is.promise(result) ? resolvePromise(result, cb) : is.iterator(result) ? resolveIterator(result, effectId, fn.name, cb) : cb(result);
  }

  function runCPSEffect(_ref5, cb) {
    var context = _ref5.context,
        fn = _ref5.fn,
        args = _ref5.args;

    // CPS (ie node style functions) can define their own cancellation logic
    // by setting cancel field on the cb

    // catch synchronous failures; see #152
    try {
      var cpsCb = function cpsCb(err, res) {
        return is.undef(err) ? cb(res) : cb(err, true);
      };
      fn.apply(context, args.concat(cpsCb));
      if (cpsCb.cancel) {
        cb.cancel = function () {
          return cpsCb.cancel();
        };
      }
    } catch (error) {
      return cb(error, true);
    }
  }

  function runForkEffect(_ref6, effectId, cb) {
    var context = _ref6.context,
        fn = _ref6.fn,
        args = _ref6.args,
        detached = _ref6.detached;

    var taskIterator = createTaskIterator({ context: context, fn: fn, args: args });

    try {
      suspend();
      var _task = proc(taskIterator, subscribe, dispatch, getState, taskContext, options, effectId, fn.name, detached ? null : noop);

      if (detached) {
        cb(_task);
      } else {
        if (taskIterator._isRunning) {
          taskQueue.addTask(_task);
          cb(_task);
        } else if (taskIterator._error) {
          taskQueue.abort(taskIterator._error);
        } else {
          cb(_task);
        }
      }
    } finally {
      flush();
    }
    // Fork effects are non cancellables
  }

  function runJoinEffect(t, cb) {
    if (t.isRunning()) {
      var joiner = { task: task, cb: cb };
      cb.cancel = function () {
        return remove(t.joiners, joiner);
      };
      t.joiners.push(joiner);
    } else {
      t.isAborted() ? cb(t.error(), true) : cb(t.result());
    }
  }

  function runCancelEffect(taskToCancel, cb) {
    if (taskToCancel === SELF_CANCELLATION) {
      taskToCancel = task;
    }
    if (taskToCancel.isRunning()) {
      taskToCancel.cancel();
    }
    cb();
    // cancel effects are non cancellables
  }

  function runAllEffect(effects, effectId, cb) {
    var keys = Object.keys(effects);

    if (!keys.length) {
      return cb(is.array(effects) ? [] : {});
    }

    var completedCount = 0;
    var completed = void 0;
    var results = {};
    var childCbs = {};

    function checkEffectEnd() {
      if (completedCount === keys.length) {
        completed = true;
        cb(is.array(effects) ? array.from(_extends({}, results, { length: keys.length })) : results);
      }
    }

    keys.forEach(function (key) {
      var chCbAtKey = function chCbAtKey(res, isErr) {
        if (completed) {
          return;
        }
        if (isErr || isEnd(res) || res === CHANNEL_END || res === TASK_CANCEL) {
          cb.cancel();
          cb(res, isErr);
        } else {
          results[key] = res;
          completedCount++;
          checkEffectEnd();
        }
      };
      chCbAtKey.cancel = noop;
      childCbs[key] = chCbAtKey;
    });

    cb.cancel = function () {
      if (!completed) {
        completed = true;
        keys.forEach(function (key) {
          return childCbs[key].cancel();
        });
      }
    };

    keys.forEach(function (key) {
      return runEffect(effects[key], effectId, key, childCbs[key]);
    });
  }

  function runRaceEffect(effects, effectId, cb) {
    var completed = void 0;
    var keys = Object.keys(effects);
    var childCbs = {};

    keys.forEach(function (key) {
      var chCbAtKey = function chCbAtKey(res, isErr) {
        if (completed) {
          return;
        }

        if (isErr) {
          // Race Auto cancellation
          cb.cancel();
          cb(res, true);
        } else if (!isEnd(res) && res !== CHANNEL_END && res !== TASK_CANCEL) {
          var _response;

          cb.cancel();
          completed = true;
          var response = (_response = {}, _response[key] = res, _response);
          cb(is.array(effects) ? [].slice.call(_extends({}, response, { length: keys.length })) : response);
        }
      };
      chCbAtKey.cancel = noop;
      childCbs[key] = chCbAtKey;
    });

    cb.cancel = function () {
      // prevents unnecessary cancellation
      if (!completed) {
        completed = true;
        keys.forEach(function (key) {
          return childCbs[key].cancel();
        });
      }
    };
    keys.forEach(function (key) {
      if (completed) {
        return;
      }
      runEffect(effects[key], effectId, key, childCbs[key]);
    });
  }

  function runSelectEffect(_ref7, cb) {
    var selector = _ref7.selector,
        args = _ref7.args;

    try {
      var state = selector.apply(undefined, [getState()].concat(args));
      cb(state);
    } catch (error) {
      cb(error, true);
    }
  }

  function runChannelEffect(_ref8, cb) {
    var pattern = _ref8.pattern,
        buffer = _ref8.buffer;

    var match = matcher(pattern);
    match.pattern = pattern;
    cb(eventChannel(subscribe, buffer || buffers.fixed(), match));
  }

  function runCancelledEffect(data, cb) {
    cb(!!mainTask.isCancelled);
  }

  function runFlushEffect(channel, cb) {
    channel.flush(cb);
  }

  function runGetContextEffect(prop, cb) {
    cb(taskContext[prop]);
  }

  function runSetContextEffect(props, cb) {
    object.assign(taskContext, props);
    cb();
  }

  function newTask(id, name, iterator, cont) {
    var _done, _ref9, _mutatorMap;

    iterator._deferredEnd = null;
    return _ref9 = {}, _ref9[TASK] = true, _ref9.id = id, _ref9.name = name, _done = 'done', _mutatorMap = {}, _mutatorMap[_done] = _mutatorMap[_done] || {}, _mutatorMap[_done].get = function () {
      if (iterator._deferredEnd) {
        return iterator._deferredEnd.promise;
      } else {
        var def = deferred();
        iterator._deferredEnd = def;
        if (!iterator._isRunning) {
          iterator._error ? def.reject(iterator._error) : def.resolve(iterator._result);
        }
        return def.promise;
      }
    }, _ref9.cont = cont, _ref9.joiners = [], _ref9.cancel = cancel, _ref9.isRunning = function isRunning() {
      return iterator._isRunning;
    }, _ref9.isCancelled = function isCancelled() {
      return iterator._isCancelled;
    }, _ref9.isAborted = function isAborted() {
      return iterator._isAborted;
    }, _ref9.result = function result() {
      return iterator._result;
    }, _ref9.error = function error() {
      return iterator._error;
    }, _ref9.setContext = function setContext(props) {
      check(props, is.object, createSetContextWarning('task', props));
      object.assign(taskContext, props);
    }, _defineEnumerableProperties(_ref9, _mutatorMap), _ref9;
  }
}

var RUN_SAGA_SIGNATURE = 'runSaga(storeInterface, saga, ...args)';
var NON_GENERATOR_ERR = RUN_SAGA_SIGNATURE + ': saga argument must be a Generator function!';

function runSaga(storeInterface, saga) {
  for (var _len = arguments.length, args = Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    args[_key - 2] = arguments[_key];
  }

  var iterator = void 0;

  if (is.iterator(storeInterface)) {
    if (process.env.NODE_ENV === 'development') {
      log('warn', 'runSaga(iterator, storeInterface) has been deprecated in favor of ' + RUN_SAGA_SIGNATURE);
    }
    iterator = storeInterface;
    storeInterface = saga;
  } else {
    check(saga, is.func, NON_GENERATOR_ERR);
    iterator = saga.apply(undefined, args);
    check(iterator, is.iterator, NON_GENERATOR_ERR);
  }

  var _storeInterface = storeInterface,
      subscribe = _storeInterface.subscribe,
      dispatch = _storeInterface.dispatch,
      getState = _storeInterface.getState,
      context = _storeInterface.context,
      sagaMonitor = _storeInterface.sagaMonitor,
      logger = _storeInterface.logger,
      onError = _storeInterface.onError;


  var effectId = uid();

  if (sagaMonitor) {
    // monitors are expected to have a certain interface, let's fill-in any missing ones
    sagaMonitor.effectTriggered = sagaMonitor.effectTriggered || noop;
    sagaMonitor.effectResolved = sagaMonitor.effectResolved || noop;
    sagaMonitor.effectRejected = sagaMonitor.effectRejected || noop;
    sagaMonitor.effectCancelled = sagaMonitor.effectCancelled || noop;
    sagaMonitor.actionDispatched = sagaMonitor.actionDispatched || noop;

    sagaMonitor.effectTriggered({ effectId: effectId, root: true, parentEffectId: 0, effect: { root: true, saga: saga, args: args } });
  }

  var task = proc(iterator, subscribe, wrapSagaDispatch(dispatch), getState, context, { sagaMonitor: sagaMonitor, logger: logger, onError: onError }, effectId, saga.name);

  if (sagaMonitor) {
    sagaMonitor.effectResolved(effectId, task);
  }

  return task;
}

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function sagaMiddlewareFactory() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  var _ref$context = _ref.context,
      context = _ref$context === undefined ? {} : _ref$context,
      options = _objectWithoutProperties(_ref, ['context']);

  var sagaMonitor = options.sagaMonitor,
      logger = options.logger,
      onError = options.onError;


  if (is.func(options)) {
    if (process.env.NODE_ENV === 'production') {
      throw new Error('Saga middleware no longer accept Generator functions. Use sagaMiddleware.run instead');
    } else {
      throw new Error('You passed a function to the Saga middleware. You are likely trying to start a        Saga by directly passing it to the middleware. This is no longer possible starting from 0.10.0.        To run a Saga, you must do it dynamically AFTER mounting the middleware into the store.\n        Example:\n          import createSagaMiddleware from \'redux-saga\'\n          ... other imports\n\n          const sagaMiddleware = createSagaMiddleware()\n          const store = createStore(reducer, applyMiddleware(sagaMiddleware))\n          sagaMiddleware.run(saga, ...args)\n      ');
    }
  }

  if (logger && !is.func(logger)) {
    throw new Error('`options.logger` passed to the Saga middleware is not a function!');
  }

  if (process.env.NODE_ENV === 'development' && options.onerror) {
    throw new Error('`options.onerror` was removed. Use `options.onError` instead.');
  }

  if (onError && !is.func(onError)) {
    throw new Error('`options.onError` passed to the Saga middleware is not a function!');
  }

  if (options.emitter && !is.func(options.emitter)) {
    throw new Error('`options.emitter` passed to the Saga middleware is not a function!');
  }

  function sagaMiddleware(_ref2) {
    var getState = _ref2.getState,
        dispatch = _ref2.dispatch;

    var sagaEmitter = emitter();
    sagaEmitter.emit = (options.emitter || ident)(sagaEmitter.emit);

    sagaMiddleware.run = runSaga.bind(null, {
      context: context,
      subscribe: sagaEmitter.subscribe,
      dispatch: dispatch,
      getState: getState,
      sagaMonitor: sagaMonitor,
      logger: logger,
      onError: onError
    });

    return function (next) {
      return function (action) {
        if (sagaMonitor && sagaMonitor.actionDispatched) {
          sagaMonitor.actionDispatched(action);
        }
        var result = next(action); // hit reducers
        sagaEmitter.emit(action);
        return result;
      };
    };
  }

  sagaMiddleware.run = function () {
    throw new Error('Before running a Saga, you must mount the Saga middleware on the Store using applyMiddleware');
  };

  sagaMiddleware.setContext = function (props) {
    check(props, is.object, createSetContextWarning('sagaMiddleware', props));
    object.assign(context, props);
  };

  return sagaMiddleware;
}

var done = { done: true, value: undefined };
var qEnd = {};

function safeName(patternOrChannel) {
  if (is.channel(patternOrChannel)) {
    return 'channel';
  } else if (Array.isArray(patternOrChannel)) {
    return String(patternOrChannel.map(function (entry) {
      return String(entry);
    }));
  } else {
    return String(patternOrChannel);
  }
}

function fsmIterator(fsm, q0) {
  var name = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'iterator';

  var updateState = void 0,
      qNext = q0;

  function next(arg, error) {
    if (qNext === qEnd) {
      return done;
    }

    if (error) {
      qNext = qEnd;
      throw error;
    } else {
      updateState && updateState(arg);

      var _fsm$qNext = fsm[qNext](),
          q = _fsm$qNext[0],
          output = _fsm$qNext[1],
          _updateState = _fsm$qNext[2];

      qNext = q;
      updateState = _updateState;
      return qNext === qEnd ? done : output;
    }
  }

  return makeIterator(next, function (error) {
    return next(null, error);
  }, name, true);
}

function takeEvery$1(patternOrChannel, worker) {
  for (var _len = arguments.length, args = Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    args[_key - 2] = arguments[_key];
  }

  var yTake = { done: false, value: take(patternOrChannel) };
  var yFork = function yFork(ac) {
    return { done: false, value: fork.apply(undefined, [worker].concat(args, [ac])) };
  };

  var action = void 0,
      setAction = function setAction(ac) {
    return action = ac;
  };

  return fsmIterator({
    q1: function q1() {
      return ['q2', yTake, setAction];
    },
    q2: function q2() {
      return action === END ? [qEnd] : ['q1', yFork(action)];
    }
  }, 'q1', 'takeEvery(' + safeName(patternOrChannel) + ', ' + worker.name + ')');
}

function takeEvery(patternOrChannel, worker) {
  for (var _len = arguments.length, args = Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    args[_key - 2] = arguments[_key];
  }

  return fork.apply(undefined, [takeEvery$1, patternOrChannel, worker].concat(args));
}

function getType(actionCreator) {
    if (actionCreator == null) {
        throw new Error('first argument is missing');
    }
    if (actionCreator.getType == null) {
        throw new Error('first argument is not an instance of "typesafe-actions"');
    }
    return actionCreator.getType();
}

function createAction(typeString, creatorFunction) {
    var actionCreator;
    if (creatorFunction != null) {
        if (typeof creatorFunction !== 'function') {
            throw new Error('second argument is not a function');
        }
        actionCreator = creatorFunction;
    }
    else {
        actionCreator = (function () { return ({ type: typeString }); });
    }
    if (typeString != null) {
        if (typeof typeString !== 'string') {
            throw new Error('first argument is not a type string');
        }
        actionCreator.getType = function () { return typeString; };
    }
    else {
        throw new Error('first argument is missing');
    }
    return actionCreator;
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

/** PURE_IMPORTS_START  PURE_IMPORTS_END */
function isFunction(x) {
    return typeof x === 'function';
}

/** PURE_IMPORTS_START  PURE_IMPORTS_END */
var _enable_super_gross_mode_that_will_cause_bad_things = false;
var config = {
    Promise: undefined,
    set useDeprecatedSynchronousErrorHandling(value) {
        if (value) {
            var error = /*@__PURE__*/ new Error();
            /*@__PURE__*/ console.warn('DEPRECATED! RxJS was set to use deprecated synchronous error handling behavior by code at: \n' + error.stack);
        }
        _enable_super_gross_mode_that_will_cause_bad_things = value;
    },
    get useDeprecatedSynchronousErrorHandling() {
        return _enable_super_gross_mode_that_will_cause_bad_things;
    },
};

/** PURE_IMPORTS_START  PURE_IMPORTS_END */
function hostReportError(err) {
    setTimeout(function () { throw err; }, 0);
}

/** PURE_IMPORTS_START _config,_util_hostReportError PURE_IMPORTS_END */
var empty = {
    closed: true,
    next: function (value) { },
    error: function (err) {
        if (config.useDeprecatedSynchronousErrorHandling) {
            throw err;
        }
        else {
            hostReportError(err);
        }
    },
    complete: function () { }
};

/** PURE_IMPORTS_START  PURE_IMPORTS_END */
var isArray = /*@__PURE__*/ (function () { return Array.isArray || (function (x) { return x && typeof x.length === 'number'; }); })();

/** PURE_IMPORTS_START  PURE_IMPORTS_END */
function isObject(x) {
    return x !== null && typeof x === 'object';
}

/** PURE_IMPORTS_START  PURE_IMPORTS_END */
var UnsubscriptionErrorImpl = /*@__PURE__*/ (function () {
    function UnsubscriptionErrorImpl(errors) {
        Error.call(this);
        this.message = errors ?
            errors.length + " errors occurred during unsubscription:\n" + errors.map(function (err, i) { return i + 1 + ") " + err.toString(); }).join('\n  ') : '';
        this.name = 'UnsubscriptionError';
        this.errors = errors;
        return this;
    }
    UnsubscriptionErrorImpl.prototype = /*@__PURE__*/ Object.create(Error.prototype);
    return UnsubscriptionErrorImpl;
})();
var UnsubscriptionError = UnsubscriptionErrorImpl;

/** PURE_IMPORTS_START _util_isArray,_util_isObject,_util_isFunction,_util_UnsubscriptionError PURE_IMPORTS_END */
var Subscription = /*@__PURE__*/ (function () {
    function Subscription(unsubscribe) {
        this.closed = false;
        this._parentOrParents = null;
        this._subscriptions = null;
        if (unsubscribe) {
            this._unsubscribe = unsubscribe;
        }
    }
    Subscription.prototype.unsubscribe = function () {
        var errors;
        if (this.closed) {
            return;
        }
        var _a = this, _parentOrParents = _a._parentOrParents, _unsubscribe = _a._unsubscribe, _subscriptions = _a._subscriptions;
        this.closed = true;
        this._parentOrParents = null;
        this._subscriptions = null;
        if (_parentOrParents instanceof Subscription) {
            _parentOrParents.remove(this);
        }
        else if (_parentOrParents !== null) {
            for (var index = 0; index < _parentOrParents.length; ++index) {
                var parent_1 = _parentOrParents[index];
                parent_1.remove(this);
            }
        }
        if (isFunction(_unsubscribe)) {
            try {
                _unsubscribe.call(this);
            }
            catch (e) {
                errors = e instanceof UnsubscriptionError ? flattenUnsubscriptionErrors(e.errors) : [e];
            }
        }
        if (isArray(_subscriptions)) {
            var index = -1;
            var len = _subscriptions.length;
            while (++index < len) {
                var sub = _subscriptions[index];
                if (isObject(sub)) {
                    try {
                        sub.unsubscribe();
                    }
                    catch (e) {
                        errors = errors || [];
                        if (e instanceof UnsubscriptionError) {
                            errors = errors.concat(flattenUnsubscriptionErrors(e.errors));
                        }
                        else {
                            errors.push(e);
                        }
                    }
                }
            }
        }
        if (errors) {
            throw new UnsubscriptionError(errors);
        }
    };
    Subscription.prototype.add = function (teardown) {
        var subscription = teardown;
        if (!teardown) {
            return Subscription.EMPTY;
        }
        switch (typeof teardown) {
            case 'function':
                subscription = new Subscription(teardown);
            case 'object':
                if (subscription === this || subscription.closed || typeof subscription.unsubscribe !== 'function') {
                    return subscription;
                }
                else if (this.closed) {
                    subscription.unsubscribe();
                    return subscription;
                }
                else if (!(subscription instanceof Subscription)) {
                    var tmp = subscription;
                    subscription = new Subscription();
                    subscription._subscriptions = [tmp];
                }
                break;
            default: {
                throw new Error('unrecognized teardown ' + teardown + ' added to Subscription.');
            }
        }
        var _parentOrParents = subscription._parentOrParents;
        if (_parentOrParents === null) {
            subscription._parentOrParents = this;
        }
        else if (_parentOrParents instanceof Subscription) {
            if (_parentOrParents === this) {
                return subscription;
            }
            subscription._parentOrParents = [_parentOrParents, this];
        }
        else if (_parentOrParents.indexOf(this) === -1) {
            _parentOrParents.push(this);
        }
        else {
            return subscription;
        }
        var subscriptions = this._subscriptions;
        if (subscriptions === null) {
            this._subscriptions = [subscription];
        }
        else {
            subscriptions.push(subscription);
        }
        return subscription;
    };
    Subscription.prototype.remove = function (subscription) {
        var subscriptions = this._subscriptions;
        if (subscriptions) {
            var subscriptionIndex = subscriptions.indexOf(subscription);
            if (subscriptionIndex !== -1) {
                subscriptions.splice(subscriptionIndex, 1);
            }
        }
    };
    Subscription.EMPTY = (function (empty) {
        empty.closed = true;
        return empty;
    }(new Subscription()));
    return Subscription;
}());
function flattenUnsubscriptionErrors(errors) {
    return errors.reduce(function (errs, err) { return errs.concat((err instanceof UnsubscriptionError) ? err.errors : err); }, []);
}

/** PURE_IMPORTS_START  PURE_IMPORTS_END */
var rxSubscriber = /*@__PURE__*/ (function () {
    return typeof Symbol === 'function'
        ? /*@__PURE__*/ Symbol('rxSubscriber')
        : '@@rxSubscriber_' + /*@__PURE__*/ Math.random();
})();

/** PURE_IMPORTS_START tslib,_util_isFunction,_Observer,_Subscription,_internal_symbol_rxSubscriber,_config,_util_hostReportError PURE_IMPORTS_END */
var Subscriber = /*@__PURE__*/ (function (_super) {
    __extends(Subscriber, _super);
    function Subscriber(destinationOrNext, error, complete) {
        var _this = _super.call(this) || this;
        _this.syncErrorValue = null;
        _this.syncErrorThrown = false;
        _this.syncErrorThrowable = false;
        _this.isStopped = false;
        switch (arguments.length) {
            case 0:
                _this.destination = empty;
                break;
            case 1:
                if (!destinationOrNext) {
                    _this.destination = empty;
                    break;
                }
                if (typeof destinationOrNext === 'object') {
                    if (destinationOrNext instanceof Subscriber) {
                        _this.syncErrorThrowable = destinationOrNext.syncErrorThrowable;
                        _this.destination = destinationOrNext;
                        destinationOrNext.add(_this);
                    }
                    else {
                        _this.syncErrorThrowable = true;
                        _this.destination = new SafeSubscriber(_this, destinationOrNext);
                    }
                    break;
                }
            default:
                _this.syncErrorThrowable = true;
                _this.destination = new SafeSubscriber(_this, destinationOrNext, error, complete);
                break;
        }
        return _this;
    }
    Subscriber.prototype[rxSubscriber] = function () { return this; };
    Subscriber.create = function (next, error, complete) {
        var subscriber = new Subscriber(next, error, complete);
        subscriber.syncErrorThrowable = false;
        return subscriber;
    };
    Subscriber.prototype.next = function (value) {
        if (!this.isStopped) {
            this._next(value);
        }
    };
    Subscriber.prototype.error = function (err) {
        if (!this.isStopped) {
            this.isStopped = true;
            this._error(err);
        }
    };
    Subscriber.prototype.complete = function () {
        if (!this.isStopped) {
            this.isStopped = true;
            this._complete();
        }
    };
    Subscriber.prototype.unsubscribe = function () {
        if (this.closed) {
            return;
        }
        this.isStopped = true;
        _super.prototype.unsubscribe.call(this);
    };
    Subscriber.prototype._next = function (value) {
        this.destination.next(value);
    };
    Subscriber.prototype._error = function (err) {
        this.destination.error(err);
        this.unsubscribe();
    };
    Subscriber.prototype._complete = function () {
        this.destination.complete();
        this.unsubscribe();
    };
    Subscriber.prototype._unsubscribeAndRecycle = function () {
        var _parentOrParents = this._parentOrParents;
        this._parentOrParents = null;
        this.unsubscribe();
        this.closed = false;
        this.isStopped = false;
        this._parentOrParents = _parentOrParents;
        return this;
    };
    return Subscriber;
}(Subscription));
var SafeSubscriber = /*@__PURE__*/ (function (_super) {
    __extends(SafeSubscriber, _super);
    function SafeSubscriber(_parentSubscriber, observerOrNext, error, complete) {
        var _this = _super.call(this) || this;
        _this._parentSubscriber = _parentSubscriber;
        var next;
        var context = _this;
        if (isFunction(observerOrNext)) {
            next = observerOrNext;
        }
        else if (observerOrNext) {
            next = observerOrNext.next;
            error = observerOrNext.error;
            complete = observerOrNext.complete;
            if (observerOrNext !== empty) {
                context = Object.create(observerOrNext);
                if (isFunction(context.unsubscribe)) {
                    _this.add(context.unsubscribe.bind(context));
                }
                context.unsubscribe = _this.unsubscribe.bind(_this);
            }
        }
        _this._context = context;
        _this._next = next;
        _this._error = error;
        _this._complete = complete;
        return _this;
    }
    SafeSubscriber.prototype.next = function (value) {
        if (!this.isStopped && this._next) {
            var _parentSubscriber = this._parentSubscriber;
            if (!config.useDeprecatedSynchronousErrorHandling || !_parentSubscriber.syncErrorThrowable) {
                this.__tryOrUnsub(this._next, value);
            }
            else if (this.__tryOrSetError(_parentSubscriber, this._next, value)) {
                this.unsubscribe();
            }
        }
    };
    SafeSubscriber.prototype.error = function (err) {
        if (!this.isStopped) {
            var _parentSubscriber = this._parentSubscriber;
            var useDeprecatedSynchronousErrorHandling = config.useDeprecatedSynchronousErrorHandling;
            if (this._error) {
                if (!useDeprecatedSynchronousErrorHandling || !_parentSubscriber.syncErrorThrowable) {
                    this.__tryOrUnsub(this._error, err);
                    this.unsubscribe();
                }
                else {
                    this.__tryOrSetError(_parentSubscriber, this._error, err);
                    this.unsubscribe();
                }
            }
            else if (!_parentSubscriber.syncErrorThrowable) {
                this.unsubscribe();
                if (useDeprecatedSynchronousErrorHandling) {
                    throw err;
                }
                hostReportError(err);
            }
            else {
                if (useDeprecatedSynchronousErrorHandling) {
                    _parentSubscriber.syncErrorValue = err;
                    _parentSubscriber.syncErrorThrown = true;
                }
                else {
                    hostReportError(err);
                }
                this.unsubscribe();
            }
        }
    };
    SafeSubscriber.prototype.complete = function () {
        var _this = this;
        if (!this.isStopped) {
            var _parentSubscriber = this._parentSubscriber;
            if (this._complete) {
                var wrappedComplete = function () { return _this._complete.call(_this._context); };
                if (!config.useDeprecatedSynchronousErrorHandling || !_parentSubscriber.syncErrorThrowable) {
                    this.__tryOrUnsub(wrappedComplete);
                    this.unsubscribe();
                }
                else {
                    this.__tryOrSetError(_parentSubscriber, wrappedComplete);
                    this.unsubscribe();
                }
            }
            else {
                this.unsubscribe();
            }
        }
    };
    SafeSubscriber.prototype.__tryOrUnsub = function (fn, value) {
        try {
            fn.call(this._context, value);
        }
        catch (err) {
            this.unsubscribe();
            if (config.useDeprecatedSynchronousErrorHandling) {
                throw err;
            }
            else {
                hostReportError(err);
            }
        }
    };
    SafeSubscriber.prototype.__tryOrSetError = function (parent, fn, value) {
        if (!config.useDeprecatedSynchronousErrorHandling) {
            throw new Error('bad call');
        }
        try {
            fn.call(this._context, value);
        }
        catch (err) {
            if (config.useDeprecatedSynchronousErrorHandling) {
                parent.syncErrorValue = err;
                parent.syncErrorThrown = true;
                return true;
            }
            else {
                hostReportError(err);
                return true;
            }
        }
        return false;
    };
    SafeSubscriber.prototype._unsubscribe = function () {
        var _parentSubscriber = this._parentSubscriber;
        this._context = null;
        this._parentSubscriber = null;
        _parentSubscriber.unsubscribe();
    };
    return SafeSubscriber;
}(Subscriber));

/** PURE_IMPORTS_START tslib,_Subscriber PURE_IMPORTS_END */
function map(project, thisArg) {
    return function mapOperation(source) {
        if (typeof project !== 'function') {
            throw new TypeError('argument is not a function. Are you looking for `mapTo()`?');
        }
        return source.lift(new MapOperator(project, thisArg));
    };
}
var MapOperator = /*@__PURE__*/ (function () {
    function MapOperator(project, thisArg) {
        this.project = project;
        this.thisArg = thisArg;
    }
    MapOperator.prototype.call = function (subscriber, source) {
        return source.subscribe(new MapSubscriber(subscriber, this.project, this.thisArg));
    };
    return MapOperator;
}());
var MapSubscriber = /*@__PURE__*/ (function (_super) {
    __extends(MapSubscriber, _super);
    function MapSubscriber(destination, project, thisArg) {
        var _this = _super.call(this, destination) || this;
        _this.project = project;
        _this.count = 0;
        _this.thisArg = thisArg || _this;
        return _this;
    }
    MapSubscriber.prototype._next = function (value) {
        var result;
        try {
            result = this.project.call(this.thisArg, value, this.count++);
        }
        catch (err) {
            this.destination.error(err);
            return;
        }
        this.destination.next(result);
    };
    return MapSubscriber;
}(Subscriber));

/** PURE_IMPORTS_START tslib,_Subscriber PURE_IMPORTS_END */
function distinctUntilChanged(compare, keySelector) {
    return function (source) { return source.lift(new DistinctUntilChangedOperator(compare, keySelector)); };
}
var DistinctUntilChangedOperator = /*@__PURE__*/ (function () {
    function DistinctUntilChangedOperator(compare, keySelector) {
        this.compare = compare;
        this.keySelector = keySelector;
    }
    DistinctUntilChangedOperator.prototype.call = function (subscriber, source) {
        return source.subscribe(new DistinctUntilChangedSubscriber(subscriber, this.compare, this.keySelector));
    };
    return DistinctUntilChangedOperator;
}());
var DistinctUntilChangedSubscriber = /*@__PURE__*/ (function (_super) {
    __extends(DistinctUntilChangedSubscriber, _super);
    function DistinctUntilChangedSubscriber(destination, compare, keySelector) {
        var _this = _super.call(this, destination) || this;
        _this.keySelector = keySelector;
        _this.hasKey = false;
        if (typeof compare === 'function') {
            _this.compare = compare;
        }
        return _this;
    }
    DistinctUntilChangedSubscriber.prototype.compare = function (x, y) {
        return x === y;
    };
    DistinctUntilChangedSubscriber.prototype._next = function (value) {
        var key;
        try {
            var keySelector = this.keySelector;
            key = keySelector ? keySelector(value) : value;
        }
        catch (err) {
            return this.destination.error(err);
        }
        var result = false;
        if (this.hasKey) {
            try {
                var compare = this.compare;
                result = compare(this.key, key);
            }
            catch (err) {
                return this.destination.error(err);
            }
        }
        else {
            this.hasKey = true;
        }
        if (!result) {
            this.key = key;
            this.destination.next(value);
        }
    };
    return DistinctUntilChangedSubscriber;
}(Subscriber));

/**
 * This class exposes a redux store that can be used when EVA or even the
 * entire internet is unavailable.
 * It is aimed at providing a way to process order with a heavily reduced
 * functionality set
 *
 * @export
 * @class SentinelOffline
 */
class SentinelOffline {
    /**
     * Creates an instance of SentinelOffline
     *
     * @param {Sentinel} parent The parent sentinel class
     */
    constructor(parent) {
        this.parent = parent;
        /**
         * Indicates we are currently in offline mode
         *
         * @type {TOnlineStatus}
         */
        this.status = 'unknown';
        /**
         * The status change stream
         *
         * @type {Subject<TOnlineStatus>}
         * @memberof SentinelStatus
         */
        this.changes$ = new Subject();
    }
    /**
     * Returns the current amount of available services
     *
     * @readonly
     */
    get availableServiceCount() {
        const availableServices = get(this.availableServices, 'Services') || [];
        return availableServices.length;
    }
    /**
     * Start the support for using the sentinel for an offline limited Redux store
     *
     */
    async start() {
        debug(`[SENTINEL:OFFLINE] Starting...`);
        if (this.status !== 'unknown') {
            await this.stop();
        }
        // Get the current sentinel endpoint and token
        // Will throw if not available or disconnected
        //
        let endpoint;
        try {
            endpoint = this.parent.getEndpoint();
        }
        catch (error) {
            // No Sentinel available so nothing to do
            //
            this.status = 'unknown';
            return;
        }
        // Call the get available services service with a custom endpoint
        // NOTE: These are the anonymous base services available on the sentinel
        //
        const [action] = getAvailableServices.createFetchAction();
        this.availableServices = await getAvailableServices.fetchData(action, undefined, undefined, endpoint);
        // If this call succeeded we are online
        //
        this.status = 'online';
        this.emitStatusChange();
        // Subscribe to sentinel status events to trigger offline mode when needed
        //
        this.statusSubscription = this.parent.status.changes$.subscribe(async (statusEvent) => {
            debug(`[SENTINEL:OFFLINE] Received new status event. Current status: ${this.status}`, statusEvent);
            // EVA is down so lets start using the sentinel (if available)
            //
            if (statusEvent.eva === 'disconnected' &&
                statusEvent.sentinel === 'connected' &&
                (this.status === 'online' || this.status === 'pending')) {
                debug(`[SENTINEL:OFFLINE] Going offline!`);
                await this.goOffline();
            }
            if (statusEvent.eva === 'connected' &&
                this.status === 'offline') {
                // Signal the application that it can resume online functionality
                //
                debug(`[SENTINEL:OFFLINE] Online is available, pending!`);
                this.status = 'pending';
                this.emitStatusChange();
            }
            debug(`[SENTINEL:OFFLINE] New offline status is ${this.status}`);
        });
    }
    /**
     * Stop the support for the offline usage of the Redux store
     *
     */
    async stop() {
        debug(`[SENTINEL:OFFLINE] Stopping...`);
        if (this.statusSubscription) {
            this.statusSubscription.unsubscribe();
            this.statusSubscription = undefined;
        }
        if (this.status === 'offline') {
            await this.goOnline();
        }
    }
    /**
     * Checks if a service is available if offline mode
     *
     * @param {string} serviceName
     * @returns {boolean}
     */
    isServiceAvailableOffline(url) {
        if (this.status === 'online') {
            return true;
        }
        const serviceName = this.getServiceNameFromUrl(url);
        // Check the available services response which are the current users
        // services
        //
        const availableServicesState = getAvailableServices.getState();
        const availableUserServices = get(availableServicesState.response, 'Services') || [];
        let service = find(availableUserServices, { Name: serviceName });
        if (!service) {
            // Since the users list is not available for some reason we will use
            // the base (anonymous user) available services from the sentinel
            // This list is far more restrictive
            //
            const availableServices = get(this.availableServices, 'Services') || [];
            service = find(availableServices, { Name: serviceName });
        }
        const serviceAvailable = !!service && !!service.AvailableOffline;
        debug(`[SENTINEL:OFFLINE] Service ${serviceName} available offline: ${serviceAvailable}`);
        return serviceAvailable;
    }
    /**
     * Helper message to infer the service name from the url
     *
     * @param {string} url The input URL
     * @returns The service name
     */
    getServiceNameFromUrl(url) {
        const serviceName = get(`${url}`.match(/\/message\/(.*)/), 1);
        return serviceName;
    }
    /**
     * Stop using the sentinel for offline support and resume using online EVA
     *
     */
    async goOnline() {
        // Dump the current store state and go online
        //
        await this.bootstrapStore();
        this.status = 'online';
        this.emitStatusChange();
    }
    /**
     * Stop using online EVA and start relying on the sentinel
     *
     */
    async goOffline() {
        if (this.parent.status.sentinel !== 'connected') {
            throw new Error('There is no sentinel available to go offline');
        }
        const isAlreadyPending = this.status === 'pending';
        // Emit that we're nog offline
        //
        this.status = 'offline';
        this.emitStatusChange();
        // Dump the current store state and go offline if were online before
        // We could be pending and going back offline again
        //
        if (!isAlreadyPending) {
            await this.bootstrapStore();
        }
    }
    /**
     * Private helper to reset the Redux store
     *
     * @private
     */
    async bootstrapStore() {
        // Purge the redux state
        // We will need to dump the entire state and start fresh every time
        // Effectively this is a new bootstrap
        //
        // When offline the sentinel becomes the source for both of these
        // The signalR settings from the application configuration will restore
        // communicator functionality
        //
        // The core.dump method will update our stateResetStatus so we are
        // no longer in `pending` status when going back online
        // This is done there is case someone decided to call it directly and bypass
        // this sentinel code
        //
        core.dump({
            getApplicationConfiguration: getApplicationConfiguration.getState(),
        });
        // Fetch initial application configuration
        // Manually call fetchData to ensure no auth token is used
        //
        try {
            debug(`[SENTINEL:OFFLINE] Bootstrapping: configuration`);
            const [applicationConfigurationAction] = getApplicationConfiguration.createFetchAction();
            const applicationConfiguration = await getApplicationConfiguration.fetchData(applicationConfigurationAction);
            // Manually dispatch the response action
            //
            const applicationConfigurationResponseAction = getApplicationConfiguration.createResponseAction(applicationConfiguration);
            store.dispatch(applicationConfigurationResponseAction);
        }
        catch (error$1) {
            error(`[SENTINEL:OFFLINE] Bootstrapping failed: configuration error. Everything is broken now`, error$1);
            throw error$1;
        }
        // Fetch the current user and the initial cart
        // The available services are part of the user chain
        //
        const [userAction, , userChainPromise] = getCurrentUser.createFetchAction();
        store.dispatch(userAction);
        const [cartAction, , cartChainPromise] = getShoppingCartInfo.createFetchAction();
        store.dispatch(cartAction);
        await Promise.all([userChainPromise, cartChainPromise]);
        // Resolve with fetch data
        //
        return [
            getApplicationConfiguration.getState().response,
            getCurrentUser.getState().response,
            getShoppingCartInfo.getState().response,
        ];
    }
    /**
     * Helper method to emit a status change
     *
     * @private
     */
    emitStatusChange() {
        this.changes$.next(this.status);
    }
}

/**
 * This class exposes the performance features of the sentinel
 * These feature are used to reduce latency for web service by providing
 * local alternatives.
 *
 * @export
 * @class SentinelPerformance
 */
class SentinelPerformance {
    /**
     * Creates an instance of SentinelPerformance
     *
     * @param {Sentinel} parent The parent sentinel class
     */
    constructor(parent) {
        this.parent = parent;
    }
    /**
     * Use the sentinel to parse a barcode for low-latency fast scanning
     *
     */
    async parseBarcode(request, timeout, authorisationToken) {
        // Get the current sentinel endpoint
        // Will throw if not available or disconnected
        //
        const sentinelUrl = this.parent.getEndpoint();
        // Call the parse barcode service with a custom endpoint
        //
        const [action] = parseBarcode.createFetchAction(request);
        const result = await parseBarcode.fetchData(action, timeout, authorisationToken, sentinelUrl);
        return result;
    }
}

/**
 * This class exposes the status features of the sentinel
 * These will listen to the sentinel to the sentinel signalR hub if available
 *
 * @export
 * @class SentinelStatus
 */
class SentinelStatus {
    /**
     * Creates an instance of SentinelStatus
     *
     * @param {Sentinel} parent The parent sentinel class
     */
    constructor(parent) {
        this.parent = parent;
        /**
         * The current status of the internet connection as reported by the sentinel
         *
         * @type {TFeatureStatus}
         */
        this.internet = 'unknown';
        /**
         * The current status of the EVA platform as reported by the sentinel
         *
         * @type {TFeatureStatus}
         */
        this.eva = 'unknown';
        /**
         * The current status of the sentinel itself
         *
         * @type {TFeatureStatus}
         */
        this.sentinel = 'unknown';
        /**
         * The status change stream
         *
         * @type {Subject<IStatusChange>}
         * @memberof SentinelStatus
         */
        this.changes$ = new Subject();
        /**
         * Reconnect counter to help maintain the SignalR connection
         *
         * @private
         */
        this.reconnectCount = 0;
    }
    /**
     * Start listening for status updated from the sentinel SignalR hub
     * Note that this can be called multiple times in succession if new application
     * configuration responses arrive.
     * We need to remember which sentinel we are talking to and stay connected if
     * the url does not change
     *
     * @returns
     */
    async start() {
        debug(`[SENTINEL:STATUS] Starting...`);
        let endpoint;
        try {
            endpoint = this.parent.getSignalREndpoint();
        }
        catch (error) {
            // No Sentinel available so set status accordingly
            // Without the sentinel we have no known status for EVA and the internet
            //
            this.sentinel = 'unavailable';
            this.internet = 'unknown';
            this.eva = 'unknown';
            return;
        }
        // Disconnect any previous connection
        //
        if (this.hub) {
            debug(`[SENTINEL:STATUS] Stopping previous connection`);
            await this.stop();
        }
        // We have a sentinel but are not connected yet
        //
        this.sentinel = 'disconnected';
        this.emitStatusChange();
        const hubDetails = this.parent.getStatusHubDetails();
        const hubName = hubDetails.Name;
        const hubUri = `${endpoint}${hubDetails.Path}`;
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUri, {
            skipNegotiation: true,
            transport: 1,
        })
            .configureLogging(settings.enableLogging ? 1 : 5) // 1 = debug, 5 = critical
            .withAutomaticReconnect()
            .build();
        // Connect to the hub
        //
        debug(`[SENTINEL:STATUS] Connecting to ${hubName}...`);
        await connection.start();
        debug(`[SENTINEL:STATUS] Connected to ${hubName}`);
        // Setup event listener(s)
        //
        connection.onclose(() => {
            log$1(`[SENTINEL:STATUS] Closed connection to ${hubName}`);
            this.sentinel = 'disconnected';
            this.eva = 'unknown';
            this.internet = 'unknown';
            this.emitStatusChange();
            // If the connection closed after SignalR tries to reconnect we have
            // network issues. We should try to reconnect at a regular interval
            //
            if (this.reconnectCount > 0) {
                this.reconnectAttempt(connection);
            }
        });
        connection.onreconnecting(() => {
            warn(`[SENTINEL:STATUS] Reconnecting to ${hubName}...`);
            this.sentinel = 'reconnecting';
            this.eva = 'unknown';
            this.internet = 'unknown';
            // Set the reconnecting state which will help if we can't recover
            //
            this.reconnectCount += 1;
        });
        connection.onreconnected(() => {
            log$1(`[SENTINEL:STATUS] Reconnected to ${hubName}`);
            this.sentinel = 'connected';
            // Unset the reconnecting state because we appear to have recovered
            //
            this.reconnectCount = 0;
            this.emitStatusChange();
            // Request a status update
            //
            this.getStatus();
        });
        connection.on('UpdateInternetStatus', (statusEvent) => {
            debug(`[SENTINEL:STATUS] Received internet status`, statusEvent);
            this.internet = statusEvent.Online ? 'connected' : 'disconnected';
            this.sentinel = 'connected';
            this.emitStatusChange();
        });
        connection.on('UpdateCloudStatus', (statusEvent) => {
            debug(`[SENTINEL:STATUS] Received EVA cloud status`, statusEvent);
            this.eva = statusEvent.Online ? 'connected' : 'disconnected';
            this.sentinel = 'connected';
            this.emitStatusChange();
        });
        // Add the hub connection
        //
        const hubConnection = {
            connection,
            token: undefined,
            hub: hubDetails,
            subjectId: 'sentinel',
            uri: hubUri,
            reconnect_count: 0,
        };
        this.hub = hubConnection;
        debug('[SENTINEL:STATUS] Started status monitoring');
        // Do initial status update (fire and forget)
        //
        this.getStatus();
        return;
    }
    /**
     * Stops all status checks
     *
     * @returns
     */
    async stop() {
        debug(`[SENTINEL:OFFLINE] Stopping...`);
        // Disconnect from the Sentinel SignalR hub
        //
        if (this.hub) {
            try {
                await this.hub.connection.stop();
                debug(`[SENTINEL:STATUS] Disconnected from Sentinel status hub`);
            }
            catch (error) {
                warn(`[SENTINEL:STATUS] Disconnect from status hub failed: ${error}`);
            }
        }
        // Reset all statuses
        //
        this.sentinel = 'unknown';
        this.internet = 'unknown';
        this.eva = 'unknown';
        debug('[SENTINEL:STATUS] Stopped status monitoring');
        this.emitStatusChange();
        return;
    }
    /**
     * On demand status update call
     *
     * @returns
     */
    async getStatus() {
        if (!this.hub) {
            throw new Error(`Not connected to sentinel status hub`);
        }
        const newStatus = await this.hub.connection.invoke('GetStatus');
        this.eva = newStatus.CloudOnline ? 'connected' : 'disconnected';
        this.internet = newStatus.InternetOnline ? 'connected' : 'disconnected';
        this.sentinel = 'connected';
        this.emitStatusChange();
        return newStatus;
    }
    /**
     * Helper method to emit a status change
     *
     * @private
     */
    emitStatusChange() {
        this.changes$.next({
            eva: this.eva,
            internet: this.internet,
            sentinel: this.sentinel,
        });
    }
    /**
     * Helper method to try to reconnect to the SignalR server
     * We will use the reconnect count and multiply it with a small interval
     * which will effectively increment it for each attempt
     * Max delay is set to 10 minutes
     *
     * @private
     * @param {IHubConnection} hubDetails
     */
    reconnectAttempt(connection) {
        this.reconnectCount += 1;
        const delayInSeconds = this.reconnectCount * 5;
        log$1(`[RT:HUB] Setting reconnect timer for sentinel status hub in ${this.reconnectCount} second(s)`);
        setTimeout(async () => {
            try {
                await connection.start();
                log$1(`[RT:HUB] Successfully (re)connected to sentinel status hub`);
                this.reconnectCount = 0;
            }
            catch (error) {
                warn(`[RT:HUB] Failed to (re)start connection for sentinel status hub`, error);
                this.reconnectAttempt(connection);
            }
        }, Math.min(600000, delayInSeconds * 1000));
    }
}

class Sentinel {
    /**
     * Creates an instance of Sentinel
     */
    constructor() {
        // Extend the sentinel class with additional features
        //
        this.offline = new SentinelOffline(this);
        this.performance = new SentinelPerformance(this);
        this.status = new SentinelStatus(this);
    }
    /**
     * Indicates sentinel support has been enabled
     *
     * @readonly
     */
    get started() {
        return !!this.configurationSubscription;
    }
    /**
     * Helper method to get the current sentinel endpoint
     *
     * @protected
     * @returns
     */
    getEndpoint() {
        if (!settings.sentinelEndPointUrl) {
            throw new Error('No sentinel available');
        }
        return settings.sentinelEndPointUrl;
    }
    /**
     * Helper method to get the current sentinel SignalR endpoint
     *
     * @protected
     * @returns
     */
    getSignalREndpoint() {
        if (!settings.sentinelEndPointUrl) {
            throw new Error('No sentinel available');
        }
        if (!this.configuration) {
            throw new Error('No sentinel configuration available');
        }
        return this.configuration.Configuration['SignalR:BaseUrl'];
    }
    /**
     * Returns the SignalR hub from getApplicationConfiguration response for the sentinel
     * Only available after sentinel has been bootstrapped
     *
     */
    getStatusHubDetails() {
        if (!this.configuration) {
            throw new Error('Sentinel has not been bootstrapped');
        }
        const hubDetails = find(get(this.configuration, 'Configuration.SignalR:Hubs'), { Name: 'SentinelHub' });
        if (!hubDetails) {
            throw new Error('Sentinel does not have a status hub');
        }
        return hubDetails;
    }
    /**
     * Returns a copy of the configuration from the Sentinel
     *
     * @returns
     */
    getConfiguration() {
        return cloneDeep(this.configuration);
    }
    /**
     * Returns the organization unit the sentinel belongs to
     *
     * @returns number|undefined
     */
    getOrganizationUnitID() {
        const organizationUnitID = get(this.configuration, 'Configuration.Sentinel.OriginalOrganizationUnitID');
        return organizationUnitID;
    }
    /**
     * Start listening to the getApplicationConfig
     * Will resolve its promise when first application config is received
     *
     * @memberof Sentinel
     */
    start() {
        return new Promise((resolve, reject) => {
            this.configurationSubscription = getApplicationConfiguration.getResponse$()
                .subscribe(async (response) => {
                if (response) {
                    try {
                        debug(`[SENTINEL] Updated application configuration`, response);
                        // Preserve last known sentinel URL
                        //
                        let sentinelEndpointUrl = get(response, 'Configuration.Sentinel.Url');
                        let sentinelAvailable = get(response, 'Configuration.Sentinel.Available');
                        if (settings.preserveLastKnownSentinel && !sentinelEndpointUrl && settings.sentinelEndPointUrl) {
                            sentinelEndpointUrl = settings.sentinelEndPointUrl;
                            sentinelAvailable = !!settings.sentinelEndPointUrl;
                        }
                        // Only (re)initialize sentinel if the endpoint has changed
                        // Also do not act if we're in offline mode because the configuration
                        // we received will from the sentinel itself and it will not contain
                        // a sentinel configuration. Sentinel inception is not a thing
                        //
                        // Also a bootstrap failure that is trying to get us to take over the
                        // startup should be handled here. This only occurs during our first start
                        //
                        debug(`[SENTINEL] Current sentinel endpoint`, sentinelEndpointUrl);
                        const firstStart = this.status.sentinel === 'unknown';
                        if ((firstStart || settings.sentinelEndPointUrl !== sentinelEndpointUrl) && this.offline.status !== 'offline') {
                            settings.sentinelEndPointUrl = sentinelEndpointUrl;
                            debug(`[SENTINEL] Sentinel endpoint url: ${sentinelEndpointUrl}`, sentinelAvailable);
                            if (sentinelEndpointUrl) {
                                // We can now get the token because we've set our private applications member
                                //
                                const [applicationConfigurationAction] = getApplicationConfiguration.createFetchAction();
                                debug('[SENTINEL] Fetching application configuration...');
                                this.configuration = await getApplicationConfiguration.fetchData(applicationConfigurationAction, undefined, undefined, sentinelEndpointUrl);
                            }
                            // Initialize all additional sentinel features
                            // This can be done even without a sentinel URL because this means
                            // there simply is no sentinel available
                            //
                            debug('[SENTINEL] (Re)starting sentinel services...');
                            await Promise.all([
                                this.status.start(),
                                this.offline.start(),
                            ]);
                            debug('[SENTINEL] Sentinel ready');
                        }
                        resolve();
                        // NOOP: Wait for changed or new application configuration instead of resolving here
                    }
                    catch (error) {
                        reject(error);
                    }
                }
            });
        });
    }
    /**
     * Stops EVA Sentinel support
     *
     * @returns
     */
    async stop() {
        // Stop any pending health and status checks
        //
        await this.offline.stop();
        await this.status.stop();
        // Stop the subscription for getApplicationConfiguration changes
        //
        if (this.configurationSubscription) {
            this.configurationSubscription.unsubscribe();
            this.configurationSubscription = undefined;
        }
        return;
    }
}
// Expose our singleton sentinel support handler
//
const sentinel = new Sentinel();

/**
 * Reusable fetchable data implementation for the Redux store
 *
 * @export
 * @class StoreFetchableData
 * @template REQ
 * @template RES
 */
class StoreFetchableData {
    /**
     * Creates an instance of StoreFetchableData
     *
     * @param {string} name The name of this piece of state
     * @param {string} url The url to fetch the data from
     * @param {*} [fetchOptions] Optional additional parameters for the fetch
     * @memberof StoreFetchableData
     */
    constructor(name, url, fetchOptions, requestOptions, additionalActions) {
        this.url = url;
        this.fetchOptions = fetchOptions;
        this.requestOptions = requestOptions;
        /**
         * The dispatch count. Used for the dispatchId
         *
         * @protected
         * @type {number}
         * @memberof StoreFetchableData
         */
        this.dispatchCount = 0;
        // Set the section name
        //
        this.name = name;
        // Declare our actions
        //
        this.actions = extend({
            fetch: createAction('FETCH_' + this.name, (request) => ({ type: 'FETCH_' + this.name, payload: request })),
            response: createAction('RESPONSE_' + this.name, (response) => ({ type: 'RESPONSE_' + this.name, payload: response })),
            error: createAction('ERROR_' + this.name, (error) => ({ type: 'ERROR_' + this.name, payload: error })),
        }, additionalActions);
        // Create our reducer based on the above actions
        //
        this.reducer = combineReducers(this.getReducerFunctions());
    }
    /**
     * Initial state
     *
     * @type {IStoreFetchableDataState<REQ, RES>}
     * @memberof StoreFetchableData
     */
    get initialState() {
        return {
            isFetching: false,
            request: null,
            response: null,
            chainStarter: this.name,
            error: null,
        };
    }
    /**
     * Expose a helper function to get the full url
     *
     * @returns
     * @memberof StoreFetchableData
     */
    getUrl() {
        return `${settings.endPointURL}${this.url}`;
    }
    /**
     * Create a fetch action using the class request type
     * This helper will setup and add promises for the fetch call
     * and any chain that is triggered
     *
     * TODO: If typings provide optional-ness correctly we can remove the Partial
     *
     * NOTE: omitSessionId is deprecated but remains to not mess up parameter ordering
     *
     * @param {REQ} [request] The request payload
     * @returns
     * @memberof StoreFetchableData
     */
    createFetchAction(request, chainStarter = this.name, timeout, omitSessionIdDeprecated = false, elevationToken, serviceContext, chainContext) {
        const dispatchId = `${this.name}_${++this.dispatchCount}`;
        const requestId = uuid();
        const serviceName = this.name;
        const fetchAction = this.actions.fetch({
            request: cloneDeep(request),
            dispatchId: dispatchId,
            chainStarter: chainStarter,
            timeout: timeout,
            elevationToken,
            serviceContext: serviceContext || `${serviceName}:${requestId}`,
            chainContext: chainContext || `${serviceName}:CHAIN:${requestId}`,
        });
        const fetchPromise = new Promise((resolve, reject) => {
            fetchAction.payload.fetchResolve = resolve;
            fetchAction.payload.fetchReject = reject;
        });
        const chainPromise = new Promise((resolve, reject) => {
            fetchAction.payload.chainResolve = resolve;
            fetchAction.payload.chainReject = reject;
        });
        // Listen and log promise results
        //
        fetchPromise
            .then((response) => {
            // logger.log( `[EVA-SDK-REDUX:${this.name}] FETCH:OK:${dispatchId}`, response );
            // NOOP
        })
            .catch((error) => {
            // logger.error( `[EVA-SDK-REDUX:${this.name}] FETCH:ERROR:${dispatchId}`, error );
            // NOOP
        });
        chainPromise
            .then((response) => {
            // logger.log( `[EVA-SDK-REDUX:${this.name}] CHAIN:OK:${dispatchId}`, response );
            // NOOP
        })
            .catch((error) => {
            // logger.error( `[EVA-SDK-REDUX:${this.name}] CHAIN:ERROR:${dispatchId}`, error );
            // NOOP
        });
        return [fetchAction, fetchPromise, chainPromise];
    }
    /**
     * Create a response action using the class response type
     *
     * @param {RES} [response] The response payload
     * @returns
     * @memberof StoreFetchableData
     */
    createResponseAction(response) {
        return this.actions.response(response);
    }
    /**
     * Retrieves the requested data from the EVA backend
     *
     * @public
     * @param {*} action The redux action
     * @param {number} [timeout] Optional service timeout to replace the default
     * @param {string} [authorisationToken] Authentication token to use for the call. Set to 'none' to force empty
     * @returns {Promise<RES>}
     * @memberof StoreFetchableData
     */
    async fetchData(action, timeout, authorisationToken, endPointURL) {
        const payload = get(action, 'payload.request');
        const fetchTimeout = get(action, 'payload.timeout', timeout);
        const serviceName = sentinel.offline.getServiceNameFromUrl(this.url);
        const elevationToken = get(action, 'payload.elevationToken');
        const serviceContext = get(action, 'payload.serviceContext');
        const chainContext = get(action, 'payload.chainContext');
        if (!endPointURL) {
            // Check if offline
            // if offline check if the service is available
            // if available change the endpoint for the actual call to use the sentinel
            //
            if (sentinel.status.sentinel === 'connected' && (sentinel.offline.status === 'offline' || sentinel.offline.status === 'pending')) {
                endPointURL = sentinel.getEndpoint();
                // If the service if offline we will return an unavailable/empty response
                //
                if (!sentinel.offline.isServiceAvailableOffline(this.url)) {
                    log$1(`[EVA-SDK-REDUX:${this.name}] OFFLINE service unavailable ${serviceName}`, this.url);
                    return Promise.resolve({ offline: true });
                }
            }
            else if (sentinel.status.sentinel === 'connected' && serviceName === 'ParseBarcode' && !settings.disableSentinelParseCode) {
                // Parse barcode is a special case which always prefers to use the sentinel
                // for performance reasons
                //
                log$1(`[EVA-SDK-REDUX:${this.name}] Using sentinel for ${serviceName}`, this.url);
                endPointURL = sentinel.getEndpoint();
            }
            else {
                log$1(`[EVA-SDK-REDUX:${this.name}] Calling EVA cloud for ${serviceName}`, this.url);
                endPointURL = settings.endPointURL;
            }
        }
        // Figure out which authentication token to use for the call
        // Can either be provided or will fallback on what is available from the settings
        // The order if application is:
        // * call provided
        // * user token
        // * default token
        // * application token (premise/cloud depending on state)
        //
        // If the value 'none' is provided for authorisationToken the Authorization
        // header will be omitted from the request
        //
        let requestAuthenticationToken = authorisationToken || settings.userToken || settings.defaultToken;
        if (requestAuthenticationToken === 'none') {
            requestAuthenticationToken = undefined;
        }
        // Use the core fetch api to call the EVA backend
        // The core.bootstrap(...) should have been called before this
        //
        let data;
        try {
            const response = await core.fetch(endPointURL + this.url, extend({}, this.fetchOptions, { body: cloneDeep(payload) }), fetchTimeout, requestAuthenticationToken, elevationToken, serviceContext, chainContext);
            // Check for default exception
            //
            data = await response.json();
        }
        catch (error) {
            if (isFunction$1(error.text)) {
                data = await error.text();
                try {
                    error.response = JSON.parse(`${data}`);
                }
                catch (jsonParseError) {
                    error.response = data;
                }
                throw error;
            }
            // Handle timeout errors
            //
            if (error instanceof TimeoutError) {
                throw {
                    code: 408,
                    message: 'Request timed out',
                    error: 'TIMEOUT',
                };
            }
            throw error;
        }
        const exceptions = get(data, 'Exception');
        if (exceptions) {
            throw data;
        }
        return data;
    }
    /**
     * Retrieves section specific state from the root state
     *
     * @returns {STATE}
     * @memberof StoreFetchableData
     */
    getState() {
        const rootState = store.getState();
        return get(rootState, this.name);
    }
    /**
     * Retrieves section specific state from the root state as an observable stream
     *
     * @returns {Observable<STATE>} The observable stream
     * @memberof StoreFetchableData
     */
    getState$() {
        return core.state$.pipe(distinctUntilChanged((prev, next) => {
            return isEqual(get(prev, this.name), get(next, this.name));
        }), map((rootState) => get(rootState, this.name)));
    }
    /**
     * Retrieves a specific property path from this reducers state from the
     * root state as an observable stream
     *
     * @param {string} propertyPath
     * @returns {Observable<any>}
     * @memberof StoreFetchableData
     */
    getPropertyState$(propertyPath) {
        const path = isArray$1(propertyPath) ? concat([this.name], propertyPath) : `${this.name}.${propertyPath}`;
        return core.state$.pipe(distinctUntilChanged((prev, next) => {
            return isEqual(get(prev, path), get(next, path));
        }), map((rootState) => get(rootState, path)));
    }
    /**
     * Returns the response property as an observable stream
     *
     * @returns {Observable<RES>}
     * @memberof StoreFetchableData
     */
    getResponse$() {
        return this.getPropertyState$('response');
    }
    /**
     * Returns the response property as an observable stream
     *
     * @returns {Observable<REQ>}
     * @memberof StoreFetchableData
     */
    getRequest$() {
        return this.getPropertyState$('request');
    }
    /**
     * Returns the isFetching property as an observable stream
     *
     * @returns {Observable<boolean>}
     * @memberof StoreFetchableData
     */
    isFetching$() {
        return this.getPropertyState$('isFetching');
    }
    /**
     * Returns the error property as an observable stream
     *
     * @returns {(Observable<Error|null>)}
     * @memberof StoreFetchableData
     */
    getError$() {
        return this.getPropertyState$('error');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StoreFetchableData
     */
    *saga() {
        // NOOP
    }
    /**
     * Helper method for an async-awaitable timeout
     *
     * @param {number} ms
     * @returns
     * @memberof StoreFetchableData
     */
    timeout(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
    /**
     * The collection of reducer functions
     *
     * @protected
     * @returns {{ [ reducerName: string ]: ( state: any, action: AnyAction ) => any }}
     * @memberof StoreFetchableData
     */
    getReducerFunctions() {
        return {
            // Update the fetching status boolean
            //
            isFetching: (state = false, action) => {
                switch (action.type) {
                    case getType(this.actions.fetch):
                        return true;
                    // Unset isFetching on response
                    //
                    case getType(this.actions.response):
                        return false;
                    // Unset isFetching on error
                    //
                    case getType(this.actions.error):
                        return false;
                    default:
                        return state;
                }
            },
            // Set the error on failure and unset on success
            //
            error: (state = null, action) => {
                switch (action.type) {
                    // Reset error on new fetch
                    //
                    case getType(this.actions.fetch):
                        return null;
                    case getType(this.actions.error):
                        return action.payload;
                    default:
                        return state;
                }
            },
            // Update the request on fetch
            //
            request: (state = null, action) => {
                switch (action.type) {
                    // Only update on fetch
                    //
                    case getType(this.actions.fetch):
                        // Optionally amend the request
                        //
                        if (this.requestOptions && this.requestOptions.addDefaultProductPropertiesPath) {
                            const path = `payload.request.${this.requestOptions.addDefaultProductPropertiesPath}`;
                            set(action, path, get(action, path, settings.defaultProductProperties));
                        }
                        return get(action, 'payload.request') || null;
                    default:
                        return state;
                }
            },
            // Update the chainStarter on fetch
            //
            chainStarter: (state = null, action) => {
                switch (action.type) {
                    // Only update on fetch
                    //
                    case getType(this.actions.fetch):
                        return get(action, 'payload.chainStarter') || this.name;
                    default:
                        return state;
                }
            },
            // Update the response on success
            //
            response: (state = null, action) => {
                switch (action.type) {
                    // Only update on success
                    // We will not remove previous responses on fetch
                    // If clients want to hide the data they can switch display based
                    // on isFetching to be true or an Error to be set
                    //
                    case getType(this.actions.response):
                        return action.payload;
                    default:
                        return state;
                }
            },
        };
    }
}

/**
 * Implements AbortTransaction fetch for the Redux store
 *
 * @export
 * @class AbortTransaction
 * @extends {StoreFetchableData<EVA.Payment.Core.AbortTransaction, EVA.Payment.Core.AbortTransactionResponse>}
 */
class AbortTransaction extends StoreFetchableData {
    /**
     * Creates an instance of AbortTransaction
     *
     * @memberof AbortTransaction
     */
    constructor() {
        super('abortTransaction', '/message/AbortTransaction');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AbortTransaction
     */
    *saga() {
        yield fork(listener$7C);
    }
}
// Export the state handling singleton
//
const abortTransaction = new AbortTransaction();
// Setup the Saga implementation
//
function* fetchCall$7C(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([abortTransaction, 'fetchData'], action);
        yield put(abortTransaction.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(abortTransaction.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7C() {
    yield takeEvery(getType(abortTransaction.actions.fetch), fetchCall$7C);
}

/**
 * Implements AddBoardingPassToOrder fetch for the Redux store
 *
 * @export
 * @class AddBoardingPassToOrder
 * @extends {StoreFetchableData<EVA.Core.AddBoardingPassToOrder, EVA.Core.EmptyResponseMessage>}
 */
class AddBoardingPassToOrder extends StoreFetchableData {
    /**
     * Creates an instance of AddBoardingPassToOrder
     *
     * @memberof AddBoardingPassToOrder
     */
    constructor() {
        super('addBoardingPassToOrder', '/message/AddBoardingPassToOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AddBoardingPassToOrder
     */
    *saga() {
        yield fork(listener$7B);
    }
}
// Export the state handling singleton
//
const addBoardingPassToOrder = new AddBoardingPassToOrder();
// Setup the Saga implementation
//
function* fetchCall$7B(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([addBoardingPassToOrder, 'fetchData'], action);
        yield put(addBoardingPassToOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(addBoardingPassToOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7B() {
    yield takeEvery(getType(addBoardingPassToOrder.actions.fetch), fetchCall$7B);
}

/**
 * Implements PlaceOrder fetch for the Redux store
 *
 * @export
 * @class PlaceOrder
 * @extends {StoreFetchableData<EVA.Core.PlaceOrder, EVA.Core.PlaceOrderResponse>}
 */
class PlaceOrder extends StoreFetchableData {
    /**
     * Creates an instance of PlaceOrder
     *
     * @memberof PlaceOrder
     */
    constructor() {
        super('placeOrder', '/message/PlaceOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PlaceOrder
     */
    *saga() {
        yield fork(listener$7A);
    }
}
// Export the state handling singleton
//
const placeOrder = new PlaceOrder();
// Setup the Saga implementation
//
function* fetchCall$7A(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([placeOrder, 'fetchData'], action);
        yield put(placeOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // AddProductToOrder is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(placeOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7A() {
    yield takeEvery(getType(placeOrder.actions.fetch), fetchCall$7A);
}

/**
 * Implements PrepareOrderForCheckout fetch for the Redux store
 *
 * @export
 * @class PrepareOrderForCheckout
 * @extends {StoreFetchableData<EVA.Core.PrepareOrderForCheckout, EVA.Core.EmptyResponseMessage>}
 */
class PrepareOrderForCheckout extends StoreFetchableData {
    /**
     * Creates an instance of PrepareOrderForCheckout
     *
     * @memberof PrepareOrderForCheckout
     */
    constructor() {
        super('prepareOrderForCheckout', '/message/PrepareOrderForCheckout');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrepareOrderForCheckout
     */
    *saga() {
        yield fork(listener$7z);
    }
}
// Export the state handling singleton
//
const prepareOrderForCheckout = new PrepareOrderForCheckout();
// Setup the Saga implementation
//
function* fetchCall$7z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([prepareOrderForCheckout, 'fetchData'], action);
        yield put(prepareOrderForCheckout.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(prepareOrderForCheckout.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7z() {
    yield takeEvery(getType(prepareOrderForCheckout.actions.fetch), fetchCall$7z);
}

/**
 * Implements ShipOrder fetch for the Redux store
 *
 * @export
 * @class ShipOrder
 * @extends {StoreFetchableData<EVA.Core.ShipOrder, EVA.Core.ShipOrderResponse>}
 */
class ShipOrder extends StoreFetchableData {
    /**
     * Creates an instance of ShipOrder
     *
     * @memberof ShipOrder
     */
    constructor() {
        super('shipOrder', '/message/ShipOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ShipOrder
     */
    *saga() {
        yield fork(listener$7y);
    }
}
// Export the state handling singleton
//
const shipOrder = new ShipOrder();
// Setup the Saga implementation
//
function* fetchCall$7y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([shipOrder, 'fetchData'], action);
        yield put(shipOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(shipOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7y() {
    yield takeEvery(getType(shipOrder.actions.fetch), fetchCall$7y);
}

/**
 * Implements ValidateOrderShipment fetch for the Redux store
 *
 * @export
 * @class ValidateOrderShipment
 * @extends {StoreFetchableData<EVA.Core.ValidateOrderShipment, EVA.Core.ValidateOrderShipmentResponse>}
 */
class ValidateOrderShipment extends StoreFetchableData {
    /**
     * Creates an instance of ValidateOrderShipment
     *
     * @memberof ValidateOrderShipment
     */
    constructor() {
        super('validateOrderShipment', '/message/ValidateOrderShipment');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ValidateOrderShipment
     */
    *saga() {
        yield fork(listener$7x);
    }
}
// Export the state handling singleton
//
const validateOrderShipment = new ValidateOrderShipment();
// Setup the Saga implementation
//
function* fetchCall$7x(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([validateOrderShipment, 'fetchData'], action);
        yield put(validateOrderShipment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(validateOrderShipment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7x() {
    yield takeEvery(getType(validateOrderShipment.actions.fetch), fetchCall$7x);
}

/**
 * Implements ChangeOrderLinesToCarryOut fetch for the Redux store
 *
 * @export
 * @class ChangeOrderLinesToCarryOut
 * @extends {StoreFetchableData<EVA.Core.ChangeOrderLinesToCarryOut, EVA.Core.ShoppingCartResponse>}
 */
class ChangeOrderLinesToCarryOut extends StoreFetchableData {
    /**
     * Creates an instance of ChangeOrderLinesToCarryOut
     *
     * @memberof ChangeOrderLinesToCarryOut
     */
    constructor() {
        super('changeOrderLinesToCarryOut', '/message/ChangeOrderLinesToCarryOut');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ChangeOrderLinesToCarryOut
     */
    *saga() {
        yield fork(listener$7w);
    }
}
// Export the state handling singleton
//
const changeOrderLinesToCarryOut = new ChangeOrderLinesToCarryOut();
// Setup and export the Saga implementation
//
function* fetchCall$7w(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([changeOrderLinesToCarryOut, 'fetchData'], action);
        yield put(changeOrderLinesToCarryOut.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // ChangeOrderLinesToCarryOut is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(changeOrderLinesToCarryOut.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7w() {
    yield takeEvery(getType(changeOrderLinesToCarryOut.actions.fetch), fetchCall$7w);
}

/**
 * Implements PrefigureDiscounts fetch for the Redux store
 *
 * @export
 * @class PrefigureDiscounts
 * @extends {StoreFetchableData<EVA.Core.PrefigureDiscounts, EVA.Core.PrefigureDiscountsResponse>}
 */
class PrefigureDiscounts extends StoreFetchableData {
    /**
     * Creates an instance of PrefigureDiscounts
     *
     * @memberof PrefigureDiscounts
     */
    constructor() {
        super('prefigureDiscounts', '/message/PrefigureDiscounts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrefigureDiscounts
     */
    *saga() {
        yield fork(listener$7v);
    }
}
// Export the state handling singleton
//
const prefigureDiscounts = new PrefigureDiscounts();
// Setup the Saga implementation
//
function* fetchCall$7v(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([prefigureDiscounts, 'fetchData'], action);
        yield put(prefigureDiscounts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(prefigureDiscounts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7v() {
    yield takeEvery(getType(prefigureDiscounts.actions.fetch), fetchCall$7v);
}

/**
 * Implements GetApplicationConfiguration fetch for the Redux store
 *
 * @export
 * @class GetApplicationConfiguration
 * @extends {StoreFetchableData<EVA.Core.GetApplicationConfiguration, EVA.Core.GetApplicationConfigurationResponse>}
 */
class GetApplicationConfiguration extends StoreFetchableData {
    /**
     * Creates an instance of GetApplicationConfiguration
     *
     * @memberof GetApplicationConfiguration
     */
    constructor() {
        super('getApplicationConfiguration', '/message/GetApplicationConfiguration');
    }
    /**
     * Convenience method to get the asset base url from the configuration
     *
     * @returns
     * @memberof GetApplicationConfiguration
     */
    getAssetsBaseUrl() {
        const state = this.getState();
        return get(state, 'response.Configuration.Urls:Assets');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetApplicationConfiguration
     */
    *saga() {
        yield fork(listener$7u);
    }
}
// Export the state handling singleton
//
const getApplicationConfiguration = new GetApplicationConfiguration();
// Setup the Saga implementation
//
function* fetchCall$7u(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getApplicationConfiguration, 'fetchData'], action);
        yield put(getApplicationConfiguration.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getApplicationConfiguration.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7u() {
    yield takeEvery(getType(getApplicationConfiguration.actions.fetch), fetchCall$7u);
}

/**
 * Global order status
 *
 * @export
 * @enum {number}
 */
var OrderStatus;
(function (OrderStatus) {
    OrderStatus["completed"] = "COMPLETED";
    OrderStatus["shipped"] = "SHIPPED";
    OrderStatus["paid"] = "PAID";
    OrderStatus["partial_payment"] = "PARTIAL_PAYMENT";
    OrderStatus["new"] = "NEW";
})(OrderStatus || (OrderStatus = {}));
/**
 * The checkout mode for the order. Is deduced based on line action types
 * and characteristics
 *
 * @export
 * @enum {number}
 */
var OrderMode;
(function (OrderMode) {
    OrderMode["empty"] = "EMPTY";
    OrderMode["sale"] = "SALE";
    OrderMode["reservation"] = "RESERVATION";
    OrderMode["delivery"] = "DELIVERY";
    OrderMode["order"] = "ORDER";
    OrderMode["interbranch"] = "INTERBRANCH";
    OrderMode["returnToSupplier"] = "RETURN_TO_SUPPLIER";
    OrderMode["mixed"] = "MIXED";
})(OrderMode || (OrderMode = {}));
/**
 * Implements GetShoppingCart fetch for the Redux store
 *
 * @export
 * @class GetShoppingCart
 * @extends {StoreFetchableData<EVA.Core.GetShoppingCart, EVA.Core.GetShoppingCartResponse>}
 */
class GetShoppingCart extends StoreFetchableData {
    /**
     * Creates an instance of GetShoppingCart
     *
     * @memberof GetShoppingCart
     */
    constructor() {
        // Add the calculate changes action
        //
        const additionalActions = {
            calculateChanges: createAction('CALCULATE_CHANGES_getShoppingCart', (request) => {
                return ({ type: 'CALCULATE_CHANGES_getShoppingCart', payload: request });
            }),
        };
        super('getShoppingCart', '/message/GetShoppingCart', undefined, undefined, additionalActions);
        // Initialize the changes feed
        // The last changes are available on the state which is a behavior subject
        // Current changes should be emitted on a regular subject
        //
        this.changes$ = new Subject();
        // Initialize the completed checkout order stream
        //
        this.completedOrder$ = new Subject();
        // Set the default checkout start steps
        //
        this.defaultCheckoutStartSteps = [
            'customer',
            'discountProduct',
            'productRequirements',
            'productBundles',
            'serialNumber',
            'insurance',
        ];
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetShoppingCart
     */
    *saga() {
        yield fork(listener$7t);
    }
    /**
     * Initial state with extended properties
     *
     * @type {IGetShoppingCartState}
     * @memberof GetShoppingCart
     */
    get initialState() {
        const initialState = {
            isFetching: false,
            request: null,
            response: null,
            error: null,
            chainStarter: this.name,
            // Required data based deductions
            //
            pickDiscountProducts: null,
            productRequirementProducts: null,
            serialNumbers: null,
            productBundles: null,
            customerValid: true,
            customerIssues: [],
            orderValid: true,
            orderIssues: [],
            orderLinesValid: true,
            orderLineIssues: {},
            // Deduced cart state
            //
            changes: [],
            status: OrderStatus.new,
            orderLineActionTypes: [],
            checkoutStatus: {
                mode: null,
                steps: {},
            },
            count: {
                discount: 0,
                non_discount: 0,
                giftcard: 0,
                bundle: 0,
                configurable: 0,
                service: 0,
                total: 0,
            },
        };
        return initialState;
    }
    /**
     * Return the current order as an observable stream
     *
     * @returns {Observable<EVA.Core.OrderDto>}
     * @memberof GetShoppingCart
     */
    getOrder$() {
        return this.getPropertyState$('response.ShoppingCart');
    }
    //////////////////////////////////////////////////////////////////////////////
    // Checkout support
    //////////////////////////////////////////////////////////////////////////////
    /**
     * Checks if it is possible to start the checkout for the current shopping cart
     * and performs the necessary actions if so
     * On success this means the App con proceed to the checkout page/UI
     *
     * The list of preCheckoutRequired steps is enforced here.
     * If required steps are valid createOrder will be called if needed
     *
     * @param {*} [stepsToCheck=this.defaultCheckoutStartSteps] Steps to check before starting which defaults to recommended set
     * @returns {Promise<ICheckoutOperationResult>}
     * @memberof EvaCheckoutProvider
     */
    async startCheckout(stepsToCheck = this.defaultCheckoutStartSteps) {
        const checkoutStatus = this.getState().checkoutStatus;
        const invalidSteps = stepsToCheck.filter((stepName) => {
            return !get(checkoutStatus, `steps.${stepName}.valid`, true);
        });
        if (invalidSteps.length === 0) {
            // Check if we need to call prepare order for checkout
            //
            if (checkoutStatus.steps.createOrder && checkoutStatus.steps.createOrder.required === true) {
                const cartState = this.getState();
                const orderId = get(cartState, 'response.ShoppingCart.ID');
                const [prepareOrderAction, , prepareOrderChainPromise] = prepareOrderForCheckout.createFetchAction({
                    OrderID: orderId,
                });
                store.dispatch(prepareOrderAction);
                await prepareOrderChainPromise;
                // The previous service (createOrderFromShoppingCart) was chained to the
                // cart info. For backwards compatibility we will dispatch that here as well
                //
                const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: orderId }, prepareOrderAction.payload.chainStarter, undefined, undefined, undefined, undefined, prepareOrderAction.payload.chainContext);
                store.dispatch(getShoppingCartInfoAction);
                await getShoppingCartInfoChainPromise;
            }
            return {
                code: 200,
                message: 'Checkout started',
                type: 'SUCCESS',
            };
        }
        else {
            throw {
                code: 412,
                message: 'Prerequisite steps invalid',
                type: 'ERROR',
                error: first(invalidSteps),
                invalid: invalidSteps,
            };
        }
    }
    /**
     * Checks if the current order is in a state where payment can commence
     * The actual payment is handled by the payment provider which will verify
     * payment method specific requirements
     *
     * @memberof EvaCheckoutProvider
     */
    async canStartPayment() {
        const checkoutStatus = this.getState().checkoutStatus;
        const paymentStep = checkoutStatus.steps.payOrder;
        if (paymentStep) {
            if (paymentStep.valid) {
                // Order has already been paid for
                //
                return {
                    code: 208,
                    message: 'Order has already been payed',
                    type: 'SUCCESS',
                };
            }
            else if (paymentStep.possible) {
                return {
                    code: 100,
                    message: 'Payment can start',
                    type: 'SUCCESS',
                };
            }
            else {
                throw {
                    code: 412,
                    message: 'Payment can not start',
                    error: 'payOrder',
                    type: 'ERROR',
                };
            }
        }
        else {
            return {
                code: 204,
                message: 'No payment required',
                type: 'SUCCESS',
            };
        }
    }
    /**
     * This method will try to proceed the checkout by performing wrap-up actions
     * such as placing and shipping the order.
     * Depending on state and requirement issues this might fail.
     * This method may call itself multiple times to keep typing to proceed with the checkout
     *
     * @param {boolean} [shipReservation=false] Will change a reservation order to ship if valid and placed
     * @memberof EvaCheckoutProvider
     */
    async performCheckout(options = {}, recursionSource) {
        // Check if order is in a completable state
        //
        // Check if:
        // * Order has been created
        // * Order has been paid if payment is required
        // * Customer is valid if required
        //
        const cartState = this.getState();
        const orderId = get(cartState, 'response.ShoppingCart.ID');
        const order = get(cartState, 'response.ShoppingCart');
        const checkoutStatus = cartState.checkoutStatus;
        const steps = values(checkoutStatus.steps);
        const allowedInvalid = concat(['payOrder', 'placeOrder', 'shipOrder'], options.allowedInvalidSteps || []);
        const invalidSteps = steps.filter((step) => {
            // Exclude payOrder, placeOrder and shipOrder
            //
            return step && allowedInvalid.indexOf(step.name) === -1 && step.required && !step.valid;
        });
        // Collect the information needed for partial shipments
        //
        const validateOrderShipmentState = validateOrderShipment.getState();
        let partialShipmentIsPaid = false;
        if (options.partialShipment) {
            // Check the validate request was done for the correct order
            //
            if (get(validateOrderShipmentState, 'request.OrderID') !== orderId) {
                throw {
                    code: 406,
                    message: `Partial shipment has not been validated for order ${orderId}`,
                    error: 'shipOrder',
                    invalid: map$1(invalidSteps, 'name'),
                };
            }
            partialShipmentIsPaid = get(validateOrderShipmentState, 'response.IsValid', false);
        }
        // Throw an error if we have invalid steps
        // Use the first invalid step as the error
        // NOTE: These exclude pay, place and ship order
        //
        if (invalidSteps.length > 0) {
            throw {
                code: 417,
                message: 'Order is invalid',
                error: first(invalidSteps).name,
                invalid: map$1(invalidSteps, 'name'),
            };
        }
        // Check if the order has been paid if required
        // There is an override option to allow place even without payment
        // This has been introduced for specific customer requirements
        // Ideally this will be governed by a backend setting in the future
        //
        // Also for a partial shipment the ValidateOrderShipment will indicate if
        // enough has been paid to proceed with shipping the order
        // The caller needs to have dispatched ValidateOrderShipment and provide the
        // flag to the partial shipping checks
        //
        if (checkoutStatus.steps.payOrder &&
            checkoutStatus.steps.payOrder.required &&
            !checkoutStatus.steps.payOrder.valid &&
            !options.allowPlaceWithoutPayment &&
            !partialShipmentIsPaid) {
            throw {
                code: 417,
                message: 'Order needs to be paid',
                error: 'payOrder',
                invalid: map$1(invalidSteps, 'name'),
            };
        }
        // If we've reached this point we should be able to at least place the order
        //
        // Check if we can PLACE the order
        //
        if (checkoutStatus.steps.placeOrder && checkoutStatus.steps.placeOrder.required && !checkoutStatus.steps.placeOrder.valid) {
            if (checkoutStatus.steps.placeOrder.possible || options.allowPlaceWithoutPayment === true) {
                log$1('[EVA-SDK-REDUX:checkout] Placing order', recursionSource);
                const [placeAction, , placeChainPromise] = placeOrder.createFetchAction(extend(options.placeOrderPayload, { OrderID: orderId }));
                store.dispatch(placeAction);
                const orderPlaced = await placeChainPromise;
                log$1('[EVA-SDK-REDUX:checkout] Order has been placed', orderPlaced);
                // Use recursion to try to finalize the order
                // Unless the previous recursion came from here as well
                //
                if (recursionSource === 'place') {
                    throw {
                        code: 424,
                        message: 'Place order failed to progress',
                        error: 'Failed dependency',
                        invalid: map$1(invalidSteps, 'name'),
                    };
                }
                return await this.performCheckout(options, 'place');
            }
            else {
                throw {
                    code: 400,
                    message: 'Cannot place order',
                    error: 'placeOrder',
                    invalid: map$1(invalidSteps, 'name'),
                };
            }
        }
        // If a reservation order is completely valid and has been placed we need to modify the line action type to ship
        // if we want to handover the product to the customer
        //
        if (checkoutStatus.mode === OrderMode.reservation && options.autoShipReservation && invalidSteps.length === 0) {
            log$1('[EVA-SDK-REDUX:checkout] Shipping reservation order', recursionSource);
            const [modifyReservationAction, , modifyReservationChainPromise] = changeOrderLinesToCarryOut.createFetchAction({ OrderID: orderId });
            store.dispatch(modifyReservationAction);
            const reservationPreparedForShip = await modifyReservationChainPromise;
            log$1('[EVA-SDK-REDUX:checkout] Reservation order has been set to shipped', reservationPreparedForShip);
            // Use recursion to try to finalize the order
            // Unless the previous recursion came from here as well
            //
            if (recursionSource === 'auto-reservation') {
                throw {
                    code: 424,
                    message: 'Reserve order failed to progress',
                    error: 'Failed dependency',
                    invalid: map$1(invalidSteps, 'name'),
                };
            }
            return await this.performCheckout(options, 'auto-reservation');
        }
        // Check if we can SHIP the order
        //
        if (options.allowCompletionWithoutShip) {
            // Force complete the order
            //
            this.completedOrder$.next(order);
            return {
                code: 200,
                message: 'Checkout completed',
                type: 'SUCCESS',
            };
        }
        else {
            if (checkoutStatus.steps.shipOrder && checkoutStatus.steps.shipOrder.required && !checkoutStatus.steps.shipOrder.valid) {
                // For a partial shipment the possible flag will not be true
                // This is because payOrder is a requirement for shipping
                // For a partial shipment the validateOrderShipment determines if enough
                // has been paid to ship the order
                // We will recreate the logic for the shipOrder possible here with the
                // exclusion of the PayOrder step
                //
                const currentRequiredDataState = cartState;
                const currentOrderLines = get(cartState, 'response.ShoppingCart.Lines');
                let shipOrderPossible = checkoutStatus.steps.shipOrder.possible;
                if (options.partialShipment) {
                    let noCustomerIssues = true;
                    each(currentRequiredDataState.customerIssues, (issue) => {
                        noCustomerIssues = noCustomerIssues && !(issue.RequiredFor & 4);
                    });
                    let noOrderIssues = true;
                    each(currentRequiredDataState.orderIssues, (issue) => {
                        // Ignore the PayOrder step
                        //
                        noOrderIssues = noOrderIssues && (!(issue.RequiredFor & 4) || (issue.Name === 'PayOrder'));
                    });
                    let noOrderLineIssues = true;
                    each(currentRequiredDataState.orderLineIssues, (lineIssues, orderLineId) => {
                        each(lineIssues, (issue) => {
                            // Check the order line is of type ship (3)
                            //
                            const currentOrderLine = find(currentOrderLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(`${orderLineId}`, 10));
                            if (currentOrderLine && currentOrderLine.LineActionType === 3) {
                                noOrderLineIssues = noOrderLineIssues && !(issue.RequiredFor & 4);
                            }
                        });
                    });
                    shipOrderPossible = partialShipmentIsPaid && noCustomerIssues && noOrderIssues && noOrderLineIssues;
                }
                if (shipOrderPossible) {
                    // Check if we've recursed into this with a partial shipping request
                    //
                    log$1('[EVA-SDK-REDUX:checkout] Checking for partial shipment', recursionSource, options.shipOrderPayload);
                    const partialShipping = recursionSource === 'ship' && !!options.shipOrderPayload;
                    if (partialShipping) {
                        log$1('[EVA-SDK-REDUX:checkout] Partially shipped order has been detected');
                        return await this.completeCheckout(get(cartState, 'response.ShoppingCart'), options.createNewCartOnCompletion, partialShipping);
                    }
                    else {
                        // Collect the shippable lines
                        //
                        const orderLines = get(cartState, 'response.ShoppingCart.Lines');
                        // We only want to ship lines that are shippable ( line action type 3 )
                        //
                        const shippableLines = orderLines.filter((orderLine) => orderLine.LineActionType === 3);
                        const orderLinesToShip = map$1(shippableLines, (orderLine) => {
                            const shipLine = {
                                OrderLineID: orderLine.ID,
                                ShipmentBackendID: undefined,
                                TrackingCode: undefined,
                                TrackingLink: undefined,
                            };
                            return shipLine;
                        });
                        const shipRequest = {
                            // We are checking if the orderLinesToShip is empty and returning undefined
                            // in case we are returning a fashioncheque (lineActionType 0) we don't want
                            // give the back-end an empty array.
                            Lines: isEmpty(orderLinesToShip) ? undefined : orderLinesToShip,
                        };
                        const [shipAction, , shipChainPromise] = shipOrder.createFetchAction(extend(shipRequest, options.shipOrderPayload, { OrderID: orderId }));
                        store.dispatch(shipAction);
                        const orderShipped = await shipChainPromise;
                        log$1('[EVA-SDK-REDUX:checkout] Order has been shipped', orderShipped);
                    }
                    // We can't fetch the cart now because if it's been shipped
                    // a new cart will be started by the backend in getShoppingCart
                    // Instead we will call getOrder and we're going to have to be creative with the
                    // response to fake a getShoppingCart response
                    // We need the response to get the checkoutStatus to a state where
                    // completeCheckout can conclude the checkout has completed
                    //
                    log$1('[EVA-SDK-REDUX:checkout] Calling GetOrder to see if order has been shipped', recursionSource);
                    const [getOrderAction, getOrderPromise] = getOrder.createFetchAction({
                        OrderID: orderId,
                        IncludeAllPayments: true,
                    });
                    store.dispatch(getOrderAction);
                    const getOrderResponse = await getOrderPromise;
                    const shoppingCartStateBeforeUpdate = this.getState();
                    const cartResponse = {
                        ShoppingCart: getOrderResponse.Result,
                        Amounts: getOrderResponse.Amounts,
                    };
                    store.dispatch(this.createResponseAction(cartResponse));
                    // Checkout status update needs a calculate changes event
                    //
                    const changePayload = {
                        chainStarter: this.name,
                        previousCart: shoppingCartStateBeforeUpdate,
                        currentCart: this.getState(),
                    };
                    store.dispatch(this.actions.calculateChanges(changePayload));
                    // Use recursion to try to finalize the order
                    // Unless the previous recursion came from here as well
                    //
                    if (recursionSource === 'ship') {
                        throw {
                            code: 424,
                            message: 'Ship order failed to progress',
                            error: 'Failed dependency',
                            invalid: map$1(invalidSteps, 'name'),
                        };
                    }
                    return await this.performCheckout(options, 'ship');
                }
                else {
                    throw {
                        code: 400,
                        message: 'Cannot ship order',
                        error: 'shipOrder',
                        invalid: map$1(invalidSteps, 'name'),
                    };
                }
            }
            // If all has gone well we will have ended up here with no invalid steps
            //
            return await this.completeCheckout(get(cartState, 'response.ShoppingCart'), options.createNewCartOnCompletion);
        }
    }
    /**
     * Try to complete the checkout if the state of the order allows it
     * Will call startNewCart() when possible and emit the completed order
     *
     * @param {EVA.Core.OrderDto} order
     * @memberof EvaCheckoutProvider
     */
    async completeCheckout(order, createNewCartOnCompletion = false, partialShipping = false) {
        // Check if all required steps are valid
        //
        const checkoutStatus = this.getState().checkoutStatus;
        const steps = values(checkoutStatus.steps);
        const invalidSteps = steps.filter((step) => {
            const originalStep = step && step.required && !step.valid;
            // Allow ship order to be incomplete for partial shipping
            // this will allow us to complete the checkout without an error
            //
            if (partialShipping && step && step.name === 'shipOrder') {
                return false;
            }
            // Allow pay order to be incomplete for partial shipping if validateOrderShipment is true
            // this will allow us to complete the checkout without an error
            //
            if (partialShipping && step && step.name === 'payOrder') {
                const validateOrderShipmentState = validateOrderShipment.getState();
                // If we have a `validateOrderShipment` we will use its validity state for this step.
                // otherwise we will use the original one.
                //
                if (validateOrderShipmentState) {
                    const validateOrderShipmentStateStepInvalid = !get(validateOrderShipmentState, 'response.IsValid');
                    return validateOrderShipmentStateStepInvalid;
                }
                else {
                    return originalStep;
                }
            }
            return originalStep;
        });
        if (invalidSteps.length === 0) {
            this.completedOrder$.next(order);
            return {
                code: 200,
                message: 'Checkout completed',
                type: 'SUCCESS',
            };
        }
        else {
            throw {
                code: 409,
                message: 'Order is not able to complete',
                error: 'createOrder',
                type: 'ERROR',
                invalid: map$1(invalidSteps, 'name'),
            };
        }
    }
    /**
     * The extended collection of reducer functions
     *
     * @protected
     * @returns {{ [ reducerName: string ]: ( state: any, action: AnyAction ) => any }}
     * @memberof GetShoppingCart
     */
    getReducerFunctions() {
        const baseReducerFuncs = super.getReducerFunctions();
        baseReducerFuncs.pickDiscountProducts = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    const order = get(payload, 'ShoppingCart');
                    const currentLines = get(order, 'Lines', []);
                    // Find the requirements of type 'PickDiscountProduct'
                    //
                    const pickDiscountProducts = {};
                    each(requiredData === null || requiredData === void 0 ? void 0 : requiredData.OrderLines, (orderLineRequirements, orderLineId) => {
                        each(orderLineRequirements, (orderLineRequirement) => {
                            if (orderLineRequirement.Name === 'PickDiscountProduct') {
                                // It really shouldn't happen that the discount line is not in
                                // the current cart. The chain ordering should ensure this
                                // We will cast it here to force typescript to compile
                                //
                                const discountLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(orderLineId, 10));
                                const productLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === get(discountLine, 'ParentID'));
                                pickDiscountProducts[orderLineId] = {
                                    requirement: orderLineRequirement,
                                    discountOrderLine: discountLine,
                                    productOrderLine: productLine,
                                };
                            }
                        });
                    });
                    return isEmpty(pickDiscountProducts) ? null : pickDiscountProducts;
                default:
                    return state;
            }
        };
        baseReducerFuncs.productRequirementProducts = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    const order = get(payload, 'ShoppingCart');
                    const currentLines = get(order, 'Lines', []);
                    const currentProductRequirementValues = get(payload, 'AdditionalOrderData.ProductRequirements', []);
                    // Find the requirements of type 'ProductRequirement'
                    //
                    const productRequirementProducts = {};
                    each(requiredData === null || requiredData === void 0 ? void 0 : requiredData.OrderLines, (orderLineRequirements, orderLineId) => {
                        each(orderLineRequirements, (orderLineRequirement) => {
                            if (orderLineRequirement.Name === 'ProductRequirement') {
                                const productLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(orderLineId, 10));
                                productRequirementProducts[orderLineId] = {
                                    requirement: orderLineRequirement,
                                    productOrderLine: productLine,
                                    values: filter(currentProductRequirementValues, (requirement) => requirement.OrderLineID === parseInt(orderLineId, 10)),
                                };
                            }
                        });
                    });
                    return isEmpty(productRequirementProducts) ? null : productRequirementProducts;
                default:
                    return state;
            }
        };
        baseReducerFuncs.serialNumbers = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    const order = get(payload, 'ShoppingCart');
                    const currentLines = get(order, 'Lines', []);
                    // Find the requirements of type 'SerialNumber'
                    //
                    const serialNumbers = {};
                    each(requiredData === null || requiredData === void 0 ? void 0 : requiredData.OrderLines, (orderLineRequirements, orderLineId) => {
                        each(orderLineRequirements, (orderLineRequirement) => {
                            if (orderLineRequirement.Name === 'SerialNumber') {
                                const productLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(orderLineId, 10));
                                serialNumbers[orderLineId] = {
                                    requirement: orderLineRequirement,
                                    productOrderLine: productLine,
                                    serialNumber: get(productLine, 'SerialNumber'),
                                };
                            }
                        });
                    });
                    return isEmpty(serialNumbers) ? null : serialNumbers;
                default:
                    return state;
            }
        };
        baseReducerFuncs.productBundles = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    const order = get(payload, 'ShoppingCart');
                    const currentLines = get(order, 'Lines', []);
                    // Find the requirements of type 'CompleteProductBundle'
                    //
                    const productBundles = {};
                    each(requiredData === null || requiredData === void 0 ? void 0 : requiredData.OrderLines, (orderLineRequirements, orderLineId) => {
                        each(orderLineRequirements, (orderLineRequirement) => {
                            if (orderLineRequirement.Name === 'CompleteProductBundle') {
                                const productLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(orderLineId, 10));
                                productBundles[orderLineId] = {
                                    requirement: orderLineRequirement,
                                    productOrderLine: productLine,
                                };
                            }
                        });
                    });
                    return isEmpty(productBundles) ? null : productBundles;
                default:
                    return state;
            }
        };
        baseReducerFuncs.customerValid = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    // Find invalid customer requirements
                    //
                    const requirements = (requiredData === null || requiredData === void 0 ? void 0 : requiredData.Customer) || [];
                    const invalidRequirements = filter(requirements, { IsValid: false });
                    return invalidRequirements.length === 0;
                default:
                    return state;
            }
        };
        baseReducerFuncs.customerIssues = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    // Find invalid customer requirements
                    //
                    const requirements = (requiredData === null || requiredData === void 0 ? void 0 : requiredData.Customer) || [];
                    const invalidRequirements = filter(requirements, { IsValid: false });
                    return invalidRequirements;
                default:
                    return state;
            }
        };
        baseReducerFuncs.orderValid = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    // Find invalid order requirements
                    //
                    const requirements = (requiredData === null || requiredData === void 0 ? void 0 : requiredData.Order) || [];
                    const invalidOrderRequirements = filter(requirements, { IsValid: false });
                    return invalidOrderRequirements.length === 0;
                default:
                    return state;
            }
        };
        baseReducerFuncs.orderIssues = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    // Find invalid order requirements
                    //
                    const requirements = (requiredData === null || requiredData === void 0 ? void 0 : requiredData.Order) || [];
                    const invalidRequirements = filter(requirements, { IsValid: false });
                    return invalidRequirements;
                default:
                    return state;
            }
        };
        baseReducerFuncs.orderLinesValid = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    // Find invalid order line requirements
                    // Order line requirements are index by their orderLineId
                    //
                    const requirements = (requiredData === null || requiredData === void 0 ? void 0 : requiredData.OrderLines) || {};
                    const invalidRequirements = pickBy(requirements, (orderLineRequirements) => {
                        return !!find(orderLineRequirements, { IsValid: false });
                    });
                    return size(invalidRequirements) === 0;
                default:
                    return state;
            }
        };
        baseReducerFuncs.orderLineIssues = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    // Find invalid order line requirements
                    // Order line requirements are index by their orderLineId
                    //
                    const requirements = (requiredData === null || requiredData === void 0 ? void 0 : requiredData.OrderLines) || {};
                    const invalidRequirements = pickBy(requirements, (orderLineRequirements) => {
                        return !!find(orderLineRequirements, { IsValid: false });
                    });
                    // Only return invalid requirements per order line
                    //
                    return mapValues(invalidRequirements, (orderLineRequirements) => {
                        return filter(orderLineRequirements, { IsValid: false });
                    });
                default:
                    return state;
            }
        };
        /**
         * Calculate the cart changes
         *
         * Changes can be:
         * - added orderLine (inverse: cancel order line)
         * - removed orderLine (inverse: add product to order)
         * - modified orderLine
         *
         * Modify further drills down to:
         * - quantity changed
         * - product changed
         * - discount tier changed (if discount product)
         *
         * To calculate all these changes we need:
         * - previous and current cart data
         * - previous and current required data state (for current discount products)
         * - potential discounts data (for discount tiers)
         *
         * Modifications also add an inverse action that can be dispatched
         * Inverted actions should not show up as changes when dispatched
         *
         */
        baseReducerFuncs.changes = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.calculateChanges):
                    const payload = get(action, 'payload');
                    const previousOrderLines = get(payload, 'previousCart.response.ShoppingCart.Lines');
                    const currentOrderLines = get(payload, 'currentCart.response.ShoppingCart.Lines');
                    const chainStarter = get(payload, 'chainStarter');
                    const previousOrderTotalAmount = get(payload, 'previousCart.response.Amounts.Total.Amount', 0);
                    const currentOrderTotalAmount = get(payload, 'currentCart.response.Amounts.Total.Amount', 0);
                    // Start with an empty changes array
                    // If the cart is empty this will reset the changes for us
                    //
                    const changes = [];
                    log$1('[EVA-SDK-REDUX:getShoppingCart] Determine cart changes...');
                    // logger.log( '[EVA-SDK-REDUX:getShoppingCart] Change payload...', JSON.stringify( payload, null, '  ' ) );
                    // Find the removed and changed orderLines
                    //
                    each(previousOrderLines, (previousOrderLine) => {
                        const orderLineId = previousOrderLine.ID;
                        const currentOrderLine = find(currentOrderLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(`${orderLineId}`, 10));
                        // Check previous and current required data for pick discount product status
                        //
                        const previousPickDiscountProductStatus = find(get(payload, `previousCart.pickDiscountProducts`), (status) => {
                            return parseInt(get(status, 'productOrderLine.ID'), 10) === parseInt(`${orderLineId}`, 10);
                        });
                        const currentPickDiscountProductStatus = find(get(payload, `currentCart.pickDiscountProducts`), (status) => {
                            return parseInt(get(status, 'productOrderLine.ID'), 10) === parseInt(`${orderLineId}`, 10);
                        });
                        // Check if this is a pick discount line
                        //
                        const previousPickDiscountStatus = get(payload, `previousCart.pickDiscountProducts.${orderLineId}`);
                        // Check if the order line was removed
                        //
                        if (!currentOrderLine) {
                            if (previousPickDiscountStatus) {
                                // With the new V2 discount engine the order line id for the discount
                                // can change. If the new cart contains a line of Type = 1 (Discount)
                                // for the same discount id we will not emit the removed
                                //
                                const discountId = previousPickDiscountStatus.discountOrderLine.DiscountID;
                                const newDiscountLine = find(currentOrderLines, (orderLine) => orderLine.Type === 1 && orderLine.DiscountID === discountId);
                                if (!newDiscountLine) {
                                    // The pick discount order line was removed
                                    //
                                    const pickDiscountProductChange = {
                                        type: 'pick-discount-product',
                                        previousOrderLine: previousOrderLine,
                                        currentOrderLine: undefined,
                                        previousPickDiscountProductLine: get(previousPickDiscountStatus, 'productOrderLine'),
                                        currentPickDiscountProductLine: undefined,
                                        isPickDiscountProduct: true,
                                        tierChange: 'removed',
                                    };
                                    changes.push(pickDiscountProductChange);
                                }
                            }
                            // Check if there is a product missing (not discounts, or other things)
                            // Also omit product with a parent id (product bundles)
                            //
                            else if (!previousPickDiscountProductStatus && previousOrderLine.Type === 0 && !previousOrderLine.ParentID) {
                                const inversePayload = {
                                    Children: [],
                                    LineActionType: previousOrderLine.LineActionType,
                                    OrderID: previousOrderLine.OrderID,
                                    ProductID: previousOrderLine.ProductID,
                                    QuantityOrdered: previousOrderLine.TotalQuantityToShip,
                                };
                                // Selected non-discount product has been removed
                                //
                                const productChange = {
                                    type: 'remove',
                                    previousOrderLine: previousOrderLine,
                                    currentOrderLine: undefined,
                                    isPickDiscountProduct: !!previousPickDiscountStatus,
                                    inverseType: 'addProductToOrder',
                                    inversePayload,
                                };
                                changes.push(productChange);
                                log$1('[EVA-SDK-REDUX:getShoppingCart] Removed product', previousOrderLine.ID);
                            }
                        }
                        else {
                            // Product still exists in the current cart
                            // Check for quantity modifications for non-discount products
                            //
                            if (!previousPickDiscountProductStatus && currentOrderLine.TotalQuantityToShip !== previousOrderLine.TotalQuantityToShip) {
                                const inversePayload = {
                                    Lines: [{
                                            OrderLineID: currentOrderLine.ID,
                                            NewQuantityOrdered: previousOrderLine.TotalQuantityToShip,
                                        }]
                                };
                                // Order line quantity changed
                                //
                                const quantityChange = {
                                    type: 'modify-quantity',
                                    previousOrderLine: previousOrderLine,
                                    currentOrderLine: currentOrderLine,
                                    inverseType: 'modifyQuantityOrdered',
                                    inversePayload,
                                    isPickDiscountProduct: !!currentPickDiscountProductStatus,
                                };
                                changes.push(quantityChange);
                                log$1('[EVA-SDK-REDUX:getShoppingCart] Changed product quantity', currentOrderLine.ID);
                            }
                        }
                    });
                    // Find the new orderLines and changed pick discount product tiers
                    //
                    const orderDiscounts = get(payload, 'currentCart.response.AdditionalOrderData.PotentialDiscounts', []);
                    each(currentOrderLines, (currentOrderLine) => {
                        var _a, _b;
                        const orderLineId = currentOrderLine.ID;
                        const previousOrderLine = find(previousOrderLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(`${orderLineId}`, 10));
                        // Check current required data for pick discount product status
                        // This check looks for the actual product order line
                        //
                        const currentPickDiscountProductStatus = find(get(payload, `currentCart.pickDiscountProducts`), (status) => {
                            return parseInt(get(status, 'productOrderLine.ID'), 10) === parseInt(`${orderLineId}`, 10);
                        });
                        // Check if this is a pick discount line
                        //
                        let previousPickDiscountStatus = get(payload, `previousCart.pickDiscountProducts.${orderLineId}`);
                        const currentPickDiscountStatus = get(payload, `currentCart.pickDiscountProducts.${orderLineId}`);
                        const currentPickProductDiscountOptions = get(payload, `pickProductDiscountOptionsForOrder.response.OptionsPerLine.${orderLineId}`) || {};
                        // If the previous changes already emitted a 'choose' or 'new' for the same line
                        // we should not do so again
                        //
                        const previousEmit = find(state, (previousChange) => {
                            return previousChange.type === 'pick-discount-product' &&
                                previousChange.currentOrderLine &&
                                previousChange.currentOrderLine.ID === currentOrderLine.ID &&
                                (previousChange.tierChange === 'choose' || previousChange.tierChange === 'new');
                        });
                        // If there is no previous order line and this is not a discount line
                        // log it as a new added product
                        //
                        // Changes for previous lines to current lines are handled in the previous lines loop above
                        //
                        if (!previousOrderLine) {
                            // Check if this is a discount line
                            //
                            if (currentPickDiscountStatus) {
                                const currentDiscountProduct = get(currentPickDiscountStatus, 'productOrderLine');
                                const currentTier = find(currentPickProductDiscountOptions.Tiers, { IsCurrentTier: true });
                                const currentTierIndex = findIndex(currentPickProductDiscountOptions.Tiers, { IsCurrentTier: true });
                                // With the new V2 discount engine the order line id for the discount
                                // can change. If the old cart contains a line of Type = 1 (Discount)
                                // for the same discount id we will change the tier from 'new' to 'upgrade'
                                //
                                const discountId = currentPickDiscountStatus.discountOrderLine.DiscountID;
                                const oldDiscountLine = find(previousOrderLines, (orderLine) => orderLine.Type === 1 && orderLine.DiscountID === discountId);
                                let newTierChangeValue = 'new';
                                if (oldDiscountLine) {
                                    previousPickDiscountStatus = get(payload, `previousCart.pickDiscountProducts.${oldDiscountLine.ID}`);
                                    // Check if this is an upgrade or a downgrade
                                    // Taking the easier approach here of comparing total amount increase
                                    // instead of iterating all the tiers
                                    //
                                    debug(`[EVA-SDK-REDUX:getShoppingCart] Cart total amount changed from ${currentOrderTotalAmount} to ${previousOrderTotalAmount}`);
                                    if (currentOrderTotalAmount > previousOrderTotalAmount) {
                                        newTierChangeValue = 'upgrade';
                                    }
                                    else {
                                        newTierChangeValue = 'downgrade';
                                    }
                                }
                                // This is a new discount line
                                // The discount line needs to emit a choose action of the tier
                                // was found and no product was selected yet
                                //
                                const pickDiscountProductChange = {
                                    type: 'pick-discount-product',
                                    previousOrderLine: previousOrderLine,
                                    currentOrderLine: currentOrderLine,
                                    previousPickDiscountProductLine: get(previousPickDiscountStatus, 'productOrderLine'),
                                    currentPickDiscountProductLine: get(currentPickDiscountStatus, 'productOrderLine'),
                                    isPickDiscountProduct: true,
                                    tierChange: !currentDiscountProduct ? 'choose' : newTierChangeValue,
                                    currentDiscountTier: currentTier,
                                    currentTierIndex: currentTierIndex === -1 ? undefined : currentTierIndex,
                                };
                                // Emit if not previously emitted or if the previous emit was for a different tier
                                //
                                if (!previousEmit || previousEmit.currentTierIndex !== pickDiscountProductChange.currentTierIndex) {
                                    log$1('[EVA-SDK-REDUX:getShoppingCart] New pick discount discount line', currentOrderLine.ID);
                                    changes.push(pickDiscountProductChange);
                                }
                                else {
                                    log$1('[EVA-SDK-REDUX:getShoppingCart] Did not emit second pick discount line change', currentOrderLine.ID);
                                }
                            }
                            else if (!currentPickDiscountProductStatus && !currentOrderLine.ParentID) {
                                const inversePayload = {
                                    OrderID: currentOrderLine.OrderID,
                                    OrderLines: [
                                        { OrderLineID: currentOrderLine.ID },
                                    ],
                                };
                                // Order line is completely new non-discount product
                                // Discount products are not added but are available through
                                // the 'pick-discount-product' type line
                                //
                                const addProductChange = {
                                    type: 'add',
                                    previousOrderLine: previousOrderLine,
                                    currentOrderLine: currentOrderLine,
                                    inverseType: 'cancelOrder',
                                    inversePayload,
                                    isPickDiscountProduct: false,
                                };
                                changes.push(addProductChange);
                                log$1('[EVA-SDK-REDUX:getShoppingCart] New line added', currentOrderLine.ID);
                            }
                        }
                        else if (currentPickDiscountStatus && previousPickDiscountStatus) {
                            // Check the tier change
                            //
                            const previousDiscountProduct = get(previousPickDiscountStatus, 'productOrderLine');
                            const currentDiscountProduct = get(currentPickDiscountStatus, 'productOrderLine');
                            const currentOrderDiscount = find(orderDiscounts, { ID: currentOrderLine.DiscountID });
                            const currentTierId = currentPickProductDiscountOptions.CurrentTier;
                            // Sort tiers depending on setting
                            //
                            if (settings.enableDiscountTierSorting && currentOrderDiscount) {
                                log$1(`[EVA-SDK-REDUX:getShoppingCart] Sorting discount tiers...`);
                                set(currentOrderDiscount, 'ActionProperties.Tiers', sortBy(get(currentOrderDiscount, 'ActionProperties.Tiers', []), ['From', 'To']));
                            }
                            const previousTierIndex = findIndex(get(currentOrderDiscount, 'ActionProperties.Tiers'), (tier) => {
                                return indexOf(tier.ProductIDs, get(previousDiscountProduct, 'ProductID')) !== -1;
                            });
                            const currentTierIndex = findIndex(get(currentOrderDiscount, 'ActionProperties.Tiers'), (tier) => {
                                // Use the current order pick discount products defined orderline tier id if available
                                //
                                if (currentTierId) {
                                    return tier.ID === currentTierId;
                                }
                                // Fallback to chosen gift lookup
                                //
                                return indexOf(tier.ProductIDs, get(currentDiscountProduct, 'ProductID')) !== -1;
                            });
                            const previousTier = get(currentOrderDiscount, `ActionProperties.Tiers.${previousTierIndex}`);
                            const currentTier = get(currentOrderDiscount, `ActionProperties.Tiers.${currentTierIndex}`);
                            log$1(`[EVA-SDK-REDUX:getShoppingCart] Tier from ${previousTierIndex} to ${currentTierIndex}`);
                            // If one of the tiers can't be found we will not emit a change
                            //
                            let tierChange = 'none';
                            // Tier change is always none when selecting a gift
                            //
                            if (chainStarter !== 'setPickProductDiscountOptionsForOrderLine') {
                                if (!currentDiscountProduct) {
                                    // No discount product has been selected yet for the current tier
                                    //
                                    tierChange = 'choose';
                                }
                                else if (previousTierIndex < currentTierIndex) {
                                    tierChange = 'upgrade';
                                }
                                else if (previousTierIndex > currentTierIndex) {
                                    tierChange = 'downgrade';
                                }
                            }
                            // This is an updated pick discount line
                            //
                            const pickDiscountProductChange = {
                                type: 'pick-discount-product',
                                previousOrderLine: previousOrderLine,
                                currentOrderLine: currentOrderLine,
                                previousPickDiscountProductLine: get(previousPickDiscountStatus, 'productOrderLine'),
                                currentPickDiscountProductLine: get(currentPickDiscountStatus, 'productOrderLine'),
                                isPickDiscountProduct: true,
                                tierChange: tierChange,
                                previousDiscountTier: previousTier,
                                currentDiscountTier: currentTier,
                                currentTierIndex: currentTierIndex === -1 ? undefined : currentTierIndex,
                                previousTierIndex: previousTierIndex === -1 ? undefined : previousTierIndex,
                            };
                            // Emit if not previously emitted or if:
                            // - the previous emit was for a different tier
                            // - tier change type differs
                            // - if the chosen products differ
                            //
                            if (!previousEmit
                                || previousEmit.currentTierIndex !== pickDiscountProductChange.currentTierIndex
                                || previousEmit.tierChange !== pickDiscountProductChange.tierChange
                                || ((_a = previousEmit.currentPickDiscountProductLine) === null || _a === void 0 ? void 0 : _a.ID) !== ((_b = pickDiscountProductChange.currentPickDiscountProductLine) === null || _b === void 0 ? void 0 : _b.ID)) {
                                log$1('[EVA-SDK-REDUX:getShoppingCart] Modify pick discount product', currentOrderLine.ID);
                                changes.push(pickDiscountProductChange);
                            }
                            else {
                                log$1('[EVA-SDK-REDUX:getShoppingCart] Did not emit second pick discount line change', currentOrderLine.ID);
                            }
                        }
                    });
                    // Emit the changes on the subject
                    // If they differ from previous
                    //
                    if (!isEqual(state, changes)) {
                        // Emit changes if the chainStarter is a cart modifying action
                        // We won't emit for bootstrap/generic updates from getShoppingCartInfo
                        // or from attach/detach/suspend/startCheckout
                        //
                        // Special care needs to be taken if login is the chain starter
                        // Post-login we might be resuming an older cart (no notifications)
                        // or get a fresh one with welcome gifts (notify gift)
                        // or an older cart can also be amended with a welcome gift (notify gift only)
                        //
                        if (chainStarter === 'login') {
                            // Filter changes to only emit the welcome gift
                            // The welcome gift will have been added to the cart recently (5m)
                            //
                            const loginChanges = filter(changes, (change) => {
                                const creationTime = new Date(get(change, 'currentOrderLine.CreationTime'));
                                return +(creationTime) > (+(new Date()) - settings.cartChangeLoginOrderLineAge);
                            });
                            if (loginChanges.length) {
                                this.changes$.next(loginChanges);
                            }
                        }
                        else if ([
                            'createOrderFromShoppingCart',
                            'createInterbranchOrder',
                            'createReturnToSupplierOrder',
                            'detachOrder',
                            'getShoppingCart',
                            'getShoppingCartInfo',
                            'placeOrder',
                            'shipOrder',
                            'suspendOrder',
                            'logout',
                            'changeOrderLinesToCarryOut',
                            'changeOrderLinesToDelivery',
                            'changeOrderLinesToPickup',
                            'updateUser',
                        ].indexOf(chainStarter) === -1) {
                            this.changes$.next(changes);
                        }
                        // Return the new changes state
                        //
                        log$1('[EVA-SDK-REDUX:getShoppingCart] New cart changes', changes);
                        return changes;
                    }
                    else {
                        // Preserve current state
                        //
                        return state;
                    }
                default:
                    return state;
            }
        };
        /**
         * Updated the checkout status based on:
         * - ShoppingCart Lines LineActionTypes
         * - GetRequiredData requirements
         */
        baseReducerFuncs.checkoutStatus = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.calculateChanges):
                    // Retrieve required data
                    //
                    const cartState = this.getState();
                    const payload = get(action, 'payload');
                    const currentOrder = get(payload, 'currentCart.response.ShoppingCart');
                    const currentOrderLines = get(payload, 'currentCart.response.ShoppingCart.Lines');
                    const currentRequiredData = get(payload, 'currentCart.response.AdditionalOrderData.RequiredData', {});
                    const orderRequirements = get(currentRequiredData, 'Order');
                    // Collect the cart order line types currently in use
                    //
                    // None = 0,
                    // ReserveLine = 1,
                    // OrderLine = 2,
                    // ShipLine = 3,
                    // Delivery = 4,
                    //
                    // The None type will be omitted
                    //
                    const lineActionTypesInCart = uniq(filter(map$1(currentOrderLines, 'LineActionType'), (lineActionType) => lineActionType !== 0));
                    // Determine the order mode
                    //
                    let orderMode = OrderMode.empty;
                    if (!!(get(currentOrder, 'Characteristics') & 4)) {
                        orderMode = OrderMode.interbranch;
                    }
                    else if (!!(get(currentOrder, 'Characteristics') & 128)) {
                        orderMode = OrderMode.returnToSupplier;
                    }
                    else if (lineActionTypesInCart.length === 1 && lineActionTypesInCart[0] === 1) {
                        orderMode = OrderMode.reservation;
                    }
                    else if (lineActionTypesInCart.length === 1 && lineActionTypesInCart[0] === 2) {
                        orderMode = OrderMode.order;
                    }
                    else if (lineActionTypesInCart.length === 1 && lineActionTypesInCart[0] === 3) {
                        orderMode = OrderMode.sale;
                    }
                    else if (lineActionTypesInCart.length === 1 && lineActionTypesInCart[0] === 4) {
                        orderMode = OrderMode.delivery;
                    }
                    else if (lineActionTypesInCart.length > 1) {
                        orderMode = OrderMode.mixed;
                    }
                    // Start a blank status object
                    // and then collect order requirements and determine steps
                    //
                    const checkoutStatus = {
                        mode: orderMode,
                        steps: {},
                    };
                    //////////////////////////////////////////////////////////////////////
                    // Order: Customer
                    //////////////////////////////////////////////////////////////////////
                    //
                    // NOTE: If the current order has an attached customer will also
                    //       add this step even without the named requirement
                    //
                    const attachCustomerOrderRequirement = find(orderRequirements, { Name: 'Customer' });
                    const orderHasAttachedCustomer = !!get(currentOrder, 'CustomerID');
                    if (attachCustomerOrderRequirement || orderHasAttachedCustomer) {
                        // Customer needs to be both attached and valid
                        //
                        const attachCustomerIsValid = attachCustomerOrderRequirement ? attachCustomerOrderRequirement.IsValid : true;
                        const customerOrderStep = {
                            name: 'customer',
                            possible: true,
                            required: true,
                            valid: attachCustomerIsValid && cartState.customerIssues.length === 0,
                        };
                        checkoutStatus.steps.customer = customerOrderStep;
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: Create order
                    //////////////////////////////////////////////////////////////////////
                    //
                    const createOrderRequirement = find(orderRequirements, { Name: 'CreateOrder' });
                    if (createOrderRequirement) {
                        // Create order can be called multiple times but we'll set the required
                        // tag to false if already valid
                        //
                        const createOrderStep = {
                            name: 'createOrder',
                            possible: true,
                            required: !createOrderRequirement.IsValid,
                            valid: createOrderRequirement.IsValid,
                        };
                        checkoutStatus.steps.createOrder = createOrderStep;
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: Place order
                    //////////////////////////////////////////////////////////////////////
                    //
                    // Check current issues to determine if possible
                    // Possible needs to start as true for this logic to work
                    //
                    // PlaceOrder = 1,
                    // Payment = 2,
                    // Ship = 4,
                    // Invoice = 8,
                    // All = 15,
                    //
                    let noCustomerIssues = true;
                    each(cartState.customerIssues, (issue) => {
                        noCustomerIssues = noCustomerIssues && !(issue.RequiredFor & 1);
                    });
                    /**
                     * We need to make an exception for the place order requirement.
                     * As it is `RequiredFor` is place, it was lowered by the backend from `RequiredFor`=4 (`Ship`) to `RequiredFor`=1 (`Place`)
                     * To fix a different flow. This is causing an issue for RMA orders, where they cannot place the order because the `PlaceOrder` requirement is invalid
                     */
                    const placeOrderCurrentOrderIssues = filter(cartState.orderIssues, (placeOrderCurrentOrderIssue) => placeOrderCurrentOrderIssue.Name !== 'PlaceOrder');
                    let noOrderIssues = true;
                    let noOrderIssuesAfterPayment = true;
                    each(placeOrderCurrentOrderIssues, (issue) => {
                        noOrderIssues = noOrderIssues && !(issue.RequiredFor & 1);
                        noOrderIssuesAfterPayment = noOrderIssuesAfterPayment && (!(issue.RequiredFor & 1) || issue.Name === 'PayOrder');
                    });
                    let noOrderLineIssues = true;
                    each(cartState.orderLineIssues, (lineIssues, orderLineId) => {
                        each(lineIssues, (issue) => {
                            noOrderLineIssues = noOrderLineIssues && !(issue.RequiredFor & 1);
                        });
                    });
                    const placeOrderRequirement = find(orderRequirements, { Name: 'PlaceOrder' });
                    const placeOrderStep = {
                        name: 'placeOrder',
                        possible: noCustomerIssues && noOrderIssues && noOrderLineIssues,
                        required: !!placeOrderRequirement,
                        valid: placeOrderRequirement ? placeOrderRequirement.IsValid : true,
                        possibleAfterPayment: noCustomerIssues && noOrderIssuesAfterPayment && noOrderLineIssues,
                    };
                    checkoutStatus.steps.placeOrder = placeOrderStep;
                    //////////////////////////////////////////////////////////////////////
                    // Order: Pay order
                    //////////////////////////////////////////////////////////////////////
                    //
                    // Check current issues to determine if possible
                    // Possible needs to start as true for this logic to work
                    //
                    // PlaceOrder = 1,
                    // Payment = 2,
                    // Ship = 4,
                    // Invoice = 8,
                    // All = 15,
                    //
                    noCustomerIssues = true;
                    each(cartState.customerIssues, (issue) => {
                        noCustomerIssues = noCustomerIssues && !(issue.RequiredFor & 2);
                    });
                    noOrderIssues = true;
                    each(cartState.orderIssues, (issue) => {
                        noOrderIssues = noOrderIssues && !(issue.RequiredFor & 2);
                    });
                    noOrderLineIssues = true;
                    each(cartState.orderLineIssues, (lineIssues, orderLineId) => {
                        each(lineIssues, (issue) => {
                            noOrderLineIssues = noOrderLineIssues && !(issue.RequiredFor & 2);
                        });
                    });
                    /** If its a delivery order, payment is not required */
                    const isDeliverMode = orderMode === OrderMode.delivery;
                    /** If its an order, payment is not required */
                    const isOrderMode = orderMode === OrderMode.order;
                    /** If its a reservation order, payment is not required */
                    const isReservationMode = orderMode === OrderMode.reservation;
                    const payOrderRequirement = find(orderRequirements, { Name: 'PayOrder' });
                    /**
                     * The payment step is required if the payOrder requirement is there and the order is not
                     * an order thats in Deliver, Order and Reservation mode as payments there are optional
                     * @see https://springtreesolutions.github.io/eva-sdk-redux/#/concepts/checkout?id=line-action-type
                     */
                    const payOrderStepRequired = isNil(payOrderRequirement) === false && (!isDeliverMode && !isOrderMode && !isReservationMode);
                    const payOrderStep = {
                        name: 'payOrder',
                        possible: noCustomerIssues && noOrderIssues && noOrderLineIssues,
                        required: payOrderStepRequired,
                        valid: payOrderRequirement ? payOrderRequirement.IsValid : true,
                    };
                    checkoutStatus.steps.payOrder = payOrderStep;
                    //////////////////////////////////////////////////////////////////////
                    // Order: Ship order
                    //////////////////////////////////////////////////////////////////////
                    //
                    // There is no 'ShipOrder' requirement but if any lines are of type ship
                    // then the order should be shipped
                    // The IsShipped property on the order indicates completion
                    //
                    // The presence of RestrictedShippingAddress on either order or orderline
                    // makes the order un-shippable
                    //
                    // Check current issues to determine if possible
                    // Possible needs to start as true for this logic to work
                    //
                    // PlaceOrder = 1,
                    // Payment = 2,
                    // Ship = 4,
                    // Invoice = 8,
                    // All = 15,
                    //
                    noCustomerIssues = true;
                    each(cartState.customerIssues, (issue) => {
                        noCustomerIssues = noCustomerIssues && !(issue.RequiredFor & 4);
                    });
                    noOrderIssues = true;
                    each(cartState.orderIssues, (issue) => {
                        noOrderIssues = noOrderIssues && !(issue.RequiredFor & 4);
                    });
                    noOrderLineIssues = true;
                    each(cartState.orderLineIssues, (lineIssues, orderLineId) => {
                        each(lineIssues, (issue) => {
                            // Check the order line is of type ship (3)
                            //
                            const currentOrderLine = find(currentOrderLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(`${orderLineId}`, 10));
                            if (currentOrderLine && currentOrderLine.LineActionType === 3) {
                                noOrderLineIssues = noOrderLineIssues && !(issue.RequiredFor & 4);
                            }
                        });
                    });
                    const hasShippingLines = lineActionTypesInCart.includes(3);
                    /**
                     * For orders with only LineActionType 0 we want to call the shipOrder method. This was added for the returning of fashion cheques flow in the pos
                     */
                    const onlyHasLineActionTypeZero = lineActionTypesInCart.every((lineActionType) => lineActionType === 0);
                    const orderRestrictedForShipping = find(cartState.orderIssues, { Name: 'RestrictedShippingAddress' });
                    const orderLineRestrictedForShipping = find(cartState.orderLineIssues, (lineIssues) => find(lineIssues, { Name: 'RestrictedShippingAddress' }) !== undefined);
                    const orderLines = get(currentOrder, 'Lines') || [];
                    /**
                     * We will use this property to decide whether the order has been shipped or not, this will be used as the 'valid' property in the 'shipOrder' step
                     * based on whether the order is full of 'LineActionType' 0 orderLines, we will be running different logic due to a return flow of fashion cheque in pos
                     */
                    let isShipped = false;
                    if (onlyHasLineActionTypeZero) {
                        isShipped = orderLines
                            .filter((line) => line.LineActionType === 0 && !line.IsCancelled)
                            .every((line) => line.IsShipped);
                    }
                    else {
                        isShipped = orderLines
                            .filter((line) => line.LineActionType === 3 && !line.IsCancelled)
                            .every((line) => line.IsShipped);
                    }
                    const shipOrderStep = {
                        name: 'shipOrder',
                        possible: noCustomerIssues && noOrderIssues && noOrderLineIssues,
                        required: hasShippingLines || onlyHasLineActionTypeZero,
                        valid: !orderRestrictedForShipping && !orderLineRestrictedForShipping && isShipped,
                    };
                    checkoutStatus.steps.shipOrder = shipOrderStep;
                    //////////////////////////////////////////////////////////////////////
                    // Order: Return to supplier
                    //////////////////////////////////////////////////////////////////////
                    //
                    if (!!(get(currentOrder, 'Characteristics') & 128)) {
                        const returnReasonRequirement = find(orderRequirements, { Name: 'ReturnReason' });
                        const shipToOrganisationRequirement = find(orderRequirements, { Name: 'ShipToOrganizationUnitID' });
                        checkoutStatus.steps.returnToSupplier = {
                            name: 'returnToSupplier',
                            possible: true,
                            required: true,
                            valid: get(returnReasonRequirement, 'IsValid', true) &&
                                get(shipToOrganisationRequirement, 'IsValid', true),
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: Interbranch
                    //////////////////////////////////////////////////////////////////////
                    //
                    if (!!(get(currentOrder, 'Characteristics') & 4)) {
                        const shipToOrganisationRequirement = find(orderRequirements, { Name: 'ShipToOrganizationUnitID' });
                        const soldToOrganisationRequirement = find(orderRequirements, { Name: 'SoldToOrganizationUnitID' });
                        checkoutStatus.steps.interbranch = {
                            name: 'interbranch',
                            possible: true,
                            required: true,
                            valid: get(shipToOrganisationRequirement, 'IsValid', true) &&
                                get(soldToOrganisationRequirement, 'IsValid', true),
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: BillingAddress
                    //////////////////////////////////////////////////////////////////////
                    //
                    const billingAddressRequirements = filter(orderRequirements, (requirement) => requirement.Name.startsWith('BillingAddress.'));
                    if (isEmpty(billingAddressRequirements) === false) {
                        const billingAddressRequirementsValid = billingAddressRequirements.every((billingAddressRequirement) => billingAddressRequirement.IsValid);
                        checkoutStatus.steps.billingAddress = {
                            name: 'billingAddress',
                            possible: true,
                            required: true,
                            valid: billingAddressRequirementsValid,
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: ShippingAddress
                    //////////////////////////////////////////////////////////////////////
                    //
                    const shippingAddressRequirements = filter(orderRequirements, (requirement) => requirement.Name.startsWith('ShippingAddress.'));
                    if (isEmpty(shippingAddressRequirements) === false) {
                        const shippingAddressRequirementsValid = shippingAddressRequirements.every((shippingAddressRequirement) => shippingAddressRequirement.IsValid);
                        checkoutStatus.steps.shippingAddress = {
                            name: 'shippingAddress',
                            possible: true,
                            required: true,
                            valid: shippingAddressRequirementsValid,
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: Insurance
                    //////////////////////////////////////////////////////////////////////
                    //
                    const insureOrderRequirement = find(orderRequirements, { Name: 'RequestInsurancesForOrder' });
                    if (insureOrderRequirement) {
                        checkoutStatus.steps.insurance = {
                            name: 'insurance',
                            possible: true,
                            required: true,
                            valid: insureOrderRequirement.IsValid,
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: EmployeeVerification
                    //////////////////////////////////////////////////////////////////////
                    //
                    const employeeVerificationRequirement = find(orderRequirements, { Name: 'EmployeeVerification' });
                    if (employeeVerificationRequirement) {
                        checkoutStatus.steps.employeeVerification = {
                            name: 'employeeVerification',
                            possible: true,
                            required: true,
                            valid: employeeVerificationRequirement.IsValid,
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: VerifyCustomer
                    //////////////////////////////////////////////////////////////////////
                    //
                    const verifyCustomerRequirement = find(orderRequirements, { Name: 'VerifyCustomer' });
                    if (verifyCustomerRequirement) {
                        checkoutStatus.steps.insurance = {
                            name: 'verifyCustomer',
                            possible: true,
                            required: true,
                            valid: verifyCustomerRequirement.IsValid,
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // OrderLine: Discounts
                    //////////////////////////////////////////////////////////////////////
                    //
                    // Only check if we have discount product lines
                    //
                    if (cartState.pickDiscountProducts) {
                        const discountProductStep = {
                            name: 'discountProduct',
                            possible: true,
                            required: true,
                            valid: !find(cartState.orderLineIssues, (orderLineIssues) => {
                                // Check all order lines for the absence of pick discount
                                // product issues
                                //
                                return !!find(orderLineIssues, { Name: 'PickDiscountProduct' });
                            }),
                        };
                        checkoutStatus.steps.discountProduct = discountProductStep;
                    }
                    //////////////////////////////////////////////////////////////////////
                    // OrderLine: Product requirements
                    //////////////////////////////////////////////////////////////////////
                    //
                    // Only check if we have product requirement
                    //
                    if (cartState.productRequirementProducts) {
                        const productRequirementsProductStep = {
                            name: 'productRequirements',
                            possible: true,
                            required: true,
                            valid: !find(cartState.orderLineIssues, (orderLineIssues) => {
                                // Check all order lines for the absence of
                                // product requirement issues
                                //
                                return !!find(orderLineIssues, { Name: 'ProductRequirement' });
                            }),
                        };
                        checkoutStatus.steps.productRequirements = productRequirementsProductStep;
                    }
                    //////////////////////////////////////////////////////////////////////
                    // OrderLine: Serial number
                    //////////////////////////////////////////////////////////////////////
                    //
                    if (cartState.serialNumbers) {
                        const serialNumbersStep = {
                            name: 'serialNumber',
                            possible: true,
                            required: true,
                            valid: !find(cartState.orderLineIssues, (orderLineIssues) => {
                                // Check all order lines for the absence of
                                // product requirement issues
                                //
                                return !!find(orderLineIssues, { Name: 'SerialNumber' });
                            }),
                        };
                        checkoutStatus.steps.serialNumber = serialNumbersStep;
                    }
                    //////////////////////////////////////////////////////////////////////
                    // OrderLine: CompleteProductBundle
                    //////////////////////////////////////////////////////////////////////
                    //
                    if (cartState.productBundles) {
                        const productBundlesStep = {
                            name: 'productBundles',
                            possible: true,
                            required: true,
                            valid: !find(cartState.orderLineIssues, (orderLineIssues) => {
                                // Check all order lines for the absence of
                                // product requirement issues
                                //
                                return !!find(orderLineIssues, { Name: 'CompleteProductBundle' });
                            }),
                        };
                        checkoutStatus.steps.productBundles = productBundlesStep;
                    }
                    return checkoutStatus;
                default:
                    return state;
            }
        };
        /**
         * Collect the unique line types present on the order
         */
        baseReducerFuncs.orderLineActionTypes = (state = [], action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    // Return list of unique line action types in the order
                    // Array size of 1 means entire order is the same
                    // Type None is removed from the list
                    //
                    const payload = get(action, 'payload');
                    return uniq(filter(map$1(get(payload, 'ShoppingCart.Lines', []), 'LineActionType'), (lineActionType) => lineActionType !== 0));
                default:
                    return state;
            }
        };
        /**
         * Determine the overall order status
         */
        baseReducerFuncs.status = (state = OrderStatus.new, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    // Return list of unique line action types in the order
                    // Array size of 1 means entire order is the same
                    //
                    const payload = get(action, 'payload');
                    const order = get(payload, 'ShoppingCart');
                    if (order) {
                        if (order.IsCompleted) {
                            return OrderStatus.completed;
                        }
                        else if (order.IsShipped) {
                            return OrderStatus.shipped;
                        }
                        else if (order.IsPaid) {
                            return OrderStatus.paid;
                        }
                        else if (get(payload, 'Amounts.Paid.InTax', 0) > 0) {
                            return OrderStatus.partial_payment;
                        }
                        else {
                            return OrderStatus.new;
                        }
                    }
                    else {
                        return state;
                    }
                default:
                    return state;
            }
        };
        /**
         * Count products ordered for certain types
         */
        baseReducerFuncs.count = (state = [], action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const count = {
                        discount: 0,
                        non_discount: 0,
                        giftcard: 0,
                        bundle: 0,
                        configurable: 0,
                        service: 0,
                        total: 0,
                    };
                    const payload = get(action, 'payload');
                    const order = get(payload, 'ShoppingCart');
                    each(get(order, 'Lines', []), (orderLine) => {
                        if (orderLine.Product) {
                            // Add to the total
                            //
                            count.total += orderLine.TotalQuantityToShip;
                            // If an orderLine with a product in it is linked to a discount it is
                            // considered to be a gift
                            //
                            if (orderLine.CustomOrderLineType === 'DISCOUNTPRODUCT') {
                                count.discount += orderLine.TotalQuantityToShip;
                            }
                            switch (orderLine.Product.Type) {
                                case 8:
                                    {
                                        count.giftcard += orderLine.TotalQuantityToShip;
                                    }
                                    break;
                                case 16:
                                    {
                                        count.service += orderLine.TotalQuantityToShip;
                                    }
                                    break;
                                case 2048:
                                    {
                                        count.bundle += orderLine.TotalQuantityToShip;
                                    }
                                    break;
                                case 16384:
                                    {
                                        count.configurable += orderLine.TotalQuantityToShip;
                                    }
                                    break;
                            }
                        }
                    });
                    // For convenience non-discount is added
                    //
                    count.non_discount = count.total - count.discount;
                    return count;
                default:
                    return state;
            }
        };
        return baseReducerFuncs;
    }
}
// Export the state handling singleton
//
const getShoppingCart = new GetShoppingCart();
// Setup the Saga implementation
//
function* fetchCall$7t(action) {
    var _a, _b;
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    fetchActionPayload.request = Object.assign({ AdditionalOrderDataOptions: settings.shoppingCartOptions }, fetchActionPayload.request);
    try {
        // Check the getShoppingCartInfo state
        // If no OrderID is present we should empty the cart
        //
        let currentOrderId = settings.currentOrderId;
        const chainStarter = action.payload.chainStarter;
        // Preserve a copy of the cart data prior to updating it
        //
        const shoppingCartStateBeforeUpdate = getShoppingCart.getState();
        // If we have an order ID or the logged in user is an employee we will
        // fetch the shopping cart. If the cart is currently empty this first
        // call to getShoppingCart will create the order.
        // We need an empty order to be able to change the order to for example an
        // interbranch order.
        // This behavior makes sense for employee apps like POS/Sales but not for
        // web shops which would quickly spawn many empty orders
        //
        if (currentOrderId) {
            const data = yield call([getShoppingCart, 'fetchData'], action);
            yield put(getShoppingCart.actions.response(data));
            // Resolve the fetch and chain promise
            //
            fetchActionPayload.fetchResolve(data);
            // Update current order id in case it was just created
            //
            const newOrderId = get(data, 'ShoppingCart.ID');
            if (newOrderId) {
                settings.currentOrderId = newOrderId;
                currentOrderId = newOrderId;
            }
            // Generate and reduce the cart changes
            //
            const changePayload = {
                chainStarter: chainStarter,
                previousCart: shoppingCartStateBeforeUpdate,
                currentCart: getShoppingCart.getState(),
            };
            yield put(getShoppingCart.actions.calculateChanges(changePayload));
            // Resolve the chain
            //
            fetchActionPayload.chainResolve([data]);
        }
        else {
            log$1('[EVA-SDK-REDUX:getShoppingCart] Current cart is empty');
            const emptyCart = {
                ShoppingCart: {
                    ID: 0,
                    Type: 0,
                    CreatedByID: 0,
                    CreationTime: '0001-01-01T00:00:00Z',
                    Payments: [],
                    Lines: [],
                    TotalAmountInTax: 0,
                    TotalAmountInvoiced: 0,
                    TaxTable: [],
                    IsCompleted: false,
                    IsDelivered: false,
                    IsShipped: false,
                    IsPaid: false,
                    IsCaptured: false,
                    IsInvoiced: false,
                    IsPlaced: false,
                    Status: 0,
                    TotalItems: 0,
                    NeedsExtendedCustomerInformation: false,
                    HasDelivery: false,
                    Properties: 0,
                    Characteristics: 0,
                    OriginatingOrganizationUnitID: 1,
                    PickupOrganizationUnitID: 1,
                    SoldFromOrganizationUnitID: 1,
                    CurrencyID: 'EUR',
                    PreferredPriceDisplayMode: 0,
                    IsTaxExempt: false,
                    UserAgentApplication: '',
                    AllowPartialFulfillment: false,
                    IgnoreDiscounts: false,
                    IgnoreShippingCosts: false,
                    IgnoreGiftWrappingCosts: false,
                    IsConfirmed: false,
                    HasReturns: false,
                },
                Amounts: {
                    Open: {
                        InTax: 0,
                        PendingInTax: 0,
                        Capturable: 0
                    },
                    OpenAmountIsZero: true,
                    Total: {
                        Amount: 0,
                        InTax: 0,
                        Tax: 0,
                    },
                    UnroundedTotal: {
                        Amount: 0,
                        InTax: 0,
                        Tax: 0,
                    },
                    Paid: {
                        Pending: 0,
                        Amount: 0,
                        Captured: 0,
                    },
                    Taxes: [],
                    Types: {
                        Discount: { Amount: 0, InTax: 0, Tax: 0 },
                        DownPayment: { Amount: 0, InTax: 0, Tax: 0 },
                        ExtraCosts: { Amount: 0, InTax: 0, Tax: 0 },
                        GiftWrappingCosts: { Amount: 0, InTax: 0, Tax: 0 },
                        NormalProduct: { Amount: 0, InTax: 0, Tax: 0 },
                        PriceCorrection: { Amount: 0, InTax: 0, Tax: 0 },
                        ReturnCosts: { Amount: 0, InTax: 0, Tax: 0 },
                        Service: { Amount: 0, InTax: 0, Tax: 0 },
                        ShippingCosts: { Amount: 0, InTax: 0, Tax: 0 },
                    },
                    Discounts: {
                        PerDiscount: [],
                        TotalDiscountAmount: 0
                    }
                },
                Metadata: {},
                AdditionalOrderData: {},
            };
            // Fetch prefigure discounts for an empty cart depending on the additional data settings
            // This should be enabled by default
            //
            if ((_a = fetchActionPayload.request.AdditionalOrderDataOptions) === null || _a === void 0 ? void 0 : _a.IncludePrefigureDiscounts) {
                // Check which discount engine is active and call potential discount or prefigure discounts
                //
                const applicationConfiguration = ((_b = getApplicationConfiguration.getState().response) === null || _b === void 0 ? void 0 : _b.Configuration) || {};
                if (true === applicationConfiguration['EVA:NewDiscountEngineEnabled']) {
                    log$1('[EVA-SDK-REDUX:getShoppingCart] Fetching prefigure discounts for empty cart');
                    const [prefigureDiscountsAction] = prefigureDiscounts.createFetchAction({}, chainStarter, undefined, undefined, undefined, undefined, chainContext);
                    const prefigureDiscountsResponse = yield call([prefigureDiscounts, 'fetchData'], prefigureDiscountsAction);
                    emptyCart.AdditionalOrderData.PrefiguredDiscounts = prefigureDiscountsResponse.Results;
                }
            }
            yield put(getShoppingCart.actions.response(emptyCart));
            // Generate and reduce the cart changes
            //
            const changePayload = {
                chainStarter: chainStarter,
                previousCart: shoppingCartStateBeforeUpdate,
                currentCart: getShoppingCart.getState(),
            };
            yield put(getShoppingCart.actions.calculateChanges(changePayload));
            // Resolve the fetch promise
            //
            fetchActionPayload.fetchResolve(emptyCart);
            fetchActionPayload.chainResolve([emptyCart, undefined]);
        }
    }
    catch (err) {
        yield put(getShoppingCart.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7t() {
    yield takeEvery(getType(getShoppingCart.actions.fetch), fetchCall$7t);
}

/**
 * Implements CreateOrder fetch for the Redux store
 *
 * @export
 * @class CreateOrder
 * @extends {StoreFetchableData<EVA.Core.CreateOrder, EVA.Core.CreateOrderResponse>}
 */
class CreateOrder extends StoreFetchableData {
    /**
     * Creates an instance of CreateOrder
     *
     * @memberof CreateOrder
     */
    constructor() {
        super('createOrder', '/message/CreateOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateOrder
     */
    *saga() {
        yield fork(listener$7s);
    }
}
// Export the state handling singleton
//
const createOrder = new CreateOrder();
// Setup the Saga implementation
//
function* fetchCall$7s(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createOrder, 'fetchData'], action);
        yield put(createOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7s() {
    yield takeEvery(getType(createOrder.actions.fetch), fetchCall$7s);
}

/**
 * Implements CancelOrder fetch for the Redux store
 *
 * @export
 * @class CancelOrder
 * @extends {StoreFetchableData<EVA.Core.CancelOrder, EVA.Core.CancelOrderResponse>}
 */
class CancelOrder extends StoreFetchableData {
    /**
     * Creates an instance of CancelOrder
     *
     * @memberof CancelOrder
     */
    constructor() {
        super('cancelOrder', '/message/CancelOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CancelOrder
     */
    *saga() {
        yield fork(listener$7r);
    }
}
// Export the state handling singleton
//
const cancelOrder = new CancelOrder();
// Setup and export the Saga implementation
//
function* fetchCall$7r(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([cancelOrder, 'fetchData'], action);
        yield put(cancelOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // CancelOrder is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(cancelOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7r() {
    yield takeEvery(getType(cancelOrder.actions.fetch), fetchCall$7r);
}

/**
 * Implements ChangeOrderLinesToDelivery fetch for the Redux store
 *
 * @export
 * @class ChangeOrderLinesToDelivery
 * @extends {StoreFetchableData<EVA.Core.ChangeOrderLinesToDelivery, EVA.Core.ShoppingCartResponse>}
 */
class ChangeOrderLinesToDelivery extends StoreFetchableData {
    /**
     * Creates an instance of ChangeOrderLinesToDelivery
     *
     * @memberof ChangeOrderLinesToDelivery
     */
    constructor() {
        super('changeOrderLinesToDelivery', '/message/ChangeOrderLinesToDelivery');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ChangeOrderLinesToDelivery
     */
    *saga() {
        yield fork(listener$7q);
    }
}
// Export the state handling singleton
//
const changeOrderLinesToDelivery = new ChangeOrderLinesToDelivery();
// Setup and export the Saga implementation
//
function* fetchCall$7q(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([changeOrderLinesToDelivery, 'fetchData'], action);
        yield put(changeOrderLinesToDelivery.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // ChangeOrderLinesToDelivery is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(changeOrderLinesToDelivery.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7q() {
    yield takeEvery(getType(changeOrderLinesToDelivery.actions.fetch), fetchCall$7q);
}

/**
 * Implements ChangeOrderLinesToPickup fetch for the Redux store
 *
 * @export
 * @class ChangeOrderLinesToPickup
 * @extends {StoreFetchableData<EVA.Core.ChangeOrderLinesToPickup, EVA.Core.ShoppingCartResponse>}
 */
class ChangeOrderLinesToPickup extends StoreFetchableData {
    /**
     * Creates an instance of ChangeOrderLinesToPickup
     *
     * @memberof ChangeOrderLinesToPickup
     */
    constructor() {
        super('changeOrderLinesToPickup', '/message/ChangeOrderLinesToPickup');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ChangeOrderLinesToPickup
     */
    *saga() {
        yield fork(listener$7p);
    }
}
// Export the state handling singleton
//
const changeOrderLinesToPickup = new ChangeOrderLinesToPickup();
// Setup and export the Saga implementation
//
function* fetchCall$7p(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([changeOrderLinesToPickup, 'fetchData'], action);
        yield put(changeOrderLinesToPickup.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // ChangeOrderLinesToPickup is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(changeOrderLinesToPickup.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7p() {
    yield takeEvery(getType(changeOrderLinesToPickup.actions.fetch), fetchCall$7p);
}

/**
 * Implements UpdateOrderBillingAddress fetch for the Redux store
 *
 * @export
 * @class UpdateOrderBillingAddress
 * @extends {StoreFetchableData<EVA.Core.UpdateOrderBillingAddress, EVA.Core.UpdateOrderAddressesResponse>}
 */
class UpdateOrderBillingAddress extends StoreFetchableData {
    /**
     * Creates an instance of UpdateOrderBillingAddress
     *
     * @memberof UpdateOrderBillingAddress
     */
    constructor() {
        super('updateOrderBillingAddress', '/message/UpdateOrderBillingAddress');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateOrderBillingAddress
     */
    *saga() {
        yield fork(listener$7o);
    }
}
// Export the state handling singleton
//
const updateOrderBillingAddress = new UpdateOrderBillingAddress();
// Setup the Saga implementation
//
function* fetchCall$7o(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([updateOrderBillingAddress, 'fetchData'], action);
        yield put(updateOrderBillingAddress.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(updateOrderBillingAddress.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7o() {
    yield takeEvery(getType(updateOrderBillingAddress.actions.fetch), fetchCall$7o);
}

/**
 * Implements UpdateOrderShippingAddress fetch for the Redux store
 *
 * @export
 * @class UpdateOrderShippingAddress
 * @extends {StoreFetchableData<EVA.Core.UpdateOrderShippingAddress, EVA.Core.UpdateOrderAddressesResponse>}
 */
class UpdateOrderShippingAddress extends StoreFetchableData {
    /**
     * Creates an instance of UpdateOrderShippingAddress
     *
     * @memberof UpdateOrderShippingAddress
     */
    constructor() {
        super('updateOrderShippingAddress', '/message/UpdateOrderShippingAddress');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateOrderShippingAddress
     */
    *saga() {
        yield fork(listener$7n);
    }
}
// Export the state handling singleton
//
const updateOrderShippingAddress = new UpdateOrderShippingAddress();
// Setup the Saga implementation
//
function* fetchCall$7n(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([updateOrderShippingAddress, 'fetchData'], action);
        yield put(updateOrderShippingAddress.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(updateOrderShippingAddress.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7n() {
    yield takeEvery(getType(updateOrderShippingAddress.actions.fetch), fetchCall$7n);
}

/**
 * Implements GetShoppingCartInfo fetch for the Redux store
 *
 * @export
 * @class GetShoppingCartInfo
 * @extends {StoreFetchableData<EVA.Core.GetShoppingCartInfo, EVA.Core.GetShoppingCartInfoResponse>}
 */
class GetShoppingCartInfo extends StoreFetchableData {
    /**
     * Creates an instance of GetShoppingCartInfo
     *
     * @memberof GetShoppingCartInfo
     */
    constructor() {
        super('getShoppingCartInfo', '/message/GetShoppingCartInfo');
        /**
         * Helper method to get access to the pending cart update and actually
         * trigger the action dispatch when there are no more pending operations
         *
         * @private
         */
        this.updateCart = ({ chainStarter, chainContext }) => {
            if (!this.pendingCartUpdate) {
                this.pendingCartUpdate = getShoppingCartInfo.createFetchAction(undefined, chainStarter, undefined, undefined, undefined, undefined, chainContext);
            }
            const cartActionAndPromises = this.pendingCartUpdate;
            // When the last operation has completed trigger the actual cart update
            //
            setTimeout(() => {
                // Mark the calling application as completed
                //
                this.pendingCartOperations$.next(Math.max(0, this.pendingCartOperations$.getValue() - 1));
                if (this.pendingCartUpdate && this.pendingCartOperations$.getValue() === 0) {
                    log$1('[EVA-SDK-REDUX:getShoppingCartInfo] Pending operations completed. Now updating cart...');
                    this.pendingCartUpdate = undefined;
                    store.dispatch(cartActionAndPromises[0]);
                }
            }, 500);
            return cartActionAndPromises;
        };
        /**
         * The amount of cart add/update calls pending
         *
         * @type {BehaviorSubject<number>}
         */
        this.pendingCartOperations$ = new BehaviorSubject(0);
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetShoppingCartInfo
     */
    *saga() {
        yield fork(listener$7m);
    }
    /**
     * Helper method to consistently get the current order id
     *
     * @returns
     */
    getCurrentOrderId() {
        const currentOrderId = get(getShoppingCartInfo.getState(), 'request.OrderID') || settings.currentOrderId || 0;
        return currentOrderId;
    }
    /**
     * Helper method to add items to the cart while automatically managing order creation
     *
     * @param {TAddToCartAction} action
     * @returns
     */
    async addToCart(action) {
        // Check we have a valid current order id
        // Create a new order if needed
        //
        let currentOrderId = this.getCurrentOrderId();
        const isCompleted = get(getShoppingCart.getState(), 'response.ShoppingCart.IsCompleted') === true;
        // Create a new order if needed but also ensure we do not have multiple
        // requests to create an order in-flight
        //
        if (!currentOrderId || isCompleted) {
            if (!this.pendingOrderCreation) {
                const [createOrderAction] = createOrder.createFetchAction({
                    Type: action.orderType || 0 // EVA.Core.OrderTypes.Sales,
                });
                this.pendingOrderCreation = createOrder.fetchData(createOrderAction);
            }
            const newOrder = await this.pendingOrderCreation;
            this.pendingOrderCreation = undefined;
            settings.currentOrderId = newOrder.OrderID;
            currentOrderId = settings.currentOrderId;
        }
        // Amend the request with the OrderID as needed
        //
        const requestPayload = Object.assign({}, action.request);
        requestPayload.OrderID = currentOrderId;
        // Create the fetch action and use it to call the service directly
        //
        let cartOperationPromise;
        let chainContext;
        this.pendingCartOperations$.next(this.pendingCartOperations$.getValue() + 1);
        try {
            const [addToCartAction] = action.reducer.createFetchAction(requestPayload, undefined, action.timeout);
            chainContext = addToCartAction.payload.chainContext;
            cartOperationPromise = action.reducer.fetchData(addToCartAction, action.timeout);
            await cartOperationPromise;
        }
        catch (error) {
            this.pendingCartOperations$.next(Math.max(0, this.pendingCartOperations$.getValue() - 1));
            throw error;
        }
        // Create and dispatch the shopping cart info action
        // returning the call and chain promise to the caller
        //
        const [, cartUpdatePromise, cartUpdateChainPromise] = this.updateCart({ chainStarter: action.reducer.name, chainContext });
        const operationResult = {
            cartOperationPromise,
            cartUpdatePromise,
            cartUpdateChainPromise,
        };
        return operationResult;
    }
    /**
     * Helper method to modify the current order
     *
     * @param {TModifyCartAction} action
     * @returns
     */
    async modifyCart(action) {
        // Check we have a valid current order id
        // Throw an error if we don't
        //
        const currentOrderId = this.getCurrentOrderId();
        if (!currentOrderId) {
            throw new Error('No current order');
        }
        // Amend the request with the OrderID as needed
        //
        const requestPayload = Object.assign({}, action.request);
        if (action.reducer === attachCustomerToOrder ||
            action.reducer === cancelOrder ||
            action.reducer === changeOrderLinesToCarryOut ||
            action.reducer === changeOrderLinesToDelivery ||
            action.reducer === changeOrderLinesToPickup ||
            action.reducer === detachCustomerFromOrder ||
            action.reducer === prepareOrderForCheckout ||
            action.reducer === updateOrderBillingAddress ||
            action.reducer === updateOrderShippingAddress) {
            requestPayload.OrderID = currentOrderId;
        }
        // Create the fetch action and use it to call the service directly
        //
        let cartOperationPromise;
        let chainContext;
        this.pendingCartOperations$.next(this.pendingCartOperations$.getValue() + 1);
        try {
            const [modifyCartAction] = action.reducer.createFetchAction(requestPayload, undefined, action.timeout);
            chainContext = modifyCartAction.payload.chainContext;
            cartOperationPromise = action.reducer.fetchData(modifyCartAction, action.timeout);
            await cartOperationPromise;
        }
        catch (error) {
            this.pendingCartOperations$.next(Math.max(0, this.pendingCartOperations$.getValue() - 1));
            throw error;
        }
        // Create and dispatch the shopping cart info action
        // returning the call and chain promise to the caller
        //
        const [, cartUpdatePromise, cartUpdateChainPromise] = this.updateCart({ chainStarter: action.reducer.name, chainContext });
        const operationResult = {
            cartOperationPromise,
            cartUpdatePromise,
            cartUpdateChainPromise,
        };
        return operationResult;
    }
}
// Export the state handling singleton
//
const getShoppingCartInfo = new GetShoppingCartInfo();
// Setup the Saga implementation
//
function* fetchCall$7m(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const orderId = get(action, 'payload.request.OrderID', settings.currentOrderId || 0);
        const chainStarter = action.payload.chainStarter;
        settings.currentOrderId = orderId;
        set(action, 'payload.request.OrderID', orderId);
        // We need to support a request to unset the current cart/order
        // Setting the order ID to 0 will accomplish this.
        // It is also the default on the backend for dummy carts/orders
        //
        let data = {
            OrderID: orderId,
            CurrencyID: 'EUR',
            Metadata: {
                ExternalIDs: {},
                UnresolvedExternalIDs: {},
            },
        };
        if (orderId) {
            data = yield call([getShoppingCartInfo, 'fetchData'], action);
            yield put(getShoppingCartInfo.actions.response(data));
        }
        else {
            yield put(getShoppingCartInfo.actions.response(data));
        }
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        log$1('[EVA-SDK-REDUX:getShoppingCartInfo] New state', data);
        // Fetch shopping cart
        // If this is an empty cart the getShoppingCart will empty itself
        //
        const [getShoppingCartAction, , getShoppingCartChainPromise] = getShoppingCart.createFetchAction({ OrderID: orderId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(getShoppingCartInfo.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7m() {
    yield takeEvery(getType(getShoppingCartInfo.actions.fetch), fetchCall$7m);
}

/**
 * Implements AddBundleProductToOrder fetch for the Redux store
 *
 * @export
 * @class AddBundleProductToOrder
 * @extends {StoreFetchableData<EVA.Core.AddBundleProductToOrder, EVA.Core.AddBundleProductToOrderResponse>}
 */
class AddBundleProductToOrder extends StoreFetchableData {
    /**
     * Creates an instance of AddBundleProductToOrder
     *
     * @memberof AddBundleProductToOrder
     */
    constructor() {
        super('addBundleProductToOrder', '/message/AddBundleProductToOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AddBundleProductToOrder
     */
    *saga() {
        yield fork(listener$7l);
    }
}
// Export the state handling singleton
//
const addBundleProductToOrder = new AddBundleProductToOrder();
// Setup and Saga implementation
//
function* fetchCall$7l(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([addBundleProductToOrder, 'fetchData'], action);
        yield put(addBundleProductToOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // AddBundleProductToOrder is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(addBundleProductToOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7l() {
    yield takeEvery(getType(addBundleProductToOrder.actions.fetch), fetchCall$7l);
}

/**
 * Implements AddDiscountCouponToOrder fetch for the Redux store
 *
 * @export
 * @class AddDiscountCouponToOrder
 * @extends {StoreFetchableData<EVA.Core.AddDiscountCouponToOrder, EVA.Core.ShoppingCartResponse>}
 */
class AddDiscountCouponToOrder extends StoreFetchableData {
    /**
     * Creates an instance of AddDiscountCouponToOrder
     *
     * @memberof AddDiscountCouponToOrder
     */
    constructor() {
        super('addDiscountCouponToOrder', '/message/AddDiscountCouponToOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AddDiscountCouponToOrder
     */
    *saga() {
        yield fork(listener$7k);
    }
}
// Export the state handling singleton
//
const addDiscountCouponToOrder = new AddDiscountCouponToOrder();
// Setup and Saga implementation
//
function* fetchCall$7k(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([addDiscountCouponToOrder, 'fetchData'], action);
        yield put(addDiscountCouponToOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // AddDiscountCouponToOrder is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(addDiscountCouponToOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7k() {
    yield takeEvery(getType(addDiscountCouponToOrder.actions.fetch), fetchCall$7k);
}

/**
 * Implements AddLabelToFullStockCount fetch for the Redux store
 *
 * @export
 * @class AddLabelToFullStockCount
 * @extends {StoreFetchableData<EVA.Core.AddLabelToFullStockCount, EVA.Core.AddLabelToFullStockCountResponse>}
 */
class AddLabelToFullStockCount extends StoreFetchableData {
    /**
     * Creates an instance of AddLabelToFullStockCount
     *
     * @memberof AddLabelToFullStockCount
     */
    constructor() {
        super('addLabelToFullStockCount', '/message/AddLabelToFullStockCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AddLabelToFullStockCount
     */
    *saga() {
        yield fork(listener$7j);
    }
}
// Export the state handling singleton
//
const addLabelToFullStockCount = new AddLabelToFullStockCount();
// Setup the Saga implementation
//
function* fetchCall$7j(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([addLabelToFullStockCount, 'fetchData'], action);
        yield put(addLabelToFullStockCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(addLabelToFullStockCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7j() {
    yield takeEvery(getType(addLabelToFullStockCount.actions.fetch), fetchCall$7j);
}

/**
 * Implements AddManualDiscountToOrder fetch for the Redux store
 *
 * @export
 * @class AddManualDiscountToOrder
 * @extends {StoreFetchableData<EVA.Core.AddManualDiscountToOrder, EVA.Core.ShoppingCartResponse>}
 */
class AddManualDiscountToOrder extends StoreFetchableData {
    /**
     * Creates an instance of AddManualDiscountToOrder
     *
     * @memberof AddManualDiscountToOrder
     */
    constructor() {
        super('addManualDiscountToOrder', '/message/AddManualDiscountToOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AddManualDiscountToOrder
     */
    *saga() {
        yield fork(listener$7i);
    }
}
// Export the state handling singleton
//
const addManualDiscountToOrder = new AddManualDiscountToOrder();
// Setup and Saga implementation
//
function* fetchCall$7i(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([addManualDiscountToOrder, 'fetchData'], action);
        yield put(addManualDiscountToOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // AddManualDiscountToOrder is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(addManualDiscountToOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7i() {
    yield takeEvery(getType(addManualDiscountToOrder.actions.fetch), fetchCall$7i);
}

/**
 * Implements AddProductToFullStockCountLabel fetch for the Redux store
 *
 * @export
 * @class AddProductToFullStockCountLabel
 * @extends {StoreFetchableData<EVA.Core.AddProductToFullStockCountLabel, EVA.Core.AddProductToFullStockCountLabelResponse>}
 */
class AddProductToFullStockCountLabel extends StoreFetchableData {
    /**
     * Creates an instance of AddProductToFullStockCountLabel
     *
     * @memberof AddProductToFullStockCountLabel
     */
    constructor() {
        super('addProductToFullStockCountLabel', '/message/AddProductToFullStockCountLabel');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AddProductToFullStockCountLabel
     */
    *saga() {
        yield fork(listener$7h);
    }
}
// Export the state handling singleton
//
const addProductToFullStockCountLabel = new AddProductToFullStockCountLabel();
// Setup the Saga implementation
//
function* fetchCall$7h(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([addProductToFullStockCountLabel, 'fetchData'], action);
        yield put(addProductToFullStockCountLabel.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(addProductToFullStockCountLabel.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7h() {
    yield takeEvery(getType(addProductToFullStockCountLabel.actions.fetch), fetchCall$7h);
}

/**
 * Implements AddProductToOrder fetch for the Redux store
 *
 * @export
 * @class AddProductToOrder
 * @extends {StoreFetchableData<IAddProductToOrderWithPlaceholder, EVA.Core.AddProductToOrderResponse>}
 */
class AddProductToOrder extends StoreFetchableData {
    /**
     * Creates an instance of AddProductToOrder
     *
     * @memberof AddProductToOrder
     */
    constructor() {
        // Define additional actions
        //
        const additionalActions = {
            addPlaceholder: createAction('ADD_PLACEHOLDER_addProductToOrder', (request) => {
                return ({ type: 'ADD_PLACEHOLDER_addProductToOrder', payload: request });
            }),
            removePlaceholder: createAction('REMOVE_PLACEHOLDER_addProductToOrder', (request) => {
                return ({ type: 'REMOVE_PLACEHOLDER_addProductToOrder', payload: request });
            }),
        };
        super('addProductToOrder', '/message/AddProductToOrder', undefined, undefined, additionalActions);
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AddProductToOrder
     */
    *saga() {
        yield fork(listener$7g);
    }
    /**
     * Initial state with extended properties
     *
     * @type {IAddProductToOrderState}
     * @memberof AddProductToOrder
     */
    get initialState() {
        const initialState = {
            isFetching: false,
            request: null,
            response: null,
            error: null,
            chainStarter: this.name,
            placeholders: [],
        };
        return initialState;
    }
    /**
     * The extended collection of reducer functions
     *
     * @protected
     * @returns {{ [ reducerName: string ]: ( state: any, action: AnyAction ) => any }}
     * @memberof AddProductToOrder
     */
    getReducerFunctions() {
        const baseReducerFuncs = super.getReducerFunctions();
        /**
         * Update the product placeholders
         */
        baseReducerFuncs.placeholders = (state = [], action) => {
            switch (action.type) {
                case getType(this.actions.addPlaceholder):
                    const payload = get(action, 'payload');
                    // Use the currency from the current cart
                    //
                    const cart = getShoppingCart.getState();
                    const currencyID = get(cart, 'response.ShoppingCart.CurrencyID', 'EUR');
                    const productId = payload.placeholder.ProductID;
                    const currentLines = get(cart, 'response.ShoppingCart.Lines');
                    // Check if the product is already in the cart
                    // If stacking is enabled we will not add a placeholder
                    //
                    if (find(currentLines, { ProductID: productId }) && settings.placeholderStacking) {
                        return state;
                    }
                    // Format the place holder as an order line
                    //
                    const tempOrderLine = {
                        DisplayProperties: {
                            OriginalAmount: 0,
                            OriginalAmountInTax: 0,
                            TotalAmount: 0,
                            TotalAmountInTax: 0,
                            UnitPrice: 0,
                            UnitPriceInTax: 0,
                        },
                        Discounts: [],
                        ReturnReason: {
                            Description: '',
                            ID: 0,
                            Name: '',
                        },
                        CreatedByID: 0,
                        QuantityDelivered: 0,
                        ID: 0,
                        OrderID: payload.orderId,
                        Type: 0,
                        StockLabel: 1,
                        Description: payload.placeholder.Description,
                        AdditionalDescription: '',
                        QuantityOrdered: payload.placeholder.Quantity,
                        QuantityOpen: 0,
                        QuantityCommitted: 0,
                        QuantityShipped: 0,
                        QuantityReserved: 0,
                        QuantityBackordered: 0,
                        QuantityReturned: 0,
                        QuantityCancelled: 0,
                        QuantityInvoiced: 0,
                        Product: {
                            ID: productId,
                            BackendID: 'placeholder',
                            CustomID: 'placeholder',
                            BrandName: '',
                            PrimitiveName: payload.placeholder.Description,
                            Type: 1,
                            CatalogID: 0,
                            LedgerClassID: '',
                            Properties: {},
                        },
                        UnitPrice: payload.placeholder.UnitPriceInTax,
                        TaxRate: 1,
                        ShowTaxInfo: true,
                        SerialNumber: '',
                        UnitPriceInTax: payload.placeholder.UnitPriceInTax,
                        IsCancelled: false,
                        TotalQuantityToShip: payload.placeholder.Quantity,
                        TotalAmountInTax: payload.placeholder.UnitPriceInTax,
                        TotalAmount: payload.placeholder.UnitPriceInTax,
                        ModificationAllowed: false,
                        Remark: '',
                        Reference: payload.reference,
                        LineActionType: 3,
                        ExportStatusID: 0,
                        ContractNumber: '',
                        IsCompleted: false,
                        IsShipped: false,
                        IsInvoiced: false,
                        CreationTime: new Date().toString(),
                        DiscountAmount: 0,
                        DiscountBackendID: '',
                        ExternalModificationStatus: 0,
                        CustomOrderLineType: '',
                        CurrencyID: currencyID,
                        TaxExemptionCode: '',
                        TaxExemptionReason: '',
                        ShippingMethod: {
                            ID: 0,
                            CarrierID: 0,
                            Carrier: {
                                ID: 0,
                                Name: '',
                                Code: '',
                            },
                            Name: '',
                            Description: '',
                            Code: '',
                            Priority: 0,
                            PaymentHandledByCarrier: false,
                            ExtraCosts: 0,
                            DeliveryType: 0,
                        },
                        CommitmentStatus: 0,
                        NetTotalAmount: 0,
                        NetTotalAmountInTax: 0,
                        ProductVariation: {},
                        Children: [],
                        DiscountCampaignName: '',
                        DiscountCouponCode: '',
                        TaxCode: { ID: 0, Name: '', Description: '' },
                        BackendReference: '',
                        CustomFieldsV2: [],
                        NetTotalTaxAmount: 0,
                        TotalTaxAmount: 0,
                        IsDigitalGiftCard: false,
                        HoldStatus: 0,
                    };
                    return concat(state, [tempOrderLine]);
                case getType(this.actions.removePlaceholder):
                    const reference = get(action, 'payload.reference');
                    return reject(state, { Reference: reference });
                default:
                    return state;
            }
        };
        return baseReducerFuncs;
    }
}
// Export the state handling singleton
//
const addProductToOrder = new AddProductToOrder();
// Setup and Saga implementation
//
function* fetchCall$7g(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        // Check if there is a placeholder
        //
        const payload = get(action, 'payload.request');
        const placeholder = get(payload, 'Placeholder');
        let placeholderPayload;
        if (payload && placeholder) {
            // Emit to add placeholder
            //
            placeholderPayload = {
                orderId: payload.OrderID,
                reference: uuid(),
                placeholder: placeholder,
            };
            yield put(addProductToOrder.actions.addPlaceholder(placeholderPayload));
        }
        const data = yield call([addProductToOrder, 'fetchData'], action);
        yield put(addProductToOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // AddProductToOrder is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        //
        yield Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
        // Emit to remove placeholder
        //
        if (placeholderPayload) {
            yield put(addProductToOrder.actions.removePlaceholder(placeholderPayload));
        }
    }
    catch (err) {
        yield put(addProductToOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7g() {
    yield takeEvery(getType(addProductToOrder.actions.fetch), fetchCall$7g);
}

/**
 * Implements AddPushNotificationDevice fetch for the Redux store
 *
 * @export
 * @class AddPushNotificationDevice
 * @extends {StoreFetchableData<EVA.Core.AddPushNotificationDevice, EVA.Core.EmptyResponseMessage>}
 */
class AddPushNotificationDevice extends StoreFetchableData {
    /**
     * Creates an instance of AddPushNotificationDevice
     *
     * @memberof AddPushNotificationDevice
     */
    constructor() {
        super('addPushNotificationDevice', '/message/AddPushNotificationDevice');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AddPushNotificationDevice
     */
    *saga() {
        yield fork(listener$7f);
    }
}
// Export the state handling singleton
//
const addPushNotificationDevice = new AddPushNotificationDevice();
// Setup the Saga implementation
//
function* fetchCall$7f(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([addPushNotificationDevice, 'fetchData'], action);
        yield put(addPushNotificationDevice.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(addPushNotificationDevice.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7f() {
    yield takeEvery(getType(addPushNotificationDevice.actions.fetch), fetchCall$7f);
}

/**
 * Implements AddServiceProductToOrder fetch for the Redux store
 *
 * @export
 * @class AddServiceProductToOrder
 * @extends {StoreFetchableData<EVA.Core.AddServiceProductToOrder, EVA.Core.AddServiceProductToOrderResponse>}
 */
class AddServiceProductToOrder extends StoreFetchableData {
    /**
     * Creates an instance of AddServiceProductToOrder
     *
     * @memberof AddServiceProductToOrder
     */
    constructor() {
        super('addServiceProductToOrder', '/message/AddServiceProductToOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AddServiceProductToOrder
     */
    *saga() {
        yield fork(listener$7e);
    }
}
// Export the state handling singleton
//
const addServiceProductToOrder = new AddServiceProductToOrder();
// Setup the Saga implementation
//
function* fetchCall$7e(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([addServiceProductToOrder, 'fetchData'], action);
        yield put(addServiceProductToOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // AddServiceProductToOrder is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(addServiceProductToOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7e() {
    yield takeEvery(getType(addServiceProductToOrder.actions.fetch), fetchCall$7e);
}

/**
 * Implements AddTrackingCodeToReturnToSupplierShipment fetch for the Redux store
 *
 * @export
 * @class AddTrackingCodeToReturnToSupplierShipment
 * @extends {StoreFetchableData<EVA.Core.AddTrackingCodeToReturnToSupplierShipment, EVA.Core.EmptyResponseMessage>}
 */
class AddTrackingCodeToReturnToSupplierShipment extends StoreFetchableData {
    /**
     * Creates an instance of AddTrackingCodeToReturnToSupplierShipment
     *
     * @memberof AddTrackingCodeToReturnToSupplierShipment
     */
    constructor() {
        super('addTrackingCodeToReturnToSupplierShipment', '/message/AddTrackingCodeToReturnToSupplierShipment');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AddTrackingCodeToReturnToSupplierShipment
     */
    *saga() {
        yield fork(listener$7d);
    }
}
// Export the state handling singleton
//
const addTrackingCodeToReturnToSupplierShipment = new AddTrackingCodeToReturnToSupplierShipment();
// Setup the Saga implementation
//
function* fetchCall$7d(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([addTrackingCodeToReturnToSupplierShipment, 'fetchData'], action);
        yield put(addTrackingCodeToReturnToSupplierShipment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(addTrackingCodeToReturnToSupplierShipment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7d() {
    yield takeEvery(getType(addTrackingCodeToReturnToSupplierShipment.actions.fetch), fetchCall$7d);
}

/**
 * Implements AddUserBorrowedProductToOrder fetch for the Redux store
 *
 * @export
 * @class AddUserBorrowedProductToOrder
 * @extends {StoreFetchableData<EVA.Core.AddUserBorrowedProductToOrder, EVA.Core.AddUserBorrowedProductToOrderResponse>}
 */
class AddUserBorrowedProductToOrder extends StoreFetchableData {
    /**
     * Creates an instance of AddUserBorrowedProductToOrder
     *
     * @memberof AddUserBorrowedProductToOrder
     */
    constructor() {
        super('addUserBorrowedProductToOrder', '/message/AddUserBorrowedProductToOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AddUserBorrowedProductToOrder
     */
    *saga() {
        yield fork(listener$7c);
    }
}
// Export the state handling singleton
//
const addUserBorrowedProductToOrder = new AddUserBorrowedProductToOrder();
// Setup the Saga implementation
//
function* fetchCall$7c(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([addUserBorrowedProductToOrder, 'fetchData'], action);
        yield put(addUserBorrowedProductToOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(addUserBorrowedProductToOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7c() {
    yield takeEvery(getType(addUserBorrowedProductToOrder.actions.fetch), fetchCall$7c);
}

/**
 * Implements AddVoucherToShoppingCart fetch for the Redux store
 *
 * @export
 * @class AddVoucherToShoppingCart
 * @extends {StoreFetchableData<EVA.Intersolve.AddVoucherToShoppingCart, EVA.Core.SimpleShoppingCartResponse>}
 */
class AddVoucherToShoppingCart extends StoreFetchableData {
    /**
     * Creates an instance of AddVoucherToShoppingCart
     *
     * @memberof AddVoucherToShoppingCart
     */
    constructor() {
        super('addVoucherToShoppingCart', '/message/AddVoucherToShoppingCart');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AddVoucherToShoppingCart
     */
    *saga() {
        yield fork(listener$7b);
    }
}
// Export the state handling singleton
//
const addVoucherToShoppingCart = new AddVoucherToShoppingCart();
// Setup the Saga implementation
//
function* fetchCall$7b(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([addVoucherToShoppingCart, 'fetchData'], action);
        yield put(addVoucherToShoppingCart.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(addVoucherToShoppingCart.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7b() {
    yield takeEvery(getType(addVoucherToShoppingCart.actions.fetch), fetchCall$7b);
}

/**
 * Implements GetStockDetailsForProduct fetch for the Redux store
 *
 * @export
 * @class GetStockDetailsForProduct
 * @extends {StoreFetchableData<EVA.Core.GetStockDetailsForProduct, EVA.Core.GetStockDetailsForProductResponse>}
 */
class GetStockDetailsForProduct extends StoreFetchableData {
    /**
     * Creates an instance of GetStockDetailsForProduct
     *
     * @memberof GetStockDetailsForProduct
     */
    constructor() {
        super('getStockDetailsForProduct', '/message/GetStockDetailsForProduct');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetStockDetailsForProduct
     */
    *saga() {
        yield fork(listener$7a);
    }
}
// Export the state handling singleton
//
const getStockDetailsForProduct = new GetStockDetailsForProduct();
// Setup the Saga implementation
//
function* fetchCall$7a(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getStockDetailsForProduct, 'fetchData'], action);
        yield put(getStockDetailsForProduct.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getStockDetailsForProduct.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7a() {
    yield takeEvery(getType(getStockDetailsForProduct.actions.fetch), fetchCall$7a);
}

/**
 * Implements AdjustStock fetch for the Redux store
 *
 * @export
 * @class AdjustStock
 * @extends {StoreFetchableData<EVA.Core.AdjustStock, EVA.Core.EmptyResponseMessage>}
 */
class AdjustStock extends StoreFetchableData {
    /**
     * Creates an instance of AdjustStock
     *
     * @memberof AdjustStock
     */
    constructor() {
        super('adjustStock', '/message/AdjustStock');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AdjustStock
     */
    *saga() {
        yield fork(listener$79);
    }
}
// Export the state handling singleton
//
const adjustStock = new AdjustStock();
// Setup the Saga implementation
//
function* fetchCall$79(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([adjustStock, 'fetchData'], action);
        yield put(adjustStock.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the product stock details
        //
        const productId = get(adjustStock.getState(), 'request.ProductID');
        const [getStockDetailsForProductAction, , getStockDetailsForProductChainPromise] = getStockDetailsForProduct.createFetchAction({ ProductID: productId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getStockDetailsForProductAction);
        // Resolve the chain
        //
        Promise.all([getStockDetailsForProductChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(adjustStock.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$79() {
    yield takeEvery(getType(adjustStock.actions.fetch), fetchCall$79);
}

/**
 * Implements ApplyBundleProductSelection fetch for the Redux store
 *
 * @export
 * @class ApplyBundleProductSelection
 * @extends {StoreFetchableData<EVA.Core.ApplyBundleProductSelection, EVA.Core.ShoppingCartResponse>}
 */
class ApplyBundleProductSelection extends StoreFetchableData {
    /**
     * Creates an instance of ApplyBundleProductSelection
     *
     * @memberof ApplyBundleProductSelection
     */
    constructor() {
        super('applyBundleProductSelection', '/message/ApplyBundleProductSelection');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ApplyBundleProductSelection
     */
    *saga() {
        yield fork(listener$78);
    }
}
// Export the state handling singleton
//
const applyBundleProductSelection = new ApplyBundleProductSelection();
// Setup and Saga implementation
//
function* fetchCall$78(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([applyBundleProductSelection, 'fetchData'], action);
        yield put(applyBundleProductSelection.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // ApplyBundleProductSelection is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(applyBundleProductSelection.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$78() {
    yield takeEvery(getType(applyBundleProductSelection.actions.fetch), fetchCall$78);
}

/**
 * Implements ApplySalesTaxEstimateForOrder fetch for the Redux store
 *
 * @export
 * @class ApplySalesTaxEstimateForOrder
 * @extends {StoreFetchableData<EVA.Core.ApplySalesTaxEstimateForOrder, EVA.Core.ApplySalesTaxEstimateForOrderResponse>}
 */
class ApplySalesTaxEstimateForOrder extends StoreFetchableData {
    /**
     * Creates an instance of ApplySalesTaxEstimateForOrder
     *
     * @memberof ApplySalesTaxEstimateForOrder
     */
    constructor() {
        super('applySalesTaxEstimateForOrder', '/message/ApplySalesTaxEstimateForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ApplySalesTaxEstimateForOrder
     */
    *saga() {
        yield fork(listener$77);
    }
}
// Export the state handling singleton
//
const applySalesTaxEstimateForOrder = new ApplySalesTaxEstimateForOrder();
// Setup the Saga implementation
//
function* fetchCall$77(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([applySalesTaxEstimateForOrder, 'fetchData'], action);
        yield put(applySalesTaxEstimateForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(applySalesTaxEstimateForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$77() {
    yield takeEvery(getType(applySalesTaxEstimateForOrder.actions.fetch), fetchCall$77);
}

/**
 * Implements ApprovePayment fetch for the Redux store
 *
 * @export
 * @class ApprovePayment
 * @extends {StoreFetchableData<EVA.Core.ApprovePayment, EVA.Core.ApprovePaymentResponse>}
 */
class ApprovePayment extends StoreFetchableData {
    /**
     * Creates an instance of ApprovePayment
     *
     * @memberof ApprovePayment
     */
    constructor() {
        super('approvePayment', '/message/ApprovePayment');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ApprovePayment
     */
    *saga() {
        yield fork(listener$76);
    }
}
// Export the state handling singleton
//
const approvePayment = new ApprovePayment();
// Setup the Saga implementation
//
function* fetchCall$76(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([approvePayment, 'fetchData'], action);
        yield put(approvePayment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(approvePayment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$76() {
    yield takeEvery(getType(approvePayment.actions.fetch), fetchCall$76);
}

/**
 * Implements AttachAddressToOrder fetch for the Redux store
 *
 * @export
 * @class AttachAddressToOrder
 * @extends {StoreFetchableData<EVA.Core.AttachAddressToOrder, EVA.Core.AttachAddressToOrderResponse>}
 */
class AttachAddressToOrder extends StoreFetchableData {
    /**
     * Creates an instance of AttachAddressToOrder
     *
     * @memberof AttachAddressToOrder
     */
    constructor() {
        super('attachAddressToOrder', '/message/AttachAddressToOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AttachAddressToOrder
     */
    *saga() {
        yield fork(listener$75);
    }
}
// Export the state handling singleton
//
const attachAddressToOrder = new AttachAddressToOrder();
// Setup the Saga implementation
//
function* fetchCall$75(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([attachAddressToOrder, 'fetchData'], action);
        yield put(attachAddressToOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(attachAddressToOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$75() {
    yield takeEvery(getType(attachAddressToOrder.actions.fetch), fetchCall$75);
}

/**
 * Implements AttachCustomerToOrder fetch for the Redux store
 *
 * @export
 * @class AttachCustomerToOrder
 * @extends {StoreFetchableData<EVA.Core.AttachCustomerToOrder, EVA.Core.EmptyResponseMessage>}
 */
class AttachCustomerToOrder extends StoreFetchableData {
    /**
     * Creates an instance of AttachCustomerToOrder
     *
     * @memberof AttachCustomerToOrder
     */
    constructor() {
        super('attachCustomerToOrder', '/message/AttachCustomerToOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AttachCustomerToOrder
     */
    *saga() {
        yield fork(listener$74);
    }
}
// Export the state handling singleton
//
const attachCustomerToOrder = new AttachCustomerToOrder();
// Setup the Saga implementation
//
function* fetchCall$74(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([attachCustomerToOrder, 'fetchData'], action);
        yield put(attachCustomerToOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(attachCustomerToOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$74() {
    yield takeEvery(getType(attachCustomerToOrder.actions.fetch), fetchCall$74);
}

/**
 * Implements AutocompleteAddress fetch for the Redux store
 *
 * @export
 * @class AutocompleteAddress
 * @extends {StoreFetchableData<EVA.Core.AutocompleteAddress, EVA.Core.AutocompleteAddressResponse>}
 */
class AutocompleteAddress extends StoreFetchableData {
    /**
     * Creates an instance of AutocompleteAddress
     *
     * @memberof AutocompleteAddress
     */
    constructor() {
        super('autocompleteAddress', '/message/AutocompleteAddress');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof AutocompleteAddress
     */
    *saga() {
        yield fork(listener$73);
    }
}
// Export the state handling singleton
//
const autocompleteAddress = new AutocompleteAddress();
// Setup and Saga implementation
//
function* fetchCall$73(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([autocompleteAddress, 'fetchData'], action);
        yield put(autocompleteAddress.actions.response(data));
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(autocompleteAddress.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$73() {
    yield takeEvery(getType(autocompleteAddress.actions.fetch), fetchCall$73);
}

/**
 * Implements CancelDiscountOrderLine fetch for the Redux store
 *
 * @export
 * @class CancelDiscountOrderLine
 * @extends {StoreFetchableData<EVA.Core.CancelDiscountOrderLine, EVA.Core.SimpleShoppingCartResponse>}
 */
class CancelDiscountOrderLine extends StoreFetchableData {
    /**
     * Creates an instance of CancelDiscountOrderLine
     *
     * @memberof CancelDiscountOrderLine
     */
    constructor() {
        super('cancelDiscountOrderLine', '/message/CancelDiscountOrderLine');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CancelDiscountOrderLine
     */
    *saga() {
        yield fork(listener$72);
    }
}
// Export the state handling singleton
//
const cancelDiscountOrderLine = new CancelDiscountOrderLine();
// Setup the Saga implementation
//
function* fetchCall$72(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([cancelDiscountOrderLine, 'fetchData'], action);
        yield put(cancelDiscountOrderLine.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(cancelDiscountOrderLine.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$72() {
    yield takeEvery(getType(cancelDiscountOrderLine.actions.fetch), fetchCall$72);
}

/**
 * Implements CancelFullStockCount fetch for the Redux store
 *
 * @export
 * @class CancelFullStockCount
 * @extends {StoreFetchableData<EVA.Core.CancelFullStockCount, EVA.Core.EmptyResponseMessage>}
 */
class CancelFullStockCount extends StoreFetchableData {
    /**
     * Creates an instance of CancelFullStockCount
     *
     * @memberof CancelFullStockCount
     */
    constructor() {
        super('cancelFullStockCount', '/message/CancelFullStockCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CancelFullStockCount
     */
    *saga() {
        yield fork(listener$71);
    }
}
// Export the state handling singleton
//
const cancelFullStockCount = new CancelFullStockCount();
// Setup the Saga implementation
//
function* fetchCall$71(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([cancelFullStockCount, 'fetchData'], action);
        yield put(cancelFullStockCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(cancelFullStockCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$71() {
    yield takeEvery(getType(cancelFullStockCount.actions.fetch), fetchCall$71);
}

/**
 * Implements CancelFullStockCountLabel fetch for the Redux store
 *
 * @export
 * @class CancelFullStockCountLabel
 * @extends {StoreFetchableData<EVA.Core.CancelFullStockCountLabel, EVA.Core.EmptyResponseMessage>}
 */
class CancelFullStockCountLabel extends StoreFetchableData {
    /**
     * Creates an instance of CancelFullStockCountLabel
     *
     * @memberof CancelFullStockCountLabel
     */
    constructor() {
        super('cancelFullStockCountLabel', '/message/CancelFullStockCountLabel');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CancelFullStockCountLabel
     */
    *saga() {
        yield fork(listener$70);
    }
}
// Export the state handling singleton
//
const cancelFullStockCountLabel = new CancelFullStockCountLabel();
// Setup the Saga implementation
//
function* fetchCall$70(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([cancelFullStockCountLabel, 'fetchData'], action);
        yield put(cancelFullStockCountLabel.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(cancelFullStockCountLabel.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$70() {
    yield takeEvery(getType(cancelFullStockCountLabel.actions.fetch), fetchCall$70);
}

/**
 * Implements CancelOrderLineFulfillments fetch for the Redux store
 *
 * @export
 * @class CancelOrderLineFulfillments
 * @extends {StoreFetchableData<EVA.Core.CancelOrderLineFulfillments, EVA.Core.EmptyResponseMessage>}
 */
class CancelOrderLineFulfillments extends StoreFetchableData {
    /**
     * Creates an instance of CancelOrderLineFulfillments
     *
     * @memberof CancelOrderLineFulfillments
     */
    constructor() {
        super('cancelOrderLineFulfillments', '/message/CancelOrderLineFulfillments');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CancelOrderLineFulfillments
     */
    *saga() {
        yield fork(listener$6$);
    }
}
// Export the state handling singleton
//
const cancelOrderLineFulfillments = new CancelOrderLineFulfillments();
// Setup the Saga implementation
//
function* fetchCall$6$(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([cancelOrderLineFulfillments, 'fetchData'], action);
        yield put(cancelOrderLineFulfillments.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(cancelOrderLineFulfillments.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6$() {
    yield takeEvery(getType(cancelOrderLineFulfillments.actions.fetch), fetchCall$6$);
}

/**
 * Implements CancelPayment fetch for the Redux store
 *
 * @export
 * @class CancelPayment
 * @extends {StoreFetchableData<EVA.Core.CancelPayment, EVA.Core.EmptyResponseMessage>}
 */
class CancelPayment extends StoreFetchableData {
    /**
     * Creates an instance of CancelPayment
     *
     * @memberof CancelPayment
     */
    constructor() {
        super('cancelPayment', '/message/CancelPayment');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CancelPayment
     */
    *saga() {
        yield fork(listener$6_);
    }
}
// Export the state handling singleton
//
const cancelPayment = new CancelPayment();
// Setup the Saga implementation
//
function* fetchCall$6_(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([cancelPayment, 'fetchData'], action);
        yield put(cancelPayment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(cancelPayment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6_() {
    yield takeEvery(getType(cancelPayment.actions.fetch), fetchCall$6_);
}

/**
 * Implements CancelSecondChanceProductRegistration fetch for the Redux store
 *
 * @export
 * @class CancelSecondChanceProductRegistration
 * @extends {StoreFetchableData<EVA.Core.CancelSecondChanceProductRegistration, EVA.Core.EmptyResponseMessage>}
 */
class CancelSecondChanceProductRegistration extends StoreFetchableData {
    /**
     * Creates an instance of CancelSecondChanceProductRegistration
     *
     * @memberof CancelSecondChanceProductRegistration
     */
    constructor() {
        super('cancelSecondChanceProductRegistration', '/message/CancelSecondChanceProductRegistration');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CancelSecondChanceProductRegistration
     */
    *saga() {
        yield fork(listener$6Z);
    }
}
// Export the state handling singleton
//
const cancelSecondChanceProductRegistration = new CancelSecondChanceProductRegistration();
// Setup the Saga implementation
//
function* fetchCall$6Z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([cancelSecondChanceProductRegistration, 'fetchData'], action);
        yield put(cancelSecondChanceProductRegistration.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(cancelSecondChanceProductRegistration.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6Z() {
    yield takeEvery(getType(cancelSecondChanceProductRegistration.actions.fetch), fetchCall$6Z);
}

/**
 * Implements CardBalanceCheck fetch for the Redux store
 *
 * @export
 * @class CardBalanceCheck
 * @extends {StoreFetchableData<EVA.Core.CardBalanceCheck, EVA.Core.CardBalanceCheckResponse>}
 */
class CardBalanceCheck extends StoreFetchableData {
    /**
     * Creates an instance of CardBalanceCheck
     *
     * @memberof CardBalanceCheck
     */
    constructor() {
        super('cardBalanceCheck', '/message/CardBalanceCheck');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CardBalanceCheck
     */
    *saga() {
        yield fork(listener$6Y);
    }
    /**
     * Will return the matching giftcard handler given a paymentMethodTypeModel.
     *
     * Beware that a paymentMethodTypeModel could have multiple matching giftcard handlers. This method will still only return the first one.
     * Consumers are responsible for facillitating a UI that allows the user to choose whatever giftcard handler they want.
     *
     * @returns The matching giftcard handler. If no match is found, returns null. The match can be then used in a cardBalanceCheck  like so
     * - `Handler` => `Handler`
     * - `CardType` in (AvailableGiftCardHandler) => `CardSubType` (in cardBalanceCheck)
     */
    getGiftCardHandlerByPaymentType(opts) {
        const matchingGiftCardHandler = opts.giftCardHandlers.find(giftCardHandler => {
            const paymentMethodTypeModelCode = opts.paymentMethodTypeModel.Code;
            // ADYEN_CHECKOUT_API_GIFTCARD is the only method that will be mapped to AdyenStoredValue handler. Otherwise matching will occur based on `Code`
            // matching `Handler`
            //
            if (paymentMethodTypeModelCode === 'ADYEN_CHECKOUT_API_GIFTCARD') {
                // This will occur in the following case at time of writing.
                // {Handler: "AdyenStoredValue", CardType: "svs"} => ADYEN_CHECKOUT_API_GIFTCARD
                //
                return giftCardHandler.Handler === 'AdyenStoredValue';
            }
            else {
                // This will occur in the following cases at the time of writing.
                // {Handler: "INTERSOLVE", CardType: null} -> INTERSOLVE
                // {Handler: "EPAY", CardType: null} -> EPAY
                // {Handler: "FASHIONCHEQUE", CardType: null} -> FASHIONCHEQUE
                // {Handler: "APIGIFTCARD", CardType: null} -> APIGIFTCARD
                //
                return giftCardHandler.Handler === paymentMethodTypeModelCode;
            }
        });
        return matchingGiftCardHandler;
    }
}
// Export the state handling singleton
//
const cardBalanceCheck = new CardBalanceCheck();
// Setup the Saga implementation
//
function* fetchCall$6Y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([cardBalanceCheck, 'fetchData'], action);
        yield put(cardBalanceCheck.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(cardBalanceCheck.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6Y() {
    yield takeEvery(getType(cardBalanceCheck.actions.fetch), fetchCall$6Y);
}

/**
 * Implements ChangeUserPassword fetch for the Redux store
 *
 * @export
 * @class ChangeUserPassword
 * @extends {StoreFetchableData<EVA.Core.ChangeUserPassword, EVA.Core.ChangeUserPasswordResponse>}
 */
class ChangeUserPassword extends StoreFetchableData {
    /**
     * Creates an instance of ChangeUserPassword
     *
     * @memberof ChangeUserPassword
     */
    constructor() {
        super('changeUserPassword', '/message/ChangeUserPassword');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ChangeUserPassword
     */
    *saga() {
        yield fork(listener$6X);
    }
}
// Export the state handling singleton
//
const changeUserPassword = new ChangeUserPassword();
// Setup the Saga implementation
//
function* fetchCall$6X(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([changeUserPassword, 'fetchData'], action);
        yield put(changeUserPassword.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(changeUserPassword.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6X() {
    yield takeEvery(getType(changeUserPassword.actions.fetch), fetchCall$6X);
}

/**
 * Implements CheckEmailAddressAvailability fetch for the Redux store
 *
 * @export
 * @class CheckEmailAddressAvailability
 * @extends {StoreFetchableData<EVA.Core.CheckEmailAddressAvailability, EVA.Core.CheckUsernameAvailabilityResponse>}
 */
class CheckEmailAddressAvailability extends StoreFetchableData {
    /**
     * Creates an instance of CheckEmailAddressAvailability
     *
     * @memberof CheckEmailAddressAvailability
     */
    constructor() {
        super('checkEmailAddressAvailability', '/message/CheckEmailAddressAvailability');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CheckEmailAddressAvailability
     */
    *saga() {
        yield fork(listener$6W);
    }
}
// Export the state handling singleton
//
const checkEmailAddressAvailability = new CheckEmailAddressAvailability();
// Setup the Saga implementation
//
function* fetchCall$6W(action) {
    const fetchActionPayload = action.payload;
    try {
        const getApplicationConfigurationState = getApplicationConfiguration.getState();
        const SplitEmployeesAccounts = get(getApplicationConfigurationState.response, 'Configuration.SplitEmployeesAccounts', false);
        if (SplitEmployeesAccounts && settings.defaultCallUserType !== undefined && fetchActionPayload.request.AsEmployee === undefined) {
            const isEmployeeApp = settings.defaultCallUserType === 'employee';
            fetchActionPayload.request = Object.assign(Object.assign({}, fetchActionPayload.request), { AsEmployee: isEmployeeApp });
        }
        const data = yield call([checkEmailAddressAvailability, 'fetchData'], action);
        yield put(checkEmailAddressAvailability.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(checkEmailAddressAvailability.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6W() {
    yield takeEvery(getType(checkEmailAddressAvailability.actions.fetch), fetchCall$6W);
}

/**
 * Implements CheckNicknameAvailability fetch for the Redux store
 *
 * @export
 * @class CheckNicknameAvailability
 * @extends {StoreFetchableData<EVA.Core.CheckNicknameAvailability, EVA.Core.CheckUsernameAvailabilityResponse>}
 */
class CheckNicknameAvailability extends StoreFetchableData {
    /**
     * Creates an instance of CheckNicknameAvailability
     *
     * @memberof CheckNicknameAvailability
     */
    constructor() {
        super('checkNicknameAvailability', '/message/CheckNicknameAvailability');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CheckNicknameAvailability
     */
    *saga() {
        yield fork(listener$6V);
    }
}
// Export the state handling singleton
//
const checkNicknameAvailability = new CheckNicknameAvailability();
// Setup the Saga implementation
//
function* fetchCall$6V(action) {
    const fetchActionPayload = action.payload;
    try {
        const getApplicationConfigurationState = getApplicationConfiguration.getState();
        const SplitEmployeesAccounts = get(getApplicationConfigurationState.response, 'Configuration.SplitEmployeesAccounts', false);
        if (SplitEmployeesAccounts && settings.defaultCallUserType !== undefined && fetchActionPayload.request.AsEmployee === undefined) {
            const isEmployeeApp = settings.defaultCallUserType === 'employee';
            fetchActionPayload.request = Object.assign(Object.assign({}, fetchActionPayload.request), { AsEmployee: isEmployeeApp });
        }
        const data = yield call([checkNicknameAvailability, 'fetchData'], action);
        yield put(checkNicknameAvailability.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(checkNicknameAvailability.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6V() {
    yield takeEvery(getType(checkNicknameAvailability.actions.fetch), fetchCall$6V);
}

/**
 * Implements ClockEmployeeOutAsAbsent fetch for the Redux store
 *
 * @export
 * @class ClockEmployeeOutAsAbsent
 * @extends {StoreFetchableData<EVA.Workforce.ClockEmployeeOutAsAbsent, EVA.Core.EmptyResponseMessage>}
 */
class ClockEmployeeOutAsAbsent extends StoreFetchableData {
    /**
     * Creates an instance of ClockEmployeeOutAsAbsent
     *
     * @memberof ClockEmployeeOutAsAbsent
     */
    constructor() {
        super('clockEmployeeOutAsAbsent', '/message/ClockEmployeeOutAsAbsent');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ClockEmployeeOutAsAbsent
     */
    *saga() {
        yield fork(listener$6U);
    }
}
// Export the state handling singleton
//
const clockEmployeeOutAsAbsent = new ClockEmployeeOutAsAbsent();
// Setup the Saga implementation
//
function* fetchCall$6U(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([clockEmployeeOutAsAbsent, 'fetchData'], action);
        yield put(clockEmployeeOutAsAbsent.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(clockEmployeeOutAsAbsent.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6U() {
    yield takeEvery(getType(clockEmployeeOutAsAbsent.actions.fetch), fetchCall$6U);
}

/**
 * Implements CloseFinancialPeriod fetch for the Redux store
 *
 * @export
 * @class CloseFinancialPeriod
 * @extends {StoreFetchableData<EVA.Core.CloseFinancialPeriod, EVA.Core.EmptyResponseMessage>}
 */
class CloseFinancialPeriod extends StoreFetchableData {
    /**
     * Creates an instance of CloseFinancialPeriod
     *
     * @memberof CloseFinancialPeriod
     */
    constructor() {
        super('closeFinancialPeriod', '/message/CloseFinancialPeriod');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CloseFinancialPeriod
     */
    *saga() {
        yield fork(listener$6T);
    }
}
// Export the state handling singleton
//
const closeFinancialPeriod = new CloseFinancialPeriod();
// Setup the Saga implementation
//
function* fetchCall$6T(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([closeFinancialPeriod, 'fetchData'], action);
        yield put(closeFinancialPeriod.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(closeFinancialPeriod.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6T() {
    yield takeEvery(getType(closeFinancialPeriod.actions.fetch), fetchCall$6T);
}

/**
 * Implements CompleteCloseCashJournal fetch for the Redux store
 *
 * @export
 * @class CompleteCloseCashJournal
 * @extends {StoreFetchableData<EVA.Core.CompleteCloseCashJournal, EVA.Core.EmptyResponseMessage>}
 */
class CompleteCloseCashJournal extends StoreFetchableData {
    /**
     * Creates an instance of CompleteCloseCashJournal
     *
     * @memberof CompleteCloseCashJournal
     */
    constructor() {
        super('completeCloseCashJournal', '/message/CompleteCloseCashJournal');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteCloseCashJournal
     */
    *saga() {
        yield fork(listener$6S);
    }
}
// Export the state handling singleton
//
const completeCloseCashJournal = new CompleteCloseCashJournal();
// Setup the Saga implementation
//
function* fetchCall$6S(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([completeCloseCashJournal, 'fetchData'], action);
        yield put(completeCloseCashJournal.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(completeCloseCashJournal.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6S() {
    yield takeEvery(getType(completeCloseCashJournal.actions.fetch), fetchCall$6S);
}

/**
 * Implements CompleteFullStockCount fetch for the Redux store
 *
 * @export
 * @class CompleteFullStockCount
 * @extends {StoreFetchableData<EVA.Core.CompleteFullStockCount, EVA.Core.EmptyResponseMessage>}
 */
class CompleteFullStockCount extends StoreFetchableData {
    /**
     * Creates an instance of CompleteFullStockCount
     *
     * @memberof CompleteFullStockCount
     */
    constructor() {
        super('completeFullStockCount', '/message/CompleteFullStockCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteFullStockCount
     */
    *saga() {
        yield fork(listener$6R);
    }
}
// Export the state handling singleton
//
const completeFullStockCount = new CompleteFullStockCount();
// Setup the Saga implementation
//
function* fetchCall$6R(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([completeFullStockCount, 'fetchData'], action);
        yield put(completeFullStockCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(completeFullStockCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6R() {
    yield takeEvery(getType(completeFullStockCount.actions.fetch), fetchCall$6R);
}

/**
 * Implements CompleteFullStockCountLabel fetch for the Redux store
 *
 * @export
 * @class CompleteFullStockCountLabel
 * @extends {StoreFetchableData<EVA.Core.CompleteFullStockCountLabel, EVA.Core.EmptyResponseMessage>}
 */
class CompleteFullStockCountLabel extends StoreFetchableData {
    /**
     * Creates an instance of CompleteFullStockCountLabel
     *
     * @memberof CompleteFullStockCountLabel
     */
    constructor() {
        super('completeFullStockCountLabel', '/message/CompleteFullStockCountLabel');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteFullStockCountLabel
     */
    *saga() {
        yield fork(listener$6Q);
    }
}
// Export the state handling singleton
//
const completeFullStockCountLabel = new CompleteFullStockCountLabel();
// Setup the Saga implementation
//
function* fetchCall$6Q(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([completeFullStockCountLabel, 'fetchData'], action);
        yield put(completeFullStockCountLabel.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(completeFullStockCountLabel.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6Q() {
    yield takeEvery(getType(completeFullStockCountLabel.actions.fetch), fetchCall$6Q);
}

/**
 * Implements CompleteOpenCashJournal fetch for the Redux store
 *
 * @export
 * @class CompleteOpenCashJournal
 * @extends {StoreFetchableData<EVA.Core.CompleteOpenCashJournal, EVA.Core.EmptyResponseMessage>}
 */
class CompleteOpenCashJournal extends StoreFetchableData {
    /**
     * Creates an instance of CompleteOpenCashJournal
     *
     * @memberof CompleteOpenCashJournal
     */
    constructor() {
        super('completeOpenCashJournal', '/message/CompleteOpenCashJournal');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteOpenCashJournal
     */
    *saga() {
        yield fork(listener$6P);
    }
}
// Export the state handling singleton
//
const completeOpenCashJournal = new CompleteOpenCashJournal();
// Setup the Saga implementation
//
function* fetchCall$6P(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([completeOpenCashJournal, 'fetchData'], action);
        yield put(completeOpenCashJournal.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(completeOpenCashJournal.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6P() {
    yield takeEvery(getType(completeOpenCashJournal.actions.fetch), fetchCall$6P);
}

/**
 * Implements CompletePrintPriceLabelTask fetch for the Redux store
 *
 * @export
 * @class CompletePrintPriceLabelTask
 * @extends {StoreFetchableData<EVA.Core.CompletePrintPriceLabelTask, EVA.Core.EmptyResponseMessage>}
 */
class CompletePrintPriceLabelTask extends StoreFetchableData {
    /**
     * Creates an instance of CompletePrintPriceLabelTask
     *
     * @memberof CompletePrintPriceLabelTask
     */
    constructor() {
        super('completePrintPriceLabelTask', '/message/CompletePrintPriceLabelTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompletePrintPriceLabelTask
     */
    *saga() {
        yield fork(listener$6O);
    }
}
// Export the state handling singleton
//
const completePrintPriceLabelTask = new CompletePrintPriceLabelTask();
// Setup the Saga implementation
//
function* fetchCall$6O(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([completePrintPriceLabelTask, 'fetchData'], action);
        yield put(completePrintPriceLabelTask.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(completePrintPriceLabelTask.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6O() {
    yield takeEvery(getType(completePrintPriceLabelTask.actions.fetch), fetchCall$6O);
}

/**
 * Implements CompletePrintTask fetch for the Redux store
 *
 * @export
 * @class CompletePrintTask
 * @extends {StoreFetchableData<EVA.Core.CompletePrintTask, EVA.Core.EmptyResponseMessage>}
 */
class CompletePrintTask extends StoreFetchableData {
    /**
     * Creates an instance of CompletePrintTask
     *
     * @memberof CompletePrintTask
     */
    constructor() {
        super('completePrintTask', '/message/CompletePrintTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompletePrintTask
     */
    *saga() {
        yield fork(listener$6N);
    }
}
// Export the state handling singleton
//
const completePrintTask = new CompletePrintTask();
// Setup the Saga implementation
//
function* fetchCall$6N(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([completePrintTask, 'fetchData'], action);
        yield put(completePrintTask.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(completePrintTask.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6N() {
    yield takeEvery(getType(completePrintTask.actions.fetch), fetchCall$6N);
}

/**
 * Implements ListAvailableUserTasks fetch for the Redux store
 *
 * @export
 * @class ListAvailableUserTasks
 * @extends {StoreFetchableData<EVA.Core.ListAvailableUserTasks, EVA.Core.ListAvailableUserTasksResponse>}
 */
class ListAvailableUserTasks extends StoreFetchableData {
    /**
     * Creates an instance of ListAvailableUserTasks
     *
     * @memberof ListAvailableUserTasks
     */
    constructor() {
        super('listAvailableUserTasks', '/message/ListAvailableUserTasks');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListAvailableUserTasks
     */
    *saga() {
        yield fork(listener$6M);
    }
}
// Export the state handling singleton
//
const listAvailableUserTasks = new ListAvailableUserTasks();
// Setup the Saga implementation
//
function* fetchCall$6M(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listAvailableUserTasks, 'fetchData'], action);
        yield put(listAvailableUserTasks.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listAvailableUserTasks.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6M() {
    yield takeEvery(getType(listAvailableUserTasks.actions.fetch), fetchCall$6M);
}

/**
 * Implements CompleteReservationCleanup fetch for the Redux store
 *
 * @export
 * @class CompleteReservationCleanup
 * @extends {StoreFetchableData<EVA.Core.CompleteReservationCleanup, EVA.Core.EmptyResponseMessage>}
 */
class CompleteReservationCleanup extends StoreFetchableData {
    /**
     * Creates an instance of CompleteReservationCleanup
     *
     * @memberof CompleteReservationCleanup
     */
    constructor() {
        super('completeReservationCleanup', '/message/CompleteReservationCleanup');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteReservationCleanup
     */
    *saga() {
        yield fork(listener$6L);
    }
}
// Export the state handling singleton
//
const completeReservationCleanup = new CompleteReservationCleanup();
// Setup the Saga implementation
//
function* fetchCall$6L(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([completeReservationCleanup, 'fetchData'], action);
        yield put(completeReservationCleanup.actions.response(data));
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        const chainStarter = action.payload.chainStarter;
        // Update current user tasks
        //
        const [listAvailableUserTasksAction, , listAvailableUserTasksChainPromise] = listAvailableUserTasks.createFetchAction(listAvailableUserTasks.getState().request, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(listAvailableUserTasksAction);
        // Resolve the chain
        //
        Promise.all([listAvailableUserTasksChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(completeReservationCleanup.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6L() {
    yield takeEvery(getType(completeReservationCleanup.actions.fetch), fetchCall$6L);
}

/**
 * Implements CompleteReservationDeviationTask fetch for the Redux store
 *
 * @export
 * @class CompleteReservationDeviation
 * @extends {StoreFetchableData<EVA.Core.CompleteReservationDeviationTask, EVA.Core.CompleteReservationDeviationTaskResponse>}
 */
class CompleteReservationDeviation extends StoreFetchableData {
    /**
     * Creates an instance of CompleteReservationDeviationTask
     *
     * @memberof CompleteReservationDeviation
     */
    constructor() {
        super('completeReservationDeviation', '/message/CompleteReservationDeviationTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteReservationDeviation
     */
    *saga() {
        yield fork(listener$6K);
    }
}
// Export the state handling singleton
//
const completeReservationDeviation = new CompleteReservationDeviation();
// Setup the Saga implementation
//
function* fetchCall$6K(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([completeReservationDeviation, 'fetchData'], action);
        yield put(completeReservationDeviation.actions.response(data));
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        const chainStarter = action.payload.chainStarter;
        // Update current user tasks
        //
        const [listAvailableUserTasksAction, , listAvailableUserTasksChainPromise] = listAvailableUserTasks.createFetchAction(listAvailableUserTasks.getState().request, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(listAvailableUserTasksAction);
        // Resolve the chain
        //
        Promise.all([listAvailableUserTasksChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(completeReservationDeviation.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6K() {
    yield takeEvery(getType(completeReservationDeviation.actions.fetch), fetchCall$6K);
}

/**
 * Implements CompleteReturnToSupplierTask fetch for the Redux store
 *
 * @export
 * @class CompleteReturnToSupplierTask
 * @extends {StoreFetchableData<EVA.Core.CompleteReturnToSupplierTask, EVA.Core.EmptyResponseMessage>}
 */
class CompleteReturnToSupplierTask extends StoreFetchableData {
    /**
     * Creates an instance of CompleteReturnToSupplierTask
     *
     * @memberof CompleteReturnToSupplierTask
     */
    constructor() {
        super('completeReturnToSupplierTask', '/message/CompleteReturnToSupplierTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteReturnToSupplierTask
     */
    *saga() {
        yield fork(listener$6J);
    }
}
// Export the state handling singleton
//
const completeReturnToSupplierTask = new CompleteReturnToSupplierTask();
// Setup the Saga implementation
//
function* fetchCall$6J(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([completeReturnToSupplierTask, 'fetchData'], action);
        yield put(completeReturnToSupplierTask.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(completeReturnToSupplierTask.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6J() {
    yield takeEvery(getType(completeReturnToSupplierTask.actions.fetch), fetchCall$6J);
}

/**
 * Implements CompleteSecondChanceProductRegistration fetch for the Redux store
 *
 * @export
 * @class CompleteSecondChanceProductRegistration
 * @extends {StoreFetchableData<EVA.Core.CompleteSecondChanceProductRegistration, EVA.Core.CompleteSecondChanceProductRegistrationResponse>}
 */
class CompleteSecondChanceProductRegistration extends StoreFetchableData {
    /**
     * Creates an instance of CompleteSecondChanceProductRegistration
     *
     * @memberof CompleteSecondChanceProductRegistration
     */
    constructor() {
        super('completeSecondChanceProductRegistration', '/message/CompleteSecondChanceProductRegistration');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteSecondChanceProductRegistration
     */
    *saga() {
        yield fork(listener$6I);
    }
}
// Export the state handling singleton
//
const completeSecondChanceProductRegistration = new CompleteSecondChanceProductRegistration();
// Setup the Saga implementation
//
function* fetchCall$6I(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([completeSecondChanceProductRegistration, 'fetchData'], action);
        yield put(completeSecondChanceProductRegistration.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(completeSecondChanceProductRegistration.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6I() {
    yield takeEvery(getType(completeSecondChanceProductRegistration.actions.fetch), fetchCall$6I);
}

/**
 * Implements CompleteShipFromStoreTask fetch for the Redux store
 *
 * @export
 * @class CompleteShipFromStoreTask
 * @extends {StoreFetchableData<EVA.Core.CompleteShipFromStoreTask, EVA.Core.EmptyResponseMessage>}
 */
class CompleteShipFromStoreTask extends StoreFetchableData {
    /**
     * Creates an instance of RitualsCompleteShipFromStoreTask
     *
     * @memberof CompleteShipFromStoreTask
     */
    constructor() {
        super('completeShipFromStoreTask', '/message/CompleteShipFromStoreTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteShipFromStoreTask
     */
    *saga() {
        yield fork(listener$6H);
    }
}
// Export the state handling singleton
//
const completeShipFromStoreTask = new CompleteShipFromStoreTask();
// Setup the Saga implementation
//
function* fetchCall$6H(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([completeShipFromStoreTask, 'fetchData'], action);
        yield put(completeShipFromStoreTask.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(completeShipFromStoreTask.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6H() {
    yield takeEvery(getType(completeShipFromStoreTask.actions.fetch), fetchCall$6H);
}

/**
 * Implements CompleteStockMovementFollowUp fetch for the Redux store
 *
 * @export
 * @class CompleteStockMovementFollowUp
 * @extends {StoreFetchableData<EVA.Core.CompleteStockMovementFollowUp, EVA.Core.EmptyResponseMessage>}
 */
class CompleteStockMovementFollowUp extends StoreFetchableData {
    /**
     * Creates an instance of CompleteStockMovementFollowUp
     *
     * @memberof CompleteStockMovementFollowUp
     */
    constructor() {
        super('completeStockMovementFollowUp', '/message/CompleteStockMovementFollowUp');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteStockMovementFollowUp
     */
    *saga() {
        yield fork(listener$6G);
    }
}
// Export the state handling singleton
//
const completeStockMovementFollowUp = new CompleteStockMovementFollowUp();
// Setup the Saga implementation
//
function* fetchCall$6G(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([completeStockMovementFollowUp, 'fetchData'], action);
        yield put(completeStockMovementFollowUp.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(completeStockMovementFollowUp.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6G() {
    yield takeEvery(getType(completeStockMovementFollowUp.actions.fetch), fetchCall$6G);
}

/**
 * Implements CompleteStockReplenishment fetch for the Redux store
 *
 * @export
 * @class CompleteStockReplenishment
 * @extends {StoreFetchableData<EVA.Core.CompleteStockReplenishment, EVA.Core.EmptyResponseMessage>}
 */
class CompleteStockReplenishment extends StoreFetchableData {
    /**
     * Creates an instance of CompleteStockReplenishment
     *
     * @memberof CompleteStockReplenishment
     */
    constructor() {
        super('completeStockReplenishment', '/message/CompleteStockReplenishment');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteStockReplenishment
     */
    *saga() {
        yield fork(listener$6F);
    }
}
// Export the state handling singleton
//
const completeStockReplenishment = new CompleteStockReplenishment();
// Setup the Saga implementation
//
function* fetchCall$6F(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([completeStockReplenishment, 'fetchData'], action);
        yield put(completeStockReplenishment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(completeStockReplenishment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6F() {
    yield takeEvery(getType(completeStockReplenishment.actions.fetch), fetchCall$6F);
}

/**
 * Implements CompleteStockReservationTask fetch for the Redux store
 *
 * @export
 * @class CompleteStockReservationTask
 * @extends {StoreFetchableData<EVA.Core.CompleteStockReservationTask, EVA.Core.EmptyResponseMessage>}
 */
class CompleteStockReservation extends StoreFetchableData {
    /**
     * Creates an instance of CompleteStockReservationTask
     *
     * @memberof CompleteStockReservation
     */
    constructor() {
        super('completeStockReservation', '/message/CompleteStockReservationTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteStockReservation
     */
    *saga() {
        yield fork(listener$6E);
    }
}
// Export the state handling singleton
//
const completeStockReservation = new CompleteStockReservation();
// Setup the Saga implementation
//
function* fetchCall$6E(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([completeStockReservation, 'fetchData'], action);
        yield put(completeStockReservation.actions.response(data));
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        const chainStarter = action.payload.chainStarter;
        // Update current user tasks
        //
        const [listAvailableUserTasksAction, , listAvailableUserTasksChainPromise] = listAvailableUserTasks.createFetchAction(listAvailableUserTasks.getState().request, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(listAvailableUserTasksAction);
        // Resolve the chain
        //
        Promise.all([listAvailableUserTasksChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(completeStockReservation.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6E() {
    yield takeEvery(getType(completeStockReservation.actions.fetch), fetchCall$6E);
}

/**
 * Implements CompleteZonedCycleCountPreCount fetch for the Redux store
 *
 * @export
 * @class CompleteZonedCycleCountPreCount
 * @extends {StoreFetchableData<EVA.Core.CompleteZonedCycleCountPreCount, EVA.Core.CountStockLabelForZonedCycleCountResponse>}
 */
class CompleteZonedCycleCountPreCount extends StoreFetchableData {
    /**
     * Creates an instance of CompleteZonedCycleCountPreCount
     *
     * @memberof CompleteZonedCycleCountPreCount
     */
    constructor() {
        super('completeZonedCycleCountPreCount', '/message/CompleteZonedCycleCountPreCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteZonedCycleCountPreCount
     */
    *saga() {
        yield fork(listener$6D);
    }
}
// Export the state handling singleton
//
const completeZonedCycleCountPreCount = new CompleteZonedCycleCountPreCount();
// Setup the Saga implementation
//
function* fetchCall$6D(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([completeZonedCycleCountPreCount, 'fetchData'], action);
        yield put(completeZonedCycleCountPreCount.actions.response(data));
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        const chainStarter = action.payload.chainStarter;
        // Update current user tasks
        //
        const [listAvailableUserTasksAction, , listAvailableUserTasksChainPromise] = listAvailableUserTasks.createFetchAction(listAvailableUserTasks.getState().request, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(listAvailableUserTasksAction);
        // Resolve the chain
        //
        Promise.all([listAvailableUserTasksChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(completeZonedCycleCountPreCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6D() {
    yield takeEvery(getType(completeZonedCycleCountPreCount.actions.fetch), fetchCall$6D);
}

/**
 * Implements CompleteZonedCycleCounts fetch for the Redux store
 *
 * @export
 * @class CompleteZonedCycleCounts
 * @extends {StoreFetchableData<EVA.Core.CompleteZonedCycleCounts, EVA.Core.CountStockLabelForZonedCycleCountResponse>}
 */
class CompleteZonedCycleCounts extends StoreFetchableData {
    /**
     * Creates an instance of CompleteZonedCycleCounts
     *
     * @memberof CompleteZonedCycleCounts
     */
    constructor() {
        super('completeZonedCycleCounts', '/message/CompleteZonedCycleCounts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CompleteZonedCycleCounts
     */
    *saga() {
        yield fork(listener$6C);
    }
}
// Export the state handling singleton
//
const completeZonedCycleCounts = new CompleteZonedCycleCounts();
// Setup the Saga implementation
//
function* fetchCall$6C(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([completeZonedCycleCounts, 'fetchData'], action);
        yield put(completeZonedCycleCounts.actions.response(data));
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        const chainStarter = action.payload.chainStarter;
        // Update current user tasks
        //
        const [listAvailableUserTasksAction, , listAvailableUserTasksChainPromise] = listAvailableUserTasks.createFetchAction(listAvailableUserTasks.getState().request, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(listAvailableUserTasksAction);
        // Resolve the chain
        //
        Promise.all([listAvailableUserTasksChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(completeZonedCycleCounts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6C() {
    yield takeEvery(getType(completeZonedCycleCounts.actions.fetch), fetchCall$6C);
}

/**
 * Implements ConfirmSubscription fetch for the Redux store
 *
 * @export
 * @class ConfirmSubscription
 * @extends {StoreFetchableData<EVA.Core.ConfirmSubscription, EVA.Core.ConfirmSubscriptionResponse>}
 */
class ConfirmSubscription extends StoreFetchableData {
    /**
     * Creates an instance of ConfirmSubscription
     *
     * @memberof ConfirmSubscription
     */
    constructor() {
        super('confirmSubscription', '/message/ConfirmSubscription');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ConfirmSubscription
     */
    *saga() {
        yield fork(listener$6B);
    }
}
// Export the state handling singleton
//
const confirmSubscription = new ConfirmSubscription();
// Setup the Saga implementation
//
function* fetchCall$6B(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([confirmSubscription, 'fetchData'], action);
        yield put(confirmSubscription.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(confirmSubscription.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6B() {
    yield takeEvery(getType(confirmSubscription.actions.fetch), fetchCall$6B);
}

/**
 * Implements CorrectWorkedHours fetch for the Redux store
 *
 * @export
 * @class CorrectWorkedHours
 * @extends {StoreFetchableData<EVA.Workforce.CorrectWorkedHours, EVA.Core.EmptyResponseMessage>}
 */
class CorrectWorkedHours extends StoreFetchableData {
    /**
     * Creates an instance of CorrectWorkedHours
     *
     * @memberof CorrectWorkedHours
     */
    constructor() {
        super('correctWorkedHours', '/message/CorrectWorkedHours');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CorrectWorkedHours
     */
    *saga() {
        yield fork(listener$6A);
    }
}
// Export the state handling singleton
//
const correctWorkedHours = new CorrectWorkedHours();
// Setup the Saga implementation
//
function* fetchCall$6A(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([correctWorkedHours, 'fetchData'], action);
        yield put(correctWorkedHours.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(correctWorkedHours.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6A() {
    yield takeEvery(getType(correctWorkedHours.actions.fetch), fetchCall$6A);
}

/**
 * Implements CountProductForInitialCycleCount fetch for the Redux store
 *
 * @export
 * @class CountProductForInitialCycleCount
 * @extends {StoreFetchableData<EVA.Core.CountProductForInitialCycleCount, EVA.Core.EmptyResponseMessage>}
 */
class CountProductForInitialCycleCount extends StoreFetchableData {
    /**
     * Creates an instance of CountProductForInitialCycleCount
     *
     * @memberof CountProductForInitialCycleCount
     */
    constructor() {
        super('countProductForInitialCycleCount', '/message/CountProductForInitialCycleCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CountProductForInitialCycleCount
     */
    *saga() {
        yield fork(listener$6z);
    }
}
// Export the state handling singleton
//
const countProductForInitialCycleCount = new CountProductForInitialCycleCount();
// Setup the Saga implementation
//
function* fetchCall$6z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([countProductForInitialCycleCount, 'fetchData'], action);
        yield put(countProductForInitialCycleCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(countProductForInitialCycleCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6z() {
    yield takeEvery(getType(countProductForInitialCycleCount.actions.fetch), fetchCall$6z);
}

/**
 * Implements CountStockLabelForZonedCycleCount fetch for the Redux store
 *
 * @export
 * @class CountStockLabelForZonedCycleCount
 * @extends {StoreFetchableData<EVA.Core.CountStockLabelForZonedCycleCount, EVA.Core.CountStockLabelForZonedCycleCountResponse>}
 */
class CountStockLabelForZonedCycleCount extends StoreFetchableData {
    /**
     * Creates an instance of CountStockLabelForZonedCycleCount
     *
     * @memberof CountStockLabelForZonedCycleCount
     */
    constructor() {
        super('countStockLabelForZonedCycleCount', '/message/CountStockLabelForZonedCycleCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CountStockLabelForZonedCycleCount
     */
    *saga() {
        yield fork(listener$6y);
    }
}
// Export the state handling singleton
//
const countStockLabelForZonedCycleCount = new CountStockLabelForZonedCycleCount();
// Setup the Saga implementation
//
function* fetchCall$6y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([countStockLabelForZonedCycleCount, 'fetchData'], action);
        yield put(countStockLabelForZonedCycleCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(countStockLabelForZonedCycleCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6y() {
    yield takeEvery(getType(countStockLabelForZonedCycleCount.actions.fetch), fetchCall$6y);
}

/**
 * Implements CreateAddressBookItem fetch for the Redux store
 *
 * @export
 * @class CreateAddressBookItem
 * @extends {StoreFetchableData<EVA.Core.CreateAddressBookItem, EVA.Core.CreateResponse>}
 */
class CreateAddressBookItem extends StoreFetchableData {
    /**
     * Creates an instance of CreateAddressBookItem
     *
     * @memberof CreateAddressBookItem
     */
    constructor() {
        super('createAddressBookItem', '/message/CreateAddressBookItem');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateAddressBookItem
     */
    *saga() {
        yield fork(listener$6x);
    }
}
// Export the state handling singleton
//
const createAddressBookItem = new CreateAddressBookItem();
// Setup the Saga implementation
//
function* fetchCall$6x(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createAddressBookItem, 'fetchData'], action);
        yield put(createAddressBookItem.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createAddressBookItem.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6x() {
    yield takeEvery(getType(createAddressBookItem.actions.fetch), fetchCall$6x);
}

/**
 * Implements CreateBlob fetch for the Redux store
 *
 * @export
 * @class CreateBlob
 * @extends {StoreFetchableData<EVA.Blobs.CreateBlob, EVA.Blobs.CreateBlobResponse>}
 */
class CreateBlob extends StoreFetchableData {
    /**
     * Creates an instance of CreateBlob
     *
     * @memberof CreateBlob
     */
    constructor() {
        super('createBlob', '/message/CreateBlob');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateBlob
     */
    *saga() {
        yield fork(listener$6w);
    }
}
// Export the state handling singleton
//
const createBlob = new CreateBlob();
// Setup the Saga implementation
//
function* fetchCall$6w(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createBlob, 'fetchData'], action);
        yield put(createBlob.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createBlob.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6w() {
    yield takeEvery(getType(createBlob.actions.fetch), fetchCall$6w);
}

/**
 * Implements CreateCashDeposit fetch for the Redux store
 *
 * @export
 * @class CreateCashDeposit
 * @extends {StoreFetchableData<EVA.Core.CreateCashDeposit, EVA.Core.EmptyResponseMessage>}
 */
class CreateCashDeposit extends StoreFetchableData {
    /**
     * Creates an instance of CreateCashDeposit
     *
     * @memberof CreateCashDeposit
     */
    constructor() {
        super('createCashDeposit', '/message/CreateCashDeposit');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateCashDeposit
     */
    *saga() {
        yield fork(listener$6v);
    }
}
// Export the state handling singleton
//
const createCashDeposit = new CreateCashDeposit();
// Setup the Saga implementation
//
function* fetchCall$6v(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createCashDeposit, 'fetchData'], action);
        yield put(createCashDeposit.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createCashDeposit.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6v() {
    yield takeEvery(getType(createCashDeposit.actions.fetch), fetchCall$6v);
}

/**
 * Implements CreateCashExpense fetch for the Redux store
 *
 * @export
 * @class CreateCashExpense
 * @extends {StoreFetchableData<EVA.Core.CreateCashExpense, EVA.Core.EmptyResponseMessage>}
 */
class CreateCashExpense extends StoreFetchableData {
    /**
     * Creates an instance of CreateCashExpense
     *
     * @memberof CreateCashExpense
     */
    constructor() {
        super('createCashExpense', '/message/CreateCashExpense');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateCashExpense
     */
    *saga() {
        yield fork(listener$6u);
    }
}
// Export the state handling singleton
//
const createCashExpense = new CreateCashExpense();
// Setup the Saga implementation
//
function* fetchCall$6u(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createCashExpense, 'fetchData'], action);
        yield put(createCashExpense.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createCashExpense.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6u() {
    yield takeEvery(getType(createCashExpense.actions.fetch), fetchCall$6u);
}

/**
 * Implements CreateCompanyForUser fetch for the Redux store
 *
 * @export
 * @class CreateCompanyForUser
 * @extends {StoreFetchableData<EVA.Core.CreateCompanyForUser, EVA.Core.EmptyResponseMessage>}
 */
class CreateCompanyForUser extends StoreFetchableData {
    /**
     * Creates an instance of CreateCompanyForUser
     *
     * @memberof CreateCompanyForUser
     */
    constructor() {
        super('createCompanyForUser', '/message/CreateCompanyForUser');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateCompanyForUser
     */
    *saga() {
        yield fork(listener$6t);
    }
}
// Export the state handling singleton
//
const createCompanyForUser = new CreateCompanyForUser();
// Setup the Saga implementation
//
function* fetchCall$6t(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createCompanyForUser, 'fetchData'], action);
        yield put(createCompanyForUser.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createCompanyForUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6t() {
    yield takeEvery(getType(createCompanyForUser.actions.fetch), fetchCall$6t);
}

/**
 * Implements GetAvailableServices fetch for the Redux store
 *
 * @export
 * @class GetAvailableServices
 * @extends {StoreFetchableData<EVA.Core.GetAvailableServices, EVA.Core.GetAvailableServicesResponse>}
 */
class GetAvailableServices extends StoreFetchableData {
    /**
     * Creates an instance of GetAvailableServices
     *
     * @memberof GetAvailableServices
     */
    constructor() {
        super('getAvailableServices', '/message/GetAvailableServices');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAvailableServices
     */
    *saga() {
        yield fork(listener$6s);
    }
}
// Export the state handling singleton
//
const getAvailableServices = new GetAvailableServices();
// Setup the Saga implementation
//
function* fetchCall$6s(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAvailableServices, 'fetchData'], action);
        yield put(getAvailableServices.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAvailableServices.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6s() {
    yield takeEvery(getType(getAvailableServices.actions.fetch), fetchCall$6s);
}

/**
 * Implements ListStationsForOrganizationUnit fetch for the Redux store
 *
 * @export
 * @class ListStationsForOrganizationUnit
 * @extends {StoreFetchableData<EVA.Core.ListStationsForOrganizationUnit, EVA.Core.ListStationsForOrganizationUnitResponse>}
 */
class ListStationsForOrganizationUnit extends StoreFetchableData {
    /**
     * Creates an instance of ListStationsForOrganizationUnit
     *
     * @memberof ListStationsForOrganizationUnit
     */
    constructor() {
        super('listStationsForOrganizationUnit', '/message/ListStationsForOrganizationUnit');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListStationsForOrganizationUnit
     */
    *saga() {
        yield fork(listener$6r);
    }
}
// Export the state handling singleton
//
const listStationsForOrganizationUnit = new ListStationsForOrganizationUnit();
// Setup the Saga implementation
//
function* fetchCall$6r(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listStationsForOrganizationUnit, 'fetchData'], action);
        yield put(listStationsForOrganizationUnit.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listStationsForOrganizationUnit.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6r() {
    yield takeEvery(getType(listStationsForOrganizationUnit.actions.fetch), fetchCall$6r);
}

/**
 * Implements GetCurrentUser fetch for the Redux store
 *
 * @export
 * @class GetCurrentUser
 * @extends {StoreFetchableData<EVA.Core.GetCurrentUser, EVA.Core.GetCurrentUserResponse>}
 */
class GetCurrentUser extends StoreFetchableData {
    /**
     * Creates an instance of GetCurrentUser
     *
     * @memberof GetCurrentUser
     */
    constructor() {
        super('getCurrentUser', '/message/GetCurrentUser');
        /**
         * User token expiration stream
         * Will emit when a service returns a 401 and the user token has expired
         *
         * @memberof GetCurrentUser
         */
        this.expired$ = new Subject();
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetCurrentUser
     */
    *saga() {
        yield fork(listener$6q);
    }
    /**
     * Initial state with extended properties
     *
     * @type {IGetCurrentUserState}
     * @memberof GetCurrentUser
     */
    get initialState() {
        return {
            isFetching: false,
            request: null,
            response: null,
            error: null,
            chainStarter: this.name,
            isAnonymous: false,
            isLoggedIn: false,
            isCustomer: false,
            isEmployee: false,
        };
    }
    /**
     * Called when a 401 response is received to expire the current user token and state
     *
     * @memberof GetCurrentUser
     */
    expire() {
        // Unset the user token and current order on the settings
        //
        settings.userToken = '';
        settings.currentOrderId = 0;
        // Get the user id before we purge the state
        //
        const userId = get(this.getState(), 'response.User.ID');
        // Purge the redux state
        // We will need to dump the entire state except:
        //
        // * getApplicationConfiguration
        //
        const oldState = core.dump({
            getApplicationConfiguration: getApplicationConfiguration.getState(),
        });
        // Store the current user state and start a new one
        //
        if (userId) {
            settings.saveState(userId, oldState);
        }
        // Fetch the current user to update the store to anonymous user
        // Because we have a new state the cart will be empty
        //
        const [getCurrentUserAction] = this.createFetchAction();
        store.dispatch(getCurrentUserAction);
        // Emit the user token expired
        //
        this.expired$.next(new Date());
    }
    /**
     * Return an observable that contains the current user if it is of type employee
     *
     * @returns {(Observable<EVA.Core.GetCurrentUserResponse>)}
     * @memberof GetCurrentUser
     */
    getEmployee$() {
        return this.getUserType$('isEmployee');
    }
    /**
     * Return an observable that contains the current user if it is of type customer
     *
     * @returns {(Observable<EVA.Core.GetCurrentUserResponse>)}
     * @memberof GetCurrentUser
     */
    getCustomer$() {
        return this.getUserType$('isCustomer');
    }
    /**
     * Return an observable that contains the current user if it is of type customer or employee
     *
     * @returns {(Observable<EVA.Core.GetCurrentUserResponse>)}
     * @memberof GetCurrentUser
     */
    getLoggedInUser$() {
        return this.getUserType$('isLoggedIn');
    }
    /**
     * Return an observable that contains the current user if it is of type anonymous
     *
     * @returns {(Observable<EVA.Core.GetCurrentUserResponse>)}
     * @memberof GetCurrentUser
     */
    getAnonymous$() {
        return this.getUserType$('isAnonymous');
    }
    /**
     * The extended collection of reducer functions
     *
     * @protected
     * @returns {{ [ reducerName: string ]: ( state: any, action: AnyAction ) => any }}
     * @memberof GetCurrentUser
     */
    getReducerFunctions() {
        const baseReducerFuncs = super.getReducerFunctions();
        baseReducerFuncs.isAnonymous = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.fetch):
                    // If logout is the chain starter unset flag
                    //
                    return get(action, 'payload.chainStarter') === 'logout' ? false : state;
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const userType = get(payload, 'User.Type');
                    return (userType & 4) !== 0;
                default:
                    return state;
            }
        };
        baseReducerFuncs.isLoggedIn = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.fetch):
                    // If logout is the chain starter unset flag
                    //
                    return get(action, 'payload.chainStarter') === 'logout' ? false : state;
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const userType = get(payload, 'User.Type');
                    return ((userType & 1) !== 0) || ((userType & 2) !== 0);
                default:
                    return state;
            }
        };
        baseReducerFuncs.isCustomer = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.fetch):
                    // If logout is the chain starter unset flag
                    //
                    return get(action, 'payload.chainStarter') === 'logout' ? false : state;
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const userType = get(payload, 'User.Type');
                    return (userType & 2) !== 0;
                default:
                    return state;
            }
        };
        baseReducerFuncs.isEmployee = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.fetch):
                    // If logout is the chain starter unset flag
                    //
                    return get(action, 'payload.chainStarter') === 'logout' ? false : state;
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const userType = get(payload, 'User.Type');
                    return (userType & 1) !== 0;
                default:
                    return state;
            }
        };
        return baseReducerFuncs;
    }
    /**
     * Helper function to check for certain user type
     *
     * @private
     * @param {('isCustomer' | 'isEmployee' | 'isAnonymous' | 'isLoggedIn')} typeCheck Type of user to check for
     * @returns {(Observable<EVA.Core.GetCurrentUserResponse>)}
     * @memberof GetCurrentUser
     */
    getUserType$(typeCheck) {
        return core.state$.pipe(distinctUntilChanged((prev, next) => {
            return isEqual(get(prev, this.name), get(next, this.name));
        }), map((rootState) => {
            const state = get(rootState, this.name);
            if (state[typeCheck]) {
                return state.response;
            }
            else {
                return null;
            }
        }));
    }
}
// Export the state handling singleton
//
const getCurrentUser = new GetCurrentUser();
// Setup the Saga implementation
//
function* fetchCall$6q(action) {
    const fetchActionPayload = action.payload;
    const chainStarter = action.payload.chainStarter;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([getCurrentUser, 'fetchData'], action);
        yield put(getCurrentUser.actions.response(data));
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Always update the application configuration when the user is updated
        //
        const [configAction, , configChainPromise] = getApplicationConfiguration.createFetchAction(undefined, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(configAction);
        // Always fetch the available services when the user is updated
        //
        const [availableServicesAction, , availableServicesChainPromise] = getAvailableServices.createFetchAction(undefined, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(availableServicesAction);
        // Fetch the station list for the organization if this is an employee login
        // We need to await the configuration and available services first
        // The available services might dictate if we are even allowed to fetch them
        //
        yield availableServicesChainPromise;
        let optionalListStationsForOrganizationUnitChainPromise = Promise.resolve(undefined);
        const userType = get(data, 'User.Type');
        if ((userType & 1) !== 0) {
            log$1('[EVA-SDK-REDUX:login] Fetching station list for employee');
            const [listStationsForOrganizationUnitAction, , listStationsForOrganizationUnitChainPromise] = listStationsForOrganizationUnit.createFetchAction({ OrganizationUnitID: get(data, 'User.CurrentOrganizationID') }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
            yield put(listStationsForOrganizationUnitAction);
            optionalListStationsForOrganizationUnitChainPromise = listStationsForOrganizationUnitChainPromise;
        }
        // Resolve the chain
        //
        Promise.all([optionalListStationsForOrganizationUnitChainPromise, configChainPromise, availableServicesChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(getCurrentUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6q() {
    yield takeEvery(getType(getCurrentUser.actions.fetch), fetchCall$6q);
}

/**
 * Implements CreateCustomer fetch for the Redux store
 *
 * @export
 * @class CreateCustomer
 * @extends {StoreFetchableData<EVA.Core.CreateCustomer, EVA.Core.CreateCustomerResponse>}
 */
class CreateCustomer extends StoreFetchableData {
    /**
     * Creates an instance of CreateCustomer
     *
     * @memberof CreateCustomer
     */
    constructor() {
        super('createCustomer', '/message/CreateCustomer');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateCustomer
     */
    *saga() {
        yield fork(listener$6p);
    }
}
// Export the state handling singleton
//
const createCustomer = new CreateCustomer();
// Setup the Saga implementation
//
function* fetchCall$6p(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([createCustomer, 'fetchData'], action);
        const createResult = data.Result;
        const userToken = get(data, 'User.AuthenticationToken');
        // EVA.Core.CreateCustomerResults
        //  * CreatedCustomer = 0
        //  * InvalidEmail = 1
        //  * UsedIncompleteAccount = 2
        //  * EmailAlreadyInUse = 3
        //
        if (createResult === 0 || createResult === 2) {
            yield put(createCustomer.actions.response(data));
            const chainStarter = action.payload.chainStarter;
            // Resolve the fetch promise
            //
            fetchActionPayload.fetchResolve(data);
            // Check for auto-login
            //
            if (userToken) {
                // Store the user token on the settings
                //
                settings.userToken = userToken;
                // Fetch the current user to update the store
                //
                const [getCurrentUserAction, , getCurrentUserChainPromise] = getCurrentUser.createFetchAction(undefined, chainStarter, undefined, undefined, undefined, undefined, chainContext);
                yield put(getCurrentUserAction);
                // Update the cart for the user
                //
                const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
                yield put(getShoppingCartInfoAction);
                // Resolve the chain
                //
                Promise.all([getCurrentUserChainPromise, getShoppingCartInfoChainPromise])
                    .then((chainResponses) => {
                    chainResponses.unshift(data);
                    fetchActionPayload.chainResolve(chainResponses);
                })
                    .catch(fetchActionPayload.chainReject);
            }
            else {
                // Resolve the chain
                //
                fetchActionPayload.chainResolve([data, undefined]);
            }
        }
        else {
            yield put(createCustomer.actions.error(data));
            // Reject both the fetch and chain promise
            //
            fetchActionPayload.fetchReject(data);
            fetchActionPayload.chainReject(data);
        }
    }
    catch (err) {
        yield put(createCustomer.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6p() {
    yield takeEvery(getType(createCustomer.actions.fetch), fetchCall$6p);
}

/**
 * Implements CreateCustomerReturn fetch for the Redux store
 *
 * @export
 * @class CreateCustomerReturn
 * @extends {StoreFetchableData<EVA.Core.CreateCustomerReturn, EVA.Core.ShoppingCartResponse>}
 */
class CreateCustomerReturn extends StoreFetchableData {
    /**
     * Creates an instance of CreateCustomerReturn
     *
     * @memberof CreateCustomerReturn
     */
    constructor() {
        super('createCustomerReturn', '/message/CreateCustomerReturn');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateCustomerReturn
     */
    *saga() {
        yield fork(listener$6o);
    }
}
// Export the state handling singleton
//
const createCustomerReturn = new CreateCustomerReturn();
// Setup the Saga implementation
//
function* fetchCall$6o(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([createCustomerReturn, 'fetchData'], action);
        yield put(createCustomerReturn.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // CreateCustomerReturn is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(createCustomerReturn.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6o() {
    yield takeEvery(getType(createCustomerReturn.actions.fetch), fetchCall$6o);
}

/**
 * Implements CreateCustomerReturnWithoutOrder fetch for the Redux store
 *
 * @export
 * @class CreateCustomerReturnWithoutOrder
 * @extends {StoreFetchableData<EVA.Core.CreateCustomerReturnWithoutOrder, EVA.Core.SimpleShoppingCartResponse>}
 */
class CreateCustomerReturnWithoutOrder extends StoreFetchableData {
    /**
     * Creates an instance of CreateCustomerReturnWithoutOrder
     *
     * @memberof CreateCustomerReturnWithoutOrder
     */
    constructor() {
        super('createCustomerReturnWithoutOrder', '/message/CreateCustomerReturnWithoutOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateCustomerReturnWithoutOrder
     */
    *saga() {
        yield fork(listener$6n);
    }
}
// Export the state handling singleton
//
const createCustomerReturnWithoutOrder = new CreateCustomerReturnWithoutOrder();
// Setup the Saga implementation
//
function* fetchCall$6n(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createCustomerReturnWithoutOrder, 'fetchData'], action);
        yield put(createCustomerReturnWithoutOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createCustomerReturnWithoutOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6n() {
    yield takeEvery(getType(createCustomerReturnWithoutOrder.actions.fetch), fetchCall$6n);
}

/**
 * Implements CreateFullStockCount fetch for the Redux store
 *
 * @export
 * @class CreateFullStockCount
 * @extends {StoreFetchableData<EVA.Core.CreateFullStockCount, EVA.Core.CreateFullStockCountResponse>}
 */
class CreateFullStockCount extends StoreFetchableData {
    /**
     * Creates an instance of CreateFullStockCount
     *
     * @memberof CreateFullStockCount
     */
    constructor() {
        super('createFullStockCount', '/message/CreateFullStockCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateFullStockCount
     */
    *saga() {
        yield fork(listener$6m);
    }
}
// Export the state handling singleton
//
const createFullStockCount = new CreateFullStockCount();
// Setup the Saga implementation
//
function* fetchCall$6m(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createFullStockCount, 'fetchData'], action);
        yield put(createFullStockCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createFullStockCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6m() {
    yield takeEvery(getType(createFullStockCount.actions.fetch), fetchCall$6m);
}

/**
 * Implements CreateIdentificationPinForEmployee fetch for the Redux store
 *
 * @export
 * @class CreateIdentificationPinForEmployee
 * @extends {StoreFetchableData<EVA.Core.Management.CreateIdentificationPinForEmployee, EVA.Core.Management.CreateIdentificationPinForEmployeeResponse>}
 */
class CreateIdentificationPinForEmployee extends StoreFetchableData {
    /**
     * Creates an instance of CreateIdentificationPinForEmployee
     *
     * @memberof CreateIdentificationPinForEmployee
     */
    constructor() {
        super('createIdentificationPinForEmployee', '/message/CreateIdentificationPinForEmployee');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateIdentificationPinForEmployee
     */
    *saga() {
        yield fork(listener$6l);
    }
}
// Export the state handling singleton
//
const createIdentificationPinForEmployee = new CreateIdentificationPinForEmployee();
// Setup the Saga implementation
//
function* fetchCall$6l(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createIdentificationPinForEmployee, 'fetchData'], action);
        yield put(createIdentificationPinForEmployee.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createIdentificationPinForEmployee.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6l() {
    yield takeEvery(getType(createIdentificationPinForEmployee.actions.fetch), fetchCall$6l);
}

/**
 * Implements CreateInterbranchOrder fetch for the Redux store
 *
 * @export
 * @class CreateInterbranchOrder
 * @extends {StoreFetchableData<EVA.Core.CreateInterbranchOrder, EVA.Core.EmptyResponseMessage>}
 */
class CreateInterbranchOrder extends StoreFetchableData {
    /**
     * Creates an instance of CreateInterbranchOrder
     *
     * @memberof CreateInterbranchOrder
     */
    constructor() {
        super('createInterbranchOrder', '/message/CreateInterbranchOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateInterbranchOrder
     */
    *saga() {
        yield fork(listener$6k);
    }
}
// Export the state handling singleton
//
const createInterbranchOrder = new CreateInterbranchOrder();
// Setup the Saga implementation
//
function* fetchCall$6k(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createInterbranchOrder, 'fetchData'], action);
        yield put(createInterbranchOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createInterbranchOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6k() {
    yield takeEvery(getType(createInterbranchOrder.actions.fetch), fetchCall$6k);
}

/**
 * Implements CreateOrderLineUnitPriceCorrection fetch for the Redux store
 *
 * @export
 * @class CreateOrderLineUnitPriceCorrection
 * @extends {StoreFetchableData<EVA.Core.CreateOrderLineUnitPriceCorrection, EVA.Core.CreateOrderLineUnitPriceCorrectionResponse>}
 */
class CreateOrderLineUnitPriceCorrection extends StoreFetchableData {
    /**
     * Creates an instance of CreateOrderLineUnitPriceCorrection
     *
     * @memberof CreateOrderLineUnitPriceCorrection
     */
    constructor() {
        super('createOrderLineUnitPriceCorrection', '/message/CreateOrderLineUnitPriceCorrection');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateOrderLineUnitPriceCorrection
     */
    *saga() {
        yield fork(listener$6j);
    }
}
// Export the state handling singleton
//
const createOrderLineUnitPriceCorrection = new CreateOrderLineUnitPriceCorrection();
// Setup the Saga implementation
//
function* fetchCall$6j(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createOrderLineUnitPriceCorrection, 'fetchData'], action);
        yield put(createOrderLineUnitPriceCorrection.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createOrderLineUnitPriceCorrection.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6j() {
    yield takeEvery(getType(createOrderLineUnitPriceCorrection.actions.fetch), fetchCall$6j);
}

/**
 * Helper function to have a promise based timeout you can await
 *
 * @param {number} timeout The amount of time to wait in milliseconds
 * @param {*} [value] The value to resolve with
 * @returns
 */
const delay = (timeout, value) => {
    return new Promise((resolve) => {
        setTimeout(resolve.bind(null, value), timeout);
    });
};

/**
 * Implements CreateRefund fetch for the Redux store
 *
 * @export
 * @class CreateRefund
 * @extends {StoreFetchableData<EVA.Core.CreateRefund, EVA.Core.CreateRefundResponse>}
 */
class CreateRefund extends StoreFetchableData {
    /**
     * Creates an instance of CreateRefund
     *
     * @memberof CreateRefund
     */
    constructor() {
        super('createRefund', '/message/CreateRefund');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateRefund
     */
    *saga() {
        yield fork(listener$6i);
    }
}
// Export the state handling singleton
//
const createRefund = new CreateRefund();
// Setup the Saga implementation
//
function* fetchCall$6i(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createRefund, 'fetchData'], action);
        yield put(createRefund.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createRefund.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6i() {
    yield takeEvery(getType(createRefund.actions.fetch), fetchCall$6i);
}

/**
 * Implements GetAvailableRefundPaymentMethodsForOrder fetch for the Redux store
 *
 * @export
 * @class GetAvailableRefundPaymentMethodsForOrder
 * @extends {StoreFetchableData<EVA.Core.GetAvailableRefundPaymentMethodsForOrder, EVA.Core.GetAvailableRefundPaymentMethodsResponse>}
 */
class GetAvailableRefundPaymentMethodsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetAvailableRefundPaymentMethodsForOrder
     *
     * @memberof GetAvailableRefundPaymentMethodsForOrder
     */
    constructor() {
        super('getAvailableRefundPaymentMethodsForOrder', '/message/GetAvailableRefundPaymentMethodsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAvailableRefundPaymentMethodsForOrder
     */
    *saga() {
        yield fork(listener$6h);
    }
}
// Export the state handling singleton
//
const getAvailableRefundPaymentMethodsForOrder = new GetAvailableRefundPaymentMethodsForOrder();
// Setup and Saga implementation
//
function* fetchCall$6h(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAvailableRefundPaymentMethodsForOrder, 'fetchData'], action);
        yield put(getAvailableRefundPaymentMethodsForOrder.actions.response(data));
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAvailableRefundPaymentMethodsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6h() {
    yield takeEvery(getType(getAvailableRefundPaymentMethodsForOrder.actions.fetch), fetchCall$6h);
}

/**
 * Implements GetOrder fetch for the Redux store
 *
 * @export
 * @class GetOrder
 * @extends {StoreFetchableData<EVA.Core.GetOrder, EVA.Core.GetOrderResponse>}
 */
class GetOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetOrder
     *
     * @memberof GetOrder
     */
    constructor() {
        // Add the calculate checkout status action
        //
        const additionalActions = {
            checkoutStatus: createAction('CHECKOUT_STATUS_getOrder', (request) => {
                return ({ type: 'CHECKOUT_STATUS_getOrder', payload: request });
            }),
        };
        super('getOrder', '/message/GetOrder', undefined, undefined, additionalActions);
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetOrder
     */
    *saga() {
        yield fork(listener$6g);
    }
    /**
     * Initial state with extended properties
     *
     * @type {IGetOrderState}
     * @memberof GetOrder
     */
    get initialState() {
        return {
            isFetching: false,
            request: null,
            response: null,
            error: null,
            chainStarter: this.name,
            // Required data based deductions
            //
            pickDiscountProducts: null,
            productRequirementProducts: null,
            serialNumbers: null,
            productBundles: null,
            customerValid: true,
            customerIssues: [],
            orderValid: true,
            orderIssues: [],
            orderLinesValid: true,
            orderLineIssues: {},
            // Deduced order status
            //
            status: OrderStatus.new,
            orderLineActionTypes: [],
            checkoutStatus: {
                mode: null,
                steps: {},
            },
            count: {
                discount: 0,
                non_discount: 0,
                giftcard: 0,
                bundle: 0,
                configurable: 0,
                service: 0,
                total: 0,
            },
        };
    }
    /**
     * Return the current order as an observable stream
     *
     * @returns {Observable<EVA.Core.OrderDto>}
     * @memberof GetShoppingCart
     */
    getOrder$() {
        return this.getPropertyState$('response.Result');
    }
    /**
     * The extended collection of reducer functions
     *
     * @protected
     * @returns {{ [ reducerName: string ]: ( state: any, action: AnyAction ) => any }}
     * @memberof GetShoppingCart
     */
    getReducerFunctions() {
        const baseReducerFuncs = super.getReducerFunctions();
        baseReducerFuncs.pickDiscountProducts = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    const order = get(payload, 'Result');
                    const currentLines = get(order, 'Lines', []);
                    // Find the requirements of type 'PickDiscountProduct'
                    //
                    const pickDiscountProducts = {};
                    each(requiredData === null || requiredData === void 0 ? void 0 : requiredData.OrderLines, (orderLineRequirements, orderLineId) => {
                        each(orderLineRequirements, (orderLineRequirement) => {
                            if (orderLineRequirement.Name === 'PickDiscountProduct') {
                                // It really shouldn't happen that the discount line is not in
                                // the current cart. The chain ordering should ensure this
                                // We will cast it here to force typescript to compile
                                //
                                const discountLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(orderLineId, 10));
                                const productLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === get(discountLine, 'ParentID'));
                                pickDiscountProducts[orderLineId] = {
                                    requirement: orderLineRequirement,
                                    discountOrderLine: discountLine,
                                    productOrderLine: productLine,
                                };
                            }
                        });
                    });
                    return isEmpty(pickDiscountProducts) ? null : pickDiscountProducts;
                default:
                    return state;
            }
        };
        baseReducerFuncs.productRequirementProducts = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    const order = get(payload, 'Result');
                    const currentLines = get(order, 'Lines', []);
                    const currentProductRequirementValues = get(payload, 'AdditionalOrderData.ProductRequirements', []);
                    // Find the requirements of type 'ProductRequirement'
                    //
                    const productRequirementProducts = {};
                    each(requiredData === null || requiredData === void 0 ? void 0 : requiredData.OrderLines, (orderLineRequirements, orderLineId) => {
                        each(orderLineRequirements, (orderLineRequirement) => {
                            if (orderLineRequirement.Name === 'ProductRequirement') {
                                const productLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(orderLineId, 10));
                                productRequirementProducts[orderLineId] = {
                                    requirement: orderLineRequirement,
                                    productOrderLine: productLine,
                                    values: filter(currentProductRequirementValues, (requirement) => requirement.OrderLineID === parseInt(orderLineId, 10)),
                                };
                            }
                        });
                    });
                    return isEmpty(productRequirementProducts) ? null : productRequirementProducts;
                default:
                    return state;
            }
        };
        baseReducerFuncs.serialNumbers = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    const order = get(payload, 'Result');
                    const currentLines = get(order, 'Lines', []);
                    // Find the requirements of type 'SerialNumber'
                    //
                    const serialNumbers = {};
                    each(requiredData === null || requiredData === void 0 ? void 0 : requiredData.OrderLines, (orderLineRequirements, orderLineId) => {
                        each(orderLineRequirements, (orderLineRequirement) => {
                            if (orderLineRequirement.Name === 'SerialNumber') {
                                const productLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(orderLineId, 10));
                                serialNumbers[orderLineId] = {
                                    requirement: orderLineRequirement,
                                    productOrderLine: productLine,
                                    serialNumber: get(productLine, 'SerialNumber'),
                                };
                            }
                        });
                    });
                    return isEmpty(serialNumbers) ? null : serialNumbers;
                default:
                    return state;
            }
        };
        baseReducerFuncs.productBundles = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    const order = get(payload, 'Result');
                    const currentLines = get(order, 'Lines', []);
                    // Find the requirements of type 'CompleteProductBundle'
                    //
                    const productBundles = {};
                    each(requiredData === null || requiredData === void 0 ? void 0 : requiredData.OrderLines, (orderLineRequirements, orderLineId) => {
                        each(orderLineRequirements, (orderLineRequirement) => {
                            if (orderLineRequirement.Name === 'CompleteProductBundle') {
                                const productLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(orderLineId, 10));
                                productBundles[orderLineId] = {
                                    requirement: orderLineRequirement,
                                    productOrderLine: productLine,
                                };
                            }
                        });
                    });
                    return isEmpty(productBundles) ? null : productBundles;
                default:
                    return state;
            }
        };
        baseReducerFuncs.customerValid = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    // Find invalid customer requirements
                    //
                    const requirements = (requiredData === null || requiredData === void 0 ? void 0 : requiredData.Customer) || [];
                    const invalidRequirements = filter(requirements, { IsValid: false });
                    return invalidRequirements.length === 0;
                default:
                    return state;
            }
        };
        baseReducerFuncs.customerIssues = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    // Find invalid customer requirements
                    //
                    const requirements = (requiredData === null || requiredData === void 0 ? void 0 : requiredData.Customer) || [];
                    const invalidRequirements = filter(requirements, { IsValid: false });
                    return invalidRequirements;
                default:
                    return state;
            }
        };
        baseReducerFuncs.orderValid = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    // Find invalid order requirements
                    //
                    const requirements = (requiredData === null || requiredData === void 0 ? void 0 : requiredData.Order) || [];
                    const invalidOrderRequirements = filter(requirements, { IsValid: false });
                    return invalidOrderRequirements.length === 0;
                default:
                    return state;
            }
        };
        baseReducerFuncs.orderIssues = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    // Find invalid order requirements
                    //
                    const requirements = (requiredData === null || requiredData === void 0 ? void 0 : requiredData.Order) || [];
                    const invalidRequirements = filter(requirements, { IsValid: false });
                    return invalidRequirements;
                default:
                    return state;
            }
        };
        baseReducerFuncs.orderLinesValid = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    // Find invalid order line requirements
                    // Order line requirements are index by their orderLineId
                    //
                    const requirements = (requiredData === null || requiredData === void 0 ? void 0 : requiredData.OrderLines) || {};
                    const invalidRequirements = pickBy(requirements, (orderLineRequirements) => {
                        return !!find(orderLineRequirements, { IsValid: false });
                    });
                    return size(invalidRequirements) === 0;
                default:
                    return state;
            }
        };
        baseReducerFuncs.orderLineIssues = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const requiredData = get(payload, 'AdditionalOrderData.RequiredData', {});
                    // Find invalid order line requirements
                    // Order line requirements are index by their orderLineId
                    //
                    const requirements = (requiredData === null || requiredData === void 0 ? void 0 : requiredData.OrderLines) || {};
                    const invalidRequirements = pickBy(requirements, (orderLineRequirements) => {
                        return !!find(orderLineRequirements, { IsValid: false });
                    });
                    // Only return invalid requirements per order line
                    //
                    return mapValues(invalidRequirements, (orderLineRequirements) => {
                        return filter(orderLineRequirements, { IsValid: false });
                    });
                default:
                    return state;
            }
        };
        /**
         * Updated the checkout status based on:
         * - Order Lines LineActionTypes
         * - GetRequiredData requirements
         */
        baseReducerFuncs.checkoutStatus = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.checkoutStatus):
                    // Retrieve required data
                    //
                    const orderState = this.getState();
                    const payload = get(action, 'payload');
                    const currentOrder = get(payload, 'order.response.Result');
                    const currentOrderLines = get(payload, 'order.response.Result.Lines');
                    const currentRequiredData = get(payload, 'order.response.AdditionalOrderData.RequiredData', {});
                    const orderRequirements = get(currentRequiredData, 'Order');
                    // Collect the cart order line types currently in use
                    //
                    // None = 0,
                    // ReserveLine = 1,
                    // OrderLine = 2,
                    // ShipLine = 3,
                    // Delivery = 4,
                    //
                    // The None type will be omitted
                    //
                    const lineActionTypesInCart = uniq(filter(map$1(currentOrderLines, 'LineActionType'), (lineActionType) => lineActionType !== 0));
                    // Determine the order mode
                    //
                    let orderMode = OrderMode.empty;
                    if (!!(get(currentOrder, 'Characteristics') & 4)) {
                        orderMode = OrderMode.interbranch;
                    }
                    else if (!!(get(currentOrder, 'Characteristics') & 128)) {
                        orderMode = OrderMode.returnToSupplier;
                    }
                    else if (lineActionTypesInCart.length === 1 && lineActionTypesInCart[0] === 1) {
                        orderMode = OrderMode.reservation;
                    }
                    else if (lineActionTypesInCart.length === 1 && lineActionTypesInCart[0] === 2) {
                        orderMode = OrderMode.order;
                    }
                    else if (lineActionTypesInCart.length === 1 && lineActionTypesInCart[0] === 3) {
                        orderMode = OrderMode.sale;
                    }
                    else if (lineActionTypesInCart.length === 1 && lineActionTypesInCart[0] === 4) {
                        orderMode = OrderMode.delivery;
                    }
                    else if (lineActionTypesInCart.length > 1) {
                        orderMode = OrderMode.mixed;
                    }
                    // Start a blank status object
                    // and then collect order requirements and determine steps
                    //
                    const checkoutStatus = {
                        mode: orderMode,
                        steps: {},
                    };
                    //////////////////////////////////////////////////////////////////////
                    // Order: Customer
                    //////////////////////////////////////////////////////////////////////
                    //
                    const attachCustomerOrderRequirement = find(orderRequirements, { Name: 'Customer' });
                    if (attachCustomerOrderRequirement) {
                        // Customer needs to be both attached and valid
                        //
                        const customerOrderStep = {
                            name: 'customer',
                            possible: true,
                            required: true,
                            valid: attachCustomerOrderRequirement.IsValid && orderState.customerIssues.length === 0,
                        };
                        checkoutStatus.steps.customer = customerOrderStep;
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: Create order
                    //////////////////////////////////////////////////////////////////////
                    //
                    const createOrderRequirement = find(orderRequirements, { Name: 'CreateOrder' });
                    if (createOrderRequirement) {
                        // Create order can be called multiple times but we'll set the required
                        // tag to false if already valid
                        //
                        const createOrderStep = {
                            name: 'createOrder',
                            possible: true,
                            required: !createOrderRequirement.IsValid,
                            valid: createOrderRequirement.IsValid,
                        };
                        checkoutStatus.steps.createOrder = createOrderStep;
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: Place order
                    //////////////////////////////////////////////////////////////////////
                    //
                    // Check current issues to determine if possible
                    // Possible needs to start as true for this logic to work
                    //
                    // PlaceOrder = 1,
                    // Payment = 2,
                    // Ship = 4,
                    // Invoice = 8,
                    // All = 15,
                    //
                    let noCustomerIssues = true;
                    each(orderState.customerIssues, (issue) => {
                        noCustomerIssues = noCustomerIssues && !(issue.RequiredFor & 1);
                    });
                    /**
                     * We need to make an exception for the place order requirement.
                     * As it is `RequiredFor` is place, it was lowered by the backend from `RequiredFor`=4 (`Ship`) to `RequiredFor`=1 (`Place`)
                     * To fix a different flow. This is causing an issue for RMA orders, where they cannot place the order because the `PlaceOrder` requirement is invalid
                     */
                    const placeOrderCurrentOrderIssues = filter(orderState.orderIssues, (placeOrderCurrentOrderIssue) => placeOrderCurrentOrderIssue.Name !== 'PlaceOrder');
                    let noOrderIssues = true;
                    let noOrderIssuesAfterPayment = true;
                    each(placeOrderCurrentOrderIssues, (issue) => {
                        noOrderIssues = noOrderIssues && !(issue.RequiredFor & 1);
                        noOrderIssuesAfterPayment = noOrderIssuesAfterPayment && (!(issue.RequiredFor & 1) || issue.Name === 'PayOrder');
                    });
                    let noOrderLineIssues = true;
                    each(orderState.orderLineIssues, (lineIssues, orderLineId) => {
                        each(lineIssues, (issue) => {
                            noOrderLineIssues = noOrderLineIssues && !(issue.RequiredFor & 1);
                        });
                    });
                    const placeOrderRequirement = find(orderRequirements, { Name: 'PlaceOrder' });
                    const placeOrderStep = {
                        name: 'placeOrder',
                        possible: noCustomerIssues && noOrderIssues && noOrderLineIssues,
                        required: !!placeOrderRequirement,
                        valid: placeOrderRequirement ? placeOrderRequirement.IsValid : true,
                        possibleAfterPayment: noCustomerIssues && noOrderIssuesAfterPayment && noOrderLineIssues,
                    };
                    checkoutStatus.steps.placeOrder = placeOrderStep;
                    //////////////////////////////////////////////////////////////////////
                    // Order: Pay order
                    //////////////////////////////////////////////////////////////////////
                    //
                    // Check current issues to determine if possible
                    // Possible needs to start as true for this logic to work
                    //
                    // PlaceOrder = 1,
                    // Payment = 2,
                    // Ship = 4,
                    // Invoice = 8,
                    // All = 15,
                    //
                    noCustomerIssues = true;
                    each(orderState.customerIssues, (issue) => {
                        noCustomerIssues = noCustomerIssues && !(issue.RequiredFor & 2);
                    });
                    noOrderIssues = true;
                    each(orderState.orderIssues, (issue) => {
                        noOrderIssues = noOrderIssues && !(issue.RequiredFor & 2);
                    });
                    noOrderLineIssues = true;
                    each(orderState.orderLineIssues, (lineIssues, orderLineId) => {
                        each(lineIssues, (issue) => {
                            noOrderLineIssues = noOrderLineIssues && !(issue.RequiredFor & 2);
                        });
                    });
                    /** If its a delivery order, payment is not required */
                    const isDeliverMode = orderMode === OrderMode.delivery;
                    /** If its an order, payment is not required */
                    const isOrderMode = orderMode === OrderMode.order;
                    /** If its a reservation order, payment is not required */
                    const isReservationMode = orderMode === OrderMode.reservation;
                    /**
                     * The payment step is required if the payOrder requirement is there and the order is not
                     * an order thats in Deliver, Order and Reservation mode as payments there are optional
                     * @see https://springtreesolutions.github.io/eva-sdk-redux/#/concepts/checkout?id=line-action-type
                     */
                    const payOrderRequirement = find(orderRequirements, { Name: 'PayOrder' });
                    const payOrderStepRequired = !!payOrderRequirement && (!isDeliverMode && !isOrderMode && !isReservationMode);
                    const payOrderStep = {
                        name: 'payOrder',
                        possible: noCustomerIssues && noOrderIssues && noOrderLineIssues,
                        required: payOrderStepRequired,
                        valid: payOrderRequirement ? payOrderRequirement.IsValid : true,
                    };
                    checkoutStatus.steps.payOrder = payOrderStep;
                    //////////////////////////////////////////////////////////////////////
                    // Order: Ship order
                    //////////////////////////////////////////////////////////////////////
                    //
                    // There is no 'ShipOrder' requirement but if any lines are of type ship
                    // then the order should be shipped
                    // The IsShipped property on the order indicates completion
                    //
                    // The presence of RestrictedShippingAddress on either order or order line
                    // makes the order un-shippable
                    //
                    // Check current issues to determine if possible
                    // Possible needs to start as true for this logic to work
                    //
                    // PlaceOrder = 1,
                    // Payment = 2,
                    // Ship = 4,
                    // Invoice = 8,
                    // All = 15,
                    //
                    noCustomerIssues = true;
                    each(orderState.customerIssues, (issue) => {
                        noCustomerIssues = noCustomerIssues && !(issue.RequiredFor & 4);
                    });
                    noOrderIssues = true;
                    each(orderState.orderIssues, (issue) => {
                        noOrderIssues = noOrderIssues && !(issue.RequiredFor & 4);
                    });
                    noOrderLineIssues = true;
                    each(orderState.orderLineIssues, (lineIssues, orderLineId) => {
                        each(lineIssues, (issue) => {
                            // Check the order line is of type ship (3)
                            //
                            const currentOrderLine = find(currentOrderLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(`${orderLineId}`, 10));
                            if (currentOrderLine && currentOrderLine.LineActionType === 3) {
                                noOrderLineIssues = noOrderLineIssues && !(issue.RequiredFor & 4);
                            }
                        });
                    });
                    const hasShippingLines = lineActionTypesInCart.includes(3);
                    /**
                     * For orders with only LineActionType 0 we want to call the shipOrder method. This was added for the returning of fashion cheques flow in the pos
                     */
                    const onlyHasLineActionTypeZero = lineActionTypesInCart.every((lineActionType) => lineActionType === 0);
                    const orderRestrictedForShipping = find(orderState.orderIssues, { Name: 'RestrictedShippingAddress' });
                    const orderLineRestrictedForShipping = find(orderState.orderLineIssues, (lineIssues) => find(lineIssues, { Name: 'RestrictedShippingAddress' }) !== undefined);
                    const orderLines = get(currentOrder, 'Lines') || [];
                    /**
                     * We will use this property to decide whether the order has been shipped or not, this will be used as the 'valid' property in the 'shipOrder' step
                     * based on whether the order is full of 'LineActionType' 0 orderLines, we will be running different logic due to a return flow of fashion cheque in pos
                     */
                    let isShipped = false;
                    if (onlyHasLineActionTypeZero) {
                        isShipped = orderLines
                            .filter((line) => line.LineActionType === 0 && !line.IsCancelled)
                            .every((line) => line.IsShipped);
                    }
                    else {
                        isShipped = orderLines
                            .filter((line) => line.LineActionType === 3 && !line.IsCancelled)
                            .every((line) => line.IsShipped);
                    }
                    const shipOrderStep = {
                        name: 'shipOrder',
                        possible: noCustomerIssues && noOrderIssues && noOrderLineIssues,
                        required: hasShippingLines || onlyHasLineActionTypeZero,
                        valid: !orderRestrictedForShipping && !orderLineRestrictedForShipping && isShipped,
                    };
                    checkoutStatus.steps.shipOrder = shipOrderStep;
                    //////////////////////////////////////////////////////////////////////
                    // Order: Return to supplier
                    //////////////////////////////////////////////////////////////////////
                    //
                    if (!!(get(currentOrder, 'Characteristics') & 128)) {
                        const returnReasonRequirement = find(orderRequirements, { Name: 'ReturnReason' });
                        const shipToOrganisationRequirement = find(orderRequirements, { Name: 'ShipToOrganizationUnitID' });
                        checkoutStatus.steps.returnToSupplier = {
                            name: 'returnToSupplier',
                            possible: true,
                            required: true,
                            valid: get(returnReasonRequirement, 'IsValid', true) &&
                                get(shipToOrganisationRequirement, 'IsValid', true),
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: Interbranch
                    //////////////////////////////////////////////////////////////////////
                    //
                    if (!!(get(currentOrder, 'Characteristics') & 4)) {
                        const shipToOrganisationRequirement = find(orderRequirements, { Name: 'ShipToOrganizationUnitID' });
                        const soldToOrganisationRequirement = find(orderRequirements, { Name: 'SoldToOrganizationUnitID' });
                        checkoutStatus.steps.interbranch = {
                            name: 'interbranch',
                            possible: true,
                            required: true,
                            valid: get(shipToOrganisationRequirement, 'IsValid', true) &&
                                get(soldToOrganisationRequirement, 'IsValid', true),
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: BillingAddress
                    //////////////////////////////////////////////////////////////////////
                    //
                    const billingAddressRequirements = filter(orderRequirements, (requirement) => requirement.Name.startsWith('BillingAddress.'));
                    if (isEmpty(billingAddressRequirements) === false) {
                        const billingAddressRequirementsValid = billingAddressRequirements.every((billingAddressRequirement) => billingAddressRequirement.IsValid);
                        checkoutStatus.steps.billingAddress = {
                            name: 'billingAddress',
                            possible: true,
                            required: true,
                            valid: billingAddressRequirementsValid,
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: ShippingAddress
                    //////////////////////////////////////////////////////////////////////
                    //
                    const shippingAddressRequirements = filter(orderRequirements, (requirement) => requirement.Name.startsWith('ShippingAddress.'));
                    if (isEmpty(shippingAddressRequirements) === false) {
                        const shippingAddressRequirementsValid = shippingAddressRequirements.every((shippingAddressRequirement) => shippingAddressRequirement.IsValid);
                        checkoutStatus.steps.shippingAddress = {
                            name: 'shippingAddress',
                            possible: true,
                            required: true,
                            valid: shippingAddressRequirementsValid,
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: Insurance
                    //////////////////////////////////////////////////////////////////////
                    //
                    const insureOrderRequirement = find(orderRequirements, { Name: 'RequestInsurancesForOrder' });
                    if (insureOrderRequirement) {
                        checkoutStatus.steps.insurance = {
                            name: 'insurance',
                            possible: true,
                            required: true,
                            valid: insureOrderRequirement.IsValid,
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: EmployeeVerification
                    //////////////////////////////////////////////////////////////////////
                    //
                    const employeeVerificationRequirement = find(orderRequirements, { Name: 'EmployeeVerification' });
                    if (employeeVerificationRequirement) {
                        checkoutStatus.steps.employeeVerification = {
                            name: 'employeeVerification',
                            possible: true,
                            required: true,
                            valid: employeeVerificationRequirement.IsValid,
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // Order: VerifyCustomer
                    //////////////////////////////////////////////////////////////////////
                    //
                    const verifyCustomerRequirement = find(orderRequirements, { Name: 'VerifyCustomer' });
                    if (verifyCustomerRequirement) {
                        checkoutStatus.steps.insurance = {
                            name: 'verifyCustomer',
                            possible: true,
                            required: true,
                            valid: verifyCustomerRequirement.IsValid,
                        };
                    }
                    //////////////////////////////////////////////////////////////////////
                    // OrderLine: Discounts
                    //////////////////////////////////////////////////////////////////////
                    //
                    // Only check if we have discount product lines
                    //
                    if (orderState.pickDiscountProducts) {
                        const discountProductStep = {
                            name: 'discountProduct',
                            possible: true,
                            required: true,
                            valid: !find(orderState.orderLineIssues, (orderLineIssues) => {
                                // Check all order lines for the absence of pick discount
                                // product issues
                                //
                                return !!find(orderLineIssues, { Name: 'PickDiscountProduct' });
                            }),
                        };
                        checkoutStatus.steps.discountProduct = discountProductStep;
                    }
                    //////////////////////////////////////////////////////////////////////
                    // OrderLine: Product requirements
                    //////////////////////////////////////////////////////////////////////
                    //
                    // Only check if we have product requirement
                    //
                    if (orderState.productRequirementProducts) {
                        const productRequirementsProductStep = {
                            name: 'productRequirements',
                            possible: true,
                            required: true,
                            valid: !find(orderState.orderLineIssues, (orderLineIssues) => {
                                // Check all order lines for the absence of
                                // product requirement issues
                                //
                                return !!find(orderLineIssues, { Name: 'ProductRequirement' });
                            }),
                        };
                        checkoutStatus.steps.productRequirements = productRequirementsProductStep;
                    }
                    //////////////////////////////////////////////////////////////////////
                    // OrderLine: Serial number
                    //////////////////////////////////////////////////////////////////////
                    //
                    if (orderState.serialNumbers) {
                        const serialNumbersStep = {
                            name: 'serialNumber',
                            possible: true,
                            required: true,
                            valid: !find(orderState.orderLineIssues, (orderLineIssues) => {
                                // Check all order lines for the absence of
                                // product requirement issues
                                //
                                return !!find(orderLineIssues, { Name: 'SerialNumber' });
                            }),
                        };
                        checkoutStatus.steps.serialNumber = serialNumbersStep;
                    }
                    //////////////////////////////////////////////////////////////////////
                    // OrderLine: CompleteProductBundle
                    //////////////////////////////////////////////////////////////////////
                    //
                    if (orderState.productBundles) {
                        const productBundlesStep = {
                            name: 'productBundles',
                            possible: true,
                            required: true,
                            valid: !find(orderState.orderLineIssues, (orderLineIssues) => {
                                // Check all order lines for the absence of
                                // product requirement issues
                                //
                                return !!find(orderLineIssues, { Name: 'CompleteProductBundle' });
                            }),
                        };
                        checkoutStatus.steps.productBundles = productBundlesStep;
                    }
                    return checkoutStatus;
                default:
                    return state;
            }
        };
        /**
         * Collect the unique line types present on the order
         */
        baseReducerFuncs.orderLineActionTypes = (state = [], action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    // Return list of unique line action types in the order
                    // Array size of 1 means entire order is the same
                    // Type None is removed from the list
                    //
                    const payload = get(action, 'payload');
                    return uniq(filter(map$1(get(payload, 'Result.Lines', []), 'LineActionType'), (lineActionType) => lineActionType !== 0));
                default:
                    return state;
            }
        };
        /**
         * Determine the overall order status
         */
        baseReducerFuncs.status = (state = OrderStatus.new, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    // Return list of unique line action types in the order
                    // Array size of 1 means entire order is the same
                    //
                    const payload = get(action, 'payload');
                    const order = get(payload, 'Result');
                    if (order) {
                        if (order.IsCompleted) {
                            return OrderStatus.completed;
                        }
                        else if (order.IsShipped) {
                            return OrderStatus.shipped;
                        }
                        else if (order.IsPaid) {
                            return OrderStatus.paid;
                        }
                        else if (get(payload, 'Amounts.Paid.InTax', 0) > 0) {
                            return OrderStatus.partial_payment;
                        }
                        else {
                            return OrderStatus.new;
                        }
                    }
                    else {
                        return state;
                    }
                default:
                    return state;
            }
        };
        /**
         * Count products ordered for certain types
         */
        baseReducerFuncs.count = (state = [], action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const count = {
                        discount: 0,
                        non_discount: 0,
                        giftcard: 0,
                        bundle: 0,
                        configurable: 0,
                        service: 0,
                        total: 0,
                    };
                    const payload = get(action, 'payload');
                    const order = get(payload, 'Result');
                    each(get(order, 'Lines', []), (orderLine) => {
                        if (orderLine.Product) {
                            // Add to the total
                            //
                            count.total += orderLine.TotalQuantityToShip;
                            // If an orderLine with a product in it is linked to a discount it is
                            // considered to be a gift
                            //
                            if (orderLine.CustomOrderLineType === 'DISCOUNTPRODUCT') {
                                count.discount += orderLine.TotalQuantityToShip;
                            }
                            switch (orderLine.Product.Type) {
                                case 8:
                                    {
                                        count.giftcard += orderLine.TotalQuantityToShip;
                                    }
                                    break;
                                case 16:
                                    {
                                        count.service += orderLine.TotalQuantityToShip;
                                    }
                                    break;
                                case 2048:
                                    {
                                        count.bundle += orderLine.TotalQuantityToShip;
                                    }
                                    break;
                                case 16384:
                                    {
                                        count.configurable += orderLine.TotalQuantityToShip;
                                    }
                                    break;
                            }
                        }
                    });
                    // For convenience non-discount is added
                    //
                    count.non_discount = count.total - count.discount;
                    return count;
                default:
                    return state;
            }
        };
        return baseReducerFuncs;
    }
    //////////////////////////////////////////////////////////////////////////////
    // Checkout support
    //////////////////////////////////////////////////////////////////////////////
    //
    // The getShoppingCart reducer has the performCheckout helper which will
    // perform the next step of the checkout and trigger the chain.
    // GetOrder is intended to be used with SignalR for updates so this approach
    // is not the same.
    // This reducer will expose the individual steps of perform checkout so the
    // client app can call them as they wish
    //
    /**
     * Checks if the current order is in a state where payment can commence
     * The actual payment is handled by the payment provider which will verify
     * payment method specific requirements
     *
     */
    async canStartPayment() {
        const checkoutStatus = this.getState().checkoutStatus;
        const paymentStep = checkoutStatus.steps.payOrder;
        if (paymentStep) {
            if (paymentStep.valid) {
                // Order has already been paid for
                //
                return {
                    code: 208,
                    message: 'Order has already been payed',
                    type: 'SUCCESS',
                };
            }
            else if (paymentStep.possible) {
                return {
                    code: 100,
                    message: 'Payment can start',
                    type: 'SUCCESS',
                };
            }
            else {
                throw {
                    code: 412,
                    message: 'Payment can not start',
                    error: 'payOrder',
                    type: 'ERROR',
                };
            }
        }
        else {
            return {
                code: 204,
                message: 'No payment required',
                type: 'SUCCESS',
            };
        }
    }
    /**
     * Check if all the checkout steps have completed taking into account
     * if you are doing a partial shipping checkout
     *
     * @param {boolean} [partialShipping=false]
     * @returns
     */
    async hasCheckoutCompleted({ partialShipping = false, }) {
        // Check if all required steps are valid
        //
        const orderState = this.getState();
        const checkoutStatus = orderState.checkoutStatus;
        const steps = values(checkoutStatus.steps);
        const invalidSteps = steps.filter((step) => {
            var _a, _b, _c, _d;
            const originalStep = step && step.required && !step.valid;
            // Allow ship order to be incomplete for partial shipping
            // this will allow us to complete the checkout without an error
            //
            if (partialShipping && step && step.name === 'shipOrder') {
                return false;
            }
            // Allow pay order to be incomplete for partial shipping if validateOrderShipment is true
            // this will allow us to complete the checkout without an error
            //
            if (partialShipping && step && step.name === 'payOrder') {
                // If we have a `validateOrderShipment` we will use its validity state for this step.
                // otherwise we will use the original one.
                //
                if (((_b = (_a = orderState.response) === null || _a === void 0 ? void 0 : _a.AdditionalOrderData) === null || _b === void 0 ? void 0 : _b.IsPaidAmountValidForShipment) !== undefined) {
                    const validateOrderShipmentStateStepInvalid = !((_d = (_c = orderState.response) === null || _c === void 0 ? void 0 : _c.AdditionalOrderData) === null || _d === void 0 ? void 0 : _d.IsPaidAmountValidForShipment);
                    return validateOrderShipmentStateStepInvalid;
                }
                else {
                    return originalStep;
                }
            }
            return originalStep;
        });
        if (invalidSteps.length === 0) {
            return {
                code: 200,
                message: 'Checkout completed',
                type: 'SUCCESS',
            };
        }
        else {
            return {
                code: 409,
                message: 'Order is not able to complete',
                type: 'ERROR',
                invalid: map$1(invalidSteps, 'name'),
            };
        }
    }
    /**
     * Helper method to perform the placing of an order.
     * There is an override option to allow a place order without a payment
     * being performed.
     *
     * @param {{
     *     options: ICheckoutOptions,
     *   }} {
     *     options = {},
     *   }
     * @returns
     */
    async placeOrder({ options = {}, }) {
        var _a, _b;
        const orderState = this.getState();
        const orderId = get(orderState, 'response.Result.ID');
        const checkoutStatus = orderState.checkoutStatus;
        const steps = values(checkoutStatus.steps);
        const allowedInvalid = concat(['payOrder', 'placeOrder', 'shipOrder'], options.allowedInvalidSteps || []);
        const invalidSteps = steps.filter((step) => {
            // Exclude payOrder, placeOrder and shipOrder
            //
            return step && allowedInvalid.indexOf(step.name) === -1 && step.required && !step.valid;
        });
        // Collect the information needed for partial shipments
        //
        const partialShipmentIsPaid = !!((_b = (_a = orderState.response) === null || _a === void 0 ? void 0 : _a.AdditionalOrderData) === null || _b === void 0 ? void 0 : _b.IsPaidAmountValidForShipment);
        // Return an error if we have invalid steps
        // Use the first invalid step as the error
        // NOTE: These exclude pay, place and ship order
        //
        if (invalidSteps.length > 0) {
            return {
                code: 417,
                message: 'Order is invalid',
                error: first(invalidSteps).name,
                invalid: map$1(invalidSteps, 'name'),
            };
        }
        // Check if the order has been paid if required
        // There is an override option to allow place even without payment
        // This has been introduced for specific customer requirements
        // Ideally this will be governed by a backend setting in the future
        //
        // Also for a partial shipment the ValidateOrderShipment will indicate if
        // enough has been paid to proceed with shipping the order
        // The caller needs to have dispatched ValidateOrderShipment and provide the
        // flag to the partial shipping checks
        //
        if (checkoutStatus.steps.payOrder &&
            checkoutStatus.steps.payOrder.required &&
            !checkoutStatus.steps.payOrder.valid &&
            !options.allowPlaceWithoutPayment &&
            !partialShipmentIsPaid) {
            return {
                code: 417,
                message: 'Order needs to be paid',
                error: 'PlaceOrder',
                invalid: map$1(invalidSteps, 'name'),
            };
        }
        // Check if we can PLACE the order
        //
        if (checkoutStatus.steps.placeOrder && checkoutStatus.steps.placeOrder.required && !checkoutStatus.steps.placeOrder.valid) {
            if (checkoutStatus.steps.placeOrder.possible || options.allowPlaceWithoutPayment === true) {
                const [placeAction, , placeChainPromise] = placeOrder.createFetchAction(extend({ OrderID: orderId }, options.placeOrderPayload));
                store.dispatch(placeAction);
                const orderPlaced = await placeChainPromise;
                log$1('[EVA-SDK-REDUX:checkout] Order has been placed', orderPlaced);
                return {
                    code: 200,
                    message: 'PlaceOrder completed',
                    type: 'SUCCESS',
                };
            }
            else {
                return {
                    code: 400,
                    message: 'Cannot place order',
                    error: 'PlaceOrder',
                    invalid: map$1(invalidSteps, 'name'),
                };
            }
        }
        return {
            code: 204,
            message: 'PlaceOrder already completed or not needed',
            type: 'SUCCESS',
        };
    }
    /**
     * Helper method to perform the shipping of an order
     * Placing an order should have been performed first.
     *
     * @param {{
     *     options: ICheckoutOptions,
     *   }} {
     *     options = {},
     *   }
     * @returns
     */
    async shipOrder({ options = {}, }) {
        var _a, _b;
        const orderState = this.getState();
        const orderId = get(orderState, 'response.Result.ID');
        const checkoutStatus = orderState.checkoutStatus;
        const steps = values(checkoutStatus.steps);
        const allowedInvalid = concat(['shipOrder'], options.allowedInvalidSteps || []);
        const invalidSteps = steps.filter((step) => {
            // Exclude payOrder, placeOrder and shipOrder
            //
            return step && allowedInvalid.indexOf(step.name) === -1 && step.required && !step.valid;
        });
        // Collect the information needed for partial shipments
        //
        const partialShipmentIsPaid = !!((_b = (_a = orderState.response) === null || _a === void 0 ? void 0 : _a.AdditionalOrderData) === null || _b === void 0 ? void 0 : _b.IsPaidAmountValidForShipment);
        // Return an error if we have invalid steps
        // Use the first invalid step as the error
        // NOTE: These only exclude ship order at this point
        //
        if (invalidSteps.length > 0) {
            return {
                code: 417,
                message: 'Order is invalid',
                error: first(invalidSteps).name,
                invalid: map$1(invalidSteps, 'name'),
            };
        }
        // Check if we can SHIP the order
        //
        if (checkoutStatus.steps.shipOrder && checkoutStatus.steps.shipOrder.required && !checkoutStatus.steps.shipOrder.valid) {
            // For a partial shipment the possible flag will not be true
            // This is because payOrder is a requirement for shipping
            // For a partial shipment the validateOrderShipment determines if enough
            // has been paid to ship the order
            // We will recreate the logic for the shipOrder possible here with the
            // exclusion of the PayOrder step
            //
            const currentOrderLines = get(orderState, 'response.Result.Lines');
            // First check if we can ship the order in its current state
            // Partial shipping has some special requirements
            //
            let shipOrderPossible = checkoutStatus.steps.shipOrder.possible;
            if (options.partialShipment) {
                let noCustomerIssues = true;
                each(orderState.customerIssues, (issue) => {
                    noCustomerIssues = noCustomerIssues && !(issue.RequiredFor & 4);
                });
                let noOrderIssues = true;
                each(orderState.orderIssues, (issue) => {
                    // Ignore the PayOrder step
                    //
                    noOrderIssues = noOrderIssues && (!(issue.RequiredFor & 4) || (issue.Name === 'PayOrder'));
                });
                let noOrderLineIssues = true;
                each(orderState.orderLineIssues, (lineIssues, orderLineId) => {
                    each(lineIssues, (issue) => {
                        // Check the order line is of type ship (3)
                        //
                        const currentOrderLine = find(currentOrderLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(`${orderLineId}`, 10));
                        if (currentOrderLine && currentOrderLine.LineActionType === 3) {
                            noOrderLineIssues = noOrderLineIssues && !(issue.RequiredFor & 4);
                        }
                    });
                });
                shipOrderPossible = partialShipmentIsPaid && noCustomerIssues && noOrderIssues && noOrderLineIssues;
            }
            if (shipOrderPossible) {
                // Collect the shippable lines
                //
                const orderLines = get(orderState, 'response.Result.Lines');
                // We only want to ship lines that are shippable ( line action type 3 )
                //
                const shippableLines = orderLines.filter((orderLine) => orderLine.LineActionType === 3);
                const orderLinesToShip = map$1(shippableLines, (orderLine) => {
                    const shipLine = {
                        OrderLineID: orderLine.ID,
                        ShipmentBackendID: undefined,
                        TrackingCode: undefined,
                        TrackingLink: undefined,
                    };
                    return shipLine;
                });
                const shipRequest = {
                    OrderID: orderId,
                    // We are checking if the orderLinesToShip is empty and returning undefined
                    // in case we are returning a fashion-cheque (lineActionType 0) we don't want
                    // give the back-end an empty array.
                    Lines: isEmpty(orderLinesToShip) ? undefined : orderLinesToShip,
                };
                const [shipAction, , shipChainPromise] = shipOrder.createFetchAction(extend(shipRequest, options.shipOrderPayload));
                store.dispatch(shipAction);
                const orderShipped = await shipChainPromise;
                log$1('[EVA-SDK-REDUX:checkout] Order has been shipped', orderShipped);
                return {
                    code: 200,
                    message: 'ShipOrder completed',
                    type: 'SUCCESS',
                };
            }
            else {
                return {
                    code: 400,
                    message: 'Cannot ship order',
                    error: 'ShipOrder',
                    invalid: map$1(invalidSteps, 'name'),
                };
            }
        }
        return {
            code: 204,
            message: 'ShipOrder already completed or not needed',
            type: 'SUCCESS',
        };
    }
}
// Export the state handling singleton
//
const getOrder = new GetOrder();
// Setup the Saga implementation
//
function* fetchCall$6g(action) {
    const fetchActionPayload = action.payload;
    fetchActionPayload.request = Object.assign({ AdditionalOrderDataOptions: settings.currentOrderOptions }, fetchActionPayload.request);
    try {
        const data = yield call([getOrder, 'fetchData'], action);
        yield put(getOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Generate and reduce the cart changes
        //
        const checkoutStatusPayload = {
            chainStarter: chainStarter,
            order: getOrder.getState(),
        };
        yield put(getOrder.actions.checkoutStatus(checkoutStatusPayload));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6g() {
    yield takeEvery(getType(getOrder.actions.fetch), fetchCall$6g);
}

/**
 * Implements GetPaymentTransaction fetch for the Redux store
 *
 * @export
 * @class GetPaymentTransaction
 * @extends {StoreFetchableData<EVA.Core.GetPaymentTransaction, EVA.Core.GetPaymentTransactionResponse>}
 */
class GetPaymentTransaction extends StoreFetchableData {
    /**
     * Creates an instance of GetPaymentTransaction
     *
     * @memberof GetPaymentTransaction
     */
    constructor() {
        super('getPaymentTransaction', '/message/GetPaymentTransaction');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetPaymentTransaction
     */
    *saga() {
        yield fork(listener$6f);
    }
}
// Export the state handling singleton
//
const getPaymentTransaction = new GetPaymentTransaction();
// Setup the Saga implementation
//
function* fetchCall$6f(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getPaymentTransaction, 'fetchData'], action);
        yield put(getPaymentTransaction.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getPaymentTransaction.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6f() {
    yield takeEvery(getType(getPaymentTransaction.actions.fetch), fetchCall$6f);
}

/**
 * Implements StartTransaction fetch for the Redux store
 *
 * @export
 * @class StartTransaction
 * @extends {StoreFetchableData<EVA.Payment.Core.StartTransaction, EVA.Payment.Core.StartTransactionResponse>}
 */
class StartTransaction extends StoreFetchableData {
    /**
     * Creates an instance of StartTransaction
     *
     * @memberof StartTransaction
     */
    constructor() {
        super('startTransaction', '/message/StartTransaction');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartTransaction
     */
    *saga() {
        yield fork(listener$6e);
    }
}
// Export the state handling singleton
//
const startTransaction = new StartTransaction();
// Setup the Saga implementation
//
function* fetchCall$6e(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startTransaction, 'fetchData'], action);
        yield put(startTransaction.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startTransaction.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6e() {
    yield takeEvery(getType(startTransaction.actions.fetch), fetchCall$6e);
}

/**
 * Implements CreatePayment fetch for the Redux store
 *
 * @export
 * @class CreatePayment
 * @extends {StoreFetchableData<EVA.Core.CreatePayment, EVA.Core.CreatePaymentResponse>}
 */
class CreatePayment extends StoreFetchableData {
    /**
     * Creates an instance of CreatePayment
     *
     * @memberof CreatePayment
     */
    constructor() {
        super('createPayment', '/message/CreatePayment');
        /**
         * Order payments stream
         * Will emit on both partial and full payment
         * Contains success state and remaining and change amounts
         *
         * @memberof EvaPaymentProvider
         */
        this.orderPayments$ = new Subject();
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreatePayment
     */
    *saga() {
        yield fork(listener$6d);
    }
    /**
     * Start the payment process using the currently selected payment method
     * and the provided order
     *
     * @memberof EvaPaymentProvider
     */
    async performPayment(paymentMethod, order, amount, options = {}) {
        // Check we have an order with an ID
        //
        const orderId = get(order, 'ID');
        if (!orderId) {
            throw {
                code: 400,
                message: 'Missing order id',
                error: 'INVALID_ORDER',
            };
        }
        // Check amount is positive. A negative amount should be done as a refund
        // and setting a negative value is now deprecated
        //
        if (amount !== undefined && amount < 0) {
            throw new Error('Negative amounts are not allowed. Did you want to create refund?');
        }
        // For backwards compatibility (and refunds) we also support the old payment method name
        //
        const paymentMethodName = isString(paymentMethod) ? paymentMethod : paymentMethod.Name;
        // Only refunds are allowed to supply the payment method as a string
        //
        if (isString(paymentMethod) && !options.refund) {
            throw new Error('Please supply a payment method model. Only refunds are allowed to supply methods by name');
        }
        const isRefund = options.refund && !isEmpty(options.refund) && options.refund.isRefund;
        log$1(`[EVA-SDK-REDUX:performPayment] Starting payment method: ${paymentMethod}, order Id: ${orderId} amount: ${amount} refund: ${isRefund}`, options);
        // Prepare the payment properties
        //
        let paymentProperties = {
            AmountGiven: options.amountGiven,
            StationID: options.stationId,
        };
        // Add additional properties based on type
        //
        const applicationConfigurationState = getApplicationConfiguration.getState();
        switch (paymentMethodName) {
            case 'INTERSOLVE':
            case 'VALUTEC':
            case 'FASHIONCHEQUE':
            case 'APIGIFTCARD':
                // Ensure we have at least a cardnumber
                //
                if (!options.cardNumber && !isRefund) {
                    throw {
                        code: 400,
                        message: `Payment method ${paymentMethod} requires a card number`,
                        error: 'CARD_NR_REQUIRED',
                    };
                }
                paymentProperties.CardNumber = options.cardNumber;
                paymentProperties.Pin = options.pinCode;
                break;
            case 'USERCARD':
                // Ensure we have at least a cardnumber
                //
                if (!options.cardNumber && !isRefund) {
                    throw {
                        code: 400,
                        message: `Payment method ${paymentMethod} requires a card number`,
                        error: 'CARD_NR_REQUIRED',
                    };
                }
                paymentProperties.UserCardID = options.cardNumber;
                break;
            case 'BUCKAROO':
                // Buckaroo needs the correct gateway and returnURL
                //
                paymentProperties.GatewayID = options.gatewayId;
                paymentProperties.ReturnUrl = options.returnUrl;
                if (options.customer) {
                    paymentProperties.CustomerGender = options.customer.gender;
                    paymentProperties.CustomerEmail = options.customer.email;
                    paymentProperties.CustomerFirstName = options.customer.firstname;
                    paymentProperties.CustomerLastName = options.customer.lastname;
                }
                break;
            case 'EVAPAY':
                // The final payment Url for the payment will become available in the store
                // in the createPayment response
                //
                paymentProperties.SendEmail = options.sendEmail;
                paymentProperties.BaseUrl = options.baseUrl;
                break;
            case 'INTERBRANCH':
            case 'PUBLICRELATIONS':
            case 'RITUALS-CHEQUES':
            case 'RITUALS-KADEOS':
            case 'RITUALS-OTHER':
            case 'TREATWELL':
            case 'SCOTCH':
            case 'MANUAL':
            case 'EGC':
            case 'CUSTOM':
            case 'GIFTCARD':
            case 'ADYEN':
            case 'LOYALTY':
                // NOOP
                break;
            case 'CASH':
            case 'PIN':
                // Ensure we have a station id before we begin
                //
                if (!options.stationId) {
                    throw {
                        code: 400,
                        message: `Payment method ${paymentMethod} requires a station`,
                        error: 'STATION_REQUIRED',
                    };
                }
                break;
            case 'ADYEN_ALIPAY':
            case 'ADYEN_PIN':
            case 'ADYEN_WECHATPAY_POS':
            case 'ADYEN_DROPIN':
                // Add Adyen configuration
                //
                paymentProperties.MerchantAccount = get(applicationConfigurationState, 'response.Configuration.Adyen:MerchantAccount');
                break;
            case 'ADYEN_CHECKOUT_API':
                paymentProperties.GatewayID = options.gatewayId;
                const shouldAddAdyenCheckoutApiGiftCardProperties = this.shouldAddAdyenCheckoutApiGiftCardProperties(paymentMethod, options.paymentTypeId);
                // Incase ADYEN_CHECKOUT_API is used in combination with ADYEN_CHECKOUT_API_GIFTCARD, we want to alter the payload
                // and map the cardNumber and pinCode to different properties
                //
                // TODO: Revisit this when refunds are supported. Likely won't need a card number
                //
                if (shouldAddAdyenCheckoutApiGiftCardProperties) {
                    // Because the properties have to be made optional, throwing an error is the only way to communicate problems with the consumer
                    //
                    if (isNil(options.selectedGiftCardHandler)) {
                        throw {
                            code: 400,
                            message: `Payment method ${paymentMethod} with type ADYEN_CHECKOUT_API_GIFTCARD requires a giftCard handler`,
                            error: 'GIFTCARD_HANDLER_REQUIRED',
                        };
                    }
                    if (isNil(options.cardNumber)) {
                        throw {
                            code: 400,
                            message: `Payment method ${paymentMethod} with type ADYEN_CHECKOUT_API_GIFTCARD requires a card number`,
                            error: 'CARD_NR_REQUIRED',
                        };
                    }
                    paymentProperties.Data = {
                        PaymentMethod: {
                            brand: options.selectedGiftCardHandler.CardType,
                            number: options.cardNumber,
                            // This is optional, but if present we will send it to adyen
                            cvc: options.pinCode,
                        }
                    };
                }
                break;
            default:
                warn('[EVA-SDK-REDUX:performPayment] Unexpected payment method:', paymentMethod);
                throw {
                    code: 400,
                    message: `Unknown payment method ${paymentMethod}`,
                    error: 'UNSUPPORTED_METHOD',
                };
        }
        // Custom payments have their own options dictated by their custom fields
        //
        if (options.customFields) {
            paymentProperties = Object.assign(Object.assign({}, paymentProperties), options.customFields);
        }
        // Start the payment or refund
        //
        const isRefundAndExistsPaymentTransaction = options.refund && options.refund.isRefund && !isNil(options.refund.paymentTransactionId);
        let performPaymentOrRefundPromise;
        if (options.refund && !isEmpty(options.refund) && options.refund.isRefund) {
            const refundPayload = {
                OrderID: orderId,
                PaymentTypeID: options.paymentTypeId,
                PaymentTransactionID: options.refund.paymentTransactionId,
                Amount: amount,
                CurrencyID: options.refund.currencyID,
                Properties: paymentProperties,
            };
            const [refundAction, refundPromise] = createRefund.createFetchAction(refundPayload, undefined, options.timeout, undefined, options.elevationToken);
            store.dispatch(refundAction);
            performPaymentOrRefundPromise = refundPromise;
        }
        else {
            if (!options.paymentTypeId) {
                throw new Error('Missing required payment type id');
            }
            // The currencyID will default to whatever is on the OU if omitted
            // It can also be supplied directly through the options
            //
            const paymentCurrencyID = options.currencyID;
            const paymentPayload = {
                OrderID: orderId,
                PaymentTypeID: options.paymentTypeId,
                Amount: amount,
                Properties: paymentProperties,
                CurrencyID: paymentCurrencyID,
            };
            const [paymentAction, paymentPromise] = createPayment.createFetchAction(paymentPayload, undefined, options.timeout, undefined, options.elevationToken);
            store.dispatch(paymentAction);
            performPaymentOrRefundPromise = paymentPromise;
        }
        try {
            // Wait for the payment to start
            // What happens after depend on payment method
            //
            const paymentResponse = await performPaymentOrRefundPromise;
            // Find the primary payment transaction
            //
            const paymentTransaction = get(paymentResponse, 'PaymentTransactions.0', get(paymentResponse, 'PaymentTransaction'));
            const paymentTransactionId = get(paymentTransaction, 'ID');
            // BEWARE: Every case must return or throw in this switch
            //
            switch (paymentMethodName) {
                case 'ADYEN_CHECKOUT_API':
                case 'ADYEN':
                case 'CASH':
                case 'EVAPAY':
                case 'BUCKAROO':
                case 'INTERBRANCH':
                case 'INTERSOLVE':
                case 'FASHIONCHEQUE':
                case 'APIGIFTCARD':
                case 'USERCARD':
                case 'PUBLICRELATIONS':
                case 'RITUALS-CHEQUES':
                case 'RITUALS-KADEOS':
                case 'RITUALS-OTHER':
                case 'TREATWELL':
                case 'VALUTEC':
                case 'USERCARD':
                case 'SCOTCH':
                case 'MANUAL':
                case 'EGC':
                case 'CUSTOM':
                case 'GIFTCARD':
                case 'ADYEN_DROPIN':
                case 'LOYALTY':
                    {
                        // Fetch the payment transaction
                        //
                        const transactionDetails = paymentTransactionId ? await this.getPaymentTransactionDetails(paymentTransactionId) : undefined;
                        // Update the cart and fetch order with pending payments
                        //
                        const orderPaymentData = await this.fetchUpdatedCartAndOrder(orderId, paymentMethodName, paymentResponse, transactionDetails, options.partialShipment);
                        // We will only fetch the getAvailableRefundPaymentMethodsForOrder again if a paymentTransactionID was provided,
                        // the payment is refund and there is openAmount greater then 0
                        //
                        if (isRefundAndExistsPaymentTransaction && orderPaymentData.openAmount !== 0) {
                            await this.fetchAvailableRefundPaymentMethods(orderId);
                        }
                        return orderPaymentData;
                    }
                case 'ADYEN_ALIPAY':
                case 'ADYEN_PIN':
                case 'ADYEN_WECHATPAY_POS':
                case 'PIN':
                    // PIN is more complicated because we need to start a transaction for
                    // a station
                    // Normal transaction timeout is 30 seconds.
                    // For PIN we have to wait for the customer to perform their actions
                    // So we increase the wait time to two minutes (120000 milliseconds)
                    // This timeout it set inside the startTransaction reducer
                    //
                    const startTransactionPayload = {
                        PaymentTransactionID: paymentTransactionId,
                        UseHardwareID: false,
                        StationID: options.stationId,
                        HardwareID: '',
                    };
                    // PIN Payments with flaky networks are tricky
                    // We need to wait for the transaction and we will poll the transaction
                    // details at the same time.
                    // Regardless of success or failure we will always update the order
                    //
                    // PIN refunds do not wait for a transaction. Only the 'PIN' method supports
                    // refunds though
                    //
                    let pinTransactionDetails;
                    if (paymentMethodName === 'PIN'
                        && options.refund
                        && !isEmpty(options.refund)
                        && options.refund.isRefund
                        && options.refund.paymentTransactionId) {
                        pinTransactionDetails = await this.getPaymentTransactionDetails(paymentTransactionId);
                    }
                    else {
                        try {
                            pinTransactionDetails = await this.waitForPinTransaction(startTransactionPayload, options.elevationToken);
                        }
                        catch (transactionError) {
                            throw transactionError;
                        }
                    }
                    log$1('[EVA-SDK-REDUX:performPayment] PIN transaction details', pinTransactionDetails);
                    // Always update the order/cart/requirements regardless of success or error
                    //
                    const orderPayment = await this.fetchUpdatedCartAndOrder(orderId, paymentMethodName, paymentResponse, pinTransactionDetails, options.partialShipment);
                    // We will only fetch the getAvailableRefundPaymentMethodsForOrder again if a paymentTransactionID was provided,
                    // the payment is refund and there is openAmount greater then 0
                    //
                    if (isRefundAndExistsPaymentTransaction && orderPayment.openAmount !== 0) {
                        await this.fetchAvailableRefundPaymentMethods(orderId);
                    }
                    return orderPayment;
                default:
                    // This cannot happen due to the previous switch checking the same methods
                    //
                    /* istanbul ignore next */
                    warn('[EVA-SDK-REDUX:performPayment] Unexpected payment method:', paymentMethod);
                    /* istanbul ignore next */
                    throw {
                        code: 400,
                        message: `Unknown payment method ${paymentMethod}`,
                        error: 'UNSUPPORTED_METHOD',
                    };
            }
        }
        catch (paymentError) {
            // Update the current cart to update paid amounts and required data status
            // This is one of the few (if not only) time to manually update the cart
            //
            const [action, , cartChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: orderId });
            store.dispatch(action);
            // Wait for cart update to complete
            //
            await cartChainPromise;
            switch (get(paymentError, 'response.Exception.Type')) {
                case 'StationRequired':
                    {
                        error('[EVA-SDK-REDUX:performPayment] Payment requires station, reason:', paymentError);
                        throw {
                            code: 421,
                            message: `Payment method ${paymentMethod} requires a station`,
                            error: 'STATION_REQUIRED',
                        };
                    }
                case 'OrderAlreadyFullyPaid':
                    {
                        // This is not an error
                        // Emit as success
                        // Cart should already be up-to-date so no reason to fetch
                        //
                        const orderPayment = {
                            paymentMethod: paymentMethodName,
                            success: true,
                            order: order,
                            openAmount: 0,
                            change: 0,
                            paymentTransactions: []
                        };
                        // Emit the payment already completed
                        //
                        this.orderPayments$.next(orderPayment);
                        return orderPayment;
                    }
                default:
                    {
                        error('[EVA-SDK-REDUX:performPayment] Payment failed, reason:', paymentError);
                        // Re-throw the error
                        //
                        throw paymentError;
                    }
            }
        }
    }
    /**
     * Helper method to update current cart and call get order for any pending
     * payments for user cards
     *
     * @private
     * @param {number} orderId
     * @returns
     * @memberof CreatePayment
     */
    async fetchUpdatedCartAndOrder(orderId, paymentMethodName, paymentResponse, transactionDetails, partialShipment) {
        // Update the current cart to update paid amounts and required data status
        // This is one of the few (if not only) time to manually update the cart
        //
        const [action, , cartChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: orderId });
        store.dispatch(action);
        // Use getOrder with pendingPayments flag to fetch pending user card payments
        //
        const [getOrderAction, getOrderPromise] = getOrder.createFetchAction({
            OrderID: orderId,
            IncludeAllPayments: true,
        });
        store.dispatch(getOrderAction);
        // Wait for both cart and order update to complete
        //
        await Promise.all([
            cartChainPromise,
            getOrderPromise,
        ]);
        // Inspect current order data and fetch open amount
        //
        const getOrderResponse = getOrder.getState().response;
        const openAmount = get(getOrderResponse, 'Amounts.Open.InTax', 0);
        const totalAmount = get(getOrderResponse, 'Amounts.Total.InTax', 0);
        const changeAmount = get(paymentResponse, 'Properties.RoundedReturnAmount', 0);
        // Find the primary payment transaction
        //
        const paymentTransaction = get(paymentResponse, 'PaymentTransactions.0', get(paymentResponse, 'PaymentTransaction'));
        const paymentTransactionId = get(paymentTransaction, 'ID');
        // The cart chain contains validateOrderShipment for partial shipment
        //
        const validateOrderShipmentResponse = validateOrderShipment.getState().response;
        debug(`[EVA-SDK-REDUX:performPayment] Payment completed. Open: ${openAmount} of total ${totalAmount}`, paymentResponse);
        // We're done for these payment methods
        //
        const orderPayment = {
            paymentMethod: paymentMethodName,
            success: get(transactionDetails, 'PaymentTransaction.Status') === 10,
            order: get(getOrderResponse, 'Result'),
            openAmount: openAmount,
            openAmountForShipment: partialShipment ? get(validateOrderShipmentResponse, 'OpenAmountForShipment', 0) : undefined,
            change: changeAmount,
            paymentTransactionId: paymentTransactionId,
            paymentTransactions: paymentResponse.PaymentTransactions,
            paymentProperties: paymentResponse.Properties
        };
        // Emit the payment activity and return it
        //
        this.orderPayments$.next(orderPayment);
        return orderPayment;
    }
    async fetchAvailableRefundPaymentMethods(orderId) {
        const [getAvailableRefundPaymentMethodsForOrderAction, getAvailableRefundPaymentMethodsForOrderResponsePromise] = getAvailableRefundPaymentMethodsForOrder.createFetchAction({
            OrderID: orderId
        });
        store.dispatch(getAvailableRefundPaymentMethodsForOrderAction);
        log$1(`[EVA-SDK-REDUX:createRefund] fetching getAvailableRefundPaymentMethodsForOrder with orderId=${orderId}`);
        try {
            await getAvailableRefundPaymentMethodsForOrderResponsePromise;
        }
        catch (refundMethodsError) {
            error('[EVA-SDK-REDUX] Failed to fetch available refund payment methods', refundMethodsError);
        }
    }
    /**
     * Helper method that both waits for a transaction and keeps fetching
     * the transaction details
     * Will also handler PIN terminal specific requirements
     *
     * @private
     * @param {EVA.Payment.Core.StartTransaction} startTransactionPayload
     * @returns {Promise<EVA.Core.GetPaymentTransactionResponse>}
     * @memberof CreatePayment
     */
    async waitForPinTransaction(startTransactionPayload, elevationToken) {
        const paymentTransactionId = startTransactionPayload.PaymentTransactionID;
        debug(`[EVA-SDK-REDUX:waitForPinTransaction] Start waiting for PIN transaction ${paymentTransactionId}`);
        // Dispatch the initial start transaction request
        // We will use a long timeout off 2 minutes for this one
        // It can return earlier for PayPlaza PIN terminal interactions
        //
        const [transactionAction, transactionPromise] = startTransaction.createFetchAction(startTransactionPayload, undefined, 120000, undefined, elevationToken);
        store.dispatch(transactionAction);
        try {
            const transactionResponse = await transactionPromise;
            switch (transactionResponse.ResultStatus) {
                case 3: {
                    // Payment has completed
                    //
                    debug(`[EVA-SDK-REDUX:waitForPinTransaction] Completed PIN transaction ${paymentTransactionId}`);
                    const transactionDetails = await this.getPaymentTransactionDetails(paymentTransactionId);
                    return transactionDetails;
                }
                case 12: {
                    debug(`[EVA-SDK-REDUX:waitForPinTransaction] PIN transaction ${paymentTransactionId} in progress`, transactionResponse);
                    await delay(3000);
                    return await this.waitForPinTransaction(startTransactionPayload, elevationToken);
                }
                default: {
                    debug(`[EVA-SDK-REDUX:waitForPinTransaction] An unknown error occured during PIN transaction ${paymentTransactionId}`, transactionResponse);
                    // Return the current transaction status
                    // This could have been a timeout of some sort
                    //
                    const transactionDetails = await this.getPaymentTransactionDetails(paymentTransactionId);
                    return transactionDetails;
                }
            }
        }
        catch (transactionError) {
            error(`[EVA-SDK-REDUX:waitForPinTransaction] Failed to start PIN transaction ${paymentTransactionId}`, transactionError);
            // Return the current transaction status
            // This could have been a timeout of some sort
            //
            const transactionDetails = await this.getPaymentTransactionDetails(paymentTransactionId);
            return transactionDetails;
        }
    }
    /**
     * Helper method to fetch payment transaction details
     *
     * @private
     * @param {number} paymentTransactionId The payment transaction identifier
     * @param {number} [timeout=3000] The default timeout
     * @returns
     * @memberof CreatePayment
     */
    async getPaymentTransactionDetails(paymentTransactionId, timeout = 3000) {
        const payload = {
            PaymentTransactionID: paymentTransactionId,
        };
        const [transactionDetailsAction, transactionDetailsPromise] = getPaymentTransaction.createFetchAction(payload, undefined, timeout);
        store.dispatch(transactionDetailsAction);
        const paymentTransactionDetails = await transactionDetailsPromise;
        debug(`[EVA-SDK-REDUX:getPaymentTransactionDetails] Details for ${paymentTransactionId}`, paymentTransactionDetails);
        return paymentTransactionDetails;
    }
    /**
     * This method will check whether we need to add extra giftcard properties that are required by adyen in the case of
     *  `ADYEN_CHECKOUT_API_GIFTCARD` payment method type model.
     *
     * @param paymentMethod the parent payment method, if its a string this method will return early. An object is needed here.
     * @param paymentTypeId the payment method type id, which points to one of the payment method `Types`. We will use this to check whether the selected payment method type is `ADYEN_CHECKOUT_API_GIFTCARD`
     * @returns whether the properties should be added or not
     */
    shouldAddAdyenCheckoutApiGiftCardProperties(paymentMethod, paymentTypeId) {
        debug('[EVA-SDK-REDUX:shouldAddAdyenCheckoutApiGiftCardProperties] checking if ADYEN_CHECKOUT_API_GIFTCARD flow properties need to be added');
        // @see https://n6k.atlassian.net/browse/OPTR-11765
        // @see https://n6k.atlassian.net/browse/OPTR-14043
        // We don't support adding these properties if we don't have the Typees array in the paymentMethod.
        // so in the case paymentMethod is a string we will return early.
        //
        if (typeof paymentMethod === 'string') {
            debug('[EVA-SDK-REDUX:shouldAddAdyenCheckoutApiGiftCardProperties] paymentMethod is a string. returning early');
            return false;
        }
        // if paymentTypeId is not defined we will return early. As we need it to check the selected payment method type.
        //
        if (isNil(paymentTypeId)) {
            debug('[EVA-SDK-REDUX:shouldAddAdyenCheckoutApiGiftCardProperties] paymentTypeId is nil. returning early');
            return false;
        }
        const matchingPaymentMethodType = paymentMethod.Types.find(type => type.ID === paymentTypeId);
        const shouldAddAdyenCheckoutApiGiftCardProperties = (matchingPaymentMethodType === null || matchingPaymentMethodType === void 0 ? void 0 : matchingPaymentMethodType.Code) === 'ADYEN_CHECKOUT_API_GIFTCARD';
        debug(`[EVA-SDK-REDUX:shouldAddAdyenCheckoutApiGiftCardProperties] shouldAddAdyenCheckoutApiGiftCardProperties is ${shouldAddAdyenCheckoutApiGiftCardProperties}`);
        return shouldAddAdyenCheckoutApiGiftCardProperties;
    }
}
// Export the state handling singleton
//
const createPayment = new CreatePayment();
// Setup the Saga implementation
//
function* fetchCall$6d(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createPayment, 'fetchData'], action);
        yield put(createPayment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createPayment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6d() {
    yield takeEvery(getType(createPayment.actions.fetch), fetchCall$6d);
}

/**
 * Implements CreatePrintTask fetch for the Redux store
 *
 * @export
 * @class CreatePrintTask
 * @extends {StoreFetchableData<EVA.Core.CreatePrintTask, EVA.Core.CreatePrintTaskResponse>}
 */
class CreatePrintTask extends StoreFetchableData {
    /**
     * Creates an instance of CreatePrintTask
     *
     * @memberof CreatePrintTask
     */
    constructor() {
        super('createPrintTask', '/message/CreatePrintTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreatePrintTask
     */
    *saga() {
        yield fork(listener$6c);
    }
}
// Export the state handling singleton
//
const createPrintTask = new CreatePrintTask();
// Setup the Saga implementation
//
function* fetchCall$6c(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createPrintTask, 'fetchData'], action);
        yield put(createPrintTask.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createPrintTask.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6c() {
    yield takeEvery(getType(createPrintTask.actions.fetch), fetchCall$6c);
}

/**
 * Implements CreatePrivacyRemovalRequest fetch for the Redux store
 *
 * @export
 * @class CreatePrivacyRemovalRequest
 * @extends {StoreFetchableData<EVA.Privacy.CreatePrivacyRemovalRequest, EVA.Privacy.CreatePrivacyRemovalRequestResponse>}
 */
class CreatePrivacyRemovalRequest extends StoreFetchableData {
    /**
     * Creates an instance of CreatePrivacyRemovalRequest
     *
     * @memberof CreatePrivacyRemovalRequest
     */
    constructor() {
        super('createPrivacyRemovalRequest', '/message/CreatePrivacyRemovalRequest');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreatePrivacyRemovalRequest
     */
    *saga() {
        yield fork(listener$6b);
    }
}
// Export the state handling singleton
//
const createPrivacyRemovalRequest = new CreatePrivacyRemovalRequest();
// Setup the Saga implementation
//
function* fetchCall$6b(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createPrivacyRemovalRequest, 'fetchData'], action);
        yield put(createPrivacyRemovalRequest.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createPrivacyRemovalRequest.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6b() {
    yield takeEvery(getType(createPrivacyRemovalRequest.actions.fetch), fetchCall$6b);
}

/**
 * Implements CreateQuickBuy fetch for the Redux store
 *
 * @export
 * @class CreateQuickBuy
 * @extends {StoreFetchableData<EVA.Core.CreateQuickBuy, EVA.Core.CreateQuickBuyResponse>}
 */
class CreateQuickBuy extends StoreFetchableData {
    /**
     * Creates an instance of CreateQuickBuy
     *
     * @memberof CreateQuickBuy
     */
    constructor() {
        super('createQuickBuy', '/message/CreateQuickBuy');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateQuickBuy
     */
    *saga() {
        yield fork(listener$6a);
    }
}
// Export the state handling singleton
//
const createQuickBuy = new CreateQuickBuy();
// Setup the Saga implementation
//
function* fetchCall$6a(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createQuickBuy, 'fetchData'], action);
        yield put(createQuickBuy.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createQuickBuy.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6a() {
    yield takeEvery(getType(createQuickBuy.actions.fetch), fetchCall$6a);
}

/**
 * Implements CreateReturnToSupplierOrder fetch for the Redux store
 *
 * @export
 * @class CreateReturnToSupplierOrder
 * @extends {StoreFetchableData<EVA.Core.CreateReturnToSupplierOrder, EVA.Core.EmptyResponseMessage>}
 */
class CreateReturnToSupplierOrder extends StoreFetchableData {
    /**
     * Creates an instance of CreateReturnToSupplierOrder
     *
     * @memberof CreateReturnToSupplierOrder
     */
    constructor() {
        super('createReturnToSupplierOrder', '/message/CreateReturnToSupplierOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateReturnToSupplierOrder
     */
    *saga() {
        yield fork(listener$69);
    }
}
// Export the state handling singleton
//
const createReturnToSupplierOrder = new CreateReturnToSupplierOrder();
// Setup the Saga implementation
//
function* fetchCall$69(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createReturnToSupplierOrder, 'fetchData'], action);
        yield put(createReturnToSupplierOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createReturnToSupplierOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$69() {
    yield takeEvery(getType(createReturnToSupplierOrder.actions.fetch), fetchCall$69);
}

/**
 * Implements CreateStockNotification fetch for the Redux store
 *
 * @export
 * @class CreateStockNotification
 * @extends {StoreFetchableData<EVA.Core.CreateStockNotification, EVA.Core.StockNotificationResponse>}
 */
class CreateStockNotification extends StoreFetchableData {
    /**
     * Creates an instance of CreateStockNotification
     *
     * @memberof CreateStockNotification
     */
    constructor() {
        super('createStockNotification', '/message/CreateStockNotification');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateStockNotification
     */
    *saga() {
        yield fork(listener$68);
    }
}
// Export the state handling singleton
//
const createStockNotification = new CreateStockNotification();
// Setup the Saga implementation
//
function* fetchCall$68(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createStockNotification, 'fetchData'], action);
        yield put(createStockNotification.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createStockNotification.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$68() {
    yield takeEvery(getType(createStockNotification.actions.fetch), fetchCall$68);
}

/**
 * Implements CreateStockNotificationForCurrentUser fetch for the Redux store
 *
 * @export
 * @class CreateStockNotificationForCurrentUser
 * @extends {StoreFetchableData<EVA.Core.CreateStockNotificationForCurrentUser, EVA.Core.StockNotificationResponse>}
 */
class CreateStockNotificationForCurrentUser extends StoreFetchableData {
    /**
     * Creates an instance of CreateStockNotificationForCurrentUser
     *
     * @memberof CreateStockNotificationForCurrentUser
     */
    constructor() {
        super('createStockNotificationForCurrentUser', '/message/CreateStockNotificationForCurrentUser');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateStockNotificationForCurrentUser
     */
    *saga() {
        yield fork(listener$67);
    }
}
// Export the state handling singleton
//
const createStockNotificationForCurrentUser = new CreateStockNotificationForCurrentUser();
// Setup the Saga implementation
//
function* fetchCall$67(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createStockNotificationForCurrentUser, 'fetchData'], action);
        yield put(createStockNotificationForCurrentUser.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createStockNotificationForCurrentUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$67() {
    yield takeEvery(getType(createStockNotificationForCurrentUser.actions.fetch), fetchCall$67);
}

/**
 * Implements CreateStockReplenishmentTask fetch for the Redux store
 *
 * @export
 * @class CreateStockReplenishmentTask
 * @extends {StoreFetchableData<EVA.Core.CreateStockReplenishmentTask, EVA.Core.EmptyResponseMessage>}
 */
class CreateStockReplenishmentTask extends StoreFetchableData {
    /**
     * Creates an instance of CreateStockReplenishmentTask
     *
     * @memberof CreateStockReplenishmentTask
     */
    constructor() {
        super('createStockReplenishmentTask', '/message/CreateStockReplenishmentTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateStockReplenishmentTask
     */
    *saga() {
        yield fork(listener$66);
    }
}
// Export the state handling singleton
//
const createStockReplenishmentTask = new CreateStockReplenishmentTask();
// Setup the Saga implementation
//
function* fetchCall$66(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createStockReplenishmentTask, 'fetchData'], action);
        yield put(createStockReplenishmentTask.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createStockReplenishmentTask.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$66() {
    yield takeEvery(getType(createStockReplenishmentTask.actions.fetch), fetchCall$66);
}

/**
 * Implements CreateStockReplenishmentTasks fetch for the Redux store
 *
 * @export
 * @class CreateStockReplenishmentTasks
 * @extends {StoreFetchableData<EVA.Core.CreateStockReplenishmentTasks, EVA.Core.EmptyResponseMessage>}
 */
class CreateStockReplenishmentTasks extends StoreFetchableData {
    /**
     * Creates an instance of CreateStockReplenishmentTasks
     *
     * @memberof CreateStockReplenishmentTasks
     */
    constructor() {
        super('createStockReplenishmentTasks', '/message/CreateStockReplenishmentTasks');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateStockReplenishmentTasks
     */
    *saga() {
        yield fork(listener$65);
    }
}
// Export the state handling singleton
//
const createStockReplenishmentTasks = new CreateStockReplenishmentTasks();
// Setup the Saga implementation
//
function* fetchCall$65(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createStockReplenishmentTasks, 'fetchData'], action);
        yield put(createStockReplenishmentTasks.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createStockReplenishmentTasks.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$65() {
    yield takeEvery(getType(createStockReplenishmentTasks.actions.fetch), fetchCall$65);
}

/**
 * Implements CreateUserCardMutation fetch for the Redux store
 *
 * @export
 * @class CreateUserCardMutation
 * @extends {StoreFetchableData<EVA.Payment.Core.CreateUserCardMutation, EVA.Core.EmptyResponseMessage>}
 */
class CreateUserCardMutation extends StoreFetchableData {
    /**
     * Creates an instance of CreateUserCardMutation
     *
     * @memberof CreateUserCardMutation
     */
    constructor() {
        super('createUserCardMutation', '/message/CreateUserCardMutation');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateUserCardMutation
     */
    *saga() {
        yield fork(listener$64);
    }
}
// Export the state handling singleton
//
const createUserCardMutation = new CreateUserCardMutation();
// Setup the Saga implementation
//
function* fetchCall$64(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createUserCardMutation, 'fetchData'], action);
        yield put(createUserCardMutation.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createUserCardMutation.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$64() {
    yield takeEvery(getType(createUserCardMutation.actions.fetch), fetchCall$64);
}

/**
 * Implements CreateUserInteraction fetch for the Redux store
 *
 * @export
 * @class CreateUserInteraction
 * @extends {StoreFetchableData<EVA.Core.CreateUserInteraction, EVA.Core.CreateUserInteractionResponse>}
 */
class CreateUserInteraction extends StoreFetchableData {
    /**
     * Creates an instance of CreateUserInteraction
     *
     * @memberof CreateUserInteraction
     */
    constructor() {
        super('createUserInteraction', '/message/CreateUserInteraction');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateUserInteraction
     */
    *saga() {
        yield fork(listener$63);
    }
}
// Export the state handling singleton
//
const createUserInteraction = new CreateUserInteraction();
// Setup the Saga implementation
//
function* fetchCall$63(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createUserInteraction, 'fetchData'], action);
        yield put(createUserInteraction.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createUserInteraction.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$63() {
    yield takeEvery(getType(createUserInteraction.actions.fetch), fetchCall$63);
}

/**
 * Implements GetUserPhoneNumbers fetch for the Redux store
 *
 * @export
 * @class GetUserPhoneNumbers
 * @extends {StoreFetchableData<EVA.Core.GetUserPhoneNumbers, EVA.Core.GetUserPhoneNumbersResponse>}
 */
class GetUserPhoneNumbers extends StoreFetchableData {
    /**
     * Creates an instance of GetUserPhoneNumbers
     *
     * @memberof GetUserPhoneNumbers
     */
    constructor() {
        super('getUserPhoneNumbers', '/message/GetUserPhoneNumbers');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUserPhoneNumbers
     */
    *saga() {
        yield fork(listener$62);
    }
}
// Export the state handling singleton
//
const getUserPhoneNumbers = new GetUserPhoneNumbers();
// Setup the Saga implementation
//
function* fetchCall$62(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getUserPhoneNumbers, 'fetchData'], action);
        yield put(getUserPhoneNumbers.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUserPhoneNumbers.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$62() {
    yield takeEvery(getType(getUserPhoneNumbers.actions.fetch), fetchCall$62);
}

/**
 * Implements CreateUserPhoneNumber fetch for the Redux store
 *
 * @export
 * @class CreateUserPhoneNumber
 * @extends {StoreFetchableData<EVA.Core.CreateUserPhoneNumber, EVA.Core.CreateResponse>}
 */
class CreateUserPhoneNumber extends StoreFetchableData {
    /**
     * Creates an instance of CreateUserPhoneNumber
     *
     * @memberof CreateUserPhoneNumber
     */
    constructor() {
        super('createUserPhoneNumber', '/message/CreateUserPhoneNumber');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateUserPhoneNumber
     */
    *saga() {
        yield fork(listener$61);
    }
}
// Export the state handling singleton
//
const createUserPhoneNumber = new CreateUserPhoneNumber();
// Setup the Saga implementation
//
function* fetchCall$61(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([createUserPhoneNumber, 'fetchData'], action);
        yield put(createUserPhoneNumber.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Fetch phone number list
        //
        const [getUserPhoneNumbersAction, , getUserPhoneNumbersChainPromise] = getUserPhoneNumbers.createFetchAction(undefined, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getUserPhoneNumbersAction);
        // Resolve the chain
        //
        Promise.all([getUserPhoneNumbersChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(createUserPhoneNumber.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$61() {
    yield takeEvery(getType(createUserPhoneNumber.actions.fetch), fetchCall$61);
}

/**
 * Implements CreateWorkedHours fetch for the Redux store
 *
 * @export
 * @class CreateWorkedHours
 * @extends {StoreFetchableData<EVA.Workforce.CreateWorkedHours, EVA.Core.EmptyResponseMessage>}
 */
class CreateWorkedHours extends StoreFetchableData {
    /**
     * Creates an instance of CreateWorkedHours
     *
     * @memberof CreateWorkedHours
     */
    constructor() {
        super('createWorkedHours', '/message/CreateWorkedHours');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateWorkedHours
     */
    *saga() {
        yield fork(listener$60);
    }
}
// Export the state handling singleton
//
const createWorkedHours = new CreateWorkedHours();
// Setup the Saga implementation
//
function* fetchCall$60(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createWorkedHours, 'fetchData'], action);
        yield put(createWorkedHours.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createWorkedHours.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$60() {
    yield takeEvery(getType(createWorkedHours.actions.fetch), fetchCall$60);
}

/**
 * Implements CreateZonedCycleCount fetch for the Redux store
 *
 * @export
 * @class CreateZonedCycleCount
 * @extends {StoreFetchableData<EVA.Core.CreateZonedCycleCount, EVA.Core.EmptyResponseMessage>}
 */
class CreateZonedCycleCount extends StoreFetchableData {
    /**
     * Creates an instance of CreateZonedCycleCount
     *
     * @memberof CreateZonedCycleCount
     */
    constructor() {
        super('createZonedCycleCount', '/message/CreateZonedCycleCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof CreateZonedCycleCount
     */
    *saga() {
        yield fork(listener$5$);
    }
}
// Export the state handling singleton
//
const createZonedCycleCount = new CreateZonedCycleCount();
// Setup the Saga implementation
//
function* fetchCall$5$(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([createZonedCycleCount, 'fetchData'], action);
        yield put(createZonedCycleCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(createZonedCycleCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5$() {
    yield takeEvery(getType(createZonedCycleCount.actions.fetch), fetchCall$5$);
}

/**
 * Implements DeactivateUserTask fetch for the Redux store
 *
 * @export
 * @class DeactivateUserTask
 * @extends {StoreFetchableData<EVA.Core.DeactivateUserTask, EVA.Core.EmptyResponseMessage>}
 */
class DeactivateUserTask extends StoreFetchableData {
    /**
     * Creates an instance of DeactivateUserTask
     *
     * @memberof DeactivateUserTask
     */
    constructor() {
        super('deactivateUserTask', '/message/DeactivateUserTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DeactivateUserTask
     */
    *saga() {
        yield fork(listener$5_);
    }
}
// Export the state handling singleton
//
const deactivateUserTask = new DeactivateUserTask();
// Setup the Saga implementation
//
function* fetchCall$5_(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([deactivateUserTask, 'fetchData'], action);
        yield put(deactivateUserTask.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(deactivateUserTask.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5_() {
    yield takeEvery(getType(deactivateUserTask.actions.fetch), fetchCall$5_);
}

/**
 * Implements DeleteAddressBookItem fetch for the Redux store
 *
 * @export
 * @class DeleteAddressBookItem
 * @extends {StoreFetchableData<EVA.Core.DeleteAddressBookItem, EVA.Core.EmptyResponseMessage>}
 */
class DeleteAddressBookItem extends StoreFetchableData {
    /**
     * Creates an instance of DeleteAddressBookItem
     *
     * @memberof DeleteAddressBookItem
     */
    constructor() {
        super('deleteAddressBookItem', '/message/DeleteAddressBookItem');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DeleteAddressBookItem
     */
    *saga() {
        yield fork(listener$5Z);
    }
}
// Export the state handling singleton
//
const deleteAddressBookItem = new DeleteAddressBookItem();
// Setup the Saga implementation
//
function* fetchCall$5Z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([deleteAddressBookItem, 'fetchData'], action);
        yield put(deleteAddressBookItem.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(deleteAddressBookItem.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5Z() {
    yield takeEvery(getType(deleteAddressBookItem.actions.fetch), fetchCall$5Z);
}

/**
 * Implements DeleteQuickBuy fetch for the Redux store
 *
 * @export
 * @class DeleteQuickBuy
 * @extends {StoreFetchableData<EVA.Core.DeleteQuickBuy, EVA.Core.EmptyResponseMessage>}
 */
class DeleteQuickBuy extends StoreFetchableData {
    /**
     * Creates an instance of DeleteQuickBuy
     *
     * @memberof DeleteQuickBuy
     */
    constructor() {
        super('deleteQuickBuy', '/message/DeleteQuickBuy');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DeleteQuickBuy
     */
    *saga() {
        yield fork(listener$5Y);
    }
}
// Export the state handling singleton
//
const deleteQuickBuy = new DeleteQuickBuy();
// Setup the Saga implementation
//
function* fetchCall$5Y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([deleteQuickBuy, 'fetchData'], action);
        yield put(deleteQuickBuy.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(deleteQuickBuy.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5Y() {
    yield takeEvery(getType(deleteQuickBuy.actions.fetch), fetchCall$5Y);
}

/**
 * Implements DeleteStockNotification fetch for the Redux store
 *
 * @export
 * @class DeleteStockNotification
 * @extends {StoreFetchableData<EVA.Core.DeleteStockNotification, EVA.Core.EmptyResponseMessage>}
 */
class DeleteStockNotification extends StoreFetchableData {
    /**
     * Creates an instance of DeleteStockNotification
     *
     * @memberof DeleteStockNotification
     */
    constructor() {
        super('deleteStockNotification', '/message/DeleteStockNotification');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DeleteStockNotification
     */
    *saga() {
        yield fork(listener$5X);
    }
}
// Export the state handling singleton
//
const deleteStockNotification = new DeleteStockNotification();
// Setup the Saga implementation
//
function* fetchCall$5X(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([deleteStockNotification, 'fetchData'], action);
        yield put(deleteStockNotification.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(deleteStockNotification.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5X() {
    yield takeEvery(getType(deleteStockNotification.actions.fetch), fetchCall$5X);
}

/**
 * Implements DeleteUser fetch for the Redux store
 *
 * @export
 * @class DeleteUser
 * @extends {StoreFetchableData<EVA.Core.DeleteUser, EVA.Core.EmptyResponseMessage>}
 */
class DeleteUser extends StoreFetchableData {
    /**
     * Creates an instance of DeleteUser
     *
     * @memberof DeleteUser
     */
    constructor() {
        super('deleteUser', '/message/DeleteUser');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DeleteUser
     */
    *saga() {
        yield fork(listener$5W);
    }
}
// Export the state handling singleton
//
const deleteUser = new DeleteUser();
// Setup the Saga implementation
//
function* fetchCall$5W(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([deleteUser, 'fetchData'], action);
        yield put(deleteUser.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(deleteUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5W() {
    yield takeEvery(getType(deleteUser.actions.fetch), fetchCall$5W);
}

/**
 * Implements DeleteUserPhoneNumber fetch for the Redux store
 *
 * @export
 * @class DeleteUserPhoneNumber
 * @extends {StoreFetchableData<EVA.Core.DeleteUserPhoneNumber, EVA.Core.EmptyResponseMessage>}
 */
class DeleteUserPhoneNumber extends StoreFetchableData {
    /**
     * Creates an instance of DeleteUserPhoneNumber
     *
     * @memberof DeleteUserPhoneNumber
     */
    constructor() {
        super('deleteUserPhoneNumber', '/message/DeleteUserPhoneNumber');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DeleteUserPhoneNumber
     */
    *saga() {
        yield fork(listener$5V);
    }
}
// Export the state handling singleton
//
const deleteUserPhoneNumber = new DeleteUserPhoneNumber();
// Setup the Saga implementation
//
function* fetchCall$5V(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([deleteUserPhoneNumber, 'fetchData'], action);
        yield put(deleteUserPhoneNumber.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Fetch phone number list
        //
        const [getUserPhoneNumbersAction, , getUserPhoneNumbersChainPromise] = getUserPhoneNumbers.createFetchAction(undefined, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getUserPhoneNumbersAction);
        // Resolve the chain
        //
        Promise.all([getUserPhoneNumbersChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(deleteUserPhoneNumber.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5V() {
    yield takeEvery(getType(deleteUserPhoneNumber.actions.fetch), fetchCall$5V);
}

/**
 * Implements DeliverShipment fetch for the Redux store
 *
 * @export
 * @class DeliverShipment
 * @extends {StoreFetchableData<EVA.Core.DeliverShipment, EVA.Core.EmptyResponseMessage>}
 */
class DeliverShipment extends StoreFetchableData {
    /**
     * Creates an instance of DeliverShipment
     *
     * @memberof DeliverShipment
     */
    constructor() {
        super('deliverShipment', '/message/DeliverShipment');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DeliverShipment
     */
    *saga() {
        yield fork(listener$5U);
    }
}
// Export the state handling singleton
//
const deliverShipment = new DeliverShipment();
// Setup the Saga implementation
//
function* fetchCall$5U(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([deliverShipment, 'fetchData'], action);
        yield put(deliverShipment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(deliverShipment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5U() {
    yield takeEvery(getType(deliverShipment.actions.fetch), fetchCall$5U);
}

/**
 * Implements DetachCompanyFromUser fetch for the Redux store
 *
 * @export
 * @class DetachCompanyFromUser
 * @extends {StoreFetchableData<EVA.Core.DetachCompanyFromUser, EVA.Core.EmptyResponseMessage>}
 */
class DetachCompanyFromUser extends StoreFetchableData {
    /**
     * Creates an instance of DetachCompanyFromUser
     *
     * @memberof DetachCompanyFromUser
     */
    constructor() {
        super('detachCompanyFromUser', '/message/DetachCompanyFromUser');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DetachCompanyFromUser
     */
    *saga() {
        yield fork(listener$5T);
    }
}
// Export the state handling singleton
//
const detachCompanyFromUser = new DetachCompanyFromUser();
// Setup the Saga implementation
//
function* fetchCall$5T(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([detachCompanyFromUser, 'fetchData'], action);
        yield put(detachCompanyFromUser.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(detachCompanyFromUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5T() {
    yield takeEvery(getType(detachCompanyFromUser.actions.fetch), fetchCall$5T);
}

/**
 * Implements DetachCustomerFromOrder fetch for the Redux store
 *
 * @export
 * @class DetachCustomerFromOrder
 * @extends {StoreFetchableData<EVA.Core.DetachCustomerFromOrder, EVA.Core.EmptyResponseMessage>}
 */
class DetachCustomerFromOrder extends StoreFetchableData {
    /**
     * Creates an instance of DetachCustomerFromOrder
     *
     * @memberof DetachCustomerFromOrder
     */
    constructor() {
        super('detachCustomerFromOrder', '/message/DetachCustomerFromOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DetachCustomerFromOrder
     */
    *saga() {
        yield fork(listener$5S);
    }
}
// Export the state handling singleton
//
const detachCustomerFromOrder = new DetachCustomerFromOrder();
// Setup the Saga implementation
//
function* fetchCall$5S(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([detachCustomerFromOrder, 'fetchData'], action);
        yield put(detachCustomerFromOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Update the shopping cart state
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(detachCustomerFromOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5S() {
    yield takeEvery(getType(detachCustomerFromOrder.actions.fetch), fetchCall$5S);
}

/**
 * Implements DetachOrder fetch for the Redux store
 *
 * @export
 * @class DetachOrder
 * @extends {StoreFetchableData<EVA.Core.DetachOrder, EVA.Core.DetachOrderResponse>}
 */
class DetachOrder extends StoreFetchableData {
    /**
     * Creates an instance of DetachOrder
     *
     * @memberof DetachOrder
     */
    constructor() {
        super('detachOrder', '/message/DetachOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DetachOrder
     */
    *saga() {
        yield fork(listener$5R);
    }
}
// Export the state handling singleton
//
const detachOrder = new DetachOrder();
// Setup the Saga implementation
//
function* fetchCall$5R(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([detachOrder, 'fetchData'], action);
        yield put(detachOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(detachOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5R() {
    yield takeEvery(getType(detachOrder.actions.fetch), fetchCall$5R);
}

/**
 * Implements DisableDiscountOnOrder fetch for the Redux store
 *
 * @export
 * @class DisableDiscountOnOrder
 * @extends {StoreFetchableData<EVA.Core.DisableDiscountOnOrder, EVA.Core.EmptyResponseMessage>}
 */
class DisableDiscountOnOrder extends StoreFetchableData {
    /**
     * Creates an instance of DisableDiscountOnOrder
     *
     * @memberof DisableDiscountOnOrder
     */
    constructor() {
        super('disableDiscountOnOrder', '/message/DisableDiscountOnOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DisableDiscountOnOrder
     */
    *saga() {
        yield fork(listener$5Q);
    }
}
// Export the state handling singleton
//
const disableDiscountOnOrder = new DisableDiscountOnOrder();
// Setup the Saga implementation
//
function* fetchCall$5Q(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([disableDiscountOnOrder, 'fetchData'], action);
        yield put(disableDiscountOnOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(disableDiscountOnOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5Q() {
    yield takeEvery(getType(disableDiscountOnOrder.actions.fetch), fetchCall$5Q);
}

/**
 * Implements DownloadInvoice fetch for the Redux store
 *
 * @export
 * @class DownloadInvoice
 * @extends {StoreFetchableData<EVA.Core.DownloadInvoice, EVA.Core.ResourceResponseMessage>}
 */
class DownloadInvoice extends StoreFetchableData {
    /**
     * Creates an instance of DownloadInvoice
     *
     * @memberof DownloadInvoice
     */
    constructor() {
        super('downloadInvoice', '/message/DownloadInvoice');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DownloadInvoice
     */
    *saga() {
        yield fork(listener$5P);
    }
}
// Export the state handling singleton
//
const downloadInvoice = new DownloadInvoice();
// Setup the Saga implementation
//
function* fetchCall$5P(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([downloadInvoice, 'fetchData'], action);
        yield put(downloadInvoice.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(downloadInvoice.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5P() {
    yield takeEvery(getType(downloadInvoice.actions.fetch), fetchCall$5P);
}

/**
 * Implements DownloadOrderConfirmation fetch for the Redux store
 *
 * @export
 * @class DownloadOrderConfirmation
 * @extends {StoreFetchableData<EVA.Core.DownloadOrderConfirmation, EVA.Core.DownloadOrderConfirmationResponse>}
 */
class DownloadOrderConfirmation extends StoreFetchableData {
    /**
     * Creates an instance of DownloadOrderConfirmation
     *
     * @memberof DownloadOrderConfirmation
     */
    constructor() {
        super('downloadOrderConfirmation', '/message/DownloadOrderConfirmation');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DownloadOrderConfirmation
     */
    *saga() {
        yield fork(listener$5O);
    }
}
// Export the state handling singleton
//
const downloadOrderConfirmation = new DownloadOrderConfirmation();
// Setup the Saga implementation
//
function* fetchCall$5O(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([downloadOrderConfirmation, 'fetchData'], action);
        yield put(downloadOrderConfirmation.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(downloadOrderConfirmation.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5O() {
    yield takeEvery(getType(downloadOrderConfirmation.actions.fetch), fetchCall$5O);
}

/**
 * Implements DropOffCashDeposit fetch for the Redux store
 *
 * @export
 * @class DropOffCashDeposit
 * @extends {StoreFetchableData<EVA.Core.DropOffCashDeposit, EVA.Core.EmptyResponseMessage>}
 */
class DropOffCashDeposit extends StoreFetchableData {
    /**
     * Creates an instance of DropOffCashDeposit
     *
     * @memberof DropOffCashDeposit
     */
    constructor() {
        super('dropOffCashDeposit', '/message/DropOffCashDeposit');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DropOffCashDeposit
     */
    *saga() {
        yield fork(listener$5N);
    }
}
// Export the state handling singleton
//
const dropOffCashDeposit = new DropOffCashDeposit();
// Setup the Saga implementation
//
function* fetchCall$5N(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([dropOffCashDeposit, 'fetchData'], action);
        yield put(dropOffCashDeposit.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(dropOffCashDeposit.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5N() {
    yield takeEvery(getType(dropOffCashDeposit.actions.fetch), fetchCall$5N);
}

/**
 * Implements DuplicateOrder fetch for the Redux store
 *
 * @export
 * @class DuplicateOrder
 * @extends {StoreFetchableData<EVA.Core.DuplicateOrder, EVA.Core.DuplicateOrderResponse>}
 */
class DuplicateOrder extends StoreFetchableData {
    /**
     * Creates an instance of DuplicateOrder
     *
     * @memberof DuplicateOrder
     */
    constructor() {
        super('duplicateOrder', '/message/DuplicateOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof DuplicateOrder
     */
    *saga() {
        yield fork(listener$5M);
    }
}
// Export the state handling singleton
//
const duplicateOrder = new DuplicateOrder();
// Setup the Saga implementation
//
function* fetchCall$5M(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([duplicateOrder, 'fetchData'], action);
        yield put(duplicateOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(duplicateOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5M() {
    yield takeEvery(getType(duplicateOrder.actions.fetch), fetchCall$5M);
}

/**
 * Implements EmailInvoice fetch for the Redux store
 *
 * @export
 * @class EmailInvoice
 * @extends {StoreFetchableData<EVA.Core.EmailInvoice, EVA.Core.EmptyResponseMessage>}
 */
class EmailInvoice extends StoreFetchableData {
    /**
     * Creates an instance of EmailInvoice
     *
     * @memberof EmailInvoice
     */
    constructor() {
        super('emailInvoice', '/message/EmailInvoice');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof EmailInvoice
     */
    *saga() {
        yield fork(listener$5L);
    }
}
// Export the state handling singleton
//
const emailInvoice = new EmailInvoice();
// Setup the Saga implementation
//
function* fetchCall$5L(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([emailInvoice, 'fetchData'], action);
        yield put(emailInvoice.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(emailInvoice.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5L() {
    yield takeEvery(getType(emailInvoice.actions.fetch), fetchCall$5L);
}

/**
 * Implements EmailOrderConfirmation fetch for the Redux store
 *
 * @export
 * @class EmailOrderConfirmation
 * @extends {StoreFetchableData<EVA.Core.EmailOrderConfirmation, EVA.Core.EmptyResponseMessage>}
 */
class EmailOrderConfirmation extends StoreFetchableData {
    /**
     * Creates an instance of EmailOrderConfirmation
     *
     * @memberof EmailOrderConfirmation
     */
    constructor() {
        super('emailOrderConfirmation', '/message/EmailOrderConfirmation');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof EmailOrderConfirmation
     */
    *saga() {
        yield fork(listener$5K);
    }
}
// Export the state handling singleton
//
const emailOrderConfirmation = new EmailOrderConfirmation();
// Setup the Saga implementation
//
function* fetchCall$5K(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([emailOrderConfirmation, 'fetchData'], action);
        yield put(emailOrderConfirmation.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(emailOrderConfirmation.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5K() {
    yield takeEvery(getType(emailOrderConfirmation.actions.fetch), fetchCall$5K);
}

/**
 * Implements EmptyGiftCard fetch for the Redux store
 *
 * @export
 * @class EmptyGiftCard
 * @extends {StoreFetchableData<EVA.Core.EmptyGiftCard, EVA.Core.EmptyGiftCardResponse>}
 */
class EmptyGiftCard extends StoreFetchableData {
    /**
     * Creates an instance of EmptyGiftCard
     *
     * @memberof EmptyGiftCard
     */
    constructor() {
        super('emptyGiftCard', '/message/EmptyGiftCard');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof EmptyGiftCard
     */
    *saga() {
        yield fork(listener$5J);
    }
}
// Export the state handling singleton
//
const emptyGiftCard = new EmptyGiftCard();
// Setup the Saga implementation
//
function* fetchCall$5J(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([emptyGiftCard, 'fetchData'], action);
        yield put(emptyGiftCard.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(emptyGiftCard.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5J() {
    yield takeEvery(getType(emptyGiftCard.actions.fetch), fetchCall$5J);
}

/**
 * Implements EnableDisabledDiscountOnOrder fetch for the Redux store
 *
 * @export
 * @class EnableDisabledDiscountOnOrder
 * @extends {StoreFetchableData<EVA.Core.EnableDisabledDiscountOnOrder, EVA.Core.EmptyResponseMessage>}
 */
class EnableDisabledDiscountOnOrder extends StoreFetchableData {
    /**
     * Creates an instance of EnableDisabledDiscountOnOrder
     *
     * @memberof EnableDisabledDiscountOnOrder
     */
    constructor() {
        super('enableDisabledDiscountOnOrder', '/message/EnableDisabledDiscountOnOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof EnableDisabledDiscountOnOrder
     */
    *saga() {
        yield fork(listener$5I);
    }
}
// Export the state handling singleton
//
const enableDisabledDiscountOnOrder = new EnableDisabledDiscountOnOrder();
// Setup the Saga implementation
//
function* fetchCall$5I(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([enableDisabledDiscountOnOrder, 'fetchData'], action);
        yield put(enableDisabledDiscountOnOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(enableDisabledDiscountOnOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5I() {
    yield takeEvery(getType(enableDisabledDiscountOnOrder.actions.fetch), fetchCall$5I);
}

/**
 * Implements EVAPayGetInfo fetch for the Redux store
 *
 * @export
 * @class EVAPayGetInfo
 * @extends {StoreFetchableData<EVA.Payment.Core.EVAPayGetInfo, EVA.Payment.Core.EVAPayGetInfoResponse>}
 */
class EVAPayGetInfo extends StoreFetchableData {
    /**
     * Creates an instance of EVAPayGetInfo
     *
     * @memberof EVAPayGetInfo
     */
    constructor() {
        super('evaPayGetInfo', '/message/EVAPayGetInfo');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof EVAPayGetInfo
     */
    *saga() {
        yield fork(listener$5H);
    }
}
// Export the state handling singleton
//
const evaPayGetInfo = new EVAPayGetInfo();
// Setup the Saga implementation
//
function* fetchCall$5H(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([evaPayGetInfo, 'fetchData'], action);
        yield put(evaPayGetInfo.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(evaPayGetInfo.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5H() {
    yield takeEvery(getType(evaPayGetInfo.actions.fetch), fetchCall$5H);
}

/**
 * Implements ExemptOrderFromTax fetch for the Redux store
 *
 * @export
 * @class ExemptOrderFromTax
 * @extends {StoreFetchableData<EVA.Core.ExemptOrderFromTax, EVA.Core.EmptyResponseMessage>}
 */
class ExemptOrderFromTax extends StoreFetchableData {
    /**
     * Creates an instance of ExemptOrderFromTax
     *
     * @memberof ExemptOrderFromTax
     */
    constructor() {
        super('exemptOrderFromTax', '/message/ExemptOrderFromTax');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ExemptOrderFromTax
     */
    *saga() {
        yield fork(listener$5G);
    }
}
// Export the state handling singleton
//
const exemptOrderFromTax = new ExemptOrderFromTax();
// Setup the Saga implementation
//
function* fetchCall$5G(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([exemptOrderFromTax, 'fetchData'], action);
        yield put(exemptOrderFromTax.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(exemptOrderFromTax.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5G() {
    yield takeEvery(getType(exemptOrderFromTax.actions.fetch), fetchCall$5G);
}

/**
 * Implements ForceVerifyCustomer fetch for the Redux store
 *
 * @export
 * @class ForceVerifyCustomer
 * @extends {StoreFetchableData<EVA.Core.ForceVerifyCustomer, EVA.Core.EmptyResponseMessage>}
 */
class ForceVerifyCustomer extends StoreFetchableData {
    /**
     * Creates an instance of ForceVerifyCustomer
     *
     * @memberof ForceVerifyCustomer
     */
    constructor() {
        super('forceVerifyCustomer', '/message/ForceVerifyCustomer');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ForceVerifyCustomer
     */
    *saga() {
        yield fork(listener$5F);
    }
}
// Export the state handling singleton
//
const forceVerifyCustomer = new ForceVerifyCustomer();
// Setup the Saga implementation
//
function* fetchCall$5F(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([forceVerifyCustomer, 'fetchData'], action);
        yield put(forceVerifyCustomer.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(forceVerifyCustomer.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5F() {
    yield takeEvery(getType(forceVerifyCustomer.actions.fetch), fetchCall$5F);
}

/**
 * Implements FullyReceiveShipment fetch for the Redux store
 *
 * @export
 * @class FullyReceiveShipment
 * @extends {StoreFetchableData<EVA.Core.FullyReceiveShipment, EVA.Core.FullyReceiveShipmentResponse>}
 */
class FullyReceiveShipment extends StoreFetchableData {
    /**
     * Creates an instance of FullyReceiveShipment
     *
     * @memberof FullyReceiveShipment
     */
    constructor() {
        super('fullyReceiveShipment', '/message/FullyReceiveShipment');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof FullyReceiveShipment
     */
    *saga() {
        yield fork(listener$5E);
    }
}
// Export the state handling singleton
//
const fullyReceiveShipment = new FullyReceiveShipment();
// Setup the Saga implementation
//
function* fetchCall$5E(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([fullyReceiveShipment, 'fetchData'], action);
        yield put(fullyReceiveShipment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(fullyReceiveShipment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5E() {
    yield takeEvery(getType(fullyReceiveShipment.actions.fetch), fetchCall$5E);
}

/**
 * Implements GenerateDeviceHubBarcode fetch for the Redux store
 *
 * @export
 * @class GenerateDeviceHubBarcode
 * @extends {StoreFetchableData<EVA.Core.GenerateDeviceHubBarcode, EVA.Core.GenerateDeviceHubBarcodeResponse>}
 */
class GenerateDeviceHubBarcode extends StoreFetchableData {
    /**
     * Creates an instance of GenerateDeviceHubBarcode
     *
     * @memberof GenerateDeviceHubBarcode
     */
    constructor() {
        super('generateDeviceHubBarcode', '/message/GenerateDeviceHubBarcode');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GenerateDeviceHubBarcode
     */
    *saga() {
        yield fork(listener$5D);
    }
}
// Export the state handling singleton
//
const generateDeviceHubBarcode = new GenerateDeviceHubBarcode();
// Setup the Saga implementation
//
function* fetchCall$5D(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([generateDeviceHubBarcode, 'fetchData'], action);
        yield put(generateDeviceHubBarcode.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(generateDeviceHubBarcode.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5D() {
    yield takeEvery(getType(generateDeviceHubBarcode.actions.fetch), fetchCall$5D);
}

/**
 * Implements GenerateElevationBarcode fetch for the Redux store
 *
 * @export
 * @class GenerateElevationBarcode
 * @extends {StoreFetchableData<EVA.Core.GenerateElevationBarcode, EVA.Core.GenerateElevationBarcodeResponse>}
 */
class GenerateElevationBarcode extends StoreFetchableData {
    /**
     * Creates an instance of GenerateElevationBarcode
     *
     * @memberof GenerateElevationBarcode
     */
    constructor() {
        super('generateElevationBarcode', '/message/GenerateElevationBarcode');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GenerateElevationBarcode
     */
    *saga() {
        yield fork(listener$5C);
    }
}
// Export the state handling singleton
//
const generateElevationBarcode = new GenerateElevationBarcode();
// Setup the Saga implementation
//
function* fetchCall$5C(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([generateElevationBarcode, 'fetchData'], action);
        yield put(generateElevationBarcode.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(generateElevationBarcode.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5C() {
    yield takeEvery(getType(generateElevationBarcode.actions.fetch), fetchCall$5C);
}

/**
 * Implements GenerateIdentificationCodeForEmployee fetch for the Redux store
 *
 * @export
 * @class GenerateIdentificationCodeForEmployee
 * @extends {StoreFetchableData<EVA.Core.Management.GenerateIdentificationCodeForEmployee, EVA.Core.Management.GenerateIdentificationCodeForEmployeeResponse>}
 */
class GenerateIdentificationCodeForEmployee extends StoreFetchableData {
    /**
     * Creates an instance of GenerateIdentificationCodeForEmployee
     *
     * @memberof GenerateIdentificationCodeForEmployee
     */
    constructor() {
        super('generateIdentificationCodeForEmployee', '/message/GenerateIdentificationCodeForEmployee');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GenerateIdentificationCodeForEmployee
     */
    *saga() {
        yield fork(listener$5B);
    }
}
// Export the state handling singleton
//
const generateIdentificationCodeForEmployee = new GenerateIdentificationCodeForEmployee();
// Setup the Saga implementation
//
function* fetchCall$5B(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([generateIdentificationCodeForEmployee, 'fetchData'], action);
        yield put(generateIdentificationCodeForEmployee.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(generateIdentificationCodeForEmployee.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5B() {
    yield takeEvery(getType(generateIdentificationCodeForEmployee.actions.fetch), fetchCall$5B);
}

/**
 * Implements GenerateIdentificationPinForEmployee fetch for the Redux store
 *
 * @export
 * @class GenerateIdentificationPinForEmployee
 * @extends {StoreFetchableData<EVA.Core.Management.GenerateIdentificationPinForEmployee, EVA.Core.Management.GenerateIdentificationPinForEmployeeResponse>}
 */
class GenerateIdentificationPinForEmployee extends StoreFetchableData {
    /**
     * Creates an instance of GenerateIdentificationPinForEmployee
     *
     * @memberof GenerateIdentificationPinForEmployee
     */
    constructor() {
        super('generateIdentificationPinForEmployee', '/message/GenerateIdentificationPinForEmployee');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GenerateIdentificationPinForEmployee
     */
    *saga() {
        yield fork(listener$5A);
    }
}
// Export the state handling singleton
//
const generateIdentificationPinForEmployee = new GenerateIdentificationPinForEmployee();
// Setup the Saga implementation
//
function* fetchCall$5A(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([generateIdentificationPinForEmployee, 'fetchData'], action);
        yield put(generateIdentificationPinForEmployee.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(generateIdentificationPinForEmployee.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5A() {
    yield takeEvery(getType(generateIdentificationPinForEmployee.actions.fetch), fetchCall$5A);
}

/**
 * Implements GenerateScanModeBarcode fetch for the Redux store
 *
 * @export
 * @class GenerateScanModeBarcode
 * @extends {StoreFetchableData<EVA.Core.GenerateScanModeBarcode, EVA.Core.GenerateScanModeBarcodeResponse>}
 */
class GenerateScanModeBarcode extends StoreFetchableData {
    /**
     * Creates an instance of GenerateScanModeBarcode
     *
     * @memberof GenerateScanModeBarcode
     */
    constructor() {
        super('generateScanModeBarcode', '/message/GenerateScanModeBarcode');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GenerateScanModeBarcode
     */
    *saga() {
        yield fork(listener$5z);
    }
}
// Export the state handling singleton
//
const generateScanModeBarcode = new GenerateScanModeBarcode();
// Setup the Saga implementation
//
function* fetchCall$5z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([generateScanModeBarcode, 'fetchData'], action);
        yield put(generateScanModeBarcode.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(generateScanModeBarcode.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5z() {
    yield takeEvery(getType(generateScanModeBarcode.actions.fetch), fetchCall$5z);
}

/**
 * Implements GenerateSessionBarcode fetch for the Redux store
 *
 * @export
 * @class GenerateSessionBarcode
 * @extends {StoreFetchableData<EVA.Core.GenerateSessionBarcode, EVA.Core.GenerateSessionBarcodeResponse>}
 */
class GenerateSessionBarcode extends StoreFetchableData {
    /**
     * Creates an instance of GenerateSessionBarcode
     *
     * @memberof GenerateSessionBarcode
     */
    constructor() {
        super('generateSessionBarcode', '/message/GenerateSessionBarcode');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GenerateSessionBarcode
     */
    *saga() {
        yield fork(listener$5y);
    }
}
// Export the state handling singleton
//
const generateSessionBarcode = new GenerateSessionBarcode();
// Setup the Saga implementation
//
function* fetchCall$5y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([generateSessionBarcode, 'fetchData'], action);
        yield put(generateSessionBarcode.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(generateSessionBarcode.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5y() {
    yield takeEvery(getType(generateSessionBarcode.actions.fetch), fetchCall$5y);
}

/**
 * Implements GenerateTemporaryElevationCode fetch for the Redux store
 *
 * @export
 * @class GenerateTemporaryElevationCode
 * @extends {StoreFetchableData<EVA.Core.GenerateTemporaryElevationCode, EVA.Core.GenerateTemporaryElevationCodeResponse>}
 */
class GenerateTemporaryElevationCode extends StoreFetchableData {
    /**
     * Creates an instance of GenerateTemporaryElevationCode
     *
     * @memberof GenerateTemporaryElevationCode
     */
    constructor() {
        super('generateTemporaryElevationCode', '/message/GenerateTemporaryElevationCode');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GenerateTemporaryElevationCode
     */
    *saga() {
        yield fork(listener$5x);
    }
}
// Export the state handling singleton
//
const generateTemporaryElevationCode = new GenerateTemporaryElevationCode();
// Setup the Saga implementation
//
function* fetchCall$5x(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([generateTemporaryElevationCode, 'fetchData'], action);
        yield put(generateTemporaryElevationCode.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(generateTemporaryElevationCode.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5x() {
    yield takeEvery(getType(generateTemporaryElevationCode.actions.fetch), fetchCall$5x);
}

/**
 * Implements GetAddressForZipCode fetch for the Redux store
 *
 * @export
 * @class GetAddressForZipCode
 * @extends {StoreFetchableData<EVA.Core.GetAddressForZipCode, EVA.Core.GetAddressForZipCodeResponse>}
 */
class GetAddressForZipCode extends StoreFetchableData {
    /**
     * Creates an instance of GetAddressForZipCode
     *
     * @memberof GetAddressForZipCode
     */
    constructor() {
        super('getAddressForZipCode', '/message/GetAddressForZipCode');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAddressForZipCode
     */
    *saga() {
        yield fork(listener$5w);
    }
}
// Export the state handling singleton
//
const getAddressForZipCode = new GetAddressForZipCode();
// Setup the Saga implementation
//
function* fetchCall$5w(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAddressForZipCode, 'fetchData'], action);
        yield put(getAddressForZipCode.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAddressForZipCode.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5w() {
    yield takeEvery(getType(getAddressForZipCode.actions.fetch), fetchCall$5w);
}

/**
 * Implements GetAddressRequirements fetch for the Redux store
 *
 * @export
 * @class GetAddressRequirements
 * @extends {StoreFetchableData<EVA.Core.GetAddressRequirements, EVA.Core.GetAddressRequirementsResponse>}
 */
class GetAddressRequirements extends StoreFetchableData {
    /**
     * Creates an instance of GetAddressRequirements
     *
     * @memberof GetAddressRequirements
     */
    constructor() {
        super('getAddressRequirements', '/message/GetAddressRequirements');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAddressRequirements
     */
    *saga() {
        yield fork(listener$5v);
    }
}
// Export the state handling singleton
//
const getAddressRequirements = new GetAddressRequirements();
// Setup the Saga implementation
//
function* fetchCall$5v(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAddressRequirements, 'fetchData'], action);
        yield put(getAddressRequirements.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAddressRequirements.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5v() {
    yield takeEvery(getType(getAddressRequirements.actions.fetch), fetchCall$5v);
}

/**
 * Implements GetAuditingSummary fetch for the Redux store
 *
 * @export
 * @class GetAuditingSummary
 * @extends {StoreFetchableData<EVA.Auditing.GetAuditingSummary, EVA.Auditing.GetAuditingSummaryResponse>}
 */
class GetAuditingSummary extends StoreFetchableData {
    /**
     * Creates an instance of GetAuditingSummary
     *
     * @memberof GetAuditingSummary
     */
    constructor() {
        super('getAuditingSummary', '/message/GetAuditingSummary');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAuditingSummary
     */
    *saga() {
        yield fork(listener$5u);
    }
}
// Export the state handling singleton
//
const getAuditingSummary = new GetAuditingSummary();
// Setup the Saga implementation
//
function* fetchCall$5u(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAuditingSummary, 'fetchData'], action);
        yield put(getAuditingSummary.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAuditingSummary.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5u() {
    yield takeEvery(getType(getAuditingSummary.actions.fetch), fetchCall$5u);
}

/**
 * Implements GetAutocompleteAddressByReference fetch for the Redux store
 *
 * @export
 * @class GetAutocompleteAddressByReference
 * @extends {StoreFetchableData<EVA.Core.GetAutocompleteAddressByReference, EVA.Core.GetAutocompleteAddressByReferenceResponse>}
 */
class GetAutocompleteAddressByReference extends StoreFetchableData {
    /**
     * Creates an instance of GetAutocompleteAddressByReference
     *
     * @memberof GetAutocompleteAddressByReference
     */
    constructor() {
        super('getAutocompleteAddressByReference', '/message/GetAutocompleteAddressByReference');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAutocompleteAddressByReference
     */
    *saga() {
        yield fork(listener$5t);
    }
}
// Export the state handling singleton
//
const getAutocompleteAddressByReference = new GetAutocompleteAddressByReference();
// Setup the Saga implementation
//
function* fetchCall$5t(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAutocompleteAddressByReference, 'fetchData'], action);
        yield put(getAutocompleteAddressByReference.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAutocompleteAddressByReference.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5t() {
    yield takeEvery(getType(getAutocompleteAddressByReference.actions.fetch), fetchCall$5t);
}

/**
 * Implements GetAvailableCashAmountForDevice fetch for the Redux store
 *
 * @export
 * @class GetAvailableCashAmountForDevice
 * @extends {StoreFetchableData<EVA.Core.GetAvailableCashAmountForDevice, EVA.Core.GetAvailableCashAmountForDeviceResponse>}
 */
class GetAvailableCashAmountForDevice extends StoreFetchableData {
    /**
     * Creates an instance of GetAvailableCashAmountForDevice
     *
     * @memberof GetAvailableCashAmountForDevice
     */
    constructor() {
        super('getAvailableCashAmountForDevice', '/message/GetAvailableCashAmountForDevice');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAvailableCashAmountForDevice
     */
    *saga() {
        yield fork(listener$5s);
    }
}
// Export the state handling singleton
//
const getAvailableCashAmountForDevice = new GetAvailableCashAmountForDevice();
// Setup the Saga implementation
//
function* fetchCall$5s(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAvailableCashAmountForDevice, 'fetchData'], action);
        yield put(getAvailableCashAmountForDevice.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAvailableCashAmountForDevice.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5s() {
    yield takeEvery(getType(getAvailableCashAmountForDevice.actions.fetch), fetchCall$5s);
}

/**
 * Implements GetAvailableDocumentsForOrder fetch for the Redux store
 *
 * @export
 * @class GetAvailableDocumentsForOrder
 * @extends {StoreFetchableData<EVA.Core.GetAvailableDocumentsForOrder, EVA.Core.GetAvailableDocumentsForOrderResponse>}
 */
class GetAvailableDocumentsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetAvailableDocumentsForOrder
     *
     * @memberof GetAvailableDocumentsForOrder
     */
    constructor() {
        super('getAvailableDocumentsForOrder', '/message/GetAvailableDocumentsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAvailableDocumentsForOrder
     */
    *saga() {
        yield fork(listener$5r);
    }
}
// Export the state handling singleton
//
const getAvailableDocumentsForOrder = new GetAvailableDocumentsForOrder();
// Setup the Saga implementation
//
function* fetchCall$5r(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAvailableDocumentsForOrder, 'fetchData'], action);
        yield put(getAvailableDocumentsForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAvailableDocumentsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5r() {
    yield takeEvery(getType(getAvailableDocumentsForOrder.actions.fetch), fetchCall$5r);
}

/**
 * Implements GetAvailableGiftWrappingLinesForOrder fetch for the Redux store
 *
 * @export
 * @class GetAvailableGiftWrappingLinesForOrder
 * @extends {StoreFetchableData<EVA.Core.GetAvailableGiftWrappingLinesForOrder, EVA.Core.GetAvailableGiftWrappingLinesForOrderResponse>}
 */
class GetAvailableGiftWrappingLinesForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetAvailableGiftWrappingLinesForOrder
     *
     * @memberof GetAvailableGiftWrappingLinesForOrder
     */
    constructor() {
        super('getAvailableGiftWrappingLinesForOrder', '/message/GetAvailableGiftWrappingLinesForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAvailableGiftWrappingLinesForOrder
     */
    *saga() {
        yield fork(listener$5q);
    }
}
// Export the state handling singleton
//
const getAvailableGiftWrappingLinesForOrder = new GetAvailableGiftWrappingLinesForOrder();
// Setup the Saga implementation
//
function* fetchCall$5q(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAvailableGiftWrappingLinesForOrder, 'fetchData'], action);
        yield put(getAvailableGiftWrappingLinesForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAvailableGiftWrappingLinesForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5q() {
    yield takeEvery(getType(getAvailableGiftWrappingLinesForOrder.actions.fetch), fetchCall$5q);
}

/**
 * Implements GetAvailableOpenIDConfigurations fetch for the Redux store
 *
 * @export
 * @class GetAvailableOpenIDConfigurations
 * @extends {StoreFetchableData<EVA.Authentication.OpenID.GetAvailableOpenIDConfigurations, EVA.Authentication.OpenID.GetAvailableOpenIDConfigurationsResponse>}
 */
class GetAvailableOpenIDConfigurations extends StoreFetchableData {
    /**
     * Creates an instance of GetAvailableOpenIDConfigurations
     *
     * @memberof GetAvailableOpenIDConfigurations
     */
    constructor() {
        super('getAvailableOpenIDConfigurations', '/message/GetAvailableOpenIDConfigurations');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAvailableOpenIDConfigurations
     */
    *saga() {
        yield fork(listener$5p);
    }
}
// Export the state handling singleton
//
const getAvailableOpenIDConfigurations = new GetAvailableOpenIDConfigurations();
// Setup the Saga implementation
//
function* fetchCall$5p(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAvailableOpenIDConfigurations, 'fetchData'], action);
        yield put(getAvailableOpenIDConfigurations.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAvailableOpenIDConfigurations.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5p() {
    yield takeEvery(getType(getAvailableOpenIDConfigurations.actions.fetch), fetchCall$5p);
}

/**
 * Implements GetAvailablePaymentMethods fetch for the Redux store
 *
 * @export
 * @class GetAvailablePaymentMethods
 * @extends {StoreFetchableData<EVA.Core.GetAvailablePaymentMethods, EVA.Core.GetAvailablePaymentMethodsResponse>}
 */
class GetAvailablePaymentMethods extends StoreFetchableData {
    /**
     * Creates an instance of GetAvailablePaymentMethods
     *
     * @memberof GetAvailablePaymentMethods
     */
    constructor() {
        super('getAvailablePaymentMethods', '/message/GetAvailablePaymentMethods');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAvailablePaymentMethods
     */
    *saga() {
        yield fork(listener$5o);
    }
}
// Export the state handling singleton
//
const getAvailablePaymentMethods = new GetAvailablePaymentMethods();
// Setup the Saga implementation
//
function* fetchCall$5o(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAvailablePaymentMethods, 'fetchData'], action);
        yield put(getAvailablePaymentMethods.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAvailablePaymentMethods.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5o() {
    yield takeEvery(getType(getAvailablePaymentMethods.actions.fetch), fetchCall$5o);
}

/**
 * Implements GetAvailableSubscriptions fetch for the Redux store
 *
 * @export
 * @class GetAvailableSubscriptions
 * @extends {StoreFetchableData<EVA.Core.GetAvailableSubscriptions, EVA.Core.GetAvailableSubscriptionsResponse>}
 */
class GetAvailableSubscriptions extends StoreFetchableData {
    /**
     * Creates an instance of GetAvailableSubscriptions
     *
     * @memberof GetAvailableSubscriptions
     */
    constructor() {
        super('getAvailableSubscriptions', '/message/GetAvailableSubscriptions');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAvailableSubscriptions
     */
    *saga() {
        yield fork(listener$5n);
    }
}
// Export the state handling singleton
//
const getAvailableSubscriptions = new GetAvailableSubscriptions();
// Setup the Saga implementation
//
function* fetchCall$5n(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAvailableSubscriptions, 'fetchData'], action);
        yield put(getAvailableSubscriptions.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAvailableSubscriptions.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5n() {
    yield takeEvery(getType(getAvailableSubscriptions.actions.fetch), fetchCall$5n);
}

/**
 * Implements GetAvailableTwoFactorAuthenticators fetch for the Redux store
 *
 * @export
 * @class GetAvailableTwoFactorAuthenticators
 * @extends {StoreFetchableData<EVA.Core.GetAvailableTwoFactorAuthenticators, EVA.Core.GetAvailableTwoFactorAuthenticatorsResponse>}
 */
class GetAvailableTwoFactorAuthenticators extends StoreFetchableData {
    /**
     * Creates an instance of GetAvailableTwoFactorAuthenticators
     *
     * @memberof GetAvailableTwoFactorAuthenticators
     */
    constructor() {
        super('getAvailableTwoFactorAuthenticators', '/message/GetAvailableTwoFactorAuthenticators');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAvailableTwoFactorAuthenticators
     */
    *saga() {
        yield fork(listener$5m);
    }
}
// Export the state handling singleton
//
const getAvailableTwoFactorAuthenticators = new GetAvailableTwoFactorAuthenticators();
// Setup the Saga implementation
//
function* fetchCall$5m(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAvailableTwoFactorAuthenticators, 'fetchData'], action);
        yield put(getAvailableTwoFactorAuthenticators.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAvailableTwoFactorAuthenticators.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5m() {
    yield takeEvery(getType(getAvailableTwoFactorAuthenticators.actions.fetch), fetchCall$5m);
}

/**
 * Implements GetAvailableUserRequirementsProperties fetch for the Redux store
 *
 * @export
 * @class GetAvailableUserRequirementsProperties
 * @extends {StoreFetchableData<EVA.Core.Management.GetAvailableUserRequirementsProperties, EVA.Core.Management.GetAvailableUserRequirementsPropertiesResponse>}
 */
class GetAvailableUserRequirementsProperties extends StoreFetchableData {
    /**
     * Creates an instance of GetAvailableUserRequirementsProperties
     *
     * @memberof GetAvailableUserRequirementsProperties
     */
    constructor() {
        super('getAvailableUserRequirementsProperties', '/message/GetAvailableUserRequirementsProperties');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAvailableUserRequirementsProperties
     */
    *saga() {
        yield fork(listener$5l);
    }
}
// Export the state handling singleton
//
const getAvailableUserRequirementsProperties = new GetAvailableUserRequirementsProperties();
// Setup the Saga implementation
//
function* fetchCall$5l(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getAvailableUserRequirementsProperties, 'fetchData'], action);
        yield put(getAvailableUserRequirementsProperties.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getAvailableUserRequirementsProperties.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5l() {
    yield takeEvery(getType(getAvailableUserRequirementsProperties.actions.fetch), fetchCall$5l);
}

/**
 * Implements GetBlobInfo fetch for the Redux store
 *
 * @export
 * @class GetBlobInfo
 * @extends {StoreFetchableData<EVA.Blobs.GetBlobInfo, EVA.Blobs.GetBlobInfoResponse>}
 */
class GetBlobInfo extends StoreFetchableData {
    /**
     * Creates an instance of GetBlobInfo
     *
     * @memberof GetBlobInfo
     */
    constructor() {
        super('getBlobInfo', '/message/GetBlobInfo');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetBlobInfo
     */
    *saga() {
        yield fork(listener$5k);
    }
}
// Export the state handling singleton
//
const getBlobInfo = new GetBlobInfo();
// Setup the Saga implementation
//
function* fetchCall$5k(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getBlobInfo, 'fetchData'], action);
        yield put(getBlobInfo.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getBlobInfo.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5k() {
    yield takeEvery(getType(getBlobInfo.actions.fetch), fetchCall$5k);
}

/**
 * Implements GetBundleProductDetailQuantityOnHand fetch for the Redux store
 *
 * @export
 * @class GetBundleProductDetailQuantityOnHand
 * @extends {StoreFetchableData<EVA.Core.GetQuantityOnHandForProducts, EVA.Core.GetQuantityOnHandForProductsResponse>}
 */
class GetBundleProductDetailQuantityOnHand extends StoreFetchableData {
    /**
     * Creates an instance of GetBundleProductDetailQuantityOnHand
     *
     * @memberof GetBundleProductDetailQuantityOnHand
     */
    constructor() {
        super('getBundleProductDetailQuantityOnHand', '/message/GetQuantityOnHandForProducts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetBundleProductDetailQuantityOnHand
     */
    *saga() {
        yield fork(listener$5j);
    }
}
// Export the state handling singleton
//
const getBundleProductDetailQuantityOnHand = new GetBundleProductDetailQuantityOnHand();
// Setup the Saga implementation
//
function* fetchCall$5j(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getBundleProductDetailQuantityOnHand, 'fetchData'], action);
        yield put(getBundleProductDetailQuantityOnHand.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getBundleProductDetailQuantityOnHand.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5j() {
    yield takeEvery(getType(getBundleProductDetailQuantityOnHand.actions.fetch), fetchCall$5j);
}

/**
 * Implements GetBundleProductDetailProductAvailability fetch for the Redux store
 *
 * @export
 * @class GetBundleProductDetailProductAvailability
 * @extends {StoreFetchableData<EVA.Core.GetProductAvailability, EVA.Core.GetProductAvailabilityResponse>}
 */
class GetBundleProductDetailProductAvailability extends StoreFetchableData {
    /**
     * Initial state with extended properties
     *
     * @type {IGetBundleProductDetailProductAvailabilityState}
     * @memberof GetBunIGetBundleProductDetailProductAvailability
     */
    get initialState() {
        return {
            isFetching: false,
            request: null,
            response: null,
            error: null,
            chainStarter: this.name,
            stock: {},
        };
    }
    /**
     * Creates an instance of GetBundleProductDetailProductAvailability
     *
     * @memberof GetBundleProductDetailProductAvailability
     */
    constructor() {
        super('getBundleProductDetailProductAvailability', '/message/GetProductAvailability');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetBundleProductDetailProductAvailability
     */
    *saga() {
        yield fork(listener$5i);
    }
    /**
     * The extended collection of reducer functions
     *
     * @protected
     * @returns {{ [ reducerName: string ]: ( state: any, action: AnyAction ) => any }}
     * @memberof GetBundleProductDetailProductAvailability
     */
    getReducerFunctions() {
        const baseReducerFuncs = super.getReducerFunctions();
        baseReducerFuncs.stock = (state = [], action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const response = action.payload;
                    const stock = {};
                    each(response.Products, (product) => {
                        const productId = product.ProductID;
                        if (productId) {
                            stock[productId] = get(product, 'Pickup.QuantityAvailable', 0);
                        }
                    });
                    return stock;
                default:
                    return state;
            }
        };
        return baseReducerFuncs;
    }
}
// Export the state handling singleton
//
const getBundleProductDetailProductAvailability = new GetBundleProductDetailProductAvailability();
// Setup the Saga implementation
//
function* fetchCall$5i(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getBundleProductDetailProductAvailability, 'fetchData'], action);
        yield put(getBundleProductDetailProductAvailability.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getBundleProductDetailProductAvailability.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5i() {
    yield takeEvery(getType(getBundleProductDetailProductAvailability.actions.fetch), fetchCall$5i);
}

/**
 * Implements GetBundleProductDetail fetch for the Redux store
 *
 * @export
 * @class GetBundleProductDetail
 * @extends {StoreFetchableData<EVA.Core.GetBundleProductDetail, EVA.Core.GetBundleProductDetailResponse>}
 */
class GetBundleProductDetail extends StoreFetchableData {
    /**
     * Creates an instance of GetBundleProductDetail
     *
     * @memberof GetBundleProductDetail
     */
    constructor() {
        super('getBundleProductDetail', '/message/GetBundleProductDetails', undefined, { addDefaultProductPropertiesPath: 'IncludedFields' });
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetBundleProductDetail
     */
    *saga() {
        yield fork(listener$5h);
    }
}
// Export the state handling singleton
//
const getBundleProductDetail = new GetBundleProductDetail();
function* fetchCall$5h(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([getBundleProductDetail, 'fetchData'], action);
        yield put(getBundleProductDetail.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        const currentUserState = getCurrentUser.getState();
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Get the product details from the store state
        //
        const productId = get(data, 'BundleProductID');
        log$1(`[EVA-SDK-REDUX:getBundleProductDetail] Fetch bundle product details`, productId);
        // Check we actually received a product
        //
        let optionalGetProductAvailabilityChainPromise = Promise.resolve(undefined);
        if (productId) {
            const currentOrganizationId = get(currentUserState, 'response.User.CurrentOrganizationID');
            const availabilityOptions = {
                Bundles: {
                    AvailabilityCalculationMethod: 1,
                    IncludeBundleInformation: true
                },
                Delivery: {
                    AvailabilityDate: true,
                },
                Pickup: {
                    AvailabilityDate: true,
                    OrganizationUnitIDs: [currentOrganizationId],
                },
                ProductAvailabilityDetails: {
                    OrganizationUnitIDs: [currentOrganizationId],
                    ProductIDs: [productId],
                }
            };
            const getProductAvailabilityRequest = {
                Products: [
                    {
                        ID: productId,
                        QuantityRequested: 1,
                    },
                ],
                Options: availabilityOptions,
            };
            log$1('[EVA-SDK-REDUX:getBundleProductDetail] Fetching product availability for cart products', getProductAvailabilityRequest);
            const [getBundleProductDetailProductAvailabilityAction, , getBundleProductDetailProductAvailabilityChainPromise] = getBundleProductDetailProductAvailability.createFetchAction(getProductAvailabilityRequest, chainStarter, undefined, undefined, undefined, undefined, chainContext);
            yield put(getBundleProductDetailProductAvailabilityAction);
            optionalGetProductAvailabilityChainPromise = getBundleProductDetailProductAvailabilityChainPromise;
            // Retrieve the quantity on hand of all the stock holding products
            // for the users current organization unit (store)
            //
            let optionalGetQuantityOnHandForProductsChainPromise = Promise.resolve(undefined);
            const organizationUnitId = get(currentUserState, 'response.User.CurrentOrganizationID');
            if (productId && organizationUnitId) {
                const getQuantityOnHandForProductsRequest = {
                    OrganizationUnitID: organizationUnitId,
                    ProductIDs: [productId],
                };
                log$1('[EVA-SDK-REDUX:getProductDetail] Fetching quantity on hand for configurable product details', getQuantityOnHandForProductsRequest);
                const [getQuantityOnHandForProductsAction, , getQuantityOnHandForProductsChainPromise] = getBundleProductDetailQuantityOnHand.createFetchAction(getQuantityOnHandForProductsRequest, chainStarter, undefined, undefined, undefined, undefined, chainContext);
                yield put(getQuantityOnHandForProductsAction);
                optionalGetQuantityOnHandForProductsChainPromise = getQuantityOnHandForProductsChainPromise;
            }
            // Resolve the chain
            //
            Promise.all([optionalGetProductAvailabilityChainPromise, optionalGetQuantityOnHandForProductsChainPromise])
                .then((chainResponses) => {
                chainResponses.unshift(data);
                fetchActionPayload.chainResolve(chainResponses);
            })
                .catch(fetchActionPayload.chainReject);
        }
        else {
            fetchActionPayload.chainResolve([data, undefined, undefined, undefined]);
        }
    }
    catch (err) {
        yield put(getBundleProductDetail.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5h() {
    yield takeEvery(getType(getBundleProductDetail.actions.fetch), fetchCall$5h);
}

/**
 * Implements GetBundleProducts fetch for the Redux store
 * This is a pre-configured filtered SearchProducts call
 * ProductType 16 = Service produc
 *
 * @export
 * @class GetBundleProducts
 * @extends {StoreFetchableData<EVA.Core.SearchProducts, EVA.Core.SearchProductsResponse>}
 */
class GetBundleProducts extends StoreFetchableData {
    /**
     * Creates an instance of GetBundleProducts
     *
     * @memberof GetBundleProducts
     */
    constructor() {
        super('getBundleProducts', '/message/SearchProducts', undefined, { addDefaultProductPropertiesPath: 'IncludedFields' });
    }
    /**
     * Retrieves the requested data from the EVA backend
     *
     * @public
     * @param {*} action The Redux action
     * @returns {Promise<EVA.Core.SearchProductsResponse>}
     * @memberof GetBundleProducts
     */
    fetchData(action) {
        // The action supplied is a SearchProducts because that is the most
        // convenient way to select add additional filtering
        //
        const searchPayload = get(action, 'payload.request') || {};
        // ProductType 2048 = Bundle product
        //
        extend(searchPayload, { Filters: { product_types: { Values: [2048] } } });
        set(action, 'payload.request', searchPayload);
        return super.fetchData(action);
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetBundleProducts
     */
    *saga() {
        yield fork(listener$5g);
    }
}
// Export the state handling singleton
//
const getBundleProducts = new GetBundleProducts();
// Setup the Saga implementation
//
function* fetchCall$5g(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getBundleProducts, 'fetchData'], action);
        yield put(getBundleProducts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getBundleProducts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5g() {
    yield takeEvery(getType(getBundleProducts.actions.fetch), fetchCall$5g);
}

/**
 * Implements GetBundleProductsForProduct fetch for the Redux store
 *
 * @export
 * @class GetBundleProductsForProduct
 * @extends {StoreFetchableData<EVA.Core.GetBundleProductsForProduct, EVA.Core.GetBundleProductsForProductResponse>}
 */
class GetBundleProductsForProduct extends StoreFetchableData {
    /**
     * Creates an instance of GetBundleProductsForProduct
     *
     * @memberof GetBundleProductsForProduct
     */
    constructor() {
        super('getBundleProductsForProduct', '/message/GetBundleProductsForProduct');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetBundleProductsForProduct
     */
    *saga() {
        yield fork(listener$5f);
    }
}
// Export the state handling singleton
//
const getBundleProductsForProduct = new GetBundleProductsForProduct();
// Setup the Saga implementation
//
function* fetchCall$5f(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getBundleProductsForProduct, 'fetchData'], action);
        yield put(getBundleProductsForProduct.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getBundleProductsForProduct.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5f() {
    yield takeEvery(getType(getBundleProductsForProduct.actions.fetch), fetchCall$5f);
}

/**
 * Implements GetCashExpenseTypes fetch for the Redux store
 *
 * @export
 * @class GetCashExpenseTypes
 * @extends {StoreFetchableData<EVA.Core.GetCashExpenseTypes, EVA.Core.GetCashExpenseTypesResponse>}
 */
class GetCashExpenseTypes extends StoreFetchableData {
    /**
     * Creates an instance of GetCashExpenseTypes
     *
     * @memberof GetCashExpenseTypes
     */
    constructor() {
        super('getCashExpenseTypes', '/message/GetCashExpenseTypes');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetCashExpenseTypes
     */
    *saga() {
        yield fork(listener$5e);
    }
}
// Export the state handling singleton
//
const getCashExpenseTypes = new GetCashExpenseTypes();
// Setup the Saga implementation
//
function* fetchCall$5e(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getCashExpenseTypes, 'fetchData'], action);
        yield put(getCashExpenseTypes.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getCashExpenseTypes.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5e() {
    yield takeEvery(getType(getCashExpenseTypes.actions.fetch), fetchCall$5e);
}

/**
 * Implements GetCashJournalDenominations fetch for the Redux store
 *
 * @export
 * @class GetCashJournalDenominations
 * @extends {StoreFetchableData<EVA.Core.GetCashJournalDenominations, EVA.Core.GetCashJournalDenominationsResponse>}
 */
class GetCashJournalDenominations extends StoreFetchableData {
    /**
     * Creates an instance of GetCashJournalDenominations
     *
     * @memberof GetCashJournalDenominations
     */
    constructor() {
        super('getCashJournalDenominations', '/message/GetCashJournalDenominations');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetCashJournalDenominations
     */
    *saga() {
        yield fork(listener$5d);
    }
}
// Export the state handling singleton
//
const getCashJournalDenominations = new GetCashJournalDenominations();
// Setup the Saga implementation
//
function* fetchCall$5d(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getCashJournalDenominations, 'fetchData'], action);
        yield put(getCashJournalDenominations.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getCashJournalDenominations.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5d() {
    yield takeEvery(getType(getCashJournalDenominations.actions.fetch), fetchCall$5d);
}

/**
 * Implements GetCFDConfiguration fetch for the Redux store
 *
 * @export
 * @class GetCFDConfiguration
 * @extends {StoreFetchableData<EVA.Core.Management.GetCFDConfiguration, EVA.Core.Management.GetCFDConfigurationResponse>}
 */
class GetCFDConfiguration extends StoreFetchableData {
    /**
     * Creates an instance of GetCFDConfiguration
     *
     * @memberof GetCFDConfiguration
     */
    constructor() {
        super('getCFDConfiguration', '/message/GetCFDConfiguration');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetCFDConfiguration
     */
    *saga() {
        yield fork(listener$5c);
    }
}
// Export the state handling singleton
//
const getCFDConfiguration = new GetCFDConfiguration();
// Setup the Saga implementation
//
function* fetchCall$5c(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getCFDConfiguration, 'fetchData'], action);
        yield put(getCFDConfiguration.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getCFDConfiguration.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5c() {
    yield takeEvery(getType(getCFDConfiguration.actions.fetch), fetchCall$5c);
}

/**
 * Implements GetCompanyForUser fetch for the Redux store
 *
 * @export
 * @class GetCompanyForUser
 * @extends {StoreFetchableData<EVA.Core.GetCompanyForUser, EVA.Core.GetCompanyForUserResponse>}
 */
class GetCompanyForUser extends StoreFetchableData {
    /**
     * Creates an instance of GetCompanyForUser
     *
     * @memberof GetCompanyForUser
     */
    constructor() {
        super('getCompanyForUser', '/message/GetCompanyForUser');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetCompanyForUser
     */
    *saga() {
        yield fork(listener$5b);
    }
}
// Export the state handling singleton
//
const getCompanyForUser = new GetCompanyForUser();
// Setup the Saga implementation
//
function* fetchCall$5b(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getCompanyForUser, 'fetchData'], action);
        yield put(getCompanyForUser.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getCompanyForUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5b() {
    yield takeEvery(getType(getCompanyForUser.actions.fetch), fetchCall$5b);
}

/**
 * Implements GetConfigurableProductDetailQuantityOnHand fetch for the Redux store
 *
 * @export
 * @class GetConfigurableProductDetailQuantityOnHand
 * @extends {StoreFetchableData<EVA.Core.GetQuantityOnHandForProducts, EVA.Core.GetQuantityOnHandForProductsResponse>}
 */
class GetConfigurableProductDetailQuantityOnHand extends StoreFetchableData {
    /**
     * Creates an instance of GetConfigurableProductDetailQuantityOnHand
     *
     * @memberof GetConfigurableProductDetailQuantityOnHand
     */
    constructor() {
        super('getConfigurableProductDetailQuantityOnHand', '/message/GetQuantityOnHandForProducts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetConfigurableProductDetailQuantityOnHand
     */
    *saga() {
        yield fork(listener$5a);
    }
}
// Export the state handling singleton
//
const getConfigurableProductDetailQuantityOnHand = new GetConfigurableProductDetailQuantityOnHand();
// Setup the Saga implementation
//
function* fetchCall$5a(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getConfigurableProductDetailQuantityOnHand, 'fetchData'], action);
        yield put(getConfigurableProductDetailQuantityOnHand.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getConfigurableProductDetailQuantityOnHand.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5a() {
    yield takeEvery(getType(getConfigurableProductDetailQuantityOnHand.actions.fetch), fetchCall$5a);
}

/**
 * Implements GetConfigurableProductSelectedProductDetail fetch for the Redux store
 *
 * @export
 * @class GetConfigurableProductSelectedProductDetail
 * @extends {StoreFetchableData<EVA.Core.GetProductDetail, EVA.Core.GetProductDetailResponse>}
 */
class GetConfigurableProductSelectedProductDetail extends StoreFetchableData {
    /**
     * Creates an instance of GetConfigurableProductSelectedProductDetail
     *
     * @memberof GetConfigurableProductSelectedProductDetail
     */
    constructor() {
        super('getConfigurableProductSelectedProductDetail', '/message/GetProductDetail');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetConfigurableProductSelectedProductDetail
     */
    *saga() {
        yield fork(listener$59);
    }
}
// Export the state handling singleton
//
const getConfigurableProductSelectedProductDetail = new GetConfigurableProductSelectedProductDetail();
// Setup the Saga implementation
//
function* fetchCall$59(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getConfigurableProductSelectedProductDetail, 'fetchData'], action);
        yield put(getConfigurableProductSelectedProductDetail.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getConfigurableProductSelectedProductDetail.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$59() {
    yield takeEvery(getType(getConfigurableProductSelectedProductDetail.actions.fetch), fetchCall$59);
}

/**
 * Implements GetConfigurableProductDetailProductAvailability fetch for the Redux store
 *
 * @export
 * @class GetConfigurableProductDetailProductAvailability
 * @extends {StoreFetchableData<EVA.Core.GetProductAvailability, EVA.Core.GetProductAvailabilityResponse>}
 */
class GetConfigurableProductDetailProductAvailability extends StoreFetchableData {
    /**
     * Creates an instance of GetConfigurableProductDetailProductAvailability
     *
     * @memberof GetConfigurableProductDetailProductAvailability
     */
    constructor() {
        super('getConfigurableProductDetailProductAvailability', '/message/GetProductAvailability');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetConfigurableProductDetailProductAvailability
     */
    *saga() {
        yield fork(listener$58);
    }
}
// Export the state handling singleton
//
const getConfigurableProductDetailProductAvailability = new GetConfigurableProductDetailProductAvailability();
// Setup the Saga implementation
//
function* fetchCall$58(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getConfigurableProductDetailProductAvailability, 'fetchData'], action);
        yield put(getConfigurableProductDetailProductAvailability.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getConfigurableProductDetailProductAvailability.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$58() {
    yield takeEvery(getType(getConfigurableProductDetailProductAvailability.actions.fetch), fetchCall$58);
}

/**
 * Implements GetConfigurableProductDetail fetch for the Redux store
 *
 * @export
 * @class GetConfigurableProductDetail
 * @extends {StoreFetchableData<EVA.Core.GetConfigurableProductDetail, EVA.Core.GetConfigurableProductDetailResponse, IGetConfigurableProductDetailState>}
 */
class GetConfigurableProductDetail extends StoreFetchableData {
    /**
     * Creates an instance of GetConfigurableProductDetail
     *
     * @memberof GetConfigurableProductDetail
     */
    constructor() {
        super('getConfigurableProductDetail', '/message/GetConfigurableProductDetail');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetConfigurableProductDetail
     */
    *saga() {
        yield fork(listener$57);
    }
    /**
     * Selects a different stock holding product
     *
     * @param {number} productId
     * @memberof GetConfigurableProductDetail
     */
    async setSelectedProduct(productId) {
        // Ensure the product is stock holding
        //
        const state = this.getState();
        const targetProduct = find(state.stockholdingProducts, { ProductID: productId });
        if (targetProduct) {
            const chainStarter = this.name;
            // Fetch selected product details
            //
            const getProductDetailRequest = {
                ID: productId,
            };
            log$1('[EVA-SDK-REDUX:getConfigurableProductDetail] Fetching details for selected product', getProductDetailRequest);
            const [getConfigurableProductSelectedProductDetailAction, , getConfigurableProductSelectedProductDetailChainPromise] = getConfigurableProductSelectedProductDetail.createFetchAction(getProductDetailRequest, chainStarter);
            store.dispatch(getConfigurableProductSelectedProductDetailAction);
            return await Promise.all([getConfigurableProductSelectedProductDetailChainPromise]);
        }
        else {
            throw {
                code: 404,
                message: 'Product not found',
                error: 'NOT_FOUND',
            };
        }
    }
    /**
     * Initial state with extended properties
     *
     * @type {IGetConfigurableProductDetailState}
     * @memberof GetConfigurableProductDetail
     */
    get initialState() {
        return {
            isFetching: false,
            request: null,
            response: null,
            error: null,
            chainStarter: this.name,
            levels: [],
            stockholdingProducts: [],
            selectedProductId: null,
        };
    }
    /**
     * The extended collection of reducer functions
     *
     * @protected
     * @returns {{ [ reducerName: string ]: ( state: any, action: AnyAction ) => any }}
     * @memberof GetConfigurableProductDetail
     */
    getReducerFunctions() {
        const baseReducerFuncs = super.getReducerFunctions();
        baseReducerFuncs.levels = (state = [], action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const currentState = this.getState();
                    const request = currentState.request;
                    const response = action.payload;
                    const levels = [];
                    const productId = get(request, 'ID');
                    this.findLevels(levels, response.Configurable, productId);
                    log$1(`[EVA-SDK-REDUX:${this.name}] findLevels result`, levels);
                    return levels;
                default:
                    return state;
            }
        };
        baseReducerFuncs.stockholdingProducts = (state = [], action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const currentState = this.getState();
                    currentState.request;
                    const response = action.payload;
                    const stockholdingProducts = [];
                    this.findStockholdingProducts(stockholdingProducts, response.Configurable);
                    log$1(`[EVA-SDK-REDUX:${this.name}] findStockholdingProducts result`, stockholdingProducts);
                    return stockholdingProducts;
                default:
                    return state;
            }
        };
        baseReducerFuncs.selectedProductId = (state = [], action) => {
            switch (action.type) {
                case getType(getConfigurableProductSelectedProductDetail.actions.response):
                    // Update if selected product details were fetched
                    //
                    const detailsReponse = action.payload;
                    return get(detailsReponse, 'Result.product_id', state);
                case getType(this.actions.response):
                    const currentState = this.getState();
                    const request = currentState.request;
                    const response = action.payload;
                    // Repeating the levels generation because I don't want to depend
                    // on the order of reducers completing
                    //
                    const levels = [];
                    const productId = get(request, 'ID');
                    this.findLevels(levels, response.Configurable, productId);
                    // Ensure a production selection has been made
                    //
                    const highestLevel = last(levels);
                    if (highestLevel) {
                        return highestLevel.selectedId;
                    }
                    else {
                        warn(`[EVA-SDK-REDUX:${this.name}] No highest level found`, levels);
                        return state;
                    }
                default:
                    return state;
            }
        };
        return baseReducerFuncs;
    }
    /**
     * Search the the nested tree of children if the product id we are looking for is in this subtree,
     * once found, return the found child
     *
     * @private
     * @param {number} productId The target product identifier
     * @param {EVA.Core.ConfigurableProductDto[]} children The collection of child products
     * @returns {(EVA.Core.ConfigurableProductDto| undefined)}
     * @memberof GetConfigurableProductDetail
     */
    findProduct(productId, children) {
        // logger.log( `[EVA-SDK-REDUX:${this.name}] findProduct`, productId, children );
        const foundChild = find(children, (child) => {
            const childChildren = get(child, 'Children');
            if (size(childChildren)) {
                return (child.ProductID === productId || this.findProduct(productId, child.Children)) !== undefined;
            }
            else {
                return child.ProductID === productId;
            }
        });
        return foundChild;
    }
    /**
     * Finds the configurable levels in the ConfigurableProductDto response
     * Recursively looks for the levels of child products
     *
     * @private
     * @param {IConfigurableLevel[]} levels The collection of levels (passed by reference)
     * @param {EVA.Core.ConfigurableProductDto} configuration The ConfigurableProductDto
     * @param {number} productId The current product identifier
     * @param {boolean} [disabled=false] Optional flag to disable a level
     * @returns {IConfigurableLevel[]} Final collection of levels
     * @memberof GetConfigurableProductDetail
     */
    findLevels(levels, configuration, productId, disabled = false) {
        // logger.log( `[EVA-SDK-REDUX:${this.name}] findLevels`, levels, configuration );
        const children = get(configuration, 'Children');
        if (!size(children)) {
            return levels;
        }
        // Check where the target productId is
        // There wil always be a firstChild at this point because children array
        // isn't empty
        //
        const selectedChild = this.findProduct(productId, children);
        const firstChild = first(children);
        // Build the level
        //
        const level = {
            name: configuration.ConfigurableProperty,
            selectedId: selectedChild ? selectedChild.ProductID : get(firstChild, 'ProductID'),
            disabled: disabled,
            options: children,
        };
        // Add the level
        //
        levels.push(level);
        if (selectedChild) {
            // Add child levels
            //
            this.findLevels(levels, selectedChild, productId);
        }
        else {
            // Add the other levels as disabled using first child
            //
            this.findLevels(levels, firstChild, productId, true);
        }
        return levels;
    }
    /**
     * Find stock holding products
     * Which means they are not configurable products (Type 16384)
     *
     * @private
     * @param {EVA.Core.ConfigurableProductDto[]} stockholdingProducts The collection of stockhold products (passed by reference)
     * @param {EVA.Core.ConfigurableProductDto} configuration The ConfigurableProductDto
     * @returns
     * @memberof GetConfigurableProductDetail
     */
    findStockholdingProducts(stockholdingProducts, configuration) {
        // logger.log( `[EVA-SDK-REDUX:${this.name}] findStockholdingProducts`, stockholdingProducts, configuration );
        const children = get(configuration, 'Children');
        if (!size(children)) {
            return stockholdingProducts;
        }
        each(children, (child) => {
            const childChildren = get(child, 'Children');
            if (size(childChildren) > 0) {
                this.findStockholdingProducts(stockholdingProducts, child);
            }
            else if (child && child.Type !== 16384) {
                stockholdingProducts.push(child);
            }
        });
        return stockholdingProducts;
    }
}
// Export the state handling singleton
//
const getConfigurableProductDetail = new GetConfigurableProductDetail();
// Setup the Saga implementation
//
function* fetchCall$57(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([getConfigurableProductDetail, 'fetchData'], action);
        yield put(getConfigurableProductDetail.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        const currentUserState = getCurrentUser.getState();
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Get the currently selected product from the state
        //
        const state = getConfigurableProductDetail.getState();
        const productId = state.selectedProductId;
        if (productId) {
            // Fetch selected product details
            //
            const getProductDetailRequest = {
                ID: productId,
            };
            log$1('[EVA-SDK-REDUX:getConfigurableProductDetail] Fetching details for selected configurable product', getProductDetailRequest);
            const [getConfigurableProductSelectedProductDetailAction, , getConfigurableProductSelectedProductDetailChainPromise] = getConfigurableProductSelectedProductDetail.createFetchAction(getProductDetailRequest, chainStarter, undefined, undefined, undefined, undefined, chainContext);
            yield put(getConfigurableProductSelectedProductDetailAction);
            // Fetch configurable product availability
            //
            let optionalGetProductAvailabilityChainPromise = Promise.resolve(undefined);
            if (productId) {
                const currentOrganizationId = get(currentUserState, 'response.User.CurrentOrganizationID');
                const availabilityOptions = {
                    Bundles: {
                        AvailabilityCalculationMethod: 1,
                        IncludeBundleInformation: true
                    },
                    Delivery: {
                        AvailabilityDate: true,
                    },
                    Pickup: {
                        AvailabilityDate: true,
                        OrganizationUnitIDs: [currentOrganizationId],
                    },
                    ProductAvailabilityDetails: {
                        OrganizationUnitIDs: [currentOrganizationId],
                        ProductIDs: map$1(state.stockholdingProducts, product => product.ProductID),
                    }
                };
                const getSelectProductProductAvailabilityRequest = {
                    Products: map$1(state.stockholdingProducts, (product) => {
                        return { ID: product.ProductID, QuantityRequested: 1 };
                    }),
                    Options: availabilityOptions,
                };
                log$1('[EVA-SDK-REDUX:getConfigurableProductDetail] Fetching product availability for configurable product', getSelectProductProductAvailabilityRequest);
                const [getConfigurableProductSelectedProductProductAvailabilityAction, , getConfigurableProductSelectedProductProductAvailabilityChainPromise] = getConfigurableProductDetailProductAvailability.createFetchAction(getSelectProductProductAvailabilityRequest, chainStarter, undefined, undefined, undefined, undefined, chainContext);
                yield put(getConfigurableProductSelectedProductProductAvailabilityAction);
                optionalGetProductAvailabilityChainPromise = getConfigurableProductSelectedProductProductAvailabilityChainPromise;
            }
            // Retrieve the quantity on hand of all the stock holding products
            // for the users current organization unit (store)
            //
            let optionalGetQuantityOnHandForProductsChainPromise = Promise.resolve(undefined);
            const organizationUnitId = get(currentUserState, 'response.User.CurrentOrganizationID');
            if (productId && organizationUnitId) {
                const getQuantityOnHandForProductsRequest = {
                    OrganizationUnitID: organizationUnitId,
                    ProductIDs: map$1(state.stockholdingProducts, (product) => product.ProductID),
                };
                log$1('[EVA-SDK-REDUX:getProductDetail] Fetching quantity on hand for configurable product details', getQuantityOnHandForProductsRequest);
                const [getQuantityOnHandForProductsAction, , getQuantityOnHandForProductsChainPromise] = getConfigurableProductDetailQuantityOnHand.createFetchAction(getQuantityOnHandForProductsRequest, chainStarter, undefined, undefined, undefined, undefined, chainContext);
                yield put(getQuantityOnHandForProductsAction);
                optionalGetQuantityOnHandForProductsChainPromise = getQuantityOnHandForProductsChainPromise;
            }
            // Resolve the chain
            //
            Promise.all([
                getConfigurableProductSelectedProductDetailChainPromise,
                optionalGetProductAvailabilityChainPromise,
                optionalGetQuantityOnHandForProductsChainPromise,
            ])
                .then((chainResponses) => {
                chainResponses.unshift(data);
                fetchActionPayload.chainResolve(chainResponses);
            })
                .catch(fetchActionPayload.chainReject);
        }
        else {
            fetchActionPayload.chainResolve([data, undefined, undefined, undefined]);
        }
    }
    catch (err) {
        yield put(getConfigurableProductDetail.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$57() {
    yield takeEvery(getType(getConfigurableProductDetail.actions.fetch), fetchCall$57);
}

/**
 * Implements GetConfigurationQrForDevice fetch for the Redux store
 *
 * @export
 * @class GetConfigurationQrForDevice
 * @extends {StoreFetchableData<EVA.Core.Management.GetConfigurationQrForDevice, EVA.Core.Management.GetConfigurationQrForDeviceResponse>}
 */
class GetConfigurationQrForDevice extends StoreFetchableData {
    /**
     * Creates an instance of GetConfigurationQrForDevice
     *
     * @memberof GetConfigurationQrForDevice
     */
    constructor() {
        super('getConfigurationQrForDevice', '/message/GetConfigurationQrForDevice');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetConfigurationQrForDevice
     */
    *saga() {
        yield fork(listener$56);
    }
}
// Export the state handling singleton
//
const getConfigurationQrForDevice = new GetConfigurationQrForDevice();
// Setup the Saga implementation
//
function* fetchCall$56(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getConfigurationQrForDevice, 'fetchData'], action);
        yield put(getConfigurationQrForDevice.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getConfigurationQrForDevice.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$56() {
    yield takeEvery(getType(getConfigurationQrForDevice.actions.fetch), fetchCall$56);
}

/**
 * Implements GetCurrentApplication fetch for the Redux store
 *
 * @export
 * @class GetCurrentApplication
 * @extends {StoreFetchableData<EVA.Core.GetCurrentApplication, EVA.Core.GetCurrentApplicationResponse>}
 */
class GetCurrentApplication extends StoreFetchableData {
    /**
     * Creates an instance of GetCurrentApplication
     *
     * @memberof GetCurrentApplication
     */
    constructor() {
        super('getCurrentApplication', '/message/GetCurrentApplication');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetCurrentApplication
     */
    *saga() {
        yield fork(listener$55);
    }
}
// Export the state handling singleton
//
const getCurrentApplication = new GetCurrentApplication();
// Setup the Saga implementation
//
function* fetchCall$55(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getCurrentApplication, 'fetchData'], action);
        yield put(getCurrentApplication.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getCurrentApplication.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$55() {
    yield takeEvery(getType(getCurrentApplication.actions.fetch), fetchCall$55);
}

/**
 * Implements GetCurrentCashJournals fetch for the Redux store
 *
 * @export
 * @class GetCurrentCashJournals
 * @extends {StoreFetchableData<EVA.Core.GetCurrentCashJournals, EVA.Core.GetCurrentCashJournalsResponse>}
 */
class GetCurrentCashJournals extends StoreFetchableData {
    /**
     * Creates an instance of GetCurrentCashJournals
     *
     * @memberof GetCurrentCashJournals
     */
    constructor() {
        super('getCurrentCashJournals', '/message/GetCurrentCashJournals');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetCurrentCashJournals
     */
    *saga() {
        yield fork(listener$54);
    }
}
// Export the state handling singleton
//
const getCurrentCashJournals = new GetCurrentCashJournals();
// Setup the Saga implementation
//
function* fetchCall$54(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getCurrentCashJournals, 'fetchData'], action);
        yield put(getCurrentCashJournals.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getCurrentCashJournals.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$54() {
    yield takeEvery(getType(getCurrentCashJournals.actions.fetch), fetchCall$54);
}

/**
 * Implements GetCurrentFinancialPeriodSummary fetch for the Redux store
 *
 * @export
 * @class GetCurrentFinancialPeriodSummary
 * @extends {StoreFetchableData<EVA.Core.GetCurrentFinancialPeriodSummary, EVA.Core.GetCurrentFinancialPeriodSummaryResponse>}
 */
class GetCurrentFinancialPeriodSummary extends StoreFetchableData {
    /**
     * Creates an instance of GetCurrentFinancialPeriodSummary
     *
     * @memberof GetCurrentFinancialPeriodSummary
     */
    constructor() {
        super('getCurrentFinancialPeriodSummary', '/message/GetCurrentFinancialPeriodSummary');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetCurrentFinancialPeriodSummary
     */
    *saga() {
        yield fork(listener$53);
    }
}
// Export the state handling singleton
//
const getCurrentFinancialPeriodSummary = new GetCurrentFinancialPeriodSummary();
// Setup the Saga implementation
//
function* fetchCall$53(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getCurrentFinancialPeriodSummary, 'fetchData'], action);
        yield put(getCurrentFinancialPeriodSummary.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getCurrentFinancialPeriodSummary.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$53() {
    yield takeEvery(getType(getCurrentFinancialPeriodSummary.actions.fetch), fetchCall$53);
}

/**
 * Implements GetCurrentInitialCycleCount fetch for the Redux store
 *
 * @export
 * @class GetCurrentInitialCycleCount
 * @extends {StoreFetchableData<EVA.Core.GetCurrentInitialCycleCount, EVA.Core.GetCurrentInitialCycleCountResponse>}
 */
class GetCurrentInitialCycleCount extends StoreFetchableData {
    /**
     * Creates an instance of GetCurrentInitialCycleCount
     *
     * @memberof GetCurrentInitialCycleCount
     */
    constructor() {
        super('getCurrentInitialCycleCount', '/message/GetCurrentInitialCycleCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetCurrentInitialCycleCount
     */
    *saga() {
        yield fork(listener$52);
    }
}
// Export the state handling singleton
//
const getCurrentInitialCycleCount = new GetCurrentInitialCycleCount();
// Setup the Saga implementation
//
function* fetchCall$52(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getCurrentInitialCycleCount, 'fetchData'], action);
        yield put(getCurrentInitialCycleCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getCurrentInitialCycleCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$52() {
    yield takeEvery(getType(getCurrentInitialCycleCount.actions.fetch), fetchCall$52);
}

/**
 * Implements GetCurrentUserIdentificationInfo fetch for the Redux store
 *
 * @export
 * @class GetCurrentUserIdentificationInfo
 * @extends {StoreFetchableData<EVA.Core.GetCurrentUserIdentificationInfo, EVA.Core.GetCurrentUserIdentificationInfoResponse>}
 */
class GetCurrentUserIdentificationInfo extends StoreFetchableData {
    /**
     * Creates an instance of GetCurrentUserIdentificationInfo
     *
     * @memberof GetCurrentUserIdentificationInfo
     */
    constructor() {
        super('getCurrentUserIdentificationInfo', '/message/GetCurrentUserIdentificationInfo');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetCurrentUserIdentificationInfo
     */
    *saga() {
        yield fork(listener$51);
    }
}
// Export the state handling singleton
//
const getCurrentUserIdentificationInfo = new GetCurrentUserIdentificationInfo();
// Setup the Saga implementation
//
function* fetchCall$51(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getCurrentUserIdentificationInfo, 'fetchData'], action);
        yield put(getCurrentUserIdentificationInfo.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getCurrentUserIdentificationInfo.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$51() {
    yield takeEvery(getType(getCurrentUserIdentificationInfo.actions.fetch), fetchCall$51);
}

/**
 * Implements GetCustomFieldTypes fetch for the Redux store
 *
 * @export
 * @class GetCustomFieldTypes
 * @extends {StoreFetchableData<EVA.Core.Management.GetCustomFieldTypes, EVA.Core.Management.GetCustomFieldTypesResponse>}
 */
class GetCustomFieldTypes extends StoreFetchableData {
    /**
     * Creates an instance of GetCustomFieldTypes
     *
     * @memberof GetCustomFieldTypes
     */
    constructor() {
        super('getCustomFieldTypes', '/message/GetCustomFieldTypes');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetCustomFieldTypes
     */
    *saga() {
        yield fork(listener$50);
    }
}
// Export the state handling singleton
//
const getCustomFieldTypes = new GetCustomFieldTypes();
// Setup the Saga implementation
//
function* fetchCall$50(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getCustomFieldTypes, 'fetchData'], action);
        yield put(getCustomFieldTypes.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getCustomFieldTypes.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$50() {
    yield takeEvery(getType(getCustomFieldTypes.actions.fetch), fetchCall$50);
}

/**
 * Implements GetCustomerReturnReasons fetch for the Redux store
 *
 * @export
 * @class GetCustomerReturnReasons
 * @extends {StoreFetchableData<EVA.Core.Management.GetReturnReasons, EVA.Core.Management.GetReturnReasonsResponse>}
 */
class GetCustomerReturnReasons extends StoreFetchableData {
    /**
     * Creates an instance of GetCustomerReturnReasons
     *
     * @memberof GetCustomerReturnReasons
     */
    constructor() {
        super('getCustomerReturnReasons', '/message/GetReturnReasons');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetCustomerReturnReasons
     */
    *saga() {
        yield fork(listener$4$);
    }
}
// Export the state handling singleton
//
const getCustomerReturnReasons = new GetCustomerReturnReasons();
// Setup the Saga implementation
//
function* fetchCall$4$(action) {
    const fetchActionPayload = action.payload;
    try {
        // Amend the request with the customer type
        //
        const requestPayload = Object.assign(Object.assign({}, action.request), { Type: 1 });
        action.request = requestPayload;
        const data = yield call([getCustomerReturnReasons, 'fetchData'], action);
        yield put(getCustomerReturnReasons.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getCustomerReturnReasons.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4$() {
    yield takeEvery(getType(getCustomerReturnReasons.actions.fetch), fetchCall$4$);
}

/**
 * Implements GetCycleCountOriginTypes fetch for the Redux store
 *
 * @export
 * @class GetCycleCountOriginTypes
 * @extends {StoreFetchableData<EVA.Core.GetCycleCountOriginTypes, EVA.Core.GetCycleCountOriginTypesResponse>}
 */
class GetCycleCountOriginTypes extends StoreFetchableData {
    /**
     * Creates an instance of GetCycleCountOriginTypes
     *
     * @memberof GetCycleCountOriginTypes
     */
    constructor() {
        super('getCycleCountOriginTypes', '/message/GetCycleCountOriginTypes');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetCycleCountOriginTypes
     */
    *saga() {
        yield fork(listener$4_);
    }
}
// Export the state handling singleton
//
const getCycleCountOriginTypes = new GetCycleCountOriginTypes();
// Setup the Saga implementation
//
function* fetchCall$4_(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getCycleCountOriginTypes, 'fetchData'], action);
        yield put(getCycleCountOriginTypes.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getCycleCountOriginTypes.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4_() {
    yield takeEvery(getType(getCycleCountOriginTypes.actions.fetch), fetchCall$4_);
}

/**
 * Implements GetDeliveryOrderData fetch for the Redux store
 *
 * @export
 * @class GetDeliveryOrderData
 * @extends {StoreFetchableData<EVA.Core.GetDeliveryOrderData, EVA.Core.GetDeliveryOrderDataResponse>}
 */
class GetDeliveryOrderData extends StoreFetchableData {
    /**
     * Creates an instance of GetDeliveryOrderData
     *
     * @memberof GetDeliveryOrderData
     */
    constructor() {
        super('getDeliveryOrderData', '/message/GetDeliveryOrderData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetDeliveryOrderData
     */
    *saga() {
        yield fork(listener$4Z);
    }
}
// Export the state handling singleton
//
const getDeliveryOrderData = new GetDeliveryOrderData();
// Setup the Saga implementation
//
function* fetchCall$4Z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getDeliveryOrderData, 'fetchData'], action);
        yield put(getDeliveryOrderData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getDeliveryOrderData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4Z() {
    yield takeEvery(getType(getDeliveryOrderData.actions.fetch), fetchCall$4Z);
}

/**
 * Implements GetDeviceTypes fetch for the Redux store
 *
 * @export
 * @class GetDeviceTypes
 * @extends {StoreFetchableData<EVA.Core.GetDeviceTypes, EVA.Core.GetDeviceTypesResponse>}
 */
class GetDeviceTypes extends StoreFetchableData {
    /**
     * Creates an instance of GetDeviceTypes
     *
     * @memberof GetDeviceTypes
     */
    constructor() {
        super('getDeviceTypes', '/message/GetDeviceTypes');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetDeviceTypes
     */
    *saga() {
        yield fork(listener$4Y);
    }
}
// Export the state handling singleton
//
const getDeviceTypes = new GetDeviceTypes();
// Setup the Saga implementation
//
function* fetchCall$4Y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getDeviceTypes, 'fetchData'], action);
        yield put(getDeviceTypes.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getDeviceTypes.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4Y() {
    yield takeEvery(getType(getDeviceTypes.actions.fetch), fetchCall$4Y);
}

/**
 * Implements GetDigitalGiftCardOptions fetch for the Redux store
 *
 * @export
 * @class GetDigitalGiftCardOptions
 * @extends {StoreFetchableData<EVA.Core.GetDigitalGiftCardOptions, EVA.Core.GetDigitalGiftCardOptionsResponse>}
 */
class GetDigitalGiftCardOptions extends StoreFetchableData {
    /**
     * Creates an instance of GetDigitalGiftCardOptions
     *
     * @memberof GetDigitalGiftCardOptions
     */
    constructor() {
        super('getDigitalGiftCardOptions', '/message/GetDigitalGiftCardOptions');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetDigitalGiftCardOptions
     */
    *saga() {
        yield fork(listener$4X);
    }
}
// Export the state handling singleton
//
const getDigitalGiftCardOptions = new GetDigitalGiftCardOptions();
// Setup the Saga implementation
//
function* fetchCall$4X(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getDigitalGiftCardOptions, 'fetchData'], action);
        yield put(getDigitalGiftCardOptions.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getDigitalGiftCardOptions.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4X() {
    yield takeEvery(getType(getDigitalGiftCardOptions.actions.fetch), fetchCall$4X);
}

/**
 * Implements GetDiscountByID fetch for the Redux store
 *
 * @export
 * @class GetDiscountByID
 * @extends {StoreFetchableData<EVA.Core.Management.GetDiscountByID, EVA.Core.Management.GetDiscountByIDResponse>}
 */
class GetDiscountByID extends StoreFetchableData {
    /**
     * Creates an instance of GetDiscountByID
     *
     * @memberof GetDiscountByID
     */
    constructor() {
        super('getDiscountById', '/message/GetDiscountByID');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetDiscountByID
     */
    *saga() {
        yield fork(listener$4W);
    }
}
// Export the state handling singleton
//
const getDiscountById = new GetDiscountByID();
// Setup the Saga implementation
//
function* fetchCall$4W(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getDiscountById, 'fetchData'], action);
        yield put(getDiscountById.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getDiscountById.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4W() {
    yield takeEvery(getType(getDiscountById.actions.fetch), fetchCall$4W);
}

/**
 * Implements GetDiscountProductOptionsForOrder fetch for the Redux store
 *
 * @export
 * @class GetDiscountProductOptionsForOrder
 * @extends {StoreFetchableData<EVA.Core.GetDiscountProductOptionsForOrder, EVA.Core.GetDiscountProductOptionsForOrderResponse>}
 */
class GetDiscountProductOptionsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetDiscountProductOptionsForOrder
     *
     * @memberof GetDiscountProductOptionsForOrder
     */
    constructor() {
        super('getDiscountProductOptionsForOrder', '/message/GetDiscountProductOptionsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetDiscountProductOptionsForOrder
     */
    *saga() {
        yield fork(listener$4V);
    }
}
// Export the state handling singleton
//
const getDiscountProductOptionsForOrder = new GetDiscountProductOptionsForOrder();
// Setup the Saga implementation
//
function* fetchCall$4V(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getDiscountProductOptionsForOrder, 'fetchData'], action);
        yield put(getDiscountProductOptionsForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getDiscountProductOptionsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4V() {
    yield takeEvery(getType(getDiscountProductOptionsForOrder.actions.fetch), fetchCall$4V);
}

/**
 * Implements GetDiscountProductOptionsForOrderLine fetch for the Redux store
 *
 * @export
 * @class GetDiscountProductOptionsForOrderLine
 * @extends {StoreFetchableData<EVA.Core.GetDiscountProductOptionsForOrderLine, EVA.Core.GetDiscountProductOptionsForOrderLineResponse>}
 */
class GetDiscountProductOptionsForOrderLine extends StoreFetchableData {
    /**
     * Creates an instance of GetDiscountProductOptionsForOrderLine
     *
     * @memberof GetDiscountProductOptionsForOrderLine
     */
    constructor() {
        super('getDiscountProductOptionsForOrderLine', '/message/GetDiscountProductOptionsForOrderLine');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetDiscountProductOptionsForOrderLine
     */
    *saga() {
        yield fork(listener$4U);
    }
}
// Export the state handling singleton
//
const getDiscountProductOptionsForOrderLine = new GetDiscountProductOptionsForOrderLine();
// Setup the Saga implementation
//
function* fetchCall$4U(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getDiscountProductOptionsForOrderLine, 'fetchData'], action);
        yield put(getDiscountProductOptionsForOrderLine.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getDiscountProductOptionsForOrderLine.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4U() {
    yield takeEvery(getType(getDiscountProductOptionsForOrderLine.actions.fetch), fetchCall$4U);
}

/**
 * Implements GetElectronicInvoiceData fetch for the Redux store
 *
 * @export
 * @class GetElectronicInvoiceData
 * @extends {StoreFetchableData<EVA.Core.GetElectronicInvoiceData, EVA.Core.GetElectronicInvoiceDataResponse>}
 */
class GetElectronicInvoiceData extends StoreFetchableData {
    /**
     * Creates an instance of GetElectronicInvoiceData
     *
     * @memberof GetElectronicInvoiceData
     */
    constructor() {
        super('getElectronicInvoiceData', '/message/GetElectronicInvoiceData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetElectronicInvoiceData
     */
    *saga() {
        yield fork(listener$4T);
    }
}
// Export the state handling singleton
//
const getElectronicInvoiceData = new GetElectronicInvoiceData();
// Setup the Saga implementation
//
function* fetchCall$4T(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getElectronicInvoiceData, 'fetchData'], action);
        yield put(getElectronicInvoiceData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getElectronicInvoiceData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4T() {
    yield takeEvery(getType(getElectronicInvoiceData.actions.fetch), fetchCall$4T);
}

/**
 * Implements GetEmailAddressDomainSuggestions fetch for the Redux store
 *
 * @export
 * @class GetEmailAddressDomainSuggestions
 * @extends {StoreFetchableData<EVA.Core.GetEmailAddressDomainSuggestions, EVA.Core.GetEmailAddressDomainSuggestionsResponse>}
 */
class GetEmailAddressDomainSuggestions extends StoreFetchableData {
    /**
     * Creates an instance of GetEmailAddressDomainSuggestions
     *
     * @memberof GetEmailAddressDomainSuggestions
     */
    constructor() {
        super('getEmailAddressDomainSuggestions', '/message/GetEmailAddressDomainSuggestions');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetEmailAddressDomainSuggestions
     */
    *saga() {
        yield fork(listener$4S);
    }
}
// Export the state handling singleton
//
const getEmailAddressDomainSuggestions = new GetEmailAddressDomainSuggestions();
// Setup the Saga implementation
//
function* fetchCall$4S(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getEmailAddressDomainSuggestions, 'fetchData'], action);
        yield put(getEmailAddressDomainSuggestions.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getEmailAddressDomainSuggestions.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4S() {
    yield takeEvery(getType(getEmailAddressDomainSuggestions.actions.fetch), fetchCall$4S);
}

/**
 * Implements GetEnumValues fetch for the Redux store
 *
 * @export
 * @class GetEnumValues
 * @extends {StoreFetchableData<EVA.Core.GetEnumValues, EVA.Core.GetEnumValuesResponse>}
 */
class GetEnumValues extends StoreFetchableData {
    /**
     * Creates an instance of GetEnumValues
     *
     * @memberof GetEnumValues
     */
    constructor() {
        super('getEnumValues', '/message/GetEnumValues');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetEnumValues
     */
    *saga() {
        yield fork(listener$4R);
    }
}
// Export the state handling singleton
//
const getEnumValues = new GetEnumValues();
// Setup the Saga implementation
//
function* fetchCall$4R(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getEnumValues, 'fetchData'], action);
        yield put(getEnumValues.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getEnumValues.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4R() {
    yield takeEvery(getType(getEnumValues.actions.fetch), fetchCall$4R);
}

/**
 * Implements GetFinancialPeriodClosingImpediments fetch for the Redux store
 *
 * @export
 * @class GetFinancialPeriodClosingImpediments
 * @extends {StoreFetchableData<EVA.Core.GetFinancialPeriodClosingImpediments, EVA.Core.GetFinancialPeriodClosingImpedimentsResponse>}
 */
class GetFinancialPeriodClosingImpediments extends StoreFetchableData {
    /**
     * Creates an instance of GetFinancialPeriodClosingImpediments
     *
     * @memberof GetFinancialPeriodClosingImpediments
     */
    constructor() {
        super('getFinancialPeriodClosingImpediments', '/message/GetFinancialPeriodClosingImpediments');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetFinancialPeriodClosingImpediments
     */
    *saga() {
        yield fork(listener$4Q);
    }
}
// Export the state handling singleton
//
const getFinancialPeriodClosingImpediments = new GetFinancialPeriodClosingImpediments();
// Setup the Saga implementation
//
function* fetchCall$4Q(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getFinancialPeriodClosingImpediments, 'fetchData'], action);
        yield put(getFinancialPeriodClosingImpediments.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getFinancialPeriodClosingImpediments.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4Q() {
    yield takeEvery(getType(getFinancialPeriodClosingImpediments.actions.fetch), fetchCall$4Q);
}

/**
 * Implements GetFinancialPeriodDetails fetch for the Redux store
 *
 * @export
 * @class GetFinancialPeriodDetails
 * @extends {StoreFetchableData<EVA.Core.GetFinancialPeriodDetails, EVA.Core.GetFinancialPeriodDetailsResponse>}
 */
class GetFinancialPeriodDetails extends StoreFetchableData {
    /**
     * Creates an instance of GetFinancialPeriodDetails
     *
     * @memberof GetFinancialPeriodDetails
     */
    constructor() {
        super('getFinancialPeriodDetails', '/message/GetFinancialPeriodDetails');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetFinancialPeriodDetails
     */
    *saga() {
        yield fork(listener$4P);
    }
}
// Export the state handling singleton
//
const getFinancialPeriodDetails = new GetFinancialPeriodDetails();
// Setup the Saga implementation
//
function* fetchCall$4P(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getFinancialPeriodDetails, 'fetchData'], action);
        yield put(getFinancialPeriodDetails.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getFinancialPeriodDetails.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4P() {
    yield takeEvery(getType(getFinancialPeriodDetails.actions.fetch), fetchCall$4P);
}

/**
 * Implements GetFinancialPeriodInformation fetch for the Redux store
 *
 * @export
 * @class GetFinancialPeriodInformation
 * @extends {StoreFetchableData<EVA.Core.GetFinancialPeriodInformation, EVA.Core.GetFinancialPeriodInformationResponse>}
 */
class GetFinancialPeriodInformation extends StoreFetchableData {
    /**
     * Creates an instance of GetFinancialPeriodInformation
     *
     * @memberof GetFinancialPeriodInformation
     */
    constructor() {
        super('getFinancialPeriodInformation', '/message/GetFinancialPeriodInformation');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetFinancialPeriodInformation
     */
    *saga() {
        yield fork(listener$4O);
    }
}
// Export the state handling singleton
//
const getFinancialPeriodInformation = new GetFinancialPeriodInformation();
// Setup the Saga implementation
//
function* fetchCall$4O(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getFinancialPeriodInformation, 'fetchData'], action);
        yield put(getFinancialPeriodInformation.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getFinancialPeriodInformation.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4O() {
    yield takeEvery(getType(getFinancialPeriodInformation.actions.fetch), fetchCall$4O);
}

/**
 * Implements GetFiscalOrderData fetch for the Redux store
 *
 * @export
 * @class GetFiscalOrderData
 * @extends {StoreFetchableData<EVA.Core.GetFiscalOrderData, EVA.Core.GetFiscalOrderDataResponse>}
 */
class GetFiscalOrderData extends StoreFetchableData {
    /**
     * Creates an instance of GetFiscalOrderData
     *
     * @memberof GetFiscalOrderData
     */
    constructor() {
        super('getFiscalOrderData', '/message/GetFiscalOrderData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetFiscalOrderData
     */
    *saga() {
        yield fork(listener$4N);
    }
}
// Export the state handling singleton
//
const getFiscalOrderData = new GetFiscalOrderData();
// Setup the Saga implementation
//
function* fetchCall$4N(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getFiscalOrderData, 'fetchData'], action);
        yield put(getFiscalOrderData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getFiscalOrderData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4N() {
    yield takeEvery(getType(getFiscalOrderData.actions.fetch), fetchCall$4N);
}

/**
 * Implements GetFullStockCount fetch for the Redux store
 *
 * @export
 * @class GetFullStockCount
 * @extends {StoreFetchableData<EVA.Core.GetFullStockCount, EVA.Core.GetFullStockCountResponse>}
 */
class GetFullStockCount extends StoreFetchableData {
    /**
     * Creates an instance of GetFullStockCount
     *
     * @memberof GetFullStockCount
     */
    constructor() {
        super('getFullStockCount', '/message/GetFullStockCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetFullStockCount
     */
    *saga() {
        yield fork(listener$4M);
    }
}
// Export the state handling singleton
//
const getFullStockCount = new GetFullStockCount();
// Setup the Saga implementation
//
function* fetchCall$4M(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getFullStockCount, 'fetchData'], action);
        yield put(getFullStockCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getFullStockCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4M() {
    yield takeEvery(getType(getFullStockCount.actions.fetch), fetchCall$4M);
}

/**
 * Implements GetGiftCardOptions fetch for the Redux store
 *
 * @export
 * @class GetGiftCardOptions
 * @extends {StoreFetchableData<EVA.Core.GetGiftCardOptions, EVA.Core.GetGiftCardOptionsResponse>}
 */
class GetGiftCardOptions extends StoreFetchableData {
    /**
     * Creates an instance of GetGiftCardOptions
     *
     * @memberof GetGiftCardOptions
     */
    constructor() {
        super('getGiftCardOptions', '/message/GetGiftCardOptions');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetGiftCardOptions
     */
    *saga() {
        yield fork(listener$4L);
    }
}
// Export the state handling singleton
//
const getGiftCardOptions = new GetGiftCardOptions();
// Setup the Saga implementation
//
function* fetchCall$4L(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getGiftCardOptions, 'fetchData'], action);
        yield put(getGiftCardOptions.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getGiftCardOptions.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4L() {
    yield takeEvery(getType(getGiftCardOptions.actions.fetch), fetchCall$4L);
}

/**
 * Implements GetGiftWrappingOptionsForOrder fetch for the Redux store
 *
 * @export
 * @class GetGiftWrappingOptionsForOrder
 * @extends {StoreFetchableData<EVA.Core.GetGiftWrappingOptionsForOrder, EVA.Core.GetGiftWrappingOptionsForOrderResponse>}
 */
class GetGiftWrappingOptionsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetGiftWrappingOptionsForOrder
     *
     * @memberof GetGiftWrappingOptionsForOrder
     */
    constructor() {
        super('getGiftWrappingOptionsForOrder', '/message/GetGiftWrappingOptionsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetGiftWrappingOptionsForOrder
     */
    *saga() {
        yield fork(listener$4K);
    }
}
// Export the state handling singleton
//
const getGiftWrappingOptionsForOrder = new GetGiftWrappingOptionsForOrder();
// Setup the Saga implementation
//
function* fetchCall$4K(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getGiftWrappingOptionsForOrder, 'fetchData'], action);
        yield put(getGiftWrappingOptionsForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getGiftWrappingOptionsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4K() {
    yield takeEvery(getType(getGiftWrappingOptionsForOrder.actions.fetch), fetchCall$4K);
}

/**
 * Implements GetGreetingCards fetch for the Redux store
 *
 * @export
 * @class GetGreetingCards
 * @extends {StoreFetchableData<EVA.Core.GetGreetingCards, EVA.Core.GetGreetingCardsResponse>}
 */
class GetGreetingCards extends StoreFetchableData {
    /**
     * Creates an instance of GetGreetingCards
     *
     * @memberof GetGreetingCards
     */
    constructor() {
        super('getGreetingCards', '/message/GetGreetingCards');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetGreetingCards
     */
    *saga() {
        yield fork(listener$4J);
    }
}
// Export the state handling singleton
//
const getGreetingCards = new GetGreetingCards();
// Setup the Saga implementation
//
function* fetchCall$4J(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getGreetingCards, 'fetchData'], action);
        yield put(getGreetingCards.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getGreetingCards.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4J() {
    yield takeEvery(getType(getGreetingCards.actions.fetch), fetchCall$4J);
}

/**
 * Implements GetInitialCycleCountLabel fetch for the Redux store
 *
 * @export
 * @class GetInitialCycleCountLabel
 * @extends {StoreFetchableData<EVA.Core.GetInitialCycleCountLabel, EVA.Core.GetInitialCycleCountLabelResponse>}
 */
class GetInitialCycleCountLabel extends StoreFetchableData {
    /**
     * Creates an instance of GetInitialCycleCountLabel
     *
     * @memberof GetInitialCycleCountLabel
     */
    constructor() {
        super('getInitialCycleCountLabel', '/message/GetInitialCycleCountLabel');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetInitialCycleCountLabel
     */
    *saga() {
        yield fork(listener$4I);
    }
}
// Export the state handling singleton
//
const getInitialCycleCountLabel = new GetInitialCycleCountLabel();
// Setup the Saga implementation
//
function* fetchCall$4I(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getInitialCycleCountLabel, 'fetchData'], action);
        yield put(getInitialCycleCountLabel.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getInitialCycleCountLabel.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4I() {
    yield takeEvery(getType(getInitialCycleCountLabel.actions.fetch), fetchCall$4I);
}

/**
 * Implements GetInquiry fetch for the Redux store
 *
 * @export
 * @class GetInquiry
 * @extends {StoreFetchableData<EVA.Core.GetInquiry, EVA.Core.GetInquiryResponse>}
 */
class GetInquiry extends StoreFetchableData {
    /**
     * Creates an instance of GetInquiry
     *
     * @memberof GetInquiry
     */
    constructor() {
        super('getInquiry', '/message/GetInquiry');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetInquiry
     */
    *saga() {
        yield fork(listener$4H);
    }
}
// Export the state handling singleton
//
const getInquiry = new GetInquiry();
// Setup the Saga implementation
//
function* fetchCall$4H(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getInquiry, 'fetchData'], action);
        yield put(getInquiry.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getInquiry.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4H() {
    yield takeEvery(getType(getInquiry.actions.fetch), fetchCall$4H);
}

/**
 * Implements GetLotteryNumber fetch for the Redux store
 *
 * @export
 * @class GetLotteryNumber
 * @extends {StoreFetchableData<EVA.Core.GetLotteryNumber, EVA.Core.GetLotteryNumberResponse>}
 */
class GetLotteryNumber extends StoreFetchableData {
    /**
     * Creates an instance of GetLotteryNumber
     *
     * @memberof GetLotteryNumber
     */
    constructor() {
        super('getLotteryNumber', '/message/GetLotteryNumber');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetLotteryNumber
     */
    *saga() {
        yield fork(listener$4G);
    }
}
// Export the state handling singleton
//
const getLotteryNumber = new GetLotteryNumber();
// Setup the Saga implementation
//
function* fetchCall$4G(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getLotteryNumber, 'fetchData'], action);
        yield put(getLotteryNumber.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getLotteryNumber.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4G() {
    yield takeEvery(getType(getLotteryNumber.actions.fetch), fetchCall$4G);
}

/**
 * Implements GetOpenCashDrawerReasons fetch for the Redux store
 *
 * @export
 * @class GetOpenCashDrawerReasons
 * @extends {StoreFetchableData<EVA.Core.GetOpenCashDrawerReasons, EVA.Core.GetOpenCashDrawerReasonsResponse>}
 */
class GetOpenCashDrawerReasons extends StoreFetchableData {
    /**
     * Creates an instance of GetOpenCashDrawerReasons
     *
     * @memberof GetOpenCashDrawerReasons
     */
    constructor() {
        super('getOpenCashDrawerReasons', '/message/GetOpenCashDrawerReasons');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetOpenCashDrawerReasons
     */
    *saga() {
        yield fork(listener$4F);
    }
}
// Export the state handling singleton
//
const getOpenCashDrawerReasons = new GetOpenCashDrawerReasons();
// Setup the Saga implementation
//
function* fetchCall$4F(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getOpenCashDrawerReasons, 'fetchData'], action);
        yield put(getOpenCashDrawerReasons.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getOpenCashDrawerReasons.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4F() {
    yield takeEvery(getType(getOpenCashDrawerReasons.actions.fetch), fetchCall$4F);
}

/**
 * Implements GetOrderDiscountInfo fetch for the Redux store
 *
 * @export
 * @class GetOrderDiscountInfo
 * @extends {StoreFetchableData<EVA.Core.GetOrderDiscountInfo, EVA.Core.GetOrderDiscountInfoResponse>}
 */
class GetOrderDiscountInfo extends StoreFetchableData {
    /**
     * Creates an instance of GetOrderDiscountInfo
     *
     * @memberof GetOrderDiscountInfo
     */
    constructor() {
        super('getOrderDiscountInfo', '/message/GetOrderDiscountInfo');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetOrderDiscountInfo
     */
    *saga() {
        yield fork(listener$4E);
    }
}
// Export the state handling singleton
//
const getOrderDiscountInfo = new GetOrderDiscountInfo();
// Setup the Saga implementation
//
function* fetchCall$4E(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getOrderDiscountInfo, 'fetchData'], action);
        yield put(getOrderDiscountInfo.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getOrderDiscountInfo.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4E() {
    yield takeEvery(getType(getOrderDiscountInfo.actions.fetch), fetchCall$4E);
}

/**
 * Implements GetOrderFulfillmentOptions fetch for the Redux store
 *
 * @export
 * @class GetOrderFulfillmentOptions
 * @extends {StoreFetchableData<EVA.Core.GetOrderFulfillmentOptions, EVA.Core.GetOrderFulfillmentOptionsResponse>}
 */
class GetOrderFulfillmentOptions extends StoreFetchableData {
    /**
     * Creates an instance of GetOrderFulfillmentOptions
     *
     * @memberof GetOrderFulfillmentOptions
     */
    constructor() {
        super('getOrderFulfillmentOptions', '/message/GetOrderFulfillmentOptions');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetOrderFulfillmentOptions
     */
    *saga() {
        yield fork(listener$4D);
    }
}
// Export the state handling singleton
//
const getOrderFulfillmentOptions = new GetOrderFulfillmentOptions();
// Setup the Saga implementation
//
function* fetchCall$4D(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getOrderFulfillmentOptions, 'fetchData'], action);
        yield put(getOrderFulfillmentOptions.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getOrderFulfillmentOptions.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4D() {
    yield takeEvery(getType(getOrderFulfillmentOptions.actions.fetch), fetchCall$4D);
}

/**
 * Implements GetOrderSignature fetch for the Redux store
 *
 * @export
 * @class GetOrderSignature
 * @extends {StoreFetchableData<EVA.DocumentSigning.GetOrderSignature, EVA.Core.ResourceResponseMessage>}
 */
class GetOrderSignature extends StoreFetchableData {
    /**
     * Creates an instance of GetOrderSignature
     *
     * @memberof GetOrderSignature
     */
    constructor() {
        super('getOrderSignature', '/message/GetOrderSignature');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetOrderSignature
     */
    *saga() {
        yield fork(listener$4C);
    }
}
// Export the state handling singleton
//
const getOrderSignature = new GetOrderSignature();
// Setup the Saga implementation
//
function* fetchCall$4C(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getOrderSignature, 'fetchData'], action);
        yield put(getOrderSignature.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getOrderSignature.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4C() {
    yield takeEvery(getType(getOrderSignature.actions.fetch), fetchCall$4C);
}

/**
 * Implements GetOrderStatus fetch for the Redux store
 *
 * @export
 * @class GetOrderStatus
 * @extends {StoreFetchableData<EVA.Waldo.GetOrderStatus, EVA.Waldo.GetOrderStatusResponse>}
 */
class GetOrderStatus extends StoreFetchableData {
    /**
     * Creates an instance of GetOrderStatus
     *
     * @memberof GetOrderStatus
     */
    constructor() {
        super('getOrderStatus', '/message/GetOrderStatus');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetOrderStatus
     */
    *saga() {
        yield fork(listener$4B);
    }
}
// Export the state handling singleton
//
const getOrderStatus = new GetOrderStatus();
// Setup the Saga implementation
//
function* fetchCall$4B(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getOrderStatus, 'fetchData'], action);
        yield put(getOrderStatus.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getOrderStatus.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4B() {
    yield takeEvery(getType(getOrderStatus.actions.fetch), fetchCall$4B);
}

/**
 * Implements GetOrderStatusForOrders fetch for the Redux store
 *
 * @export
 * @class GetOrderStatusForOrders
 * @extends {StoreFetchableData<EVA.Waldo.GetOrderStatusForOrders, EVA.Waldo.GetOrderStatusForOrdersResponse>}
 */
class GetOrderStatusForOrders extends StoreFetchableData {
    /**
     * Creates an instance of GetOrderStatusForOrders
     *
     * @memberof GetOrderStatusForOrders
     */
    constructor() {
        super('getOrderStatusForOrders', '/message/GetOrderStatusForOrders');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetOrderStatusForOrders
     */
    *saga() {
        yield fork(listener$4A);
    }
}
// Export the state handling singleton
//
const getOrderStatusForOrders = new GetOrderStatusForOrders();
// Setup the Saga implementation
//
function* fetchCall$4A(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getOrderStatusForOrders, 'fetchData'], action);
        yield put(getOrderStatusForOrders.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getOrderStatusForOrders.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4A() {
    yield takeEvery(getType(getOrderStatusForOrders.actions.fetch), fetchCall$4A);
}

/**
 * Implements GetOrderStatusHistory fetch for the Redux store
 *
 * @export
 * @class GetOrderStatusHistory
 * @extends {StoreFetchableData<EVA.Waldo.GetOrderStatusHistory, EVA.Waldo.GetOrderStatusHistoryResponse>}
 */
class GetOrderStatusHistory extends StoreFetchableData {
    /**
     * Creates an instance of GetOrderStatusHistory
     *
     * @memberof GetOrderStatusHistory
     */
    constructor() {
        super('getOrderStatusHistory', '/message/GetOrderStatusHistory');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetOrderStatusHistory
     */
    *saga() {
        yield fork(listener$4z);
    }
}
// Export the state handling singleton
//
const getOrderStatusHistory = new GetOrderStatusHistory();
// Setup the Saga implementation
//
function* fetchCall$4z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getOrderStatusHistory, 'fetchData'], action);
        yield put(getOrderStatusHistory.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getOrderStatusHistory.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4z() {
    yield takeEvery(getType(getOrderStatusHistory.actions.fetch), fetchCall$4z);
}

/**
 * Implements GetOrderSummaryForShipping fetch for the Redux store
 *
 * @export
 * @class GetOrderSummaryForShipping
 * @extends {StoreFetchableData<EVA.Core.GetOrderSummaryForShipping, EVA.Core.GetOrderSummaryForShippingResponse>}
 */
class GetOrderSummaryForShipping extends StoreFetchableData {
    /**
     * Creates an instance of GetOrderSummaryForShipping
     *
     * @memberof GetOrderSummaryForShipping
     */
    constructor() {
        super('getOrderSummaryForShipping', '/message/GetOrderSummaryForShipping');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetOrderSummaryForShipping
     */
    *saga() {
        yield fork(listener$4y);
    }
}
// Export the state handling singleton
//
const getOrderSummaryForShipping = new GetOrderSummaryForShipping();
// Setup the Saga implementation
//
function* fetchCall$4y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getOrderSummaryForShipping, 'fetchData'], action);
        yield put(getOrderSummaryForShipping.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getOrderSummaryForShipping.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4y() {
    yield takeEvery(getType(getOrderSummaryForShipping.actions.fetch), fetchCall$4y);
}

/**
 * Implements GetOrganizationUnitDetailed fetch for the Redux store
 *
 * @export
 * @class GetOrganizationUnitDetailed
 * @extends {StoreFetchableData<EVA.Core.GetOrganizationUnitDetailed, EVA.Core.GetOrganizationUnitDetailedResponse>}
 */
class GetOrganizationUnitDetailed extends StoreFetchableData {
    /**
     * Creates an instance of GetOrganizationUnitDetailed
     *
     * @memberof GetOrganizationUnitDetailed
     */
    constructor() {
        super('getOrganizationUnitDetailed', '/message/GetOrganizationUnitDetailed');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetOrganizationUnitDetailed
     */
    *saga() {
        yield fork(listener$4x);
    }
}
// Export the state handling singleton
//
const getOrganizationUnitDetailed = new GetOrganizationUnitDetailed();
// Setup the Saga implementation
//
function* fetchCall$4x(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getOrganizationUnitDetailed, 'fetchData'], action);
        yield put(getOrganizationUnitDetailed.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getOrganizationUnitDetailed.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4x() {
    yield takeEvery(getType(getOrganizationUnitDetailed.actions.fetch), fetchCall$4x);
}

/**
 * Implements GetOrganizationUnitSummary fetch for the Redux store
 *
 * @export
 * @class GetOrganizationUnitSummary
 * @extends {StoreFetchableData<EVA.Core.GetOrganizationUnitSummary, EVA.Core.GetOrganizationUnitSummaryResponse>}
 */
class GetOrganizationUnitSummary extends StoreFetchableData {
    /**
     * Creates an instance of GetOrganizationUnitSummary
     *
     * @memberof GetOrganizationUnitSummary
     */
    constructor() {
        super('getOrganizationUnitSummary', '/message/GetOrganizationUnitSummary');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetOrganizationUnitSummary
     */
    *saga() {
        yield fork(listener$4w);
    }
}
// Export the state handling singleton
//
const getOrganizationUnitSummary = new GetOrganizationUnitSummary();
// Setup the Saga implementation
//
function* fetchCall$4w(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getOrganizationUnitSummary, 'fetchData'], action);
        yield put(getOrganizationUnitSummary.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getOrganizationUnitSummary.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4w() {
    yield takeEvery(getType(getOrganizationUnitSummary.actions.fetch), fetchCall$4w);
}

/**
 * Implements GetPackagesForCarrier fetch for the Redux store
 *
 * @export
 * @class GetPackagesForCarrier
 * @extends {StoreFetchableData<EVA.Core.Management.GetPackagesForCarrier, EVA.Core.Management.GetPackagesResponse>}
 */
class GetPackagesForCarrier extends StoreFetchableData {
    /**
     * Creates an instance of GetPackagesForCarrier
     *
     * @memberof GetPackagesForCarrier
     */
    constructor() {
        super('getPackagesForCarrier', '/message/GetPackagesForCarrier');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetPackagesForCarrier
     */
    *saga() {
        yield fork(listener$4v);
    }
}
// Export the state handling singleton
//
const getPackagesForCarrier = new GetPackagesForCarrier();
// Setup the Saga implementation
//
function* fetchCall$4v(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getPackagesForCarrier, 'fetchData'], action);
        yield put(getPackagesForCarrier.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getPackagesForCarrier.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4v() {
    yield takeEvery(getType(getPackagesForCarrier.actions.fetch), fetchCall$4v);
}

/**
 * Implements GetPaymentTypeByID fetch for the Redux store
 *
 * @export
 * @class GetPaymentTypeByID
 * @extends {StoreFetchableData<EVA.Core.Management.GetPaymentTypeByID, EVA.Core.Management.GetPaymentTypeByIDResponse>}
 */
class GetPaymentTypeByID extends StoreFetchableData {
    /**
     * Creates an instance of GetPaymentTypeByID
     *
     * @memberof GetPaymentTypeByID
     */
    constructor() {
        super('getPaymentTypeByID', '/message/GetPaymentTypeByID');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetPaymentTypeByID
     */
    *saga() {
        yield fork(listener$4u);
    }
}
// Export the state handling singleton
//
const getPaymentTypeByID = new GetPaymentTypeByID();
// Setup the Saga implementation
//
function* fetchCall$4u(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getPaymentTypeByID, 'fetchData'], action);
        yield put(getPaymentTypeByID.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getPaymentTypeByID.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4u() {
    yield takeEvery(getType(getPaymentTypeByID.actions.fetch), fetchCall$4u);
}

/**
 * Implements GetPaymentTransactionsForOrder fetch for the Redux store
 *
 * @export
 * @class GetPaymentTransactionsForOrder
 * @extends {StoreFetchableData<EVA.Core.GetPaymentTransactionsForOrder, EVA.Core.GetPaymentTransactionsResponse>}
 */
class GetPaymentTransactionsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetPaymentTransactionsForOrder
     *
     * @memberof GetPaymentTransactionsForOrder
     */
    constructor() {
        super('getPaymentTransactionsForOrder', '/message/GetPaymentTransactionsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetPaymentTransactionsForOrder
     */
    *saga() {
        yield fork(listener$4t);
    }
}
// Export the state handling singleton
//
const getPaymentTransactionsForOrder = new GetPaymentTransactionsForOrder();
// Setup the Saga implementation
//
function* fetchCall$4t(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getPaymentTransactionsForOrder, 'fetchData'], action);
        yield put(getPaymentTransactionsForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getPaymentTransactionsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4t() {
    yield takeEvery(getType(getPaymentTransactionsForOrder.actions.fetch), fetchCall$4t);
}

/**
 * Implements GetPickProductDiscountOptionsForOrder fetch for the Redux store
 *
 * @export
 * @class GetPickProductDiscountOptionsForOrderProducts
 * @extends {StoreFetchableData<EVA.Core.GetPickProductDiscountOptionsForOrderResponse, EVA.Core.GetProductsResponse>}
 */
class GetPickProductDiscountOptionsForOrderProducts extends StoreFetchableData {
    /**
     * Creates an instance of GetPickProductDiscountOptionsForOrderProducts
     *
     * @memberof GetPickProductDiscountOptionsForOrderProducts
     */
    constructor() {
        super('getPickProductDiscountOptionsForOrderProducts', '/message/GetProducts');
    }
    /**
     * Retrieves the requested data from the EVA backend
     *
     * @public
     * @param {*} action The Redux action
     * @returns {Promise<EVA.Core.GetProductsResponse>}
     * @memberof GetPickProductDiscountOptionsForOrder
     */
    fetchData(action) {
        // The action supplied is a EVA.Core.GetPickProductDiscountOptionsForOrderResponse
        // This contains all the pickable discount products
        //
        const pickDiscountProductOptionsResponse = get(action, 'payload.request');
        // Collect the product ID's
        //
        const productIDs = [];
        each(get(pickDiscountProductOptionsResponse, 'OptionsPerLine'), (pickDiscountProductsForOrderLine, orderLineId) => {
            each(get(pickDiscountProductsForOrderLine, 'Options'), (productId) => {
                productIDs.push(productId);
            });
        });
        // Build the search products payload with the parent hierarchy filter
        // to retrieve the variants
        // Use the included fields from the settings
        //
        const getProductsPayload = {
            ProductIDs: uniq(productIDs),
            IncludedFields: settings.defaultProductProperties,
        };
        set(action, 'payload.request', getProductsPayload);
        // Check if there are products being fetched
        //
        if (!isEmpty(productIDs)) {
            return super.fetchData(action);
        }
        else {
            return Promise.resolve({ Products: [] });
        }
    }
    /**
     * Initial state with extended properties
     *
     * @type {IGetPickProductDiscountOptionsForOrderProductsState}
     * @memberof GetPickProductDiscountOptionsForOrder
     */
    get initialState() {
        return {
            isFetching: false,
            request: null,
            response: null,
            error: null,
            chainStarter: this.name,
            optionsPerLine: {},
        };
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetPickProductDiscountOptionsForOrder
     */
    *saga() {
        yield fork(listener$4s);
    }
    /**
     * The extended collection of reducer functions
     *
     * @protected
     * @returns {{ [ reducerName: string ]: ( state: any, action: AnyAction ) => any }}
     * @memberof GetPickProductDiscountOptionsForOrder
     */
    getReducerFunctions() {
        const baseReducerFuncs = super.getReducerFunctions();
        baseReducerFuncs.optionsPerLine = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const request = this.getState().request;
                    const response = get(action, 'payload');
                    const optionsPerLine = {};
                    each(get(request, 'OptionsPerLine'), (options, index) => {
                        const orderLineId = parseInt(index, 10);
                        optionsPerLine[orderLineId] = [];
                        each(options.Options, (productId) => {
                            // Find the product in the search response and add it to the
                            // options per line for this order line id
                            //
                            const product = find(get(response, 'Products'), (getProducts) => getProducts.product_id === parseInt(`${productId}`, 10));
                            optionsPerLine[orderLineId].push(product);
                        });
                    });
                    return optionsPerLine;
                default:
                    return state;
            }
        };
        return baseReducerFuncs;
    }
}
// Export the state handling singleton
//
const getPickProductDiscountOptionsForOrderProducts = new GetPickProductDiscountOptionsForOrderProducts();
// Setup the Saga implementation
//
function* fetchCall$4s(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getPickProductDiscountOptionsForOrderProducts, 'fetchData'], action);
        yield put(getPickProductDiscountOptionsForOrderProducts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getPickProductDiscountOptionsForOrderProducts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4s() {
    yield takeEvery(getType(getPickProductDiscountOptionsForOrderProducts.actions.fetch), fetchCall$4s);
}

/**
 * Implements GetPickProductDiscountOptionsForOrder fetch for the Redux store
 *
 * @export
 * @class GetPickProductDiscountOptionsForOrder
 * @extends {StoreFetchableData<EVA.Core.GetPickProductDiscountOptionsForOrder, EVA.Core.GetPickProductDiscountOptionsForOrderResponse>}
 */
class GetPickProductDiscountOptionsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetPickProductDiscountOptionsForOrder
     *
     * @memberof GetPickProductDiscountOptionsForOrder
     */
    constructor() {
        super('getPickProductDiscountOptionsForOrder', '/message/GetPickProductDiscountOptionsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetPickProductDiscountOptionsForOrder
     */
    *saga() {
        yield fork(listener$4r);
    }
}
// Export the state handling singleton
//
const getPickProductDiscountOptionsForOrder = new GetPickProductDiscountOptionsForOrder();
// Setup the Saga implementation
//
function* fetchCall$4r(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([getPickProductDiscountOptionsForOrder, 'fetchData'], action);
        yield put(getPickProductDiscountOptionsForOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Check if we have pick discount products
        // If so fetch the product option details
        //
        let optionalGetPickProductOptionsForOrderProductsChainPromise = Promise.resolve(undefined);
        if (!isEmpty(data.OptionsPerLine)) {
            log$1('[EVA-SDK-REDUX:getPickProductDiscountOptionsForOrder] Fetching pick discount product option details', data);
            const [getPickProductDiscountOptionsForOrderProductsAction, , getPickProductDiscountOptionsForOrderProductsChainPromise] = getPickProductDiscountOptionsForOrderProducts.createFetchAction(data, chainStarter, undefined, undefined, undefined, undefined, chainContext);
            yield put(getPickProductDiscountOptionsForOrderProductsAction);
            optionalGetPickProductOptionsForOrderProductsChainPromise = getPickProductDiscountOptionsForOrderProductsChainPromise;
        }
        // Resolve the chain
        //
        Promise.all([optionalGetPickProductOptionsForOrderProductsChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(getPickProductDiscountOptionsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4r() {
    yield takeEvery(getType(getPickProductDiscountOptionsForOrder.actions.fetch), fetchCall$4r);
}

/**
 * Implements GetPickProductDiscountOptionsForOrderLine fetch for the Redux store
 *
 * @export
 * @class GetPickProductDiscountOptionsForOrderLine
 * @extends {StoreFetchableData<EVA.Core.GetPickProductDiscountOptionsForOrderLine, EVA.Core.GetPickProductDiscountOptionsForOrderLineResponse>}
 */
class GetPickProductDiscountOptionsForOrderLine extends StoreFetchableData {
    /**
     * Creates an instance of GetPickProductDiscountOptionsForOrderLine
     *
     * @memberof GetPickProductDiscountOptionsForOrderLine
     */
    constructor() {
        super('getPickProductDiscountOptionsForOrderLine', '/message/GetPickProductDiscountOptionsForOrderLine');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetPickProductDiscountOptionsForOrderLine
     */
    *saga() {
        yield fork(listener$4q);
    }
}
// Export the state handling singleton
//
const getPickProductDiscountOptionsForOrderLine = new GetPickProductDiscountOptionsForOrderLine();
// Setup the Saga implementation
//
function* fetchCall$4q(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getPickProductDiscountOptionsForOrderLine, 'fetchData'], action);
        yield put(getPickProductDiscountOptionsForOrderLine.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getPickProductDiscountOptionsForOrderLine.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4q() {
    yield takeEvery(getType(getPickProductDiscountOptionsForOrderLine.actions.fetch), fetchCall$4q);
}

/**
 * Implements GetPotentialBundleProductsForOrder fetch for the Redux store
 *
 * @export
 * @class GetPotentialBundleProductsForOrder
 * @extends {StoreFetchableData<EVA.Core.GetPotentialBundleProductsForOrder, EVA.Core.GetPotentialBundleProductsForOrderResponse>}
 */
class GetPotentialBundleProductsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetPotentialBundleProductsForOrder
     *
     * @memberof GetPotentialBundleProductsForOrder
     */
    constructor() {
        super('getPotentialBundleProductsForOrder', '/message/GetPotentialBundleProductsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetPotentialBundleProductsForOrder
     */
    *saga() {
        yield fork(listener$4p);
    }
}
// Export the state handling singleton
//
const getPotentialBundleProductsForOrder = new GetPotentialBundleProductsForOrder();
// Setup the Saga implementation
//
function* fetchCall$4p(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getPotentialBundleProductsForOrder, 'fetchData'], action);
        yield put(getPotentialBundleProductsForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getPotentialBundleProductsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4p() {
    yield takeEvery(getType(getPotentialBundleProductsForOrder.actions.fetch), fetchCall$4p);
}

/**
 * Implements GetPriceListUsageTypes fetch for the Redux store
 *
 * @export
 * @class GetPriceListUsageTypes
 * @extends {StoreFetchableData<EVA.Core.Management.GetPriceListUsageTypes, EVA.Core.Management.GetPriceListUsageTypesResponse>}
 */
class GetPriceListUsageTypes extends StoreFetchableData {
    /**
     * Creates an instance of GetPriceListUsageTypes
     *
     * @memberof GetPriceListUsageTypes
     */
    constructor() {
        super('getPriceListUsageTypes', '/message/GetPriceListUsageTypes');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetPriceListUsageTypes
     */
    *saga() {
        yield fork(listener$4o);
    }
}
// Export the state handling singleton
//
const getPriceListUsageTypes = new GetPriceListUsageTypes();
// Setup the Saga implementation
//
function* fetchCall$4o(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getPriceListUsageTypes, 'fetchData'], action);
        yield put(getPriceListUsageTypes.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getPriceListUsageTypes.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4o() {
    yield takeEvery(getType(getPriceListUsageTypes.actions.fetch), fetchCall$4o);
}

/**
 * Implements GetProductAvailability fetch for the Redux store
 *
 * @export
 * @class GetProductAvailability
 * @extends {StoreFetchableData<EVA.Core.GetProductAvailability, EVA.Core.GetProductAvailabilityResponse>}
 */
class GetProductAvailability extends StoreFetchableData {
    /**
     * Creates an instance of GetProductAvailability
     *
     * @memberof GetProductAvailability
     */
    constructor() {
        super('getProductAvailability', '/message/GetProductAvailability');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductAvailability
     */
    *saga() {
        yield fork(listener$4n);
    }
}
// Export the state handling singleton
//
const getProductAvailability = new GetProductAvailability();
// Setup the Saga implementation
//
function* fetchCall$4n(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProductAvailability, 'fetchData'], action);
        yield put(getProductAvailability.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProductAvailability.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4n() {
    yield takeEvery(getType(getProductAvailability.actions.fetch), fetchCall$4n);
}

/**
 * Implements GetProductDetailQuantityOnHand fetch for the Redux store
 *
 * @export
 * @class GetProductDetailQuantityOnHand
 * @extends {StoreFetchableData<EVA.Core.GetQuantityOnHandForProducts, EVA.Core.GetQuantityOnHandForProductsResponse>}
 */
class GetProductDetailQuantityOnHand extends StoreFetchableData {
    /**
     * Creates an instance of GetProductDetailQuantityOnHand
     *
     * @memberof GetProductDetailQuantityOnHand
     */
    constructor() {
        super('getProductDetailQuantityOnHand', '/message/GetQuantityOnHandForProducts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductDetailQuantityOnHand
     */
    *saga() {
        yield fork(listener$4m);
    }
}
// Export the state handling singleton
//
const getProductDetailQuantityOnHand = new GetProductDetailQuantityOnHand();
// Setup the Saga implementation
//
function* fetchCall$4m(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProductDetailQuantityOnHand, 'fetchData'], action);
        yield put(getProductDetailQuantityOnHand.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProductDetailQuantityOnHand.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4m() {
    yield takeEvery(getType(getProductDetailQuantityOnHand.actions.fetch), fetchCall$4m);
}

/**
 * Implements GetProductVariants fetch for the Redux store
 * This is a pre-configured filtered SearchProducts call
 *
 * @export
 * @class GetProductVariants
 * @extends {StoreFetchableData<EVA.Core.GetProductDetail, EVA.Core.SearchProductsResponse>}
 */
class GetProductVariants extends StoreFetchableData {
    /**
     * Creates an instance of GetProductVariants
     *
     * @memberof GetProductVariants
     */
    constructor() {
        super('getProductVariants', '/message/SearchProducts', undefined, { addDefaultProductPropertiesPath: 'IncludedFields' });
    }
    /**
     * Retrieves the requested data from the EVA backend
     *
     * @public
     * @param {*} action The Redux action
     * @returns {Promise<EVA.Core.SearchProductsResponse>}
     * @memberof GetProductVariants
     */
    fetchData(action) {
        // The action supplied is a GetProductDetails because that is the most
        // convenient way to select a product
        //
        const fetchProductDetails = get(action, 'payload.request');
        // Build the search products payload with the parent hierarchy filter
        // to retrieve the variants
        // Use the included fields from the settings
        //
        const searchVariantsPayload = {
            PageSize: 80,
            Filters: {
                parent_hierarchy: {
                    ExactMatch: true,
                    From: '',
                    IncludeMissing: false,
                    Negation: false,
                    To: '',
                    Values: [get(fetchProductDetails, 'ID')],
                },
            },
            IncludedFields: settings.defaultProductProperties,
        };
        set(action, 'payload.request', searchVariantsPayload);
        return super.fetchData(action);
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductVariants
     */
    *saga() {
        yield fork(listener$4l);
    }
}
// Export the state handling singleton
//
const getProductVariants = new GetProductVariants();
// Setup the Saga implementation
//
function* fetchCall$4l(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProductVariants, 'fetchData'], action);
        yield put(getProductVariants.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProductVariants.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4l() {
    yield takeEvery(getType(getProductVariants.actions.fetch), fetchCall$4l);
}

/**
 * Implements GetProductDetailProductAvailability fetch for the Redux store
 *
 * @export
 * @class GetProductDetailProductAvailability
 * @extends {StoreFetchableData<EVA.Core.GetProductAvailability, EVA.Core.GetProductAvailabilityResponse>}
 */
class GetProductDetailProductAvailability extends StoreFetchableData {
    /**
     * Creates an instance of GetProductDetailProductAvailability
     *
     * @memberof GetProductDetailProductAvailability
     */
    constructor() {
        super('getProductDetailProductAvailability', '/message/GetProductAvailability');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductDetailProductAvailability
     */
    *saga() {
        yield fork(listener$4k);
    }
}
// Export the state handling singleton
//
const getProductDetailProductAvailability = new GetProductDetailProductAvailability();
// Setup the Saga implementation
//
function* fetchCall$4k(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProductDetailProductAvailability, 'fetchData'], action);
        yield put(getProductDetailProductAvailability.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProductDetailProductAvailability.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4k() {
    yield takeEvery(getType(getProductDetailProductAvailability.actions.fetch), fetchCall$4k);
}

/**
 * Implements GetProductDetail fetch for the Redux store
 *
 * @export
 * @class GetProductDetail
 * @extends {StoreFetchableData<EVA.Core.GetProductDetail, EVA.Core.GetProductDetailResponse>}
 */
class GetProductDetail extends StoreFetchableData {
    /**
     * Creates an instance of GetProductDetail
     *
     * @memberof GetProductDetail
     */
    constructor() {
        super('getProductDetail', '/message/GetProductDetail');
    }
    /**
     * Initial state with extended properties
     *
     * @type {IGetProductDetailState}
     * @memberof GetProductDetail
     */
    get initialState() {
        return {
            isFetching: false,
            request: null,
            response: null,
            error: null,
            chainStarter: this.name,
            quantityInCart: 0,
            orderLines: [],
            isConfigurable: false,
        };
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductDetail
     */
    *saga() {
        yield fork(listener$4j);
    }
    /**
     * The extended collection of reducer functions
     *
     * @protected
     * @returns {{ [ reducerName: string ]: ( state: any, action: AnyAction ) => any }}
     * @memberof GetProductDetail
     */
    getReducerFunctions() {
        const baseReducerFuncs = super.getReducerFunctions();
        // Collect orderLines this product contains
        //
        baseReducerFuncs.orderLines = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const productId = get(payload, 'Result.product_id');
                    const shoppingCartState = getShoppingCart.getState();
                    const lines = get(shoppingCartState, 'response.ShoppingCart.Lines', []);
                    const productLines = filter(lines, { ProductID: productId });
                    return productLines;
                default:
                    return state;
            }
        };
        // Counts total number of items for the product id in the cart
        //
        baseReducerFuncs.quantityInCart = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const productId = get(payload, 'Result.product_id');
                    const shoppingCartState = getShoppingCart.getState();
                    const lines = get(shoppingCartState, 'response.ShoppingCart.Lines', []);
                    const productLines = filter(lines, { ProductID: productId });
                    return sumBy(productLines, 'TotalQuantityToShip');
                case getType(getShoppingCart.actions.response):
                    const cartResponse = get(action, 'payload');
                    const currentProductId = get(this.getState(), 'request.ID');
                    const cartLines = get(cartResponse, 'ShoppingCart.Lines', []);
                    return sumBy(filter(cartLines, { ProductID: currentProductId }), 'TotalQuantityToShip');
                default:
                    return state;
            }
        };
        // Determines if the product is configurable
        //
        baseReducerFuncs.isConfigurable = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    const productTypes = get(payload, 'Result.product_types') || [];
                    return (indexOf(productTypes, 16384) !== -1);
                default:
                    return state;
            }
        };
        return baseReducerFuncs;
    }
}
// Export the state handling singleton
//
const getProductDetail = new GetProductDetail();
function* fetchCall$4j(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([getProductDetail, 'fetchData'], action);
        yield put(getProductDetail.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        const currentUserState = getCurrentUser.getState();
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Get the product details from the store state
        //
        const productData = get(data, 'Result') || {};
        const productId = get(productData, 'product_id') || get(productData, 'ID');
        log$1(`[EVA-SDK-REDUX:getProductDetail] Fetched product details`, productId, productData);
        // Check we actually received a product
        //
        if (productId && productData) {
            // Fetch variants
            //
            const fetchVariantRequest = {
                ID: productId,
            };
            log$1('[EVA-SDK-REDUX:getProductDetail] Fetching product variants', fetchVariantRequest);
            const [getProductVariantsAction, , getProductVariantsChainPromise] = getProductVariants.createFetchAction(fetchVariantRequest, chainStarter, undefined, undefined, undefined, undefined, chainContext);
            yield put(getProductVariantsAction);
            // Fetch configurable if this is a configurable product
            // 16384 is configurable product
            //
            const productTypes = get(productData, 'product_types') || [];
            let optionalConfigurableDetailChainPromise = Promise.resolve(undefined);
            if ((indexOf(productTypes, 16384) !== -1) || !isEmpty(get(productData, 'configurable_properties'))) {
                const fetchConfigurableDetailsRequest = {
                    ID: productId,
                    IncludedFields: settings.defaultProductProperties,
                };
                log$1('[EVA-SDK-REDUX:getProductDetail] Fetching configurable product details', fetchConfigurableDetailsRequest);
                const [getConfigurableProductDetailAction, , getConfigurableProductDetailChainPromise] = getConfigurableProductDetail.createFetchAction(fetchConfigurableDetailsRequest, chainStarter, undefined, undefined, undefined, undefined, chainContext);
                yield put(getConfigurableProductDetailAction);
                optionalConfigurableDetailChainPromise = getConfigurableProductDetailChainPromise;
            }
            // Fetch availability indication
            //
            let optionalGetProductAvailabilityChainPromise = Promise.resolve(undefined);
            if (productId) {
                const currentOrganizationId = get(currentUserState, 'response.User.CurrentOrganizationID');
                const availabilityOptions = {
                    Bundles: {
                        AvailabilityCalculationMethod: 1,
                        IncludeBundleInformation: true
                    },
                    Delivery: {
                        AvailabilityDate: true,
                    },
                    Pickup: {
                        AvailabilityDate: true,
                        OrganizationUnitIDs: [currentOrganizationId],
                    },
                    ProductAvailabilityDetails: {
                        OrganizationUnitIDs: [currentOrganizationId],
                        ProductIDs: [productId],
                    }
                };
                const getProductAvailabilityRequest = {
                    Products: [
                        {
                            ID: productId,
                            QuantityRequested: 1,
                        },
                    ],
                    Options: availabilityOptions,
                };
                log$1('[EVA-SDK-REDUX:getProductDetail] Fetching availability for product details', getProductAvailabilityRequest);
                const [getProductDetailProductAvailabilityAction, , getProductDetailProductAvailabilityChainPromise] = getProductDetailProductAvailability.createFetchAction(getProductAvailabilityRequest, chainStarter, undefined, undefined, undefined, undefined, chainContext);
                yield put(getProductDetailProductAvailabilityAction);
                optionalGetProductAvailabilityChainPromise = getProductDetailProductAvailabilityChainPromise;
            }
            // Retrieve the quantity on hand of all the stock holding products
            // for the users current organization unit (store)
            //
            let optionalGetQuantityOnHandForProductsChainPromise = Promise.resolve(undefined);
            const organizationUnitId = get(currentUserState, 'response.User.CurrentOrganizationID');
            if (productId && organizationUnitId) {
                const getQuantityOnHandForProductsRequest = {
                    OrganizationUnitID: organizationUnitId,
                    ProductIDs: [productId],
                };
                log$1('[EVA-SDK-REDUX:getProductDetail] Fetching quantity on hand for product details', getQuantityOnHandForProductsRequest);
                const [getQuantityOnHandForProductsAction, , getQuantityOnHandForProductsChainPromise] = getProductDetailQuantityOnHand.createFetchAction(getQuantityOnHandForProductsRequest, chainStarter, undefined, undefined, undefined, undefined, chainContext);
                yield put(getQuantityOnHandForProductsAction);
                optionalGetQuantityOnHandForProductsChainPromise = getQuantityOnHandForProductsChainPromise;
            }
            // Resolve the chain
            //
            Promise.all([
                getProductVariantsChainPromise,
                optionalConfigurableDetailChainPromise,
                optionalGetProductAvailabilityChainPromise,
                optionalGetQuantityOnHandForProductsChainPromise,
            ])
                .then((chainResponses) => {
                chainResponses.unshift(data);
                fetchActionPayload.chainResolve(chainResponses);
            })
                .catch(fetchActionPayload.chainReject);
        }
        else {
            fetchActionPayload.chainResolve([data, undefined, undefined, undefined]);
        }
    }
    catch (err) {
        yield put(getProductDetail.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4j() {
    yield takeEvery(getType(getProductDetail.actions.fetch), fetchCall$4j);
}

/**
 * Implements GetProductPrices fetch for the Redux store
 *
 * @export
 * @class GetProductPrices
 * @extends {StoreFetchableData<EVA.Core.GetProductPrices, EVA.Core.GetProductPricesResponse>}
 */
class GetProductPrices extends StoreFetchableData {
    /**
     * Creates an instance of GetProductPrices
     *
     * @memberof GetProductPrices
     */
    constructor() {
        super('getProductPrices', '/message/GetProductPrices');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductPrices
     */
    *saga() {
        yield fork(listener$4i);
    }
}
// Export the state handling singleton
//
const getProductPrices = new GetProductPrices();
// Setup the Saga implementation
//
function* fetchCall$4i(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProductPrices, 'fetchData'], action);
        yield put(getProductPrices.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProductPrices.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4i() {
    yield takeEvery(getType(getProductPrices.actions.fetch), fetchCall$4i);
}

/**
 * Implements GetProductPricesForSearchProducts fetch for the Redux store
 *
 * @export
 * @class GetProductPricesForSearchProducts
 * @extends {StoreFetchableData<EVA.Core.GetProductPrices, EVA.Core.GetProductPricesResponse>}
 */
class GetProductPricesForSearchProducts extends StoreFetchableData {
    /**
     * Creates an instance of GetProductPrices
     *
     * @memberof GetProductPricesForSearchProducts
     */
    constructor() {
        super('getProductPricesForSearchProducts', '/message/GetProductPrices');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductPricesForSearchProducts
     */
    *saga() {
        yield fork(listener$4h);
    }
}
// Export the state handling singleton
//
const getProductPricesForSearchProducts = new GetProductPricesForSearchProducts();
// Setup the Saga implementation
//
function* fetchCall$4h(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProductPricesForSearchProducts, 'fetchData'], action);
        yield put(getProductPricesForSearchProducts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProductPricesForSearchProducts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4h() {
    yield takeEvery(getType(getProductPricesForSearchProducts.actions.fetch), fetchCall$4h);
}

/**
 * Implements GetProductPricesForShoppingCart fetch for the Redux store
 *
 * @export
 * @class GetProductPricesForShoppingCart
 * @extends {StoreFetchableData<EVA.Core.GetProductPrices, EVA.Core.GetProductPricesResponse>}
 */
class GetProductPricesForShoppingCart extends StoreFetchableData {
    /**
     * Creates an instance of GetProductPrices
     *
     * @memberof GetProductPricesForShoppingCart
     */
    constructor() {
        super('getProductPricesForShoppingCart', '/message/GetProductPrices');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductPricesForShoppingCart
     */
    *saga() {
        yield fork(listener$4g);
    }
}
// Export the state handling singleton
//
const getProductPricesForShoppingCart = new GetProductPricesForShoppingCart();
// Setup the Saga implementation
//
function* fetchCall$4g(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProductPricesForShoppingCart, 'fetchData'], action);
        yield put(getProductPricesForShoppingCart.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProductPricesForShoppingCart.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4g() {
    yield takeEvery(getType(getProductPricesForShoppingCart.actions.fetch), fetchCall$4g);
}

/**
 * Implements GetProductPricing fetch for the Redux store
 *
 * @export
 * @class GetProductPricing
 * @extends {StoreFetchableData<EVA.Core.GetProductPricing, EVA.Core.GetProductPricingResponse>}
 */
class GetProductPricing extends StoreFetchableData {
    /**
     * Creates an instance of GetProductPricing
     *
     * @memberof GetProductPricing
     */
    constructor() {
        super('getProductPricing', '/message/GetProductPricing');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductPricing
     */
    *saga() {
        yield fork(listener$4f);
    }
}
// Export the state handling singleton
//
const getProductPricing = new GetProductPricing();
// Setup the Saga implementation
//
function* fetchCall$4f(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProductPricing, 'fetchData'], action);
        yield put(getProductPricing.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProductPricing.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4f() {
    yield takeEvery(getType(getProductPricing.actions.fetch), fetchCall$4f);
}

/**
 * Implements GetProductRequirementByID fetch for the Redux store
 *
 * @export
 * @class GetProductRequirementByID
 * @extends {StoreFetchableData<EVA.Core.Management.GetProductRequirementByIDResponse, EVA.Core.Management.GetProductRequirementByIDResponse>}
 */
class GetProductRequirementByID extends StoreFetchableData {
    /**
     * Creates an instance of GetProductRequirementByID
     *
     * @memberof GetProductRequirementByID
     */
    constructor() {
        super('getProductRequirementById', '/message/GetProductRequirementByID');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductRequirementByID
     */
    *saga() {
        yield fork(listener$4e);
    }
}
// Export the state handling singleton
//
const getProductRequirementById = new GetProductRequirementByID();
// Setup the Saga implementation
//
function* fetchCall$4e(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProductRequirementById, 'fetchData'], action);
        yield put(getProductRequirementById.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProductRequirementById.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4e() {
    yield takeEvery(getType(getProductRequirementById.actions.fetch), fetchCall$4e);
}

/**
 * Implements GetProductRequirementValuesForOrder fetch for the Redux store
 *
 * @export
 * @class GetProductRequirementValuesForOrder
 * @extends {StoreFetchableData<EVA.Core.GetProductRequirementValuesForOrder, EVA.Core.GetProductRequirementValuesForOrderResponse>}
 */
class GetProductRequirementValuesForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetProductRequirementValuesForOrder
     *
     * @memberof GetProductRequirementValuesForOrder
     */
    constructor() {
        super('getProductRequirementValuesForOrder', '/message/GetProductRequirementValuesForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductRequirementValuesForOrder
     */
    *saga() {
        yield fork(listener$4d);
    }
}
// Export the state handling singleton
//
const getProductRequirementValuesForOrder = new GetProductRequirementValuesForOrder();
// Setup the Saga implementation
//
function* fetchCall$4d(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProductRequirementValuesForOrder, 'fetchData'], action);
        yield put(getProductRequirementValuesForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProductRequirementValuesForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4d() {
    yield takeEvery(getType(getProductRequirementValuesForOrder.actions.fetch), fetchCall$4d);
}

/**
 * Implements GetProductRequirementValuesForOrderLine fetch for the Redux store
 *
 * @export
 * @class GetProductRequirementValuesForOrderLine
 * @extends {StoreFetchableData<EVA.Core.GetProductRequirementValuesForOrderLine, EVA.Core.GetProductRequirementValuesForOrderLineResponse>}
 */
class GetProductRequirementValuesForOrderLine extends StoreFetchableData {
    /**
     * Creates an instance of GetProductRequirementValuesForOrderLine
     *
     * @memberof GetProductRequirementValuesForOrderLine
     */
    constructor() {
        super('getProductRequirementValuesForOrderLine', '/message/GetProductRequirementValuesForOrderLine');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductRequirementValuesForOrderLine
     */
    *saga() {
        yield fork(listener$4c);
    }
}
// Export the state handling singleton
//
const getProductRequirementValuesForOrderLine = new GetProductRequirementValuesForOrderLine();
// Setup the Saga implementation
//
function* fetchCall$4c(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProductRequirementValuesForOrderLine, 'fetchData'], action);
        yield put(getProductRequirementValuesForOrderLine.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProductRequirementValuesForOrderLine.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4c() {
    yield takeEvery(getType(getProductRequirementValuesForOrderLine.actions.fetch), fetchCall$4c);
}

/**
 * Implements GetProductSearchSuggestions fetch for the Redux store
 *
 * @export
 * @class GetProductSearchSuggestions
 * @extends {StoreFetchableData<EVA.Core.GetProductSearchSuggestions, EVA.Core.GetProductSearchSuggestionsResponse>}
 */
class GetProductSearchSuggestions extends StoreFetchableData {
    /**
     * Creates an instance of GetProductSearchSuggestions
     *
     * @memberof GetProductSearchSuggestions
     */
    constructor() {
        super('getProductSearchSuggestions', '/message/GetProductSearchSuggestions');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductSearchSuggestions
     */
    *saga() {
        yield fork(listener$4b);
    }
}
// Export the state handling singleton
//
const getProductSearchSuggestions = new GetProductSearchSuggestions();
// Setup the Saga implementation
//
function* fetchCall$4b(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProductSearchSuggestions, 'fetchData'], action);
        yield put(getProductSearchSuggestions.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProductSearchSuggestions.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4b() {
    yield takeEvery(getType(getProductSearchSuggestions.actions.fetch), fetchCall$4b);
}

/**
 * Implements GetProductUnitOfMeasureQuantities fetch for the Redux store
 *
 * @export
 * @class GetProductUnitOfMeasureQuantities
 * @extends {StoreFetchableData<EVA.Core.GetProductUnitOfMeasureQuantities, EVA.Core.GetProductUnitOfMeasureQuantitiesResponse>}
 */
class GetProductUnitOfMeasureQuantities extends StoreFetchableData {
    /**
     * Creates an instance of GetProductUnitOfMeasureQuantities
     *
     * @memberof GetProductUnitOfMeasureQuantities
     */
    constructor() {
        super('getProductUnitOfMeasureQuantities', '/message/GetProductUnitOfMeasureQuantities');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProductUnitOfMeasureQuantities
     */
    *saga() {
        yield fork(listener$4a);
    }
}
// Export the state handling singleton
//
const getProductUnitOfMeasureQuantities = new GetProductUnitOfMeasureQuantities();
// Setup the Saga implementation
//
function* fetchCall$4a(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProductUnitOfMeasureQuantities, 'fetchData'], action);
        yield put(getProductUnitOfMeasureQuantities.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProductUnitOfMeasureQuantities.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4a() {
    yield takeEvery(getType(getProductUnitOfMeasureQuantities.actions.fetch), fetchCall$4a);
}

/**
 * Implements GetProducts fetch for the Redux store
 *
 * @export
 * @class GetProducts
 * @extends {StoreFetchableData<EVA.Core.GetProducts, EVA.Core.GetProductsResponse>}
 */
class GetProducts extends StoreFetchableData {
    /**
     * Creates an instance of GetProducts
     *
     * @memberof GetProducts
     */
    constructor() {
        super('getProducts', '/message/GetProducts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetProducts
     */
    *saga() {
        yield fork(listener$49);
    }
}
// Export the state handling singleton
//
const getProducts = new GetProducts();
// Setup the Saga implementation
//
function* fetchCall$49(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getProducts, 'fetchData'], action);
        yield put(getProducts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getProducts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$49() {
    yield takeEvery(getType(getProducts.actions.fetch), fetchCall$49);
}

/**
 * Implements GetQuantityOnHandForProducts fetch for the Redux store
 *
 * @export
 * @class GetQuantityOnHandForProducts
 * @extends {StoreFetchableData<EVA.Core.GetQuantityOnHandForProducts, EVA.Core.GetQuantityOnHandForProductsResponse>}
 */
class GetQuantityOnHandForProducts extends StoreFetchableData {
    /**
     * Creates an instance of GetQuantityOnHandForProducts
     *
     * @memberof GetQuantityOnHandForProducts
     */
    constructor() {
        super('getQuantityOnHandForProducts', '/message/GetQuantityOnHandForProducts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetQuantityOnHandForProducts
     */
    *saga() {
        yield fork(listener$48);
    }
}
// Export the state handling singleton
//
const getQuantityOnHandForProducts = new GetQuantityOnHandForProducts();
// Setup the Saga implementation
//
function* fetchCall$48(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getQuantityOnHandForProducts, 'fetchData'], action);
        yield put(getQuantityOnHandForProducts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getQuantityOnHandForProducts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$48() {
    yield takeEvery(getType(getQuantityOnHandForProducts.actions.fetch), fetchCall$48);
}

/**
 * Implements GetQuickBuy fetch for the Redux store
 *
 * @export
 * @class GetQuickBuy
 * @extends {StoreFetchableData<EVA.Core.GetQuickBuy, EVA.Core.GetQuickBuyResponse>}
 */
class GetQuickBuy extends StoreFetchableData {
    /**
     * Creates an instance of GetQuickBuy
     *
     * @memberof GetQuickBuy
     */
    constructor() {
        super('getQuickBuy', '/message/GetQuickBuy');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetQuickBuy
     */
    *saga() {
        yield fork(listener$47);
    }
}
// Export the state handling singleton
//
const getQuickBuy = new GetQuickBuy();
// Setup the Saga implementation
//
function* fetchCall$47(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getQuickBuy, 'fetchData'], action);
        yield put(getQuickBuy.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getQuickBuy.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$47() {
    yield takeEvery(getType(getQuickBuy.actions.fetch), fetchCall$47);
}

/**
 * Implements GetQuickBuyOptions fetch for the Redux store
 *
 * @export
 * @class GetQuickBuyOptions
 * @extends {StoreFetchableData<EVA.Core.GetQuickBuyOptions, EVA.Core.GetQuickBuyOptionsResponse>}
 */
class GetQuickBuyOptions extends StoreFetchableData {
    /**
     * Creates an instance of GetQuickBuyOptions
     *
     * @memberof GetQuickBuyOptions
     */
    constructor() {
        super('getQuickBuyOptions', '/message/GetQuickBuyOptions');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetQuickBuyOptions
     */
    *saga() {
        yield fork(listener$46);
    }
}
// Export the state handling singleton
//
const getQuickBuyOptions = new GetQuickBuyOptions();
// Setup the Saga implementation
//
function* fetchCall$46(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getQuickBuyOptions, 'fetchData'], action);
        yield put(getQuickBuyOptions.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getQuickBuyOptions.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$46() {
    yield takeEvery(getType(getQuickBuyOptions.actions.fetch), fetchCall$46);
}

/**
 * Implements GetQuickBuys fetch for the Redux store
 *
 * @export
 * @class GetQuickBuys
 * @extends {StoreFetchableData<EVA.Core.GetQuickBuys, EVA.Core.GetQuickBuysResponse>}
 */
class GetQuickBuys extends StoreFetchableData {
    /**
     * Creates an instance of GetQuickBuys
     *
     * @memberof GetQuickBuys
     */
    constructor() {
        super('getQuickBuys', '/message/GetQuickBuys');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetQuickBuys
     */
    *saga() {
        yield fork(listener$45);
    }
}
// Export the state handling singleton
//
const getQuickBuys = new GetQuickBuys();
// Setup the Saga implementation
//
function* fetchCall$45(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getQuickBuys, 'fetchData'], action);
        yield put(getQuickBuys.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getQuickBuys.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$45() {
    yield takeEvery(getType(getQuickBuys.actions.fetch), fetchCall$45);
}

/**
 * Implements GetAvailableRefundPaymentMethods fetch for the Redux store
 *
 * @export
 * @class GetAvailableRefundPaymentMethods
 * @extends {StoreFetchableData<EVA.Core.GetAvailableRefundPaymentMethodsForOrder, EVA.Core.GetAvailableRefundPaymentMethodsResponse>}
 */
class GetAvailableRefundPaymentMethods extends StoreFetchableData {
    /**
     * Creates an instance of GetAvailableRefundPaymentMethods
     *
     * @memberof GetAvailableRefundPaymentMethods
     */
    constructor() {
        super('getRefundOptionsForOrder', '/message/GetAvailableRefundPaymentMethods');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetAvailableRefundPaymentMethods
     */
    *saga() {
        yield fork(listener$44);
    }
}
// Export the state handling singleton
//
const getRefundOptionsForOrder = new GetAvailableRefundPaymentMethods();
// Setup the Saga implementation
//
function* fetchCall$44(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getRefundOptionsForOrder, 'fetchData'], action);
        yield put(getRefundOptionsForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getRefundOptionsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$44() {
    yield takeEvery(getType(getRefundOptionsForOrder.actions.fetch), fetchCall$44);
}

/**
 * Implements GetReturnToSupplierData fetch for the Redux store
 *
 * @export
 * @class GetReturnToSupplierData
 * @extends {StoreFetchableData<EVA.Core.GetReturnToSupplierData, EVA.Core.GetReturnToSupplierDataResponse>}
 */
class GetReturnToSupplierData extends StoreFetchableData {
    /**
     * Creates an instance of GetReturnToSupplierData
     *
     * @memberof GetReturnToSupplierData
     */
    constructor() {
        super('getReturnToSupplierData', '/message/GetReturnToSupplierData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetReturnToSupplierData
     */
    *saga() {
        yield fork(listener$43);
    }
}
// Export the state handling singleton
//
const getReturnToSupplierData = new GetReturnToSupplierData();
// Setup the Saga implementation
//
function* fetchCall$43(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getReturnToSupplierData, 'fetchData'], action);
        yield put(getReturnToSupplierData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getReturnToSupplierData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$43() {
    yield takeEvery(getType(getReturnToSupplierData.actions.fetch), fetchCall$43);
}

/**
 * Implements GetRequiredDataForOrder fetch for the Redux store
 *
 * @export
 * @class GetRequiredDataForOrder
 * @extends {StoreFetchableData<EVA.Core.GetRequiredDataForOrder, EVA.Core.GetRequiredDataForOrderResponse>}
 */
class GetRequiredDataForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetRequiredDataForOrder
     *
     * @memberof GetRequiredDataForOrder
     */
    constructor(name = 'getRequiredDataForOrder', url = '/message/GetRequiredDataForOrder', fetchOptions, requestOptions, additionalActions) {
        super(name, url, fetchOptions, requestOptions, additionalActions);
        this.url = url;
        this.fetchOptions = fetchOptions;
        this.requestOptions = requestOptions;
        /**
         * Default required for level
         * Can be overridden for each fetch call
         * Changing will trigger a fetch if needed
         * Fetching with a different value will update it
         *
         * Default level is: 7 (placeOrder + Payment + Ship)
         *
         * PlaceOrder = 1,
         * Payment = 2,
         * Ship = 4,
         * Invoice = 8,
         * All = 15,
         *
         * @protected
         * @type {number}
         * @memberof GetRequiredDataForOrder
         */
        this.theRequiredFor = 7;
    }
    /**
     * Gets the current required for level
     *
     * @readonly
     * @type {EVA.Core.RequiredFor}
     * @memberof GetRequiredDataForOrder
     */
    get requiredFor() {
        return this.theRequiredFor;
    }
    /**
     * Update the RequiredFor level and fetch new data if changed
     *
     * @memberof GetRequiredDataForOrder
     */
    set requiredFor(newRequiredFor) {
        if (this.theRequiredFor !== newRequiredFor) {
            this.theRequiredFor = newRequiredFor;
            // Update if there is a current order
            //
            const orderId = get(getShoppingCart.getState(), 'response.ShoppingCart.ID');
            if (orderId) {
                const [action] = this.createFetchAction({
                    OrderID: orderId,
                    RequiredFor: newRequiredFor,
                });
                store.dispatch(action);
            }
        }
    }
    /**
     * Initial state with extended properties
     *
     * @type {IGetRequiredDataForOrderState}
     * @memberof GetRequiredDataForOrder
     */
    get initialState() {
        return {
            isFetching: false,
            request: null,
            response: null,
            error: null,
            chainStarter: this.name,
            pickDiscountProducts: null,
            productRequirementProducts: null,
            serialNumbers: null,
            productBundles: null,
            customerValid: true,
            customerIssues: [],
            orderValid: true,
            orderIssues: [],
            orderLinesValid: true,
            orderLineIssues: {},
        };
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetRequiredDataForOrder
     */
    *saga() {
        yield fork(listener$42);
    }
    /**
     * Retrieves the requested data from the EVA backend
     *
     * @public
     * @param {*} action The Redux action
     * @returns {Promise<EVA.Core.GetRequiredDataForOrderResponse>}
     * @memberof GetBundleProducts
     */
    fetchData(action) {
        // Update theRequiredFor if supplied
        //
        this.theRequiredFor = get(action, 'payload.request.RequiredFor', this.theRequiredFor);
        // Call the base class as usual
        //
        return super.fetchData(action);
    }
    /**
     * The extended collection of reducer functions
     *
     * @protected
     * @returns {{ [ reducerName: string ]: ( state: any, action: AnyAction ) => any }}
     * @memberof GetRequiredDataForOrder
     */
    getReducerFunctions() {
        const baseReducerFuncs = super.getReducerFunctions();
        baseReducerFuncs.pickDiscountProducts = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const currentCart = get(getShoppingCart.getState(), 'response');
                    const currentLines = get(currentCart, 'ShoppingCart.Lines');
                    const payload = get(action, 'payload');
                    // Find the requirements of type 'PickDiscountProduct'
                    //
                    const pickDiscountProducts = {};
                    each(get(payload, 'RequiredData.OrderLines'), (orderLineRequirements, orderLineId) => {
                        each(orderLineRequirements, (orderLineRequirement) => {
                            if (orderLineRequirement.Name === 'PickDiscountProduct') {
                                // It really shouldn't happen that the discount line is not in
                                // the current cart. The chain ordering should ensure this
                                // We will cast it here to force typescript to compile
                                //
                                const discountLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(orderLineId, 10));
                                const productLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === get(discountLine, 'ParentID'));
                                pickDiscountProducts[orderLineId] = {
                                    requirement: orderLineRequirement,
                                    discountOrderLine: discountLine,
                                    productOrderLine: productLine,
                                };
                            }
                        });
                    });
                    return isEmpty(pickDiscountProducts) ? null : pickDiscountProducts;
                default:
                    return state;
            }
        };
        baseReducerFuncs.productRequirementProducts = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const currentCart = get(getShoppingCart.getState(), 'response');
                    const currentLines = get(currentCart, 'ShoppingCart.Lines');
                    const payload = get(action, 'payload');
                    const currentProductRequirementValues = getProductRequirementValuesForOrder.getState();
                    // Find the requirements of type 'ProductRequirement'
                    //
                    const productRequirementProducts = {};
                    each(get(payload, 'RequiredData.OrderLines'), (orderLineRequirements, orderLineId) => {
                        each(orderLineRequirements, (orderLineRequirement) => {
                            if (orderLineRequirement.Name === 'ProductRequirement') {
                                const productLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(orderLineId, 10));
                                productRequirementProducts[orderLineId] = {
                                    requirement: orderLineRequirement,
                                    productOrderLine: productLine,
                                    values: get(currentProductRequirementValues, `response.Requirements.${orderLineId}`),
                                };
                            }
                        });
                    });
                    return isEmpty(productRequirementProducts) ? null : productRequirementProducts;
                case getType(getProductRequirementValuesForOrder.actions.response):
                    const newProductRequirementValues = get(action, 'payload');
                    const currentState = this.getState();
                    // Update any requirement values as needed
                    //
                    each(currentState.productRequirementProducts, (productRequirementState, requirementOrderLineId) => {
                        productRequirementState.values = get(newProductRequirementValues, `Requirements.${requirementOrderLineId}`);
                    });
                default:
                    return state;
            }
        };
        baseReducerFuncs.serialNumbers = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const currentCart = get(getShoppingCart.getState(), 'response');
                    const currentLines = get(currentCart, 'ShoppingCart.Lines');
                    const payload = get(action, 'payload');
                    // Find the requirements of type 'SerialNumber'
                    //
                    const serialNumbers = {};
                    each(get(payload, 'RequiredData.OrderLines'), (orderLineRequirements, orderLineId) => {
                        each(orderLineRequirements, (orderLineRequirement) => {
                            if (orderLineRequirement.Name === 'SerialNumber') {
                                const productLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(orderLineId, 10));
                                serialNumbers[orderLineId] = {
                                    requirement: orderLineRequirement,
                                    productOrderLine: productLine,
                                    serialNumber: get(productLine, 'SerialNumber'),
                                };
                            }
                        });
                    });
                    return isEmpty(serialNumbers) ? null : serialNumbers;
                default:
                    return state;
            }
        };
        baseReducerFuncs.productBundles = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const currentCart = get(getShoppingCart.getState(), 'response');
                    const currentLines = get(currentCart, 'ShoppingCart.Lines');
                    const payload = get(action, 'payload');
                    // Find the requirements of type 'CompleteProductBundle'
                    //
                    const productBundles = {};
                    each(get(payload, 'RequiredData.OrderLines'), (orderLineRequirements, orderLineId) => {
                        each(orderLineRequirements, (orderLineRequirement) => {
                            if (orderLineRequirement.Name === 'CompleteProductBundle') {
                                const productLine = find(currentLines, (orderLine) => parseInt(`${orderLine.ID}`, 10) === parseInt(orderLineId, 10));
                                productBundles[orderLineId] = {
                                    requirement: orderLineRequirement,
                                    productOrderLine: productLine,
                                };
                            }
                        });
                    });
                    return isEmpty(productBundles) ? null : productBundles;
                default:
                    return state;
            }
        };
        baseReducerFuncs.customerValid = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    // Find invalid customer requirements
                    //
                    const requirements = get(payload, 'RequiredData.Customer');
                    const invalidRequirements = filter(requirements, { IsValid: false });
                    return invalidRequirements.length === 0;
                default:
                    return state;
            }
        };
        baseReducerFuncs.customerIssues = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    // Find invalid customer requirements
                    //
                    const requirements = get(payload, 'RequiredData.Customer');
                    const invalidRequirements = filter(requirements, { IsValid: false });
                    return invalidRequirements;
                default:
                    return state;
            }
        };
        baseReducerFuncs.orderValid = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    // Find invalid order requirements
                    //
                    const requirements = get(payload, 'RequiredData.Order');
                    const invalidOrderRequirements = filter(requirements, { IsValid: false });
                    return invalidOrderRequirements.length === 0;
                default:
                    return state;
            }
        };
        baseReducerFuncs.orderIssues = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    // Find invalid order requirements
                    //
                    const requirements = get(payload, 'RequiredData.Order');
                    const invalidRequirements = filter(requirements, { IsValid: false });
                    return invalidRequirements;
                default:
                    return state;
            }
        };
        baseReducerFuncs.orderLinesValid = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    // Find invalid order line requirements
                    // Order line requirements are index by their orderLineId
                    //
                    const requirements = get(payload, 'RequiredData.OrderLines');
                    const invalidRequirements = pickBy(requirements, (orderLineRequirements) => {
                        return !!find(orderLineRequirements, { IsValid: false });
                    });
                    return size(invalidRequirements) === 0;
                default:
                    return state;
            }
        };
        baseReducerFuncs.orderLineIssues = (state = null, action) => {
            switch (action.type) {
                case getType(this.actions.response):
                    const payload = get(action, 'payload');
                    // Find invalid order line requirements
                    // Order line requirements are index by their orderLineId
                    //
                    const requirements = get(payload, 'RequiredData.OrderLines');
                    const invalidRequirements = pickBy(requirements, (orderLineRequirements) => {
                        return !!find(orderLineRequirements, { IsValid: false });
                    });
                    // Only return invalid requirements per order line
                    //
                    return mapValues(invalidRequirements, (orderLineRequirements) => {
                        return filter(orderLineRequirements, { IsValid: false });
                    });
                default:
                    return state;
            }
        };
        return baseReducerFuncs;
    }
}
// Export the state handling singleton
//
const getRequiredDataForOrder = new GetRequiredDataForOrder();
// Setup the Saga implementation
//
function* fetchCall$42(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    // Apply requiredFor if not supplied
    //
    set(action, 'payload.request.RequiredFor', get(action, 'payload.request.RequiredFor', getRequiredDataForOrder.requiredFor));
    try {
        const data = yield call([getRequiredDataForOrder, 'fetchData'], action);
        yield put(getRequiredDataForOrder.actions.response(data));
        const requiredDataState = getRequiredDataForOrder.getState();
        const shoppingCartState = getShoppingCart.getState();
        const chainStarter = action.payload.chainStarter;
        const orderId = get(action, 'payload.request.OrderID');
        const order = get(shoppingCartState, 'response.ShoppingCart');
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Check if we have pick discount products
        // If so fetch the product options for each line
        //
        let optionalGetPotentialDiscountsForShoppingCartChainPromise = Promise.resolve(undefined);
        if (!isEmpty(requiredDataState.pickDiscountProducts)) {
            log$1('[EVA-SDK-REDUX:getRequiredDataForOrder] Fetching pick discount product options');
            const [getPickProductDiscountOptionsForOrderAction, , getPickProductDiscountOptionsForOrderChainPromise] = getPickProductDiscountOptionsForOrder.createFetchAction({ OrderID: orderId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
            yield put(getPickProductDiscountOptionsForOrderAction);
            optionalGetPotentialDiscountsForShoppingCartChainPromise = getPickProductDiscountOptionsForOrderChainPromise;
        }
        else {
            // Empty out any old order required data if cart has no order ID at this point
            // Could in theory happen if the employee receives a dummy cart
            //
            const emptyData = {
                OptionsPerLine: {},
            };
            yield put(getPickProductDiscountOptionsForOrder.actions.response(emptyData));
        }
        // Product requirement values are retrieved for the entire order and the
        // corresponding value is reduced as needed into the IProductRequirementState
        //
        // Serial numbers order line requirement current values are reduced from
        // the OrderLineDTO values
        //
        // If this is a return to supplier order we should fetch the current data
        //
        // export enum OrderCharacteristics {
        //   None = 0,
        //   IsPickup = 1,
        //   CreatedByEmployee = 2,
        //   IsInterbranch = 4,
        //   IsB2B = 8,
        //   Autocompleted = 32,
        //   CreatedFromWishList = 64,
        //   ReturnToSupplier = 128,
        // }
        //
        let optionalGetReturnToSupplierDataChainPromise = Promise.resolve(undefined);
        if (get(order, 'Characteristics') & 128) {
            log$1('[EVA-SDK-REDUX:getRequiredDataForOrder] Fetching return to supplier data');
            const [getReturnToSupplierDataAction, , getReturnToSupplierDataChainPromise] = getReturnToSupplierData.createFetchAction({ OrderID: orderId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
            yield put(getReturnToSupplierDataAction);
            optionalGetReturnToSupplierDataChainPromise = getReturnToSupplierDataChainPromise;
        }
        // Resolve the chain
        //
        Promise.all([optionalGetPotentialDiscountsForShoppingCartChainPromise, optionalGetReturnToSupplierDataChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(getRequiredDataForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$42() {
    yield takeEvery(getType(getRequiredDataForOrder.actions.fetch), fetchCall$42);
}

/**
 * Implements GetReturnOrdersForOrder fetch for the Redux store
 *
 * @export
 * @class GetReturnOrdersForOrder
 * @extends {StoreFetchableData<EVA.Core.GetReturnOrdersForOrder, EVA.Core.GetReturnOrdersForOrderResponse>}
 */
class GetReturnOrdersForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetReturnOrdersForOrder
     *
     * @memberof GetReturnOrdersForOrder
     */
    constructor() {
        super('getReturnOrdersForOrder', '/message/GetReturnOrdersForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetReturnOrdersForOrder
     */
    *saga() {
        yield fork(listener$41);
    }
}
// Export the state handling singleton
//
const getReturnOrdersForOrder = new GetReturnOrdersForOrder();
// Setup the Saga implementation
//
function* fetchCall$41(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getReturnOrdersForOrder, 'fetchData'], action);
        yield put(getReturnOrdersForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getReturnOrdersForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$41() {
    yield takeEvery(getType(getReturnOrdersForOrder.actions.fetch), fetchCall$41);
}

/**
 * Implements GetReturnReasons fetch for the Redux store
 *
 * @export
 * @class GetReturnReasons
 * @extends {StoreFetchableData<EVA.Core.Management.GetReturnReasons, EVA.Core.Management.GetReturnReasonsResponse>}
 */
class GetReturnReasons extends StoreFetchableData {
    /**
     * Creates an instance of GetReturnReasons
     *
     * @memberof GetReturnReasons
     */
    constructor() {
        super('getReturnReasons', '/message/GetReturnReasons');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetReturnReasons
     */
    *saga() {
        yield fork(listener$40);
    }
}
// Export the state handling singleton
//
const getReturnReasons = new GetReturnReasons();
// Setup the Saga implementation
//
function* fetchCall$40(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getReturnReasons, 'fetchData'], action);
        yield put(getReturnReasons.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getReturnReasons.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$40() {
    yield takeEvery(getType(getReturnReasons.actions.fetch), fetchCall$40);
}

/**
 * Implements GetReturnableStatusForOrder fetch for the Redux store
 *
 * @export
 * @class GetReturnableStatusForOrder
 * @extends {StoreFetchableData<EVA.Core.GetReturnableStatusForOrder, EVA.Core.GetReturnableStatusForOrderResponse>}
 */
class GetReturnableStatusForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetReturnableStatusForOrder
     *
     * @memberof GetReturnableStatusForOrder
     */
    constructor() {
        super('getReturnableStatusForOrder', '/message/GetReturnableStatusForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetReturnableStatusForOrder
     */
    *saga() {
        yield fork(listener$3$);
    }
}
// Export the state handling singleton
//
const getReturnableStatusForOrder = new GetReturnableStatusForOrder();
// Setup the Saga implementation
//
function* fetchCall$3$(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getReturnableStatusForOrder, 'fetchData'], action);
        yield put(getReturnableStatusForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getReturnableStatusForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3$() {
    yield takeEvery(getType(getReturnableStatusForOrder.actions.fetch), fetchCall$3$);
}

/**
 * Implements GetSalesTaxEstimateForOrder fetch for the Redux store
 *
 * @export
 * @class GetSalesTaxEstimateForOrder
 * @extends {StoreFetchableData<EVA.Core.GetSalesTaxEstimateForOrder, EVA.Core.GetSalesTaxEstimateForOrderResponse>}
 */
class GetSalesTaxEstimateForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetSalesTaxEstimateForOrder
     *
     * @memberof GetSalesTaxEstimateForOrder
     */
    constructor() {
        super('getSalesTaxEstimateForOrder', '/message/GetSalesTaxEstimateForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetSalesTaxEstimateForOrder
     */
    *saga() {
        yield fork(listener$3_);
    }
}
// Export the state handling singleton
//
const getSalesTaxEstimateForOrder = new GetSalesTaxEstimateForOrder();
// Setup the Saga implementation
//
function* fetchCall$3_(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getSalesTaxEstimateForOrder, 'fetchData'], action);
        yield put(getSalesTaxEstimateForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getSalesTaxEstimateForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3_() {
    yield takeEvery(getType(getSalesTaxEstimateForOrder.actions.fetch), fetchCall$3_);
}

/**
 * Implements GetServiceProducts fetch for the Redux store
 * This is a pre-configured filtered SearchProducts call
 * ProductType 16 = Service produc
 *
 * @export
 * @class GetServiceProducts
 * @extends {StoreFetchableData<EVA.Core.SearchProducts, EVA.Core.SearchProductsResponse>}
 */
class GetServiceProducts extends StoreFetchableData {
    /**
     * Creates an instance of GetServiceProducts
     *
     * @memberof GetServiceProducts
     */
    constructor() {
        super('getServiceProducts', '/message/SearchProducts', undefined, { addDefaultProductPropertiesPath: 'IncludedFields' });
    }
    /**
     * Retrieves the requested data from the EVA backend
     *
     * @public
     * @param {*} action The Redux action
     * @returns {Promise<EVA.Core.SearchProductsResponse>}
     * @memberof GetServiceProducts
     */
    fetchData(action) {
        // The action supplied is a SearchProducts because that is the most
        // convenient way to select add additional filtering
        //
        const searchPayload = get(action, 'payload.request') || {};
        // So there are two types of service products:
        // * regular priced (service product type: 16)
        // * variable priced (service product type: 16 and 64)
        //
        extend(searchPayload, { Filters: { product_types: { Values: [16, 64] } } });
        set(action, 'payload.request', searchPayload);
        return super.fetchData(action);
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetServiceProducts
     */
    *saga() {
        yield fork(listener$3Z);
    }
}
// Export the state handling singleton
//
const getServiceProducts = new GetServiceProducts();
// Setup the Saga implementation
//
function* fetchCall$3Z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getServiceProducts, 'fetchData'], action);
        yield put(getServiceProducts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getServiceProducts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3Z() {
    yield takeEvery(getType(getServiceProducts.actions.fetch), fetchCall$3Z);
}

/**
 * Implements GetShipmentDetails fetch for the Redux store
 *
 * @export
 * @class GetShipmentDetails
 * @extends {StoreFetchableData<EVA.Core.GetShipmentDetails, EVA.Core.GetShipmentDetailsResponse>}
 */
class GetShipmentDetails extends StoreFetchableData {
    /**
     * Creates an instance of GetShipmentDetails
     *
     * @memberof GetShipmentDetails
     */
    constructor() {
        super('getShipmentDetails', '/message/GetShipmentDetails');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetShipmentDetails
     */
    *saga() {
        yield fork(listener$3Y);
    }
}
// Export the state handling singleton
//
const getShipmentDetails = new GetShipmentDetails();
// Setup the Saga implementation
//
function* fetchCall$3Y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getShipmentDetails, 'fetchData'], action);
        yield put(getShipmentDetails.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getShipmentDetails.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3Y() {
    yield takeEvery(getType(getShipmentDetails.actions.fetch), fetchCall$3Y);
}

/**
 * Implements GetShopsByProximity fetch for the Redux store
 *
 * @export
 * @class GetShopsByProximity
 * @extends {StoreFetchableData<EVA.Core.GetShopsByProximity, EVA.Core.GetShopsByProximityResponse>}
 */
class GetShopsByProximity extends StoreFetchableData {
    /**
     * Creates an instance of GetShopsByProximity
     *
     * @memberof GetShopsByProximity
     */
    constructor() {
        super('getShopsByProximity', '/message/GetShopsByProximity');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetShopsByProximity
     */
    *saga() {
        yield fork(listener$3X);
    }
}
// Export the state handling singleton
//
const getShopsByProximity = new GetShopsByProximity();
// Setup the Saga implementation
//
function* fetchCall$3X(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getShopsByProximity, 'fetchData'], action);
        yield put(getShopsByProximity.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getShopsByProximity.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3X() {
    yield takeEvery(getType(getShopsByProximity.actions.fetch), fetchCall$3X);
}

/**
 * Implements GetStation fetch for the Redux store
 *
 * @export
 * @class GetStation
 * @extends {StoreFetchableData<EVA.Core.GetStation, EVA.Core.GetStationResponse>}
 */
class GetStation extends StoreFetchableData {
    /**
     * Creates an instance of GetStation
     *
     * @memberof GetStation
     */
    constructor() {
        super('getStation', '/message/GetStation');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetStation
     */
    *saga() {
        yield fork(listener$3W);
    }
}
// Export the state handling singleton
//
const getStation = new GetStation();
// Setup the Saga implementation
//
function* fetchCall$3W(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getStation, 'fetchData'], action);
        yield put(getStation.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getStation.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3W() {
    yield takeEvery(getType(getStation.actions.fetch), fetchCall$3W);
}

/**
 * Implements GetStatusForZonedCycleCountPreCounts fetch for the Redux store
 *
 * @export
 * @class GetStatusForZonedCycleCountPreCounts
 * @extends {StoreFetchableData<EVA.Core.GetStatusForZonedCycleCountPreCounts, EVA.Core.GetStatusForZonedCycleCountPreCountsResponse>}
 */
class GetStatusForZonedCycleCountPreCounts extends StoreFetchableData {
    /**
     * Creates an instance of GetStatusForZonedCycleCountPreCounts
     *
     * @memberof GetStatusForZonedCycleCountPreCounts
     */
    constructor() {
        super('getStatusForZonedCycleCountPreCounts', '/message/GetStatusForZonedCycleCountPreCounts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetStatusForZonedCycleCountPreCounts
     */
    *saga() {
        yield fork(listener$3V);
    }
}
// Export the state handling singleton
//
const getStatusForZonedCycleCountPreCounts = new GetStatusForZonedCycleCountPreCounts();
// Setup the Saga implementation
//
function* fetchCall$3V(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getStatusForZonedCycleCountPreCounts, 'fetchData'], action);
        yield put(getStatusForZonedCycleCountPreCounts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getStatusForZonedCycleCountPreCounts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3V() {
    yield takeEvery(getType(getStatusForZonedCycleCountPreCounts.actions.fetch), fetchCall$3V);
}

/**
 * Implements GetStockAvailabilityEstimateForOrder fetch for the Redux store
 *
 * @export
 * @class GetStockAvailabilityEstimateForOrder
 * @extends {StoreFetchableData<EVA.Core.GetStockAvailabilityEstimateForOrder, EVA.Core.GetStockAvailabilityEstimateForOrderResponse>}
 */
class GetStockAvailabilityEstimateForOrder extends StoreFetchableData {
    /**
     * Creates an instance of GetStockAvailabilityEstimateForOrder
     *
     * @memberof GetStockAvailabilityEstimateForOrder
     */
    constructor() {
        super('getStockAvailabilityEstimateForOrder', '/message/GetStockAvailabilityEstimateForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetStockAvailabilityEstimateForOrder
     */
    *saga() {
        yield fork(listener$3U);
    }
}
// Export the state handling singleton
//
const getStockAvailabilityEstimateForOrder = new GetStockAvailabilityEstimateForOrder();
// Setup the Saga implementation
//
function* fetchCall$3U(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getStockAvailabilityEstimateForOrder, 'fetchData'], action);
        yield put(getStockAvailabilityEstimateForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getStockAvailabilityEstimateForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3U() {
    yield takeEvery(getType(getStockAvailabilityEstimateForOrder.actions.fetch), fetchCall$3U);
}

/**
 * Implements GetStockAvailabilityEstimateForProducts fetch for the Redux store
 *
 * @export
 * @class GetStockAvailabilityEstimateForProducts
 * @extends {StoreFetchableData<EVA.Core.GetStockAvailabilityEstimateForProducts, EVA.Core.GetStockAvailabilityEstimateForProductsResponse>}
 */
class GetStockAvailabilityEstimateForProducts extends StoreFetchableData {
    /**
     * Creates an instance of GetStockAvailabilityEstimateForProducts
     *
     * @memberof GetStockAvailabilityEstimateForProducts
     */
    constructor() {
        super('getStockAvailabilityEstimateForProducts', '/message/GetStockAvailabilityEstimateForProducts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetStockAvailabilityEstimateForProducts
     */
    *saga() {
        yield fork(listener$3T);
    }
}
// Export the state handling singleton
//
const getStockAvailabilityEstimateForProducts = new GetStockAvailabilityEstimateForProducts();
// Setup the Saga implementation
//
function* fetchCall$3T(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getStockAvailabilityEstimateForProducts, 'fetchData'], action);
        yield put(getStockAvailabilityEstimateForProducts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getStockAvailabilityEstimateForProducts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3T() {
    yield takeEvery(getType(getStockAvailabilityEstimateForProducts.actions.fetch), fetchCall$3T);
}

/**
 * Implements GetStockAvailabilityTimeline fetch for the Redux store
 *
 * @export
 * @class GetStockAvailabilityTimeline
 * @extends {StoreFetchableData<EVA.Core.GetStockAvailabilityTimeline, EVA.Core.GetStockAvailabilityTimelineResponse>}
 */
class GetStockAvailabilityTimeline extends StoreFetchableData {
    /**
     * Creates an instance of GetStockAvailabilityTimeline
     *
     * @memberof GetStockAvailabilityTimeline
     */
    constructor() {
        super('getStockAvailabilityTimeline', '/message/GetStockAvailabilityTimeline');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetStockAvailabilityTimeline
     */
    *saga() {
        yield fork(listener$3S);
    }
}
// Export the state handling singleton
//
const getStockAvailabilityTimeline = new GetStockAvailabilityTimeline();
// Setup the Saga implementation
//
function* fetchCall$3S(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getStockAvailabilityTimeline, 'fetchData'], action);
        yield put(getStockAvailabilityTimeline.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getStockAvailabilityTimeline.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3S() {
    yield takeEvery(getType(getStockAvailabilityTimeline.actions.fetch), fetchCall$3S);
}

/**
 * Implements GetStockLabels fetch for the Redux store
 *
 * @export
 * @class GetStockLabels
 * @extends {StoreFetchableData<EVA.Core.GetStockLabels, EVA.Core.GetStockLabelsResponse>}
 */
class GetStockLabels extends StoreFetchableData {
    /**
     * Creates an instance of GetStockLabels
     *
     * @memberof GetStockLabels
     */
    constructor() {
        super('getStockLabels', '/message/GetStockLabels');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetStockLabels
     */
    *saga() {
        yield fork(listener$3R);
    }
}
// Export the state handling singleton
//
const getStockLabels = new GetStockLabels();
// Setup the Saga implementation
//
function* fetchCall$3R(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getStockLabels, 'fetchData'], action);
        yield put(getStockLabels.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getStockLabels.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3R() {
    yield takeEvery(getType(getStockLabels.actions.fetch), fetchCall$3R);
}

/**
 * Implements GetStockMutationReasons fetch for the Redux store
 *
 * @export
 * @class GetStockMutationReasons
 * @extends {StoreFetchableData<EVA.Core.GetEnumValues, EVA.Core.GetEnumValuesResponse>}
 */
class GetStockMutationReasons extends StoreFetchableData {
    /**
     * Creates an instance of GetStockMutationReasons
     *
     * @memberof GetStockMutationReasons
     */
    constructor() {
        super('getStockMutationReasons', '/message/GetEnumValues');
    }
    /**
     * Retrieves the requested data from the EVA backend
     *
     * @public
     * @param {*} action The Redux action
     * @returns {Promise<EVA.Core.GetEnumValuesResponse>}
     * @memberof GetStockMutationReasons
     */
    fetchData(action) {
        // Build the list enum values payload for stock mutation reasons
        //
        const listEnumValuesPayload = {
            Name: 'StockMutationReasons',
        };
        set(action, 'payload.request', listEnumValuesPayload);
        return super.fetchData(action);
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetStockMutationReasons
     */
    *saga() {
        yield fork(listener$3Q);
    }
}
// Export the state handling singleton
//
const getStockMutationReasons = new GetStockMutationReasons();
// Setup the Saga implementation
//
function* fetchCall$3Q(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getStockMutationReasons, 'fetchData'], action);
        yield put(getStockMutationReasons.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getStockMutationReasons.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3Q() {
    yield takeEvery(getType(getStockMutationReasons.actions.fetch), fetchCall$3Q);
}

/**
 * Implements GetStockNotification fetch for the Redux store
 *
 * @export
 * @class GetStockNotification
 * @extends {StoreFetchableData<EVA.Core.GetStockNotification, EVA.Core.StockNotificationResponse>}
 */
class GetStockNotification extends StoreFetchableData {
    /**
     * Creates an instance of GetStockNotification
     *
     * @memberof GetStockNotification
     */
    constructor() {
        super('getStockNotification', '/message/GetStockNotification');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetStockNotification
     */
    *saga() {
        yield fork(listener$3P);
    }
}
// Export the state handling singleton
//
const getStockNotification = new GetStockNotification();
// Setup the Saga implementation
//
function* fetchCall$3P(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getStockNotification, 'fetchData'], action);
        yield put(getStockNotification.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getStockNotification.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3P() {
    yield takeEvery(getType(getStockNotification.actions.fetch), fetchCall$3P);
}

/**
 * Implements GetStockNotificationForCurrentUser fetch for the Redux store
 *
 * @export
 * @class GetStockNotificationForCurrentUser
 * @extends {StoreFetchableData<EVA.Core.GetStockNotificationForCurrentUser, EVA.Core.StockNotificationResponse>}
 */
class GetStockNotificationForCurrentUser extends StoreFetchableData {
    /**
     * Creates an instance of GetStockNotificationForCurrentUser
     *
     * @memberof GetStockNotificationForCurrentUser
     */
    constructor() {
        super('getStockNotificationForCurrentUser', '/message/GetStockNotificationForCurrentUser');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetStockNotificationForCurrentUser
     */
    *saga() {
        yield fork(listener$3O);
    }
}
// Export the state handling singleton
//
const getStockNotificationForCurrentUser = new GetStockNotificationForCurrentUser();
// Setup the Saga implementation
//
function* fetchCall$3O(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getStockNotificationForCurrentUser, 'fetchData'], action);
        yield put(getStockNotificationForCurrentUser.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getStockNotificationForCurrentUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3O() {
    yield takeEvery(getType(getStockNotificationForCurrentUser.actions.fetch), fetchCall$3O);
}

/**
 * Implements GetStockReplenishmentTaskData fetch for the Redux store
 *
 * @export
 * @class GetStockReplenishmentTaskData
 * @extends {StoreFetchableData<EVA.Core.GetStockReplenishmentTaskData, EVA.Core.GetStockReplenishmentTaskDataResponse>}
 */
class GetStockReplenishmentTaskData extends StoreFetchableData {
    /**
     * Creates an instance of GetStockReplenishmentTaskData
     *
     * @memberof GetStockReplenishmentTaskData
     */
    constructor() {
        super('getStockReplenishmentTaskData', '/message/GetStockReplenishmentTaskData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetStockReplenishmentTaskData
     */
    *saga() {
        yield fork(listener$3N);
    }
}
// Export the state handling singleton
//
const getStockReplenishmentTaskData = new GetStockReplenishmentTaskData();
// Setup the Saga implementation
//
function* fetchCall$3N(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getStockReplenishmentTaskData, 'fetchData'], action);
        yield put(getStockReplenishmentTaskData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getStockReplenishmentTaskData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3N() {
    yield takeEvery(getType(getStockReplenishmentTaskData.actions.fetch), fetchCall$3N);
}

/**
 * Implements GetStockReplenishmentTaskZones fetch for the Redux store
 *
 * @export
 * @class GetStockReplenishmentTaskZones
 * @extends {StoreFetchableData<EVA.Core.GetStockReplenishmentTaskZones, EVA.Core.GetStockReplenishmentTaskZonesResponse>}
 */
class GetStockReplenishmentTaskZones extends StoreFetchableData {
    /**
     * Creates an instance of GetStockReplenishmentTaskZones
     *
     * @memberof GetStockReplenishmentTaskZones
     */
    constructor() {
        super('getStockReplenishmentTaskZones', '/message/GetStockReplenishmentTaskZones');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetStockReplenishmentTaskZones
     */
    *saga() {
        yield fork(listener$3M);
    }
}
// Export the state handling singleton
//
const getStockReplenishmentTaskZones = new GetStockReplenishmentTaskZones();
// Setup the Saga implementation
//
function* fetchCall$3M(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getStockReplenishmentTaskZones, 'fetchData'], action);
        yield put(getStockReplenishmentTaskZones.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getStockReplenishmentTaskZones.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3M() {
    yield takeEvery(getType(getStockReplenishmentTaskZones.actions.fetch), fetchCall$3M);
}

/**
 * Implements GetTaxExemptionReasons fetch for the Redux store
 *
 * @export
 * @class GetTaxExemptionReasons
 * @extends {StoreFetchableData<EVA.Core.GetTaxExemptionReasons, EVA.Core.GetTaxExemptionReasonsResponse>}
 */
class GetTaxExemptionReasons extends StoreFetchableData {
    /**
     * Creates an instance of GetTaxExemptionReasons
     *
     * @memberof GetTaxExemptionReasons
     */
    constructor() {
        super('getTaxExemptionReasons', '/message/GetTaxExemptionReasons');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetTaxExemptionReasons
     */
    *saga() {
        yield fork(listener$3L);
    }
}
// Export the state handling singleton
//
const getTaxExemptionReasons = new GetTaxExemptionReasons();
// Setup the Saga implementation
//
function* fetchCall$3L(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getTaxExemptionReasons, 'fetchData'], action);
        yield put(getTaxExemptionReasons.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getTaxExemptionReasons.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3L() {
    yield takeEvery(getType(getTaxExemptionReasons.actions.fetch), fetchCall$3L);
}

/**
 * Implements GetTransportOrderLineData fetch for the Redux store
 *
 * @export
 * @class GetTransportOrderLineData
 * @extends {StoreFetchableData<EVA.Core.GetTransportOrderLineData, EVA.Core.GetTransportOrderLineDataResponse>}
 */
class GetTransportOrderLineData extends StoreFetchableData {
    /**
     * Creates an instance of GetTransportOrderLineData
     *
     * @memberof GetTransportOrderLineData
     */
    constructor() {
        super('getTransportOrderLineData', '/message/GetTransportOrderLineData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetTransportOrderLineData
     */
    *saga() {
        yield fork(listener$3K);
    }
}
// Export the state handling singleton
//
const getTransportOrderLineData = new GetTransportOrderLineData();
// Setup the Saga implementation
//
function* fetchCall$3K(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getTransportOrderLineData, 'fetchData'], action);
        yield put(getTransportOrderLineData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getTransportOrderLineData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3K() {
    yield takeEvery(getType(getTransportOrderLineData.actions.fetch), fetchCall$3K);
}

/**
 * Implements GetUser fetch for the Redux store
 *
 * @export
 * @class GetUser
 * @extends {StoreFetchableData<EVA.Core.GetUser, EVA.Core.GetUserResponse>}
 */
class GetUser extends StoreFetchableData {
    /**
     * Creates an instance of GetUser
     *
     * @memberof GetUser
     */
    constructor() {
        super('getUser', '/message/GetUser');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUser
     */
    *saga() {
        yield fork(listener$3J);
    }
}
// Export the state handling singleton
//
const getUser = new GetUser();
// Setup the Saga implementation
//
function* fetchCall$3J(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getUser, 'fetchData'], action);
        yield put(getUser.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3J() {
    yield takeEvery(getType(getUser.actions.fetch), fetchCall$3J);
}

/**
 * Implements GetUserBySocialSecurityNumber fetch for the Redux store
 *
 * @export
 * @class GetUserBySocialSecurityNumber
 * @extends {StoreFetchableData<EVA.Core.GetUserBySocialSecurityNumber, EVA.Core.GetUserBySocialSecurityNumberResponse>}
 */
class GetUserBySocialSecurityNumber extends StoreFetchableData {
    /**
     * Creates an instance of GetUserBySocialSecurityNumber
     *
     * @memberof GetUserBySocialSecurityNumber
     */
    constructor() {
        super('getUserBySocialSecurityNumber', '/message/GetUserBySocialSecurityNumber');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUserBySocialSecurityNumber
     */
    *saga() {
        yield fork(listener$3I);
    }
}
// Export the state handling singleton
//
const getUserBySocialSecurityNumber = new GetUserBySocialSecurityNumber();
// Setup the Saga implementation
//
function* fetchCall$3I(action) {
    const fetchActionPayload = action.payload;
    try {
        // There was check here for the settings.defaultCallUserType here
        // This has been removed as this service is always used to find customers
        // so checking against the employee SSN is not a good default.
        // If needed the caller can still specify it in the request
        //
        const data = yield call([getUserBySocialSecurityNumber, 'fetchData'], action);
        yield put(getUserBySocialSecurityNumber.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUserBySocialSecurityNumber.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3I() {
    yield takeEvery(getType(getUserBySocialSecurityNumber.actions.fetch), fetchCall$3I);
}

/**
 * Implements GetUserCardBalance fetch for the Redux store
 *
 * @export
 * @class GetUserCardBalance
 * @extends {StoreFetchableData<EVA.Payment.Core.GetUserCardBalance, EVA.Payment.Core.GetUserCardBalanceResponse>}
 */
class GetUserCardBalance extends StoreFetchableData {
    /**
     * Creates an instance of GetUserCardBalance
     *
     * @memberof GetUserCardBalance
     */
    constructor() {
        super('getUserCardBalance', '/message/GetUserCardBalance');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUserCardBalance
     */
    *saga() {
        yield fork(listener$3H);
    }
}
// Export the state handling singleton
//
const getUserCardBalance = new GetUserCardBalance();
// Setup the Saga implementation
//
function* fetchCall$3H(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getUserCardBalance, 'fetchData'], action);
        yield put(getUserCardBalance.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUserCardBalance.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3H() {
    yield takeEvery(getType(getUserCardBalance.actions.fetch), fetchCall$3H);
}

/**
 * Implements GetUserCardDetails fetch for the Redux store
 *
 * @export
 * @class GetUserCardDetails
 * @extends {StoreFetchableData<EVA.Payment.Core.GetUserCardDetails, EVA.Payment.Core.GetUserCardDetailsResponse>}
 */
class GetUserCardDetails extends StoreFetchableData {
    /**
     * Creates an instance of GetUserCardDetails
     *
     * @memberof GetUserCardDetails
     */
    constructor() {
        super('getUserCardDetails', '/message/GetUserCardDetails');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUserCardDetails
     */
    *saga() {
        yield fork(listener$3G);
    }
}
// Export the state handling singleton
//
const getUserCardDetails = new GetUserCardDetails();
// Setup the Saga implementation
//
function* fetchCall$3G(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getUserCardDetails, 'fetchData'], action);
        yield put(getUserCardDetails.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUserCardDetails.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3G() {
    yield takeEvery(getType(getUserCardDetails.actions.fetch), fetchCall$3G);
}

/**
 * Implements GetUserCardsForUser fetch for the Redux store
 *
 * @export
 * @class GetUserCardsForUser
 * @extends {StoreFetchableData<EVA.Payment.Core.GetUserCardsForUser, EVA.Payment.Core.GetUserCardsForUserResponse>}
 */
class GetUserCardsForUser extends StoreFetchableData {
    /**
     * Creates an instance of GetUserCardsForUser
     *
     * @memberof GetUserCardsForUser
     */
    constructor() {
        super('getUserCardsForUser', '/message/GetUserCardsForUser');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUserCardsForUser
     */
    *saga() {
        yield fork(listener$3F);
    }
}
// Export the state handling singleton
//
const getUserCardsForUser = new GetUserCardsForUser();
// Setup the Saga implementation
//
function* fetchCall$3F(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getUserCardsForUser, 'fetchData'], action);
        yield put(getUserCardsForUser.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUserCardsForUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3F() {
    yield takeEvery(getType(getUserCardsForUser.actions.fetch), fetchCall$3F);
}

/**
 * Implements GetUserInfoBySocialSecurityNumber fetch for the Redux store
 *
 * @export
 * @class GetUserInfoBySocialSecurityNumber
 * @extends {StoreFetchableData<EVA.Core.GetUserInfoBySocialSecurityNumber, EVA.Core.GetUserInfoBySocialSecurityNumberResponse>}
 */
class GetUserInfoBySocialSecurityNumber extends StoreFetchableData {
    /**
     * Creates an instance of GetUserInfoBySocialSecurityNumber
     *
     * @memberof GetUserInfoBySocialSecurityNumber
     */
    constructor() {
        super('getUserInfoBySocialSecurityNumber', '/message/GetUserInfoBySocialSecurityNumber');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUserInfoBySocialSecurityNumber
     */
    *saga() {
        yield fork(listener$3E);
    }
}
// Export the state handling singleton
//
const getUserInfoBySocialSecurityNumber = new GetUserInfoBySocialSecurityNumber();
// Setup the Saga implementation
//
function* fetchCall$3E(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getUserInfoBySocialSecurityNumber, 'fetchData'], action);
        yield put(getUserInfoBySocialSecurityNumber.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUserInfoBySocialSecurityNumber.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3E() {
    yield takeEvery(getType(getUserInfoBySocialSecurityNumber.actions.fetch), fetchCall$3E);
}

/**
 * Implements GetUserOrigins fetch for the Redux store
 *
 * @export
 * @class GetUserOrigins
 * @extends {StoreFetchableData<EVA.Core.Management.GetUserOrigins, EVA.Core.Management.GetUserOriginsResponse>}
 */
class GetUserOrigins extends StoreFetchableData {
    /**
     * Creates an instance of GetUserOrigins
     *
     * @memberof GetUserOrigins
     */
    constructor() {
        super('getUserOrigins', '/message/GetUserOrigins');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUserOrigins
     */
    *saga() {
        yield fork(listener$3D);
    }
}
// Export the state handling singleton
//
const getUserOrigins = new GetUserOrigins();
// Setup the Saga implementation
//
function* fetchCall$3D(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getUserOrigins, 'fetchData'], action);
        yield put(getUserOrigins.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUserOrigins.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3D() {
    yield takeEvery(getType(getUserOrigins.actions.fetch), fetchCall$3D);
}

/**
 * Implements GetUserRequirements fetch for the Redux store
 *
 * @export
 * @class GetUserRequirements
 * @extends {StoreFetchableData<EVA.Core.Management.GetUserRequirements, EVA.Core.Management.GetUserRequirementsResponse>}
 */
class GetUserRequirements extends StoreFetchableData {
    /**
     * Creates an instance of GetUserRequirements
     *
     * @memberof GetUserRequirements
     */
    constructor() {
        super('getUserRequirements', '/message/GetUserRequirements');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUserRequirements
     */
    *saga() {
        yield fork(listener$3C);
    }
}
// Export the state handling singleton
//
const getUserRequirements = new GetUserRequirements();
// Setup the Saga implementation
//
function* fetchCall$3C(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getUserRequirements, 'fetchData'], action);
        yield put(getUserRequirements.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUserRequirements.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3C() {
    yield takeEvery(getType(getUserRequirements.actions.fetch), fetchCall$3C);
}

/**
 * Implements GetUserRequirementsForSubscriptions fetch for the Redux store
 *
 * @export
 * @class GetUserRequirementsForSubscriptions
 * @extends {StoreFetchableData<EVA.Core.Management.GetUserRequirementsForSubscriptions, EVA.Core.Management.GetUserRequirementsForSubscriptionsResponse>}
 */
class GetUserRequirementsForSubscriptions extends StoreFetchableData {
    /**
     * Creates an instance of GetUserRequirementsForSubscriptions
     *
     * @memberof GetUserRequirementsForSubscriptions
     */
    constructor() {
        super('getUserRequirementsForSubscriptions', '/message/GetUserRequirementsForSubscriptions');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUserRequirementsForSubscriptions
     */
    *saga() {
        yield fork(listener$3B);
    }
}
// Export the state handling singleton
//
const getUserRequirementsForSubscriptions = new GetUserRequirementsForSubscriptions();
// Setup the Saga implementation
//
function* fetchCall$3B(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getUserRequirementsForSubscriptions, 'fetchData'], action);
        yield put(getUserRequirementsForSubscriptions.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUserRequirementsForSubscriptions.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3B() {
    yield takeEvery(getType(getUserRequirementsForSubscriptions.actions.fetch), fetchCall$3B);
}

/**
 * Implements GetUserSubscriptions fetch for the Redux store
 *
 * @export
 * @class GetUserSubscriptions
 * @extends {StoreFetchableData<EVA.Core.GetUserSubscriptions, EVA.Core.GetUserSubscriptionsResponse>}
 */
class GetUserSubscriptions extends StoreFetchableData {
    /**
     * Creates an instance of GetUserSubscriptions
     *
     * @memberof GetUserSubscriptions
     */
    constructor() {
        super('getUserSubscriptions', '/message/GetUserSubscriptions');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUserSubscriptions
     */
    *saga() {
        yield fork(listener$3A);
    }
}
// Export the state handling singleton
//
const getUserSubscriptions = new GetUserSubscriptions();
// Setup the Saga implementation
//
function* fetchCall$3A(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getUserSubscriptions, 'fetchData'], action);
        yield put(getUserSubscriptions.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUserSubscriptions.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3A() {
    yield takeEvery(getType(getUserSubscriptions.actions.fetch), fetchCall$3A);
}

/**
 * Implements GetUserTaskCounts fetch for the Redux store
 *
 * @export
 * @class GetUserTaskCounts
 * @extends {StoreFetchableData<EVA.Core.GetUserTaskCounts, EVA.Core.GetUserTaskCountsResponse>}
 */
class GetUserTaskCounts extends StoreFetchableData {
    /**
     * Creates an instance of GetUserTaskCounts
     *
     * @memberof GetUserTaskCounts
     */
    constructor() {
        super('getUserTaskCounts', '/message/GetUserTaskCounts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUserTaskCounts
     */
    *saga() {
        yield fork(listener$3z);
    }
}
// Export the state handling singleton
//
const getUserTaskCounts = new GetUserTaskCounts();
// Setup the Saga implementation
//
function* fetchCall$3z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getUserTaskCounts, 'fetchData'], action);
        yield put(getUserTaskCounts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUserTaskCounts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3z() {
    yield takeEvery(getType(getUserTaskCounts.actions.fetch), fetchCall$3z);
}

/**
 * Implements GetUserTaskDetails fetch for the Redux store
 *
 * @export
 * @class GetUserTaskDetails
 * @extends {StoreFetchableData<EVA.Core.GetUserTaskDetails, EVA.Core.GetUserTaskDetailsResponse>}
 */
class GetUserTaskDetails extends StoreFetchableData {
    /**
     * Creates an instance of GetUserTaskDetails
     *
     * @memberof GetUserTaskDetails
     */
    constructor() {
        super('getUserTaskDetails', '/message/GetUserTaskDetails');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUserTaskDetails
     */
    *saga() {
        yield fork(listener$3y);
    }
}
// Export the state handling singleton
//
const getUserTaskDetails = new GetUserTaskDetails();
// Setup the Saga implementation
//
function* fetchCall$3y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getUserTaskDetails, 'fetchData'], action);
        yield put(getUserTaskDetails.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUserTaskDetails.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3y() {
    yield takeEvery(getType(getUserTaskDetails.actions.fetch), fetchCall$3y);
}

/**
 * Implements GetUserTaskNotCompletedReasons fetch for the Redux store
 *
 * @export
 * @class GetUserTaskNotCompletedReasons
 * @extends {StoreFetchableData<EVA.Core.GetUserTaskNotCompletedReasons, EVA.Core.GetUserTaskNotCompletedReasonsResponse>}
 */
class GetUserTaskNotCompletedReasons extends StoreFetchableData {
    /**
     * Creates an instance of GetUserTaskNotCompletedReasons
     *
     * @memberof GetUserTaskNotCompletedReasons
     */
    constructor() {
        super('getUserTaskNotCompletedReasons', '/message/GetUserTaskNotCompletedReasons');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetUserTaskNotCompletedReasons
     */
    *saga() {
        yield fork(listener$3x);
    }
}
// Export the state handling singleton
//
const getUserTaskNotCompletedReasons = new GetUserTaskNotCompletedReasons();
// Setup the Saga implementation
//
function* fetchCall$3x(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getUserTaskNotCompletedReasons, 'fetchData'], action);
        yield put(getUserTaskNotCompletedReasons.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getUserTaskNotCompletedReasons.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3x() {
    yield takeEvery(getType(getUserTaskNotCompletedReasons.actions.fetch), fetchCall$3x);
}

/**
 * Implements GetWarehouseOrderData fetch for the Redux store
 *
 * @export
 * @class GetWarehouseOrderData
 * @extends {StoreFetchableData<EVA.Core.GetWarehouseOrderData, EVA.Core.GetWarehouseOrderDataResponse>}
 */
class GetWarehouseOrderData extends StoreFetchableData {
    /**
     * Creates an instance of GetWarehouseOrderData
     *
     * @memberof GetWarehouseOrderData
     */
    constructor() {
        super('getWarehouseOrderData', '/message/GetWarehouseOrderData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetWarehouseOrderData
     */
    *saga() {
        yield fork(listener$3w);
    }
}
// Export the state handling singleton
//
const getWarehouseOrderData = new GetWarehouseOrderData();
// Setup the Saga implementation
//
function* fetchCall$3w(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getWarehouseOrderData, 'fetchData'], action);
        yield put(getWarehouseOrderData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getWarehouseOrderData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3w() {
    yield takeEvery(getType(getWarehouseOrderData.actions.fetch), fetchCall$3w);
}

/**
 * Implements GetWebshops fetch for the Redux store
 *
 * @export
 * @class GetWebshops
 * @extends {StoreFetchableData<EVA.Core.GetWebshops, EVA.Core.GetWebshopsResponse>}
 */
class GetWebshops extends StoreFetchableData {
    /**
     * Creates an instance of GetWebshops
     *
     * @memberof GetWebshops
     */
    constructor() {
        super('getWebshops', '/message/GetWebshops');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetWebshops
     */
    *saga() {
        yield fork(listener$3v);
    }
}
// Export the state handling singleton
//
const getWebshops = new GetWebshops();
// Setup the Saga implementation
//
function* fetchCall$3v(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getWebshops, 'fetchData'], action);
        yield put(getWebshops.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getWebshops.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3v() {
    yield takeEvery(getType(getWebshops.actions.fetch), fetchCall$3v);
}

/**
 * Implements GetZonedCycleCount fetch for the Redux store
 *
 * @export
 * @class GetZonedCycleCount
 * @extends {StoreFetchableData<EVA.Core.GetZonedCycleCount, EVA.Core.GetZonedCycleCountResponse>}
 */
class GetZonedCycleCount extends StoreFetchableData {
    /**
     * Creates an instance of GetZonedCycleCount
     *
     * @memberof GetZonedCycleCount
     */
    constructor() {
        super('getZonedCycleCount', '/message/GetZonedCycleCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetZonedCycleCount
     */
    *saga() {
        yield fork(listener$3u);
    }
}
// Export the state handling singleton
//
const getZonedCycleCount = new GetZonedCycleCount();
// Setup the Saga implementation
//
function* fetchCall$3u(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getZonedCycleCount, 'fetchData'], action);
        yield put(getZonedCycleCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getZonedCycleCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3u() {
    yield takeEvery(getType(getZonedCycleCount.actions.fetch), fetchCall$3u);
}

/**
 * Implements GetZonedCycleCountPreCount fetch for the Redux store
 *
 * @export
 * @class GetZonedCycleCountPreCount
 * @extends {StoreFetchableData<EVA.Core.GetZonedCycleCountPreCount, EVA.Core.GetZonedCycleCountPreCountResponse>}
 */
class GetZonedCycleCountPreCount extends StoreFetchableData {
    /**
     * Creates an instance of GetZonedCycleCountPreCount
     *
     * @memberof GetZonedCycleCountPreCount
     */
    constructor() {
        super('getZonedCycleCountPreCount', '/message/GetZonedCycleCountPreCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GetZonedCycleCountPreCount
     */
    *saga() {
        yield fork(listener$3t);
    }
}
// Export the state handling singleton
//
const getZonedCycleCountPreCount = new GetZonedCycleCountPreCount();
// Setup the Saga implementation
//
function* fetchCall$3t(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([getZonedCycleCountPreCount, 'fetchData'], action);
        yield put(getZonedCycleCountPreCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(getZonedCycleCountPreCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3t() {
    yield takeEvery(getType(getZonedCycleCountPreCount.actions.fetch), fetchCall$3t);
}

/**
 * Implements GlobalBlueGetEligibilityInformation fetch for the Redux store
 *
 * @export
 * @class GlobalBlueGetEligibilityInformation
 * @extends {StoreFetchableData<EVA.GlobalBlue.GlobalBlueGetEligibilityInformation, EVA.GlobalBlue.GlobalBlueGetEligibilityInformationResponse>}
 */
class GlobalBlueGetEligibilityInformation extends StoreFetchableData {
    /**
     * Creates an instance of GlobalBlueGetEligibilityInformation
     *
     * @memberof GlobalBlueGetEligibilityInformation
     */
    constructor() {
        super('globalBlueGetEligibilityInformation', '/message/GlobalBlueGetEligibilityInformation');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GlobalBlueGetEligibilityInformation
     */
    *saga() {
        yield fork(listener$3s);
    }
}
// Export the state handling singleton
//
const globalBlueGetEligibilityInformation = new GlobalBlueGetEligibilityInformation();
// Setup the Saga implementation
//
function* fetchCall$3s(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([globalBlueGetEligibilityInformation, 'fetchData'], action);
        yield put(globalBlueGetEligibilityInformation.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(globalBlueGetEligibilityInformation.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3s() {
    yield takeEvery(getType(globalBlueGetEligibilityInformation.actions.fetch), fetchCall$3s);
}

/**
 * Implements GlobalBluePrintForm fetch for the Redux store
 *
 * @export
 * @class GlobalBluePrintForm
 * @extends {StoreFetchableData<EVA.GlobalBlue.GlobalBluePrintForm, EVA.Core.EmptyResponseMessage>}
 */
class GlobalBluePrintForm extends StoreFetchableData {
    /**
     * Creates an instance of GlobalBluePrintForm
     *
     * @memberof GlobalBluePrintForm
     */
    constructor() {
        super('globalBluePrintForm', '/message/GlobalBluePrintForm');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GlobalBluePrintForm
     */
    *saga() {
        yield fork(listener$3r);
    }
}
// Export the state handling singleton
//
const globalBluePrintForm = new GlobalBluePrintForm();
// Setup the Saga implementation
//
function* fetchCall$3r(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([globalBluePrintForm, 'fetchData'], action);
        yield put(globalBluePrintForm.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(globalBluePrintForm.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3r() {
    yield takeEvery(getType(globalBluePrintForm.actions.fetch), fetchCall$3r);
}

/**
 * Implements GlobalBlueSetGlobalBlueIdentifier fetch for the Redux store
 *
 * @export
 * @class GlobalBlueSetGlobalBlueIdentifier
 * @extends {StoreFetchableData<EVA.GlobalBlue.GlobalBlueSetGlobalBlueIdentifier, EVA.Core.EmptyResponseMessage>}
 */
class GlobalBlueSetGlobalBlueIdentifier extends StoreFetchableData {
    /**
     * Creates an instance of GlobalBlueSetGlobalBlueIdentifier
     *
     * @memberof GlobalBlueSetGlobalBlueIdentifier
     */
    constructor() {
        super('globalBlueSetGlobalBlueIdentifier', '/message/GlobalBlueSetGlobalBlueIdentifier');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GlobalBlueSetGlobalBlueIdentifier
     */
    *saga() {
        yield fork(listener$3q);
    }
}
// Export the state handling singleton
//
const globalBlueSetGlobalBlueIdentifier = new GlobalBlueSetGlobalBlueIdentifier();
// Setup the Saga implementation
//
function* fetchCall$3q(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([globalBlueSetGlobalBlueIdentifier, 'fetchData'], action);
        yield put(globalBlueSetGlobalBlueIdentifier.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(globalBlueSetGlobalBlueIdentifier.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3q() {
    yield takeEvery(getType(globalBlueSetGlobalBlueIdentifier.actions.fetch), fetchCall$3q);
}

/**
 * Implements GlobalBlueStartTaxFree fetch for the Redux store
 *
 * @export
 * @class GlobalBlueStartTaxFree
 * @extends {StoreFetchableData<EVA.GlobalBlue.GlobalBlueStartTaxFree, EVA.GlobalBlue.GlobalBlueStartTaxFreeResponse>}
 */
class GlobalBlueStartTaxFree extends StoreFetchableData {
    /**
     * Creates an instance of GlobalBlueStartTaxFree
     *
     * @memberof GlobalBlueStartTaxFree
     */
    constructor() {
        super('globalBlueStartTaxFree', '/message/GlobalBlueStartTaxFree');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof GlobalBlueStartTaxFree
     */
    *saga() {
        yield fork(listener$3p);
    }
}
// Export the state handling singleton
//
const globalBlueStartTaxFree = new GlobalBlueStartTaxFree();
// Setup the Saga implementation
//
function* fetchCall$3p(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([globalBlueStartTaxFree, 'fetchData'], action);
        yield put(globalBlueStartTaxFree.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(globalBlueStartTaxFree.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3p() {
    yield takeEvery(getType(globalBlueStartTaxFree.actions.fetch), fetchCall$3p);
}

/**
 * Implements JanrainRequestPasswordResetToken fetch for the Redux store
 *
 * @export
 * @class JanrainRequestPasswordResetToken
 * @extends {StoreFetchableData<EVA.Rituals.JanrainRequestPasswordResetToken, EVA.Rituals.JanrainRequestPasswordResetTokenResponse>}
 */
class JanrainRequestPasswordResetToken extends StoreFetchableData {
    /**
     * Creates an instance of JanrainRequestPasswordResetToken
     *
     * @memberof JanrainRequestPasswordResetToken
     */
    constructor() {
        super('janrainRequestPasswordResetToken', '/message/JanrainRequestPasswordResetToken');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof JanrainRequestPasswordResetToken
     */
    *saga() {
        yield fork(listener$3o);
    }
}
// Export the state handling singleton
//
const janrainRequestPasswordResetToken = new JanrainRequestPasswordResetToken();
// Setup the Saga implementation
//
function* fetchCall$3o(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([janrainRequestPasswordResetToken, 'fetchData'], action);
        yield put(janrainRequestPasswordResetToken.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(janrainRequestPasswordResetToken.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3o() {
    yield takeEvery(getType(janrainRequestPasswordResetToken.actions.fetch), fetchCall$3o);
}

/**
 * Implements JanrainResetPassword fetch for the Redux store
 *
 * @export
 * @class JanrainResetPassword
 * @extends {StoreFetchableData<EVA.Rituals.JanrainResetPassword, EVA.Core.EmptyResponseMessage>}
 */
class JanrainResetPassword extends StoreFetchableData {
    /**
     * Creates an instance of JanrainResetPassword
     *
     * @memberof JanrainResetPassword
     */
    constructor() {
        super('janrainResetPassword', '/message/JanrainResetPassword');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof JanrainResetPassword
     */
    *saga() {
        yield fork(listener$3n);
    }
}
// Export the state handling singleton
//
const janrainResetPassword = new JanrainResetPassword();
// Setup the Saga implementation
//
function* fetchCall$3n(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([janrainResetPassword, 'fetchData'], action);
        yield put(janrainResetPassword.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(janrainResetPassword.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3n() {
    yield takeEvery(getType(janrainResetPassword.actions.fetch), fetchCall$3n);
}

/**
 * Implements ListAddressBook fetch for the Redux store
 *
 * @export
 * @class ListAddressBook
 * @extends {StoreFetchableData<EVA.Core.ListAddressBook, EVA.Core.ListAddressBookResponse>}
 */
class ListAddressBook extends StoreFetchableData {
    /**
     * Creates an instance of ListAddressBook
     *
     * @memberof ListAddressBook
     */
    constructor() {
        super('listAddressBook', '/message/ListAddressBook');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListAddressBook
     */
    *saga() {
        yield fork(listener$3m);
    }
}
// Export the state handling singleton
//
const listAddressBook = new ListAddressBook();
// Setup the Saga implementation
//
function* fetchCall$3m(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listAddressBook, 'fetchData'], action);
        yield put(listAddressBook.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listAddressBook.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3m() {
    yield takeEvery(getType(listAddressBook.actions.fetch), fetchCall$3m);
}

/**
 * Implements ListAdyenCheckoutGateways fetch for the Redux store
 *
 * @export
 * @class ListAdyenCheckoutGateways
 * @extends {StoreFetchableData<EVA.Payment.Adyen.ListAdyenCheckoutGateways, EVA.Payment.Adyen.ListAdyenCheckoutGatewaysResponse>}
 */
class ListAdyenCheckoutGateways extends StoreFetchableData {
    /**
     * Creates an instance of ListAdyenCheckoutGateways
     *
     * @memberof ListAdyenCheckoutGateways
     */
    constructor() {
        super('listAdyenCheckoutGateways', '/message/ListAdyenCheckoutGateways');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListAdyenCheckoutGateways
     */
    *saga() {
        yield fork(listener$3l);
    }
}
// Export the state handling singleton
//
const listAdyenCheckoutGateways = new ListAdyenCheckoutGateways();
// Setup the Saga implementation
//
function* fetchCall$3l(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listAdyenCheckoutGateways, 'fetchData'], action);
        yield put(listAdyenCheckoutGateways.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listAdyenCheckoutGateways.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3l() {
    yield takeEvery(getType(listAdyenCheckoutGateways.actions.fetch), fetchCall$3l);
}

/**
 * Implements ListAppSettings fetch for the Redux store
 *
 * @export
 * @class ListAppSettings
 * @extends {StoreFetchableData<EVA.Core.Management.ListAppSettings, EVA.Core.Management.ListAppSettingsResponse>}
 */
class ListAppSettings extends StoreFetchableData {
    /**
     * Creates an instance of ListAppSettings
     *
     * @memberof ListAppSettings
     */
    constructor() {
        super('listAppSettings', '/message/ListAppSettings');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListAppSettings
     */
    *saga() {
        yield fork(listener$3k);
    }
}
// Export the state handling singleton
//
const listAppSettings = new ListAppSettings();
// Setup the Saga implementation
//
function* fetchCall$3k(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listAppSettings, 'fetchData'], action);
        yield put(listAppSettings.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listAppSettings.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3k() {
    yield takeEvery(getType(listAppSettings.actions.fetch), fetchCall$3k);
}

/**
 * Implements ListApplicationCultures fetch for the Redux store
 *
 * @export
 * @class ListApplicationCultures
 * @extends {StoreFetchableData<EVA.PIM.ListApplicationCultures, EVA.PIM.ListApplicationCulturesResponse>}
 */
class ListApplicationCultures extends StoreFetchableData {
    /**
     * Creates an instance of ListApplicationCultures
     *
     * @memberof ListApplicationCultures
     */
    constructor() {
        super('listApplicationCultures', '/message/ListApplicationCultures');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListApplicationCultures
     */
    *saga() {
        yield fork(listener$3j);
    }
}
// Export the state handling singleton
//
const listApplicationCultures = new ListApplicationCultures();
// Setup the Saga implementation
//
function* fetchCall$3j(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listApplicationCultures, 'fetchData'], action);
        yield put(listApplicationCultures.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listApplicationCultures.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3j() {
    yield takeEvery(getType(listApplicationCultures.actions.fetch), fetchCall$3j);
}

/**
 * Implements ListApplications fetch for the Redux store
 *
 * @export
 * @class ListApplications
 * @extends {StoreFetchableData<EVA.Core.ListApplications, EVA.Core.ListApplicationsResponse>}
 */
class ListApplications extends StoreFetchableData {
    /**
     * Creates an instance of ListApplications
     *
     * @memberof ListApplications
     */
    constructor() {
        super('listApplications', '/message/ListApplications');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListApplications
     */
    *saga() {
        yield fork(listener$3i);
    }
}
// Export the state handling singleton
//
const listApplications = new ListApplications();
// Setup the Saga implementation
//
function* fetchCall$3i(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listApplications, 'fetchData'], action);
        yield put(listApplications.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listApplications.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3i() {
    yield takeEvery(getType(listApplications.actions.fetch), fetchCall$3i);
}

/**
 * Implements ListAvailableShippingMethods fetch for the Redux store
 *
 * @export
 * @class ListAvailableShippingMethods
 * @extends {StoreFetchableData<EVA.Core.ListAvailableShippingMethods, EVA.Core.ListAvailableShippingMethodsResponse>}
 */
class ListAvailableShippingMethods extends StoreFetchableData {
    /**
     * Creates an instance of ListAvailableShippingMethods
     *
     * @memberof ListAvailableShippingMethods
     */
    constructor() {
        super('listAvailableShippingMethods', '/message/ListAvailableShippingMethods');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListAvailableShippingMethods
     */
    *saga() {
        yield fork(listener$3h);
    }
}
// Export the state handling singleton
//
const listAvailableShippingMethods = new ListAvailableShippingMethods();
// Setup the Saga implementation
//
function* fetchCall$3h(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listAvailableShippingMethods, 'fetchData'], action);
        yield put(listAvailableShippingMethods.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listAvailableShippingMethods.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3h() {
    yield takeEvery(getType(listAvailableShippingMethods.actions.fetch), fetchCall$3h);
}

/**
 * Implements ListBlobs fetch for the Redux store
 *
 * @export
 * @class ListBlobs
 * @extends {StoreFetchableData<EVA.Blobs.ListBlobs, EVA.Blobs.ListBlobsResponse>}
 */
class ListBlobs extends StoreFetchableData {
    /**
     * Creates an instance of ListBlobs
     *
     * @memberof ListBlobs
     */
    constructor() {
        super('listBlobs', '/message/ListBlobs');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListBlobs
     */
    *saga() {
        yield fork(listener$3g);
    }
}
// Export the state handling singleton
//
const listBlobs = new ListBlobs();
// Setup the Saga implementation
//
function* fetchCall$3g(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listBlobs, 'fetchData'], action);
        yield put(listBlobs.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listBlobs.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3g() {
    yield takeEvery(getType(listBlobs.actions.fetch), fetchCall$3g);
}

/**
 * Implements ListBlobsForOrder fetch for the Redux store
 *
 * @export
 * @class ListBlobsForOrder
 * @extends {StoreFetchableData<EVA.Core.ListBlobsForOrder, EVA.Core.ListBlobsForOrderResponse>}
 */
class ListBlobsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of ListBlobsForOrder
     *
     * @memberof ListBlobsForOrder
     */
    constructor() {
        super('listBlobsForOrder', '/message/ListBlobsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListBlobsForOrder
     */
    *saga() {
        yield fork(listener$3f);
    }
}
// Export the state handling singleton
//
const listBlobsForOrder = new ListBlobsForOrder();
// Setup the Saga implementation
//
function* fetchCall$3f(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listBlobsForOrder, 'fetchData'], action);
        yield put(listBlobsForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listBlobsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3f() {
    yield takeEvery(getType(listBlobsForOrder.actions.fetch), fetchCall$3f);
}

/**
 * Implements ListBookings fetch for the Redux store
 *
 * @export
 * @class ListBookings
 * @extends {StoreFetchableData<EVA.Core.ListBookings, EVA.Core.ListBookingsResponse>}
 */
class ListBookings extends StoreFetchableData {
    /**
     * Creates an instance of ListBookings
     *
     * @memberof ListBookings
     */
    constructor() {
        super('listBookings', '/message/ListBookings');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListBookings
     */
    *saga() {
        yield fork(listener$3e);
    }
}
// Export the state handling singleton
//
const listBookings = new ListBookings();
// Setup the Saga implementation
//
function* fetchCall$3e(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listBookings, 'fetchData'], action);
        yield put(listBookings.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listBookings.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3e() {
    yield takeEvery(getType(listBookings.actions.fetch), fetchCall$3e);
}

/**
 * Implements ListCashDeposits fetch for the Redux store
 *
 * @export
 * @class ListCashDeposits
 * @extends {StoreFetchableData<EVA.Core.ListCashDeposits, EVA.Core.ListCashDepositsResponse>}
 */
class ListCashDeposits extends StoreFetchableData {
    /**
     * Creates an instance of ListCashDeposits
     *
     * @memberof ListCashDeposits
     */
    constructor() {
        super('listCashDeposits', '/message/ListCashDeposits');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListCashDeposits
     */
    *saga() {
        yield fork(listener$3d);
    }
}
// Export the state handling singleton
//
const listCashDeposits = new ListCashDeposits();
// Setup the Saga implementation
//
function* fetchCall$3d(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listCashDeposits, 'fetchData'], action);
        yield put(listCashDeposits.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listCashDeposits.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3d() {
    yield takeEvery(getType(listCashDeposits.actions.fetch), fetchCall$3d);
}

/**
 * Implements ListCashExpenses fetch for the Redux store
 *
 * @export
 * @class ListCashExpenses
 * @extends {StoreFetchableData<EVA.Core.ListCashExpenses, EVA.Core.ListCashExpensesResponse>}
 */
class ListCashExpenses extends StoreFetchableData {
    /**
     * Creates an instance of ListCashExpenses
     *
     * @memberof ListCashExpenses
     */
    constructor() {
        super('listCashExpenses', '/message/ListCashExpenses');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListCashExpenses
     */
    *saga() {
        yield fork(listener$3c);
    }
}
// Export the state handling singleton
//
const listCashExpenses = new ListCashExpenses();
// Setup the Saga implementation
//
function* fetchCall$3c(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listCashExpenses, 'fetchData'], action);
        yield put(listCashExpenses.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listCashExpenses.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3c() {
    yield takeEvery(getType(listCashExpenses.actions.fetch), fetchCall$3c);
}

/**
 * Implements ListCashTransactionLedger fetch for the Redux store
 *
 * @export
 * @class ListCashTransactionLedger
 * @extends {StoreFetchableData<EVA.Core.ListCashTransactionLedger, EVA.Core.ListCashTransactionLedgerResponse>}
 */
class ListCashTransactionLedger extends StoreFetchableData {
    /**
     * Creates an instance of ListCashTransactionLedger
     *
     * @memberof ListCashTransactionLedger
     */
    constructor() {
        super('listCashTransactionLedger', '/message/ListCashTransactionLedger');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListCashTransactionLedger
     */
    *saga() {
        yield fork(listener$3b);
    }
}
// Export the state handling singleton
//
const listCashTransactionLedger = new ListCashTransactionLedger();
// Setup the Saga implementation
//
function* fetchCall$3b(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listCashTransactionLedger, 'fetchData'], action);
        yield put(listCashTransactionLedger.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listCashTransactionLedger.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3b() {
    yield takeEvery(getType(listCashTransactionLedger.actions.fetch), fetchCall$3b);
}

/**
 * Implements ListCashTransactionLedgerGroups fetch for the Redux store
 *
 * @export
 * @class ListCashTransactionLedgerGroups
 * @extends {StoreFetchableData<EVA.Core.ListCashTransactionLedgerGroups, EVA.Core.ListCashTransactionLedgerGroupsResponse>}
 */
class ListCashTransactionLedgerGroups extends StoreFetchableData {
    /**
     * Creates an instance of ListCashTransactionLedgerGroups
     *
     * @memberof ListCashTransactionLedgerGroups
     */
    constructor() {
        super('listCashTransactionLedgerGroups', '/message/ListCashTransactionLedgerGroups');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListCashTransactionLedgerGroups
     */
    *saga() {
        yield fork(listener$3a);
    }
}
// Export the state handling singleton
//
const listCashTransactionLedgerGroups = new ListCashTransactionLedgerGroups();
// Setup the Saga implementation
//
function* fetchCall$3a(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listCashTransactionLedgerGroups, 'fetchData'], action);
        yield put(listCashTransactionLedgerGroups.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listCashTransactionLedgerGroups.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3a() {
    yield takeEvery(getType(listCashTransactionLedgerGroups.actions.fetch), fetchCall$3a);
}

/**
 * Implements ListClockedInEmployeesForFinancialPeriod fetch for the Redux store
 *
 * @export
 * @class ListClockedInEmployeesForFinancialPeriod
 * @extends {StoreFetchableData<EVA.Workforce.ListClockedInEmployeesForFinancialPeriod, EVA.Workforce.ListClockedInEmployeesForFinancialPeriodResponse>}
 */
class ListClockedInEmployeesForFinancialPeriod extends StoreFetchableData {
    /**
     * Creates an instance of ListClockedInEmployeesForFinancialPeriod
     *
     * @memberof ListClockedInEmployeesForFinancialPeriod
     */
    constructor() {
        super('listClockedInEmployeesForFinancialPeriod', '/message/ListClockedInEmployeesForFinancialPeriod');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListClockedInEmployeesForFinancialPeriod
     */
    *saga() {
        yield fork(listener$39);
    }
}
// Export the state handling singleton
//
const listClockedInEmployeesForFinancialPeriod = new ListClockedInEmployeesForFinancialPeriod();
// Setup the Saga implementation
//
function* fetchCall$39(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listClockedInEmployeesForFinancialPeriod, 'fetchData'], action);
        yield put(listClockedInEmployeesForFinancialPeriod.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listClockedInEmployeesForFinancialPeriod.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$39() {
    yield takeEvery(getType(listClockedInEmployeesForFinancialPeriod.actions.fetch), fetchCall$39);
}

/**
 * Implements ListCountries fetch for the Redux store
 *
 * @export
 * @class ListCountries
 * @extends {StoreFetchableData<EVA.Core.ListCountries, EVA.Core.ListCountriesResponse>}
 */
class ListCountries extends StoreFetchableData {
    /**
     * Creates an instance of ListCountries
     *
     * @memberof ListCountries
     */
    constructor() {
        super('listCountries', '/message/ListCountries');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListCountries
     */
    *saga() {
        yield fork(listener$38);
    }
}
// Export the state handling singleton
//
const listCountries = new ListCountries();
// Setup the Saga implementation
//
function* fetchCall$38(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listCountries, 'fetchData'], action);
        yield put(listCountries.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listCountries.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$38() {
    yield takeEvery(getType(listCountries.actions.fetch), fetchCall$38);
}

/**
 * Implements ListCultures fetch for the Redux store
 *
 * @export
 * @class ListCultures
 * @extends {StoreFetchableData<EVA.Core.Management.ListCultures, EVA.Core.Management.ListCulturesResponse>}
 */
class ListCultures extends StoreFetchableData {
    /**
     * Creates an instance of ListCultures
     *
     * @memberof ListCultures
     */
    constructor() {
        super('listCultures', '/message/ListCultures');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListCultures
     */
    *saga() {
        yield fork(listener$37);
    }
}
// Export the state handling singleton
//
const listCultures = new ListCultures();
// Setup the Saga implementation
//
function* fetchCall$37(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listCultures, 'fetchData'], action);
        yield put(listCultures.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listCultures.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$37() {
    yield takeEvery(getType(listCultures.actions.fetch), fetchCall$37);
}

/**
 * Implements ListCustomFields fetch for the Redux store
 *
 * @export
 * @class ListCustomFields
 * @extends {StoreFetchableData<EVA.Core.Management.ListCustomFields, EVA.Core.Management.ListCustomFieldsResponse>}
 */
class ListCustomFields extends StoreFetchableData {
    /**
     * Creates an instance of ListCustomFields
     *
     * @memberof ListCustomFields
     */
    constructor() {
        super('listCustomFields', '/message/ListCustomFields');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListCustomFields
     */
    *saga() {
        yield fork(listener$36);
    }
}
// Export the state handling singleton
//
const listCustomFields = new ListCustomFields();
// Setup the Saga implementation
//
function* fetchCall$36(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listCustomFields, 'fetchData'], action);
        yield put(listCustomFields.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listCustomFields.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$36() {
    yield takeEvery(getType(listCustomFields.actions.fetch), fetchCall$36);
}

/**
 * Implements ListCustomOrderStatus fetch for the Redux store
 *
 * @export
 * @class ListCustomOrderStatus
 * @extends {StoreFetchableData<EVA.Core.ListCustomOrderStatus, EVA.Core.ListCustomOrderStatusResponse>}
 */
class ListCustomOrderStatus extends StoreFetchableData {
    /**
     * Creates an instance of ListCustomOrderStatus
     *
     * @memberof ListCustomOrderStatus
     */
    constructor() {
        super('listCustomOrderStatus', '/message/ListCustomOrderStatus');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListCustomOrderStatus
     */
    *saga() {
        yield fork(listener$35);
    }
}
// Export the state handling singleton
//
const listCustomOrderStatus = new ListCustomOrderStatus();
// Setup the Saga implementation
//
function* fetchCall$35(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listCustomOrderStatus, 'fetchData'], action);
        yield put(listCustomOrderStatus.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listCustomOrderStatus.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$35() {
    yield takeEvery(getType(listCustomOrderStatus.actions.fetch), fetchCall$35);
}

/**
 * Implements ListDevices fetch for the Redux store
 *
 * @export
 * @class ListDevices
 * @extends {StoreFetchableData<EVA.Core.ListDevices, EVA.Core.ListDevicesResponse>}
 */
class ListDevices extends StoreFetchableData {
    /**
     * Creates an instance of ListDevices
     *
     * @memberof ListDevices
     */
    constructor() {
        super('listDevices', '/message/ListDevices');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListDevices
     */
    *saga() {
        yield fork(listener$34);
    }
}
// Export the state handling singleton
//
const listDevices = new ListDevices();
// Setup the Saga implementation
//
function* fetchCall$34(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listDevices, 'fetchData'], action);
        yield put(listDevices.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listDevices.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$34() {
    yield takeEvery(getType(listDevices.actions.fetch), fetchCall$34);
}

/**
 * Implements ListDisabledDiscountsOnOrder fetch for the Redux store
 *
 * @export
 * @class ListDisabledDiscountsOnOrder
 * @extends {StoreFetchableData<EVA.Core.ListDisabledDiscountsOnOrder, EVA.Core.ListDisabledDiscountsOnOrderResponse>}
 */
class ListDisabledDiscountsOnOrder extends StoreFetchableData {
    /**
     * Creates an instance of ListDisabledDiscountsOnOrder
     *
     * @memberof ListDisabledDiscountsOnOrder
     */
    constructor() {
        super('listDisabledDiscountsOnOrder', '/message/ListDisabledDiscountsOnOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListDisabledDiscountsOnOrder
     */
    *saga() {
        yield fork(listener$33);
    }
}
// Export the state handling singleton
//
const listDisabledDiscountsOnOrder = new ListDisabledDiscountsOnOrder();
// Setup the Saga implementation
//
function* fetchCall$33(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listDisabledDiscountsOnOrder, 'fetchData'], action);
        yield put(listDisabledDiscountsOnOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listDisabledDiscountsOnOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$33() {
    yield takeEvery(getType(listDisabledDiscountsOnOrder.actions.fetch), fetchCall$33);
}

/**
 * Implements ListEmployeesForOrganizationUnit fetch for the Redux store
 *
 * @export
 * @class ListEmployeesForOrganizationUnit
 * @extends {StoreFetchableData<EVA.Workforce.ListEmployeesForOrganizationUnit, EVA.Workforce.ListEmployeesForOrganizationUnitResponse>}
 */
class ListEmployeesForOrganizationUnit extends StoreFetchableData {
    /**
     * Creates an instance of ListEmployeesForOrganizationUnit
     *
     * @memberof ListEmployeesForOrganizationUnit
     */
    constructor() {
        super('listEmployeesForOrganizationUnit', '/message/ListEmployeesForOrganizationUnit');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListEmployeesForOrganizationUnit
     */
    *saga() {
        yield fork(listener$32);
    }
}
// Export the state handling singleton
//
const listEmployeesForOrganizationUnit = new ListEmployeesForOrganizationUnit();
// Setup the Saga implementation
//
function* fetchCall$32(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listEmployeesForOrganizationUnit, 'fetchData'], action);
        yield put(listEmployeesForOrganizationUnit.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listEmployeesForOrganizationUnit.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$32() {
    yield takeEvery(getType(listEmployeesForOrganizationUnit.actions.fetch), fetchCall$32);
}

/**
 * Implements ListEventLedger fetch for the Redux store
 *
 * @export
 * @class ListEventLedger
 * @extends {StoreFetchableData<EVA.Core.ListEventLedger, EVA.Core.ListEventLedgerResponse>}
 */
class ListEventLedger extends StoreFetchableData {
    /**
     * Creates an instance of ListEventLedger
     *
     * @memberof ListEventLedger
     */
    constructor() {
        super('listEventLedger', '/message/ListEventLedger');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListEventLedger
     */
    *saga() {
        yield fork(listener$31);
    }
}
// Export the state handling singleton
//
const listEventLedger = new ListEventLedger();
// Setup the Saga implementation
//
function* fetchCall$31(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listEventLedger, 'fetchData'], action);
        yield put(listEventLedger.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listEventLedger.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$31() {
    yield takeEvery(getType(listEventLedger.actions.fetch), fetchCall$31);
}

/**
 * Implements ListFinancialPeriods fetch for the Redux store
 *
 * @export
 * @class ListFinancialPeriods
 * @extends {StoreFetchableData<EVA.Core.ListFinancialPeriods, EVA.Core.ListFinancialPeriodsResponse>}
 */
class ListFinancialPeriods extends StoreFetchableData {
    /**
     * Creates an instance of ListFinancialPeriods
     *
     * @memberof ListFinancialPeriods
     */
    constructor() {
        super('listFinancialPeriods', '/message/ListFinancialPeriods');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListFinancialPeriods
     */
    *saga() {
        yield fork(listener$30);
    }
}
// Export the state handling singleton
//
const listFinancialPeriods = new ListFinancialPeriods();
// Setup the Saga implementation
//
function* fetchCall$30(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listFinancialPeriods, 'fetchData'], action);
        yield put(listFinancialPeriods.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listFinancialPeriods.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$30() {
    yield takeEvery(getType(listFinancialPeriods.actions.fetch), fetchCall$30);
}

/**
 * Implements ListFullStockCountLabelProducts fetch for the Redux store
 *
 * @export
 * @class ListFullStockCountLabelProducts
 * @extends {StoreFetchableData<EVA.Core.ListFullStockCountLabelProducts, EVA.Core.ListFullStockCountLabelProductsResponse>}
 */
class ListFullStockCountLabelProducts extends StoreFetchableData {
    /**
     * Creates an instance of ListFullStockCountLabelProducts
     *
     * @memberof ListFullStockCountLabelProducts
     */
    constructor() {
        super('listFullStockCountLabelProducts', '/message/ListFullStockCountLabelProducts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListFullStockCountLabelProducts
     */
    *saga() {
        yield fork(listener$2$);
    }
}
// Export the state handling singleton
//
const listFullStockCountLabelProducts = new ListFullStockCountLabelProducts();
// Setup the Saga implementation
//
function* fetchCall$2$(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listFullStockCountLabelProducts, 'fetchData'], action);
        yield put(listFullStockCountLabelProducts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listFullStockCountLabelProducts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2$() {
    yield takeEvery(getType(listFullStockCountLabelProducts.actions.fetch), fetchCall$2$);
}

/**
 * Implements ListFullStockCountLabels fetch for the Redux store
 *
 * @export
 * @class ListFullStockCountLabels
 * @extends {StoreFetchableData<EVA.Core.ListFullStockCountLabels, EVA.Core.ListFullStockCountLabelsResponse>}
 */
class ListFullStockCountLabels extends StoreFetchableData {
    /**
     * Creates an instance of ListFullStockCountLabels
     *
     * @memberof ListFullStockCountLabels
     */
    constructor() {
        super('listFullStockCountLabels', '/message/ListFullStockCountLabels');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListFullStockCountLabels
     */
    *saga() {
        yield fork(listener$2_);
    }
}
// Export the state handling singleton
//
const listFullStockCountLabels = new ListFullStockCountLabels();
// Setup the Saga implementation
//
function* fetchCall$2_(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listFullStockCountLabels, 'fetchData'], action);
        yield put(listFullStockCountLabels.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listFullStockCountLabels.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2_() {
    yield takeEvery(getType(listFullStockCountLabels.actions.fetch), fetchCall$2_);
}

/**
 * Implements ListFullStockCountResult fetch for the Redux store
 *
 * @export
 * @class ListFullStockCountResult
 * @extends {StoreFetchableData<EVA.Core.ListFullStockCountResult, EVA.Core.ListFullStockCountResultResponse>}
 */
class ListFullStockCountResult extends StoreFetchableData {
    /**
     * Creates an instance of ListFullStockCountResult
     *
     * @memberof ListFullStockCountResult
     */
    constructor() {
        super('listFullStockCountResult', '/message/ListFullStockCountResult');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListFullStockCountResult
     */
    *saga() {
        yield fork(listener$2Z);
    }
}
// Export the state handling singleton
//
const listFullStockCountResult = new ListFullStockCountResult();
// Setup the Saga implementation
//
function* fetchCall$2Z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listFullStockCountResult, 'fetchData'], action);
        yield put(listFullStockCountResult.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listFullStockCountResult.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2Z() {
    yield takeEvery(getType(listFullStockCountResult.actions.fetch), fetchCall$2Z);
}

/**
 * Implements ListFullStockCountSummary fetch for the Redux store
 *
 * @export
 * @class ListFullStockCountSummary
 * @extends {StoreFetchableData<EVA.Core.ListFullStockCountSummary, EVA.Core.ListFullStockCountSummaryResponse>}
 */
class ListFullStockCountSummary extends StoreFetchableData {
    /**
     * Creates an instance of ListFullStockCountSummary
     *
     * @memberof ListFullStockCountSummary
     */
    constructor() {
        super('listFullStockCountSummary', '/message/ListFullStockCountSummary');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListFullStockCountSummary
     */
    *saga() {
        yield fork(listener$2Y);
    }
}
// Export the state handling singleton
//
const listFullStockCountSummary = new ListFullStockCountSummary();
// Setup the Saga implementation
//
function* fetchCall$2Y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listFullStockCountSummary, 'fetchData'], action);
        yield put(listFullStockCountSummary.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listFullStockCountSummary.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2Y() {
    yield takeEvery(getType(listFullStockCountSummary.actions.fetch), fetchCall$2Y);
}

/**
 * Implements ListGeneralLedgers fetch for the Redux store
 *
 * @export
 * @class ListGeneralLedgers
 * @extends {StoreFetchableData<EVA.Core.ListGeneralLedgers, EVA.Core.ListGeneralLedgersResponse>}
 */
class ListGeneralLedgers extends StoreFetchableData {
    /**
     * Creates an instance of ListGeneralLedgers
     *
     * @memberof ListGeneralLedgers
     */
    constructor() {
        super('listGeneralLedgers', '/message/ListGeneralLedgers');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListGeneralLedgers
     */
    *saga() {
        yield fork(listener$2X);
    }
}
// Export the state handling singleton
//
const listGeneralLedgers = new ListGeneralLedgers();
// Setup the Saga implementation
//
function* fetchCall$2X(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listGeneralLedgers, 'fetchData'], action);
        yield put(listGeneralLedgers.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listGeneralLedgers.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2X() {
    yield takeEvery(getType(listGeneralLedgers.actions.fetch), fetchCall$2X);
}

/**
 * Implements ListInitialCycleCountLabels fetch for the Redux store
 *
 * @export
 * @class ListInitialCycleCountLabels
 * @extends {StoreFetchableData<EVA.Core.ListInitialCycleCountLabels, EVA.Core.ListInitialCycleCountLabelsResponse>}
 */
class ListInitialCycleCountLabels extends StoreFetchableData {
    /**
     * Creates an instance of ListInitialCycleCountLabels
     *
     * @memberof ListInitialCycleCountLabels
     */
    constructor() {
        super('listInitialCycleCountLabels', '/message/ListInitialCycleCountLabels');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListInitialCycleCountLabels
     */
    *saga() {
        yield fork(listener$2W);
    }
}
// Export the state handling singleton
//
const listInitialCycleCountLabels = new ListInitialCycleCountLabels();
// Setup the Saga implementation
//
function* fetchCall$2W(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listInitialCycleCountLabels, 'fetchData'], action);
        yield put(listInitialCycleCountLabels.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listInitialCycleCountLabels.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2W() {
    yield takeEvery(getType(listInitialCycleCountLabels.actions.fetch), fetchCall$2W);
}

/**
 * Implements ListInterbranchOrganizationUnitForOrder fetch for the Redux store
 *
 * @export
 * @class ListInterbranchOrganizationUnitForOrder
 * @extends {StoreFetchableData<EVA.Core.ListInterbranchOrganizationUnitForOrder, EVA.Core.ListInterbranchOrganizationUnitForOrderResponse>}
 */
class ListInterbranchOrganizationUnitForOrder extends StoreFetchableData {
    /**
     * Creates an instance of ListInterbranchOrganizationUnitForOrder
     *
     * @memberof ListInterbranchOrganizationUnitForOrder
     */
    constructor() {
        super('listInterbranchOrganizationUnitForOrder', '/message/ListInterbranchOrganizationUnitForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListInterbranchOrganizationUnitForOrder
     */
    *saga() {
        yield fork(listener$2V);
    }
}
// Export the state handling singleton
//
const listInterbranchOrganizationUnitForOrder = new ListInterbranchOrganizationUnitForOrder();
// Setup the Saga implementation
//
function* fetchCall$2V(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listInterbranchOrganizationUnitForOrder, 'fetchData'], action);
        yield put(listInterbranchOrganizationUnitForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listInterbranchOrganizationUnitForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2V() {
    yield takeEvery(getType(listInterbranchOrganizationUnitForOrder.actions.fetch), fetchCall$2V);
}

/**
 * Implements ListInvoicesForOrder fetch for the Redux store
 *
 * @export
 * @class ListInvoicesForOrder
 * @extends {StoreFetchableData<EVA.Core.ListInvoicesForOrder, EVA.Core.ListInvoicesForOrderResponse>}
 */
class ListInvoicesForOrder extends StoreFetchableData {
    /**
     * Creates an instance of ListInvoicesForOrder
     *
     * @memberof ListInvoicesForOrder
     */
    constructor() {
        super('listInvoicesForOrder', '/message/ListInvoicesForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListInvoicesForOrder
     */
    *saga() {
        yield fork(listener$2U);
    }
}
// Export the state handling singleton
//
const listInvoicesForOrder = new ListInvoicesForOrder();
// Setup the Saga implementation
//
function* fetchCall$2U(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listInvoicesForOrder, 'fetchData'], action);
        yield put(listInvoicesForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listInvoicesForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2U() {
    yield takeEvery(getType(listInvoicesForOrder.actions.fetch), fetchCall$2U);
}

/**
 * Implements ListManualDiscounts fetch for the Redux store
 *
 * @export
 * @class ListManualDiscounts
 * @extends {StoreFetchableData<EVA.Core.ListManualDiscounts, EVA.Core.ListManualDiscountsResponse>}
 */
class ListManualDiscounts extends StoreFetchableData {
    /**
     * Creates an instance of ListManualDiscounts
     *
     * @memberof ListManualDiscounts
     */
    constructor() {
        super('listManualDiscounts', '/message/ListManualDiscounts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListManualDiscounts
     */
    *saga() {
        yield fork(listener$2T);
    }
}
// Export the state handling singleton
//
const listManualDiscounts = new ListManualDiscounts();
// Setup the Saga implementation
//
function* fetchCall$2T(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listManualDiscounts, 'fetchData'], action);
        yield put(listManualDiscounts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listManualDiscounts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2T() {
    yield takeEvery(getType(listManualDiscounts.actions.fetch), fetchCall$2T);
}

/**
 * Implements ListOpeningHours fetch for the Redux store
 *
 * @export
 * @class ListOpeningHours
 * @extends {StoreFetchableData<EVA.Core.Management.ListOpeningHours, EVA.Core.Management.ListOpeningHoursResponse>}
 */
class ListOpeningHours extends StoreFetchableData {
    /**
     * Creates an instance of ListOpeningHours
     *
     * @memberof ListOpeningHours
     */
    constructor() {
        super('listOpeningHours', '/message/ListOpeningHours');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListOpeningHours
     */
    *saga() {
        yield fork(listener$2S);
    }
}
// Export the state handling singleton
//
const listOpeningHours = new ListOpeningHours();
// Setup the Saga implementation
//
function* fetchCall$2S(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listOpeningHours, 'fetchData'], action);
        yield put(listOpeningHours.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listOpeningHours.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2S() {
    yield takeEvery(getType(listOpeningHours.actions.fetch), fetchCall$2S);
}

/**
 * Implements ListOrderFulfillments fetch for the Redux store
 *
 * @export
 * @class ListOrderFulfillments
 * @extends {StoreFetchableData<EVA.Core.ListOrderFulfillments, EVA.Core.ListOrderFulfillmentsResponse>}
 */
class ListOrderFulfillments extends StoreFetchableData {
    /**
     * Creates an instance of ListOrderFulfillments
     *
     * @memberof ListOrderFulfillments
     */
    constructor() {
        super('listOrderFulfillments', '/message/ListOrderFulfillments');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListOrderFulfillments
     */
    *saga() {
        yield fork(listener$2R);
    }
}
// Export the state handling singleton
//
const listOrderFulfillments = new ListOrderFulfillments();
// Setup the Saga implementation
//
function* fetchCall$2R(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listOrderFulfillments, 'fetchData'], action);
        yield put(listOrderFulfillments.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listOrderFulfillments.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2R() {
    yield takeEvery(getType(listOrderFulfillments.actions.fetch), fetchCall$2R);
}

/**
 * Implements ListOrderLines fetch for the Redux store
 *
 * @export
 * @class ListOrderLines
 * @extends {StoreFetchableData<EVA.Core.ListOrderLines, EVA.Core.ListOrderLinesResponse>}
 */
class ListOrderLines extends StoreFetchableData {
    /**
     * Creates an instance of ListOrderLines
     *
     * @memberof ListOrderLines
     */
    constructor() {
        super('listOrderLines', '/message/ListOrderLines');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListOrderLines
     */
    *saga() {
        yield fork(listener$2Q);
    }
}
// Export the state handling singleton
//
const listOrderLines = new ListOrderLines();
// Setup the Saga implementation
//
function* fetchCall$2Q(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listOrderLines, 'fetchData'], action);
        yield put(listOrderLines.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listOrderLines.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2Q() {
    yield takeEvery(getType(listOrderLines.actions.fetch), fetchCall$2Q);
}

/**
 * Implements ListOrderShipments fetch for the Redux store
 *
 * @export
 * @class ListOrderShipments
 * @extends {StoreFetchableData<EVA.Core.ListOrderShipments, EVA.Core.ListOrderShipmentsResponse>}
 */
class ListOrderShipments extends StoreFetchableData {
    /**
     * Creates an instance of ListOrderShipments
     *
     * @memberof ListOrderShipments
     */
    constructor() {
        super('listOrderShipments', '/message/ListOrderShipments');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListOrderShipments
     */
    *saga() {
        yield fork(listener$2P);
    }
}
// Export the state handling singleton
//
const listOrderShipments = new ListOrderShipments();
// Setup the Saga implementation
//
function* fetchCall$2P(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listOrderShipments, 'fetchData'], action);
        yield put(listOrderShipments.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listOrderShipments.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2P() {
    yield takeEvery(getType(listOrderShipments.actions.fetch), fetchCall$2P);
}

/**
 * Implements ListOrdersForCustomer fetch for the Redux store
 *
 * @export
 * @class ListOrdersForCustomer
 * @extends {StoreFetchableData<EVA.Core.ListOrdersForCustomer, EVA.Core.ListOrdersForCustomerResponse>}
 */
class ListOrdersForCustomer extends StoreFetchableData {
    /**
     * Creates an instance of ListOrdersForCustomer
     *
     * @memberof ListOrdersForCustomer
     */
    constructor() {
        super('listOrdersForCustomer', '/message/ListOrdersForCustomer');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListOrdersForCustomer
     */
    *saga() {
        yield fork(listener$2O);
    }
}
// Export the state handling singleton
//
const listOrdersForCustomer = new ListOrdersForCustomer();
// Setup the Saga implementation
//
function* fetchCall$2O(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listOrdersForCustomer, 'fetchData'], action);
        yield put(listOrdersForCustomer.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listOrdersForCustomer.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2O() {
    yield takeEvery(getType(listOrdersForCustomer.actions.fetch), fetchCall$2O);
}

/**
 * Implements ListOrdersWithCustomerReferences fetch for the Redux store
 *
 * @export
 * @class ListOrdersWithCustomerReferences
 * @extends {StoreFetchableData<EVA.Core.ListOrdersWithCustomerReferences, EVA.Core.ListOrdersWithCustomerReferencesResponse>}
 */
class ListOrdersWithCustomerReferences extends StoreFetchableData {
    /**
     * Creates an instance of ListOrdersWithCustomerReferences
     *
     * @memberof ListOrdersWithCustomerReferences
     */
    constructor() {
        super('listOrdersWithCustomerReferences', '/message/ListOrdersWithCustomerReferences');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListOrdersWithCustomerReferences
     */
    *saga() {
        yield fork(listener$2N);
    }
}
// Export the state handling singleton
//
const listOrdersWithCustomerReferences = new ListOrdersWithCustomerReferences();
// Setup the Saga implementation
//
function* fetchCall$2N(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listOrdersWithCustomerReferences, 'fetchData'], action);
        yield put(listOrdersWithCustomerReferences.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listOrdersWithCustomerReferences.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2N() {
    yield takeEvery(getType(listOrdersWithCustomerReferences.actions.fetch), fetchCall$2N);
}

/**
 * Implements ListOrganizationUnitLanguages fetch for the Redux store
 *
 * @export
 * @class ListOrganizationUnitLanguages
 * @extends {StoreFetchableData<EVA.Core.Management.ListOrganizationUnitLanguages, EVA.Core.Management.ListOrganizationUnitLanguagesResponse>}
 */
class ListOrganizationUnitLanguages extends StoreFetchableData {
    /**
     * Creates an instance of ListOrganizationUnitLanguages
     *
     * @memberof ListOrganizationUnitLanguages
     */
    constructor() {
        super('listOrganizationUnitLanguages', '/message/ListOrganizationUnitLanguages');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListOrganizationUnitLanguages
     */
    *saga() {
        yield fork(listener$2M);
    }
}
// Export the state handling singleton
//
const listOrganizationUnitLanguages = new ListOrganizationUnitLanguages();
// Setup the Saga implementation
//
function* fetchCall$2M(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listOrganizationUnitLanguages, 'fetchData'], action);
        yield put(listOrganizationUnitLanguages.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listOrganizationUnitLanguages.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2M() {
    yield takeEvery(getType(listOrganizationUnitLanguages.actions.fetch), fetchCall$2M);
}

/**
 * Implements ListOrganizationUnitSummaries fetch for the Redux store
 *
 * @export
 * @class ListOrganizationUnitSummaries
 * @extends {StoreFetchableData<EVA.Core.ListOrganizationUnitSummaries, EVA.Core.ListOrganizationUnitSummariesResponse>}
 */
class ListOrganizationUnitSummaries extends StoreFetchableData {
    /**
     * Creates an instance of ListOrganizationUnitSummaries
     *
     * @memberof ListOrganizationUnitSummaries
     */
    constructor() {
        super('listOrganizationUnitSummaries', '/message/ListOrganizationUnitSummaries');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListOrganizationUnitSummaries
     */
    *saga() {
        yield fork(listener$2L);
    }
}
// Export the state handling singleton
//
const listOrganizationUnitSummaries = new ListOrganizationUnitSummaries();
// Setup the Saga implementation
//
function* fetchCall$2L(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listOrganizationUnitSummaries, 'fetchData'], action);
        yield put(listOrganizationUnitSummaries.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listOrganizationUnitSummaries.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2L() {
    yield takeEvery(getType(listOrganizationUnitSummaries.actions.fetch), fetchCall$2L);
}

/**
 * Implements ListOrganizationUnitsDetailed fetch for the Redux store
 *
 * @export
 * @class ListOrganizationUnitsDetailed
 * @extends {StoreFetchableData<EVA.Core.ListOrganizationUnitsDetailed, EVA.Core.ListOrganizationUnitsDetailedResponse>}
 */
class ListOrganizationUnitsDetailed extends StoreFetchableData {
    /**
     * Creates an instance of ListOrganizationUnitsDetailed
     *
     * @memberof ListOrganizationUnitsDetailed
     */
    constructor() {
        super('listOrganizationUnitsDetailed', '/message/ListOrganizationUnitsDetailed');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListOrganizationUnitsDetailed
     */
    *saga() {
        yield fork(listener$2K);
    }
}
// Export the state handling singleton
//
const listOrganizationUnitsDetailed = new ListOrganizationUnitsDetailed();
// Setup the Saga implementation
//
function* fetchCall$2K(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listOrganizationUnitsDetailed, 'fetchData'], action);
        yield put(listOrganizationUnitsDetailed.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listOrganizationUnitsDetailed.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2K() {
    yield takeEvery(getType(listOrganizationUnitsDetailed.actions.fetch), fetchCall$2K);
}

/**
 * Implements ListProductBundles fetch for the Redux store
 *
 * @export
 * @class ListProductBundles
 * @extends {StoreFetchableData<EVA.Core.ListProductBundles, EVA.Core.ListProductBundlesResponse>}
 */
class ListProductBundles extends StoreFetchableData {
    /**
     * Creates an instance of ListProductBundles
     *
     * @memberof ListProductBundles
     */
    constructor() {
        super('listProductBundles', '/message/ListProductBundles');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListProductBundles
     */
    *saga() {
        yield fork(listener$2J);
    }
}
// Export the state handling singleton
//
const listProductBundles = new ListProductBundles();
// Setup the Saga implementation
//
function* fetchCall$2J(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listProductBundles, 'fetchData'], action);
        yield put(listProductBundles.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listProductBundles.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2J() {
    yield takeEvery(getType(listProductBundles.actions.fetch), fetchCall$2J);
}

/**
 * Implements ListPurchaseOrderShipments fetch for the Redux store
 *
 * @export
 * @class ListPurchaseOrderShipments
 * @extends {StoreFetchableData<EVA.Core.ListPurchaseOrderShipments, EVA.Core.ListPurchaseOrderShipmentsResponse>}
 */
class ListPurchaseOrderShipments extends StoreFetchableData {
    /**
     * Creates an instance of ListPurchaseOrderShipments
     *
     * @memberof ListPurchaseOrderShipments
     */
    constructor() {
        super('listPurchaseOrderShipments', '/message/ListPurchaseOrderShipments');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListPurchaseOrderShipments
     */
    *saga() {
        yield fork(listener$2I);
    }
}
// Export the state handling singleton
//
const listPurchaseOrderShipments = new ListPurchaseOrderShipments();
// Setup the Saga implementation
//
function* fetchCall$2I(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listPurchaseOrderShipments, 'fetchData'], action);
        yield put(listPurchaseOrderShipments.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listPurchaseOrderShipments.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2I() {
    yield takeEvery(getType(listPurchaseOrderShipments.actions.fetch), fetchCall$2I);
}

/**
 * Implements ListReturnToSupplierTaskOrderLines fetch for the Redux store
 *
 * @export
 * @class ListReturnToSupplierTaskOrderLines
 * @extends {StoreFetchableData<EVA.Core.ListReturnToSupplierTaskOrderLines, EVA.Core.ListReturnToSupplierTaskOrderLinesResponse>}
 */
class ListReturnToSupplierTaskOrderLines extends StoreFetchableData {
    /**
     * Creates an instance of ListReturnToSupplierTaskOrderLines
     *
     * @memberof ListReturnToSupplierTaskOrderLines
     */
    constructor() {
        super('listReturnToSupplierTaskOrderLines', '/message/ListReturnToSupplierTaskOrderLines');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListReturnToSupplierTaskOrderLines
     */
    *saga() {
        yield fork(listener$2H);
    }
}
// Export the state handling singleton
//
const listReturnToSupplierTaskOrderLines = new ListReturnToSupplierTaskOrderLines();
// Setup the Saga implementation
//
function* fetchCall$2H(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listReturnToSupplierTaskOrderLines, 'fetchData'], action);
        yield put(listReturnToSupplierTaskOrderLines.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listReturnToSupplierTaskOrderLines.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2H() {
    yield takeEvery(getType(listReturnToSupplierTaskOrderLines.actions.fetch), fetchCall$2H);
}

/**
 * Implements ListReturnToSupplierTaskShipmentLines fetch for the Redux store
 *
 * @export
 * @class ListReturnToSupplierTaskShipmentLines
 * @extends {StoreFetchableData<EVA.Core.ListReturnToSupplierTaskShipmentLines, EVA.Core.ListReturnToSupplierTaskShipmentLinesResponse>}
 */
class ListReturnToSupplierTaskShipmentLines extends StoreFetchableData {
    /**
     * Creates an instance of ListReturnToSupplierTaskShipmentLines
     *
     * @memberof ListReturnToSupplierTaskShipmentLines
     */
    constructor() {
        super('listReturnToSupplierTaskShipmentLines', '/message/ListReturnToSupplierTaskShipmentLines');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListReturnToSupplierTaskShipmentLines
     */
    *saga() {
        yield fork(listener$2G);
    }
}
// Export the state handling singleton
//
const listReturnToSupplierTaskShipmentLines = new ListReturnToSupplierTaskShipmentLines();
// Setup the Saga implementation
//
function* fetchCall$2G(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listReturnToSupplierTaskShipmentLines, 'fetchData'], action);
        yield put(listReturnToSupplierTaskShipmentLines.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listReturnToSupplierTaskShipmentLines.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2G() {
    yield takeEvery(getType(listReturnToSupplierTaskShipmentLines.actions.fetch), fetchCall$2G);
}

/**
 * Implements ListReturnableSuppliersForOrder fetch for the Redux store
 *
 * @export
 * @class ListReturnableSuppliersForOrder
 * @extends {StoreFetchableData<EVA.Core.ListReturnableSuppliersForOrder, EVA.Core.ListReturnableSuppliersForOrderResponse>}
 */
class ListReturnableSuppliersForOrder extends StoreFetchableData {
    /**
     * Creates an instance of ListReturnableSuppliersForOrder
     *
     * @memberof ListReturnableSuppliersForOrder
     */
    constructor() {
        super('listReturnableSuppliersForOrder', '/message/ListReturnableSuppliersForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListReturnableSuppliersForOrder
     */
    *saga() {
        yield fork(listener$2F);
    }
}
// Export the state handling singleton
//
const listReturnableSuppliersForOrder = new ListReturnableSuppliersForOrder();
// Setup the Saga implementation
//
function* fetchCall$2F(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listReturnableSuppliersForOrder, 'fetchData'], action);
        yield put(listReturnableSuppliersForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listReturnableSuppliersForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2F() {
    yield takeEvery(getType(listReturnableSuppliersForOrder.actions.fetch), fetchCall$2F);
}

/**
 * Implements ListShelfProducts fetch for the Redux store
 *
 * @export
 * @class ListShelfProducts
 * @extends {StoreFetchableData<EVA.ShelfManagement.ListShelfProducts, EVA.ShelfManagement.ListShelfProductsResponse>}
 */
class ListShelfProducts extends StoreFetchableData {
    /**
     * Creates an instance of ListShelfProducts
     *
     * @memberof ListShelfProducts
     */
    constructor() {
        super('listShelfProducts', '/message/ListShelfProducts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListShelfProducts
     */
    *saga() {
        yield fork(listener$2E);
    }
}
// Export the state handling singleton
//
const listShelfProducts = new ListShelfProducts();
// Setup the Saga implementation
//
function* fetchCall$2E(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listShelfProducts, 'fetchData'], action);
        yield put(listShelfProducts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listShelfProducts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2E() {
    yield takeEvery(getType(listShelfProducts.actions.fetch), fetchCall$2E);
}

/**
 * Implements ListShipmentLines fetch for the Redux store
 *
 * @export
 * @class ListShipmentLines
 * @extends {StoreFetchableData<EVA.Core.ListShipmentLines, EVA.Core.ListShipmentLinesResponse>}
 */
class ListShipmentLines extends StoreFetchableData {
    /**
     * Creates an instance of ListShipmentLines
     *
     * @memberof ListShipmentLines
     */
    constructor() {
        super('listShipmentLines', '/message/ListShipmentLines');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListShipmentLines
     */
    *saga() {
        yield fork(listener$2D);
    }
}
// Export the state handling singleton
//
const listShipmentLines = new ListShipmentLines();
// Setup the Saga implementation
//
function* fetchCall$2D(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listShipmentLines, 'fetchData'], action);
        yield put(listShipmentLines.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listShipmentLines.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2D() {
    yield takeEvery(getType(listShipmentLines.actions.fetch), fetchCall$2D);
}

/**
 * Implements ListShopsInArea fetch for the Redux store
 *
 * @export
 * @class ListShopsInArea
 * @extends {StoreFetchableData<EVA.Core.ListShopsInArea, EVA.Core.ListShopsInAreaResponse>}
 */
class ListShopsInArea extends StoreFetchableData {
    /**
     * Creates an instance of ListShopsInArea
     *
     * @memberof ListShopsInArea
     */
    constructor() {
        super('listShopsInArea', '/message/ListShopsInArea');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListShopsInArea
     */
    *saga() {
        yield fork(listener$2C);
    }
}
// Export the state handling singleton
//
const listShopsInArea = new ListShopsInArea();
// Setup the Saga implementation
//
function* fetchCall$2C(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listShopsInArea, 'fetchData'], action);
        yield put(listShopsInArea.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listShopsInArea.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2C() {
    yield takeEvery(getType(listShopsInArea.actions.fetch), fetchCall$2C);
}

/**
 * Implements ListStockMutationReasons fetch for the Redux store
 *
 * @export
 * @class ListStockMutationReasons
 * @extends {StoreFetchableData<EVA.Core.Management.ListStockMutationReasons, EVA.Core.Management.ListStockMutationReasonsResponse>}
 */
class ListStockMutationReasons extends StoreFetchableData {
    /**
     * Creates an instance of ListStockMutationReasons
     *
     * @memberof ListStockMutationReasons
     */
    constructor() {
        super('listStockMutationReasons', '/message/ListStockMutationReasons');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListStockMutationReasons
     */
    *saga() {
        yield fork(listener$2B);
    }
}
// Export the state handling singleton
//
const listStockMutationReasons = new ListStockMutationReasons();
// Setup the Saga implementation
//
function* fetchCall$2B(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listStockMutationReasons, 'fetchData'], action);
        yield put(listStockMutationReasons.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listStockMutationReasons.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2B() {
    yield takeEvery(getType(listStockMutationReasons.actions.fetch), fetchCall$2B);
}

/**
 * Implements ListStockReplenishmentTaskData fetch for the Redux store
 *
 * @export
 * @class ListStockReplenishmentTaskData
 * @extends {StoreFetchableData<EVA.Core.ListStockReplenishmentTaskData, EVA.Core.ListStockReplenishmentTaskDataResponse>}
 */
class ListStockReplenishmentTaskData extends StoreFetchableData {
    /**
     * Creates an instance of ListStockReplenishmentTaskData
     *
     * @memberof ListStockReplenishmentTaskData
     */
    constructor() {
        super('listStockReplenishmentTaskData', '/message/ListStockReplenishmentTaskData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListStockReplenishmentTaskData
     */
    *saga() {
        yield fork(listener$2A);
    }
}
// Export the state handling singleton
//
const listStockReplenishmentTaskData = new ListStockReplenishmentTaskData();
// Setup the Saga implementation
//
function* fetchCall$2A(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listStockReplenishmentTaskData, 'fetchData'], action);
        yield put(listStockReplenishmentTaskData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listStockReplenishmentTaskData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2A() {
    yield takeEvery(getType(listStockReplenishmentTaskData.actions.fetch), fetchCall$2A);
}

/**
 * Implements ListSuspendedOrders fetch for the Redux store
 *
 * @export
 * @class ListSuspendedOrders
 * @extends {StoreFetchableData<EVA.Core.ListSuspendedOrders, EVA.Core.ListSuspendedOrdersResponse>}
 */
class ListSuspendedOrders extends StoreFetchableData {
    /**
     * Creates an instance of ListSuspendedOrders
     *
     * @memberof ListSuspendedOrders
     */
    constructor() {
        super('listSuspendedOrders', '/message/ListSuspendedOrders');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListSuspendedOrders
     */
    *saga() {
        yield fork(listener$2z);
    }
}
// Export the state handling singleton
//
const listSuspendedOrders = new ListSuspendedOrders();
// Setup the Saga implementation
//
function* fetchCall$2z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listSuspendedOrders, 'fetchData'], action);
        yield put(listSuspendedOrders.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listSuspendedOrders.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2z() {
    yield takeEvery(getType(listSuspendedOrders.actions.fetch), fetchCall$2z);
}

/**
 * Implements ListTaxCodes fetch for the Redux store
 *
 * @export
 * @class ListTaxCodes
 * @extends {StoreFetchableData<EVA.Core.ListTaxCodes, EVA.Core.ListTaxCodesResponse>}
 */
class ListTaxCodes extends StoreFetchableData {
    /**
     * Creates an instance of ListTaxCodes
     *
     * @memberof ListTaxCodes
     */
    constructor() {
        super('listTaxCodes', '/message/ListTaxCodes');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListTaxCodes
     */
    *saga() {
        yield fork(listener$2y);
    }
}
// Export the state handling singleton
//
const listTaxCodes = new ListTaxCodes();
// Setup the Saga implementation
//
function* fetchCall$2y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listTaxCodes, 'fetchData'], action);
        yield put(listTaxCodes.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listTaxCodes.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2y() {
    yield takeEvery(getType(listTaxCodes.actions.fetch), fetchCall$2y);
}

/**
 * Implements ListUserBorrowedProducts fetch for the Redux store
 *
 * @export
 * @class ListUserBorrowedProducts
 * @extends {StoreFetchableData<EVA.Core.ListUserBorrowedProducts, EVA.Core.ListUserBorrowedProductsResponse>}
 */
class ListUserBorrowedProducts extends StoreFetchableData {
    /**
     * Creates an instance of ListUserBorrowedProducts
     *
     * @memberof ListUserBorrowedProducts
     */
    constructor() {
        super('listUserBorrowedProducts', '/message/ListUserBorrowedProducts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListUserBorrowedProducts
     */
    *saga() {
        yield fork(listener$2x);
    }
}
// Export the state handling singleton
//
const listUserBorrowedProducts = new ListUserBorrowedProducts();
// Setup the Saga implementation
//
function* fetchCall$2x(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listUserBorrowedProducts, 'fetchData'], action);
        yield put(listUserBorrowedProducts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listUserBorrowedProducts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2x() {
    yield takeEvery(getType(listUserBorrowedProducts.actions.fetch), fetchCall$2x);
}

/**
 * Implements ListUserCardMutations fetch for the Redux store
 *
 * @export
 * @class ListUserCardMutations
 * @extends {StoreFetchableData<EVA.Payment.Core.ListUserCardMutations, EVA.Payment.Core.ListUserCardMutationsResponse>}
 */
class ListUserCardMutations extends StoreFetchableData {
    /**
     * Creates an instance of ListUserCardMutations
     *
     * @memberof ListUserCardMutations
     */
    constructor() {
        super('listUserCardMutations', '/message/ListUserCardMutations');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListUserCardMutations
     */
    *saga() {
        yield fork(listener$2w);
    }
}
// Export the state handling singleton
//
const listUserCardMutations = new ListUserCardMutations();
// Setup the Saga implementation
//
function* fetchCall$2w(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listUserCardMutations, 'fetchData'], action);
        yield put(listUserCardMutations.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listUserCardMutations.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2w() {
    yield takeEvery(getType(listUserCardMutations.actions.fetch), fetchCall$2w);
}

/**
 * Implements ListUserInteractions fetch for the Redux store
 *
 * @export
 * @class ListUserInteractions
 * @extends {StoreFetchableData<EVA.Core.ListUserInteractions, EVA.Core.ListUserInteractionsResponse>}
 */
class ListUserInteractions extends StoreFetchableData {
    /**
     * Creates an instance of ListUserInteractions
     *
     * @memberof ListUserInteractions
     */
    constructor() {
        super('listUserInteractions', '/message/ListUserInteractions');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListUserInteractions
     */
    *saga() {
        yield fork(listener$2v);
    }
}
// Export the state handling singleton
//
const listUserInteractions = new ListUserInteractions();
// Setup the Saga implementation
//
function* fetchCall$2v(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listUserInteractions, 'fetchData'], action);
        yield put(listUserInteractions.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listUserInteractions.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2v() {
    yield takeEvery(getType(listUserInteractions.actions.fetch), fetchCall$2v);
}

/**
 * Implements ListUserRequirementSets fetch for the Redux store
 *
 * @export
 * @class ListUserRequirementSets
 * @extends {StoreFetchableData<EVA.Core.Management.ListUserRequirementSets, EVA.Core.Management.ListUserRequirementSetsResponse>}
 */
class ListUserRequirementSets extends StoreFetchableData {
    /**
     * Creates an instance of ListUserRequirementSets
     *
     * @memberof ListUserRequirementSets
     */
    constructor() {
        super('listUserRequirementSets', '/message/ListUserRequirementSets');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListUserRequirementSets
     */
    *saga() {
        yield fork(listener$2u);
    }
}
// Export the state handling singleton
//
const listUserRequirementSets = new ListUserRequirementSets();
// Setup the Saga implementation
//
function* fetchCall$2u(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listUserRequirementSets, 'fetchData'], action);
        yield put(listUserRequirementSets.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listUserRequirementSets.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2u() {
    yield takeEvery(getType(listUserRequirementSets.actions.fetch), fetchCall$2u);
}

/**
 * Implements ListUserRequirements fetch for the Redux store
 *
 * @export
 * @class ListUserRequirements
 * @extends {StoreFetchableData<EVA.Core.Management.ListUserRequirements, EVA.Core.Management.ListUserRequirementsResponse>}
 */
class ListUserRequirements extends StoreFetchableData {
    /**
     * Creates an instance of ListUserRequirements
     *
     * @memberof ListUserRequirements
     */
    constructor() {
        super('listUserRequirements', '/message/ListUserRequirements');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListUserRequirements
     */
    *saga() {
        yield fork(listener$2t);
    }
}
// Export the state handling singleton
//
const listUserRequirements = new ListUserRequirements();
// Setup the Saga implementation
//
function* fetchCall$2t(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listUserRequirements, 'fetchData'], action);
        yield put(listUserRequirements.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listUserRequirements.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2t() {
    yield takeEvery(getType(listUserRequirements.actions.fetch), fetchCall$2t);
}

/**
 * Implements ListWorkedHours fetch for the Redux store
 *
 * @export
 * @class ListWorkedHours
 * @extends {StoreFetchableData<EVA.Workforce.ListWorkedHours, EVA.Workforce.ListWorkedHoursResponse>}
 */
class ListWorkedHours extends StoreFetchableData {
    /**
     * Creates an instance of ListWorkedHours
     *
     * @memberof ListWorkedHours
     */
    constructor() {
        super('listWorkedHours', '/message/ListWorkedHours');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListWorkedHours
     */
    *saga() {
        yield fork(listener$2s);
    }
}
// Export the state handling singleton
//
const listWorkedHours = new ListWorkedHours();
// Setup the Saga implementation
//
function* fetchCall$2s(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listWorkedHours, 'fetchData'], action);
        yield put(listWorkedHours.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listWorkedHours.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2s() {
    yield takeEvery(getType(listWorkedHours.actions.fetch), fetchCall$2s);
}

/**
 * Implements ListWorkedHoursForCurrentEmployee fetch for the Redux store
 *
 * @export
 * @class ListWorkedHoursForCurrentEmployee
 * @extends {StoreFetchableData<EVA.Workforce.ListWorkedHoursForCurrentEmployee, EVA.Workforce.ListWorkedHoursForCurrentEmployeeResponse>}
 */
class ListWorkedHoursForCurrentEmployee extends StoreFetchableData {
    /**
     * Creates an instance of ListWorkedHoursForCurrentEmployee
     *
     * @memberof ListWorkedHoursForCurrentEmployee
     */
    constructor() {
        super('listWorkedHoursForCurrentEmployee', '/message/ListWorkedHoursForCurrentEmployee');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListWorkedHoursForCurrentEmployee
     */
    *saga() {
        yield fork(listener$2r);
    }
}
// Export the state handling singleton
//
const listWorkedHoursForCurrentEmployee = new ListWorkedHoursForCurrentEmployee();
// Setup the Saga implementation
//
function* fetchCall$2r(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listWorkedHoursForCurrentEmployee, 'fetchData'], action);
        yield put(listWorkedHoursForCurrentEmployee.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listWorkedHoursForCurrentEmployee.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2r() {
    yield takeEvery(getType(listWorkedHoursForCurrentEmployee.actions.fetch), fetchCall$2r);
}

/**
 * Implements ListWorkedHoursForFinancialPeriod fetch for the Redux store
 *
 * @export
 * @class ListWorkedHoursForFinancialPeriod
 * @extends {StoreFetchableData<EVA.Workforce.ListWorkedHoursForFinancialPeriod, EVA.Workforce.ListWorkedHoursForFinancialPeriodResponse>}
 */
class ListWorkedHoursForFinancialPeriod extends StoreFetchableData {
    /**
     * Creates an instance of ListWorkedHoursForFinancialPeriod
     *
     * @memberof ListWorkedHoursForFinancialPeriod
     */
    constructor() {
        super('listWorkedHoursForFinancialPeriod', '/message/ListWorkedHoursForFinancialPeriod');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListWorkedHoursForFinancialPeriod
     */
    *saga() {
        yield fork(listener$2q);
    }
}
// Export the state handling singleton
//
const listWorkedHoursForFinancialPeriod = new ListWorkedHoursForFinancialPeriod();
// Setup the Saga implementation
//
function* fetchCall$2q(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listWorkedHoursForFinancialPeriod, 'fetchData'], action);
        yield put(listWorkedHoursForFinancialPeriod.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listWorkedHoursForFinancialPeriod.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2q() {
    yield takeEvery(getType(listWorkedHoursForFinancialPeriod.actions.fetch), fetchCall$2q);
}

/**
 * Implements ListZonedCycleCounts fetch for the Redux store
 *
 * @export
 * @class ListZonedCycleCounts
 * @extends {StoreFetchableData<EVA.Core.ListZonedCycleCounts, EVA.Core.ListZonedCycleCountsResponse>}
 */
class ListZonedCycleCounts extends StoreFetchableData {
    /**
     * Creates an instance of ListZonedCycleCounts
     *
     * @memberof ListZonedCycleCounts
     */
    constructor() {
        super('listZonedCycleCounts', '/message/ListZonedCycleCounts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ListZonedCycleCounts
     */
    *saga() {
        yield fork(listener$2p);
    }
}
// Export the state handling singleton
//
const listZonedCycleCounts = new ListZonedCycleCounts();
// Setup the Saga implementation
//
function* fetchCall$2p(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([listZonedCycleCounts, 'fetchData'], action);
        yield put(listZonedCycleCounts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(listZonedCycleCounts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2p() {
    yield takeEvery(getType(listZonedCycleCounts.actions.fetch), fetchCall$2p);
}

/**
 * Implements Login fetch for the Redux store
 *
 * @export
 * @class Login
 * @extends {StoreFetchableData<EVA.Core.Login, EVA.Core.LoginResponse>}
 */
class Login extends StoreFetchableData {
    /**
     * Creates an instance of Login
     *
     * @memberof Login
     */
    constructor() {
        super('login', '/message/Login');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof Login
     */
    *saga() {
        yield fork(listener$2o);
    }
}
// Export the state handling singleton
//
const login = new Login();
// Setup and export the Saga implementation
//
function* fetchCall$2o(action) {
    var _a;
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const getApplicationConfigurationState = getApplicationConfiguration.getState();
        const splitEmployeesAccounts = get(getApplicationConfigurationState.response, 'Configuration.SplitEmployeesAccounts', false);
        if (splitEmployeesAccounts && settings.defaultCallUserType !== undefined && fetchActionPayload.request.AsEmployee === undefined) {
            const isEmployeeApp = settings.defaultCallUserType === 'employee';
            fetchActionPayload.request = Object.assign(Object.assign({}, fetchActionPayload.request), { AsEmployee: isEmployeeApp });
        }
        const data = yield call([login, 'fetchData'], action);
        const authentication = data.Authentication;
        const chainStarter = action.payload.chainStarter;
        // EVA.Core.AuthenticationResults.
        //  * NotAuthenticated = 0
        //  * NotAuthorized = 1
        //  * Authenticated = 2
        //  * NeedsTwoStepAuthentication = 3
        //  * NeedsOrganizationUnitID = 4
        //
        if (authentication !== 2) {
            yield put(login.actions.error(data));
            // Reject both the fetch and chain promise
            //
            fetchActionPayload.fetchReject(data);
            fetchActionPayload.chainReject(data);
        }
        else {
            // Collect some details about the currently logged in user
            //
            const currentUserState = getCurrentUser.getState();
            const currentUserId = get(currentUserState, 'response.User.ID');
            const newUserId = get(data, 'User.ID');
            if (newUserId !== currentUserId) {
                // Check if there is a save state for the user
                //
                const savedState = settings.getSavedState(newUserId);
                let oldState;
                if (savedState) {
                    log$1('[EVA-SDK-REDUX:Login] Resuming previous user state');
                    // Restore redux state
                    //
                    oldState = core.dump(savedState.state);
                }
                else {
                    // Purge the redux state
                    // We will need to dump the entire state except:
                    //
                    // * getApplicationConfiguration
                    //
                    oldState = core.dump({
                        getApplicationConfiguration: getApplicationConfiguration.getState(),
                    });
                }
                // Save state if old user was not anonymous
                //
                if (!currentUserState.isAnonymous) {
                    settings.saveState(currentUserId, oldState);
                }
            }
            // Store the user token on the settings
            // This needs to be done after core.dump() because the un-sets the token
            //
            settings.userToken = (_a = data.User) === null || _a === void 0 ? void 0 : _a.AuthenticationToken;
            yield put(login.actions.response(data));
            // Resolve the fetch promise
            //
            fetchActionPayload.fetchResolve(data);
            // Fetch the current user to update the store to anonymous user
            //
            const [getCurrentUserAction, , getCurrentUserChainPromise] = getCurrentUser.createFetchAction(undefined, chainStarter, undefined, undefined, undefined, undefined, chainContext);
            yield put(getCurrentUserAction);
            // Update the cart for the user
            //
            const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: 0 }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
            yield put(getShoppingCartInfoAction);
            // Resolve the chain
            //
            Promise.all([getCurrentUserChainPromise, getShoppingCartInfoChainPromise])
                .then((chainResponses) => {
                chainResponses.unshift(data);
                fetchActionPayload.chainResolve(chainResponses);
            })
                .catch(fetchActionPayload.chainReject);
        }
    }
    catch (err) {
        yield put(login.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2o() {
    yield takeEvery(getType(login.actions.fetch), fetchCall$2o);
}

/**
 * Implements Logout fetch for the Redux store
 *
 * @export
 * @class Logout
 * @extends {StoreFetchableData<EVA.Core.Logout, EVA.Core.LogoutResponse>}
 */
class Logout extends StoreFetchableData {
    /**
     * Creates an instance of Logout
     *
     * @memberof Logout
     */
    constructor() {
        super('logout', '/message/Logout');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof Logout
     */
    *saga() {
        yield fork(listener$2n);
    }
}
// Export the state handling singleton
//
const logout = new Logout();
// Setup and export the Saga implementation
//
function* fetchCall$2n(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([logout, 'fetchData'], action);
        yield put(logout.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Unset the user token and current order on the settings
        //
        settings.userToken = '';
        settings.currentOrderId = 0;
        // Get the user id before we purge the state
        //
        const userId = get(getCurrentUser.getState(), 'response.User.ID');
        // Purge the redux state
        // We will need to dump the entire state except:
        //
        // * getApplicationConfiguration
        // * logout
        //
        const oldState = core.dump({
            getApplicationConfiguration: getApplicationConfiguration.getState(),
            logout: logout.getState(),
        });
        // Purge any store user state
        // Logout is an explicit action initiated by the user where data should be
        // forgotten
        //
        if (userId) {
            settings.purgeSavedState(userId);
        }
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Fetch the current user to update the store to anonymous user
        //
        const [getCurrentUserAction, , getCurrentUserChainPromise] = getCurrentUser.createFetchAction(undefined, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getCurrentUserAction);
        // Fetch the new empty cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: 0 }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getCurrentUserChainPromise, getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(logout.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2n() {
    yield takeEvery(getType(logout.actions.fetch), fetchCall$2n);
}

/**
 * Implements MakeUserPhoneNumberPrimary fetch for the Redux store
 *
 * @export
 * @class MakeUserPhoneNumberPrimary
 * @extends {StoreFetchableData<EVA.Core.MakeUserPhoneNumberPrimary, EVA.Core.EmptyResponseMessage>}
 */
class MakeUserPhoneNumberPrimary extends StoreFetchableData {
    /**
     * Creates an instance of MakeUserPhoneNumberPrimary
     *
     * @memberof MakeUserPhoneNumberPrimary
     */
    constructor() {
        super('makeUserPhoneNumberPrimary', '/message/MakeUserPhoneNumberPrimary');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof MakeUserPhoneNumberPrimary
     */
    *saga() {
        yield fork(listener$2m);
    }
}
// Export the state handling singleton
//
const makeUserPhoneNumberPrimary = new MakeUserPhoneNumberPrimary();
// Setup the Saga implementation
//
function* fetchCall$2m(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([makeUserPhoneNumberPrimary, 'fetchData'], action);
        yield put(makeUserPhoneNumberPrimary.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Fetch phone number list
        //
        const [getUserPhoneNumbersAction, , getUserPhoneNumbersChainPromise] = getUserPhoneNumbers.createFetchAction(undefined, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getUserPhoneNumbersAction);
        // Resolve the chain
        //
        Promise.all([getUserPhoneNumbersChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(makeUserPhoneNumberPrimary.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2m() {
    yield takeEvery(getType(makeUserPhoneNumberPrimary.actions.fetch), fetchCall$2m);
}

/**
 * Implements MissingProductForInitialCycleCount fetch for the Redux store
 *
 * @export
 * @class MissingProductForInitialCycleCount
 * @extends {StoreFetchableData<EVA.Core.MissingProductForInitialCycleCount, EVA.Core.EmptyResponseMessage>}
 */
class MissingProductForInitialCycleCount extends StoreFetchableData {
    /**
     * Creates an instance of MissingProductForInitialCycleCount
     *
     * @memberof MissingProductForInitialCycleCount
     */
    constructor() {
        super('missingProductForInitialCycleCount', '/message/MissingProductForInitialCycleCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof MissingProductForInitialCycleCount
     */
    *saga() {
        yield fork(listener$2l);
    }
}
// Export the state handling singleton
//
const missingProductForInitialCycleCount = new MissingProductForInitialCycleCount();
// Setup the Saga implementation
//
function* fetchCall$2l(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([missingProductForInitialCycleCount, 'fetchData'], action);
        yield put(missingProductForInitialCycleCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(missingProductForInitialCycleCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2l() {
    yield takeEvery(getType(missingProductForInitialCycleCount.actions.fetch), fetchCall$2l);
}

/**
 * Implements ModifyOrderLinePrice fetch for the Redux store
 *
 * @export
 * @class ModifyOrderLinePrice
 * @extends {StoreFetchableData<EVA.Core.ModifyOrderLinePrice, EVA.Core.ShoppingCartResponse>}
 */
class ModifyOrderLinePrice extends StoreFetchableData {
    /**
     * Creates an instance of ModifyOrderLinePrice
     *
     * @memberof ModifyOrderLinePrice
     */
    constructor() {
        super('modifyOrderLinePrice', '/message/ModifyOrderLinePrice');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ModifyOrderLinePrice
     */
    *saga() {
        yield fork(listener$2k);
    }
}
// Export the state handling singleton
//
const modifyOrderLinePrice = new ModifyOrderLinePrice();
// Setup and export the Saga implementation
//
function* fetchCall$2k(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([modifyOrderLinePrice, 'fetchData'], action);
        yield put(modifyOrderLinePrice.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // ModifyOrderLinePrice should be for the current cart
        //
        const currentOrderId = getShoppingCartInfo.getCurrentOrderId();
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: currentOrderId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(modifyOrderLinePrice.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2k() {
    yield takeEvery(getType(modifyOrderLinePrice.actions.fetch), fetchCall$2k);
}

/**
 * Implements ModifyQuantityOrdered fetch for the Redux store
 *
 * @export
 * @class ModifyQuantityOrdered
 * @extends {StoreFetchableData<EVA.Core.ModifyQuantityOrdered, EVA.Core.ModifyQuantityOrderedResponse>}
 */
class ModifyQuantityOrdered extends StoreFetchableData {
    /**
     * Creates an instance of ModifyQuantityOrdered
     *
     * @memberof ModifyQuantityOrdered
     */
    constructor() {
        super('modifyQuantityOrdered', '/message/ModifyQuantityOrdered');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ModifyQuantityOrdered
     */
    *saga() {
        yield fork(listener$2j);
    }
}
// Export the state handling singleton
//
const modifyQuantityOrdered = new ModifyQuantityOrdered();
// Setup and export the Saga implementation
//
function* fetchCall$2j(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([modifyQuantityOrdered, 'fetchData'], action);
        yield put(modifyQuantityOrdered.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // ModifyQuantityOrdered should be for the current cart
        //
        const currentOrderId = getShoppingCartInfo.getCurrentOrderId();
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: currentOrderId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(modifyQuantityOrdered.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2j() {
    yield takeEvery(getType(modifyQuantityOrdered.actions.fetch), fetchCall$2j);
}

/**
 * Implements MoveCash fetch for the Redux store
 *
 * @export
 * @class MoveCash
 * @extends {StoreFetchableData<EVA.Core.MoveCash, EVA.Core.EmptyResponseMessage>}
 */
class MoveCash extends StoreFetchableData {
    /**
     * Creates an instance of MoveCash
     *
     * @memberof MoveCash
     */
    constructor() {
        super('moveCash', '/message/MoveCash');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof MoveCash
     */
    *saga() {
        yield fork(listener$2i);
    }
}
// Export the state handling singleton
//
const moveCash = new MoveCash();
// Setup the Saga implementation
//
function* fetchCall$2i(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([moveCash, 'fetchData'], action);
        yield put(moveCash.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(moveCash.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2i() {
    yield takeEvery(getType(moveCash.actions.fetch), fetchCall$2i);
}

/**
 * Implements MoveStock fetch for the Redux store
 *
 * @export
 * @class MoveStock
 * @extends {StoreFetchableData<EVA.Core.MoveStock, EVA.Core.EmptyResponseMessage>}
 */
class MoveStock extends StoreFetchableData {
    /**
     * Creates an instance of MoveStock
     *
     * @memberof MoveStock
     */
    constructor() {
        super('moveStock', '/message/MoveStock');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof MoveStock
     */
    *saga() {
        yield fork(listener$2h);
    }
}
// Export the state handling singleton
//
const moveStock = new MoveStock();
// Setup the Saga implementation
//
function* fetchCall$2h(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([moveStock, 'fetchData'], action);
        yield put(moveStock.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // AddProductToOrder is always for the current cart
        //
        const productId = get(moveStock.getState(), 'request.ProductID');
        const [getStockDetailsForProductAction, , getStockDetailsForProductChainPromise] = getStockDetailsForProduct.createFetchAction({ ProductID: productId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getStockDetailsForProductAction);
        // Resolve the chain
        //
        Promise.all([getStockDetailsForProductChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(moveStock.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2h() {
    yield takeEvery(getType(moveStock.actions.fetch), fetchCall$2h);
}

/**
 * Implements OpenCashDrawer fetch for the Redux store
 *
 * @export
 * @class OpenCashDrawer
 * @extends {StoreFetchableData<EVA.Payment.Core.OpenCashDrawer, EVA.Payment.Core.OpenCashDrawerResponse>}
 */
class OpenCashDrawer extends StoreFetchableData {
    /**
     * Creates an instance of OpenCashDrawer
     *
     * @memberof OpenCashDrawer
     */
    constructor() {
        super('openCashDrawer', '/message/OpenCashDrawer');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof OpenCashDrawer
     */
    *saga() {
        yield fork(listener$2g);
    }
}
// Export the state handling singleton
//
const openCashDrawer = new OpenCashDrawer();
// Setup the Saga implementation
//
function* fetchCall$2g(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([openCashDrawer, 'fetchData'], action);
        yield put(openCashDrawer.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(openCashDrawer.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2g() {
    yield takeEvery(getType(openCashDrawer.actions.fetch), fetchCall$2g);
}

/**
 * Implements OpenFinancialPeriod fetch for the Redux store
 *
 * @export
 * @class OpenFinancialPeriod
 * @extends {StoreFetchableData<EVA.Core.OpenFinancialPeriod, EVA.Core.EmptyResponseMessage>}
 */
class OpenFinancialPeriod extends StoreFetchableData {
    /**
     * Creates an instance of OpenFinancialPeriod
     *
     * @memberof OpenFinancialPeriod
     */
    constructor() {
        super('openFinancialPeriod', '/message/OpenFinancialPeriod');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof OpenFinancialPeriod
     */
    *saga() {
        yield fork(listener$2f);
    }
}
// Export the state handling singleton
//
const openFinancialPeriod = new OpenFinancialPeriod();
// Setup the Saga implementation
//
function* fetchCall$2f(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([openFinancialPeriod, 'fetchData'], action);
        yield put(openFinancialPeriod.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(openFinancialPeriod.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2f() {
    yield takeEvery(getType(openFinancialPeriod.actions.fetch), fetchCall$2f);
}

/**
 * Implements PaazlGetShippingOptionsForOrder fetch for the Redux store
 *
 * @export
 * @class PaazlGetShippingOptionsForOrder
 * @extends {StoreFetchableData<EVA.Carrier.Paazl.PaazlGetShippingOptionsForOrder, EVA.Carrier.Paazl.PaazlGetShippingOptionsForOrderResponse>}
 */
class PaazlGetShippingOptionsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of PaazlGetShippingOptionsForOrder
     *
     * @memberof PaazlGetShippingOptionsForOrder
     */
    constructor() {
        super('paazlGetShippingOptionsForOrder', '/message/PaazlGetShippingOptionsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PaazlGetShippingOptionsForOrder
     */
    *saga() {
        yield fork(listener$2e);
    }
}
// Export the state handling singleton
//
const paazlGetShippingOptionsForOrder = new PaazlGetShippingOptionsForOrder();
// Setup the Saga implementation
//
function* fetchCall$2e(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([paazlGetShippingOptionsForOrder, 'fetchData'], action);
        yield put(paazlGetShippingOptionsForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(paazlGetShippingOptionsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2e() {
    yield takeEvery(getType(paazlGetShippingOptionsForOrder.actions.fetch), fetchCall$2e);
}

/**
 * Implements PaazlUpdateShippingOptionsForOrder fetch for the Redux store
 *
 * @export
 * @class PaazlUpdateShippingOptionsForOrder
 * @extends {StoreFetchableData<EVA.Carrier.Paazl.PaazlUpdateShippingOptionsForOrder, EVA.Core.EmptyResponseMessage>}
 */
class PaazlUpdateShippingOptionsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of PaazlUpdateShippingOptionsForOrder
     *
     * @memberof PaazlUpdateShippingOptionsForOrder
     */
    constructor() {
        super('paazlUpdateShippingOptionsForOrder', '/message/PaazlUpdateShippingOptionsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PaazlUpdateShippingOptionsForOrder
     */
    *saga() {
        yield fork(listener$2d);
    }
}
// Export the state handling singleton
//
const paazlUpdateShippingOptionsForOrder = new PaazlUpdateShippingOptionsForOrder();
// Setup the Saga implementation
//
function* fetchCall$2d(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([paazlUpdateShippingOptionsForOrder, 'fetchData'], action);
        yield put(paazlUpdateShippingOptionsForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(paazlUpdateShippingOptionsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2d() {
    yield takeEvery(getType(paazlUpdateShippingOptionsForOrder.actions.fetch), fetchCall$2d);
}

/**
 * Implements ParseBarcode fetch for the Redux store
 *
 * @export
 * @class ParseBarcode
 * @extends {StoreFetchableData<EVA.Core.ParseBarcode, EVA.Core.ParseBarcodeResponse>}
 */
class ParseBarcode extends StoreFetchableData {
    /**
     * Creates an instance of ParseBarcode
     *
     * @memberof ParseBarcode
     */
    constructor() {
        super('parseBarcode', '/message/ParseBarcode');
    }
    /**
     * Override base class method to set lower default timeout
     *
     * @param {Partial<REQ>} [request] The request payload
     * @param {string} [chainStarter=this.name] The chain starter
     * @param {number} [timeout=3000] The default timeout
     * @returns {[ AnyAction, Promise<RES>, Promise<CHAIN> ]}
     * @memberof ParseBarcode
     */
    createFetchAction(request, chainStarter, timeout = 3000) {
        return super.createFetchAction(request, chainStarter, timeout);
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ParseBarcode
     */
    *saga() {
        yield fork(listener$2c);
    }
}
// Export the state handling singleton
//
const parseBarcode = new ParseBarcode();
// Setup the Saga implementation
//
function* fetchCall$2c(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([parseBarcode, 'fetchData'], action);
        yield put(parseBarcode.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(parseBarcode.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2c() {
    yield takeEvery(getType(parseBarcode.actions.fetch), fetchCall$2c);
}

/**
 * Implements Ping fetch for the Redux store
 *
 * @export
 * @class Ping
 * @extends {StoreFetchableData<EVA.Core.Ping, EVA.Core.EmptyResponseMessage>}
 */
class Ping extends StoreFetchableData {
    /**
     * Creates an instance of Ping
     *
     * @memberof Ping
     */
    constructor() {
        super('ping', '/message/Ping');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof Ping
     */
    *saga() {
        yield fork(listener$2b);
    }
}
// Export the state handling singleton
//
const ping = new Ping();
// Setup the Saga implementation
//
function* fetchCall$2b(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ping, 'fetchData'], action);
        yield put(ping.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ping.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2b() {
    yield takeEvery(getType(ping.actions.fetch), fetchCall$2b);
}

/**
 * Implements PostNLGetAvailablePickUpLocationsForOrder fetch for the Redux store
 *
 * @export
 * @class PostNLGetAvailablePickUpLocationsForOrder
 * @extends {StoreFetchableData<EVA.Carrier.PostNL.PostNLGetAvailablePickUpLocationsForOrder, EVA.Carrier.PostNL.PostNLGetAvailablePickUpLocationsForOrderResponse>}
 */
class PostNLGetAvailablePickUpLocationsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of PostNLGetAvailablePickUpLocationsForOrder
     *
     * @memberof PostNLGetAvailablePickUpLocationsForOrder
     */
    constructor() {
        super('postNLGetAvailablePickUpLocationsForOrder', '/message/PostNLGetAvailablePickUpLocationsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PostNLGetAvailablePickUpLocationsForOrder
     */
    *saga() {
        yield fork(listener$2a);
    }
}
// Export the state handling singleton
//
const postNLGetAvailablePickUpLocationsForOrder = new PostNLGetAvailablePickUpLocationsForOrder();
// Setup the Saga implementation
//
function* fetchCall$2a(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([postNLGetAvailablePickUpLocationsForOrder, 'fetchData'], action);
        yield put(postNLGetAvailablePickUpLocationsForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(postNLGetAvailablePickUpLocationsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2a() {
    yield takeEvery(getType(postNLGetAvailablePickUpLocationsForOrder.actions.fetch), fetchCall$2a);
}

/**
 * Implements PostNLGetAvailableTimeframesForOrder fetch for the Redux store
 *
 * @export
 * @class PostNLGetAvailableTimeframesForOrder
 * @extends {StoreFetchableData<EVA.Carrier.PostNL.PostNLGetAvailableTimeframesForOrder, EVA.Carrier.PostNL.PostNLGetAvailableTimeframesForOrderResponse>}
 */
class PostNLGetAvailableTimeframesForOrder extends StoreFetchableData {
    /**
     * Creates an instance of PostNLGetAvailableTimeframesForOrder
     *
     * @memberof PostNLGetAvailableTimeframesForOrder
     */
    constructor() {
        super('postNLGetAvailableTimeframesForOrder', '/message/PostNLGetAvailableTimeframesForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PostNLGetAvailableTimeframesForOrder
     */
    *saga() {
        yield fork(listener$29);
    }
}
// Export the state handling singleton
//
const postNLGetAvailableTimeframesForOrder = new PostNLGetAvailableTimeframesForOrder();
// Setup the Saga implementation
//
function* fetchCall$29(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([postNLGetAvailableTimeframesForOrder, 'fetchData'], action);
        yield put(postNLGetAvailableTimeframesForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(postNLGetAvailableTimeframesForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$29() {
    yield takeEvery(getType(postNLGetAvailableTimeframesForOrder.actions.fetch), fetchCall$29);
}

/**
 * Implements PostNLGetShippingOptionsForOrder fetch for the Redux store
 *
 * @export
 * @class PostNLGetShippingOptionsForOrder
 * @extends {StoreFetchableData<EVA.Carrier.PostNL.PostNLGetShippingOptionsForOrder, EVA.Carrier.PostNL.PostNLGetShippingOptionsForOrderResponse>}
 */
class PostNLGetShippingOptionsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of PostNLGetShippingOptionsForOrder
     *
     * @memberof PostNLGetShippingOptionsForOrder
     */
    constructor() {
        super('postNLGetShippingOptionsForOrder', '/message/PostNLGetShippingOptionsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PostNLGetShippingOptionsForOrder
     */
    *saga() {
        yield fork(listener$28);
    }
}
// Export the state handling singleton
//
const postNLGetShippingOptionsForOrder = new PostNLGetShippingOptionsForOrder();
// Setup the Saga implementation
//
function* fetchCall$28(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([postNLGetShippingOptionsForOrder, 'fetchData'], action);
        yield put(postNLGetShippingOptionsForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(postNLGetShippingOptionsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$28() {
    yield takeEvery(getType(postNLGetShippingOptionsForOrder.actions.fetch), fetchCall$28);
}

/**
 * Implements PostNLUpdateShippingOptionsForOrder fetch for the Redux store
 *
 * @export
 * @class PostNLUpdateShippingOptionsForOrder
 * @extends {StoreFetchableData<EVA.Carrier.PostNL.PostNLUpdateShippingOptionsForOrder, EVA.Core.EmptyResponseMessage>}
 */
class PostNLUpdateShippingOptionsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of PostNLUpdateShippingOptionsForOrder
     *
     * @memberof PostNLUpdateShippingOptionsForOrder
     */
    constructor() {
        super('postNLUpdateShippingOptionsForOrder', '/message/PostNLUpdateShippingOptionsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PostNLUpdateShippingOptionsForOrder
     */
    *saga() {
        yield fork(listener$27);
    }
}
// Export the state handling singleton
//
const postNLUpdateShippingOptionsForOrder = new PostNLUpdateShippingOptionsForOrder();
// Setup the Saga implementation
//
function* fetchCall$27(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([postNLUpdateShippingOptionsForOrder, 'fetchData'], action);
        yield put(postNLUpdateShippingOptionsForOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Fetch selected option for order
        //
        const orderId = action.payload.request.OrderID;
        debug('[EVA-SDK-REDUX] Update current shipping option for order', orderId);
        const [postNLGetShippingOptionForOrderAction, , postNLGetShippingOptionForOrderChainPromise] = postNLGetShippingOptionsForOrder.createFetchAction({ OrderID: orderId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(postNLGetShippingOptionForOrderAction);
        // Resolve the chain
        //
        Promise.all([postNLGetShippingOptionForOrderChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(postNLUpdateShippingOptionsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$27() {
    yield takeEvery(getType(postNLUpdateShippingOptionsForOrder.actions.fetch), fetchCall$27);
}

/**
 * Implements PreCountInitialCycleCountLabel fetch for the Redux store
 *
 * @export
 * @class PreCountInitialCycleCountLabel
 * @extends {StoreFetchableData<EVA.Core.PreCountInitialCycleCountLabel, EVA.Core.EmptyResponseMessage>}
 */
class PreCountInitialCycleCountLabel extends StoreFetchableData {
    /**
     * Creates an instance of PreCountInitialCycleCountLabel
     *
     * @memberof PreCountInitialCycleCountLabel
     */
    constructor() {
        super('preCountInitialCycleCountLabel', '/message/PreCountInitialCycleCountLabel');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PreCountInitialCycleCountLabel
     */
    *saga() {
        yield fork(listener$26);
    }
}
// Export the state handling singleton
//
const preCountInitialCycleCountLabel = new PreCountInitialCycleCountLabel();
// Setup the Saga implementation
//
function* fetchCall$26(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([preCountInitialCycleCountLabel, 'fetchData'], action);
        yield put(preCountInitialCycleCountLabel.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(preCountInitialCycleCountLabel.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$26() {
    yield takeEvery(getType(preCountInitialCycleCountLabel.actions.fetch), fetchCall$26);
}

/**
 * Implements PreviewOrderFulfillment fetch for the Redux store
 *
 * @export
 * @class PreviewOrderFulfillment
 * @extends {StoreFetchableData<EVA.Symphony.PreviewOrderFulFillment, EVA.Symphony.PreviewOrderFulFillmentResponse>}
 */
class PreviewOrderFulFillment extends StoreFetchableData {
    /**
     * Creates an instance of PreviewOrderFulfillment
     *
     * @memberof PreviewOrderFulfillment
     */
    constructor() {
        super('previewOrderFulFillment', '/message/PreviewOrderFulfillment');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PreviewOrderFulfillment
     */
    *saga() {
        yield fork(listener$25);
    }
}
// Export the state handling singleton
//
const previewOrderFulFillment = new PreviewOrderFulFillment();
// Setup the Saga implementation
//
function* fetchCall$25(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([previewOrderFulFillment, 'fetchData'], action);
        yield put(previewOrderFulFillment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(previewOrderFulFillment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$25() {
    yield takeEvery(getType(previewOrderFulFillment.actions.fetch), fetchCall$25);
}

/**
 * Implements PrintControlDocument fetch for the Redux store
 *
 * @export
 * @class PrintControlDocument
 * @extends {StoreFetchableData<EVA.Auditing.PrintControlDocument, EVA.Core.EmptyResponseMessage>}
 */
class PrintControlDocument extends StoreFetchableData {
    /**
     * Creates an instance of PrintControlDocument
     *
     * @memberof PrintControlDocument
     */
    constructor() {
        super('printControlDocument', '/message/PrintControlDocument');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintControlDocument
     */
    *saga() {
        yield fork(listener$24);
    }
}
// Export the state handling singleton
//
const printControlDocument = new PrintControlDocument();
// Setup the Saga implementation
//
function* fetchCall$24(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printControlDocument, 'fetchData'], action);
        yield put(printControlDocument.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printControlDocument.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$24() {
    yield takeEvery(getType(printControlDocument.actions.fetch), fetchCall$24);
}

/**
 * Implements PrintFullStockCountLabels fetch for the Redux store
 *
 * @export
 * @class PrintFullStockCountLabels
 * @extends {StoreFetchableData<EVA.Core.PrintFullStockCountLabels, EVA.Core.EmptyResponseMessage>}
 */
class PrintFullStockCountLabels extends StoreFetchableData {
    /**
     * Creates an instance of PrintFullStockCountLabels
     *
     * @memberof PrintFullStockCountLabels
     */
    constructor() {
        super('printFullStockCountLabels', '/message/PrintFullStockCountLabels');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintFullStockCountLabels
     */
    *saga() {
        yield fork(listener$23);
    }
}
// Export the state handling singleton
//
const printFullStockCountLabels = new PrintFullStockCountLabels();
// Setup the Saga implementation
//
function* fetchCall$23(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printFullStockCountLabels, 'fetchData'], action);
        yield put(printFullStockCountLabels.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printFullStockCountLabels.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$23() {
    yield takeEvery(getType(printFullStockCountLabels.actions.fetch), fetchCall$23);
}

/**
 * Implements PrintInterbranchOrderReceipt fetch for the Redux store
 *
 * @export
 * @class PrintInterbranchOrderReceipt
 * @extends {StoreFetchableData<EVA.Core.PrintInterbranchOrderReceipt, EVA.Core.EmptyResponseMessage>}
 */
class PrintInterbranchOrderReceipt extends StoreFetchableData {
    /**
     * Creates an instance of PrintInterbranchOrderReceipt
     *
     * @memberof PrintInterbranchOrderReceipt
     */
    constructor() {
        super('printInterbranchOrderReceipt', '/message/PrintInterbranchOrderReceipt');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintInterbranchOrderReceipt
     */
    *saga() {
        yield fork(listener$22);
    }
}
// Export the state handling singleton
//
const printInterbranchOrderReceipt = new PrintInterbranchOrderReceipt();
// Setup the Saga implementation
//
function* fetchCall$22(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printInterbranchOrderReceipt, 'fetchData'], action);
        yield put(printInterbranchOrderReceipt.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printInterbranchOrderReceipt.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$22() {
    yield takeEvery(getType(printInterbranchOrderReceipt.actions.fetch), fetchCall$22);
}

/**
 * Implements PrintPaperInvoice fetch for the Redux store
 *
 * @export
 * @class PrintPaperInvoice
 * @extends {StoreFetchableData<EVA.Core.PrintPaperInvoice, EVA.Core.EmptyResponseMessage>}
 */
class PrintPaperInvoice extends StoreFetchableData {
    /**
     * Creates an instance of PrintPaperInvoice
     *
     * @memberof PrintPaperInvoice
     */
    constructor() {
        super('printPaperInvoice', '/message/PrintPaperInvoice');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintPaperInvoice
     */
    *saga() {
        yield fork(listener$21);
    }
}
// Export the state handling singleton
//
const printPaperInvoice = new PrintPaperInvoice();
// Setup the Saga implementation
//
function* fetchCall$21(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printPaperInvoice, 'fetchData'], action);
        yield put(printPaperInvoice.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printPaperInvoice.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$21() {
    yield takeEvery(getType(printPaperInvoice.actions.fetch), fetchCall$21);
}

/**
 * Implements PrintPaperInvoices fetch for the Redux store
 *
 * @export
 * @class PrintPaperInvoices
 * @extends {StoreFetchableData<EVA.Core.PrintPaperInvoices, EVA.Core.EmptyResponseMessage>}
 */
class PrintPaperInvoices extends StoreFetchableData {
    /**
     * Creates an instance of PrintPaperInvoices
     *
     * @memberof PrintPaperInvoices
     */
    constructor() {
        super('printPaperInvoices', '/message/PrintPaperInvoices');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintPaperInvoices
     */
    *saga() {
        yield fork(listener$20);
    }
}
// Export the state handling singleton
//
const printPaperInvoices = new PrintPaperInvoices();
// Setup the Saga implementation
//
function* fetchCall$20(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printPaperInvoices, 'fetchData'], action);
        yield put(printPaperInvoices.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printPaperInvoices.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$20() {
    yield takeEvery(getType(printPaperInvoices.actions.fetch), fetchCall$20);
}

/**
 * Implements PrintPaperOrderConfirmation fetch for the Redux store
 *
 * @export
 * @class PrintPaperOrderConfirmation
 * @extends {StoreFetchableData<EVA.Core.PrintPaperOrderConfirmation, EVA.Core.EmptyResponseMessage>}
 */
class PrintPaperOrderConfirmation extends StoreFetchableData {
    /**
     * Creates an instance of PrintPaperOrderConfirmation
     *
     * @memberof PrintPaperOrderConfirmation
     */
    constructor() {
        super('printPaperOrderConfirmation', '/message/PrintPaperOrderConfirmation');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintPaperOrderConfirmation
     */
    *saga() {
        yield fork(listener$1$);
    }
}
// Export the state handling singleton
//
const printPaperOrderConfirmation = new PrintPaperOrderConfirmation();
// Setup the Saga implementation
//
function* fetchCall$1$(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printPaperOrderConfirmation, 'fetchData'], action);
        yield put(printPaperOrderConfirmation.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printPaperOrderConfirmation.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1$() {
    yield takeEvery(getType(printPaperOrderConfirmation.actions.fetch), fetchCall$1$);
}

/**
 * Implements PrintPriceLabel fetch for the Redux store
 *
 * @export
 * @class PrintPriceLabel
 * @extends {StoreFetchableData<EVA.Core.PrintPriceLabel, EVA.Core.EmptyResponseMessage>}
 */
class PrintPriceLabel extends StoreFetchableData {
    /**
     * Creates an instance of PrintPriceLabel
     *
     * @memberof PrintPriceLabel
     */
    constructor() {
        super('printPriceLabel', '/message/PrintPriceLabel');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintPriceLabel
     */
    *saga() {
        yield fork(listener$1_);
    }
}
// Export the state handling singleton
//
const printPriceLabel = new PrintPriceLabel();
// Setup the Saga implementation
//
function* fetchCall$1_(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printPriceLabel, 'fetchData'], action);
        yield put(printPriceLabel.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printPriceLabel.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1_() {
    yield takeEvery(getType(printPriceLabel.actions.fetch), fetchCall$1_);
}

/**
 * Implements PrintProductPriceLabel fetch for the Redux store
 *
 * @export
 * @class PrintProductPriceLabel
 * @extends {StoreFetchableData<EVA.Core.PrintProductPriceLabel, EVA.Core.PrintProductPriceLabelResponse>}
 */
class PrintProductPriceLabel extends StoreFetchableData {
    /**
     * Creates an instance of PrintProductPriceLabel
     *
     * @memberof PrintProductPriceLabel
     */
    constructor() {
        super('printProductPriceLabel', '/message/PrintProductPriceLabel');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintProductPriceLabel
     */
    *saga() {
        yield fork(listener$1Z);
    }
}
// Export the state handling singleton
//
const printProductPriceLabel = new PrintProductPriceLabel();
// Setup the Saga implementation
//
function* fetchCall$1Z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printProductPriceLabel, 'fetchData'], action);
        yield put(printProductPriceLabel.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printProductPriceLabel.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1Z() {
    yield takeEvery(getType(printProductPriceLabel.actions.fetch), fetchCall$1Z);
}

/**
 * Implements PrintQRCode fetch for the Redux store
 *
 * @export
 * @class PrintQRCode
 * @extends {StoreFetchableData<EVA.Core.Management.PrintQRCode, EVA.Core.EmptyResponseMessage>}
 */
class PrintQRCode extends StoreFetchableData {
    /**
     * Creates an instance of PrintQRCode
     *
     * @memberof PrintQRCode
     */
    constructor() {
        super('printQrCode', '/message/PrintQRCode');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintQRCode
     */
    *saga() {
        yield fork(listener$1Y);
    }
}
// Export the state handling singleton
//
const printQrCode = new PrintQRCode();
// Setup the Saga implementation
//
function* fetchCall$1Y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printQrCode, 'fetchData'], action);
        yield put(printQrCode.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printQrCode.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1Y() {
    yield takeEvery(getType(printQrCode.actions.fetch), fetchCall$1Y);
}

/**
 * Implements PrintReturnToSupplierTaskShipment fetch for the Redux store
 *
 * @export
 * @class PrintReturnToSupplierTaskShipment
 * @extends {StoreFetchableData<EVA.Core.PrintReturnToSupplierTaskShipment, EVA.Core.EmptyResponseMessage>}
 */
class PrintReturnToSupplierTaskShipment extends StoreFetchableData {
    /**
     * Creates an instance of PrintReturnToSupplierTaskShipment
     *
     * @memberof PrintReturnToSupplierTaskShipment
     */
    constructor() {
        super('printReturnToSupplierTaskShipment', '/message/PrintReturnToSupplierTaskShipment');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintReturnToSupplierTaskShipment
     */
    *saga() {
        yield fork(listener$1X);
    }
}
// Export the state handling singleton
//
const printReturnToSupplierTaskShipment = new PrintReturnToSupplierTaskShipment();
// Setup the Saga implementation
//
function* fetchCall$1X(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printReturnToSupplierTaskShipment, 'fetchData'], action);
        yield put(printReturnToSupplierTaskShipment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printReturnToSupplierTaskShipment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1X() {
    yield takeEvery(getType(printReturnToSupplierTaskShipment.actions.fetch), fetchCall$1X);
}

/**
 * Implements PrintStockReservationReceipt fetch for the Redux store
 *
 * @export
 * @class PrintStockReservationReceipt
 * @extends {StoreFetchableData<EVA.Core.PrintStockReservationReceipt, EVA.Core.EmptyResponseMessage>}
 */
class PrintStockReservationReceipt extends StoreFetchableData {
    /**
     * Creates an instance of PrintStockReservationReceipt
     *
     * @memberof PrintStockReservationReceipt
     */
    constructor() {
        super('printStockReservationReceipt', '/message/PrintStockReservationReceipt');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintStockReservationReceipt
     */
    *saga() {
        yield fork(listener$1W);
    }
}
// Export the state handling singleton
//
const printStockReservationReceipt = new PrintStockReservationReceipt();
// Setup the Saga implementation
//
function* fetchCall$1W(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printStockReservationReceipt, 'fetchData'], action);
        yield put(printStockReservationReceipt.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printStockReservationReceipt.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1W() {
    yield takeEvery(getType(printStockReservationReceipt.actions.fetch), fetchCall$1W);
}

/**
 * Implements PrintTerminalReport fetch for the Redux store
 *
 * @export
 * @class PrintTerminalReport
 * @extends {StoreFetchableData<EVA.Auditing.PrintTerminalReport, EVA.Core.EmptyResponseMessage>}
 */
class PrintTerminalReport extends StoreFetchableData {
    /**
     * Creates an instance of PrintTerminalReport
     *
     * @memberof PrintTerminalReport
     */
    constructor() {
        super('printTerminalReport', '/message/PrintTerminalReport');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintTerminalReport
     */
    *saga() {
        yield fork(listener$1V);
    }
}
// Export the state handling singleton
//
const printTerminalReport = new PrintTerminalReport();
// Setup the Saga implementation
//
function* fetchCall$1V(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printTerminalReport, 'fetchData'], action);
        yield put(printTerminalReport.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printTerminalReport.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1V() {
    yield takeEvery(getType(printTerminalReport.actions.fetch), fetchCall$1V);
}

/**
 * Implements PrintThermalGiftReceipt fetch for the Redux store
 *
 * @export
 * @class PrintThermalGiftReceipt
 * @extends {StoreFetchableData<EVA.Core.PrintThermalGiftReceipt, EVA.Core.EmptyResponseMessage>}
 */
class PrintThermalGiftReceipt extends StoreFetchableData {
    /**
     * Creates an instance of PrintThermalGiftReceipt
     *
     * @memberof PrintThermalGiftReceipt
     */
    constructor() {
        super('printThermalGiftReceipt', '/message/PrintThermalGiftReceipt');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintThermalGiftReceipt
     */
    *saga() {
        yield fork(listener$1U);
    }
}
// Export the state handling singleton
//
const printThermalGiftReceipt = new PrintThermalGiftReceipt();
// Setup the Saga implementation
//
function* fetchCall$1U(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printThermalGiftReceipt, 'fetchData'], action);
        yield put(printThermalGiftReceipt.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printThermalGiftReceipt.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1U() {
    yield takeEvery(getType(printThermalGiftReceipt.actions.fetch), fetchCall$1U);
}

/**
 * Implements PrintThermalInvoice fetch for the Redux store
 *
 * @export
 * @class PrintThermalInvoice
 * @extends {StoreFetchableData<EVA.Core.PrintThermalInvoice, EVA.Core.EmptyResponseMessage>}
 */
class PrintThermalInvoice extends StoreFetchableData {
    /**
     * Creates an instance of PrintThermalInvoice
     *
     * @memberof PrintThermalInvoice
     */
    constructor() {
        super('printThermalInvoice', '/message/PrintThermalInvoice');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintThermalInvoice
     */
    *saga() {
        yield fork(listener$1T);
    }
}
// Export the state handling singleton
//
const printThermalInvoice = new PrintThermalInvoice();
// Setup the Saga implementation
//
function* fetchCall$1T(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printThermalInvoice, 'fetchData'], action);
        yield put(printThermalInvoice.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printThermalInvoice.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1T() {
    yield takeEvery(getType(printThermalInvoice.actions.fetch), fetchCall$1T);
}

/**
 * Implements PrintThermalInvoices fetch for the Redux store
 *
 * @export
 * @class PrintThermalInvoices
 * @extends {StoreFetchableData<EVA.Core.PrintThermalInvoices, EVA.Core.EmptyResponseMessage>}
 */
class PrintThermalInvoices extends StoreFetchableData {
    /**
     * Creates an instance of PrintThermalInvoices
     *
     * @memberof PrintThermalInvoices
     */
    constructor() {
        super('printThermalInvoices', '/message/PrintThermalInvoices');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintThermalInvoices
     */
    *saga() {
        yield fork(listener$1S);
    }
}
// Export the state handling singleton
//
const printThermalInvoices = new PrintThermalInvoices();
// Setup the Saga implementation
//
function* fetchCall$1S(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printThermalInvoices, 'fetchData'], action);
        yield put(printThermalInvoices.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printThermalInvoices.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1S() {
    yield takeEvery(getType(printThermalInvoices.actions.fetch), fetchCall$1S);
}

/**
 * Implements PrintThermalOrderConfirmation fetch for the Redux store
 *
 * @export
 * @class PrintThermalOrderConfirmation
 * @extends {StoreFetchableData<EVA.Core.PrintThermalOrderConfirmation, EVA.Core.EmptyResponseMessage>}
 */
class PrintThermalOrderConfirmation extends StoreFetchableData {
    /**
     * Creates an instance of PrintThermalOrderConfirmation
     *
     * @memberof PrintThermalOrderConfirmation
     */
    constructor() {
        super('printThermalOrderConfirmation', '/message/PrintThermalOrderConfirmation');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintThermalOrderConfirmation
     */
    *saga() {
        yield fork(listener$1R);
    }
}
// Export the state handling singleton
//
const printThermalOrderConfirmation = new PrintThermalOrderConfirmation();
// Setup the Saga implementation
//
function* fetchCall$1R(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printThermalOrderConfirmation, 'fetchData'], action);
        yield put(printThermalOrderConfirmation.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printThermalOrderConfirmation.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1R() {
    yield takeEvery(getType(printThermalOrderConfirmation.actions.fetch), fetchCall$1R);
}

/**
 * Implements PrintThermalPaymentProof fetch for the Redux store
 *
 * @export
 * @class PrintThermalPaymentProof
 * @extends {StoreFetchableData<EVA.Core.PrintThermalPaymentProof, EVA.Core.EmptyResponseMessage>}
 */
class PrintThermalPaymentProof extends StoreFetchableData {
    /**
     * Creates an instance of PrintThermalPaymentProof
     *
     * @memberof PrintThermalPaymentProof
     */
    constructor() {
        super('printThermalPaymentProof', '/message/PrintThermalPaymentProof');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof PrintThermalPaymentProof
     */
    *saga() {
        yield fork(listener$1Q);
    }
}
// Export the state handling singleton
//
const printThermalPaymentProof = new PrintThermalPaymentProof();
// Setup the Saga implementation
//
function* fetchCall$1Q(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([printThermalPaymentProof, 'fetchData'], action);
        yield put(printThermalPaymentProof.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(printThermalPaymentProof.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1Q() {
    yield takeEvery(getType(printThermalPaymentProof.actions.fetch), fetchCall$1Q);
}

/**
 * Implements ProcessPrintTasks fetch for the Redux store
 *
 * @export
 * @class ProcessPrintTasks
 * @extends {StoreFetchableData<EVA.Core.ProcessPrintTasks, EVA.Core.EmptyResponseMessage>}
 */
class ProcessPrintTasks extends StoreFetchableData {
    /**
     * Creates an instance of ProcessPrintTasks
     *
     * @memberof ProcessPrintTasks
     */
    constructor() {
        super('processPrintTasks', '/message/ProcessPrintTasks');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ProcessPrintTasks
     */
    *saga() {
        yield fork(listener$1P);
    }
}
// Export the state handling singleton
//
const processPrintTasks = new ProcessPrintTasks();
// Setup the Saga implementation
//
function* fetchCall$1P(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([processPrintTasks, 'fetchData'], action);
        yield put(processPrintTasks.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(processPrintTasks.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1P() {
    yield takeEvery(getType(processPrintTasks.actions.fetch), fetchCall$1P);
}

/**
 * Implements ProduceCashDepositDocument fetch for the Redux store
 *
 * @export
 * @class ProduceCashDepositDocument
 * @extends {StoreFetchableData<EVA.Core.ProduceCashDepositDocument, EVA.Core.EmptyResponseMessage>}
 */
class ProduceCashDepositDocument extends StoreFetchableData {
    /**
     * Creates an instance of ProduceCashDepositDocument
     *
     * @memberof ProduceCashDepositDocument
     */
    constructor() {
        super('produceCashDepositDocument', '/message/ProduceCashDepositDocument');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ProduceCashDepositDocument
     */
    *saga() {
        yield fork(listener$1O);
    }
}
// Export the state handling singleton
//
const produceCashDepositDocument = new ProduceCashDepositDocument();
// Setup the Saga implementation
//
function* fetchCall$1O(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([produceCashDepositDocument, 'fetchData'], action);
        yield put(produceCashDepositDocument.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(produceCashDepositDocument.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1O() {
    yield takeEvery(getType(produceCashDepositDocument.actions.fetch), fetchCall$1O);
}

/**
 * Implements ProduceCashExpenseDocument fetch for the Redux store
 *
 * @export
 * @class ProduceCashExpenseDocument
 * @extends {StoreFetchableData<EVA.Core.ProduceCashExpenseDocument, EVA.Core.EmptyResponseMessage>}
 */
class ProduceCashExpenseDocument extends StoreFetchableData {
    /**
     * Creates an instance of ProduceCashExpenseDocument
     *
     * @memberof ProduceCashExpenseDocument
     */
    constructor() {
        super('produceCashExpenseDocument', '/message/ProduceCashExpenseDocument');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ProduceCashExpenseDocument
     */
    *saga() {
        yield fork(listener$1N);
    }
}
// Export the state handling singleton
//
const produceCashExpenseDocument = new ProduceCashExpenseDocument();
// Setup the Saga implementation
//
function* fetchCall$1N(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([produceCashExpenseDocument, 'fetchData'], action);
        yield put(produceCashExpenseDocument.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(produceCashExpenseDocument.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1N() {
    yield takeEvery(getType(produceCashExpenseDocument.actions.fetch), fetchCall$1N);
}

/**
 * Implements ProduceDocuments fetch for the Redux store
 *
 * @export
 * @class ProduceDocuments
 * @extends {StoreFetchableData<EVA.Core.ProduceDocuments, EVA.Core.ProduceDocumentsResponse>}
 */
class ProduceDocuments extends StoreFetchableData {
    /**
     * Creates an instance of ProduceDocuments
     *
     * @memberof ProduceDocuments
     */
    constructor() {
        super('produceDocuments', '/message/ProduceDocuments');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ProduceDocuments
     */
    *saga() {
        yield fork(listener$1M);
    }
}
// Export the state handling singleton
//
const produceDocuments = new ProduceDocuments();
// Setup the Saga implementation
//
function* fetchCall$1M(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([produceDocuments, 'fetchData'], action);
        yield put(produceDocuments.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(produceDocuments.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1M() {
    yield takeEvery(getType(produceDocuments.actions.fetch), fetchCall$1M);
}

/**
 * Implements ProduceShipmentDocuments fetch for the Redux store
 *
 * @export
 * @class ProduceShipmentDocuments
 * @extends {StoreFetchableData<EVA.Core.ProduceShipmentDocuments, EVA.Core.ProduceShipmentDocumentsResponse>}
 */
class ProduceShipmentDocuments extends StoreFetchableData {
    /**
     * Creates an instance of ProduceShipmentDocuments
     *
     * @memberof ProduceShipmentDocuments
     */
    constructor() {
        super('produceShipmentDocuments', '/message/ProduceShipmentDocuments');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ProduceShipmentDocuments
     */
    *saga() {
        yield fork(listener$1L);
    }
}
// Export the state handling singleton
//
const produceShipmentDocuments = new ProduceShipmentDocuments();
// Setup the Saga implementation
//
function* fetchCall$1L(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([produceShipmentDocuments, 'fetchData'], action);
        yield put(produceShipmentDocuments.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(produceShipmentDocuments.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1L() {
    yield takeEvery(getType(produceShipmentDocuments.actions.fetch), fetchCall$1L);
}

/**
 * Implements ProductCategorySearch fetch for the Redux store
 *
 * @export
 * @class ProductCategorySearch
 * @extends {StoreFetchableData<EVA.Core.SearchProducts, EVA.Core.SearchProductsResponse>}
 */
class ProductCategorySearch extends StoreFetchableData {
    /**
     * Creates an instance of ProductCategorySearch
     *
     * @memberof ProductCategorySearch
     */
    constructor() {
        // Add the paginate action
        //
        const additionalActions = {
            paginate: createAction('PAGINATE_productCategorySearch', (request) => {
                return ({ type: 'PAGINATE_productCategorySearch', payload: request });
            }),
        };
        super('productCategorySearch', '/message/SearchProducts', undefined, { addDefaultProductPropertiesPath: 'IncludedFields' }, additionalActions);
    }
    /**
     * Initial state with extended properties
     *
     * @type {IProductCategorySearchState}
     * @memberof ProductCategorySearch
     */
    get initialState() {
        return {
            isFetching: false,
            request: null,
            response: null,
            error: null,
            chainStarter: this.name,
            pages: {},
        };
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ProductCategorySearch
     */
    *saga() {
        yield fork(listener$1K);
    }
    /**
     * The extended collection of reducer functions
     *
     * @protected
     * @returns {{ [ reducerName: string ]: ( state: any, action: AnyAction ) => any }}
     * @memberof ProductCategorySearch
     */
    getReducerFunctions() {
        const baseReducerFuncs = super.getReducerFunctions();
        baseReducerFuncs.pages = (state = [], action) => {
            switch (action.type) {
                case getType(this.actions.paginate):
                    const pageData = action.payload;
                    const pageNr = get(pageData, 'searchProducts.Page');
                    // Check if the request filters have changed
                    // If so purge the existing pages
                    //
                    const firstPage = first(values(state));
                    const firstRequest = JSON.stringify(omit(get(firstPage, 'searchProductsRequest'), 'Page'));
                    const currentRequest = JSON.stringify(omit(get(pageData, 'searchProductsRequest'), 'Page'));
                    const sameRequest = firstRequest === currentRequest;
                    if (!pageNr) {
                        log$1('[EVA-SDK-REDUX:productCategorySearch] Not adding page. Missing page nr', pageData);
                        // Don't add the response if missing Page number
                        //
                        return state;
                    }
                    else if (firstPage && !sameRequest) {
                        const newPages = {};
                        newPages[pageNr] = pageData;
                        log$1('[EVA-SDK-REDUX:productCategorySearch] Reset pages', newPages);
                        return newPages;
                    }
                    else {
                        // Add the new page into the existing empty set
                        //
                        const newPages = cloneDeep(state);
                        newPages[pageNr] = pageData;
                        log$1('[EVA-SDK-REDUX:productCategorySearch] Added new page to existing set', newPages);
                        return newPages;
                    }
                default:
                    return state;
            }
        };
        return baseReducerFuncs;
    }
}
// Export the state handling singleton
//
const productCategorySearch = new ProductCategorySearch();
// Setup the Saga implementation
//
function* fetchCall$1K(action) {
    const fetchActionPayload = action.payload;
    fetchActionPayload.request = Object.assign({ Options: settings.productSearchOptions }, fetchActionPayload.request);
    try {
        const data = yield call([productCategorySearch, 'fetchData'], action);
        yield put(productCategorySearch.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
        // Update the pagination
        //
        const newPage = {
            searchProductsRequest: action.payload.request,
            searchProducts: data,
        };
        yield put(productCategorySearch.actions.paginate(newPage));
    }
    catch (err) {
        yield put(productCategorySearch.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1K() {
    yield takeEvery(getType(productCategorySearch.actions.fetch), fetchCall$1K);
}

/**
 * Implements ReceiveShipment fetch for the Redux store
 *
 * @export
 * @class ReceiveShipment
 * @extends {StoreFetchableData<EVA.Core.ReceiveShipment, EVA.Core.EmptyResponseMessage>}
 */
class ReceiveShipment extends StoreFetchableData {
    /**
     * Creates an instance of ReceiveShipment
     *
     * @memberof ReceiveShipment
     */
    constructor() {
        super('receiveShipment', '/message/ReceiveShipment');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ReceiveShipment
     */
    *saga() {
        yield fork(listener$1J);
    }
}
// Export the state handling singleton
//
const receiveShipment = new ReceiveShipment();
// Setup the Saga implementation
//
function* fetchCall$1J(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([receiveShipment, 'fetchData'], action);
        yield put(receiveShipment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(receiveShipment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1J() {
    yield takeEvery(getType(receiveShipment.actions.fetch), fetchCall$1J);
}

/**
 * Implements RecoverShoppingCart fetch for the Redux store
 *
 * @export
 * @class RecoverShoppingCart
 * @extends {StoreFetchableData<EVA.Core.RecoverShoppingCart, EVA.Core.ShoppingCartResponse>}
 */
class RecoverShoppingCart extends StoreFetchableData {
    /**
     * Creates an instance of RecoverShoppingCart
     *
     * @memberof RecoverShoppingCart
     */
    constructor() {
        super('recoverShoppingCart', '/message/RecoverShoppingCart');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RecoverShoppingCart
     */
    *saga() {
        yield fork(listener$1I);
    }
}
// Export the state handling singleton
//
const recoverShoppingCart = new RecoverShoppingCart();
// Setup the Saga implementation
//
function* fetchCall$1I(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([recoverShoppingCart, 'fetchData'], action);
        yield put(recoverShoppingCart.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(recoverShoppingCart.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1I() {
    yield takeEvery(getType(recoverShoppingCart.actions.fetch), fetchCall$1I);
}

/**
 * Implements RefundOrder fetch for the Redux store
 *
 * @export
 * @class RefundOrder
 * @extends {StoreFetchableData<EVA.Core.RefundOrder, EVA.Core.EmptyResponseMessage>}
 */
class RefundOrder extends StoreFetchableData {
    /**
     * Creates an instance of RefundOrder
     *
     * @memberof RefundOrder
     */
    constructor() {
        super('refundOrder', '/message/RefundOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RefundOrder
     */
    *saga() {
        yield fork(listener$1H);
    }
}
// Export the state handling singleton
//
const refundOrder = new RefundOrder();
// Setup the Saga implementation
//
function* fetchCall$1H(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([refundOrder, 'fetchData'], action);
        yield put(refundOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(refundOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1H() {
    yield takeEvery(getType(refundOrder.actions.fetch), fetchCall$1H);
}

/**
 * Implements RegisterUserBorrowedProducts fetch for the Redux store
 *
 * @export
 * @class RegisterUserBorrowedProducts
 * @extends {StoreFetchableData<EVA.Core.RegisterUserBorrowedProducts, EVA.Core.EmptyResponseMessage>}
 */
class RegisterUserBorrowedProducts extends StoreFetchableData {
    /**
     * Creates an instance of RegisterUserBorrowedProducts
     *
     * @memberof RegisterUserBorrowedProducts
     */
    constructor() {
        super('registerUserBorrowedProducts', '/message/RegisterUserBorrowedProducts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RegisterUserBorrowedProducts
     */
    *saga() {
        yield fork(listener$1G);
    }
}
// Export the state handling singleton
//
const registerUserBorrowedProducts = new RegisterUserBorrowedProducts();
// Setup the Saga implementation
//
function* fetchCall$1G(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([registerUserBorrowedProducts, 'fetchData'], action);
        yield put(registerUserBorrowedProducts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(registerUserBorrowedProducts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1G() {
    yield takeEvery(getType(registerUserBorrowedProducts.actions.fetch), fetchCall$1G);
}

/**
 * Implements RemoveOrderLineUnitPriceCorrection fetch for the Redux store
 *
 * @export
 * @class RemoveOrderLineUnitPriceCorrection
 * @extends {StoreFetchableData<EVA.Core.RemoveOrderLineUnitPriceCorrection, EVA.Core.EmptyResponseMessage>}
 */
class RemoveOrderLineUnitPriceCorrection extends StoreFetchableData {
    /**
     * Creates an instance of RemoveOrderLineUnitPriceCorrection
     *
     * @memberof RemoveOrderLineUnitPriceCorrection
     */
    constructor() {
        super('removeOrderLineUnitPriceCorrection', '/message/RemoveOrderLineUnitPriceCorrection');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RemoveOrderLineUnitPriceCorrection
     */
    *saga() {
        yield fork(listener$1F);
    }
}
// Export the state handling singleton
//
const removeOrderLineUnitPriceCorrection = new RemoveOrderLineUnitPriceCorrection();
// Setup the Saga implementation
//
function* fetchCall$1F(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([removeOrderLineUnitPriceCorrection, 'fetchData'], action);
        yield put(removeOrderLineUnitPriceCorrection.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(removeOrderLineUnitPriceCorrection.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1F() {
    yield takeEvery(getType(removeOrderLineUnitPriceCorrection.actions.fetch), fetchCall$1F);
}

/**
 * Implements RemovePushNotificationDevice fetch for the Redux store
 *
 * @export
 * @class RemovePushNotificationDevice
 * @extends {StoreFetchableData<EVA.Core.RemovePushNotificationDevice, EVA.Core.EmptyResponseMessage>}
 */
class RemovePushNotificationDevice extends StoreFetchableData {
    /**
     * Creates an instance of RemovePushNotificationDevice
     *
     * @memberof RemovePushNotificationDevice
     */
    constructor() {
        super('removePushNotificationDevice', '/message/RemovePushNotificationDevice');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RemovePushNotificationDevice
     */
    *saga() {
        yield fork(listener$1E);
    }
}
// Export the state handling singleton
//
const removePushNotificationDevice = new RemovePushNotificationDevice();
// Setup the Saga implementation
//
function* fetchCall$1E(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([removePushNotificationDevice, 'fetchData'], action);
        yield put(removePushNotificationDevice.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(removePushNotificationDevice.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1E() {
    yield takeEvery(getType(removePushNotificationDevice.actions.fetch), fetchCall$1E);
}

/**
 * Implements RenderThermalPrintResult fetch for the Redux store
 *
 * @export
 * @class RenderThermalPrintResult
 * @extends {StoreFetchableData<EVA.Core.RenderThermalPrintResult, EVA.Core.RenderThermalPrintResultResponse>}
 */
class RenderThermalPrintResult extends StoreFetchableData {
    /**
     * Creates an instance of RenderThermalPrintResult
     *
     * @memberof RenderThermalPrintResult
     */
    constructor() {
        super('renderThermalPrintResult', '/message/RenderThermalPrintResult');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RenderThermalPrintResult
     */
    *saga() {
        yield fork(listener$1D);
    }
}
// Export the state handling singleton
//
const renderThermalPrintResult = new RenderThermalPrintResult();
// Setup the Saga implementation
//
function* fetchCall$1D(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([renderThermalPrintResult, 'fetchData'], action);
        yield put(renderThermalPrintResult.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(renderThermalPrintResult.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1D() {
    yield takeEvery(getType(renderThermalPrintResult.actions.fetch), fetchCall$1D);
}

/**
 * Implements ReprintThermalPrintResult fetch for the Redux store
 *
 * @export
 * @class ReprintThermalPrintResult
 * @extends {StoreFetchableData<EVA.Core.ReprintThermalPrintResult, EVA.Core.EmptyResponseMessage>}
 */
class ReprintThermalPrintResult extends StoreFetchableData {
    /**
     * Creates an instance of ReprintThermalPrintResult
     *
     * @memberof ReprintThermalPrintResult
     */
    constructor() {
        super('reprintThermalPrintResult', '/message/ReprintThermalPrintResult');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ReprintThermalPrintResult
     */
    *saga() {
        yield fork(listener$1C);
    }
}
// Export the state handling singleton
//
const reprintThermalPrintResult = new ReprintThermalPrintResult();
// Setup the Saga implementation
//
function* fetchCall$1C(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([reprintThermalPrintResult, 'fetchData'], action);
        yield put(reprintThermalPrintResult.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(reprintThermalPrintResult.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1C() {
    yield takeEvery(getType(reprintThermalPrintResult.actions.fetch), fetchCall$1C);
}

/**
 * Implements RequestDeviceAuthenticationToken fetch for the Redux store
 *
 * @export
 * @class RequestDeviceAuthenticationToken
 * @extends {StoreFetchableData<EVA.Core.RequestDeviceAuthenticationToken, EVA.Core.RequestDeviceAuthenticationTokenResponse>}
 */
class RequestDeviceAuthenticationToken extends StoreFetchableData {
    /**
     * Creates an instance of RequestDeviceAuthenticationToken
     *
     * @memberof RequestDeviceAuthenticationToken
     */
    constructor() {
        super('requestDeviceAuthenticationToken', '/message/RequestDeviceAuthenticationToken');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RequestDeviceAuthenticationToken
     */
    *saga() {
        yield fork(listener$1B);
    }
}
// Export the state handling singleton
//
const requestDeviceAuthenticationToken = new RequestDeviceAuthenticationToken();
// Setup the Saga implementation
//
function* fetchCall$1B(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([requestDeviceAuthenticationToken, 'fetchData'], action);
        yield put(requestDeviceAuthenticationToken.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(requestDeviceAuthenticationToken.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1B() {
    yield takeEvery(getType(requestDeviceAuthenticationToken.actions.fetch), fetchCall$1B);
}

/**
 * Implements RequestPasswordResetToken fetch for the Redux store
 *
 * @export
 * @class RequestPasswordResetToken
 * @extends {StoreFetchableData<EVA.Core.RequestPasswordResetToken, EVA.Core.EmptyResponseMessage>}
 */
class RequestPasswordResetToken extends StoreFetchableData {
    /**
     * Creates an instance of RequestPasswordResetToken
     *
     * @memberof RequestPasswordResetToken
     */
    constructor() {
        super('requestPasswordResetToken', '/message/RequestPasswordResetToken');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RequestPasswordResetToken
     */
    *saga() {
        yield fork(listener$1A);
    }
}
// Export the state handling singleton
//
const requestPasswordResetToken = new RequestPasswordResetToken();
// Setup the Saga implementation
//
function* fetchCall$1A(action) {
    const fetchActionPayload = action.payload;
    try {
        const getApplicationConfigurationState = getApplicationConfiguration.getState();
        const SplitEmployeesAccounts = get(getApplicationConfigurationState.response, 'Configuration.SplitEmployeesAccounts', false);
        if (SplitEmployeesAccounts && settings.defaultCallUserType !== undefined && fetchActionPayload.request.AsEmployee === undefined) {
            const isEmployeeApp = settings.defaultCallUserType === 'employee';
            fetchActionPayload.request = Object.assign(Object.assign({}, fetchActionPayload.request), { AsEmployee: isEmployeeApp });
        }
        const data = yield call([requestPasswordResetToken, 'fetchData'], action);
        yield put(requestPasswordResetToken.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(requestPasswordResetToken.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1A() {
    yield takeEvery(getType(requestPasswordResetToken.actions.fetch), fetchCall$1A);
}

/**
 * Implements RequestPaymentUpdate fetch for the Redux store
 *
 * @export
 * @class RequestPaymentUpdate
 * @extends {StoreFetchableData<EVA.Core.RequestPaymentUpdate, EVA.Core.RequestPaymentUpdateResponse>}
 */
class RequestPaymentUpdate extends StoreFetchableData {
    /**
     * Creates an instance of RequestPaymentUpdate
     *
     * @memberof RequestPaymentUpdate
     */
    constructor() {
        super('requestPaymentUpdate', '/message/RequestPaymentUpdate');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RequestPaymentUpdate
     */
    *saga() {
        yield fork(listener$1z);
    }
}
// Export the state handling singleton
//
const requestPaymentUpdate = new RequestPaymentUpdate();
// Setup the Saga implementation
//
function* fetchCall$1z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([requestPaymentUpdate, 'fetchData'], action);
        yield put(requestPaymentUpdate.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(requestPaymentUpdate.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1z() {
    yield takeEvery(getType(requestPaymentUpdate.actions.fetch), fetchCall$1z);
}

/**
 * Implements ReserveOrder fetch for the Redux store
 *
 * @export
 * @class ReserveOrder
 * @extends {StoreFetchableData<EVA.Core.ReserveOrder, EVA.Core.EmptyResponseMessage>}
 */
class ReserveOrder extends StoreFetchableData {
    /**
     * Creates an instance of ReserveOrder
     *
     * @memberof ReserveOrder
     */
    constructor() {
        super('reserveOrder', '/message/ReserveOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ReserveOrder
     */
    *saga() {
        yield fork(listener$1y);
    }
}
// Export the state handling singleton
//
const reserveOrder = new ReserveOrder();
// Setup the Saga implementation
//
function* fetchCall$1y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([reserveOrder, 'fetchData'], action);
        yield put(reserveOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(reserveOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1y() {
    yield takeEvery(getType(reserveOrder.actions.fetch), fetchCall$1y);
}

/**
 * Implements ResetUserPassword fetch for the Redux store
 *
 * @export
 * @class ResetUserPassword
 * @extends {StoreFetchableData<EVA.Core.ResetUserPassword, EVA.Core.ResetUserPasswordResponse>}
 */
class ResetUserPassword extends StoreFetchableData {
    /**
     * Creates an instance of ResetUserPassword
     *
     * @memberof ResetUserPassword
     */
    constructor() {
        super('resetUserPassword', '/message/ResetUserPassword');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ResetUserPassword
     */
    *saga() {
        yield fork(listener$1x);
    }
}
// Export the state handling singleton
//
const resetUserPassword = new ResetUserPassword();
// Setup the Saga implementation
//
function* fetchCall$1x(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([resetUserPassword, 'fetchData'], action);
        yield put(resetUserPassword.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(resetUserPassword.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1x() {
    yield takeEvery(getType(resetUserPassword.actions.fetch), fetchCall$1x);
}

/**
 * Implements ReturnOrderLines fetch for the Redux store
 *
 * @export
 * @class ReturnOrderLines
 * @extends {StoreFetchableData<EVA.Core.ReturnOrderLines, EVA.Core.SimpleShoppingCartResponse>}
 */
class ReturnOrderLines extends StoreFetchableData {
    /**
     * Creates an instance of ReturnOrderLines
     *
     * @memberof ReturnOrderLines
     */
    constructor() {
        super('returnOrderLines', '/message/ReturnOrderLines');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ReturnOrderLines
     */
    *saga() {
        yield fork(listener$1w);
    }
}
// Export the state handling singleton
//
const returnOrderLines = new ReturnOrderLines();
// Setup the Saga implementation
//
function* fetchCall$1w(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([returnOrderLines, 'fetchData'], action);
        yield put(returnOrderLines.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        //
        const currentOrderId = getShoppingCartInfo.getCurrentOrderId();
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: currentOrderId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(returnOrderLines.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1w() {
    yield takeEvery(getType(returnOrderLines.actions.fetch), fetchCall$1w);
}

/**
 * Implements ReturnUserBorrowedProducts fetch for the Redux store
 *
 * @export
 * @class ReturnUserBorrowedProducts
 * @extends {StoreFetchableData<EVA.Core.ReturnUserBorrowedProducts, EVA.Core.EmptyResponseMessage>}
 */
class ReturnUserBorrowedProducts extends StoreFetchableData {
    /**
     * Creates an instance of ReturnUserBorrowedProducts
     *
     * @memberof ReturnUserBorrowedProducts
     */
    constructor() {
        super('returnUserBorrowedProducts', '/message/ReturnUserBorrowedProducts');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ReturnUserBorrowedProducts
     */
    *saga() {
        yield fork(listener$1v);
    }
}
// Export the state handling singleton
//
const returnUserBorrowedProducts = new ReturnUserBorrowedProducts();
// Setup the Saga implementation
//
function* fetchCall$1v(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([returnUserBorrowedProducts, 'fetchData'], action);
        yield put(returnUserBorrowedProducts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(returnUserBorrowedProducts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1v() {
    yield takeEvery(getType(returnUserBorrowedProducts.actions.fetch), fetchCall$1v);
}

/**
 * Implements RitualsAddGiftCardToOrder fetch for the Redux store
 *
 * @export
 * @class RitualsAddGiftCardToOrder
 * @extends {StoreFetchableData<EVA.Rituals.RitualsAddGiftCardToOrder, EVA.Core.EmptyResponseMessage>}
 */
class RitualsAddGiftCardToOrder extends StoreFetchableData {
    /**
     * Creates an instance of RitualsAddGiftCardToOrder
     *
     * @memberof RitualsAddGiftCardToOrder
     */
    constructor() {
        super('ritualsAddGiftCardToOrder', '/message/RitualsAddGiftCardToOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsAddGiftCardToOrder
     */
    *saga() {
        yield fork(listener$1u);
    }
}
// Export the state handling singleton
//
const ritualsAddGiftCardToOrder = new RitualsAddGiftCardToOrder();
// Setup the Saga implementation
//
function* fetchCall$1u(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsAddGiftCardToOrder, 'fetchData'], action);
        yield put(ritualsAddGiftCardToOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsAddGiftCardToOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1u() {
    yield takeEvery(getType(ritualsAddGiftCardToOrder.actions.fetch), fetchCall$1u);
}

/**
 * Implements RitualsApplyEmployeeDiscount fetch for the Redux store
 *
 * @export
 * @class RitualsApplyEmployeeDiscount
 * @extends {StoreFetchableData<EVA.Rituals.RitualsApplyEmployeeDiscount, EVA.Core.EmptyResponseMessage>}
 */
class RitualsApplyEmployeeDiscount extends StoreFetchableData {
    /**
     * Creates an instance of RitualsApplyEmployeeDiscount
     *
     * @memberof RitualsApplyEmployeeDiscount
     */
    constructor() {
        super('ritualsApplyEmployeeDiscount', '/message/RitualsApplyEmployeeDiscount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsApplyEmployeeDiscount
     */
    *saga() {
        yield fork(listener$1t);
    }
}
// Export the state handling singleton
//
const ritualsApplyEmployeeDiscount = new RitualsApplyEmployeeDiscount();
// Setup the Saga implementation
//
function* fetchCall$1t(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsApplyEmployeeDiscount, 'fetchData'], action);
        yield put(ritualsApplyEmployeeDiscount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsApplyEmployeeDiscount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1t() {
    yield takeEvery(getType(ritualsApplyEmployeeDiscount.actions.fetch), fetchCall$1t);
}

/**
 * Implements RitualsAttachCustomerToOrder fetch for the Redux store
 *
 * @export
 * @class RitualsAttachCustomerToOrder
 * @extends {StoreFetchableData<EVA.Rituals.RitualsAttachCustomerToOrder, EVA.Core.EmptyResponseMessage>}
 */
class RitualsAttachCustomerToOrder extends StoreFetchableData {
    /**
     * Creates an instance of RitualsAttachCustomerToOrder
     *
     * @memberof RitualsAttachCustomerToOrder
     */
    constructor() {
        super('ritualsAttachCustomerToOrder', '/message/RitualsAttachCustomerToOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsAttachCustomerToOrder
     */
    *saga() {
        yield fork(listener$1s);
    }
}
// Export the state handling singleton
//
const ritualsAttachCustomerToOrder = new RitualsAttachCustomerToOrder();
// Setup the Saga implementation
//
function* fetchCall$1s(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsAttachCustomerToOrder, 'fetchData'], action);
        yield put(ritualsAttachCustomerToOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsAttachCustomerToOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1s() {
    yield takeEvery(getType(ritualsAttachCustomerToOrder.actions.fetch), fetchCall$1s);
}

/**
 * Implements RitualsCreateCustomerReturn fetch for the Redux store
 *
 * @export
 * @class RitualsCreateCustomerReturn
 * @extends {StoreFetchableData<EVA.Rituals.RitualsCreateCustomerReturn, EVA.Rituals.RitualsCreateCustomerReturnResponse>}
 */
class RitualsCreateCustomerReturn extends StoreFetchableData {
    /**
     * Creates an instance of RitualsCreateCustomerReturn
     *
     * @memberof RitualsCreateCustomerReturn
     */
    constructor() {
        super('ritualsCreateCustomerReturn', '/message/RitualsCreateCustomerReturn');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsCreateCustomerReturn
     */
    *saga() {
        yield fork(listener$1r);
    }
}
// Export the state handling singleton
//
const ritualsCreateCustomerReturn = new RitualsCreateCustomerReturn();
// Setup the Saga implementation
//
function* fetchCall$1r(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsCreateCustomerReturn, 'fetchData'], action);
        yield put(ritualsCreateCustomerReturn.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsCreateCustomerReturn.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1r() {
    yield takeEvery(getType(ritualsCreateCustomerReturn.actions.fetch), fetchCall$1r);
}

/**
 * Implements RitualsGeneratePassbookPass fetch for the Redux store
 *
 * @export
 * @class RitualsGeneratePassbookPass
 * @extends {StoreFetchableData<EVA.Rituals.RitualsGeneratePassbookPass, EVA.Core.ResourceResponseMessage>}
 */
class RitualsGeneratePassbookPass extends StoreFetchableData {
    /**
     * Creates an instance of RitualsGeneratePassbookPass
     *
     * @memberof RitualsGeneratePassbookPass
     */
    constructor() {
        super('ritualsGeneratePassbookPass', '/message/RitualsGeneratePassbookPass');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsGeneratePassbookPass
     */
    *saga() {
        yield fork(listener$1q);
    }
}
// Export the state handling singleton
//
const ritualsGeneratePassbookPass = new RitualsGeneratePassbookPass();
// Setup the Saga implementation
//
function* fetchCall$1q(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsGeneratePassbookPass, 'fetchData'], action);
        yield put(ritualsGeneratePassbookPass.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsGeneratePassbookPass.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1q() {
    yield takeEvery(getType(ritualsGeneratePassbookPass.actions.fetch), fetchCall$1q);
}

/**
 * Implements RitualsGetBlobsForReturn fetch for the Redux store
 *
 * @export
 * @class RitualsGetBlobsForReturn
 * @extends {StoreFetchableData<EVA.Rituals.RitualsGetBlobsForReturn, EVA.Rituals.RitualsGetBlobsForReturnResponse>}
 */
class RitualsGetBlobsForReturn extends StoreFetchableData {
    /**
     * Creates an instance of RitualsGetBlobsForReturn
     *
     * @memberof RitualsGetBlobsForReturn
     */
    constructor() {
        super('ritualsGetBlobsForReturn', '/message/RitualsGetBlobsForReturn');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsGetBlobsForReturn
     */
    *saga() {
        yield fork(listener$1p);
    }
}
// Export the state handling singleton
//
const ritualsGetBlobsForReturn = new RitualsGetBlobsForReturn();
// Setup the Saga implementation
//
function* fetchCall$1p(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsGetBlobsForReturn, 'fetchData'], action);
        yield put(ritualsGetBlobsForReturn.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsGetBlobsForReturn.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1p() {
    yield takeEvery(getType(ritualsGetBlobsForReturn.actions.fetch), fetchCall$1p);
}

/**
 * Implements RitualsGetCartData fetch for the Redux store
 *
 * @export
 * @class RitualsGetCartData
 * @extends {StoreFetchableData<EVA.Rituals.RitualsGetCartData, EVA.Rituals.RitualsGetCartDataResponse>}
 */
class RitualsGetCartData extends StoreFetchableData {
    /**
     * Creates an instance of RitualsGetCartData
     *
     * @memberof RitualsGetCartData
     */
    constructor() {
        super('ritualsGetCartData', '/message/RitualsGetCartData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsGetCartData
     */
    *saga() {
        yield fork(listener$1o);
    }
}
// Export the state handling singleton
//
const ritualsGetCartData = new RitualsGetCartData();
// Setup the Saga implementation
//
function* fetchCall$1o(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsGetCartData, 'fetchData'], action);
        yield put(ritualsGetCartData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsGetCartData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1o() {
    yield takeEvery(getType(ritualsGetCartData.actions.fetch), fetchCall$1o);
}

/**
 * Implements RitualsGetCustomerData fetch for the Redux store
 *
 * @export
 * @class RitualsGetCustomerData
 * @extends {StoreFetchableData<EVA.Rituals.RitualsGetCustomerData, EVA.Rituals.RitualsGetCustomerDataResponse>}
 */
class RitualsGetCustomerData extends StoreFetchableData {
    /**
     * Creates an instance of RitualsGetCustomerData
     *
     * @memberof RitualsGetCustomerData
     */
    constructor() {
        super('ritualsGetCustomerData', '/message/RitualsGetCustomerData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsGetCustomerData
     */
    *saga() {
        yield fork(listener$1n);
    }
}
// Export the state handling singleton
//
const ritualsGetCustomerData = new RitualsGetCustomerData();
// Setup the Saga implementation
//
function* fetchCall$1n(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsGetCustomerData, 'fetchData'], action);
        yield put(ritualsGetCustomerData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsGetCustomerData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1n() {
    yield takeEvery(getType(ritualsGetCustomerData.actions.fetch), fetchCall$1n);
}

/**
 * Implements RitualsGetDeliveryOrderData fetch for the Redux store
 *
 * @export
 * @class RitualsGetDeliveryOrderData
 * @extends {StoreFetchableData<EVA.Rituals.RitualsGetDeliveryOrderData, EVA.Core.GetDeliveryOrderDataResponse>}
 */
class RitualsGetDeliveryOrderData extends StoreFetchableData {
    /**
     * Creates an instance of RitualsGetDeliveryOrderData
     *
     * @memberof RitualsGetDeliveryOrderData
     */
    constructor() {
        super('ritualsGetDeliveryOrderData', '/message/RitualsGetDeliveryOrderData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsGetDeliveryOrderData
     */
    *saga() {
        yield fork(listener$1m);
    }
}
// Export the state handling singleton
//
const ritualsGetDeliveryOrderData = new RitualsGetDeliveryOrderData();
// Setup the Saga implementation
//
function* fetchCall$1m(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsGetDeliveryOrderData, 'fetchData'], action);
        yield put(ritualsGetDeliveryOrderData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsGetDeliveryOrderData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1m() {
    yield takeEvery(getType(ritualsGetDeliveryOrderData.actions.fetch), fetchCall$1m);
}

/**
 * Implements RitualsGetEmployeeDiscount fetch for the Redux store
 *
 * @export
 * @class RitualsGetEmployeeDiscount
 * @extends {StoreFetchableData<EVA.Rituals.RitualsGetEmployeeDiscount, EVA.Rituals.RitualsGetEmployeeDiscountResponse>}
 */
class RitualsGetEmployeeDiscount extends StoreFetchableData {
    /**
     * Creates an instance of RitualsGetEmployeeDiscount
     *
     * @memberof RitualsGetEmployeeDiscount
     */
    constructor() {
        super('ritualsGetEmployeeDiscount', '/message/RitualsGetEmployeeDiscount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsGetEmployeeDiscount
     */
    *saga() {
        yield fork(listener$1l);
    }
}
// Export the state handling singleton
//
const ritualsGetEmployeeDiscount = new RitualsGetEmployeeDiscount();
// Setup the Saga implementation
//
function* fetchCall$1l(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsGetEmployeeDiscount, 'fetchData'], action);
        yield put(ritualsGetEmployeeDiscount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsGetEmployeeDiscount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1l() {
    yield takeEvery(getType(ritualsGetEmployeeDiscount.actions.fetch), fetchCall$1l);
}

/**
 * Implements RitualsGetOrderForReturn fetch for the Redux store
 *
 * @export
 * @class RitualsGetOrderForReturn
 * @extends {StoreFetchableData<EVA.Rituals.RitualsGetOrderForReturn, EVA.Rituals.RitualsGetOrderForReturnResponse>}
 */
class RitualsGetOrderForReturn extends StoreFetchableData {
    /**
     * Creates an instance of RitualsGetOrderForReturn
     *
     * @memberof RitualsGetOrderForReturn
     */
    constructor() {
        super('ritualsGetOrderForReturn', '/message/RitualsGetOrderForReturn');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsGetOrderForReturn
     */
    *saga() {
        yield fork(listener$1k);
    }
}
// Export the state handling singleton
//
const ritualsGetOrderForReturn = new RitualsGetOrderForReturn();
// Setup the Saga implementation
//
function* fetchCall$1k(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsGetOrderForReturn, 'fetchData'], action);
        yield put(ritualsGetOrderForReturn.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsGetOrderForReturn.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1k() {
    yield takeEvery(getType(ritualsGetOrderForReturn.actions.fetch), fetchCall$1k);
}

/**
 * Implements RitualsGetUserDataForUser fetch for the Redux store
 *
 * @export
 * @class RitualsGetUserDataForUser
 * @extends {StoreFetchableData<EVA.Rituals.RitualsGetUserDataForUser, EVA.Rituals.RitualsGetUserDataForUserResponse>}
 */
class RitualsGetUserDataForUser extends StoreFetchableData {
    /**
     * Creates an instance of RitualsGetUserDataForUser
     *
     * @memberof RitualsGetUserDataForUser
     */
    constructor() {
        super('ritualsGetUserDataForUser', '/message/RitualsGetUserDataForUser');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsGetUserDataForUser
     */
    *saga() {
        yield fork(listener$1j);
    }
}
// Export the state handling singleton
//
const ritualsGetUserDataForUser = new RitualsGetUserDataForUser();
// Setup the Saga implementation
//
function* fetchCall$1j(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsGetUserDataForUser, 'fetchData'], action);
        yield put(ritualsGetUserDataForUser.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsGetUserDataForUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1j() {
    yield takeEvery(getType(ritualsGetUserDataForUser.actions.fetch), fetchCall$1j);
}

/**
 * Implements RitualsListAvailablePickupStores fetch for the Redux store
 *
 * @export
 * @class RitualsListAvailablePickupStores
 * @extends {StoreFetchableData<EVA.Rituals.RitualsListAvailablePickupStores, EVA.Rituals.RitualsListAvailablePickupStoresResponse>}
 */
class RitualsListAvailablePickupStores extends StoreFetchableData {
    /**
     * Creates an instance of RitualsListAvailablePickupStores
     *
     * @memberof RitualsListAvailablePickupStores
     */
    constructor() {
        super('ritualsListAvailablePickupStores', '/message/RitualsListAvailablePickupStores');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsListAvailablePickupStores
     */
    *saga() {
        yield fork(listener$1i);
    }
}
// Export the state handling singleton
//
const ritualsListAvailablePickupStores = new RitualsListAvailablePickupStores();
// Setup the Saga implementation
//
function* fetchCall$1i(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsListAvailablePickupStores, 'fetchData'], action);
        yield put(ritualsListAvailablePickupStores.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsListAvailablePickupStores.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1i() {
    yield takeEvery(getType(ritualsListAvailablePickupStores.actions.fetch), fetchCall$1i);
}

/**
 * Implements RitualsPaazlGetShippingOptionsForOrder fetch for the Redux store
 *
 * @export
 * @class RitualsPaazlGetShippingOptionsForOrder
 * @extends {StoreFetchableData<EVA.Rituals.RitualsPaazlGetShippingOptionsForOrder, EVA.Carrier.Paazl.PaazlGetShippingOptionsForOrderResponse>}
 */
class RitualsPaazlGetShippingOptionsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of RitualsPaazlGetShippingOptionsForOrder
     *
     * @memberof RitualsPaazlGetShippingOptionsForOrder
     */
    constructor() {
        super('ritualsPaazlGetShippingOptionsForOrder', '/message/RitualsPaazlGetShippingOptionsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsPaazlGetShippingOptionsForOrder
     */
    *saga() {
        yield fork(listener$1h);
    }
}
// Export the state handling singleton
//
const ritualsPaazlGetShippingOptionsForOrder = new RitualsPaazlGetShippingOptionsForOrder();
// Setup the Saga implementation
//
function* fetchCall$1h(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsPaazlGetShippingOptionsForOrder, 'fetchData'], action);
        yield put(ritualsPaazlGetShippingOptionsForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsPaazlGetShippingOptionsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1h() {
    yield takeEvery(getType(ritualsPaazlGetShippingOptionsForOrder.actions.fetch), fetchCall$1h);
}

/**
 * Implements RitualsPaazlUpdateShippingOptionsForOrder fetch for the Redux store
 *
 * @export
 * @class RitualsPaazlUpdateShippingOptionsForOrder
 * @extends {StoreFetchableData<EVA.Rituals.RitualsPaazlUpdateShippingOptionsForOrder, EVA.Core.EmptyResponseMessage>}
 */
class RitualsPaazlUpdateShippingOptionsForOrder extends StoreFetchableData {
    /**
     * Creates an instance of RitualsPaazlUpdateShippingOptionsForOrder
     *
     * @memberof RitualsPaazlUpdateShippingOptionsForOrder
     */
    constructor() {
        super('ritualsPaazlUpdateShippingOptionsForOrder', '/message/RitualsPaazlUpdateShippingOptionsForOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsPaazlUpdateShippingOptionsForOrder
     */
    *saga() {
        yield fork(listener$1g);
    }
}
// Export the state handling singleton
//
const ritualsPaazlUpdateShippingOptionsForOrder = new RitualsPaazlUpdateShippingOptionsForOrder();
// Setup the Saga implementation
//
function* fetchCall$1g(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsPaazlUpdateShippingOptionsForOrder, 'fetchData'], action);
        yield put(ritualsPaazlUpdateShippingOptionsForOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsPaazlUpdateShippingOptionsForOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1g() {
    yield takeEvery(getType(ritualsPaazlUpdateShippingOptionsForOrder.actions.fetch), fetchCall$1g);
}

/**
 * Implements RitualsPrintTalismanDocuments fetch for the Redux store
 *
 * @export
 * @class RitualsPrintTalismanDocuments
 * @extends {StoreFetchableData<EVA.Rituals.RitualsPrintTalismanDocuments, EVA.Core.EmptyResponseMessage>}
 */
class RitualsPrintTalismanDocuments extends StoreFetchableData {
    /**
     * Creates an instance of RitualsPrintTalismanDocuments
     *
     * @memberof RitualsPrintTalismanDocuments
     */
    constructor() {
        super('ritualsPrintTalismanDocuments', '/message/RitualsPrintTalismanDocuments');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsPrintTalismanDocuments
     */
    *saga() {
        yield fork(listener$1f);
    }
}
// Export the state handling singleton
//
const ritualsPrintTalismanDocuments = new RitualsPrintTalismanDocuments();
// Setup the Saga implementation
//
function* fetchCall$1f(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsPrintTalismanDocuments, 'fetchData'], action);
        yield put(ritualsPrintTalismanDocuments.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsPrintTalismanDocuments.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1f() {
    yield takeEvery(getType(ritualsPrintTalismanDocuments.actions.fetch), fetchCall$1f);
}

/**
 * Implements RitualsScanLoyaltyCard fetch for the Redux store
 *
 * @export
 * @class RitualsScanLoyaltyCard
 * @extends {StoreFetchableData<EVA.Rituals.RitualsScanLoyaltyCard, EVA.Rituals.RitualsScanLoyaltyCardResponse>}
 */
class RitualsScanLoyaltyCard extends StoreFetchableData {
    /**
     * Creates an instance of RitualsScanLoyaltyCard
     *
     * @memberof RitualsScanLoyaltyCard
     */
    constructor() {
        super('ritualsScanLoyaltyCard', '/message/RitualsScanLoyaltyCard');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsScanLoyaltyCard
     */
    *saga() {
        yield fork(listener$1e);
    }
}
// Export the state handling singleton
//
const ritualsScanLoyaltyCard = new RitualsScanLoyaltyCard();
// Setup the Saga implementation
//
function* fetchCall$1e(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsScanLoyaltyCard, 'fetchData'], action);
        yield put(ritualsScanLoyaltyCard.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsScanLoyaltyCard.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1e() {
    yield takeEvery(getType(ritualsScanLoyaltyCard.actions.fetch), fetchCall$1e);
}

/**
 * Implements RitualsSendAddressFormLinkFromCart fetch for the Redux store
 *
 * @export
 * @class RitualsSendAddressFormLinkFromCart
 * @extends {StoreFetchableData<EVA.Rituals.RitualsSendAddressFormLinkFromCart, EVA.Rituals.SOSUrlResponse>}
 */
class RitualsSendAddressFormLinkFromCart extends StoreFetchableData {
    /**
     * Creates an instance of RitualsSendAddressFormLinkFromCart
     *
     * @memberof RitualsSendAddressFormLinkFromCart
     */
    constructor() {
        super('ritualsSendAddressFormLinkFromCart', '/message/RitualsSendAddressFormLinkFromCart');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsSendAddressFormLinkFromCart
     */
    *saga() {
        yield fork(listener$1d);
    }
}
// Export the state handling singleton
//
const ritualsSendAddressFormLinkFromCart = new RitualsSendAddressFormLinkFromCart();
// Setup the Saga implementation
//
function* fetchCall$1d(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsSendAddressFormLinkFromCart, 'fetchData'], action);
        yield put(ritualsSendAddressFormLinkFromCart.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsSendAddressFormLinkFromCart.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1d() {
    yield takeEvery(getType(ritualsSendAddressFormLinkFromCart.actions.fetch), fetchCall$1d);
}

/**
 * Implements RitualsSendCarouselFromCart fetch for the Redux store
 *
 * @export
 * @class RitualsSendCarouselFromCart
 * @extends {StoreFetchableData<EVA.Rituals.RitualsSendCarouselFromCart, EVA.Core.EmptyResponseMessage>}
 */
class RitualsSendCarouselFromCart extends StoreFetchableData {
    /**
     * Creates an instance of RitualsSendCarouselFromCart
     *
     * @memberof RitualsSendCarouselFromCart
     */
    constructor() {
        super('ritualsSendCarouselFromCart', '/message/RitualsSendCarouselFromCart');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsSendCarouselFromCart
     */
    *saga() {
        yield fork(listener$1c);
    }
}
// Export the state handling singleton
//
const ritualsSendCarouselFromCart = new RitualsSendCarouselFromCart();
// Setup the Saga implementation
//
function* fetchCall$1c(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsSendCarouselFromCart, 'fetchData'], action);
        yield put(ritualsSendCarouselFromCart.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsSendCarouselFromCart.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1c() {
    yield takeEvery(getType(ritualsSendCarouselFromCart.actions.fetch), fetchCall$1c);
}

/**
 * Implements RitualsSendPaazlLinkFromCart fetch for the Redux store
 *
 * @export
 * @class RitualsSendPaazlLinkFromCart
 * @extends {StoreFetchableData<EVA.Rituals.RitualsSendPaazlLinkFromCart, EVA.Rituals.SOSUrlResponse>}
 */
class RitualsSendPaazlLinkFromCart extends StoreFetchableData {
    /**
     * Creates an instance of RitualsSendPaazlLinkFromCart
     *
     * @memberof RitualsSendPaazlLinkFromCart
     */
    constructor() {
        super('ritualsSendPaazlLinkFromCart', '/message/RitualsSendPaazlLinkFromCart');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsSendPaazlLinkFromCart
     */
    *saga() {
        yield fork(listener$1b);
    }
}
// Export the state handling singleton
//
const ritualsSendPaazlLinkFromCart = new RitualsSendPaazlLinkFromCart();
// Setup the Saga implementation
//
function* fetchCall$1b(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsSendPaazlLinkFromCart, 'fetchData'], action);
        yield put(ritualsSendPaazlLinkFromCart.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsSendPaazlLinkFromCart.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1b() {
    yield takeEvery(getType(ritualsSendPaazlLinkFromCart.actions.fetch), fetchCall$1b);
}

/**
 * Implements RitualsSendPaymentLinkFromCart fetch for the Redux store
 *
 * @export
 * @class RitualsSendPaymentLinkFromCart
 * @extends {StoreFetchableData<EVA.Rituals.RitualsSendPaymentLinkFromCart, EVA.Rituals.SOSUrlResponse>}
 */
class RitualsSendPaymentLinkFromCart extends StoreFetchableData {
    /**
     * Creates an instance of RitualsSendPaymentLinkFromCart
     *
     * @memberof RitualsSendPaymentLinkFromCart
     */
    constructor() {
        super('ritualsSendPaymentLinkFromCart', '/message/RitualsSendPaymentLinkFromCart');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsSendPaymentLinkFromCart
     */
    *saga() {
        yield fork(listener$1a);
    }
}
// Export the state handling singleton
//
const ritualsSendPaymentLinkFromCart = new RitualsSendPaymentLinkFromCart();
// Setup the Saga implementation
//
function* fetchCall$1a(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsSendPaymentLinkFromCart, 'fetchData'], action);
        yield put(ritualsSendPaymentLinkFromCart.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsSendPaymentLinkFromCart.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1a() {
    yield takeEvery(getType(ritualsSendPaymentLinkFromCart.actions.fetch), fetchCall$1a);
}

/**
 * Implements RitualsSendReceiptFromCart fetch for the Redux store
 *
 * @export
 * @class RitualsSendReceiptFromCart
 * @extends {StoreFetchableData<EVA.Rituals.RitualsSendReceiptFromCart, EVA.Core.EmptyResponseMessage>}
 */
class RitualsSendReceiptFromCart extends StoreFetchableData {
    /**
     * Creates an instance of RitualsSendReceiptFromCart
     *
     * @memberof RitualsSendReceiptFromCart
     */
    constructor() {
        super('ritualsSendReceiptFromCart', '/message/RitualsSendReceiptFromCart');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsSendReceiptFromCart
     */
    *saga() {
        yield fork(listener$19);
    }
}
// Export the state handling singleton
//
const ritualsSendReceiptFromCart = new RitualsSendReceiptFromCart();
// Setup the Saga implementation
//
function* fetchCall$19(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsSendReceiptFromCart, 'fetchData'], action);
        yield put(ritualsSendReceiptFromCart.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsSendReceiptFromCart.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$19() {
    yield takeEvery(getType(ritualsSendReceiptFromCart.actions.fetch), fetchCall$19);
}

/**
 * Implements RitualsSetCartData fetch for the Redux store
 *
 * @export
 * @class RitualsSetCartData
 * @extends {StoreFetchableData<EVA.Rituals.RitualsSetCartData, EVA.Core.EmptyResponseMessage>}
 */
class RitualsSetCartData extends StoreFetchableData {
    /**
     * Creates an instance of RitualsSetCartData
     *
     * @memberof RitualsSetCartData
     */
    constructor() {
        super('ritualsSetCartData', '/message/RitualsSetCartData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsSetCartData
     */
    *saga() {
        yield fork(listener$18);
    }
}
// Export the state handling singleton
//
const ritualsSetCartData = new RitualsSetCartData();
// Setup the Saga implementation
//
function* fetchCall$18(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([ritualsSetCartData, 'fetchData'], action);
        yield put(ritualsSetCartData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(ritualsSetCartData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$18() {
    yield takeEvery(getType(ritualsSetCartData.actions.fetch), fetchCall$18);
}

/**
 * Implements RitualsUpdateLoyalty fetch for the Redux store
 *
 * @export
 * @class RitualsUpdateLoyalty
 * @extends {StoreFetchableData<EVA.Rituals.RitualsUpdateLoyalty, EVA.Core.EmptyResponseMessage>}
 */
class RitualsUpdateLoyalty extends StoreFetchableData {
    /**
     * Creates an instance of RitualsUpdateLoyalty
     *
     * @memberof RitualsUpdateLoyalty
     */
    constructor() {
        super('ritualsUpdateLoyalty', '/message/RitualsUpdateLoyalty');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof RitualsUpdateLoyalty
     */
    *saga() {
        yield fork(listener$17);
    }
}
// Export the state handling singleton
//
const ritualsUpdateLoyalty = new RitualsUpdateLoyalty();
// Setup the Saga implementation
//
function* fetchCall$17(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([ritualsUpdateLoyalty, 'fetchData'], action);
        yield put(ritualsUpdateLoyalty.actions.response(data));
        fetchActionPayload.fetchResolve(data);
        // Once the RitualsUpdateLoyalty call is done, the user might gets a gift from rituals. In that case the cart and requirements will change
        // therefor we will be doing a getShoppingCartInfo call
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction(undefined, action.payload.chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        });
    }
    catch (err) {
        yield put(ritualsUpdateLoyalty.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$17() {
    yield takeEvery(getType(ritualsUpdateLoyalty.actions.fetch), fetchCall$17);
}

/**
 * Implements SearchOrders fetch for the Redux store
 *
 * @export
 * @class SearchOrders
 * @extends {StoreFetchableData<EVA.Core.SearchOrders, EVA.Core.SearchOrdersResponse>}
 */
class SearchOrders extends StoreFetchableData {
    /**
     * Creates an instance of SearchOrders
     *
     * @memberof SearchOrders
     */
    constructor() {
        super('searchOrders', '/message/SearchOrders');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SearchOrders
     */
    *saga() {
        yield fork(listener$16);
    }
}
// Export the state handling singleton
//
const searchOrders = new SearchOrders();
// Setup the Saga implementation
//
function* fetchCall$16(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([searchOrders, 'fetchData'], action);
        yield put(searchOrders.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(searchOrders.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$16() {
    yield takeEvery(getType(searchOrders.actions.fetch), fetchCall$16);
}

/**
 * Implements SearchProducts fetch for the Redux store
 *
 * @export
 * @class SearchProducts
 * @extends {StoreFetchableData<EVA.Core.SearchProducts, EVA.Core.SearchProductsResponse>}
 */
class SearchProducts extends StoreFetchableData {
    /**
     * Creates an instance of SearchProducts
     *
     * @memberof SearchProducts
     */
    constructor() {
        // Add the paginate action
        //
        const additionalActions = {
            paginate: createAction('PAGINATE_searchProducts', (request) => {
                return ({ type: 'PAGINATE_searchProducts', payload: request });
            }),
        };
        super('searchProducts', '/message/SearchProducts', undefined, { addDefaultProductPropertiesPath: 'IncludedFields' }, additionalActions);
    }
    /**
     * Initial state with extended properties (pages)
     *
     * @type {ISearchProductsState}
     * @memberof SearchProducts
     */
    get initialState() {
        return {
            isFetching: false,
            request: null,
            response: null,
            error: null,
            chainStarter: this.name,
            pages: {},
        };
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SearchProducts
     */
    *saga() {
        yield fork(listener$15);
    }
    /**
     * The extended collection of reducer functions
     *
     * @protected
     * @returns {{ [ reducerName: string ]: ( state: any, action: AnyAction ) => any }}
     * @memberof SearchProductsAvailabilityIndication
     */
    getReducerFunctions() {
        const baseReducerFuncs = super.getReducerFunctions();
        baseReducerFuncs.pages = (state = [], action) => {
            switch (action.type) {
                case getType(this.actions.paginate):
                    const pageData = action.payload;
                    const pageNr = get(pageData, 'searchProducts.Page');
                    // Check if the request filters have changed
                    // If so purge the existing pages
                    //
                    const firstPage = first(values(state));
                    const firstRequest = JSON.stringify(omit(get(firstPage, 'searchProductsRequest'), 'Page'));
                    const currentRequest = JSON.stringify(omit(get(pageData, 'searchProductsRequest'), 'Page'));
                    const sameRequest = firstRequest === currentRequest;
                    if (!pageNr) {
                        log$1('[EVA-SDK-REDUX:searchProducts] Not adding page. Missing page nr', pageData);
                        // Don't add the response if missing Page number
                        //
                        return state;
                    }
                    else if (firstPage && !sameRequest) {
                        const newPages = {};
                        newPages[pageNr] = pageData;
                        log$1('[EVA-SDK-REDUX:searchProducts] Reset pages', newPages);
                        return newPages;
                    }
                    else {
                        // Add the new page into the existing empty set
                        //
                        const newPages = cloneDeep(state);
                        newPages[pageNr] = pageData;
                        log$1('[EVA-SDK-REDUX:searchProducts] Added new page to existing set', newPages);
                        return newPages;
                    }
                default:
                    return state;
            }
        };
        return baseReducerFuncs;
    }
}
// Export the state handling singleton
//
const searchProducts = new SearchProducts();
// Setup the Saga implementation
//
function* fetchCall$15(action) {
    var _a;
    // Merge the settings into the request to fetch availability and discount data
    //
    const fetchActionPayload = action.payload;
    fetchActionPayload.request = Object.assign({ Options: Object.assign({}, settings.productSearchOptions) }, fetchActionPayload.request);
    // Set current order id if needed
    //
    if (settings.currentOrderId && fetchActionPayload.request.Options) {
        fetchActionPayload.request.Options.OrderID = settings.currentOrderId;
    }
    // Ensure prefigure discounts is not requested if unavailable due to old
    // discount engine
    //
    const applicationConfiguration = ((_a = getApplicationConfiguration.getState().response) === null || _a === void 0 ? void 0 : _a.Configuration) || {};
    if (false === applicationConfiguration['EVA:NewDiscountEngineEnabled'] && fetchActionPayload.request.Options) {
        fetchActionPayload.request.Options.IncludePrefigureDiscounts = false;
    }
    try {
        const data = yield call([searchProducts, 'fetchData'], action);
        yield put(searchProducts.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
        // Update the pagination
        //
        const newPage = {
            searchProductsRequest: action.payload.request,
            searchProducts: data,
        };
        yield put(searchProducts.actions.paginate(newPage));
    }
    catch (err) {
        yield put(searchProducts.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$15() {
    yield takeEvery(getType(searchProducts.actions.fetch), fetchCall$15);
}

/**
 * Implements SearchUsers fetch for the Redux store
 *
 * @export
 * @class SearchUsers
 * @extends {StoreFetchableData<EVA.Core.SearchUsers, EVA.Core.SearchUsersResponse>}
 */
class SearchUsers extends StoreFetchableData {
    /**
     * Creates an instance of SearchUsers
     *
     * @memberof SearchUsers
     */
    constructor() {
        super('searchUsers', '/message/SearchUsers');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SearchUsers
     */
    *saga() {
        yield fork(listener$14);
    }
}
// Export the state handling singleton
//
const searchUsers = new SearchUsers();
// Setup the Saga implementation
//
function* fetchCall$14(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([searchUsers, 'fetchData'], action);
        yield put(searchUsers.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(searchUsers.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$14() {
    yield takeEvery(getType(searchUsers.actions.fetch), fetchCall$14);
}

/**
 * Implements SetCustomUserData fetch for the Redux store
 *
 * @export
 * @class SetCustomUserData
 * @extends {StoreFetchableData<EVA.Core.SetCustomUserData, EVA.Core.EmptyResponseMessage>}
 */
class SetCustomUserData extends StoreFetchableData {
    /**
     * Creates an instance of SetCustomUserData
     *
     * @memberof SetCustomUserData
     */
    constructor() {
        super('setCustomUserData', '/message/SetCustomUserData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetCustomUserData
     */
    *saga() {
        yield fork(listener$13);
    }
}
// Export the state handling singleton
//
const setCustomUserData = new SetCustomUserData();
// Setup the Saga implementation
//
function* fetchCall$13(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setCustomUserData, 'fetchData'], action);
        yield put(setCustomUserData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setCustomUserData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$13() {
    yield takeEvery(getType(setCustomUserData.actions.fetch), fetchCall$13);
}

/**
 * Implements SetCustomerReferencesOnOrder fetch for the Redux store
 *
 * @export
 * @class SetCustomerReferencesOnOrder
 * @extends {StoreFetchableData<EVA.Core.SetCustomerReferencesOnOrder, EVA.Core.EmptyResponseMessage>}
 */
class SetCustomerReferencesOnOrder extends StoreFetchableData {
    /**
     * Creates an instance of SetCustomerReferencesOnOrder
     *
     * @memberof SetCustomerReferencesOnOrder
     */
    constructor() {
        super('setCustomerReferencesOnOrder', '/message/SetCustomerReferencesOnOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetCustomerReferencesOnOrder
     */
    *saga() {
        yield fork(listener$12);
    }
}
// Export the state handling singleton
//
const setCustomerReferencesOnOrder = new SetCustomerReferencesOnOrder();
// Setup the Saga implementation
//
function* fetchCall$12(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setCustomerReferencesOnOrder, 'fetchData'], action);
        yield put(setCustomerReferencesOnOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setCustomerReferencesOnOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$12() {
    yield takeEvery(getType(setCustomerReferencesOnOrder.actions.fetch), fetchCall$12);
}

/**
 * Implements SetDefaultBillingAddress fetch for the Redux store
 *
 * @export
 * @class SetDefaultBillingAddress
 * @extends {StoreFetchableData<EVA.Core.SetDefaultBillingAddress, EVA.Core.EmptyResponseMessage>}
 */
class SetDefaultBillingAddress extends StoreFetchableData {
    /**
     * Creates an instance of SetDefaultBillingAddress
     *
     * @memberof SetDefaultBillingAddress
     */
    constructor() {
        super('setDefaultBillingAddress', '/message/SetDefaultBillingAddress');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetDefaultBillingAddress
     */
    *saga() {
        yield fork(listener$11);
    }
}
// Export the state handling singleton
//
const setDefaultBillingAddress = new SetDefaultBillingAddress();
// Setup the Saga implementation
//
function* fetchCall$11(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setDefaultBillingAddress, 'fetchData'], action);
        yield put(setDefaultBillingAddress.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setDefaultBillingAddress.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$11() {
    yield takeEvery(getType(setDefaultBillingAddress.actions.fetch), fetchCall$11);
}

/**
 * Implements SetDefaultShippingAddress fetch for the Redux store
 *
 * @export
 * @class SetDefaultShippingAddress
 * @extends {StoreFetchableData<EVA.Core.SetDefaultShippingAddress, EVA.Core.EmptyResponseMessage>}
 */
class SetDefaultShippingAddress extends StoreFetchableData {
    /**
     * Creates an instance of SetDefaultShippingAddress
     *
     * @memberof SetDefaultShippingAddress
     */
    constructor() {
        super('setDefaultShippingAddress', '/message/SetDefaultShippingAddress');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetDefaultShippingAddress
     */
    *saga() {
        yield fork(listener$10);
    }
}
// Export the state handling singleton
//
const setDefaultShippingAddress = new SetDefaultShippingAddress();
// Setup the Saga implementation
//
function* fetchCall$10(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setDefaultShippingAddress, 'fetchData'], action);
        yield put(setDefaultShippingAddress.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setDefaultShippingAddress.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$10() {
    yield takeEvery(getType(setDefaultShippingAddress.actions.fetch), fetchCall$10);
}

/**
 * Implements SetDiscountProductOption fetch for the Redux store
 *
 * @export
 * @class SetDiscountProductOption
 * @extends {StoreFetchableData<EVA.Core.SetDiscountProductOption, EVA.Core.EmptyResponseMessage>}
 */
class SetDiscountProductOption extends StoreFetchableData {
    /**
     * Creates an instance of SetDiscountProductOption
     *
     * @memberof SetDiscountProductOption
     */
    constructor() {
        super('setDiscountProductOption', '/message/SetDiscountProductOption');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetDiscountProductOption
     */
    *saga() {
        yield fork(listener$$);
    }
}
// Export the state handling singleton
//
const setDiscountProductOption = new SetDiscountProductOption();
// Setup the Saga implementation
//
function* fetchCall$$(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([setDiscountProductOption, 'fetchData'], action);
        yield put(setDiscountProductOption.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // AddServiceProductToOrder is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(setDiscountProductOption.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$$() {
    yield takeEvery(getType(setDiscountProductOption.actions.fetch), fetchCall$$);
}

/**
 * Implements SetElectronicInvoiceData fetch for the Redux store
 *
 * @export
 * @class SetElectronicInvoiceData
 * @extends {StoreFetchableData<EVA.Core.SetElectronicInvoiceData, EVA.Core.EmptyResponseMessage>}
 */
class SetElectronicInvoiceData extends StoreFetchableData {
    /**
     * Creates an instance of SetElectronicInvoiceData
     *
     * @memberof SetElectronicInvoiceData
     */
    constructor() {
        super('setElectronicInvoiceData', '/message/SetElectronicInvoiceData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetElectronicInvoiceData
     */
    *saga() {
        yield fork(listener$_);
    }
}
// Export the state handling singleton
//
const setElectronicInvoiceData = new SetElectronicInvoiceData();
// Setup the Saga implementation
//
function* fetchCall$_(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setElectronicInvoiceData, 'fetchData'], action);
        yield put(setElectronicInvoiceData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setElectronicInvoiceData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$_() {
    yield takeEvery(getType(setElectronicInvoiceData.actions.fetch), fetchCall$_);
}

/**
 * Implements SetFiscalOrderData fetch for the Redux store
 *
 * @export
 * @class SetFiscalOrderData
 * @extends {StoreFetchableData<EVA.Core.SetFiscalOrderData, EVA.Core.EmptyResponseMessage>}
 */
class SetFiscalOrderData extends StoreFetchableData {
    /**
     * Creates an instance of SetFiscalOrderData
     *
     * @memberof SetFiscalOrderData
     */
    constructor() {
        super('setFiscalOrderData', '/message/SetFiscalOrderData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetFiscalOrderData
     */
    *saga() {
        yield fork(listener$Z);
    }
}
// Export the state handling singleton
//
const setFiscalOrderData = new SetFiscalOrderData();
// Setup the Saga implementation
//
function* fetchCall$Z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setFiscalOrderData, 'fetchData'], action);
        yield put(setFiscalOrderData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setFiscalOrderData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$Z() {
    yield takeEvery(getType(setFiscalOrderData.actions.fetch), fetchCall$Z);
}

/**
 * Implements SetGiftWrappingOptionsOnOrder fetch for the Redux store
 *
 * @export
 * @class SetGiftWrappingOptionsOnOrder
 * @extends {StoreFetchableData<EVA.Core.SetGiftWrappingOptionsOnOrder, EVA.Core.EmptyResponseMessage>}
 */
class SetGiftWrappingOptionsOnOrder extends StoreFetchableData {
    /**
     * Creates an instance of SetGiftWrappingOptionsOnOrder
     *
     * @memberof SetGiftWrappingOptionsOnOrder
     */
    constructor() {
        super('setGiftWrappingOptionsOnOrder', '/message/SetGiftWrappingOptionsOnOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetGiftWrappingOptionsOnOrder
     */
    *saga() {
        yield fork(listener$Y);
    }
}
// Export the state handling singleton
//
const setGiftWrappingOptionsOnOrder = new SetGiftWrappingOptionsOnOrder();
// Setup the Saga implementation
//
function* fetchCall$Y(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([setGiftWrappingOptionsOnOrder, 'fetchData'], action);
        yield put(setGiftWrappingOptionsOnOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Fetch order gift wrap options
        //
        const orderId = get(action, 'payload.request.OrderID');
        const getGiftWrapOptionsRequest = {
            OrderID: orderId,
        };
        const [getGiftWrappingOptionsForOrderAction, , getGiftWrappingOptionsForOrderChainPromise] = getGiftWrappingOptionsForOrder.createFetchAction(getGiftWrapOptionsRequest, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getGiftWrappingOptionsForOrderAction);
        // Update the shopping cart state
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getGiftWrappingOptionsForOrderChainPromise, getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(setGiftWrappingOptionsOnOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$Y() {
    yield takeEvery(getType(setGiftWrappingOptionsOnOrder.actions.fetch), fetchCall$Y);
}

/**
 * Implements SetLineForReturnToSupplierShipment fetch for the Redux store
 *
 * @export
 * @class SetLineForReturnToSupplierShipment
 * @extends {StoreFetchableData<EVA.Core.SetLineForReturnToSupplierShipment, EVA.Core.EmptyResponseMessage>}
 */
class SetLineForReturnToSupplierShipment extends StoreFetchableData {
    /**
     * Creates an instance of SetLineForReturnToSupplierShipment
     *
     * @memberof SetLineForReturnToSupplierShipment
     */
    constructor() {
        super('setLineForReturnToSupplierShipment', '/message/SetLineForReturnToSupplierShipment');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetLineForReturnToSupplierShipment
     */
    *saga() {
        yield fork(listener$X);
    }
}
// Export the state handling singleton
//
const setLineForReturnToSupplierShipment = new SetLineForReturnToSupplierShipment();
// Setup the Saga implementation
//
function* fetchCall$X(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setLineForReturnToSupplierShipment, 'fetchData'], action);
        yield put(setLineForReturnToSupplierShipment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setLineForReturnToSupplierShipment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$X() {
    yield takeEvery(getType(setLineForReturnToSupplierShipment.actions.fetch), fetchCall$X);
}

/**
 * Implements SetLotteryNumber fetch for the Redux store
 *
 * @export
 * @class SetLotteryNumber
 * @extends {StoreFetchableData<EVA.Core.SetLotteryNumber, EVA.Core.EmptyResponseMessage>}
 */
class SetLotteryNumber extends StoreFetchableData {
    /**
     * Creates an instance of SetLotteryNumber
     *
     * @memberof SetLotteryNumber
     */
    constructor() {
        super('setLotteryNumber', '/message/SetLotteryNumber');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetLotteryNumber
     */
    *saga() {
        yield fork(listener$W);
    }
}
// Export the state handling singleton
//
const setLotteryNumber = new SetLotteryNumber();
// Setup the Saga implementation
//
function* fetchCall$W(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setLotteryNumber, 'fetchData'], action);
        yield put(setLotteryNumber.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setLotteryNumber.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$W() {
    yield takeEvery(getType(setLotteryNumber.actions.fetch), fetchCall$W);
}

/**
 * Implements SetOrderFulfillmentOptions fetch for the Redux store
 *
 * @export
 * @class SetOrderFulfillmentOptions
 * @extends {StoreFetchableData<EVA.Core.SetOrderFulfillmentOptions, EVA.Core.EmptyResponseMessage>}
 */
class SetOrderFulfillmentOptions extends StoreFetchableData {
    /**
     * Creates an instance of SetOrderFulfillmentOptions
     *
     * @memberof SetOrderFulfillmentOptions
     */
    constructor() {
        super('setOrderFulfillmentOptions', '/message/SetOrderFulfillmentOptions');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetOrderFulfillmentOptions
     */
    *saga() {
        yield fork(listener$V);
    }
}
// Export the state handling singleton
//
const setOrderFulfillmentOptions = new SetOrderFulfillmentOptions();
// Setup the Saga implementation
//
function* fetchCall$V(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setOrderFulfillmentOptions, 'fetchData'], action);
        yield put(setOrderFulfillmentOptions.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setOrderFulfillmentOptions.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$V() {
    yield takeEvery(getType(setOrderFulfillmentOptions.actions.fetch), fetchCall$V);
}

/**
 * Implements SetOrderSoldBy fetch for the Redux store
 *
 * @export
 * @class SetOrderSoldBy
 * @extends {StoreFetchableData<EVA.Core.SetOrderSoldBy, EVA.Core.EmptyResponseMessage>}
 */
class SetOrderSoldBy extends StoreFetchableData {
    /**
     * Creates an instance of SetOrderSoldBy
     *
     * @memberof SetOrderSoldBy
     */
    constructor() {
        super('setOrderSoldBy', '/message/SetOrderSoldBy');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetOrderSoldBy
     */
    *saga() {
        yield fork(listener$U);
    }
}
// Export the state handling singleton
//
const setOrderSoldBy = new SetOrderSoldBy();
// Setup the Saga implementation
//
function* fetchCall$U(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setOrderSoldBy, 'fetchData'], action);
        yield put(setOrderSoldBy.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setOrderSoldBy.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$U() {
    yield takeEvery(getType(setOrderSoldBy.actions.fetch), fetchCall$U);
}

/**
 * Implements SetPCIQuestionAnswerForFinancialPeriod fetch for the Redux store
 *
 * @export
 * @class SetPCIQuestionAnswerForFinancialPeriod
 * @extends {StoreFetchableData<EVA.Core.SetPCIQuestionAnswerForFinancialPeriod, EVA.Core.EmptyResponseMessage>}
 */
class SetPCIQuestionAnswerForFinancialPeriod extends StoreFetchableData {
    /**
     * Creates an instance of SetPCIQuestionAnswerForFinancialPeriod
     *
     * @memberof SetPCIQuestionAnswerForFinancialPeriod
     */
    constructor() {
        super('setPCIQuestionAnswerForFinancialPeriod', '/message/SetPCIQuestionAnswerForFinancialPeriod');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetPCIQuestionAnswerForFinancialPeriod
     */
    *saga() {
        yield fork(listener$T);
    }
}
// Export the state handling singleton
//
const setPCIQuestionAnswerForFinancialPeriod = new SetPCIQuestionAnswerForFinancialPeriod();
// Setup the Saga implementation
//
function* fetchCall$T(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setPCIQuestionAnswerForFinancialPeriod, 'fetchData'], action);
        yield put(setPCIQuestionAnswerForFinancialPeriod.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setPCIQuestionAnswerForFinancialPeriod.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$T() {
    yield takeEvery(getType(setPCIQuestionAnswerForFinancialPeriod.actions.fetch), fetchCall$T);
}

/**
 * Implements SetPickProductDiscountOptionsForOrderLine fetch for the Redux store
 *
 * @export
 * @class SetPickProductDiscountOptionsForOrderLine
 * @extends {StoreFetchableData<EVA.Core.SetPickProductDiscountOptionsForOrderLine, EVA.Core.EmptyResponseMessage>}
 */
class SetPickProductDiscountOptionsForOrderLine extends StoreFetchableData {
    /**
     * Creates an instance of SetPickProductDiscountOptionsForOrderLine
     *
     * @memberof SetPickProductDiscountOptionsForOrderLine
     */
    constructor() {
        super('setPickProductDiscountOptionsForOrderLine', '/message/SetPickProductDiscountOptionsForOrderLine');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetPickProductDiscountOptionsForOrderLine
     */
    *saga() {
        yield fork(listener$S);
    }
}
// Export the state handling singleton
//
const setPickProductDiscountOptionsForOrderLine = new SetPickProductDiscountOptionsForOrderLine();
// Setup the Saga implementation
//
function* fetchCall$S(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([setPickProductDiscountOptionsForOrderLine, 'fetchData'], action);
        yield put(setPickProductDiscountOptionsForOrderLine.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // AddProductToOrder is always for the current cart
        //
        const currentOrderId = getShoppingCartInfo.getCurrentOrderId();
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: currentOrderId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(setPickProductDiscountOptionsForOrderLine.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$S() {
    yield takeEvery(getType(setPickProductDiscountOptionsForOrderLine.actions.fetch), fetchCall$S);
}

/**
 * Implements SetPickupOrganizationUnit fetch for the Redux store
 *
 * @export
 * @class SetPickupOrganizationUnit
 * @extends {StoreFetchableData<EVA.Core.SetPickupOrganizationUnit, EVA.Core.SimpleShoppingCartResponse>}
 */
class SetPickupOrganizationUnit extends StoreFetchableData {
    /**
     * Creates an instance of SetPickupOrganizationUnit
     *
     * @memberof SetPickupOrganizationUnit
     */
    constructor() {
        super('setPickupOrganizationUnit', '/message/SetPickupOrganizationUnit');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetPickupOrganizationUnit
     */
    *saga() {
        yield fork(listener$R);
    }
}
// Export the state handling singleton
//
const setPickupOrganizationUnit = new SetPickupOrganizationUnit();
// Setup the Saga implementation
//
function* fetchCall$R(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setPickupOrganizationUnit, 'fetchData'], action);
        yield put(setPickupOrganizationUnit.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setPickupOrganizationUnit.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$R() {
    yield takeEvery(getType(setPickupOrganizationUnit.actions.fetch), fetchCall$R);
}

/**
 * Implements SetRemarkOnOrder fetch for the Redux store
 *
 * @export
 * @class SetRemarkOnOrder
 * @extends {StoreFetchableData<EVA.Core.SetRemarkOnOrder, EVA.Core.EmptyResponseMessage>}
 */
class SetRemarkOnOrder extends StoreFetchableData {
    /**
     * Creates an instance of SetRemarkOnOrder
     *
     * @memberof SetRemarkOnOrder
     */
    constructor() {
        super('setRemarkOnOrder', '/message/SetRemarkOnOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetRemarkOnOrder
     */
    *saga() {
        yield fork(listener$Q);
    }
}
// Export the state handling singleton
//
const setRemarkOnOrder = new SetRemarkOnOrder();
// Setup the Saga implementation
//
function* fetchCall$Q(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setRemarkOnOrder, 'fetchData'], action);
        yield put(setRemarkOnOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setRemarkOnOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$Q() {
    yield takeEvery(getType(setRemarkOnOrder.actions.fetch), fetchCall$Q);
}

/**
 * Implements SetRequestedDate fetch for the Redux store
 *
 * @export
 * @class SetRequestedDate
 * @extends {StoreFetchableData<EVA.Core.SetRequestedDate, EVA.Core.EmptyResponseMessage>}
 */
class SetRequestedDate extends StoreFetchableData {
    /**
     * Creates an instance of SetRequestedDate
     *
     * @memberof SetRequestedDate
     */
    constructor() {
        super('setRequestedDate', '/message/SetRequestedDate');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetRequestedDate
     */
    *saga() {
        yield fork(listener$P);
    }
}
// Export the state handling singleton
//
const setRequestedDate = new SetRequestedDate();
// Setup the Saga implementation
//
function* fetchCall$P(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([setRequestedDate, 'fetchData'], action);
        yield put(setRequestedDate.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Fetch shopping cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(setRequestedDate.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$P() {
    yield takeEvery(getType(setRequestedDate.actions.fetch), fetchCall$P);
}

/**
 * Implements SetSerialNumbers fetch for the Redux store
 *
 * @export
 * @class SetSerialNumbers
 * @extends {StoreFetchableData<EVA.Core.SetSerialNumbers, EVA.Core.EmptyResponseMessage>}
 */
class SetSerialNumbers extends StoreFetchableData {
    /**
     * Creates an instance of SetSerialNumbers
     *
     * @memberof SetSerialNumbers
     */
    constructor() {
        super('setSerialNumbers', '/message/SetSerialNumbers');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetSerialNumbers
     */
    *saga() {
        yield fork(listener$O);
    }
}
// Export the state handling singleton
//
const setSerialNumbers = new SetSerialNumbers();
// Setup the Saga implementation
//
function* fetchCall$O(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setSerialNumbers, 'fetchData'], action);
        yield put(setSerialNumbers.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setSerialNumbers.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$O() {
    yield takeEvery(getType(setSerialNumbers.actions.fetch), fetchCall$O);
}

/**
 * Implements SetShippingMethod fetch for the Redux store
 *
 * @export
 * @class SetShippingMethod
 * @extends {StoreFetchableData<EVA.Core.SetShippingMethod, EVA.Core.ShoppingCartResponse>}
 */
class SetShippingMethod extends StoreFetchableData {
    /**
     * Creates an instance of SetShippingMethod
     *
     * @memberof SetShippingMethod
     */
    constructor() {
        super('setShippingMethod', '/message/SetShippingMethod');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetShippingMethod
     */
    *saga() {
        yield fork(listener$N);
    }
}
// Export the state handling singleton
//
const setShippingMethod = new SetShippingMethod();
// Setup the Saga implementation
//
function* fetchCall$N(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setShippingMethod, 'fetchData'], action);
        yield put(setShippingMethod.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setShippingMethod.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$N() {
    yield takeEvery(getType(setShippingMethod.actions.fetch), fetchCall$N);
}

/**
 * Implements SetWarehouseOrderData fetch for the Redux store
 *
 * @export
 * @class SetWarehouseOrderData
 * @extends {StoreFetchableData<EVA.Core.SetWarehouseOrderData, EVA.Core.SimpleShoppingCartResponse>}
 */
class SetWarehouseOrderData extends StoreFetchableData {
    /**
     * Creates an instance of SetWarehouseOrderData
     *
     * @memberof SetWarehouseOrderData
     */
    constructor() {
        super('setWarehouseOrderData', '/message/SetWarehouseOrderData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SetWarehouseOrderData
     */
    *saga() {
        yield fork(listener$M);
    }
}
// Export the state handling singleton
//
const setWarehouseOrderData = new SetWarehouseOrderData();
// Setup the Saga implementation
//
function* fetchCall$M(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([setWarehouseOrderData, 'fetchData'], action);
        yield put(setWarehouseOrderData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(setWarehouseOrderData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$M() {
    yield takeEvery(getType(setWarehouseOrderData.actions.fetch), fetchCall$M);
}

/**
 * Implements SignOrder fetch for the Redux store
 *
 * @export
 * @class SignOrder
 * @extends {StoreFetchableData<EVA.DocumentSigning.SignOrder, EVA.Core.EmptyResponseMessage>}
 */
class SignOrder extends StoreFetchableData {
    /**
     * Creates an instance of SignOrder
     *
     * @memberof SignOrder
     */
    constructor() {
        super('signOrder', '/message/SignOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SignOrder
     */
    *saga() {
        yield fork(listener$L);
    }
}
// Export the state handling singleton
//
const signOrder = new SignOrder();
// Setup the Saga implementation
//
function* fetchCall$L(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([signOrder, 'fetchData'], action);
        yield put(signOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(signOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$L() {
    yield takeEvery(getType(signOrder.actions.fetch), fetchCall$L);
}

/**
 * Implements SplitOrderLine fetch for the Redux store
 *
 * @export
 * @class SplitOrderLine
 * @extends {StoreFetchableData<EVA.Core.SplitOrderLine, EVA.Core.SplitOrderLineResponse>}
 */
class SplitOrderLine extends StoreFetchableData {
    /**
     * Creates an instance of SplitOrderLine
     *
     * @memberof SplitOrderLine
     */
    constructor() {
        super('splitOrderLine', '/message/SplitOrderLine');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SplitOrderLine
     */
    *saga() {
        yield fork(listener$K);
    }
}
// Export the state handling singleton
//
const splitOrderLine = new SplitOrderLine();
// Setup the Saga implementation
//
function* fetchCall$K(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([splitOrderLine, 'fetchData'], action);
        yield put(splitOrderLine.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // SplitOrderLine is always for the current cart
        //
        const currentOrderId = getShoppingCartInfo.getCurrentOrderId();
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: currentOrderId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(splitOrderLine.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$K() {
    yield takeEvery(getType(splitOrderLine.actions.fetch), fetchCall$K);
}

/**
 * Implements StartCloseCashJournal fetch for the Redux store
 *
 * @export
 * @class StartCloseCashJournal
 * @extends {StoreFetchableData<EVA.Core.StartCloseCashJournal, EVA.Core.StartCloseCashJournalResponse>}
 */
class StartCloseCashJournal extends StoreFetchableData {
    /**
     * Creates an instance of StartCloseCashJournal
     *
     * @memberof StartCloseCashJournal
     */
    constructor() {
        super('startCloseCashJournal', '/message/StartCloseCashJournal');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartCloseCashJournal
     */
    *saga() {
        yield fork(listener$J);
    }
}
// Export the state handling singleton
//
const startCloseCashJournal = new StartCloseCashJournal();
// Setup the Saga implementation
//
function* fetchCall$J(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startCloseCashJournal, 'fetchData'], action);
        yield put(startCloseCashJournal.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startCloseCashJournal.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$J() {
    yield takeEvery(getType(startCloseCashJournal.actions.fetch), fetchCall$J);
}

/**
 * Implements StartCompositeUserTask fetch for the Redux store
 *
 * @export
 * @class StartCompositeUserTask
 * @extends {StoreFetchableData<EVA.Core.StartCompositeUserTask, EVA.Core.StartCompositeUserTaskResponse>}
 */
class StartCompositeUserTask extends StoreFetchableData {
    /**
     * Creates an instance of StartCompositeUserTask
     *
     * @memberof StartCompositeUserTask
     */
    constructor() {
        super('startCompositeUserTask', '/message/StartCompositeUserTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartCompositeUserTask
     */
    *saga() {
        yield fork(listener$I);
    }
}
// Export the state handling singleton
//
const startCompositeUserTask = new StartCompositeUserTask();
// Setup the Saga implementation
//
function* fetchCall$I(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startCompositeUserTask, 'fetchData'], action);
        yield put(startCompositeUserTask.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startCompositeUserTask.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$I() {
    yield takeEvery(getType(startCompositeUserTask.actions.fetch), fetchCall$I);
}

/**
 * Implements StartFullStockCount fetch for the Redux store
 *
 * @export
 * @class StartFullStockCount
 * @extends {StoreFetchableData<EVA.Core.StartFullStockCount, EVA.Core.StartFullStockCountResponse>}
 */
class StartFullStockCount extends StoreFetchableData {
    /**
     * Creates an instance of StartFullStockCount
     *
     * @memberof StartFullStockCount
     */
    constructor() {
        super('startFullStockCount', '/message/StartFullStockCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartFullStockCount
     */
    *saga() {
        yield fork(listener$H);
    }
}
// Export the state handling singleton
//
const startFullStockCount = new StartFullStockCount();
// Setup the Saga implementation
//
function* fetchCall$H(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startFullStockCount, 'fetchData'], action);
        yield put(startFullStockCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startFullStockCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$H() {
    yield takeEvery(getType(startFullStockCount.actions.fetch), fetchCall$H);
}

/**
 * Implements StartFullStockCountLabel fetch for the Redux store
 *
 * @export
 * @class StartFullStockCountLabel
 * @extends {StoreFetchableData<EVA.Core.StartFullStockCountLabel, EVA.Core.StartFullStockCountLabelResponse>}
 */
class StartFullStockCountLabel extends StoreFetchableData {
    /**
     * Creates an instance of StartFullStockCountLabel
     *
     * @memberof StartFullStockCountLabel
     */
    constructor() {
        super('startFullStockCountLabel', '/message/StartFullStockCountLabel');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartFullStockCountLabel
     */
    *saga() {
        yield fork(listener$G);
    }
}
// Export the state handling singleton
//
const startFullStockCountLabel = new StartFullStockCountLabel();
// Setup the Saga implementation
//
function* fetchCall$G(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startFullStockCountLabel, 'fetchData'], action);
        yield put(startFullStockCountLabel.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startFullStockCountLabel.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$G() {
    yield takeEvery(getType(startFullStockCountLabel.actions.fetch), fetchCall$G);
}

/**
 * Implements StartOpenCashJournal fetch for the Redux store
 *
 * @export
 * @class StartOpenCashJournal
 * @extends {StoreFetchableData<EVA.Core.StartOpenCashJournal, EVA.Core.StartOpenCashJournalResponse>}
 */
class StartOpenCashJournal extends StoreFetchableData {
    /**
     * Creates an instance of StartOpenCashJournal
     *
     * @memberof StartOpenCashJournal
     */
    constructor() {
        super('startOpenCashJournal', '/message/StartOpenCashJournal');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartOpenCashJournal
     */
    *saga() {
        yield fork(listener$F);
    }
}
// Export the state handling singleton
//
const startOpenCashJournal = new StartOpenCashJournal();
// Setup the Saga implementation
//
function* fetchCall$F(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startOpenCashJournal, 'fetchData'], action);
        yield put(startOpenCashJournal.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startOpenCashJournal.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$F() {
    yield takeEvery(getType(startOpenCashJournal.actions.fetch), fetchCall$F);
}

/**
 * Implements StartPrintPriceLabelTask fetch for the Redux store
 *
 * @export
 * @class StartPrintPriceLabelTask
 * @extends {StoreFetchableData<EVA.Core.StartPrintPriceLabelTask, EVA.Core.StartPrintPriceLabelTaskResponse>}
 */
class StartPrintPriceLabelTask extends StoreFetchableData {
    /**
     * Creates an instance of StartPrintPriceLabelTask
     *
     * @memberof StartPrintPriceLabelTask
     */
    constructor() {
        super('startPrintPriceLabelTask', '/message/StartPrintPriceLabelTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartPrintPriceLabelTask
     */
    *saga() {
        yield fork(listener$E);
    }
}
// Export the state handling singleton
//
const startPrintPriceLabelTask = new StartPrintPriceLabelTask();
// Setup the Saga implementation
//
function* fetchCall$E(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startPrintPriceLabelTask, 'fetchData'], action);
        yield put(startPrintPriceLabelTask.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startPrintPriceLabelTask.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$E() {
    yield takeEvery(getType(startPrintPriceLabelTask.actions.fetch), fetchCall$E);
}

/**
 * Implements StartPrintTask fetch for the Redux store
 *
 * @export
 * @class StartPrintTask
 * @extends {StoreFetchableData<EVA.Core.StartPrintTask, EVA.Core.StartPrintTaskResponse>}
 */
class StartPrintTask extends StoreFetchableData {
    /**
     * Creates an instance of StartPrintTask
     *
     * @memberof StartPrintTask
     */
    constructor() {
        super('startPrintTask', '/message/StartPrintTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartPrintTask
     */
    *saga() {
        yield fork(listener$D);
    }
}
// Export the state handling singleton
//
const startPrintTask = new StartPrintTask();
// Setup the Saga implementation
//
function* fetchCall$D(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startPrintTask, 'fetchData'], action);
        yield put(startPrintTask.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startPrintTask.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$D() {
    yield takeEvery(getType(startPrintTask.actions.fetch), fetchCall$D);
}

/**
 * Implements StartReceiveShipment fetch for the Redux store
 *
 * @export
 * @class StartReceiveShipment
 * @extends {StoreFetchableData<EVA.Core.StartReceiveShipment, EVA.Core.StartReceiveShipmentResponse>}
 */
class StartReceiveShipment extends StoreFetchableData {
    /**
     * Creates an instance of StartReceiveShipment
     *
     * @memberof StartReceiveShipment
     */
    constructor() {
        super('startReceiveShipment', '/message/StartReceiveShipment');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartReceiveShipment
     */
    *saga() {
        yield fork(listener$C);
    }
}
// Export the state handling singleton
//
const startReceiveShipment = new StartReceiveShipment();
// Setup the Saga implementation
//
function* fetchCall$C(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startReceiveShipment, 'fetchData'], action);
        yield put(startReceiveShipment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startReceiveShipment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$C() {
    yield takeEvery(getType(startReceiveShipment.actions.fetch), fetchCall$C);
}

/**
 * Implements StartReservationCleanup fetch for the Redux store
 *
 * @export
 * @class StartReservationCleanup
 * @extends {StoreFetchableData<EVA.Core.StartReservationCleanup, EVA.Core.StartReservationCleanupResponse>}
 */
class StartReservationCleanup extends StoreFetchableData {
    /**
     * Creates an instance of StartReservationCleanup
     *
     * @memberof StartReservationCleanup
     */
    constructor() {
        super('startReservationCleanup', '/message/StartReservationCleanup');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartReservationCleanup
     */
    *saga() {
        yield fork(listener$B);
    }
}
// Export the state handling singleton
//
const startReservationCleanup = new StartReservationCleanup();
// Setup the Saga implementation
//
function* fetchCall$B(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startReservationCleanup, 'fetchData'], action);
        yield put(startReservationCleanup.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startReservationCleanup.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$B() {
    yield takeEvery(getType(startReservationCleanup.actions.fetch), fetchCall$B);
}

/**
 * Implements StartReservationDeviationTask fetch for the Redux store
 *
 * @export
 * @class StartReservationDeviationTask
 * @extends {StoreFetchableData<EVA.Core.StartReservationDeviationTask, EVA.Core.StartReservationDeviationTaskResponse>}
 */
class StartReservationDeviation extends StoreFetchableData {
    /**
     * Creates an instance of StartReservationDeviationTask
     *
     * @memberof StartReservationDeviation
     */
    constructor() {
        super('startReservationDeviation', '/message/StartReservationDeviationTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartReservationDeviation
     */
    *saga() {
        yield fork(listener$A);
    }
}
// Export the state handling singleton
//
const startReservationDeviation = new StartReservationDeviation();
// Setup the Saga implementation
//
function* fetchCall$A(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startReservationDeviation, 'fetchData'], action);
        yield put(startReservationDeviation.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startReservationDeviation.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$A() {
    yield takeEvery(getType(startReservationDeviation.actions.fetch), fetchCall$A);
}

/**
 * Implements StartReturnToSupplierTask fetch for the Redux store
 *
 * @export
 * @class StartReturnToSupplierTask
 * @extends {StoreFetchableData<EVA.Core.StartReturnToSupplierTask, EVA.Core.EmptyResponseMessage>}
 */
class StartReturnToSupplierTask extends StoreFetchableData {
    /**
     * Creates an instance of StartReturnToSupplierTask
     *
     * @memberof StartReturnToSupplierTask
     */
    constructor() {
        super('startReturnToSupplierTask', '/message/StartReturnToSupplierTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartReturnToSupplierTask
     */
    *saga() {
        yield fork(listener$z);
    }
}
// Export the state handling singleton
//
const startReturnToSupplierTask = new StartReturnToSupplierTask();
// Setup the Saga implementation
//
function* fetchCall$z(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startReturnToSupplierTask, 'fetchData'], action);
        yield put(startReturnToSupplierTask.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startReturnToSupplierTask.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$z() {
    yield takeEvery(getType(startReturnToSupplierTask.actions.fetch), fetchCall$z);
}

/**
 * Implements StartSecondChanceProductRegistration fetch for the Redux store
 *
 * @export
 * @class StartSecondChanceProductRegistration
 * @extends {StoreFetchableData<EVA.Core.StartSecondChanceProductRegistration, EVA.Core.StartSecondChanceProductRegistrationResponse>}
 */
class StartSecondChanceProductRegistration extends StoreFetchableData {
    /**
     * Creates an instance of StartSecondChanceProductRegistration
     *
     * @memberof StartSecondChanceProductRegistration
     */
    constructor() {
        super('startSecondChanceProductRegistration', '/message/StartSecondChanceProductRegistration');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartSecondChanceProductRegistration
     */
    *saga() {
        yield fork(listener$y);
    }
}
// Export the state handling singleton
//
const startSecondChanceProductRegistration = new StartSecondChanceProductRegistration();
// Setup the Saga implementation
//
function* fetchCall$y(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startSecondChanceProductRegistration, 'fetchData'], action);
        yield put(startSecondChanceProductRegistration.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startSecondChanceProductRegistration.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$y() {
    yield takeEvery(getType(startSecondChanceProductRegistration.actions.fetch), fetchCall$y);
}

/**
 * Implements StartShipFromStoreTask fetch for the Redux store
 *
 * @export
 * @class StartShipFromStoreTask
 * @extends {StoreFetchableData<EVA.Core.StartShipFromStoreTask, EVA.Core.StartShipFromStoreTaskResponse>}
 */
class StartShipFromStoreTask extends StoreFetchableData {
    /**
     * Creates an instance of StartShipFromStoreTask
     *
     * @memberof StartShipFromStoreTask
     */
    constructor() {
        super('startShipFromStoreTask', '/message/StartShipFromStoreTask');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartShipFromStoreTask
     */
    *saga() {
        yield fork(listener$x);
    }
}
// Export the state handling singleton
//
const startShipFromStoreTask = new StartShipFromStoreTask();
// Setup the Saga implementation
//
function* fetchCall$x(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startShipFromStoreTask, 'fetchData'], action);
        yield put(startShipFromStoreTask.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startShipFromStoreTask.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$x() {
    yield takeEvery(getType(startShipFromStoreTask.actions.fetch), fetchCall$x);
}

/**
 * Implements StartStockMovementFollowUp fetch for the Redux store
 *
 * @export
 * @class StartStockMovementFollowUp
 * @extends {StoreFetchableData<EVA.Core.StartStockMovementFollowUp, EVA.Core.StartStockMovementFollowUpResponse>}
 */
class StartStockMovementFollowUp extends StoreFetchableData {
    /**
     * Creates an instance of StartStockMovementFollowUp
     *
     * @memberof StartStockMovementFollowUp
     */
    constructor() {
        super('startStockMovementFollowUp', '/message/StartStockMovementFollowUp');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartStockMovementFollowUp
     */
    *saga() {
        yield fork(listener$w);
    }
}
// Export the state handling singleton
//
const startStockMovementFollowUp = new StartStockMovementFollowUp();
// Setup the Saga implementation
//
function* fetchCall$w(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startStockMovementFollowUp, 'fetchData'], action);
        yield put(startStockMovementFollowUp.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startStockMovementFollowUp.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$w() {
    yield takeEvery(getType(startStockMovementFollowUp.actions.fetch), fetchCall$w);
}

/**
 * Implements StartStockReplenishment fetch for the Redux store
 *
 * @export
 * @class StartStockReplenishment
 * @extends {StoreFetchableData<EVA.Core.StartStockReplenishment, EVA.Core.StartStockReplenishmentResponse>}
 */
class StartStockReplenishment extends StoreFetchableData {
    /**
     * Creates an instance of StartStockReplenishment
     *
     * @memberof StartStockReplenishment
     */
    constructor() {
        super('startStockReplenishment', '/message/StartStockReplenishment');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartStockReplenishment
     */
    *saga() {
        yield fork(listener$v);
    }
}
// Export the state handling singleton
//
const startStockReplenishment = new StartStockReplenishment();
// Setup the Saga implementation
//
function* fetchCall$v(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startStockReplenishment, 'fetchData'], action);
        yield put(startStockReplenishment.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startStockReplenishment.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$v() {
    yield takeEvery(getType(startStockReplenishment.actions.fetch), fetchCall$v);
}

/**
 * Implements StartStockReservation2 fetch for the Redux store
 *
 * @export
 * @class StartStockReservation2
 * @extends {StoreFetchableData<EVA.Core.StartStockReservation2, EVA.Core.StartStockReservationResponse2>}
 */
class StartStockReservation2 extends StoreFetchableData {
    /**
     * Creates an instance of StartStockReservation2
     *
     * @memberof StartStockReservation2
     */
    constructor() {
        super('startStockReservation', '/message/StartStockReservation2');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartStockReservation2
     */
    *saga() {
        yield fork(listener$u);
    }
}
// Export the state handling singleton
//
const startStockReservation = new StartStockReservation2();
// Setup the Saga implementation
//
function* fetchCall$u(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startStockReservation, 'fetchData'], action);
        yield put(startStockReservation.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startStockReservation.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$u() {
    yield takeEvery(getType(startStockReservation.actions.fetch), fetchCall$u);
}

/**
 * Implements StartZonedCycleCount fetch for the Redux store
 *
 * @export
 * @class StartZonedCycleCount
 * @extends {StoreFetchableData<EVA.Core.StartZonedCycleCount, EVA.Core.StartZonedCycleCountResponse>}
 */
class StartZonedCycleCount extends StoreFetchableData {
    /**
     * Creates an instance of StartZonedCycleCount
     *
     * @memberof StartZonedCycleCount
     */
    constructor() {
        super('startZonedCycleCount', '/message/StartZonedCycleCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartZonedCycleCount
     */
    *saga() {
        yield fork(listener$t);
    }
}
// Export the state handling singleton
//
const startZonedCycleCount = new StartZonedCycleCount();
// Setup the Saga implementation
//
function* fetchCall$t(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startZonedCycleCount, 'fetchData'], action);
        yield put(startZonedCycleCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startZonedCycleCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$t() {
    yield takeEvery(getType(startZonedCycleCount.actions.fetch), fetchCall$t);
}

/**
 * Implements StartZonedCycleCountPreCount fetch for the Redux store
 *
 * @export
 * @class StartZonedCycleCountPreCount
 * @extends {StoreFetchableData<EVA.Core.StartZonedCycleCountPreCount, EVA.Core.StartZonedCycleCountPreCountResponse>}
 */
class StartZonedCycleCountPreCount extends StoreFetchableData {
    /**
     * Creates an instance of StartZonedCycleCountPreCount
     *
     * @memberof StartZonedCycleCountPreCount
     */
    constructor() {
        super('startZonedCycleCountPreCount', '/message/StartZonedCycleCountPreCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StartZonedCycleCountPreCount
     */
    *saga() {
        yield fork(listener$s);
    }
}
// Export the state handling singleton
//
const startZonedCycleCountPreCount = new StartZonedCycleCountPreCount();
// Setup the Saga implementation
//
function* fetchCall$s(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([startZonedCycleCountPreCount, 'fetchData'], action);
        yield put(startZonedCycleCountPreCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(startZonedCycleCountPreCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$s() {
    yield takeEvery(getType(startZonedCycleCountPreCount.actions.fetch), fetchCall$s);
}

/**
 * Implements StoreBlob fetch for the Redux store
 *
 * @export
 * @class StoreBlob
 * @extends {StoreFetchableData<EVA.Blobs.StoreBlob, EVA.Blobs.StoreBlobResponse>}
 */
class StoreBlob extends StoreFetchableData {
    /**
     * Creates an instance of StoreBlob
     *
     * @memberof StoreBlob
     */
    constructor() {
        super('storeBlob', '/message/StoreBlob');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof StoreBlob
     */
    *saga() {
        yield fork(listener$r);
    }
}
// Export the state handling singleton
//
const storeBlob = new StoreBlob();
// Setup the Saga implementation
//
function* fetchCall$r(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([storeBlob, 'fetchData'], action);
        yield put(storeBlob.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(storeBlob.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$r() {
    yield takeEvery(getType(storeBlob.actions.fetch), fetchCall$r);
}

/**
 * Implements SubmitInquiry fetch for the Redux store
 *
 * @export
 * @class SubmitInquiry
 * @extends {StoreFetchableData<EVA.Core.SubmitInquiry, EVA.Core.EmptyResponseMessage>}
 */
class SubmitInquiry extends StoreFetchableData {
    /**
     * Creates an instance of SubmitInquiry
     *
     * @memberof SubmitInquiry
     */
    constructor() {
        super('submitInquiry', '/message/SubmitInquiry');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SubmitInquiry
     */
    *saga() {
        yield fork(listener$q);
    }
}
// Export the state handling singleton
//
const submitInquiry = new SubmitInquiry();
// Setup the Saga implementation
//
function* fetchCall$q(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([submitInquiry, 'fetchData'], action);
        yield put(submitInquiry.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(submitInquiry.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$q() {
    yield takeEvery(getType(submitInquiry.actions.fetch), fetchCall$q);
}

/**
 * Implements SubscribeUser fetch for the Redux store
 *
 * @export
 * @class SubscribeUser
 * @extends {StoreFetchableData<EVA.Core.SubscribeUser, EVA.Core.SubscribeUserResponse>}
 */
class SubscribeUser extends StoreFetchableData {
    /**
     * Creates an instance of SubscribeUser
     *
     * @memberof SubscribeUser
     */
    constructor() {
        super('subscribeUser', '/message/SubscribeUser');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SubscribeUser
     */
    *saga() {
        yield fork(listener$p);
    }
}
// Export the state handling singleton
//
const subscribeUser = new SubscribeUser();
// Setup the Saga implementation
//
function* fetchCall$p(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([subscribeUser, 'fetchData'], action);
        yield put(subscribeUser.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(subscribeUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$p() {
    yield takeEvery(getType(subscribeUser.actions.fetch), fetchCall$p);
}

/**
 * Implements SuspendOrder fetch for the Redux store
 *
 * @export
 * @class SuspendOrder
 * @extends {StoreFetchableData<EVA.Core.SuspendOrder, EVA.Core.EmptyResponseMessage>}
 */
class SuspendOrder extends StoreFetchableData {
    /**
     * Creates an instance of SuspendOrder
     *
     * @memberof SuspendOrder
     */
    constructor() {
        super('suspendOrder', '/message/SuspendOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof SuspendOrder
     */
    *saga() {
        yield fork(listener$o);
    }
}
// Export the state handling singleton
//
const suspendOrder = new SuspendOrder();
// Setup the Saga implementation
//
function* fetchCall$o(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([suspendOrder, 'fetchData'], action);
        yield put(suspendOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // SuspendOrder is always for the current cart
        //
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: 0 }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(suspendOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$o() {
    yield takeEvery(getType(suspendOrder.actions.fetch), fetchCall$o);
}

/**
 * Implements UnsubscribeUser fetch for the Redux store
 *
 * @export
 * @class UnsubscribeUser
 * @extends {StoreFetchableData<EVA.Core.UnsubscribeUser, EVA.Core.UnsubscribeUserResponse>}
 */
class UnsubscribeUser extends StoreFetchableData {
    /**
     * Creates an instance of UnsubscribeUser
     *
     * @memberof UnsubscribeUser
     */
    constructor() {
        super('unsubscribeUser', '/message/UnsubscribeUser');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UnsubscribeUser
     */
    *saga() {
        yield fork(listener$n);
    }
}
// Export the state handling singleton
//
const unsubscribeUser = new UnsubscribeUser();
// Setup the Saga implementation
//
function* fetchCall$n(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([unsubscribeUser, 'fetchData'], action);
        yield put(unsubscribeUser.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(unsubscribeUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$n() {
    yield takeEvery(getType(unsubscribeUser.actions.fetch), fetchCall$n);
}

/**
 * Implements UpdateAddressBookItem fetch for the Redux store
 *
 * @export
 * @class UpdateAddressBookItem
 * @extends {StoreFetchableData<EVA.Core.UpdateAddressBookItem, EVA.Core.EmptyResponseMessage>}
 */
class UpdateAddressBookItem extends StoreFetchableData {
    /**
     * Creates an instance of UpdateAddressBookItem
     *
     * @memberof UpdateAddressBookItem
     */
    constructor() {
        super('updateAddressBookItem', '/message/UpdateAddressBookItem');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateAddressBookItem
     */
    *saga() {
        yield fork(listener$m);
    }
}
// Export the state handling singleton
//
const updateAddressBookItem = new UpdateAddressBookItem();
// Setup the Saga implementation
//
function* fetchCall$m(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([updateAddressBookItem, 'fetchData'], action);
        yield put(updateAddressBookItem.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(updateAddressBookItem.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$m() {
    yield takeEvery(getType(updateAddressBookItem.actions.fetch), fetchCall$m);
}

/**
 * Implements UpdateCompanyForUser fetch for the Redux store
 *
 * @export
 * @class UpdateCompanyForUser
 * @extends {StoreFetchableData<EVA.Core.UpdateCompanyForUser, EVA.Core.EmptyResponseMessage>}
 */
class UpdateCompanyForUser extends StoreFetchableData {
    /**
     * Creates an instance of UpdateCompanyForUser
     *
     * @memberof UpdateCompanyForUser
     */
    constructor() {
        super('updateCompanyForUser', '/message/UpdateCompanyForUser');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateCompanyForUser
     */
    *saga() {
        yield fork(listener$l);
    }
}
// Export the state handling singleton
//
const updateCompanyForUser = new UpdateCompanyForUser();
// Setup the Saga implementation
//
function* fetchCall$l(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([updateCompanyForUser, 'fetchData'], action);
        yield put(updateCompanyForUser.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(updateCompanyForUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$l() {
    yield takeEvery(getType(updateCompanyForUser.actions.fetch), fetchCall$l);
}

/**
 * Implements UpdateDigitalGiftCardOptions fetch for the Redux store
 *
 * @export
 * @class UpdateDigitalGiftCardOptions
 * @extends {StoreFetchableData<EVA.Core.UpdateDigitalGiftCardOptions, EVA.Core.EmptyResponseMessage>}
 */
class UpdateDigitalGiftCardOptions extends StoreFetchableData {
    /**
     * Creates an instance of UpdateDigitalGiftCardOptions
     *
     * @memberof UpdateDigitalGiftCardOptions
     */
    constructor() {
        super('updateDigitalGiftCardOptions', '/message/UpdateDigitalGiftCardOptions');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateDigitalGiftCardOptions
     */
    *saga() {
        yield fork(listener$k);
    }
}
// Export the state handling singleton
//
const updateDigitalGiftCardOptions = new UpdateDigitalGiftCardOptions();
// Setup the Saga implementation
//
function* fetchCall$k(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([updateDigitalGiftCardOptions, 'fetchData'], action);
        yield put(updateDigitalGiftCardOptions.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(updateDigitalGiftCardOptions.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$k() {
    yield takeEvery(getType(updateDigitalGiftCardOptions.actions.fetch), fetchCall$k);
}

/**
 * Implements UpdateInterbranchOrder fetch for the Redux store
 *
 * @export
 * @class UpdateInterbranchOrder
 * @extends {StoreFetchableData<EVA.Core.UpdateInterbranchOrder, EVA.Core.EmptyResponseMessage>}
 */
class UpdateInterbranchOrder extends StoreFetchableData {
    /**
     * Creates an instance of UpdateInterbranchOrder
     *
     * @memberof UpdateInterbranchOrder
     */
    constructor() {
        super('updateInterbranchOrder', '/message/UpdateInterbranchOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateInterbranchOrder
     */
    *saga() {
        yield fork(listener$j);
    }
}
// Export the state handling singleton
//
const updateInterbranchOrder = new UpdateInterbranchOrder();
// Setup the Saga implementation
//
function* fetchCall$j(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([updateInterbranchOrder, 'fetchData'], action);
        yield put(updateInterbranchOrder.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch
        //
        fetchActionPayload.fetchResolve(data);
        // If this is the current cart update it
        //
        const orderId = fetchActionPayload.request.OrderID;
        const getShoppingCartState = getShoppingCart.getState();
        let optionalGetShoppingCartInfoChainPromise = Promise.resolve(undefined);
        if (get(getShoppingCartState, 'response.ShoppingCart.ID') === orderId) {
            log$1('[EVA-SDK-REDUX:getShoppingCart] Fetching cart for updated interbranch order');
            const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
            yield put(getShoppingCartInfoAction);
            optionalGetShoppingCartInfoChainPromise = getShoppingCartInfoChainPromise;
        }
        // Resolve the chain
        //
        Promise.all([optionalGetShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(updateInterbranchOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$j() {
    yield takeEvery(getType(updateInterbranchOrder.actions.fetch), fetchCall$j);
}

/**
 * Implements UpdateOrderAddresses fetch for the Redux store
 *
 * @export
 * @class UpdateOrderAddresses
 * @extends {StoreFetchableData<EVA.Core.UpdateOrderAddresses, EVA.Core.UpdateOrderAddressesResponse>}
 */
class UpdateOrderAddresses extends StoreFetchableData {
    /**
     * Creates an instance of UpdateOrderAddresses
     *
     * @memberof UpdateOrderAddresses
     */
    constructor() {
        super('updateOrderAddresses', '/message/UpdateOrderAddresses');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateOrderAddresses
     */
    *saga() {
        yield fork(listener$i);
    }
}
// Export the state handling singleton
//
const updateOrderAddresses = new UpdateOrderAddresses();
// Setup the Saga implementation
//
function* fetchCall$i(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([updateOrderAddresses, 'fetchData'], action);
        yield put(updateOrderAddresses.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(updateOrderAddresses.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$i() {
    yield takeEvery(getType(updateOrderAddresses.actions.fetch), fetchCall$i);
}

/**
 * Implements UpdateOrderCheckoutOptions fetch for the Redux store
 *
 * @export
 * @class UpdateOrderCheckoutOptions
 * @extends {StoreFetchableData<EVA.Core.UpdateOrderCheckoutOptions, EVA.Core.EmptyResponseMessage>}
 */
class UpdateOrderCheckoutOptions extends StoreFetchableData {
    /**
     * Creates an instance of UpdateOrderCheckoutOptions
     *
     * @memberof UpdateOrderCheckoutOptions
     */
    constructor() {
        super('updateOrderCheckoutOptions', '/message/UpdateOrderCheckoutOptions');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateOrderCheckoutOptions
     */
    *saga() {
        yield fork(listener$h);
    }
}
// Export the state handling singleton
//
const updateOrderCheckoutOptions = new UpdateOrderCheckoutOptions();
// Setup the Saga implementation
//
function* fetchCall$h(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([updateOrderCheckoutOptions, 'fetchData'], action);
        yield put(updateOrderCheckoutOptions.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(updateOrderCheckoutOptions.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$h() {
    yield takeEvery(getType(updateOrderCheckoutOptions.actions.fetch), fetchCall$h);
}

/**
 * Implements UpdateOrderCustomFields fetch for the Redux store
 *
 * @export
 * @class UpdateOrderCustomFields
 * @extends {StoreFetchableData<EVA.Core.UpdateOrderCustomFields, EVA.Core.EmptyResponseMessage>}
 */
class UpdateOrderCustomFields extends StoreFetchableData {
    /**
     * Creates an instance of UpdateOrderCustomFields
     *
     * @memberof UpdateOrderCustomFields
     */
    constructor() {
        super('updateOrderCustomFields', '/message/UpdateOrderCustomFields');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateOrderCustomFields
     */
    *saga() {
        yield fork(listener$g);
    }
}
// Export the state handling singleton
//
const updateOrderCustomFields = new UpdateOrderCustomFields();
// Setup the Saga implementation
//
function* fetchCall$g(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([updateOrderCustomFields, 'fetchData'], action);
        yield put(updateOrderCustomFields.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(updateOrderCustomFields.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$g() {
    yield takeEvery(getType(updateOrderCustomFields.actions.fetch), fetchCall$g);
}

/**
 * Implements UpdateOrderLineStockLabel fetch for the Redux store
 *
 * @export
 * @class UpdateOrderLineStockLabel
 * @extends {StoreFetchableData<EVA.Core.UpdateOrderLineStockLabel, EVA.Core.EmptyResponseMessage>}
 */
class UpdateOrderLineStockLabel extends StoreFetchableData {
    /**
     * Creates an instance of UpdateOrderLineStockLabel
     *
     * @memberof UpdateOrderLineStockLabel
     */
    constructor() {
        super('updateOrderLineStockLabel', '/message/UpdateOrderLineStockLabel');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateOrderLineStockLabel
     */
    *saga() {
        yield fork(listener$f);
    }
}
// Export the state handling singleton
//
const updateOrderLineStockLabel = new UpdateOrderLineStockLabel();
// Setup the Saga implementation
//
function* fetchCall$f(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([updateOrderLineStockLabel, 'fetchData'], action);
        yield put(updateOrderLineStockLabel.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction(undefined, action.payload.chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        });
    }
    catch (err) {
        yield put(updateOrderLineStockLabel.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$f() {
    yield takeEvery(getType(updateOrderLineStockLabel.actions.fetch), fetchCall$f);
}

/**
 * Implements UpdateProductRequirementValuesForOrderLine fetch for the Redux store
 *
 * @export
 * @class UpdateProductRequirementValuesForOrderLine
 * @extends {StoreFetchableData<EVA.Core.UpdateProductRequirementValuesForOrderLine, EVA.Core.EmptyResponseMessage>}
 */
class UpdateProductRequirementValuesForOrderLine extends StoreFetchableData {
    /**
     * Creates an instance of UpdateProductRequirementValuesForOrderLine
     *
     * @memberof UpdateProductRequirementValuesForOrderLine
     */
    constructor() {
        super('updateProductRequirementValuesForOrderLine', '/message/UpdateProductRequirementValuesForOrderLine');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateProductRequirementValuesForOrderLine
     */
    *saga() {
        yield fork(listener$e);
    }
}
// Export the state handling singleton
//
const updateProductRequirementValuesForOrderLine = new UpdateProductRequirementValuesForOrderLine();
// Setup the Saga implementation
//
function* fetchCall$e(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([updateProductRequirementValuesForOrderLine, 'fetchData'], action);
        yield put(updateProductRequirementValuesForOrderLine.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart state
        // SuspendOrder is always for the current cart
        //
        const currentOrderId = getShoppingCartInfo.getCurrentOrderId();
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: currentOrderId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(updateProductRequirementValuesForOrderLine.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$e() {
    yield takeEvery(getType(updateProductRequirementValuesForOrderLine.actions.fetch), fetchCall$e);
}

/**
 * Implements UpdateQuickBuy fetch for the Redux store
 *
 * @export
 * @class UpdateQuickBuy
 * @extends {StoreFetchableData<EVA.Core.UpdateQuickBuy, EVA.Core.EmptyResponseMessage>}
 */
class UpdateQuickBuy extends StoreFetchableData {
    /**
     * Creates an instance of UpdateQuickBuy
     *
     * @memberof UpdateQuickBuy
     */
    constructor() {
        super('updateQuickBuy', '/message/UpdateQuickBuy');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateQuickBuy
     */
    *saga() {
        yield fork(listener$d);
    }
}
// Export the state handling singleton
//
const updateQuickBuy = new UpdateQuickBuy();
// Setup the Saga implementation
//
function* fetchCall$d(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([updateQuickBuy, 'fetchData'], action);
        yield put(updateQuickBuy.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(updateQuickBuy.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$d() {
    yield takeEvery(getType(updateQuickBuy.actions.fetch), fetchCall$d);
}

/**
 * Implements UpdateReturnToSupplierOrder fetch for the Redux store
 *
 * @export
 * @class UpdateReturnToSupplierOrder
 * @extends {StoreFetchableData<EVA.Core.UpdateReturnToSupplierOrder, EVA.Core.EmptyResponseMessage>}
 */
class UpdateReturnToSupplierOrder extends StoreFetchableData {
    /**
     * Creates an instance of UpdateReturnToSupplierOrder
     *
     * @memberof UpdateReturnToSupplierOrder
     */
    constructor() {
        super('updateReturnToSupplierOrder', '/message/UpdateReturnToSupplierOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateReturnToSupplierOrder
     */
    *saga() {
        yield fork(listener$c);
    }
}
// Export the state handling singleton
//
const updateReturnToSupplierOrder = new UpdateReturnToSupplierOrder();
// Setup the Saga implementation
//
function* fetchCall$c(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([updateReturnToSupplierOrder, 'fetchData'], action);
        yield put(updateReturnToSupplierOrder.actions.response(data));
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch
        //
        fetchActionPayload.fetchResolve(data);
        // If this is the current cart update it
        //
        const orderId = fetchActionPayload.request.OrderID;
        const getShoppingCartState = getShoppingCart.getState();
        let optionalGetShoppingCartInfoChainPromise = Promise.resolve(undefined);
        if (get(getShoppingCartState, 'response.ShoppingCart.ID') === orderId) {
            log$1('[EVA-SDK-REDUX:getShoppingCart] Fetching cart for updated return to supplier order');
            const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: action.payload.request.OrderID }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
            yield put(getShoppingCartInfoAction);
            optionalGetShoppingCartInfoChainPromise = getShoppingCartInfoChainPromise;
        }
        // Resolve the chain
        //
        Promise.all([optionalGetShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(updateReturnToSupplierOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$c() {
    yield takeEvery(getType(updateReturnToSupplierOrder.actions.fetch), fetchCall$c);
}

/**
 * Implements UpdateSerialNumber fetch for the Redux store
 *
 * @export
 * @class UpdateSerialNumber
 * @extends {StoreFetchableData<EVA.Core.UpdateSerialNumber, EVA.Core.UpdateSerialNumberResponse>}
 */
class UpdateSerialNumber extends StoreFetchableData {
    /**
     * Creates an instance of UpdateSerialNumber
     *
     * @memberof UpdateSerialNumber
     */
    constructor() {
        super('updateSerialNumber', '/message/UpdateSerialNumber');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateSerialNumber
     */
    *saga() {
        yield fork(listener$b);
    }
}
// Export the state handling singleton
//
const updateSerialNumber = new UpdateSerialNumber();
// Setup the Saga implementation
//
function* fetchCall$b(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([updateSerialNumber, 'fetchData'], action);
        yield put(updateSerialNumber.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Fetch shopping cart
        //
        const currentOrderId = getShoppingCartInfo.getCurrentOrderId();
        const [getShoppingCartInfoAction, , getShoppingCartInfoChainPromise] = getShoppingCartInfo.createFetchAction({ OrderID: currentOrderId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getShoppingCartInfoAction);
        // Resolve the chain
        //
        Promise.all([getShoppingCartInfoChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(updateSerialNumber.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$b() {
    yield takeEvery(getType(updateSerialNumber.actions.fetch), fetchCall$b);
}

/**
 * Implements UpdateStockReplenishmentTaskData fetch for the Redux store
 *
 * @export
 * @class UpdateStockReplenishmentTaskData
 * @extends {StoreFetchableData<EVA.Core.UpdateStockReplenishmentTaskData, EVA.Core.EmptyResponseMessage>}
 */
class UpdateStockReplenishmentTaskData extends StoreFetchableData {
    /**
     * Creates an instance of UpdateStockReplenishmentTaskData
     *
     * @memberof UpdateStockReplenishmentTaskData
     */
    constructor() {
        super('updateStockReplenishmentTaskData', '/message/UpdateStockReplenishmentTaskData');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateStockReplenishmentTaskData
     */
    *saga() {
        yield fork(listener$a);
    }
}
// Export the state handling singleton
//
const updateStockReplenishmentTaskData = new UpdateStockReplenishmentTaskData();
// Setup the Saga implementation
//
function* fetchCall$a(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([updateStockReplenishmentTaskData, 'fetchData'], action);
        yield put(updateStockReplenishmentTaskData.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(updateStockReplenishmentTaskData.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$a() {
    yield takeEvery(getType(updateStockReplenishmentTaskData.actions.fetch), fetchCall$a);
}

/**
 * Implements UpdateUser fetch for the Redux store
 *
 * @export
 * @class UpdateUser
 * @extends {StoreFetchableData<EVA.Core.UpdateUser, EVA.Core.UpdateUserResponse>}
 */
class UpdateUser extends StoreFetchableData {
    /**
     * Creates an instance of UpdateUser
     *
     * @memberof UpdateUser
     */
    constructor() {
        super('updateUser', '/message/UpdateUser');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateUser
     */
    *saga() {
        yield fork(listener$9);
    }
}
// Export the state handling singleton
//
const updateUser = new UpdateUser();
// Setup the Saga implementation
//
function* fetchCall$9(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([updateUser, 'fetchData'], action);
        yield put(updateUser.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Update the shopping cart if this is the current customer
        //
        const cartState = getShoppingCart.getState();
        const cartUserId = get(cartState, 'response.ShoppingCart.CustomerID');
        const cartId = get(cartState, 'response.ShoppingCart.ID');
        const updatedUserId = get(updateUser.getState(), 'request.ID');
        if (cartUserId === updatedUserId) {
            log$1('[EVA-SDK-REDUX] Updated cart because it is attached to updated user');
            // Go straight for shopping-cart instead of info because info won't change
            // RequiredData for instance will change customer validation
            //
            const [getShoppingCartAction, , getShoppingCartChainPromise] = getShoppingCart.createFetchAction({ OrderID: cartId }, chainStarter, undefined, undefined, undefined, undefined, chainContext);
            yield put(getShoppingCartAction);
            // Resolve the chain
            //
            Promise.all([getShoppingCartChainPromise])
                .then((chainResponses) => {
                chainResponses.unshift(data);
                fetchActionPayload.chainResolve(chainResponses);
            });
        }
        else {
            // Resolve the chain
            //
            fetchActionPayload.chainResolve([data, undefined]);
        }
    }
    catch (err) {
        yield put(updateUser.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$9() {
    yield takeEvery(getType(updateUser.actions.fetch), fetchCall$9);
}

/**
 * Implements UpdateUserAccountType fetch for the Redux store
 *
 * @export
 * @class UpdateUserAccountType
 * @extends {StoreFetchableData<EVA.Core.UpdateUserAccountType, EVA.Core.EmptyResponseMessage>}
 */
class UpdateUserAccountType extends StoreFetchableData {
    /**
     * Creates an instance of UpdateUserAccountType
     *
     * @memberof UpdateUserAccountType
     */
    constructor() {
        super('updateUserAccountType', '/message/UpdateUserAccountType');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateUserAccountType
     */
    *saga() {
        yield fork(listener$8);
    }
}
// Export the state handling singleton
//
const updateUserAccountType = new UpdateUserAccountType();
// Setup the Saga implementation
//
function* fetchCall$8(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([updateUserAccountType, 'fetchData'], action);
        yield put(updateUserAccountType.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(updateUserAccountType.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$8() {
    yield takeEvery(getType(updateUserAccountType.actions.fetch), fetchCall$8);
}

/**
 * Implements UpdateUserPhoneNumber fetch for the Redux store
 *
 * @export
 * @class UpdateUserPhoneNumber
 * @extends {StoreFetchableData<EVA.Core.UpdateUserPhoneNumber, EVA.Core.EmptyResponseMessage>}
 */
class UpdateUserPhoneNumber extends StoreFetchableData {
    /**
     * Creates an instance of UpdateUserPhoneNumber
     *
     * @memberof UpdateUserPhoneNumber
     */
    constructor() {
        super('updateUserPhoneNumber', '/message/UpdateUserPhoneNumber');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof UpdateUserPhoneNumber
     */
    *saga() {
        yield fork(listener$7);
    }
}
// Export the state handling singleton
//
const updateUserPhoneNumber = new UpdateUserPhoneNumber();
// Setup the Saga implementation
//
function* fetchCall$7(action) {
    const fetchActionPayload = action.payload;
    const chainContext = fetchActionPayload.chainContext;
    try {
        const data = yield call([updateUserPhoneNumber, 'fetchData'], action);
        yield put(updateUserPhoneNumber.actions.response(data));
        const chainStarter = action.payload.chainStarter;
        // Resolve the fetch promise
        //
        fetchActionPayload.fetchResolve(data);
        // Fetch phone number list
        //
        const [getUserPhoneNumbersAction, , getUserPhoneNumbersChainPromise] = getUserPhoneNumbers.createFetchAction(undefined, chainStarter, undefined, undefined, undefined, undefined, chainContext);
        yield put(getUserPhoneNumbersAction);
        // Resolve the chain
        //
        Promise.all([getUserPhoneNumbersChainPromise])
            .then((chainResponses) => {
            chainResponses.unshift(data);
            fetchActionPayload.chainResolve(chainResponses);
        })
            .catch(fetchActionPayload.chainReject);
    }
    catch (err) {
        yield put(updateUserPhoneNumber.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$7() {
    yield takeEvery(getType(updateUserPhoneNumber.actions.fetch), fetchCall$7);
}

/**
 * Implements ValidateIdentificationPin fetch for the Redux store
 *
 * @export
 * @class ValidateIdentificationPin
 * @extends {StoreFetchableData<EVA.Core.ValidateIdentificationPin, EVA.Core.ValidateIdentificationPinResponse>}
 */
class ValidateIdentificationPin extends StoreFetchableData {
    /**
     * Creates an instance of ValidateIdentificationPin
     *
     * @memberof ValidateIdentificationPin
     */
    constructor() {
        super('validateIdentificationPin', '/message/ValidateIdentificationPin');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ValidateIdentificationPin
     */
    *saga() {
        yield fork(listener$6);
    }
}
// Export the state handling singleton
//
const validateIdentificationPin = new ValidateIdentificationPin();
// Setup the Saga implementation
//
function* fetchCall$6(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([validateIdentificationPin, 'fetchData'], action);
        yield put(validateIdentificationPin.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(validateIdentificationPin.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$6() {
    yield takeEvery(getType(validateIdentificationPin.actions.fetch), fetchCall$6);
}

/**
 * Implements ValidateFiscalID fetch for the Redux store
 *
 * @export
 * @class ValidateFiscalID
 * @extends {StoreFetchableData<EVA.Auditing.ValidateFiscalID, EVA.Auditing.ValidateFiscalIDResponse>}
 */
class ValidateFiscalID extends StoreFetchableData {
    /**
     * Creates an instance of ValidateFiscalID
     *
     * @memberof ValidateFiscalID
     */
    constructor() {
        super('validateFiscalID', '/message/ValidateFiscalID');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ValidateFiscalID
     */
    *saga() {
        yield fork(listener$5);
    }
}
// Export the state handling singleton
//
const validateFiscalID = new ValidateFiscalID();
// Setup the Saga implementation
//
function* fetchCall$5(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([validateFiscalID, 'fetchData'], action);
        yield put(validateFiscalID.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(validateFiscalID.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$5() {
    yield takeEvery(getType(validateFiscalID.actions.fetch), fetchCall$5);
}

/**
 * Implements ValidatePreCount fetch for the Redux store
 *
 * @export
 * @class ValidatePreCount
 * @extends {StoreFetchableData<EVA.Core.ValidatePreCount, EVA.Core.ValidatePreCountResponse>}
 */
class ValidatePreCount extends StoreFetchableData {
    /**
     * Creates an instance of ValidatePreCount
     *
     * @memberof ValidatePreCount
     */
    constructor() {
        super('validatePreCount', '/message/ValidatePreCount');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ValidatePreCount
     */
    *saga() {
        yield fork(listener$4);
    }
}
// Export the state handling singleton
//
const validatePreCount = new ValidatePreCount();
// Setup the Saga implementation
//
function* fetchCall$4(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([validatePreCount, 'fetchData'], action);
        yield put(validatePreCount.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(validatePreCount.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$4() {
    yield takeEvery(getType(validatePreCount.actions.fetch), fetchCall$4);
}

/**
 * Implements ValidateToken fetch for the Redux store
 *
 * @export
 * @class ValidateToken
 * @extends {StoreFetchableData<EVA.Core.ValidateToken, EVA.Core.EmptyResponseMessage>}
 */
class ValidateToken extends StoreFetchableData {
    /**
     * Creates an instance of ValidateToken
     *
     * @memberof ValidateToken
     */
    constructor() {
        super('validateToken', '/message/ValidateToken');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ValidateToken
     */
    *saga() {
        yield fork(listener$3);
    }
}
// Export the state handling singleton
//
const validateToken = new ValidateToken();
// Setup the Saga implementation
//
function* fetchCall$3(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([validateToken, 'fetchData'], action);
        yield put(validateToken.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(validateToken.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$3() {
    yield takeEvery(getType(validateToken.actions.fetch), fetchCall$3);
}

/**
 * Implements ValidateVatNumber fetch for the Redux store
 *
 * @export
 * @class ValidateVatNumber
 * @extends {StoreFetchableData<EVA.Core.ValidateVatNumber, EVA.Core.ValidateVatNumberResponse>}
 */
class ValidateVatNumber extends StoreFetchableData {
    /**
     * Creates an instance of ValidateVatNumber
     *
     * @memberof ValidateVatNumber
     */
    constructor() {
        super('validateVatNumber', '/message/ValidateVatNumber');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof ValidateVatNumber
     */
    *saga() {
        yield fork(listener$2);
    }
}
// Export the state handling singleton
//
const validateVatNumber = new ValidateVatNumber();
// Setup the Saga implementation
//
function* fetchCall$2(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([validateVatNumber, 'fetchData'], action);
        yield put(validateVatNumber.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(validateVatNumber.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$2() {
    yield takeEvery(getType(validateVatNumber.actions.fetch), fetchCall$2);
}

/**
 * Implements VerifyCustomer fetch for the Redux store
 *
 * @export
 * @class VerifyCustomer
 * @extends {StoreFetchableData<EVA.Core.VerifyCustomer, EVA.Core.EmptyResponseMessage>}
 */
class VerifyCustomer extends StoreFetchableData {
    /**
     * Creates an instance of VerifyCustomer
     *
     * @memberof VerifyCustomer
     */
    constructor() {
        super('verifyCustomer', '/message/VerifyCustomer');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof VerifyCustomer
     */
    *saga() {
        yield fork(listener$1);
    }
}
// Export the state handling singleton
//
const verifyCustomer = new VerifyCustomer();
// Setup the Saga implementation
//
function* fetchCall$1(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([verifyCustomer, 'fetchData'], action);
        yield put(verifyCustomer.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(verifyCustomer.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener$1() {
    yield takeEvery(getType(verifyCustomer.actions.fetch), fetchCall$1);
}

/**
 * Implements VerifyOrder fetch for the Redux store
 *
 * @export
 * @class VerifyOrder
 * @extends {StoreFetchableData<EVA.Core.VerifyOrder, EVA.Core.EmptyResponseMessage>}
 */
class VerifyOrder extends StoreFetchableData {
    /**
     * Creates an instance of VerifyOrder
     *
     * @memberof VerifyOrder
     */
    constructor() {
        super('verifyOrder', '/message/VerifyOrder');
    }
    /**
     * The saga listener for this store section
     *
     * @memberof VerifyOrder
     */
    *saga() {
        yield fork(listener);
    }
}
// Export the state handling singleton
//
const verifyOrder = new VerifyOrder();
// Setup the Saga implementation
//
function* fetchCall(action) {
    const fetchActionPayload = action.payload;
    try {
        const data = yield call([verifyOrder, 'fetchData'], action);
        yield put(verifyOrder.actions.response(data));
        // Resolve the fetch and chain promise
        //
        fetchActionPayload.fetchResolve(data);
        fetchActionPayload.chainResolve([data]);
    }
    catch (err) {
        yield put(verifyOrder.actions.error(err));
        // Reject both the fetch and chain promise
        //
        fetchActionPayload.fetchReject(err);
        fetchActionPayload.chainReject(err);
    }
}
function* listener() {
    yield takeEvery(getType(verifyOrder.actions.fetch), fetchCall);
}

var reducers = /*#__PURE__*/Object.freeze({
    __proto__: null,
    abortTransaction: abortTransaction,
    addBoardingPassToOrder: addBoardingPassToOrder,
    addBundleProductToOrder: addBundleProductToOrder,
    addDiscountCouponToOrder: addDiscountCouponToOrder,
    addLabelToFullStockCount: addLabelToFullStockCount,
    addManualDiscountToOrder: addManualDiscountToOrder,
    addProductToFullStockCountLabel: addProductToFullStockCountLabel,
    addProductToOrder: addProductToOrder,
    addPushNotificationDevice: addPushNotificationDevice,
    addServiceProductToOrder: addServiceProductToOrder,
    addTrackingCodeToReturnToSupplierShipment: addTrackingCodeToReturnToSupplierShipment,
    addUserBorrowedProductToOrder: addUserBorrowedProductToOrder,
    addVoucherToShoppingCart: addVoucherToShoppingCart,
    adjustStock: adjustStock,
    applyBundleProductSelection: applyBundleProductSelection,
    applySalesTaxEstimateForOrder: applySalesTaxEstimateForOrder,
    approvePayment: approvePayment,
    attachAddressToOrder: attachAddressToOrder,
    attachCustomerToOrder: attachCustomerToOrder,
    autocompleteAddress: autocompleteAddress,
    cancelDiscountOrderLine: cancelDiscountOrderLine,
    cancelFullStockCount: cancelFullStockCount,
    cancelFullStockCountLabel: cancelFullStockCountLabel,
    cancelOrder: cancelOrder,
    cancelOrderLineFulfillments: cancelOrderLineFulfillments,
    cancelPayment: cancelPayment,
    cancelSecondChanceProductRegistration: cancelSecondChanceProductRegistration,
    cardBalanceCheck: cardBalanceCheck,
    changeOrderLinesToCarryOut: changeOrderLinesToCarryOut,
    changeOrderLinesToDelivery: changeOrderLinesToDelivery,
    changeOrderLinesToPickup: changeOrderLinesToPickup,
    changeUserPassword: changeUserPassword,
    checkEmailAddressAvailability: checkEmailAddressAvailability,
    checkNicknameAvailability: checkNicknameAvailability,
    clockEmployeeOutAsAbsent: clockEmployeeOutAsAbsent,
    closeFinancialPeriod: closeFinancialPeriod,
    completeCloseCashJournal: completeCloseCashJournal,
    completeFullStockCount: completeFullStockCount,
    completeFullStockCountLabel: completeFullStockCountLabel,
    completeOpenCashJournal: completeOpenCashJournal,
    completePrintPriceLabelTask: completePrintPriceLabelTask,
    completePrintTask: completePrintTask,
    completeReservationCleanup: completeReservationCleanup,
    completeReservationDeviation: completeReservationDeviation,
    completeReturnToSupplierTask: completeReturnToSupplierTask,
    completeSecondChanceProductRegistration: completeSecondChanceProductRegistration,
    completeShipFromStoreTask: completeShipFromStoreTask,
    completeStockMovementFollowUp: completeStockMovementFollowUp,
    completeStockReplenishment: completeStockReplenishment,
    completeStockReservation: completeStockReservation,
    completeZonedCycleCountPreCount: completeZonedCycleCountPreCount,
    completeZonedCycleCounts: completeZonedCycleCounts,
    confirmSubscription: confirmSubscription,
    correctWorkedHours: correctWorkedHours,
    countProductForInitialCycleCount: countProductForInitialCycleCount,
    countStockLabelForZonedCycleCount: countStockLabelForZonedCycleCount,
    createAddressBookItem: createAddressBookItem,
    createBlob: createBlob,
    createCashDeposit: createCashDeposit,
    createCashExpense: createCashExpense,
    createCompanyForUser: createCompanyForUser,
    createCustomer: createCustomer,
    createCustomerReturn: createCustomerReturn,
    createCustomerReturnWithoutOrder: createCustomerReturnWithoutOrder,
    createFullStockCount: createFullStockCount,
    createIdentificationPinForEmployee: createIdentificationPinForEmployee,
    createInterbranchOrder: createInterbranchOrder,
    createOrder: createOrder,
    createOrderLineUnitPriceCorrection: createOrderLineUnitPriceCorrection,
    createPayment: createPayment,
    createPrintTask: createPrintTask,
    createPrivacyRemovalRequest: createPrivacyRemovalRequest,
    createQuickBuy: createQuickBuy,
    createRefund: createRefund,
    createReturnToSupplierOrder: createReturnToSupplierOrder,
    createStockNotification: createStockNotification,
    createStockNotificationForCurrentUser: createStockNotificationForCurrentUser,
    createStockReplenishmentTask: createStockReplenishmentTask,
    createStockReplenishmentTasks: createStockReplenishmentTasks,
    createUserCardMutation: createUserCardMutation,
    createUserInteraction: createUserInteraction,
    createUserPhoneNumber: createUserPhoneNumber,
    createWorkedHours: createWorkedHours,
    createZonedCycleCount: createZonedCycleCount,
    deactivateUserTask: deactivateUserTask,
    deleteAddressBookItem: deleteAddressBookItem,
    deleteQuickBuy: deleteQuickBuy,
    deleteStockNotification: deleteStockNotification,
    deleteUser: deleteUser,
    deleteUserPhoneNumber: deleteUserPhoneNumber,
    deliverShipment: deliverShipment,
    detachCompanyFromUser: detachCompanyFromUser,
    detachCustomerFromOrder: detachCustomerFromOrder,
    detachOrder: detachOrder,
    disableDiscountOnOrder: disableDiscountOnOrder,
    downloadInvoice: downloadInvoice,
    downloadOrderConfirmation: downloadOrderConfirmation,
    dropOffCashDeposit: dropOffCashDeposit,
    duplicateOrder: duplicateOrder,
    emailInvoice: emailInvoice,
    emailOrderConfirmation: emailOrderConfirmation,
    emptyGiftCard: emptyGiftCard,
    enableDisabledDiscountOnOrder: enableDisabledDiscountOnOrder,
    evaPayGetInfo: evaPayGetInfo,
    exemptOrderFromTax: exemptOrderFromTax,
    forceVerifyCustomer: forceVerifyCustomer,
    fullyReceiveShipment: fullyReceiveShipment,
    generateDeviceHubBarcode: generateDeviceHubBarcode,
    generateElevationBarcode: generateElevationBarcode,
    generateIdentificationCodeForEmployee: generateIdentificationCodeForEmployee,
    generateIdentificationPinForEmployee: generateIdentificationPinForEmployee,
    generateScanModeBarcode: generateScanModeBarcode,
    generateSessionBarcode: generateSessionBarcode,
    generateTemporaryElevationCode: generateTemporaryElevationCode,
    getAddressForZipCode: getAddressForZipCode,
    getAddressRequirements: getAddressRequirements,
    getApplicationConfiguration: getApplicationConfiguration,
    getAuditingSummary: getAuditingSummary,
    getAutocompleteAddressByReference: getAutocompleteAddressByReference,
    getAvailableCashAmountForDevice: getAvailableCashAmountForDevice,
    getAvailableDocumentsForOrder: getAvailableDocumentsForOrder,
    getAvailableGiftWrappingLinesForOrder: getAvailableGiftWrappingLinesForOrder,
    getAvailableOpenIDConfigurations: getAvailableOpenIDConfigurations,
    getAvailablePaymentMethods: getAvailablePaymentMethods,
    getAvailableRefundPaymentMethodsForOrder: getAvailableRefundPaymentMethodsForOrder,
    getAvailableServices: getAvailableServices,
    getAvailableSubscriptions: getAvailableSubscriptions,
    getAvailableTwoFactorAuthenticators: getAvailableTwoFactorAuthenticators,
    getAvailableUserRequirementsProperties: getAvailableUserRequirementsProperties,
    getBlobInfo: getBlobInfo,
    getBundleProductDetail: getBundleProductDetail,
    getBundleProductDetailProductAvailability: getBundleProductDetailProductAvailability,
    getBundleProductDetailQuantityOnHand: getBundleProductDetailQuantityOnHand,
    getBundleProducts: getBundleProducts,
    getBundleProductsForProduct: getBundleProductsForProduct,
    getCashExpenseTypes: getCashExpenseTypes,
    getCashJournalDenominations: getCashJournalDenominations,
    getCFDConfiguration: getCFDConfiguration,
    getCompanyForUser: getCompanyForUser,
    getConfigurableProductDetail: getConfigurableProductDetail,
    getConfigurableProductDetailProductAvailability: getConfigurableProductDetailProductAvailability,
    getConfigurableProductDetailQuantityOnHand: getConfigurableProductDetailQuantityOnHand,
    getConfigurableProductSelectedProductDetail: getConfigurableProductSelectedProductDetail,
    getConfigurationQrForDevice: getConfigurationQrForDevice,
    getCurrentApplication: getCurrentApplication,
    getCurrentCashJournals: getCurrentCashJournals,
    getCurrentFinancialPeriodSummary: getCurrentFinancialPeriodSummary,
    getCurrentInitialCycleCount: getCurrentInitialCycleCount,
    getCurrentUser: getCurrentUser,
    getCurrentUserIdentificationInfo: getCurrentUserIdentificationInfo,
    getCustomFieldTypes: getCustomFieldTypes,
    getCustomerReturnReasons: getCustomerReturnReasons,
    getCycleCountOriginTypes: getCycleCountOriginTypes,
    getDeliveryOrderData: getDeliveryOrderData,
    getDeviceTypes: getDeviceTypes,
    getDigitalGiftCardOptions: getDigitalGiftCardOptions,
    getDiscountById: getDiscountById,
    getDiscountProductOptionsForOrder: getDiscountProductOptionsForOrder,
    getDiscountProductOptionsForOrderLine: getDiscountProductOptionsForOrderLine,
    getElectronicInvoiceData: getElectronicInvoiceData,
    getEmailAddressDomainSuggestions: getEmailAddressDomainSuggestions,
    getEnumValues: getEnumValues,
    getFinancialPeriodClosingImpediments: getFinancialPeriodClosingImpediments,
    getFinancialPeriodDetails: getFinancialPeriodDetails,
    getFinancialPeriodInformation: getFinancialPeriodInformation,
    getFiscalOrderData: getFiscalOrderData,
    getFullStockCount: getFullStockCount,
    getGiftCardOptions: getGiftCardOptions,
    getGiftWrappingOptionsForOrder: getGiftWrappingOptionsForOrder,
    getGreetingCards: getGreetingCards,
    getInitialCycleCountLabel: getInitialCycleCountLabel,
    getInquiry: getInquiry,
    getLotteryNumber: getLotteryNumber,
    getOpenCashDrawerReasons: getOpenCashDrawerReasons,
    getOrder: getOrder,
    getOrderDiscountInfo: getOrderDiscountInfo,
    getOrderFulfillmentOptions: getOrderFulfillmentOptions,
    getOrderSignature: getOrderSignature,
    getOrderStatus: getOrderStatus,
    getOrderStatusForOrders: getOrderStatusForOrders,
    getOrderStatusHistory: getOrderStatusHistory,
    getOrderSummaryForShipping: getOrderSummaryForShipping,
    getOrganizationUnitDetailed: getOrganizationUnitDetailed,
    getOrganizationUnitSummary: getOrganizationUnitSummary,
    getPackagesForCarrier: getPackagesForCarrier,
    getPaymentTypeByID: getPaymentTypeByID,
    getPaymentTransaction: getPaymentTransaction,
    getPaymentTransactionsForOrder: getPaymentTransactionsForOrder,
    getPickProductDiscountOptionsForOrder: getPickProductDiscountOptionsForOrder,
    getPickProductDiscountOptionsForOrderLine: getPickProductDiscountOptionsForOrderLine,
    getPickProductDiscountOptionsForOrderProducts: getPickProductDiscountOptionsForOrderProducts,
    getPotentialBundleProductsForOrder: getPotentialBundleProductsForOrder,
    getPriceListUsageTypes: getPriceListUsageTypes,
    getProductAvailability: getProductAvailability,
    getProductDetail: getProductDetail,
    getProductDetailProductAvailability: getProductDetailProductAvailability,
    getProductDetailQuantityOnHand: getProductDetailQuantityOnHand,
    getProductPrices: getProductPrices,
    getProductPricesForSearchProducts: getProductPricesForSearchProducts,
    getProductPricesForShoppingCart: getProductPricesForShoppingCart,
    getProductPricing: getProductPricing,
    getProductRequirementById: getProductRequirementById,
    getProductRequirementValuesForOrder: getProductRequirementValuesForOrder,
    getProductRequirementValuesForOrderLine: getProductRequirementValuesForOrderLine,
    getProductSearchSuggestions: getProductSearchSuggestions,
    getProductUnitOfMeasureQuantities: getProductUnitOfMeasureQuantities,
    getProductVariants: getProductVariants,
    getProducts: getProducts,
    getQuantityOnHandForProducts: getQuantityOnHandForProducts,
    getQuickBuy: getQuickBuy,
    getQuickBuyOptions: getQuickBuyOptions,
    getQuickBuys: getQuickBuys,
    getRefundOptionsForOrder: getRefundOptionsForOrder,
    getRequiredDataForOrder: getRequiredDataForOrder,
    getReturnOrdersForOrder: getReturnOrdersForOrder,
    getReturnReasons: getReturnReasons,
    getReturnToSupplierData: getReturnToSupplierData,
    getReturnableStatusForOrder: getReturnableStatusForOrder,
    getSalesTaxEstimateForOrder: getSalesTaxEstimateForOrder,
    getServiceProducts: getServiceProducts,
    getShipmentDetails: getShipmentDetails,
    getShoppingCart: getShoppingCart,
    getShoppingCartInfo: getShoppingCartInfo,
    getShopsByProximity: getShopsByProximity,
    getStation: getStation,
    getStatusForZonedCycleCountPreCounts: getStatusForZonedCycleCountPreCounts,
    getStockAvailabilityEstimateForOrder: getStockAvailabilityEstimateForOrder,
    getStockAvailabilityEstimateForProducts: getStockAvailabilityEstimateForProducts,
    getStockAvailabilityTimeline: getStockAvailabilityTimeline,
    getStockDetailsForProduct: getStockDetailsForProduct,
    getStockLabels: getStockLabels,
    getStockMutationReasons: getStockMutationReasons,
    getStockNotification: getStockNotification,
    getStockNotificationForCurrentUser: getStockNotificationForCurrentUser,
    getStockReplenishmentTaskData: getStockReplenishmentTaskData,
    getStockReplenishmentTaskZones: getStockReplenishmentTaskZones,
    getTaxExemptionReasons: getTaxExemptionReasons,
    getTransportOrderLineData: getTransportOrderLineData,
    getUser: getUser,
    getUserBySocialSecurityNumber: getUserBySocialSecurityNumber,
    getUserCardBalance: getUserCardBalance,
    getUserCardDetails: getUserCardDetails,
    getUserCardsForUser: getUserCardsForUser,
    getUserInfoBySocialSecurityNumber: getUserInfoBySocialSecurityNumber,
    getUserOrigins: getUserOrigins,
    getUserPhoneNumbers: getUserPhoneNumbers,
    getUserRequirements: getUserRequirements,
    getUserRequirementsForSubscriptions: getUserRequirementsForSubscriptions,
    getUserSubscriptions: getUserSubscriptions,
    getUserTaskCounts: getUserTaskCounts,
    getUserTaskDetails: getUserTaskDetails,
    getUserTaskNotCompletedReasons: getUserTaskNotCompletedReasons,
    getWarehouseOrderData: getWarehouseOrderData,
    getWebshops: getWebshops,
    getZonedCycleCount: getZonedCycleCount,
    getZonedCycleCountPreCount: getZonedCycleCountPreCount,
    globalBlueGetEligibilityInformation: globalBlueGetEligibilityInformation,
    globalBluePrintForm: globalBluePrintForm,
    globalBlueSetGlobalBlueIdentifier: globalBlueSetGlobalBlueIdentifier,
    globalBlueStartTaxFree: globalBlueStartTaxFree,
    janrainRequestPasswordResetToken: janrainRequestPasswordResetToken,
    janrainResetPassword: janrainResetPassword,
    listAddressBook: listAddressBook,
    listAdyenCheckoutGateways: listAdyenCheckoutGateways,
    listAppSettings: listAppSettings,
    listApplicationCultures: listApplicationCultures,
    listApplications: listApplications,
    listAvailableShippingMethods: listAvailableShippingMethods,
    listAvailableUserTasks: listAvailableUserTasks,
    listBlobs: listBlobs,
    listBlobsForOrder: listBlobsForOrder,
    listBookings: listBookings,
    listCashDeposits: listCashDeposits,
    listCashExpenses: listCashExpenses,
    listCashTransactionLedger: listCashTransactionLedger,
    listCashTransactionLedgerGroups: listCashTransactionLedgerGroups,
    listClockedInEmployeesForFinancialPeriod: listClockedInEmployeesForFinancialPeriod,
    listCountries: listCountries,
    listCultures: listCultures,
    listCustomFields: listCustomFields,
    listCustomOrderStatus: listCustomOrderStatus,
    listDevices: listDevices,
    listDisabledDiscountsOnOrder: listDisabledDiscountsOnOrder,
    listEmployeesForOrganizationUnit: listEmployeesForOrganizationUnit,
    listEventLedger: listEventLedger,
    listFinancialPeriods: listFinancialPeriods,
    listFullStockCountLabelProducts: listFullStockCountLabelProducts,
    listFullStockCountLabels: listFullStockCountLabels,
    listFullStockCountResult: listFullStockCountResult,
    listFullStockCountSummary: listFullStockCountSummary,
    listGeneralLedgers: listGeneralLedgers,
    listInitialCycleCountLabels: listInitialCycleCountLabels,
    listInterbranchOrganizationUnitForOrder: listInterbranchOrganizationUnitForOrder,
    listInvoicesForOrder: listInvoicesForOrder,
    listManualDiscounts: listManualDiscounts,
    listOpeningHours: listOpeningHours,
    listOrderFulfillments: listOrderFulfillments,
    listOrderLines: listOrderLines,
    listOrderShipments: listOrderShipments,
    listOrdersForCustomer: listOrdersForCustomer,
    listOrdersWithCustomerReferences: listOrdersWithCustomerReferences,
    listOrganizationUnitLanguages: listOrganizationUnitLanguages,
    listOrganizationUnitSummaries: listOrganizationUnitSummaries,
    listOrganizationUnitsDetailed: listOrganizationUnitsDetailed,
    listProductBundles: listProductBundles,
    listPurchaseOrderShipments: listPurchaseOrderShipments,
    listReturnToSupplierTaskOrderLines: listReturnToSupplierTaskOrderLines,
    listReturnToSupplierTaskShipmentLines: listReturnToSupplierTaskShipmentLines,
    listReturnableSuppliersForOrder: listReturnableSuppliersForOrder,
    listShelfProducts: listShelfProducts,
    listShipmentLines: listShipmentLines,
    listShopsInArea: listShopsInArea,
    listStationsForOrganizationUnit: listStationsForOrganizationUnit,
    listStockMutationReasons: listStockMutationReasons,
    listStockReplenishmentTaskData: listStockReplenishmentTaskData,
    listSuspendedOrders: listSuspendedOrders,
    listTaxCodes: listTaxCodes,
    listUserBorrowedProducts: listUserBorrowedProducts,
    listUserCardMutations: listUserCardMutations,
    listUserInteractions: listUserInteractions,
    listUserRequirementSets: listUserRequirementSets,
    listUserRequirements: listUserRequirements,
    listWorkedHours: listWorkedHours,
    listWorkedHoursForCurrentEmployee: listWorkedHoursForCurrentEmployee,
    listWorkedHoursForFinancialPeriod: listWorkedHoursForFinancialPeriod,
    listZonedCycleCounts: listZonedCycleCounts,
    login: login,
    logout: logout,
    makeUserPhoneNumberPrimary: makeUserPhoneNumberPrimary,
    missingProductForInitialCycleCount: missingProductForInitialCycleCount,
    modifyOrderLinePrice: modifyOrderLinePrice,
    modifyQuantityOrdered: modifyQuantityOrdered,
    moveCash: moveCash,
    moveStock: moveStock,
    openCashDrawer: openCashDrawer,
    openFinancialPeriod: openFinancialPeriod,
    paazlGetShippingOptionsForOrder: paazlGetShippingOptionsForOrder,
    paazlUpdateShippingOptionsForOrder: paazlUpdateShippingOptionsForOrder,
    parseBarcode: parseBarcode,
    ping: ping,
    placeOrder: placeOrder,
    postNLGetAvailablePickUpLocationsForOrder: postNLGetAvailablePickUpLocationsForOrder,
    postNLGetAvailableTimeframesForOrder: postNLGetAvailableTimeframesForOrder,
    postNLGetShippingOptionsForOrder: postNLGetShippingOptionsForOrder,
    postNLUpdateShippingOptionsForOrder: postNLUpdateShippingOptionsForOrder,
    preCountInitialCycleCountLabel: preCountInitialCycleCountLabel,
    prefigureDiscounts: prefigureDiscounts,
    prepareOrderForCheckout: prepareOrderForCheckout,
    previewOrderFulFillment: previewOrderFulFillment,
    printControlDocument: printControlDocument,
    printFullStockCountLabels: printFullStockCountLabels,
    printInterbranchOrderReceipt: printInterbranchOrderReceipt,
    printPaperInvoice: printPaperInvoice,
    printPaperInvoices: printPaperInvoices,
    printPaperOrderConfirmation: printPaperOrderConfirmation,
    printPriceLabel: printPriceLabel,
    printProductPriceLabel: printProductPriceLabel,
    printQrCode: printQrCode,
    printReturnToSupplierTaskShipment: printReturnToSupplierTaskShipment,
    printStockReservationReceipt: printStockReservationReceipt,
    printTerminalReport: printTerminalReport,
    printThermalGiftReceipt: printThermalGiftReceipt,
    printThermalInvoice: printThermalInvoice,
    printThermalInvoices: printThermalInvoices,
    printThermalOrderConfirmation: printThermalOrderConfirmation,
    printThermalPaymentProof: printThermalPaymentProof,
    processPrintTasks: processPrintTasks,
    produceCashDepositDocument: produceCashDepositDocument,
    produceCashExpenseDocument: produceCashExpenseDocument,
    produceDocuments: produceDocuments,
    produceShipmentDocuments: produceShipmentDocuments,
    productCategorySearch: productCategorySearch,
    receiveShipment: receiveShipment,
    recoverShoppingCart: recoverShoppingCart,
    refundOrder: refundOrder,
    registerUserBorrowedProducts: registerUserBorrowedProducts,
    removeOrderLineUnitPriceCorrection: removeOrderLineUnitPriceCorrection,
    removePushNotificationDevice: removePushNotificationDevice,
    renderThermalPrintResult: renderThermalPrintResult,
    reprintThermalPrintResult: reprintThermalPrintResult,
    requestDeviceAuthenticationToken: requestDeviceAuthenticationToken,
    requestPasswordResetToken: requestPasswordResetToken,
    requestPaymentUpdate: requestPaymentUpdate,
    reserveOrder: reserveOrder,
    resetUserPassword: resetUserPassword,
    returnOrderLines: returnOrderLines,
    returnUserBorrowedProducts: returnUserBorrowedProducts,
    ritualsAddGiftCardToOrder: ritualsAddGiftCardToOrder,
    ritualsApplyEmployeeDiscount: ritualsApplyEmployeeDiscount,
    ritualsAttachCustomerToOrder: ritualsAttachCustomerToOrder,
    ritualsCreateCustomerReturn: ritualsCreateCustomerReturn,
    ritualsGeneratePassbookPass: ritualsGeneratePassbookPass,
    ritualsGetBlobsForReturn: ritualsGetBlobsForReturn,
    ritualsGetCartData: ritualsGetCartData,
    ritualsGetCustomerData: ritualsGetCustomerData,
    ritualsGetDeliveryOrderData: ritualsGetDeliveryOrderData,
    ritualsGetEmployeeDiscount: ritualsGetEmployeeDiscount,
    ritualsGetOrderForReturn: ritualsGetOrderForReturn,
    ritualsGetUserDataForUser: ritualsGetUserDataForUser,
    ritualsListAvailablePickupStores: ritualsListAvailablePickupStores,
    ritualsPaazlGetShippingOptionsForOrder: ritualsPaazlGetShippingOptionsForOrder,
    ritualsPaazlUpdateShippingOptionsForOrder: ritualsPaazlUpdateShippingOptionsForOrder,
    ritualsPrintTalismanDocuments: ritualsPrintTalismanDocuments,
    ritualsScanLoyaltyCard: ritualsScanLoyaltyCard,
    ritualsSendAddressFormLinkFromCart: ritualsSendAddressFormLinkFromCart,
    ritualsSendCarouselFromCart: ritualsSendCarouselFromCart,
    ritualsSendPaazlLinkFromCart: ritualsSendPaazlLinkFromCart,
    ritualsSendPaymentLinkFromCart: ritualsSendPaymentLinkFromCart,
    ritualsSendReceiptFromCart: ritualsSendReceiptFromCart,
    ritualsSetCartData: ritualsSetCartData,
    ritualsUpdateLoyalty: ritualsUpdateLoyalty,
    searchOrders: searchOrders,
    searchProducts: searchProducts,
    searchUsers: searchUsers,
    setCustomUserData: setCustomUserData,
    setCustomerReferencesOnOrder: setCustomerReferencesOnOrder,
    setDefaultBillingAddress: setDefaultBillingAddress,
    setDefaultShippingAddress: setDefaultShippingAddress,
    setDiscountProductOption: setDiscountProductOption,
    setElectronicInvoiceData: setElectronicInvoiceData,
    setFiscalOrderData: setFiscalOrderData,
    setGiftWrappingOptionsOnOrder: setGiftWrappingOptionsOnOrder,
    setLineForReturnToSupplierShipment: setLineForReturnToSupplierShipment,
    setLotteryNumber: setLotteryNumber,
    setOrderFulfillmentOptions: setOrderFulfillmentOptions,
    setOrderSoldBy: setOrderSoldBy,
    setPCIQuestionAnswerForFinancialPeriod: setPCIQuestionAnswerForFinancialPeriod,
    setPickProductDiscountOptionsForOrderLine: setPickProductDiscountOptionsForOrderLine,
    setPickupOrganizationUnit: setPickupOrganizationUnit,
    setRemarkOnOrder: setRemarkOnOrder,
    setRequestedDate: setRequestedDate,
    setSerialNumbers: setSerialNumbers,
    setShippingMethod: setShippingMethod,
    setWarehouseOrderData: setWarehouseOrderData,
    shipOrder: shipOrder,
    signOrder: signOrder,
    splitOrderLine: splitOrderLine,
    startCloseCashJournal: startCloseCashJournal,
    startCompositeUserTask: startCompositeUserTask,
    startFullStockCount: startFullStockCount,
    startFullStockCountLabel: startFullStockCountLabel,
    startOpenCashJournal: startOpenCashJournal,
    startPrintPriceLabelTask: startPrintPriceLabelTask,
    startPrintTask: startPrintTask,
    startReceiveShipment: startReceiveShipment,
    startReservationCleanup: startReservationCleanup,
    startReservationDeviation: startReservationDeviation,
    startReturnToSupplierTask: startReturnToSupplierTask,
    startSecondChanceProductRegistration: startSecondChanceProductRegistration,
    startShipFromStoreTask: startShipFromStoreTask,
    startStockMovementFollowUp: startStockMovementFollowUp,
    startStockReplenishment: startStockReplenishment,
    startStockReservation: startStockReservation,
    startTransaction: startTransaction,
    startZonedCycleCount: startZonedCycleCount,
    startZonedCycleCountPreCount: startZonedCycleCountPreCount,
    storeBlob: storeBlob,
    submitInquiry: submitInquiry,
    subscribeUser: subscribeUser,
    suspendOrder: suspendOrder,
    unsubscribeUser: unsubscribeUser,
    updateAddressBookItem: updateAddressBookItem,
    updateCompanyForUser: updateCompanyForUser,
    updateDigitalGiftCardOptions: updateDigitalGiftCardOptions,
    updateInterbranchOrder: updateInterbranchOrder,
    updateOrderAddresses: updateOrderAddresses,
    updateOrderBillingAddress: updateOrderBillingAddress,
    updateOrderCheckoutOptions: updateOrderCheckoutOptions,
    updateOrderCustomFields: updateOrderCustomFields,
    updateOrderLineStockLabel: updateOrderLineStockLabel,
    updateOrderShippingAddress: updateOrderShippingAddress,
    updateProductRequirementValuesForOrderLine: updateProductRequirementValuesForOrderLine,
    updateQuickBuy: updateQuickBuy,
    updateReturnToSupplierOrder: updateReturnToSupplierOrder,
    updateSerialNumber: updateSerialNumber,
    updateStockReplenishmentTaskData: updateStockReplenishmentTaskData,
    updateUser: updateUser,
    updateUserAccountType: updateUserAccountType,
    updateUserPhoneNumber: updateUserPhoneNumber,
    validateIdentificationPin: validateIdentificationPin,
    validateFiscalID: validateFiscalID,
    validateOrderShipment: validateOrderShipment,
    validatePreCount: validatePreCount,
    validateToken: validateToken,
    validateVatNumber: validateVatNumber,
    verifyCustomer: verifyCustomer,
    verifyOrder: verifyOrder
});

/**
 * This error will be thrown when the fetch API timesout, this will help checking whether the error by the fetch API were caused by a
 * timeout. Timeouts of the fetch API are a custom implementation and therefor we throw custom errors.
 *
 * ### Example
 * ```typescript
 * const [action, promise] = reducerName.createFetchAction();
 * store.dispatch(action);
 * // Request timesout
 * promise.catch( error => {
 *   if ( error instanceof TimeoutError ) {
 *     // handle time out here
 *   }
 * })
 * ```
 */
class TimeoutError extends Error {
    // I don't know how to cover the super line in the tests, see following github issue for more inforamtion
    // @see https://github.com/istanbuljs/istanbuljs/issues/70
    //
    /* istanbul ignore next */
    constructor(message) {
        super(message);
        // https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
        Object.setPrototypeOf(this, TimeoutError.prototype);
    }
}

// Compose the enhancers adding Redux devtools support if available
//
const composeEnhancers = (typeof global !== 'undefined' && global.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose;
const storeReducer = combineReducers(mapValues(reducers, 'reducer'));
// Build the initial state
//
const initialState = mapValues(reducers, 'initialState');
// Build the root reducer
//
const rootReducer = (state, action) => {
    switch (action.type) {
        case 'RESET':
            {
                // Reset the store state and merge with optional payload
                //
                state = extend({}, initialState, action.payload);
            }
            break;
    }
    return storeReducer(state, action);
};
////////////////////////////////////////////////////////////////////////////////
// STORE
////////////////////////////////////////////////////////////////////////////////
//
const sagaMiddleware = sagaMiddlewareFactory();
function configureStore(initialStoreState) {
    // configure middlewares
    //
    const middlewares = [
        sagaMiddleware,
    ];
    if (typeof global !== 'undefined' && global.__REDUX_DEVTOOLS_FLIPPER_MIDDLEWARE__) {
        middlewares.push(global.__REDUX_DEVTOOLS_FLIPPER_MIDDLEWARE__);
    }
    // compose enhancers
    //
    const enhancer = composeEnhancers(applyMiddleware(...middlewares));
    // create store
    //
    return createStore(rootReducer, initialStoreState, enhancer);
}
// Export store singleton instance
//
const store = configureStore(initialState);
log$1('[EVA-SDK-REDUX:store] Store initialised', store.getState());
////////////////////////////////////////////////////////////////////////////////
// SAGA
////////////////////////////////////////////////////////////////////////////////
//
// Run the all the sagas
//
each(reducers, (reducer) => {
    sagaMiddleware.run(reducer.saga);
});

/**
 * The realtime communicator handles the SignalR hubs and their connections
 *
 * @export
 * @class RealtimeCommunicator
 */
class RealtimeCommunicator {
    /**
     * Creates an instance of RealtimeCommunicator
     * The realtime communicator uses SignalR to connect to a number of hubs supplied
     * by EVA.
     * The getApplicationConfiguration response will contain the required urls
     *
     * @memberof RealtimeCommunicator
     */
    constructor() {
        /**
         * The current list of EVA SignalR hubs
         *
         * @private
         * @type {EVA.Core.GetApplicationConfigurationResponse}
         * @memberof RealtimeCommunicator
         */
        this.hubs = [];
        /**
         * All the connection grouped by hub
         *
         * @type {{[hubName: string]: {[id: string]: IHubConnection } }}
         * @memberof RealtimeCommunicator
         */
        this.connection = {};
        /**
         * This is the special purpose channel used for client status notifications
         *
         * @private
         */
        this.clientAppStatusChannel = 'ClientApps';
        /**
         * The list of all channels we are connected to and a unix timestamp
         * of when we joined it
         *
         * @private
         * @type {{[channelName: string]: IChannelStatus}}
         */
        this.channels = {};
        /**
         * The list of seen peers from ping responses
         *
         * @private
         * @type {{[UUID: string]: IPeerClientStatus }}
         */
        this.peers = {};
        /**
         * The current peers exposed as an observable stream
         *
         * @private
         * @type {BehaviorSubject<IPeerClientStatus[]>}
         */
        this._peers$ = new BehaviorSubject(Object.values(this.peers));
        this.peers$ = this._peers$.asObservable();
        /**
         * Used to mark peers that haven't sent a message recently
         *
         * @private
         */
        this.idleThreshold = 10000; // 10 seconds
        /**
         * Used to mark peers that have missed multiple heartbeats as disconnected
         *
         * @private
         */
        this.disconnectedThreshold = 60000; // 60 seconds (4 heartbeats)
        /**
         * Used to remove peers that haven't sent a heartbeat in a while
         *
         * @private
         */
        this.staleThreshold = 1800000; // 5 minutes
        /**
         * The interval at which the client sends an update about its peers
         *
         * @private
         */
        this.heartbeatInterval = 1000; // 1 second
        /**
         * The interval at which the client sends a status update
         *
         * @private
         */
        this.clientStatusInterval = 15000; // 15 seconds
        /**
         * The date the last client status update was sent at
         *
         * @private
         */
        this.lastSentClientStatusAt = new Date(0);
        /**
         * Used to track any in flight reconnect attempts so we don't end up creating multiple
         *
         * @private
         * @type {Promise<void>}
         */
        this.pendingReconnectAttempt = {};
        // Initialize the events feed
        //
        this.event$ = new Subject();
        // Initialize the activity feed
        //
        this.activity$ = new Subject();
    }
    /**
     * Start listening to the getApplicationConfig
     * Will resolve its promise when first application config is received
     *
     * @memberof RealtimeCommunicator
     */
    start() {
        return new Promise((resolve, reject) => {
            debug('[RT:HUB] Start listening for application configuration');
            this.configurationSubscription = getApplicationConfiguration.getResponse$()
                .subscribe((response) => {
                if (response) {
                    debug(`[RT:HUB] Updated application configuration`, response);
                    const hubEndpointUrl = get(response, 'Configuration.SignalR:BaseUrl');
                    settings.communicatorEndPointURL = hubEndpointUrl;
                    debug(`[RT:HUB] Communicator endpoint url`, hubEndpointUrl);
                    const newHubs = get(response, 'Configuration.SignalR:Hubs', []);
                    // Setup hubs if response differs
                    //
                    if (!isEqual(this.hubs, newHubs)) {
                        this.disconnectAll()
                            .then(() => {
                            // Update current hubs
                            //
                            this.hubs = newHubs;
                            // Prepare for hub connections
                            //
                            each(newHubs, (hub) => {
                                this.connection[hub.Name] = [];
                            });
                            resolve();
                        })
                            .catch(reject);
                    }
                    // NOOP: Wait for changed or new application configuration
                }
            });
        });
    }
    /**
     * Stop listening for application configuration changes and disconnect all hubs
     *
     * @memberof RealtimeCommunicator
     */
    async stop() {
        // Disconnect all existing hub connections
        //
        await this.disconnectAll();
        // Stop the subscription for getApplicationConfiguration changes
        //
        if (this.configurationSubscription) {
            this.configurationSubscription.unsubscribe();
            this.configurationSubscription = undefined;
        }
        // Clear the known hubs
        //
        this.hubs = [];
        return;
    }
    /**
     * Helper method to build signalr connection headers
     *
     * @param {IHubJoinRequestBase} joinRequest
     */
    buildHeaders(joinRequest) {
        const options = joinRequest.options;
        // Prepare the headers
        //
        const headers = {};
        headers['EVA-User-Agent'] = `${settings.appName}/${settings.appVersion}`;
        headers['EVA-API-Version'] = `${EVA_API_VERSION}`;
        // Add the current application token
        //
        if (options && options.applicationToken) {
            headers['EVA-App-Token'] = options.applicationToken;
        }
        else if (settings.applicationToken) {
            headers['EVA-App-Token'] = settings.applicationToken;
        }
        // Add the requested organization unit id
        //
        if (options && options.requestedOrganizationUnitID) {
            headers['EVA-Requested-OrganizationUnitID'] = `${options.requestedOrganizationUnitID}`;
        }
        else if (settings.requestedOrganizationUnitID) {
            headers['EVA-Requested-OrganizationUnitID'] = `${settings.requestedOrganizationUnitID}`;
        }
        // Add the requested organization unit query
        //
        if (options && options.requestedOrganizationUnitQuery) {
            headers['EVA-Requested-OrganizationUnit-Query'] = JSON.stringify(options.requestedOrganizationUnitQuery);
        }
        else if (settings.requestedOrganizationUnitQuery) {
            headers['EVA-Requested-OrganizationUnit-Query'] = JSON.stringify(settings.requestedOrganizationUnitQuery);
        }
        return headers;
    }
    /**
     * Helper method to build signalr connection query parameters
     *
     * @protected
     * @param {IHubJoinRequestBase} joinRequest
     * @returns
     */
    buildQueryParams(joinRequest) {
        const options = joinRequest.options;
        const params = [];
        // Add the requested organization unit id
        //
        if (options && options.requestedOrganizationUnitID) {
            params.push(`requestedOrganizationUnitID=${options.requestedOrganizationUnitID}`);
        }
        else if (settings.requestedOrganizationUnitID) {
            params.push(`requestedOrganizationUnitID=${settings.requestedOrganizationUnitID}`);
        }
        // Add the requested organization unit query
        //
        if (options && options.requestedOrganizationUnitQuery) {
            params.push(`requestedOrganizationUnitQuery=${JSON.stringify(options.requestedOrganizationUnitQuery)}`);
        }
        else if (settings.requestedOrganizationUnitQuery) {
            params.push(`requestedOrganizationUnitQuery=${JSON.stringify(settings.requestedOrganizationUnitQuery)}`);
        }
        // Add Global Blue group identifier if supplied
        //
        if (options === null || options === void 0 ? void 0 : options.globalBlueGroupID) {
            params.push(`groupID=${options.globalBlueGroupID}`);
        }
        return params;
    }
    /**
     * Disconnect all existing hub connections
     *
     * @returns
     * @memberof RealtimeCommunicator
     */
    async disconnectAll() {
        await Promise.all(map$1(this.connection, async (hubConnections, hubName) => {
            return await Promise.all(map$1(hubConnections, async (hubConnection) => {
                try {
                    await hubConnection.connection.stop();
                    debug(`[RT:HUB] Disconnected ${hubName}/${hubConnection.subjectId}`);
                }
                catch (error) {
                    warn(`[RT:HUB] Disconnect failed: ${error}`);
                }
            }));
        }));
        // Purge all connection regardless of failure state
        //
        this.connection = {};
        return;
    }
    //////////////////////////////////////////////////////////////////////////////
    // Device hub
    //////////////////////////////////////////////////////////////////////////////
    //
    /**
     * Start listening to device hub events for a specific device
     * You can either supply a callback(s) here or listen to the event$ observable
     *
     * @param {IHubDeviceJoinRequest} subscription The subscription details
     * @returns
     * @memberof RealtimeCommunicator
     */
    async joinDeviceHub(subscription) {
        const hubName = subscription.hubName || 'DeviceHub';
        debug(`[RT:HUB] Connecting to ${hubName}`);
        // Create a new hub connection
        // Combine server endpoint with hub path
        //
        const hubDetails = find(this.hubs, { Name: hubName });
        if (!hubDetails) {
            throw new Error(`Unknown hub ${hubName}`);
        }
        const token = subscription.token;
        let hubUri = settings.communicatorEndPointURL + hubDetails.Path;
        const params = this.buildQueryParams(subscription);
        if (params.length) {
            hubUri += `?${params.join('&')}`;
        }
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUri, {
            accessTokenFactory: () => { console.log(`[RT:HUB] Setting token for hub ${hubName}`, token); return token; },
            skipNegotiation: true,
            transport: 1,
            headers: this.buildHeaders(subscription),
        })
            .configureLogging(settings.enableLogging ? 1 : 5) // 1 = debug, 5 = critical
            .withAutomaticReconnect()
            .build();
        // Connect to the hub
        //
        await connection.start();
        debug(`[RT:HUB] Connected ${hubName}`);
        // Setup event listener(s)
        //
        connection.onclose(() => {
            log$1(`[RT:HUB] Closed connection to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'close',
                payload: `${hubConnection.reconnect_count}`,
                ts: new Date(),
            });
            // If the connection closed after SignalR tries to reconnect we have
            // network issues. We should try to reconnect at a regular interval
            //
            if (hubConnection.reconnect_count > 0) {
                this.reconnectAttempt(hubConnection);
            }
        });
        connection.onreconnecting(() => {
            warn(`[RT:HUB] Reconnecting to ${hubName}...`);
            this.activity$.next({
                hubName,
                type: 'reconnecting',
                payload: undefined,
                ts: new Date(),
            });
            // Set the reconnecting state which will help if we can't recover
            //
            hubConnection.reconnect_count += 1;
        });
        connection.onreconnected(async () => {
            log$1(`[RT:HUB] Reconnected to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'reconnected',
                payload: undefined,
                ts: new Date(),
            });
            // Unset the reconnecting state because we appear to have recovered
            //
            hubConnection.reconnect_count = 0;
            // Resubscribe because the connection id might have changed
            // There are 2 levels of reconnect: signalr and our own fallback
            // The fallback is a completely new connection start
            // This is the signalR reconnect support
            //
            if (hubConnection.subscribe) {
                try {
                    await hubConnection.subscribe();
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Failed to resubscribe to events for hub ${hubName}`, error);
                }
            }
        });
        connection.on('BarcodeScanned', (scanEvent) => {
            debug(`[RT:HUB:EVENT] Received barcode scan`, scanEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'BarcodeScanned',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.barcodeScannedCallback)) {
                subscription.barcodeScannedCallback(scanEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'BarcodeScanned',
                subjectId: subscription.deviceId,
                data: scanEvent,
            });
        });
        connection.on('SessionPushedToDeviceID', (transferResponse) => {
            debug(`[RT:HUB:EVENT] Received session transfer event`, transferResponse);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'SessionPushedToDeviceID',
                ts: new Date(),
            });
            // Call RequestDeviceAuthenticationToken for a full token
            // We need to set the received token as the authorization header value
            //
            const [fetchAction] = requestDeviceAuthenticationToken.createFetchAction();
            requestDeviceAuthenticationToken.fetchData(fetchAction, undefined, transferResponse.AuthenticationRequestToken)
                .then((tokenResponse) => {
                const fullAuthenticationToken = tokenResponse.AuthenticationToken;
                const transferEvent = {
                    OrderID: transferResponse.OrderID,
                    DeviceID: transferResponse.DeviceID,
                    AuthenticationToken: fullAuthenticationToken,
                };
                // Do the callback if provided
                //
                if (isFunction$1(subscription.sessionPushedToDeviceCallback)) {
                    subscription.sessionPushedToDeviceCallback(transferEvent);
                }
                // Emit event on the observable
                //
                this.event$.next({
                    hub: hubDetails.Name,
                    method: 'SessionPushedToDeviceID',
                    subjectId: subscription.deviceId,
                    data: transferEvent,
                });
            })
                .catch((error$1) => {
                error('[RT:HUB:EVENT] Failed to generate device authentication token', error$1);
            });
        });
        connection.on('SubscribedToDeviceID', (joinEvent) => {
            debug(`[RT:HUB:EVENT] Received subscribed event`, joinEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'SubscribedToDeviceID',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.joinCallback)) {
                subscription.joinCallback(joinEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'JoinHub',
                subjectId: subscription.deviceId,
                data: joinEvent,
            });
        });
        connection.on('UnsubscribedFromDeviceID', (leaveEvent) => {
            debug(`[RT:HUB:EVENT] Received unsubscribed event`, leaveEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'UnsubscribedFromDeviceID',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.leaveCallback)) {
                subscription.leaveCallback(leaveEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'LeaveHub',
                subjectId: subscription.deviceId,
                data: leaveEvent,
            });
        });
        // Add the hub connection
        //
        const hubConnection = {
            connection,
            hub: hubDetails,
            uri: hubUri,
            subjectId: subscription.deviceId,
            token: subscription.token,
            reconnect_count: 0,
            subscribe: async () => {
                const subResponse = await connection.invoke('SubscribeToDeviceID', { DeviceID: subscription.deviceId, SubscriberName: subscription.subscriberName });
                debug(`[RT:HUB:EVENT] Subscribed to device events`, subResponse);
                this.activity$.next({
                    hubName,
                    type: 'send',
                    payload: 'SubscribeToDeviceID',
                    ts: new Date(),
                });
            }
        };
        this.connection[hubName].push(hubConnection);
        // Subscribe to device events
        //
        if (hubConnection.subscribe) {
            try {
                await hubConnection.subscribe();
            }
            catch (error) {
                warn(`[RT:HUB:EVENT] Failed to subscribe to device`, error);
            }
        }
        return hubConnection;
    }
    /**
     * Stop listening to device hub events for a specific device
     *
     * @param {string} deviceId The device to stop listening to
     * @param {string} [hub='DeviceHub'] The hub name
     * @memberof RealtimeCommunicator
     */
    async leaveDeviceHub(subscription) {
        const hubName = subscription.hubName || 'DeviceHub';
        debug(`[RT:HUB] Disconnecting from ${hubName}`);
        const hubConnections = filter(this.connection[hubName], { subjectId: subscription.deviceId });
        if (hubConnections.length) {
            for (const hubConnection of hubConnections) {
                // Unsubscribe from order
                //
                try {
                    await hubConnection.connection.invoke('UnsubscribeFromDeviceID', { DeviceID: subscription.deviceId, SubscriberName: subscription.subscriberName });
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Error unsubscribing from device events`, error);
                }
                this.activity$.next({
                    hubName,
                    type: 'send',
                    payload: 'UnsubscribeFromDeviceID',
                    ts: new Date(),
                });
                // Stop listening to events
                //
                hubConnection.connection.off('BarcodeScanned');
                hubConnection.connection.off('SessionPushedToDeviceID');
                hubConnection.connection.off('SubscribedToDeviceID');
                hubConnection.connection.off('UnsubscribedFromDeviceID');
                try {
                    await hubConnection.connection.stop();
                }
                catch (error) {
                    warn(`[RT:HUB] Error disconnecting from ${hubName}/${hubConnection.subjectId}`, error);
                }
            }
            // Remove the connection(s)
            //
            remove$1(this.connection[hubName], { subjectId: subscription.deviceId });
            return;
        }
        throw new Error(`Not connected to hub ${hubName}/${subscription.deviceId}`);
    }
    /**
     * Send a scanned barcode to a device hub
     *
     * @param {IEventBarcodeScanned} scanEvent
     * @param {string} [hub='DeviceHub']
     * @returns
     * @memberof RealtimeCommunicator
     */
    async sendScannedBarcode(scanEvent, hubName = 'DeviceHub') {
        const hubConnections = filter(this.connection[hubName], { subjectId: scanEvent.DeviceID });
        if (hubConnections.length) {
            this.activity$.next({
                hubName,
                type: 'send',
                payload: 'ScanBarcode',
                ts: new Date(),
            });
            for (const hubConnection of hubConnections) {
                try {
                    await hubConnection.connection.invoke('ScanBarcode', scanEvent);
                }
                catch (error) {
                    warn(`[RT:HUB] Error sending barcode ${hubName}/${hubConnection.subjectId}`, error);
                }
            }
        }
        throw new Error(`Not connected to hub ${hubName}/${scanEvent.DeviceID}`);
    }
    /**
     * Transfers a session to another device
     *
     * @param {IHubPushSessionToDeviceID} transferSessionEvent
     * @param {string} [hubName='DeviceHub']
     * @returns
     * @memberof RealtimeCommunicator
     */
    async sendSessionToDevice(transferSessionEvent, hubName = 'DeviceHub') {
        const hubConnections = filter(this.connection[hubName], { subjectId: transferSessionEvent.DeviceID });
        if (hubConnections.length) {
            this.activity$.next({
                hubName,
                type: 'send',
                payload: 'PushSessionToDeviceID',
                ts: new Date(),
            });
            for (const hubConnection of hubConnections) {
                try {
                    await hubConnection.connection.invoke('PushSessionToDeviceID', transferSessionEvent);
                }
                catch (error) {
                    warn(`[RT:HUB] Error pushing session to device ${hubName}/${hubConnection.subjectId}`, error);
                }
            }
        }
        throw new Error(`Not connected to hub ${hubName}/${transferSessionEvent.DeviceID}`);
    }
    //////////////////////////////////////////////////////////////////////////////
    // Order hub
    //////////////////////////////////////////////////////////////////////////////
    //
    /**
     * Start listening to order hub events for a specific order
     * You can either supply a callback(s) here or listen to the event$ observable
     *
     * @param {IHubOrderJoinRequest} subscription The subscription details
     * @returns
     * @memberof RealtimeCommunicator
     */
    async joinOrderHub(subscription) {
        const hubName = subscription.hubName || 'OrderHub';
        debug(`[RT:HUB] Connecting to ${hubName}`);
        // Create a new hub connection
        // Combine server endpoint with hub path
        //
        const hubDetails = find(this.hubs, { Name: hubName });
        if (!hubDetails) {
            throw new Error(`Unknown hub ${hubName}`);
        }
        const token = subscription.token;
        let hubUri = settings.communicatorEndPointURL + hubDetails.Path;
        const params = this.buildQueryParams(subscription);
        if (params.length) {
            hubUri += `?${params.join('&')}`;
        }
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUri, {
            accessTokenFactory: () => { console.log(`[RT:HUB] Setting token for hub ${hubName}`, token); return token; },
            skipNegotiation: true,
            transport: 1,
            headers: this.buildHeaders(subscription),
        })
            .configureLogging(settings.enableLogging ? 1 : 5) // 1 = debug, 5 = critical
            .withAutomaticReconnect()
            .build();
        // Connect to the hub
        //
        await connection.start();
        debug(`[RT:HUB] Connected ${hubName}`);
        // Setup event listener(s)
        //
        connection.onclose(() => {
            log$1(`[RT:HUB] Closed connection to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'close',
                payload: `${hubConnection.reconnect_count}`,
                ts: new Date(),
            });
            // If the connection closed after SignalR tries to reconnect we have
            // network issues. We should try to reconnect at a regular interval
            //
            if (hubConnection.reconnect_count > 0) {
                this.reconnectAttempt(hubConnection);
            }
        });
        connection.onreconnecting(() => {
            warn(`[RT:HUB] Reconnecting to ${hubName}...`);
            this.activity$.next({
                hubName,
                type: 'reconnecting',
                payload: undefined,
                ts: new Date(),
            });
            // Set the reconnecting state which will help if we can't recover
            //
            hubConnection.reconnect_count += 1;
        });
        connection.onreconnected(async () => {
            log$1(`[RT:HUB] Reconnected to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'reconnected',
                payload: undefined,
                ts: new Date(),
            });
            // Unset the reconnecting state because we appear to have recovered
            //
            hubConnection.reconnect_count = 0;
            // Resubscribe because the connection id might have changed
            // There are 2 levels of reconnect: signalr and our own fallback
            // The fallback is a completely new connection start
            // This is the signalR reconnect support
            //
            if (hubConnection.subscribe) {
                try {
                    await hubConnection.subscribe();
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Failed to resubscribe to events for hub ${hubName}`, error);
                }
            }
        });
        connection.on('SubscribedOrderUpdated', (orderEvent) => {
            debug(`[RT:HUB:EVENT] Received order updated`, orderEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'SubscribedOrderUpdated',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.orderUpdatedCallback)) {
                subscription.orderUpdatedCallback(orderEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'OrderUpdated',
                subjectId: subscription.orderId,
                data: orderEvent,
            });
        });
        connection.on('SubscribedOrderAdditionalInformation', (orderEvent) => {
            debug(`[RT:HUB:EVENT] Received order additional information`, orderEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'SubscribedOrderAdditionalInformation',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.subscribedOrderAdditionalInformationCallback)) {
                subscription.subscribedOrderAdditionalInformationCallback(orderEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'SubscribedOrderAdditionalInformation',
                subjectId: subscription.orderId,
                data: orderEvent,
            });
        });
        connection.on('SessionChanged', (sessionEvent) => {
            debug(`[RT:HUB:EVENT] Received session changed`, sessionEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'SessionChanged',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.sessionChangedCallback)) {
                subscription.sessionChangedCallback(sessionEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'SessionChanged',
                subjectId: subscription.orderId,
                data: sessionEvent,
            });
        });
        connection.on('SubscribedToOrderID', (joinEvent) => {
            debug(`[RT:HUB:EVENT] Received subscribed to order event`, joinEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'SubscribedToOrderID',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.joinCallback)) {
                subscription.joinCallback(joinEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'JoinHub',
                subjectId: subscription.orderId,
                data: joinEvent,
            });
        });
        connection.on('UnsubscribedFromOrderID', (leaveEvent) => {
            debug(`[RT:HUB:EVENT] Received unsubscribed order id event`, leaveEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'UnsubscribeFromOrderID',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.leaveCallback)) {
                subscription.leaveCallback(leaveEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'LeaveHub',
                subjectId: subscription.orderId,
                data: leaveEvent,
            });
        });
        // Add the hub connection
        //
        const hubConnection = {
            connection,
            hub: hubDetails,
            uri: hubUri,
            subjectId: subscription.orderId,
            token: subscription.token,
            reconnect_count: 0,
            subscribe: async () => {
                const subResponse = await connection.invoke('SubscribeToOrderID', { OrderID: subscription.orderId, SubscriberName: subscription.subscriberName });
                debug(`[RT:HUB:EVENT] Subscribed to order events`, subResponse);
                this.activity$.next({
                    hubName,
                    type: 'send',
                    payload: 'SubscribeToOrderID',
                    ts: new Date(),
                });
            }
        };
        this.connection[hubName].push(hubConnection);
        // Subscribe to order events
        //
        if (hubConnection.subscribe) {
            try {
                await hubConnection.subscribe();
            }
            catch (error) {
                warn(`[RT:HUB:EVENT] Failed to subscribe to order`, error);
            }
        }
        return hubConnection;
    }
    /**
     * Stop listening to order hub events for a specific order
     *
     * @param {IHubOrderJoinRequest} subscription The subscription details
     * @memberof RealtimeCommunicator
     */
    async leaveOrderHub(subscription) {
        const hubName = subscription.hubName || 'OrderHub';
        const hubConnections = filter(this.connection[hubName], { subjectId: subscription.orderId });
        debug(`[RT:HUB] Disconnecting from ${hubName}`);
        if (hubConnections.length) {
            for (const hubConnection of hubConnections) {
                // Unsubscribe from order
                //
                try {
                    await hubConnection.connection.invoke('UnsubscribeFromOrderID', { OrderID: subscription.orderId });
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Error unsubscribing from order events`, error);
                }
                this.activity$.next({
                    hubName,
                    type: 'send',
                    payload: 'UnsubscribeFromOrderID',
                    ts: new Date(),
                });
                // Stop listening to events
                //
                hubConnection.connection.off('SubscribedOrderUpdated');
                hubConnection.connection.off('SessionChanged');
                hubConnection.connection.off('SubscribedToOrderID');
                hubConnection.connection.off('UnsubscribedFromOrderID');
                try {
                    await hubConnection.connection.stop();
                }
                catch (error) {
                    warn(`[RT:HUB] Error disconnecting from ${hubName}/${hubConnection.subjectId}`, error);
                }
            }
            // Remove the connection(s)
            //
            remove$1(this.connection[hubName], { subjectId: subscription.orderId });
            return;
        }
        throw new Error(`Not connected to hub ${hubName}/${subscription.orderId}`);
    }
    //////////////////////////////////////////////////////////////////////////////
    // UserTask hub
    //////////////////////////////////////////////////////////////////////////////
    //
    /**
     * Start listening to user task hub events
     * You can either supply a callback(s) here or listen to the event$ observable
     *
     * @param {IHubUserTaskJoinRequest} subscription The subscription details
     * @returns
     * @memberof RealtimeCommunicator
     */
    async joinUserTaskHub(subscription) {
        const hubName = subscription.hubName || 'UserTaskHub';
        const subjectId = 'UserTaskUpdates';
        debug(`[RT:HUB] Connecting to ${hubName}`);
        // Create a new hub connection
        // Combine server endpoint with hub path
        //
        const hubDetails = find(this.hubs, { Name: hubName });
        if (!hubDetails) {
            throw new Error(`Unknown hub ${hubName}`);
        }
        const token = subscription.token;
        let hubUri = settings.communicatorEndPointURL + hubDetails.Path;
        const params = this.buildQueryParams(subscription);
        if (params.length) {
            hubUri += `?${params.join('&')}`;
        }
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUri, {
            accessTokenFactory: () => { console.log(`[RT:HUB] Setting token for hub ${hubName}`, token); return token; },
            skipNegotiation: true,
            transport: 1,
            headers: this.buildHeaders(subscription),
        })
            .configureLogging(settings.enableLogging ? 1 : 5) // 1 = debug, 5 = critical
            .withAutomaticReconnect()
            .build();
        // Connect to the hub
        //
        await connection.start();
        debug(`[RT:HUB] Connected ${hubName}`);
        // Setup event listener(s)
        //
        connection.onclose(() => {
            log$1(`[RT:HUB] Closed connection to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'close',
                payload: `${hubConnection.reconnect_count}`,
                ts: new Date(),
            });
            // If the connection closed after SignalR tries to reconnect we have
            // network issues. We should try to reconnect at a regular interval
            //
            if (hubConnection.reconnect_count > 0) {
                this.reconnectAttempt(hubConnection);
            }
        });
        connection.onreconnecting(() => {
            warn(`[RT:HUB] Reconnecting to ${hubName}...`);
            this.activity$.next({
                hubName,
                type: 'reconnecting',
                payload: undefined,
                ts: new Date(),
            });
            // Set the reconnecting state which will help if we can't recover
            //
            hubConnection.reconnect_count += 1;
        });
        connection.onreconnected(async () => {
            log$1(`[RT:HUB] Reconnected to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'reconnected',
                payload: undefined,
                ts: new Date(),
            });
            // Unset the reconnecting state because we appear to have recovered
            //
            hubConnection.reconnect_count = 0;
            // Resubscribe because the connection id might have changed
            // There are 2 levels of reconnect: signalr and our own fallback
            // The fallback is a completely new connection start
            // This is the signalR reconnect support
            //
            if (hubConnection.subscribe) {
                try {
                    await hubConnection.subscribe();
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Failed to resubscribe to events for hub ${hubName}`, error);
                }
            }
        });
        connection.on('UserTaskCreated', (userTaskCreatedEvent) => {
            debug(`[RT:HUB:EVENT] Received user task created`, userTaskCreatedEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'UserTaskCreated',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.userTaskCreatedCallback)) {
                subscription.userTaskCreatedCallback(userTaskCreatedEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                subjectId,
                hub: hubDetails.Name,
                method: 'UserTaskCreated',
                data: userTaskCreatedEvent,
            });
        });
        connection.on('UserTaskUpdated', (userTaskUpdatedEvent) => {
            debug(`[RT:HUB:EVENT] Received user task updated`, userTaskUpdatedEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'UserTaskUpdated',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.userTaskUpdatedCallback)) {
                subscription.userTaskUpdatedCallback(userTaskUpdatedEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                subjectId,
                hub: hubDetails.Name,
                method: 'UserTaskUpdated',
                data: userTaskUpdatedEvent,
            });
        });
        // Add the hub connection
        //
        const hubConnection = {
            connection,
            subjectId,
            hub: hubDetails,
            uri: hubUri,
            token: subscription.token,
            reconnect_count: 0,
        };
        this.connection[hubName].push(hubConnection);
        // Do the callback if provided
        //
        if (isFunction$1(subscription.joinCallback)) {
            subscription.joinCallback({ SubscriberName: subscription.subscriberName });
        }
        return hubConnection;
    }
    /**
     * Stop listening to user task hub events for a specific device
     *
     * @param {string} [hub='UserTaskHub'] The hub name
     * @memberof RealtimeCommunicator
     */
    async leaveUserTaskHub(subscription) {
        const hubName = subscription.hubName || 'UserTaskHub';
        const subjectId = 'UserTaskUpdates';
        debug(`[RT:HUB] Disconnecting from ${hubName}`);
        const hubConnections = filter(this.connection[hubName], { subjectId });
        if (hubConnections.length) {
            for (const hubConnection of hubConnections) {
                // Stop listening to events
                //
                hubConnection.connection.off('UserTaskCreated');
                hubConnection.connection.off('UserTaskUpdated');
                try {
                    await hubConnection.connection.stop();
                }
                catch (error) {
                    warn(`[RT:HUB] Error disconnecting from ${hubName}/${hubConnection.subjectId}`, error);
                }
            }
            // Remove the connection(s)
            //
            remove$1(this.connection[hubName], { subjectId });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.leaveCallback)) {
                subscription.leaveCallback({ SubscriberName: subscription.subscriberName });
            }
            return;
        }
        throw new Error(`Not connected to hub ${hubName}/${subjectId}`);
    }
    //////////////////////////////////////////////////////////////////////////////
    // CustomerFacingDisplay hub
    //////////////////////////////////////////////////////////////////////////////
    //
    /**
     * Start listening to CFD hub events
     * You can either supply a callback(s) here or listen to the event$ observable
     *
     * @param {IHubCustomerFacingDisplayJoinRequest} subscription The subscription details
     * @returns
     * @memberof RealtimeCommunicator
     */
    async joinCustomerFacingDisplayHub(subscription) {
        const hubName = subscription.hubName || 'CustomerFacingDisplayHub';
        const subjectId = subscription.deviceId;
        debug(`[RT:HUB] Connecting to ${hubName}`);
        // Create a new hub connection
        // Combine server endpoint with hub path
        //
        const hubDetails = find(this.hubs, { Name: hubName });
        if (!hubDetails) {
            throw new Error(`Unknown hub ${hubName}`);
        }
        const token = subscription.token;
        let hubUri = settings.communicatorEndPointURL + hubDetails.Path;
        const params = this.buildQueryParams(subscription);
        if (params.length) {
            hubUri += `?${params.join('&')}`;
        }
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUri, {
            accessTokenFactory: () => { console.log(`[RT:HUB] Setting token for hub ${hubName}`, token); return token; },
            skipNegotiation: true,
            transport: 1,
            headers: this.buildHeaders(subscription),
        })
            .configureLogging(settings.enableLogging ? 1 : 5) // 1 = debug, 5 = critical
            .withAutomaticReconnect()
            .build();
        // Connect to the hub
        //
        await connection.start();
        debug(`[RT:HUB] Connected ${hubName}`);
        // Setup event listener(s)
        //
        connection.onclose(() => {
            log$1(`[RT:HUB] Closed connection to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'close',
                payload: `${hubConnection.reconnect_count}`,
                ts: new Date(),
            });
            // If the connection closed after SignalR tries to reconnect we have
            // network issues. We should try to reconnect at a regular interval
            //
            if (hubConnection.reconnect_count > 0) {
                this.reconnectAttempt(hubConnection);
            }
        });
        connection.onreconnecting(() => {
            warn(`[RT:HUB] Reconnecting to ${hubName}...`);
            this.activity$.next({
                hubName,
                type: 'reconnecting',
                payload: undefined,
                ts: new Date(),
            });
            // Set the reconnecting state which will help if we can't recover
            //
            hubConnection.reconnect_count += 1;
        });
        connection.onreconnected(async () => {
            log$1(`[RT:HUB] Reconnected to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'reconnected',
                payload: undefined,
                ts: new Date(),
            });
            // Unset the reconnecting state because we appear to have recovered
            //
            hubConnection.reconnect_count = 0;
            // Resubscribe because the connection id might have changed
            // There are 2 levels of reconnect: signalr and our own fallback
            // The fallback is a completely new connection start
            // This is the signalR reconnect support
            //
            if (hubConnection.subscribe) {
                try {
                    await hubConnection.subscribe();
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Failed to resubscribe to events for hub ${hubName}`, error);
                }
            }
        });
        connection.on('DisplayOrderUpdated', (displayOrderUpdatedEvent) => {
            debug(`[RT:HUB:EVENT] Received display order updated`, displayOrderUpdatedEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'DisplayOrderUpdated',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.displayOrderUpdatedCallback)) {
                subscription.displayOrderUpdatedCallback(displayOrderUpdatedEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                subjectId,
                hub: hubDetails.Name,
                method: 'DisplayOrderUpdated',
                data: displayOrderUpdatedEvent,
            });
        });
        connection.on('FeedbackReceived', (feedbackReceivedEvent) => {
            debug(`[RT:HUB:EVENT] Received CFD feedback`, feedbackReceivedEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'FeedbackReceived',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.feedbackReceivedCallback)) {
                subscription.feedbackReceivedCallback(feedbackReceivedEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                subjectId,
                hub: hubDetails.Name,
                method: 'FeedbackReceived',
                data: feedbackReceivedEvent,
            });
        });
        connection.on('SubscribedToDisplay', (joinEvent) => {
            debug(`[RT:HUB:EVENT] Received CFD subscribed event`, joinEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'SubscribedToDisplay',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.joinCallback)) {
                subscription.joinCallback(joinEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'JoinHub',
                subjectId: subscription.deviceId,
                data: joinEvent,
            });
        });
        connection.on('UnsubscribedFromDisplay', (leaveEvent) => {
            debug(`[RT:HUB:EVENT] Received CFD unsubscribed event`, leaveEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'UnsubscribedFromDisplay',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.leaveCallback)) {
                subscription.leaveCallback(leaveEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'LeaveHub',
                subjectId: subscription.deviceId,
                data: leaveEvent,
            });
        });
        // Add the hub connection
        //
        const hubConnection = {
            connection,
            subjectId,
            hub: hubDetails,
            uri: hubUri,
            token: subscription.token,
            reconnect_count: 0,
            subscribe: async () => {
                const subResponse = await connection.invoke('SubscribeToDisplay', { DeviceID: subscription.deviceId, SubscriberName: subscription.subscriberName });
                debug(`[RT:HUB:EVENT] Subscribed to customer facing display events`, subResponse);
                this.activity$.next({
                    hubName,
                    type: 'send',
                    payload: 'SubscribeToDisplay',
                    ts: new Date(),
                });
            }
        };
        this.connection[hubName].push(hubConnection);
        // Subscribe to device events
        //
        if (hubConnection.subscribe) {
            try {
                await hubConnection.subscribe();
            }
            catch (error) {
                warn(`[RT:HUB:EVENT] Failed to subscribe to CFD`, error);
            }
        }
        return hubConnection;
    }
    /**
     * Stop listening to CFD hub events for a specific device
     *
     * @param {string} [hub='CustomerFacingDisplayHub'] The hub name
     * @memberof RealtimeCommunicator
     */
    async leaveCustomerFacingDisplayHub(subscription) {
        const hubName = subscription.hubName || 'CustomerFacingDisplayHub';
        const subjectId = subscription.deviceId;
        debug(`[RT:HUB] Disconnecting from ${hubName}`);
        const hubConnections = filter(this.connection[hubName], { subjectId });
        if (hubConnections.length) {
            for (const hubConnection of hubConnections) {
                // Unsubscribe from CFD
                //
                try {
                    await hubConnection.connection.invoke('UnsubscribeFromDisplay', { DeviceID: subscription.deviceId, SubscriberName: subscription.subscriberName });
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Error unsubscribing from CFD events`, error);
                }
                this.activity$.next({
                    hubName,
                    type: 'send',
                    payload: 'UnsubscribeFromDisplay',
                    ts: new Date(),
                });
                // Stop listening to events
                //
                hubConnection.connection.off('DisplayOrderUpdated');
                hubConnection.connection.off('FeedbackReceived');
                hubConnection.connection.off('SubscribedToDisplay');
                hubConnection.connection.off('UnsubscribedFromDisplay');
                try {
                    await hubConnection.connection.stop();
                }
                catch (error) {
                    warn(`[RT:HUB] Error disconnecting from ${hubName}/${hubConnection.subjectId}`, error);
                }
            }
            // Remove the connection(s)
            //
            remove$1(this.connection[hubName], { subjectId });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.leaveCallback)) {
                subscription.leaveCallback({ SubscriberName: subscription.subscriberName });
            }
            return;
        }
        throw new Error(`Not connected to hub ${hubName}/${subjectId}`);
    }
    /**
     * Push an update for the displayed order to the customer facing display
     *
     * @param {IHubPushDisplayOrderToDeviceID} updateDisplayOrderEvent
     * @param {string} [hubName='CustomerFacingDisplayHub']
     * @returns
     * @memberof RealtimeCommunicator
     */
    async pushDisplayOrderToDevice(updateDisplayOrderEvent, hubName = 'CustomerFacingDisplayHub') {
        const hubConnections = filter(this.connection[hubName], { subjectId: updateDisplayOrderEvent.DeviceID });
        if (hubConnections.length) {
            this.activity$.next({
                hubName,
                type: 'send',
                payload: 'PushOrder',
                ts: new Date(),
            });
            return await Promise.all(map$1(hubConnections, async (hubConnection) => {
                try {
                    debug(`[RT:HUB:EVENT] Start invoking CFD hub`, updateDisplayOrderEvent);
                    await hubConnection.connection.invoke('PushOrder', updateDisplayOrderEvent);
                    debug(`[RT:HUB:EVENT] Done invoking CFD hub`, updateDisplayOrderEvent);
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Error invoking CFD hub`, error);
                }
            }));
        }
        throw new Error(`Not connected to hub ${hubName}/${updateDisplayOrderEvent.DeviceID}`);
    }
    //////////////////////////////////////////////////////////////////////////////
    // Payment hub
    //////////////////////////////////////////////////////////////////////////////
    //
    /**
     * Start listening to updates for a payment transaction
     *
     * @param {IHubPaymentJoinRequest} subscription
     * @returns
     */
    async joinPaymentHub(subscription) {
        const hubName = subscription.hubName || 'PaymentHub';
        const subjectId = subscription.paymentTransactionId || subscription.orderId;
        debug(`[RT:HUB] Connecting to ${hubName}`);
        if (subjectId === undefined) {
            throw new Error('Must provide either an order or transaction id');
        }
        // Create a new hub connection
        // Combine server endpoint with hub path
        //
        const hubDetails = find(this.hubs, { Name: hubName });
        if (!hubDetails) {
            throw new Error(`Unknown hub ${hubName}`);
        }
        const token = subscription.token;
        let hubUri = settings.communicatorEndPointURL + hubDetails.Path;
        const params = this.buildQueryParams(subscription);
        if (params.length) {
            hubUri += `?${params.join('&')}`;
        }
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUri, {
            accessTokenFactory: () => { console.log(`[RT:HUB] Setting token for hub ${hubName}`, token); return token; },
            skipNegotiation: true,
            transport: 1,
            headers: this.buildHeaders(subscription),
        })
            .configureLogging(settings.enableLogging ? 1 : 5) // 1 = debug, 5 = critical
            .withAutomaticReconnect()
            .build();
        // Connect to the hub
        //
        await connection.start();
        debug(`[RT:HUB] Connected ${hubName}`);
        // Setup event listener(s)
        //
        connection.onclose(() => {
            log$1(`[RT:HUB] Closed connection to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'close',
                payload: `${hubConnection.reconnect_count}`,
                ts: new Date(),
            });
            // If the connection closed after SignalR tries to reconnect we have
            // network issues. We should try to reconnect at a regular interval
            //
            if (hubConnection.reconnect_count > 0) {
                this.reconnectAttempt(hubConnection);
            }
        });
        connection.onreconnecting(() => {
            warn(`[RT:HUB] Reconnecting to ${hubName}...`);
            this.activity$.next({
                hubName,
                type: 'reconnecting',
                payload: undefined,
                ts: new Date(),
            });
            // Set the reconnecting state which will help if we can't recover
            //
            hubConnection.reconnect_count += 1;
        });
        connection.onreconnected(async () => {
            log$1(`[RT:HUB] Reconnected to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'reconnected',
                payload: undefined,
                ts: new Date(),
            });
            // Unset the reconnecting state because we appear to have recovered
            //
            hubConnection.reconnect_count = 0;
            // Resubscribe because the connection id might have changed
            // There are 2 levels of reconnect: signalr and our own fallback
            // The fallback is a completely new connection start
            // This is the signalR reconnect support
            //
            if (hubConnection.subscribe) {
                try {
                    await hubConnection.subscribe();
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Failed to resubscribe to events for hub ${hubName}`, error);
                }
            }
        });
        connection.on('SubscribedPaymentTransactionUpdated', (paymentTransactionUpdatedEvent) => {
            debug(`[RT:HUB:EVENT] Received payment transaction updated`, paymentTransactionUpdatedEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'PaymentTransactionUpdated',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.paymentTransactionUpdatedCallback)) {
                subscription.paymentTransactionUpdatedCallback(paymentTransactionUpdatedEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                subjectId,
                hub: hubDetails.Name,
                method: 'PaymentTransactionUpdated',
                data: paymentTransactionUpdatedEvent,
            });
        });
        connection.on('SubscribedPaymentTransactionCreated', (paymentTransactionCreatedEvent) => {
            debug(`[RT:HUB:EVENT] Received payment transaction created`, paymentTransactionCreatedEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'PaymentTransactionCreated',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.paymentTransactionCreatedCallback)) {
                subscription.paymentTransactionCreatedCallback(paymentTransactionCreatedEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                subjectId,
                hub: hubDetails.Name,
                method: 'PaymentTransactionCreated',
                data: paymentTransactionCreatedEvent,
            });
        });
        // Add the hub connection
        //
        const hubConnection = {
            connection,
            subjectId,
            hub: hubDetails,
            uri: hubUri,
            token: subscription.token,
            reconnect_count: 0,
            subscribe: async () => {
                if (subscription.paymentTransactionId) {
                    const subResponse = await connection.invoke('SubscribeToPaymentTransaction', { PaymentTransactionID: subscription.paymentTransactionId, SubscriberName: subscription.subscriberName });
                    debug(`[RT:HUB:EVENT] Subscribed to payment transaction events`, subResponse);
                    this.activity$.next({
                        hubName,
                        type: 'send',
                        payload: 'SubscribeToPaymentTransaction',
                        ts: new Date(),
                    });
                }
                if (subscription.orderId) {
                    const subResponse = await connection.invoke('SubscribeToOrderPaymentTransaction', { OrderID: subscription.orderId, SubscriberName: subscription.subscriberName });
                    debug(`[RT:HUB:EVENT] Subscribed to order payment transaction events`, subResponse);
                    this.activity$.next({
                        hubName,
                        type: 'send',
                        payload: 'SubscribeToOrderPaymentTransaction',
                        ts: new Date(),
                    });
                }
            }
        };
        this.connection[hubName].push(hubConnection);
        // Subscribe to device events
        //
        if (hubConnection.subscribe) {
            try {
                await hubConnection.subscribe();
            }
            catch (error) {
                warn(`[RT:HUB:EVENT] Failed to subscribe to payment transaction`, error);
            }
        }
        return hubConnection;
    }
    /**
     * Stop listening to payment transaction update events
     *
     * @param {IHubPaymentJoinRequest} subscription
     * @returns
     */
    async leavePaymentHub(subscription) {
        const hubName = subscription.hubName || 'PaymentHub';
        const subjectId = subscription.paymentTransactionId;
        debug(`[RT:HUB] Disconnecting from ${hubName}`);
        const hubConnections = filter(this.connection[hubName], { subjectId });
        if (hubConnections.length) {
            for (const hubConnection of hubConnections) {
                // Unsubscribe from payment transaction
                //
                try {
                    await hubConnection.connection.invoke('UnsubscribeFromPaymentTransaction', { PaymentTransactionID: subscription.paymentTransactionId });
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Error unsubscribing from payment transaction events`, error);
                }
                // Stop listening to events
                //
                hubConnection.connection.off('SubscribedPaymentTransactionCreated');
                hubConnection.connection.off('SubscribedPaymentTransactionUpdated');
                hubConnection.connection.off('SubscribedToPaymentTransaction');
                hubConnection.connection.off('UnsubscribedFromPaymentTransaction');
                try {
                    await hubConnection.connection.stop();
                }
                catch (error) {
                    warn(`[RT:HUB] Error disconnecting from ${hubName}/${hubConnection.subjectId}`, error);
                }
            }
            // Remove the connection(s)
            //
            remove$1(this.connection[hubName], { subjectId });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.leaveCallback)) {
                subscription.leaveCallback({ SubscriberName: subscription.subscriberName });
            }
            return;
        }
        throw new Error(`Not connected to hub ${hubName}/${subjectId}`);
    }
    /**
     * Sends the status of this client to the others
     * Will be sent on request when a Ping is received and at an interval
     *
     * @private
     */
    sendClientStatus(params = {}) {
        if (this.clientApp) {
            if (this.debouncedStatusTimer) {
                clearTimeout(this.debouncedStatusTimer);
            }
            this.debouncedStatusTimer = setTimeout(() => {
                const clientStatus = {
                    Action: 'client:status',
                    Data: {
                        Channels: Object.keys(this.channels),
                        Peers: mapValues(this.peers, peer => peer.lastSeen),
                        Status: params.status || 'active',
                    },
                };
                const message = {
                    ChannelID: this.clientAppStatusChannel,
                    Message: clientStatus,
                    UUID: params.uuid,
                };
                return this.sendAppMessage(message);
            }, 750);
        }
    }
    /**
     * Removes all clients from the peers list that haven't been seen in a while
     */
    purgeStalePeers() {
        const staleClientUUIDs = [];
        const now = new Date().valueOf();
        forEach(this.peers, (peer, uuid) => {
            if (now - peer.lastSeen.valueOf() > this.staleThreshold) {
                staleClientUUIDs.push(uuid);
            }
            else if (now - peer.lastSeen.valueOf() > this.disconnectedThreshold) {
                peer.status = 'disconnected';
            }
            else if (now - peer.lastSeen.valueOf() > this.idleThreshold) {
                peer.status = 'idle';
            }
        });
        forEach(staleClientUUIDs, (uuid) => {
            delete this.peers[uuid];
        });
        this._peers$.next(Object.values(this.peers));
    }
    /**
     * Retrieves the names of the currently connected channels
     *
     * @return {*}  {string[]}
     */
    getChannels() {
        return Object.keys(this.channels);
    }
    /**
     * Retrieves the list of all other clients currently connected to the
     * client app hub.
     *
     * @return {*}  {IPeerClientStatus[]}
     */
    getPeers() {
        return [...Object.values(this.peers)];
    }
    /**
     * Retrieves the connected clients for a specific channel
     *
     * @param {string} channelName
     * @return {*} IChannelStatus
     */
    getChannelStatus(channelName) {
        if (!this.channels[channelName]) {
            return;
        }
        // Find all the clients in the channel
        //
        const appsInChannel = filter(this.peers, (peer) => {
            return peer.channels.indexOf(channelName) !== -1;
        });
        const now = new Date().valueOf();
        const channelStatus = {
            channelName,
            clients: appsInChannel.map((peer) => {
                return {
                    app: peer.clientApp,
                    lastSeen: peer.lastSeen,
                    status: now - peer.lastSeen.valueOf() > 60000 ? 'stale' : 'active',
                };
            }),
        };
        return channelStatus;
    }
    /**
     * Start listening to updates from the client app hub
     *
     * @param {IHubClientAppJoinRequest} subscription
     * @returns
     */
    async joinClientAppHub(subscription) {
        const hubName = subscription.hubName || 'ClientAppHub';
        const subjectId = subscription.app.UUID;
        this.clientApp = subscription.app;
        this.channels = {};
        debug(`[RT:HUB] Connecting to ${hubName}`);
        // Create a new hub connection
        // Combine server endpoint with hub path
        //
        const hubDetails = find(this.hubs, { Name: hubName });
        if (!hubDetails) {
            throw new Error(`Unknown hub ${hubName}`);
        }
        const token = subscription.token;
        let hubUri = settings.communicatorEndPointURL + hubDetails.Path;
        const params = this.buildQueryParams(subscription);
        if (params.length) {
            hubUri += `?${params.join('&')}`;
        }
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUri, {
            accessTokenFactory: () => { console.log(`[RT:HUB] Setting token for hub ${hubName}`, token); return token; },
            skipNegotiation: true,
            transport: 1,
            headers: this.buildHeaders(subscription),
        })
            .configureLogging(settings.enableLogging ? 1 : 5) // 1 = debug, 5 = critical
            .withAutomaticReconnect()
            .build();
        // Connect to the hub
        //
        await connection.start();
        debug(`[RT:HUB] Connected ${hubName}`);
        // Prepare the hub connection
        //
        const hubConnection = {
            connection,
            subjectId,
            hub: hubDetails,
            uri: hubUri,
            token: subscription.token,
            reconnect_count: 0,
            subscribe: async () => {
                // Set UserAgent from the service settings if not provided
                //
                if (!subscription.app.UserAgent) {
                    subscription.app.UserAgent = `${settings.appName}/${settings.appVersion}`;
                }
                const request = {
                    App: subscription.app,
                };
                // Register on the client app
                //
                const subResponse = await connection.invoke('Register', request);
                debug(`[RT:HUB:EVENT] Subscribed to client app hub for client ${subscription.app.UUID}`, subResponse);
                this.activity$.next({
                    hubName,
                    type: 'send',
                    payload: 'Register',
                    ts: new Date(),
                });
                // Always join the client app status channel
                // We cannot use the this.joinChannel method as it prevents
                // manipulation of the client status channel
                //
                const joinChannelRequest = {
                    ChannelID: this.clientAppStatusChannel,
                };
                await connection.invoke('JoinChannel', joinChannelRequest);
                await this.sendClientStatus({ status: 'connected' });
                // Re-join all the channels
                //
                if (hubConnection.channels && hubConnection.channels.length) {
                    for (const channel of hubConnection.channels) {
                        const joinRequest = {
                            App: subscription.app,
                            ChannelID: channel,
                        };
                        const joinResponse = await connection.invoke('JoinChannel', joinRequest);
                        debug(`[RT:HUB:EVENT] Joined channel ${channel} on client app hub for client ${subscription.app.UUID}`, joinResponse);
                    }
                }
            }
        };
        // Setup event listener(s)
        //
        connection.onclose(() => {
            log$1(`[RT:HUB] Closed connection to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'close',
                payload: `${hubConnection.reconnect_count}`,
                ts: new Date(),
            });
            // If the connection closed after SignalR tries to reconnect we have
            // network issues. We should try to reconnect at a regular interval
            //
            if (hubConnection.reconnect_count > 0) {
                this.reconnectAttempt(hubConnection);
            }
        });
        connection.onreconnecting(() => {
            warn(`[RT:HUB] Reconnecting to ${hubName}...`);
            this.activity$.next({
                hubName,
                type: 'reconnecting',
                payload: undefined,
                ts: new Date(),
            });
            // Set the reconnecting state which will help if we can't recover
            //
            hubConnection.reconnect_count += 1;
        });
        connection.onreconnected(async () => {
            log$1(`[RT:HUB] Reconnected to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'reconnected',
                payload: undefined,
                ts: new Date(),
            });
            // Unset the reconnecting state because we appear to have recovered
            //
            hubConnection.reconnect_count = 0;
            // Resubscribe because the connection id might have changed
            // There are 2 levels of reconnect: signalr and our own fallback
            // The fallback is a completely new connection start
            // This is the signalR reconnect support
            //
            if (hubConnection.subscribe) {
                try {
                    await hubConnection.subscribe();
                    debug(`[RT:HUB:EVENT] (re)subscribe to events for hub ${hubName}`);
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Failed to resubscribe to events for hub ${hubName}`, error);
                }
            }
        });
        connection.on('Registered', (appRegistration) => {
            debug(`[RT:HUB:EVENT] Received client registered message`, appRegistration);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'Registered',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.registeredCallback)) {
                subscription.registeredCallback(appRegistration);
            }
            // Emit event on the observable
            //
            this.event$.next({
                subjectId,
                hub: hubDetails.Name,
                method: 'Registered',
                data: appRegistration,
            });
        });
        connection.on('Unregistered', (appRegistration) => {
            debug(`[RT:HUB:EVENT] Received client unregistered message`, appRegistration);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'Unregistered',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.unregisteredCallback)) {
                subscription.unregisteredCallback(appRegistration);
            }
            // Emit event on the observable
            //
            this.event$.next({
                subjectId,
                hub: hubDetails.Name,
                method: 'Unregistered',
                data: appRegistration,
            });
        });
        connection.on('JoinedChannel', (event) => {
            var _a;
            debug(`[RT:HUB:EVENT] Received client joined channel message`, event);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'JoinedChannel',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.joinChannelCallback)) {
                subscription.joinChannelCallback(event);
            }
            // Emit event on the observable
            //
            this.event$.next({
                subjectId,
                hub: hubDetails.Name,
                method: 'JoinedChannel',
                data: event,
            });
            // Also whenever a new client joins a channel we will send our status directly
            // to that client
            //
            const peerUUID = (_a = event.App) === null || _a === void 0 ? void 0 : _a.UUID;
            if (peerUUID) {
                this.sendClientStatus({ uuid: peerUUID });
                if (this.peers[peerUUID]) {
                    this.peers[peerUUID].lastSeen = new Date();
                    this.peers[peerUUID].channels.push(event.ChannelID);
                }
                else {
                    this.peers[peerUUID] = {
                        channels: [event.ChannelID],
                        clientApp: event.App,
                        lastSeen: new Date(),
                        status: 'active',
                    };
                }
            }
        });
        connection.on('LeftChannel', (event) => {
            var _a;
            debug(`[RT:HUB:EVENT] Received client left channel message`, event);
            const peerUUID = (_a = event.App) === null || _a === void 0 ? void 0 : _a.UUID;
            if (peerUUID) {
                if (this.peers[peerUUID]) {
                    this.peers[peerUUID].lastSeen = new Date();
                    this.peers[peerUUID].channels =
                        this.peers[peerUUID].channels.filter((channelName) => { return channelName !== event.ChannelID; });
                    if (event.Disconnected) {
                        this.peers[peerUUID].status = 'disconnected';
                    }
                }
            }
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'LeftChannel',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.leaveChannelCallback)) {
                subscription.leaveChannelCallback(event);
            }
            // Emit event on the observable
            //
            this.event$.next({
                subjectId,
                hub: hubDetails.Name,
                method: 'LeftChannel',
                data: event,
            });
        });
        connection.on('ReceivedMessage', (appMessage) => {
            var _a, _b, _c;
            debug(`[RT:HUB:EVENT] Received client app message`, appMessage);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'ReceiveMessage',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.messageReceivedCallback)) {
                subscription.messageReceivedCallback(appMessage);
            }
            // Emit event on the observable
            //
            this.event$.next({
                subjectId,
                hub: hubDetails.Name,
                method: 'ReceiveMessage',
                data: appMessage,
            });
            // Always update last update for a client when a message is received
            // After join/leave channels clients should always send a client:status
            // so listening for messages should be enough
            //
            const peerUUID = (_a = appMessage.App) === null || _a === void 0 ? void 0 : _a.UUID;
            if (peerUUID && this.peers[peerUUID]) {
                this.peers[peerUUID].lastSeen = new Date();
            }
            if (this.clientApp && appMessage.ChannelID === this.clientAppStatusChannel) {
                switch (appMessage.Message.Action) {
                    case 'client:ping':
                        {
                            const clientPing = appMessage.Message;
                            if (clientPing.Data.UUID === ((_b = this.clientApp) === null || _b === void 0 ? void 0 : _b.UUID)) {
                                this.sendClientStatus({ uuid: (_c = appMessage.App) === null || _c === void 0 ? void 0 : _c.UUID });
                            }
                        }
                        break;
                    case 'client:status':
                        {
                            const clientStatus = appMessage.Message;
                            this.peers[peerUUID] = {
                                channels: clientStatus.Data.Channels,
                                clientApp: appMessage.App,
                                lastSeen: new Date(),
                                status: clientStatus.Data.Status,
                            };
                        }
                        break;
                }
            }
        });
        // Add the hub connection
        //
        this.connection[hubName].push(hubConnection);
        // Re-register application and join previous channels
        //
        if (hubConnection.subscribe) {
            try {
                await hubConnection.subscribe();
            }
            catch (error) {
                warn(`[RT:HUB:EVENT] Failed to subscribe to client app events`, error);
            }
        }
        // Setup our heartbeat activity timer
        //
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
        }
        log$1('[RT:HUB] Starting ClientApp status heartbeat timer');
        this.heartbeatTimer = setInterval(() => {
            this.purgeStalePeers();
            const now = new Date();
            if (now.valueOf() - this.lastSentClientStatusAt.valueOf() > this.clientStatusInterval) {
                this.sendClientStatus();
                this.lastSentClientStatusAt = new Date();
            }
        }, this.heartbeatInterval);
        return hubConnection;
    }
    /**
     * Stop listening to client app update events
     *
     * @param {IHubPaymentJoinRequest} subscription
     * @returns
     */
    async leaveClientAppHub(subscription) {
        const hubName = subscription.hubName || 'ClientAppHub';
        const subjectId = subscription.app.UUID;
        debug(`[RT:HUB] Disconnecting from ${hubName}`);
        const hubConnections = filter(this.connection[hubName], { subjectId });
        // First wait for any pending reconnects to finish
        // or we might have a race condition on our hands
        //
        try {
            await this.pendingReconnectAttempt[hubName];
        }
        catch (error) {
            // NOOP
        }
        if (hubConnections.length) {
            for (const hubConnection of hubConnections) {
                // Unregister client app
                //
                try {
                    // Send last status update to indicate we are disconnecting
                    //
                    await hubConnection.connection.invoke('Unregister', { App: subscription.app });
                    await this.sendClientStatus({ status: 'disconnected' });
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Error unsubscribing from client app`, error);
                }
                // Stop listening to events
                //
                hubConnection.connection.off('Registered');
                hubConnection.connection.off('Unregistered');
                hubConnection.connection.off('JoinedChannel');
                hubConnection.connection.off('LeftChannel');
                hubConnection.connection.off('ReceivedMessage');
                try {
                    await hubConnection.connection.stop();
                }
                catch (error) {
                    warn(`[RT:HUB] Error disconnecting from ${hubName}/${hubConnection.subjectId}`, error);
                }
            }
            // Remove the connection(s)
            //
            remove$1(this.connection[hubName], { subjectId });
            return;
        }
        // Cleanup timer and channels
        //
        this.channels = {};
        if (this.heartbeatTimer) {
            log$1('[RT:HUB] Stopping ClientApp status heartbeat timer');
            clearInterval(this.heartbeatTimer);
        }
        throw new Error(`Not connected to hub ${hubName}/${subjectId}`);
    }
    /**
     * Join a channel on a client app hub connection
     *
     * @param {IHubEventJoinedChannelMessage} joinRequest
     * @param {string} [hubName='ClientAppHub']
     * @returns
     */
    async joinChannel(joinRequest, hubName = 'ClientAppHub') {
        // Do not allow client manipulation of the client app status channel
        //
        if (joinRequest.ChannelID === this.clientAppStatusChannel) {
            return;
        }
        const hubConnections = filter(this.connection[hubName], { subjectId: joinRequest.App.UUID });
        if (hubConnections.length) {
            this.activity$.next({
                hubName,
                type: 'send',
                payload: 'JoinChannel',
                ts: new Date(),
            });
            this.channels[joinRequest.ChannelID] = new Date().valueOf();
            return await Promise.all(map$1(hubConnections, async (hubConnection) => {
                try {
                    await hubConnection.connection.invoke('JoinChannel', joinRequest);
                    debug(`[RT:HUB:EVENT] Joined channel on client app hub`, hubConnection.channels);
                    if (!hubConnection.channels) {
                        hubConnection.channels = [];
                    }
                    hubConnection.channels.push(joinRequest.ChannelID);
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Error joining channel on client app hub`, error);
                }
            }));
        }
        throw new Error(`Not connected to hub ${hubName}/${joinRequest.App.UUID}`);
    }
    /**
     * Leave a channel on a client app hub connection
     *
     * @param {IHubEventJoinedChannelMessage} joinRequest
     * @param {string} [hubName='ClientAppHub']
     * @returns
     */
    async leaveChannel(leaveRequest, hubName = 'ClientAppHub') {
        // Do not allow client manipulation of the client app status channel
        //
        if (leaveRequest.ChannelID === this.clientAppStatusChannel) {
            return;
        }
        const hubConnections = filter(this.connection[hubName], { subjectId: leaveRequest.App.UUID });
        if (hubConnections.length) {
            this.activity$.next({
                hubName,
                type: 'send',
                payload: 'JoinChannel',
                ts: new Date(),
            });
            delete this.channels[leaveRequest.ChannelID];
            return await Promise.all(map$1(hubConnections, async (hubConnection) => {
                try {
                    await hubConnection.connection.invoke('LeaveChannel', leaveRequest);
                    hubConnection.channels = without(hubConnection.channels, leaveRequest.ChannelID);
                    debug(`[RT:HUB:EVENT] Left channel on client app hub`, hubConnection.channels);
                    await this.sendClientStatus();
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Error leaving channel on client app hub`, error);
                }
            }));
        }
        throw new Error(`Not connected to hub ${hubName}/${leaveRequest.App.UUID}`);
    }
    /**
     * Push a message to all apps registered on the same hub
     *
     * @param {IClientAppMessage} appMessage
     * @param {string} [hubName='CustomerFacingDisplayHub']
     * @returns
     * @memberof RealtimeCommunicator
     */
    async sendAppMessage(appMessage, hubName = 'ClientAppHub') {
        // Find the connections on the right channel
        //
        const hubConnections = filter(this.connection[hubName], connection => connection.channels && (connection.channels.indexOf(appMessage.ChannelID) !== -1 || appMessage.ChannelID === this.clientAppStatusChannel));
        if (hubConnections.length) {
            this.activity$.next({
                hubName,
                type: 'send',
                payload: 'SendMessage',
                ts: new Date(),
            });
            return await Promise.all(map$1(hubConnections, async (hubConnection) => {
                try {
                    if (hubConnection.connection && hubConnection.connection.state === 'Connected') {
                        await hubConnection.connection.invoke('SendMessage', appMessage);
                    }
                    else {
                        debug('Not sending message because signalr is not connected', hubConnection.connection);
                    }
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Error sending message to client app hub`, error, appMessage);
                }
            }));
        }
        throw new Error(`Not connected to hub/channel ${hubName}/${appMessage.ChannelID}`);
    }
    /**
     * Manually triggers a ping to see which other clients are currently connected to the hub
     *
     * @returns
     */
    ping(uuid) {
        if (!this.clientApp) {
            throw new Error('Client application has not been registered');
        }
        const request = {
            Action: 'client:ping',
            Data: {
                UUID: uuid,
            },
        };
        const message = {
            ChannelID: this.clientAppStatusChannel,
            Message: request,
        };
        return this.sendAppMessage(message);
    }
    /**
     * Helper method to try to reconnect to the SignalR server
     * We will use the reconnect count and multiply it with a small interval
     * which will effectively increment it for each attempt
     * Max delay is set to 10 minutes
     *
     * @private
     * @param {IHubConnection} hubDetails
     */
    async reconnectAttempt(hubDetails) {
        const hubName = hubDetails.hub.Name;
        if (this.pendingReconnectAttempt[hubName]) {
            return this.pendingReconnectAttempt[hubName];
        }
        // Call ValidateToken to ensure it is still valid
        //
        if (hubDetails.token) {
            try {
                const [action] = validateToken.createFetchAction();
                await validateToken.fetchData(action, 3000, hubDetails.token);
            }
            catch (error) {
                if (error.status === 401) {
                    warn(`[SignalR:HUB] Token has expired for hub ${hubName}. Leaving connection closed.`);
                    hubDetails.reconnect_count = 0;
                    if (this.reconnectTimer) {
                        clearTimeout(this.reconnectTimer);
                    }
                    return;
                }
            }
        }
        hubDetails.reconnect_count += 1;
        const delayInSeconds = hubDetails.reconnect_count * 5;
        log$1(`[RT:HUB] Setting reconnect timer for hub ${hubName} in ${delayInSeconds} second(s). Attempt: ${hubDetails.reconnect_count}`);
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
        }
        this.reconnectTimer = setTimeout(async () => {
            try {
                this.pendingReconnectAttempt[hubName] = hubDetails.connection.start();
                await this.pendingReconnectAttempt[hubName];
                delete this.pendingReconnectAttempt[hubName];
                log$1(`[RT:HUB] Successfully (re)connected to hub ${hubName}`);
                this.activity$.next({
                    hubName,
                    type: 'reconnected',
                    payload: `attempts: ${hubDetails.reconnect_count}`,
                    ts: new Date(),
                });
                hubDetails.reconnect_count = 0;
                if (hubDetails.subscribe) {
                    try {
                        await hubDetails.subscribe();
                    }
                    catch (error) {
                        warn(`[RT:HUB] Failed to resubscribe to hub ${hubName}`, error);
                    }
                }
            }
            catch (error) {
                warn(`[RT:HUB] Failed to (re)start connection for hub ${hubName}`, error);
                delete this.pendingReconnectAttempt[hubName];
                this.activity$.next({
                    hubName,
                    type: 'reconnecting',
                    payload: `attempt: ${hubDetails.reconnect_count}`,
                    ts: new Date(),
                });
                this.reconnectAttempt(hubDetails);
            }
        }, Math.min(600000, delayInSeconds * 1000));
    }
    /**
     * Force an immediate reconnection attempt. Can be useful for clients coming
     * out of a sleep or apps being resumed. ClientApp hub is usually the one
     * you want to immediately reconnect to
     */
    async reconnect(hubName = 'ClientAppHub') {
        const hubConnections = filter(this.connection[hubName]);
        if (this.clientApp && hubConnections.length) {
            return await Promise.all(map$1(hubConnections, async (hubConnection) => {
                try {
                    hubConnection.reconnect_count = 0;
                    await this.reconnectAttempt(hubConnection);
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Error reconnecting to hub`, error);
                }
            }));
        }
    }
    //////////////////////////////////////////////////////////////////////////////
    // Global Blue hub
    //////////////////////////////////////////////////////////////////////////////
    //
    /**
     * Start listening to Global Blue hub events for a group identifier
     * You can either supply a callback(s) here or listen to the event$ observable
     *
     * @param {IHubGlobalBlueJoinRequest} subscription The subscription details
     * @returns
     * @memberof RealtimeCommunicator
     */
    async joinGlobalBlueHub(subscription) {
        const hubName = subscription.hubName || 'GlobalBlueHub';
        debug(`[RT:HUB] Connecting to ${hubName}`);
        // Check we have a group ID and apply it to the options if set
        //
        if (!subscription.groupId) {
            throw new Error('Missing Global Blue group identifier');
        }
        subscription.options = Object.assign(Object.assign({}, subscription.options), { globalBlueGroupID: subscription.groupId });
        // Create a new hub connection
        // Combine server endpoint with hub path
        //
        const hubDetails = find(this.hubs, { Name: hubName });
        if (!hubDetails) {
            throw new Error(`Unknown hub ${hubName}`);
        }
        const token = subscription.token;
        let hubUri = settings.communicatorEndPointURL + hubDetails.Path;
        const params = this.buildQueryParams(subscription);
        if (params.length) {
            hubUri += `?${params.join('&')}`;
        }
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUri, {
            accessTokenFactory: () => { console.log(`[RT:HUB] Setting token for hub ${hubName}`, token); return token; },
            skipNegotiation: true,
            transport: 1,
            headers: this.buildHeaders(subscription),
        })
            .configureLogging(settings.enableLogging ? 1 : 5) // 1 = debug, 5 = critical
            .withAutomaticReconnect()
            .build();
        // Connect to the hub
        //
        await connection.start();
        debug(`[RT:HUB] Connected ${hubName}`);
        // Setup event listener(s)
        //
        connection.onclose(() => {
            log$1(`[RT:HUB] Closed connection to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'close',
                payload: `${hubConnection.reconnect_count}`,
                ts: new Date(),
            });
            // If the connection closed after SignalR tries to reconnect we have
            // network issues. We should try to reconnect at a regular interval
            //
            if (hubConnection.reconnect_count > 0) {
                this.reconnectAttempt(hubConnection);
            }
        });
        connection.onreconnecting(() => {
            warn(`[RT:HUB] Reconnecting to ${hubName}...`);
            this.activity$.next({
                hubName,
                type: 'reconnecting',
                payload: undefined,
                ts: new Date(),
            });
            // Set the reconnecting state which will help if we can't recover
            //
            hubConnection.reconnect_count += 1;
        });
        connection.onreconnected(async () => {
            log$1(`[RT:HUB] Reconnected to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'reconnected',
                payload: undefined,
                ts: new Date(),
            });
            // Unset the reconnecting state because we appear to have recovered
            //
            hubConnection.reconnect_count = 0;
            // Resubscribe because the connection id might have changed
            // There are 2 levels of reconnect: signalr and our own fallback
            // The fallback is a completely new connection start
            // This is the signalR reconnect support
            //
            if (hubConnection.subscribe) {
                try {
                    await hubConnection.subscribe();
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Failed to resubscribe to events for hub ${hubName}`, error);
                }
            }
        });
        connection.on('ShowForm', (globalBlueEvent) => {
            debug(`[RT:HUB:EVENT] Received Global Blue showForm`, globalBlueEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'GlobalBlue:showForm',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.showFormCallback)) {
                subscription.showFormCallback(globalBlueEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'GlobalBlueEvent:showForm',
                subjectId: subscription.groupId,
                data: globalBlueEvent,
            });
        });
        connection.on('HideForm', (globalBlueEvent) => {
            debug(`[RT:HUB:EVENT] Received Global Blue hideForm`, globalBlueEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'GlobalBlue:hideForm',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.hideFormCallback)) {
                subscription.hideFormCallback(globalBlueEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'GlobalBlueEvent:hideForm',
                subjectId: subscription.groupId,
                data: globalBlueEvent,
            });
        });
        connection.on('IssueSuccessful', (globalBlueEvent) => {
            debug(`[RT:HUB:EVENT] Received Global Blue issueSuccessful`, globalBlueEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'GlobalBlue:issueSuccessful',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.issueSuccessfulCallback)) {
                subscription.issueSuccessfulCallback(globalBlueEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'GlobalBlueEvent:issueSuccessful',
                subjectId: subscription.groupId,
                data: globalBlueEvent,
            });
        });
        connection.on('IssueFailed', (globalBlueEvent) => {
            debug(`[RT:HUB:EVENT] Received Global Blue issueFailed`, globalBlueEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'GlobalBlue:issueFailed',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.issueFailedCallback)) {
                subscription.issueFailedCallback(globalBlueEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'GlobalBlueEvent:issueFailed',
                subjectId: subscription.groupId,
                data: globalBlueEvent,
            });
        });
        // Add the hub connection
        //
        const hubConnection = {
            connection,
            hub: hubDetails,
            uri: hubUri,
            subjectId: subscription.groupId,
            token: subscription.token,
            reconnect_count: 0,
        };
        this.connection[hubName].push(hubConnection);
        return hubConnection;
    }
    /**
     * Stop listening to Global Blue hub events
     *
     * @param {IHubGlobalBlueJoinRequest} subscription The original join request which we need for the group id
     * @returns
     */
    async leaveGlobalBlueHub(subscription) {
        const hubName = subscription.hubName || 'GlobalBlueHub';
        debug(`[RT:HUB] Disconnecting from ${hubName}`);
        const hubConnections = filter(this.connection[hubName], { subjectId: subscription.groupId });
        if (hubConnections.length) {
            for (const hubConnection of hubConnections) {
                // Stop listening to events
                //
                hubConnection.connection.off('showForm');
                hubConnection.connection.off('hideForm');
                hubConnection.connection.off('issueSuccessful');
                hubConnection.connection.off('issueFailed');
                try {
                    await hubConnection.connection.stop();
                }
                catch (error) {
                    warn(`[RT:HUB] Error disconnecting from ${hubName}/${hubConnection.subjectId}`, error);
                }
            }
            // Remove the connection(s)
            //
            remove$1(this.connection[hubName], { subjectId: subscription.groupId });
            return;
        }
        throw new Error(`Not connected to hub ${hubName}/${subscription.groupId}`);
    }
    //////////////////////////////////////////////////////////////////////////////
    // Magic Link hub
    //////////////////////////////////////////////////////////////////////////////
    //
    /**
     * Start listening to magic link hub events for a specific RequestToken
     * You can either supply a callback(s) here or listen to the event$ observable
     *
     * @param {IHubMagicLinkJoinRequest} subscription The subscription details
     * @returns
     * @memberof RealtimeCommunicator
     */
    async joinMagicLinkHub(subscription) {
        const hubName = subscription.hubName || 'MagicLinkHub';
        debug(`[RT:HUB] Connecting to ${hubName}`);
        // Create a new hub connection
        // Combine server endpoint with hub path
        //
        const hubDetails = find(this.hubs, { Name: hubName });
        if (!hubDetails) {
            throw new Error(`Unknown hub ${hubName}`);
        }
        const token = subscription.token;
        let hubUri = settings.communicatorEndPointURL + hubDetails.Path;
        const params = this.buildQueryParams(subscription);
        if (params.length) {
            hubUri += `?${params.join('&')}`;
        }
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUri, {
            accessTokenFactory: () => { console.log(`[RT:HUB] Setting token for hub ${hubName}`, token); return token; },
            skipNegotiation: true,
            transport: 1,
            headers: this.buildHeaders(subscription),
        })
            .configureLogging(settings.enableLogging ? 1 : 5) // 1 = debug, 5 = critical
            .withAutomaticReconnect()
            .build();
        // Connect to the hub
        //
        await connection.start();
        debug(`[RT:HUB] Connected ${hubName}`);
        // Setup event listener(s)
        //
        connection.onclose(() => {
            log$1(`[RT:HUB] Closed connection to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'close',
                payload: `${hubConnection.reconnect_count}`,
                ts: new Date(),
            });
            // If the connection closed after SignalR tries to reconnect we have
            // network issues. We should try to reconnect at a regular interval
            //
            if (hubConnection.reconnect_count > 0) {
                this.reconnectAttempt(hubConnection);
            }
        });
        connection.onreconnecting(() => {
            warn(`[RT:HUB] Reconnecting to ${hubName}...`);
            this.activity$.next({
                hubName,
                type: 'reconnecting',
                payload: undefined,
                ts: new Date(),
            });
            // Set the reconnecting state which will help if we can't recover
            //
            hubConnection.reconnect_count += 1;
        });
        connection.onreconnected(async () => {
            log$1(`[RT:HUB] Reconnected to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'reconnected',
                payload: undefined,
                ts: new Date(),
            });
            // Unset the reconnecting state because we appear to have recovered
            //
            hubConnection.reconnect_count = 0;
            // Resubscribe because the connection id might have changed
            // There are 2 levels of reconnect: signalr and our own fallback
            // The fallback is a completely new connection start
            // This is the signalR reconnect support
            //
            if (hubConnection.subscribe) {
                try {
                    await hubConnection.subscribe();
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Failed to resubscribe to events for hub ${hubName}`, error);
                }
            }
        });
        connection.on('Confirmed', (confirmedEvent) => {
            debug(`[RT:HUB:EVENT] Received confirmed magic link`, confirmedEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'MagicLink:Confirmed',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.confirmedCallback)) {
                subscription.confirmedCallback(confirmedEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'MagicLink:Confirmed',
                subjectId: subscription.requestToken,
                data: confirmedEvent,
            });
        });
        // Add the hub connection
        //
        const hubConnection = {
            connection,
            hub: hubDetails,
            uri: hubUri,
            subjectId: subscription.requestToken,
            token: subscription.token,
            reconnect_count: 0,
            subscribe: async () => {
                const subResponse = await connection.invoke('Subscribe', { RequestToken: subscription.requestToken });
                debug(`[RT:HUB:EVENT] Subscribed to magic link events`, subResponse);
                this.activity$.next({
                    hubName,
                    type: 'send',
                    payload: 'MagicLink:Subscribe',
                    ts: new Date(),
                });
            }
        };
        this.connection[hubName].push(hubConnection);
        // Subscribe to order events
        //
        if (hubConnection.subscribe) {
            try {
                await hubConnection.subscribe();
            }
            catch (error) {
                warn(`[RT:HUB:EVENT] Failed to subscribe to magic link`, error);
            }
        }
        return hubConnection;
    }
    /**
     * Stop listening to magic link hub events
     *
     * @param {IHubMagicLinkJoinRequest} subscription The subscription details
     * @memberof RealtimeCommunicator
     */
    async leaveMagicLinkHub(subscription) {
        const hubName = subscription.hubName || 'MagicLinkHub';
        const hubConnections = filter(this.connection[hubName], { subjectId: subscription.requestToken });
        debug(`[RT:HUB] Disconnecting from ${hubName}`);
        if (hubConnections.length) {
            for (const hubConnection of hubConnections) {
                // Stop listening to events
                //
                hubConnection.connection.off('Confirmed');
                try {
                    await hubConnection.connection.stop();
                }
                catch (error) {
                    warn(`[RT:HUB] Error disconnecting from ${hubName}/${hubConnection.subjectId}`, error);
                }
            }
            // Remove the connection(s)
            //
            remove$1(this.connection[hubName], { subjectId: subscription.requestToken });
            return;
        }
        throw new Error(`Not connected to hub ${hubName}/${subscription.requestToken}`);
    }
    //////////////////////////////////////////////////////////////////////////////
    // Elevation Barcode hub
    //////////////////////////////////////////////////////////////////////////////
    //
    /**
     * Start listening to elevation barcode hub events for a specific RequestToken
     * You can either supply a callback(s) here or listen to the event$ observable
     *
     * @param {IHubElevationBarcodeJoinRequest} subscription The subscription details
     * @returns
     * @memberof RealtimeCommunicator
     */
    async joinElevationBarcodeHub(subscription) {
        const hubName = subscription.hubName || 'ElevationBarcodeHub';
        debug(`[RT:HUB] Connecting to ${hubName}`);
        // Create a new hub connection
        // Combine server endpoint with hub path
        //
        const hubDetails = find(this.hubs, { Name: hubName });
        if (!hubDetails) {
            throw new Error(`Unknown hub ${hubName}`);
        }
        const token = subscription.token;
        let hubUri = settings.communicatorEndPointURL + hubDetails.Path;
        const params = this.buildQueryParams(subscription);
        if (params.length) {
            hubUri += `?${params.join('&')}`;
        }
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUri, {
            accessTokenFactory: () => { console.log(`[RT:HUB] Setting token for hub ${hubName}`, token); return token; },
            skipNegotiation: true,
            transport: 1,
            headers: this.buildHeaders(subscription),
        })
            .configureLogging(settings.enableLogging ? 1 : 5) // 1 = debug, 5 = critical
            .withAutomaticReconnect()
            .build();
        // Connect to the hub
        //
        await connection.start();
        debug(`[RT:HUB] Connected ${hubName}`);
        // Setup event listener(s)
        //
        connection.onclose(() => {
            log$1(`[RT:HUB] Closed connection to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'close',
                payload: `${hubConnection.reconnect_count}`,
                ts: new Date(),
            });
            // If the connection closed after SignalR tries to reconnect we have
            // network issues. We should try to reconnect at a regular interval
            //
            if (hubConnection.reconnect_count > 0) {
                this.reconnectAttempt(hubConnection);
            }
        });
        connection.onreconnecting(() => {
            warn(`[RT:HUB] Reconnecting to ${hubName}...`);
            this.activity$.next({
                hubName,
                type: 'reconnecting',
                payload: undefined,
                ts: new Date(),
            });
            // Set the reconnecting state which will help if we can't recover
            //
            hubConnection.reconnect_count += 1;
        });
        connection.onreconnected(async () => {
            log$1(`[RT:HUB] Reconnected to ${hubName}`);
            this.activity$.next({
                hubName,
                type: 'reconnected',
                payload: undefined,
                ts: new Date(),
            });
            // Unset the reconnecting state because we appear to have recovered
            //
            hubConnection.reconnect_count = 0;
            // Resubscribe because the connection id might have changed
            // There are 2 levels of reconnect: signalr and our own fallback
            // The fallback is a completely new connection start
            // This is the signalR reconnect support
            //
            if (hubConnection.subscribe) {
                try {
                    await hubConnection.subscribe();
                }
                catch (error) {
                    warn(`[RT:HUB:EVENT] Failed to resubscribe to events for hub ${hubName}`, error);
                }
            }
        });
        connection.on('Confirmed', (confirmedEvent) => {
            debug(`[RT:HUB:EVENT] Received confirmed elevation barcode`, confirmedEvent);
            this.activity$.next({
                hubName,
                type: 'receive',
                payload: 'ElevationBarcode:Confirmed',
                ts: new Date(),
            });
            // Do the callback if provided
            //
            if (isFunction$1(subscription.confirmedCallback)) {
                subscription.confirmedCallback(confirmedEvent);
            }
            // Emit event on the observable
            //
            this.event$.next({
                hub: hubDetails.Name,
                method: 'ElevationBarcode:Confirmed',
                subjectId: subscription.requestToken,
                data: confirmedEvent,
            });
        });
        // Add the hub connection
        //
        const hubConnection = {
            connection,
            hub: hubDetails,
            uri: hubUri,
            subjectId: subscription.requestToken,
            token: subscription.token,
            reconnect_count: 0,
            subscribe: async () => {
                const subResponse = await connection.invoke('Subscribe', { RequestToken: subscription.requestToken });
                debug(`[RT:HUB:EVENT] Subscribed to elevation barcode events`, subResponse);
                this.activity$.next({
                    hubName,
                    type: 'send',
                    payload: 'ElevationBarcode:Subscribe',
                    ts: new Date(),
                });
            }
        };
        this.connection[hubName].push(hubConnection);
        // Subscribe to order events
        //
        if (hubConnection.subscribe) {
            try {
                await hubConnection.subscribe();
            }
            catch (error) {
                warn(`[RT:HUB:EVENT] Failed to subscribe to elevation barcode events`, error);
            }
        }
        return hubConnection;
    }
    /**
     * Stop listening to elevation barcode hub events
     *
     * @param {IHubElevationBarcodeJoinRequest} subscription The subscription details
     * @memberof RealtimeCommunicator
     */
    async leaveElevationBarcodeHub(subscription) {
        const hubName = subscription.hubName || 'ElevationBarcodeHub';
        const hubConnections = filter(this.connection[hubName], { subjectId: subscription.requestToken });
        debug(`[RT:HUB] Disconnecting from ${hubName}`);
        if (hubConnections.length) {
            for (const hubConnection of hubConnections) {
                // Stop listening to events
                //
                hubConnection.connection.off('Confirmed');
                try {
                    await hubConnection.connection.stop();
                }
                catch (error) {
                    warn(`[RT:HUB] Error disconnecting from ${hubName}/${hubConnection.subjectId}`, error);
                }
            }
            // Remove the connection(s)
            //
            remove$1(this.connection[hubName], { subjectId: subscription.requestToken });
            return;
        }
        throw new Error(`Not connected to hub ${hubName}/${subscription.requestToken}`);
    }
}
const communicator = new RealtimeCommunicator();

/**
 * The payment transaction properties added when it is updated
 *
 * @export
 * @enum {number}
 */
var PaymentTransactionProperties;
(function (PaymentTransactionProperties) {
    PaymentTransactionProperties[PaymentTransactionProperties["None"] = 0] = "None";
    // The transaction was reversed by the psp
    PaymentTransactionProperties[PaymentTransactionProperties["Reversed"] = 1] = "Reversed";
    // The transaction was charged back due to a dispute
    PaymentTransactionProperties[PaymentTransactionProperties["Chargedback"] = 2] = "Chargedback";
    // The losses created by a Reversal or a Chargeback are booked
    PaymentTransactionProperties[PaymentTransactionProperties["DisputeResolved"] = 4] = "DisputeResolved";
    // A request for cancelling the remaining capturable amount has been done
    PaymentTransactionProperties[PaymentTransactionProperties["CaptureCancelRequested"] = 8] = "CaptureCancelRequested";
    // A request for cancelling the remaining capturable amount has been confirmed
    PaymentTransactionProperties[PaymentTransactionProperties["CaptureCancelConfirmed"] = 16] = "CaptureCancelConfirmed";
    // A request for a fraud check has been issued, pending external verification
    PaymentTransactionProperties[PaymentTransactionProperties["FraudCheckRequested"] = 32] = "FraudCheckRequested";
    // A request for a fraud check has passed
    PaymentTransactionProperties[PaymentTransactionProperties["FraudCheckPassed"] = 64] = "FraudCheckPassed";
    // A request for a fraud check has been denied
    PaymentTransactionProperties[PaymentTransactionProperties["FraudCheckDenied"] = 128] = "FraudCheckDenied";
})(PaymentTransactionProperties || (PaymentTransactionProperties = {}));

/**
 * This collector and emitter can help applications with creating a local log
 * of actions happening in the SDK. It could also prove valuable for analytics.
 * The NetworkHealthMonitor uses these events for monitoring purposes
 *
 * @export
 * @class EventLogger
 */
class EventLogger {
    constructor() {
        /**
         * The activity log stream
         *
         * @type {Subject<EventLog>}
         */
        this.changes$ = new Subject();
        // Emit the connection activity events from the SignalR hubs
        //
        communicator.activity$.subscribe((event) => {
            this.changes$.next({
                event_type: 'communicator',
                data: event,
            });
        });
        // Emit the connection status events from the sentinel
        //
        sentinel.status.changes$.subscribe((event) => {
            this.changes$.next({
                event_type: 'sentinel-status',
                data: event,
            });
        });
    }
    /**
     * This method will emit a service activity event.
     * Will be called by core.fetch but is exposed for other fetch sources
     *
     * @param {IServiceActivity} event
     */
    addServiceEvent(event) {
        this.changes$.next({
            event_type: 'service',
            data: event,
        });
    }
    /**
     * This method will emit an application activity event.
     * Can be used by the application using the SDK to log an event
     *
     * @param {IApplicationActivity} event
     */
    addApplicationEvent(event) {
        this.changes$.next({
            event_type: 'application',
            data: event,
        });
    }
}
const eventLogger = new EventLogger();

// AUTO-GENERATED Wed Apr 12 2023 14:09:55 GMT+0200 (Central European Summer Time) do not edit or replace
const EVA_API_VERSION = 684;

/**
 * The elevation helper is provided any 403 responses from core.fetch that contain headers
 * indicating an elevated permission check could allow the call to proceed.
 *
 * @export
 * @class ElevationHelper
 */
class ElevationHelper {
    constructor() {
        /**
         * This observable stream will emit when a service response is received that contains
         * elevation headers and needs manager approval
         *
         * @type {Subject<IElevationEvent>}
         */
        this.elevationEvent$ = new Subject();
        /**
         * Private helper object to translate returned header values to enum raw values
         *
         * @private
         */
        this.elevationScopeValues = {
            None: 0,
            Create: 1,
            Edit: 2,
            Delete: 4,
            View: 8,
            Manage: 31
        };
    }
    /**
     * Returns a copy of the currently active elevation event (if any)
     */
    getCurrentElevationEvent() {
        if (this.currentElevationEvent) {
            return Object.assign({}, this.currentElevationEvent);
        }
    }
    /**
     * If he core fetch implementation encounters a 403 will pass it to this method.
     * This method will check for the elevation headers and then generate
     * the elevation barcode and connect to the elevation signalr hub if needed
     *
     * @param {{
     *     response: Response,
     *   }} {
     *     response,
     *   }
     */
    async checkForElevationResponse({ serviceName, response, serviceContext, }) {
        log$1('Headers', response.headers);
        const elevationFunctionality = response.headers.get('EVA-Elevation-Functionality');
        const elevationScopeName = response.headers.get('EVA-Elevation-Scope') || '';
        const elevationScope = this.elevationScopeValues[elevationScopeName];
        const organizationUnitId = parseInt(response.headers.get('EVA-OrganizationUnitId') || '', 10);
        if (elevationFunctionality && elevationScope !== undefined) {
            log$1('Detected elevation event', elevationFunctionality, elevationScope);
            // We can only have one active elevation event at a time with the way the communicator
            // is setup in the SDK. So we must first expire any existing event
            //
            if (this.currentElevationEvent) {
                this.currentElevationEvent.status = 'EXPIRED';
                this.elevationEvent$.next(this.currentElevationEvent);
                this.currentElevationEvent = undefined;
            }
            const [generateBarcodeAction] = generateElevationBarcode.createFetchAction({
                Functionality: elevationFunctionality,
                Scope: elevationScope,
                OrganizationUnitID: organizationUnitId
            });
            const generateBarcodeResponse = await generateElevationBarcode.fetchData(generateBarcodeAction);
            const elevationEvent = {
                barcode: generateBarcodeResponse.Barcode,
                functionality: elevationFunctionality,
                requestToken: generateBarcodeResponse.RequestToken,
                scope: elevationScope,
                serviceContext,
                serviceName,
                status: 'ACTIVE',
            };
            this.currentElevationEvent = elevationEvent;
            // Emit initial occurrence
            //
            this.elevationEvent$.next(elevationEvent);
            const joinElevationHubRequest = {
                requestToken: generateBarcodeResponse.RequestToken,
                token: settings.userToken,
                subscriberName: 'evaSdkReduxElevationHelper',
                confirmedCallback: async (confirmEvent) => {
                    elevationEvent.status = 'CONFIRMED';
                    elevationEvent.confirmedUserId = confirmEvent.ConfirmedUserID;
                    this.elevationEvent$.next(elevationEvent);
                    this.currentElevationEvent = undefined;
                },
            };
            // Join will invoke Subscribe
            //
            communicator.joinElevationBarcodeHub(joinElevationHubRequest);
        }
    }
}
const elevationHelper = new ElevationHelper();

/**
 * EVA Redux SDK core which handles configuration and provides shared request
 * implementation
 *
 * @export
 * @class Core
 */
class Core {
    /**
     * Creates an instance of Core
     * @memberof Core
     */
    constructor() {
        /**
         * The store as an observable stream
         * @see https://medium.com/@fahad19/streaming-redux-state-as-an-observable-with-rxjs-390a8f7bc08c
         *
         * @memberof Core
         */
        this.state$ = new BehaviorSubject(store.getState());
        /**
         * Indicates the core has completed the bootstrap at least once
         *
         * @memberof Core
         */
        this.hasBootstrapped = false;
        // Emit on every new state changes
        //
        store.subscribe(() => {
            this.state$.next(store.getState());
        });
    }
    /**
     * Bootstraps the SDK with provided settings
     * Performs initial data fetch unless instructed not to
     * Will start checking user status for token expiration
     *
     * @param {IBootstrapOptions} options
     * @memberof Core
     */
    async bootstrap(options) {
        ////////////////////////////////////////////////////////////////////////////
        // Settings
        ////////////////////////////////////////////////////////////////////////////
        //
        // Enable debug logging if asked
        //
        settings.enableLogging = !!options.debug;
        // Configure application name for EVA-User-Agent
        //
        settings.appName = options.appName;
        settings.appVersion = options.appVersion;
        // Apply end point for EVA
        //
        settings.endPointURL = options.endPointUrl;
        // Set the language i.e. set the Accept-Language header
        //
        if (options.language) {
            settings.language = options.language;
        }
        // Set the last known sentinelEndPointUrl which is used for offline startup
        //
        if (options.sentinelEndPointUrl) {
            settings.sentinelEndPointUrl = options.sentinelEndPointUrl;
        }
        // Setting the default call user type setting
        //
        settings.defaultCallUserType = options.defaultCallUserType;
        ////////////////////////////////////////////////////////////////////////////
        // Bootstrap
        ////////////////////////////////////////////////////////////////////////////
        //
        // We will need to fetch the application token first using list applications
        // If EVA cloud is down this could fail at which point we will need to get
        // creative with onPremise support
        //
        // Start the real-time communicator if requested
        // The sentinel requires the communicator to function so if either option
        // is set we will start it
        //
        let optionalCommunicatorStartPromise = Promise.resolve();
        if (options.startCommunicator || options.enableSentinel) {
            // Communicator relies on getApplicationConfiguration response
            // which we fetched just before
            //
            optionalCommunicatorStartPromise = communicator.start();
            debug('[EVA-SDK-REDUX] Started the communicator');
        }
        // Enable sentinel support if requested
        // This needs to be done before trying to connect to EVA cloud
        // If the internet or EVA is unreachable this should allow us to start working
        // in an offline fashion.
        // Once a connection is restored the bootstrap support of the sentinel
        // should complete the process as started here
        //
        let optionalSentinelStartPromise = Promise.resolve();
        if (options.enableSentinel) {
            // Sentinel relies on getApplicationConfiguration response
            // which we fetched just before
            //
            try {
                optionalSentinelStartPromise = sentinel.start();
                debug('[EVA-SDK-REDUX] Enabled sentinel support');
            }
            catch (error$1) {
                error('[EVA-SDK-REDUX] Sentinel bootstrap has failed', error$1);
            }
        }
        // Fetch initial application configuration
        // Manually call fetchData to ensure no auth token is used
        //
        try {
            const [applicationConfigurationAction] = getApplicationConfiguration.createFetchAction();
            const applicationConfiguration = await getApplicationConfiguration.fetchData(applicationConfigurationAction);
            // Manually dispatch the response action
            //
            const applicationConfigurationResponseAction = getApplicationConfiguration.createResponseAction(applicationConfiguration);
            store.dispatch(applicationConfigurationResponseAction);
        }
        catch (error$1) {
            error('[EVA-SDK-REDUX] Bootstrap failed', error$1);
            // Preserve last known sentinel when EVA cloud is not responding
            //
            settings.preserveLastKnownSentinel = true;
            // At this point we need to emit an empty configuration response if a sentinel
            // is configured. This will allow it to spring into action and take over
            // the bootstrap
            //
            if (settings.sentinelEndPointUrl) {
                warn('[EVA-SDK-REDUX] Sentinel is available. Trying to defer bootstrap...');
                const emptyConfigurationAction = getApplicationConfiguration.createResponseAction({ Configuration: {}, Metadata: {} });
                store.dispatch(emptyConfigurationAction);
            }
            throw error$1;
        }
        // Setup anonymous default token if supplied
        // This should not be needed anymore but the option remains for
        // advanced use cases and backwards compatibility
        //
        if (options.defaultToken) {
            settings.defaultToken = options.defaultToken;
        }
        // Setup user token if supplied
        // Can be unset later if it has expired
        //
        if (options.userToken) {
            settings.userToken = options.userToken;
        }
        // Setup last know application token if supplied
        //
        if (options.applicationToken) {
            settings.applicationToken = options.applicationToken;
        }
        // Wait for communicator and sentinel before proceeding
        //
        await optionalCommunicatorStartPromise;
        await optionalSentinelStartPromise;
        // Bootstrap the data if requested
        //
        if (options.disableDataBootstrap !== true) {
            // The current user chain will re-fetch the application configuration
            //
            const [userAction, , userChainPromise] = getCurrentUser.createFetchAction();
            store.dispatch(userAction);
            try {
                await userChainPromise;
            }
            catch (userError) {
                warn('[EVA-SDK-REDUX] Failed to fetch user during bootstrap', userError);
            }
            if (options.disableCartBootstrap !== true) {
                const [cartAction, , cartChainPromise] = getShoppingCartInfo.createFetchAction();
                store.dispatch(cartAction);
                try {
                    await cartChainPromise;
                }
                catch (cartError) {
                    warn('[EVA-SDK-REDUX] Failed to fetch cart during bootstrap', cartError);
                }
            }
        }
        // Indicate we have bootstrapped
        //
        this.hasBootstrapped = true;
        // Resolve with fetch data
        //
        return [
            getApplicationConfiguration.getState().response,
            getCurrentUser.getState().response,
            getShoppingCartInfo.getState().response,
        ];
    }
    /**
     * Wraps the low level fetch API adding the default request headers for EVA
     *
     * @param {string} uri
     * @param {*} [options]
     * @returns {Promise<any>}
     * @memberof Core
     */
    fetch(uri, options, timeout = 15000, authorisationToken, elevationToken, serviceContext = uuid(), chainContext) {
        // Merge the default options into the fetch options
        // Allow the call parameter to override any default
        // Fetch is pretty low level so we need to enable cors and cookie
        // transfer for credentials.
        // Default method is POST for post message type calls
        //
        const defaultHeaders = {
            'Accept': 'application/json',
            'Accept-Language': settings.language,
            'Content-type': 'application/json',
            'ClientName': settings.clientName,
            'ClientVersion': settings.clientVersion,
            'EVA-User-Agent': `${settings.appName}/${settings.appVersion}`,
            'EVA-API-Version': `${EVA_API_VERSION}`,
            'EVA-App-ContextID': serviceContext,
        };
        if (chainContext) {
            defaultHeaders['EVA-App-ContextChainID'] = chainContext;
        }
        // Add the currently used station to the headers if set
        // This is added for legal reasons in certain regions to track where
        // a sale or its related actions take place
        //
        if (settings.currentStationID) {
            defaultHeaders['EVA-StationID'] = `${settings.currentStationID}`;
        }
        // Add elevation token if supplied
        //
        if (elevationToken) {
            defaultHeaders['EVA-Elevation-Token'] = elevationToken;
        }
        // Set optionally supplied authorisation token
        //
        if (authorisationToken) {
            defaultHeaders.Authorization = authorisationToken;
        }
        // Add the currently used application token
        // This is used for authenticated access to anonymous orders
        //
        if (settings.applicationToken) {
            defaultHeaders['EVA-App-Token'] = `${settings.applicationToken}`;
        }
        // Add the requested organization unit id
        //
        if (settings.requestedOrganizationUnitID) {
            defaultHeaders['EVA-Requested-OrganizationUnitID'] = `${settings.requestedOrganizationUnitID}`;
        }
        // Add the requested organization unit query
        //
        if (settings.requestedOrganizationUnitQuery) {
            defaultHeaders['EVA-Requested-OrganizationUnit-Query'] = JSON.stringify(settings.requestedOrganizationUnitQuery);
        }
        // Add the id mode option if set
        //
        if (settings.backendIdMode) {
            defaultHeaders['EVA-IDs-Mode'] = `${settings.backendIdMode}`;
        }
        // Add the id backend system option if set
        //
        if (settings.backendIdSystemID) {
            defaultHeaders['EVA-IDs-BackendSystemID'] = `${settings.backendIdSystemID}`;
        }
        const defaultOptions = {
            mode: 'cors',
            method: 'POST',
            credentials: 'include',
            headers: defaultHeaders,
        };
        // Prepare the fetch options
        //
        const fetchOptions = extend({}, defaultOptions, omit(options, ['headers', 'body']));
        // Merge the headers
        //
        if (options && !isEmpty(options.headers)) {
            extend(fetchOptions.headers, options.headers);
        }
        // Serialize the request body
        //
        if (get(fetchOptions, 'headers.Content-type') === 'application/json') {
            // Ensure options are set
            //
            if (!isObject$1(options)) {
                options = {};
            }
            // JSON body content
            //
            fetchOptions.body = JSON.stringify(options.body);
        }
        else {
            // Custom body content
            //
            fetchOptions.body = options.body;
        }
        const serviceName = sentinel.offline.getServiceNameFromUrl(uri) || 'none';
        log$1(`[EVA-SDK-REDUX:core:${serviceName}] Doing fetch`, uri, fetchOptions);
        if (isFunction$1(settings.beforeFetch)) {
            settings.beforeFetch({ serviceContext, chainContext, serviceName, url: uri, requestPayload: options.body });
        }
        return new Promise((resolve, reject) => {
            // Prepare the service event to emit when the call is done
            //
            const serviceEvent = {
                start_ts: new Date().valueOf(),
                end_ts: 0,
                status: 0,
                request: {
                    url: uri,
                    headers: options.headers,
                    data: options.body,
                },
            };
            // Prepare the timeout
            //
            let timedOut = false;
            const timer = setTimeout(() => {
                error('[EVA-SDK-REDUX:core] Got fetch timeout');
                timedOut = true;
                const error$1 = new TimeoutError('Request timed out');
                serviceEvent.end_ts = new Date().valueOf();
                serviceEvent.status = 408;
                eventLogger.addServiceEvent(serviceEvent);
                if (isFunction$1(settings.afterFetch)) {
                    settings.afterFetch({
                        serviceContext,
                        chainContext,
                        serviceName,
                        url: uri,
                        status: serviceEvent.status,
                        requestPayload: options.body,
                        responsePayload: undefined,
                        responseError: error$1,
                    });
                }
                reject(error$1);
            }, timeout);
            // Call fetch but return a more sensible promise result
            //
            fetch(uri, fetchOptions)
                .then((response) => {
                try {
                    log$1(`[EVA-SDK-REDUX:core:${serviceName}] Got fetch response`, response.body ? response.body.toString() : response);
                }
                catch (error) {
                    log$1(`[EVA-SDK-REDUX:core:${serviceName}] Got fetch response`, response);
                }
                if (timedOut) {
                    log$1(`[EVA-SDK-REDUX:core:${serviceName}] Got fetch response after timeout`, response);
                }
                else {
                    // Stop the timer
                    //
                    clearTimeout(timer);
                    if (response.ok) {
                        // Check for EVA-Warning and EVA-App-Token header(s)
                        //
                        response.headers.forEach((value, name) => {
                            if (`${name}`.toLowerCase() === 'EVA-Warning'.toLowerCase()) {
                                // This uses the console directly on purpose
                                // EVA Warnings should always be logged
                                //
                                console.warn('[EVA-WARNING]', value);
                            }
                            if (`${name}`.toLowerCase() === 'EVA-App-Token'.toLowerCase()) {
                                // We received a new application token
                                //
                                console.log('[EVA-APP] New token received', value);
                                settings.applicationToken = value;
                            }
                        });
                        serviceEvent.end_ts = new Date().valueOf();
                        serviceEvent.status = response.status;
                        serviceEvent.response = response;
                        eventLogger.addServiceEvent(serviceEvent);
                        if (isFunction$1(settings.afterFetch)) {
                            settings.afterFetch({
                                serviceContext,
                                chainContext,
                                serviceName,
                                url: uri,
                                status: serviceEvent.status,
                                requestPayload: options.body,
                                responsePayload: response,
                            });
                        }
                        resolve(response);
                    }
                    else {
                        const statusCode = get(response, 'status');
                        // Pass any 403 response to the elevation helper to check for
                        // possible elevation headers to handle scanning and signalr based feedback
                        //
                        if (statusCode === 403) {
                            debug('Received 403 response, checking for elevation event');
                            elevationHelper.checkForElevationResponse({ response, serviceName, serviceContext });
                        }
                        // Check for a 401 and notify getCurrentUser if a userToken is
                        // currently set
                        //
                        if (statusCode === 401 && authorisationToken === settings.userToken) {
                            warn('[EVA-SDK-REDUX] User token has expired', settings.userToken);
                            getCurrentUser.expire();
                        }
                        serviceEvent.end_ts = new Date().valueOf();
                        serviceEvent.status = response.status;
                        serviceEvent.response = response;
                        eventLogger.addServiceEvent(serviceEvent);
                        if (isFunction$1(settings.afterFetch)) {
                            settings.afterFetch({
                                serviceContext,
                                chainContext,
                                serviceName,
                                url: uri,
                                status: serviceEvent.status,
                                requestPayload: options.body,
                                responsePayload: response,
                            });
                        }
                        reject(response);
                    }
                }
            })
                .catch((error$1) => {
                // Stop the timeout timer
                //
                clearTimeout(timer);
                if (timedOut) {
                    error('[EVA-SDK-REDUX:core] Got fetch fail response after timeout', error$1);
                }
                else {
                    error('[EVA-SDK-REDUX] Fetch failed', error$1.message, error$1);
                    serviceEvent.end_ts = new Date().valueOf();
                    serviceEvent.status = 0;
                    eventLogger.addServiceEvent(serviceEvent);
                    if (isFunction$1(settings.afterFetch)) {
                        settings.afterFetch({
                            serviceContext,
                            chainContext,
                            serviceName,
                            url: uri,
                            status: serviceEvent.status,
                            requestPayload: options.body,
                            responsePayload: undefined,
                            responseError: error$1,
                        });
                    }
                    reject(error$1);
                }
            });
        });
    }
    /**
     * Resets the store state to initial and returns the previous state
     *
     * @param {IRootState} [initialState] Optional new state to merge with initial. Can be used for rehydration
     * @returns {IRootState} The state before the dump
     * @memberof Core
     */
    dump(initialState) {
        const currentState = store.getState();
        // Unset user token
        //
        settings.userToken = '';
        // Call root state reset
        //
        store.dispatch({ type: 'RESET', payload: initialState });
        return currentState;
    }
}
const core = new Core();

/**
 * The network health monitor tries to detect when the network connection to
 * the EVA backend becomes unstable or goes down.
 * If a sentinel is present it can defer most of its work to the Sentinel offline status
 *
 * @export
 * @class NetworkHealthMonitor
 */
class NetworkHealthMonitor {
    /**
     * Creates an instance of NetworkHealthMonitor with its threshold values.
     * Should they not suffice applications can modify them
     *
     * @param {number} [stableThreshold=85] This score and up is considered stable
     * @param {number} [unstableThreshold=35] This score up to stable is considered unstable
     */
    constructor(stableThreshold = 85, unstableThreshold = 35) {
        this.stableThreshold = stableThreshold;
        this.unstableThreshold = unstableThreshold;
        /**
         * The current network health status
         *
         * @type {TNetworkHealth}
         */
        this.status = 'unknown';
        /**
         * The numerical score used to determine the health status
         *
         * @type {number}
         */
        this.score = 100;
        /**
         * The status change stream
         *
         * @type {Subject<INetworkHealth>}
         */
        this.changes$ = new Subject();
        // Start listening to the various network related events from within the SDK
        //
        // We will source our info from (in order):
        //
        // * sentinel status
        // * service call failures (408/0)
        // * signalr hub connections
        //
        // The SDK event logger will be a good source for combining all these events
        //
        eventLogger.changes$.subscribe((event) => {
            switch (event.event_type) {
                case 'sentinel-status':
                    {
                        // Sentinel events are deemed the most reliable
                        //
                        const sentinelEvent = event.data;
                        if (sentinelEvent.eva === 'connected') {
                            this.updateScore(100);
                        }
                        if (sentinelEvent.eva === 'disconnected') {
                            this.updateScore(-100);
                        }
                        if (sentinelEvent.eva === 'reconnecting') {
                            this.updateScore(-1);
                        }
                    }
                    break;
                case 'service':
                    {
                        const serviceEvent = event.data;
                        const serviceStatus = serviceEvent.status || 0;
                        if (serviceStatus === 0 || serviceStatus === 408) {
                            // Timeout or error connecting issues
                            //
                            this.updateScore(-10);
                        }
                        else {
                            this.updateScore(100);
                        }
                    }
                    break;
                case 'communicator':
                    {
                        const communicatorEvent = event.data;
                        if (communicatorEvent.type === 'reconnected') {
                            this.updateScore(100);
                        }
                        // Receiving events is more valuable then a reconnect message
                        //
                        if (communicatorEvent.type === 'receive') {
                            this.updateScore(20);
                        }
                        if (communicatorEvent.type === 'reconnecting') {
                            this.updateScore(-1);
                        }
                        if (communicatorEvent.type === 'close') {
                            // If we have a reconnect count greater then 0 there are connection
                            // issues
                            //
                            const reconnectCount = parseInt(`${communicatorEvent.payload}`, 10);
                            if (reconnectCount && reconnectCount > 1) {
                                this.updateScore(-5);
                            }
                        }
                    }
                    break;
            }
        });
    }
    updateScore(difference) {
        // Ensure score stays between 0 and 100
        //
        this.score = Math.max(Math.min(this.score + difference, 100), 0);
        // Calculate new status
        //
        if (this.score >= this.stableThreshold) {
            this.status = 'stable';
        }
        else if (this.score >= this.unstableThreshold) {
            this.status = 'unstable';
        }
        else {
            this.status = 'down';
        }
        // Emit change
        //
        this.changes$.next(this.getCurrentStatus());
    }
    /**
     * Return the current status and score for consumption by application clients
     *
     * @returns INetworkHealth
     */
    getCurrentStatus() {
        const update = {
            status: this.status,
            score: this.score
        };
        return update;
    }
}
const networkHealthMonitor = new NetworkHealthMonitor();

// Polyfill promise support
// This will update the global Promise implementation as needed
//
require('es6-promise/auto');
// Polyfill fetch support
//
require('cross-fetch/polyfill');

export { EVA_API_VERSION, ElevationHelper, EventLogger, NetworkHealthMonitor, OrderMode, OrderStatus, PaymentTransactionProperties, RealtimeCommunicator, Sentinel, TimeoutError, abortTransaction, addBoardingPassToOrder, addBundleProductToOrder, addDiscountCouponToOrder, addLabelToFullStockCount, addManualDiscountToOrder, addProductToFullStockCountLabel, addProductToOrder, addPushNotificationDevice, addServiceProductToOrder, addTrackingCodeToReturnToSupplierShipment, addUserBorrowedProductToOrder, addVoucherToShoppingCart, adjustStock, applyBundleProductSelection, applySalesTaxEstimateForOrder, approvePayment, attachAddressToOrder, attachCustomerToOrder, autocompleteAddress, cancelDiscountOrderLine, cancelFullStockCount, cancelFullStockCountLabel, cancelOrder, cancelOrderLineFulfillments, cancelPayment, cancelSecondChanceProductRegistration, cardBalanceCheck, changeOrderLinesToCarryOut, changeOrderLinesToDelivery, changeOrderLinesToPickup, changeUserPassword, checkEmailAddressAvailability, checkNicknameAvailability, clockEmployeeOutAsAbsent, closeFinancialPeriod, communicator, completeCloseCashJournal, completeFullStockCount, completeFullStockCountLabel, completeOpenCashJournal, completePrintPriceLabelTask, completePrintTask, completeReservationCleanup, completeReservationDeviation, completeReturnToSupplierTask, completeSecondChanceProductRegistration, completeShipFromStoreTask, completeStockMovementFollowUp, completeStockReplenishment, completeStockReservation, completeZonedCycleCountPreCount, completeZonedCycleCounts, confirmSubscription, core, correctWorkedHours, countProductForInitialCycleCount, countStockLabelForZonedCycleCount, createAddressBookItem, createBlob, createCashDeposit, createCashExpense, createCompanyForUser, createCustomer, createCustomerReturn, createCustomerReturnWithoutOrder, createFullStockCount, createIdentificationPinForEmployee, createInterbranchOrder, createOrder, createOrderLineUnitPriceCorrection, createPayment, createPrintTask, createPrivacyRemovalRequest, createQuickBuy, createRefund, createReturnToSupplierOrder, createStockNotification, createStockNotificationForCurrentUser, createStockReplenishmentTask, createStockReplenishmentTasks, createUserCardMutation, createUserInteraction, createUserPhoneNumber, createWorkedHours, createZonedCycleCount, deactivateUserTask, deleteAddressBookItem, deleteQuickBuy, deleteStockNotification, deleteUser, deleteUserPhoneNumber, deliverShipment, detachCompanyFromUser, detachCustomerFromOrder, detachOrder, disableDiscountOnOrder, downloadInvoice, downloadOrderConfirmation, dropOffCashDeposit, duplicateOrder, elevationHelper, emailInvoice, emailOrderConfirmation, emptyGiftCard, enableDisabledDiscountOnOrder, evaPayGetInfo, eventLogger, exemptOrderFromTax, forceVerifyCustomer, fullyReceiveShipment, generateDeviceHubBarcode, generateElevationBarcode, generateIdentificationCodeForEmployee, generateIdentificationPinForEmployee, generateScanModeBarcode, generateSessionBarcode, generateTemporaryElevationCode, getAddressForZipCode, getAddressRequirements, getApplicationConfiguration, getAuditingSummary, getAutocompleteAddressByReference, getAvailableCashAmountForDevice, getAvailableDocumentsForOrder, getAvailableGiftWrappingLinesForOrder, getAvailableOpenIDConfigurations, getAvailablePaymentMethods, getAvailableRefundPaymentMethodsForOrder, getAvailableServices, getAvailableSubscriptions, getAvailableTwoFactorAuthenticators, getAvailableUserRequirementsProperties, getBlobInfo, getBundleProductDetail, getBundleProductDetailProductAvailability, getBundleProductDetailQuantityOnHand, getBundleProducts, getBundleProductsForProduct, getCFDConfiguration, getCashExpenseTypes, getCashJournalDenominations, getCompanyForUser, getConfigurableProductDetail, getConfigurableProductDetailProductAvailability, getConfigurableProductDetailQuantityOnHand, getConfigurableProductSelectedProductDetail, getConfigurationQrForDevice, getCurrentApplication, getCurrentCashJournals, getCurrentFinancialPeriodSummary, getCurrentInitialCycleCount, getCurrentUser, getCurrentUserIdentificationInfo, getCustomFieldTypes, getCustomerReturnReasons, getCycleCountOriginTypes, getDeliveryOrderData, getDeviceTypes, getDigitalGiftCardOptions, getDiscountById, getDiscountProductOptionsForOrder, getDiscountProductOptionsForOrderLine, getElectronicInvoiceData, getEmailAddressDomainSuggestions, getEnumValues, getFinancialPeriodClosingImpediments, getFinancialPeriodDetails, getFinancialPeriodInformation, getFiscalOrderData, getFullStockCount, getGiftCardOptions, getGiftWrappingOptionsForOrder, getGreetingCards, getInitialCycleCountLabel, getInquiry, getLotteryNumber, getOpenCashDrawerReasons, getOrder, getOrderDiscountInfo, getOrderFulfillmentOptions, getOrderSignature, getOrderStatus, getOrderStatusForOrders, getOrderStatusHistory, getOrderSummaryForShipping, getOrganizationUnitDetailed, getOrganizationUnitSummary, getPackagesForCarrier, getPaymentTransaction, getPaymentTransactionsForOrder, getPaymentTypeByID, getPickProductDiscountOptionsForOrder, getPickProductDiscountOptionsForOrderLine, getPickProductDiscountOptionsForOrderProducts, getPotentialBundleProductsForOrder, getPriceListUsageTypes, getProductAvailability, getProductDetail, getProductDetailProductAvailability, getProductDetailQuantityOnHand, getProductPrices, getProductPricesForSearchProducts, getProductPricesForShoppingCart, getProductPricing, getProductRequirementById, getProductRequirementValuesForOrder, getProductRequirementValuesForOrderLine, getProductSearchSuggestions, getProductUnitOfMeasureQuantities, getProductVariants, getProducts, getQuantityOnHandForProducts, getQuickBuy, getQuickBuyOptions, getQuickBuys, getRefundOptionsForOrder, getRequiredDataForOrder, getReturnOrdersForOrder, getReturnReasons, getReturnToSupplierData, getReturnableStatusForOrder, getSalesTaxEstimateForOrder, getServiceProducts, getShipmentDetails, getShoppingCart, getShoppingCartInfo, getShopsByProximity, getStation, getStatusForZonedCycleCountPreCounts, getStockAvailabilityEstimateForOrder, getStockAvailabilityEstimateForProducts, getStockAvailabilityTimeline, getStockDetailsForProduct, getStockLabels, getStockMutationReasons, getStockNotification, getStockNotificationForCurrentUser, getStockReplenishmentTaskData, getStockReplenishmentTaskZones, getTaxExemptionReasons, getTransportOrderLineData, getUser, getUserBySocialSecurityNumber, getUserCardBalance, getUserCardDetails, getUserCardsForUser, getUserInfoBySocialSecurityNumber, getUserOrigins, getUserPhoneNumbers, getUserRequirements, getUserRequirementsForSubscriptions, getUserSubscriptions, getUserTaskCounts, getUserTaskDetails, getUserTaskNotCompletedReasons, getWarehouseOrderData, getWebshops, getZonedCycleCount, getZonedCycleCountPreCount, globalBlueGetEligibilityInformation, globalBluePrintForm, globalBlueSetGlobalBlueIdentifier, globalBlueStartTaxFree, janrainRequestPasswordResetToken, janrainResetPassword, listAddressBook, listAdyenCheckoutGateways, listAppSettings, listApplicationCultures, listApplications, listAvailableShippingMethods, listAvailableUserTasks, listBlobs, listBlobsForOrder, listBookings, listCashDeposits, listCashExpenses, listCashTransactionLedger, listCashTransactionLedgerGroups, listClockedInEmployeesForFinancialPeriod, listCountries, listCultures, listCustomFields, listCustomOrderStatus, listDevices, listDisabledDiscountsOnOrder, listEmployeesForOrganizationUnit, listEventLedger, listFinancialPeriods, listFullStockCountLabelProducts, listFullStockCountLabels, listFullStockCountResult, listFullStockCountSummary, listGeneralLedgers, listInitialCycleCountLabels, listInterbranchOrganizationUnitForOrder, listInvoicesForOrder, listManualDiscounts, listOpeningHours, listOrderFulfillments, listOrderLines, listOrderShipments, listOrdersForCustomer, listOrdersWithCustomerReferences, listOrganizationUnitLanguages, listOrganizationUnitSummaries, listOrganizationUnitsDetailed, listProductBundles, listPurchaseOrderShipments, listReturnToSupplierTaskOrderLines, listReturnToSupplierTaskShipmentLines, listReturnableSuppliersForOrder, listShelfProducts, listShipmentLines, listShopsInArea, listStationsForOrganizationUnit, listStockMutationReasons, listStockReplenishmentTaskData, listSuspendedOrders, listTaxCodes, listUserBorrowedProducts, listUserCardMutations, listUserInteractions, listUserRequirementSets, listUserRequirements, listWorkedHours, listWorkedHoursForCurrentEmployee, listWorkedHoursForFinancialPeriod, listZonedCycleCounts, login, logout, makeUserPhoneNumberPrimary, missingProductForInitialCycleCount, modifyOrderLinePrice, modifyQuantityOrdered, moveCash, moveStock, networkHealthMonitor, openCashDrawer, openFinancialPeriod, paazlGetShippingOptionsForOrder, paazlUpdateShippingOptionsForOrder, parseBarcode, ping, placeOrder, postNLGetAvailablePickUpLocationsForOrder, postNLGetAvailableTimeframesForOrder, postNLGetShippingOptionsForOrder, postNLUpdateShippingOptionsForOrder, preCountInitialCycleCountLabel, prefigureDiscounts, prepareOrderForCheckout, previewOrderFulFillment, printControlDocument, printFullStockCountLabels, printInterbranchOrderReceipt, printPaperInvoice, printPaperInvoices, printPaperOrderConfirmation, printPriceLabel, printProductPriceLabel, printQrCode, printReturnToSupplierTaskShipment, printStockReservationReceipt, printTerminalReport, printThermalGiftReceipt, printThermalInvoice, printThermalInvoices, printThermalOrderConfirmation, printThermalPaymentProof, processPrintTasks, produceCashDepositDocument, produceCashExpenseDocument, produceDocuments, produceShipmentDocuments, productCategorySearch, receiveShipment, recoverShoppingCart, refundOrder, registerUserBorrowedProducts, removeOrderLineUnitPriceCorrection, removePushNotificationDevice, renderThermalPrintResult, reprintThermalPrintResult, requestDeviceAuthenticationToken, requestPasswordResetToken, requestPaymentUpdate, reserveOrder, resetUserPassword, returnOrderLines, returnUserBorrowedProducts, ritualsAddGiftCardToOrder, ritualsApplyEmployeeDiscount, ritualsAttachCustomerToOrder, ritualsCreateCustomerReturn, ritualsGeneratePassbookPass, ritualsGetBlobsForReturn, ritualsGetCartData, ritualsGetCustomerData, ritualsGetDeliveryOrderData, ritualsGetEmployeeDiscount, ritualsGetOrderForReturn, ritualsGetUserDataForUser, ritualsListAvailablePickupStores, ritualsPaazlGetShippingOptionsForOrder, ritualsPaazlUpdateShippingOptionsForOrder, ritualsPrintTalismanDocuments, ritualsScanLoyaltyCard, ritualsSendAddressFormLinkFromCart, ritualsSendCarouselFromCart, ritualsSendPaazlLinkFromCart, ritualsSendPaymentLinkFromCart, ritualsSendReceiptFromCart, ritualsSetCartData, ritualsUpdateLoyalty, searchOrders, searchProducts, searchUsers, sentinel, setCustomUserData, setCustomerReferencesOnOrder, setDefaultBillingAddress, setDefaultShippingAddress, setDiscountProductOption, setElectronicInvoiceData, setFiscalOrderData, setGiftWrappingOptionsOnOrder, setLineForReturnToSupplierShipment, setLotteryNumber, setOrderFulfillmentOptions, setOrderSoldBy, setPCIQuestionAnswerForFinancialPeriod, setPickProductDiscountOptionsForOrderLine, setPickupOrganizationUnit, setRemarkOnOrder, setRequestedDate, setSerialNumbers, setShippingMethod, setWarehouseOrderData, settings, shipOrder, signOrder, splitOrderLine, startCloseCashJournal, startCompositeUserTask, startFullStockCount, startFullStockCountLabel, startOpenCashJournal, startPrintPriceLabelTask, startPrintTask, startReceiveShipment, startReservationCleanup, startReservationDeviation, startReturnToSupplierTask, startSecondChanceProductRegistration, startShipFromStoreTask, startStockMovementFollowUp, startStockReplenishment, startStockReservation, startTransaction, startZonedCycleCount, startZonedCycleCountPreCount, store, storeBlob, submitInquiry, subscribeUser, suspendOrder, unsubscribeUser, updateAddressBookItem, updateCompanyForUser, updateDigitalGiftCardOptions, updateInterbranchOrder, updateOrderAddresses, updateOrderBillingAddress, updateOrderCheckoutOptions, updateOrderCustomFields, updateOrderLineStockLabel, updateOrderShippingAddress, updateProductRequirementValuesForOrderLine, updateQuickBuy, updateReturnToSupplierOrder, updateSerialNumber, updateStockReplenishmentTaskData, updateUser, updateUserAccountType, updateUserPhoneNumber, validateFiscalID, validateIdentificationPin, validateOrderShipment, validatePreCount, validateToken, validateVatNumber, verifyCustomer, verifyOrder };
